import styled, { css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { color, spacing, typography, boxShadows, zIndex } from '@icecreamsocial/themes';
import require$$0$2 from 'util';

/**
 * Creates a structured Anatomy
 * @param {*[]} rules an Array of CSS style rules
 * @param {ReactPropTypes} propTypes
 * @returns {Anatomy}
 */

const makeAnatomy = (rules, propTypes) => {
  if (!Array.isArray(rules)) throw Error('Rules must be an array');
  return {
    rules,
    propTypes
  };
};
/**
 *
 * @param {Anatomy} anatomy CSS interpolated string
 * @returns {StyledAnatomyCallback}
 */

const styledAnatomyFactory = anatomy => {
  return displayName => makeStyledObject(anatomy, displayName);
};
/**
 * @typedef {Object} StyledObjectFn
 * @param {*} [Component]
 */

/**
 * @todo clean up this function
 * @todo figure out how to document this so that the linter doesn't get angry
 *
 * @param {*} anatomy
 * @param {*} displayName
 * @return {StyledObjectFn}
 */

const makeStyledObject = (anatomy, displayName) => {
  /**
   * @type {StyledObjectFn}
   */
  let styledFn = Component => {
    const fn = createStyledAnatomyFunction(anatomy, displayName, Component);
    fn.attrs = makeAttrsFunction(anatomy, displayName, Component);
    return fn;
  };

  const styledObjProps = getPropsFromStyledComponents((styleObj, element) => {
    const attrsFn = makeAttrsFunction(anatomy, displayName, element);
    const styleFn = createStyledAnatomyFunction(anatomy, displayName, element);
    styleFn.attrs = attrsFn;
    return Object.assign({}, styleObj, {
      [element]: styleFn
    });
  });
  styledFn = Object.assign(styledFn, styledObjProps);
  return styledFn;
};

const getPropsFromStyledComponents = reduceCallback => Object.keys(styled).reduce(reduceCallback, {});
/**
 * Factory method that returns an attribute function
 * @param {*} anatomy
 * @param {*} displayName
 * @param {*} element
 */


const makeAttrsFunction = (anatomy, displayName, element) => attrs => createStyledAnatomyFunction(anatomy, displayName, element, attrs);

const createStyledAnatomyFunction = (anatomy, displayName, element, attrs = null) => (...styles) => {
  const Component = (styled[element] || styled(element)).attrs(attrs)`
		${composeStyles(...anatomy.rules)};
		${css( // @ts-ignore
  ...styles)};
	`;
  Component.propTypes = anatomy.propTypes;
  Component.displayName = displayName;
  return Component;
};
/**
 * Takes in CSS Rules as arguments and returns a single CSS Rule
 * @param  {...[TemplateStringsArray]} styleRules
 * @returns {*} Interpolated CSS
 */


const composeStyles = (...styleRules) => styleRules.reduce((style, rule) => css`
			${style}
			${css(rule)}
		`, css``);
/**
 * Used to combine multiple anatomies into one.  The result can be used just like styledAnatomy
 *
 * @param  {...Anatomy} anatomies
 * @returns {Anatomy} styledObject
 */

const mergeAnatomies = (...anatomies) => {
  return anatomies.reduce((newAnatomy, Anatomy) => ({
    rules: [...newAnatomy.rules, ...Anatomy.rules],
    propTypes: { ...newAnatomy.propTypes,
      ...Anatomy.propTypes
    }
  }));
};
/**
 * @todo needs testing
 * A utility function that gets the affected prop from the theme.  If `affectedProp` is not
 * on the theme, it will use the fallback if provided or the default on the theme object
 *
 * @param {String} affectedProp
 * @param {Object} themeObject
 * @param {String | Number} [fallback] - a default value
 */

const getValueFromTheme = (affectedProp, themeObject, fallback) => {
  // accept non-truthy values
  if (themeObject[affectedProp] || themeObject[affectedProp] === 0 || themeObject[affectedProp] === false) {
    return themeObject[affectedProp];
  }

  return fallback || themeObject.default;
};
/**
 * convenience method for adding a special 'data-slate-*' attribute to a component.
 * mainly used for test automation or component specific selection for exposing it to custom styles
 *
 * @param {string} dataAttrValue the name or value for this component
 * @param {string} [dataAttrPrefix = 'component'] the prefix for the data attribute
 * @returns {object} object containing attributes for the component
 */

const attachDataAttr = (dataAttrValue, dataAttrPrefix = 'component') => (attrs = {}) => ({
  [`data-slate-${dataAttrPrefix}`]: dataAttrValue,
  ...attrs
});
/**
 * @todo test this
 * used to turn something camelcase into kebabcase
 * usually used to make a camelCase prop into a valid css prop
 * @param {string} camelCase a camelcase string
 * @returns {string} a kebabcased string
 */

const kebabCase = camelCase => camelCase.replace(/([A-Z])/g, match => ` ${match}`).split(' ').join('-').toLowerCase();
/**
 * @todo needs testing
 * Takes any array of strings and returns an object with the values as keys and values
 *
 * @param {String[]} enums
 * @param {*} [defaultValue] set a default value for the enums, or simply set to the enum
 * @returns {Object} the enums in JSON format
 */

const enumsToJSON = (enums, defaultValue) => enums.reduce((map, val) => ({ ...map,
  [val]: defaultValue || val
}), {});
/**
 * @todo deperecate this function
 * Creates a propMap for a certain component property
 * this is used to help autogenerate tests and knobs
 *
 * @param {String} propName a component prop
 * @param {('enum'|'string'|'number'|'object'|'array'|'boolean')} propType the type of the component
 * @param {Object} defaults an object with keys that are options for a specific prop and their values
 * @param {Object} cssPropGetters an object with keys that correspond to css properties and values are a callback function that receives a prop value and returns a css value
 * @returns {Object} propMap
 */

const createPropMap = (propName, propType, defaults, cssPropGetters) => {
  const values = Object.keys(defaults);
  const knobType = determineKnobType(propType);
  const knobArgs = [propName, ...determineKnobArgs(knobType, values)];
  return {
    [propName]: {
      defaults,
      propType,
      values,
      knobType,
      knobArgs,
      cssProps: cssPropGetters
    }
  };
};
/**
 *
 * @param {Object} propTypes the propTypes for a component
 * @returns {PropMapper}
 */

const createPropMap2 = propTypes => mapperFn => {
  try {
    return Object.keys(propTypes).reduce((propMap, prop) => {
      const {
        propType,
        expectedValueMap,
        cssPropGetters
      } = mapperFn(prop);
      const values = Object.keys(expectedValueMap);
      const knobType = determineKnobType(propType);
      const knobArgs = [prop, ...determineKnobArgs(knobType, values)];
      return { ...propMap,
        [prop]: {
          propType,
          values,
          knobType,
          knobArgs,
          defaults: expectedValueMap,
          cssProps: cssPropGetters
        }
      };
    }, {});
  } catch (e) {
    console.error(e);
  }
};
const mergePropMaps = (...propMaps) => propMaps.reduce((newPropMap, propMap) => ({ ...newPropMap,
  ...propMap
}));
/**
 * @callback ResponsiveStyleFunction function called to apply styles to a media query
 * @param {*} val the current value being iterated on
 * @returns {*} the css for that breakpoint
 *
 * Generates responsive breakpoints for multiple values passed to a prop.  This works in a mobile-first fashion.  if more propvalues are provided than there are breakpoints, then the extra values will be ignored
 * @param {Number[]} breakpoints sorted array of breakpoints with the smallest pixel value first
 * @param {*} propVal a singular value or an array of values that should be reduced into a series of media queries.  If an array, they should be sorted in the same way, with the value for the smallest breakpoint first
 * @param {ResponsiveStyleFunction} styleFn a callback for each value given to propVal
 * @returns {*} the media queries for responsive styles
 */

const makeResponsiveStyles = (breakpoints, propVal, styleFn) => {
  if (!Array.isArray(propVal) || Array.isArray(propVal) && propVal.length === 1) return styleFn(propVal); // copy the array first so we don't mutate

  const copy = [...propVal];
  const mobileFirst = copy.shift();
  const styles = copy.reduce((responsive, val, index) => breakpoints[++index] ? css`
						${responsive};
						@media (min-width: ${breakpoints[index]}px) {
							${styleFn(val)};
						}
				  ` : responsive, css`
			${styleFn(mobileFirst)};
		`);
  styles.fallback = withFallbackStyles(styles);
  return styles;
};
/**
 * Callback that is called to determine if fallback styles should be applied
 * @callback FallbackApplier
 * @param {FallbackValidator} when determines if the fallback should be applied
 * @returns {FallbackApplierObject}
 *
 * @typedef FallbackApplierObject
 * @property {FallbackApplierFunction} apply the function that will apply the modified styles or the original styles
 *
 * @callback FallbackApplierFunction
 * @param {StyleFunction} styleFn
 * @returns {*} the styled component or an updated styled component
 *
 * @callback StyleFunction
 * @param {*} styles the original styles to be modified
 * @param {Boolean} [applyFallback] the original styles to be modified. passed for convenience. not required.
 * @returns {*} an updated styled component
 *
 * @callback FallbackValidator
 * @returns {Boolean} whether or not the fallback styles should be applied
 *
 * @param {*} styles the original styles that may need a fallback
 * @returns {FallbackApplier}
 */

const withFallbackStyles = styles => when => {
  try {
    if (!when && typeof when !== 'function') {
      throw Error("Make sure you pass the 'when' function for your fallbacks");
    }

    const applyFallback = when();
    return {
      apply: styleFn => applyFallback ? css(styleFn(styles, applyFallback)) : css(styles)
    };
  } catch (e) {
    console.error(e);
  }
};
const composeStyleRules = (...rules) => props => rules.reduce((rule, ruleFn) => css`
			${rule};
			${ruleFn(props)};
		`, '');

const determineKnobType = propType => {
  const propTypeMap = {
    enum: 'select',
    string: 'text',
    boolean: 'boolean',
    number: 'number',
    object: 'object',
    array: 'array'
  };
  return propTypeMap[propType] || 'text';
};
/**
 * Addon-knobs have different parameters based on the knob type
 *
 * @param {*} knobType
 * @param {*} value
 */


const determineKnobArgs = (knobType, value) => {
  if (knobType === 'select') {
    return [['', ...value], ''];
  }

  if (knobType === 'number') {
    return [value, {}];
  }

  if (knobType === 'array') {
    return [value, ','];
  }

  return [value];
};

/**
 * Constants
 */

const COLOR_ENUMS = Object.keys(color);
/**
 * PropTypes
 */

const propTypes$d = {
  /** Sets the color of the text */
  textColor: PropTypes.oneOfType(COLOR_ENUMS),

  /** Sets the color of visited text */
  visitedTextColor: PropTypes.oneOfType(COLOR_ENUMS),

  /** Sets the color of the border */
  borderColor: PropTypes.oneOfType(COLOR_ENUMS),

  /** Sets the color of the background */
  backgroundColor: PropTypes.oneOfType(COLOR_ENUMS),

  /** Sets the color of the background gradient */
  backgroundGradientColor: PropTypes.string
};
/**
 * CSS rules
 */

const textColorRules = props => props.textColor && css`
		color: ${getValueFromTheme(props.textColor, props.theme.colors, props.textColor)};
	`;
const visitedTextColorRules = props => props.visitedTextColor && css`
		&:visited {
			color: ${getValueFromTheme(props.visitedTextColor, props.theme.colors)};
		}
	`;
const borderColorRules = props => props.borderColor && makeResponsiveStyles(props.theme.breakpoints, props.borderColor, val => css`
			border-color: ${getValueFromTheme(val, props.theme.colors, val)};
		`);
const backgroundColorRules = props => props.backgroundColor && css`
		background-color: ${getValueFromTheme(props.backgroundColor, props.theme.colors, props.backgroundColor)};
	`;
const backgroundGradientRules = props => props.backgroundGradientColor && css`
		background-image: linear-gradient(${props.backgroundGradientColor});
	`;
const allColorRules = [textColorRules, visitedTextColorRules, borderColorRules, backgroundColorRules, backgroundGradientRules];
/**
 * Maps for testing and storybook
 */

const mapperFn$6 = prop => {
  const isGradient = prop === 'backgroundGradientColor';
  const isColor = prop === 'textColor';
  const isVisitedText = prop === 'visitedTextColor';
  const cssProp = isGradient ? 'background-image' : isColor ? 'color' : isVisitedText ? 'color' : kebabCase(prop);
  const propType = isGradient ? 'string' : 'enum';
  const expectedValueMap = isGradient ? {
    'to right,rgba(255,0,0,0),rgba(255,0,0,1)': 'to right,rgba(255,0,0,0),rgba(255,0,0,1)'
  } : color;
  return {
    propType,
    expectedValueMap,
    cssPropGetters: {
      [cssProp]: propVal => {
        return isGradient ? 'linear-gradient(to right,rgba(255,0,0,0),rgba(255,0,0,1))' : color[propVal];
      }
    }
  };
};

const map$g = createPropMap2(propTypes$d)(mapperFn$6);
/**
 * the ColorAnatomy
 */

const anatomy$f = makeAnatomy(allColorRules, propTypes$d);
/**
 * The default export - the styled anatomy
 */

var index$h = styledAnatomyFactory(anatomy$f);

/**
 * Constants
 */

const SPACING_ENUMS = Object.keys(spacing);
/**
 * PropTypes
 */

const propTypes$c = {
  /** not sure if we want to control gutter at this level */
  // gutter: PropTypes.oneOf(SPACING_ENUMS),
  // gutterLeft: PropTypes.oneOf(SPACING_ENUMS),
  // gutterRight: PropTypes.oneOf(SPACING_ENUMS),
  // gutterTop: PropTypes.oneOf(SPACING_ENUMS),
  // gutterBottom: PropTypes.oneOf(SPACING_ENUMS),

  /** Sets the padding properties */
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  paddingLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  paddingRight: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  paddingTop: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  paddingBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),

  /** Sets the margin properties */
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  marginTop: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))]),
  marginBottom: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(SPACING_ENUMS)]))])
};
/**
 * CSS Rules
 */

const paddingRules = props => props.padding && makeResponsiveStyles(props.theme.breakpoints, props.padding, val => css`
			padding: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const paddingLeftRules = props => props.paddingLeft && makeResponsiveStyles(props.theme.breakpoints, props.paddingLeft, val => css`
			padding-left: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const paddingRightRules = props => props.paddingRight && makeResponsiveStyles(props.theme.breakpoints, props.paddingRight, val => css`
			padding-right: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const paddingTopRules = props => props.paddingTop && makeResponsiveStyles(props.theme.breakpoints, props.paddingTop, val => css`
			padding-top: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const paddingBottomRules = props => props.paddingBottom && makeResponsiveStyles(props.theme.breakpoints, props.paddingBottom, val => css`
			padding-bottom: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const marginRules = props => props.margin && makeResponsiveStyles(props.theme.breakpoints, props.margin, val => css`
			margin: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const marginLeftRules = props => props.marginLeft && makeResponsiveStyles(props.theme.breakpoints, props.marginLeft, val => css`
			margin-left: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const marginRightRules = props => props.marginRight && makeResponsiveStyles(props.theme.breakpoints, props.marginRight, val => css`
			margin-right: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const marginBottomRules = props => props.marginBottom && makeResponsiveStyles(props.theme.breakpoints, props.marginBottom, val => css`
			margin-bottom: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const marginTopRules = props => props.marginTop && makeResponsiveStyles(props.theme.breakpoints, props.marginTop, val => css`
			margin-top: ${getValueFromTheme(val, props.theme.spacing, val)};
		`);
const allPaddingRules = [paddingRules, paddingLeftRules, paddingRightRules, paddingBottomRules, paddingTopRules];
const allMarginRules = [marginRules, marginLeftRules, marginRightRules, marginBottomRules, marginTopRules];
const allSpacingRules = [...allPaddingRules, ...allMarginRules];
/**
 * Maps for testing and storybook
 */

const map$f = Object.keys(propTypes$c).reduce((propMap, prop) => {
  const cssProp = kebabCase(prop);
  return { ...propMap,
    ...createPropMap(prop, 'enum', spacing, {
      [cssProp]: propVal => {
        return spacing[propVal];
      }
    })
  };
}, {});
const anatomy$e = makeAnatomy(allSpacingRules, propTypes$c);
/**
 * The default export - the anatomy
 */

var index$g = styledAnatomyFactory(anatomy$e);

/**
 * Constants
 */

const POSITION_ENUMS = ['relative', 'fixed', 'absolute', 'sticky', 'static'];
const positionDefaults = enumsToJSON(POSITION_ENUMS);
const positionEnumProps = {
  position: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf(POSITION_ENUMS)])
};
const positionCustomProps = {
  positionTop: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  positionBottom: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  positionLeft: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  positionRight: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
};
/**
 * PropTypes
 */

const propTypes$b = { ...positionEnumProps,
  ...positionCustomProps
};
/**
 * CSS rules
 */

const positionRules = props => props.position && makeResponsiveStyles(props.theme.breakpoints, props.position, val => css`
			position: ${val};
		`);
const positionTopRules = props => props.positionTop && makeResponsiveStyles(props.theme.breakpoints, props.positionTop, val => css`
			top: ${val};
		`);
const positionBottomRules = props => props.positionBottom && makeResponsiveStyles(props.theme.breakpoints, props.positionBottom, val => css`
			bottom: ${val};
		`);
const positionLeftRules = props => props.positionLeft && makeResponsiveStyles(props.theme.breakpoints, props.positionLeft, val => css`
			left: ${val};
		`);
const positionRightRules = props => props.positionRight && makeResponsiveStyles(props.theme.breakpoints, props.positionRight, val => css`
			right: ${val};
		`);
const allPositionRules = [positionRules, positionTopRules, positionBottomRules, positionLeftRules, positionRightRules];
/**
 * Maps for testing and storybook
 */

const positionMap = createPropMap('position', 'enum', positionDefaults, {
  position: propVal => propVal
});
const positionCustomMap = Object.keys(positionCustomProps).reduce((propMap, prop) => {
  const cssProp = prop.replace('position', '').toLowerCase();
  return { ...propMap,
    ...createPropMap(prop, 'string', {
      '1px': '1px'
    }, {
      [cssProp]: propVal => propVal
    })
  };
}, {});
const map$e = { ...positionMap,
  ...positionCustomMap
};
const anatomy$d = makeAnatomy(allPositionRules, propTypes$b);
/**
 * The default export - the anatomy
 */

var index$f = styledAnatomyFactory(anatomy$d);

/**
 * Constants
 */

const TYPO_HEIGHT_ENUMS = Object.keys(typography.lineHeight);
const TYPO_WEIGHT_ENUMS = Object.keys(typography.fontWeight);
const TYPO_SIZE_ENUMS = Object.keys(typography.fontSize);
/**
 * PropTypes
 */

const propTypes$a = {
  typoHeight: PropTypes.oneOfType([PropTypes.oneOf(TYPO_HEIGHT_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(TYPO_HEIGHT_ENUMS))]),
  typoWeight: PropTypes.oneOfType([PropTypes.oneOf(TYPO_WEIGHT_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(TYPO_WEIGHT_ENUMS))]),
  typoSize: PropTypes.oneOfType([PropTypes.oneOf(TYPO_SIZE_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(TYPO_SIZE_ENUMS))])
};
/**
 * CSS rules
 */

const typoHeightRules = props => props.typoHeight && makeResponsiveStyles(props.theme.breakpoints, props.typoHeight, val => css`
			line-height: ${getValueFromTheme(val, props.theme.typography.lineHeight, val)};
		`);
const typoWeightRules = props => props.typoWeight && makeResponsiveStyles(props.theme.breakpoints, props.typoWeight, val => css`
			font-weight: ${getValueFromTheme(val, props.theme.typography.fontWeight, val)};
		`);
const typoSizeRules = props => props.typoSize && makeResponsiveStyles(props.theme.breakpoints, props.typoSize, val => css`
			font-size: ${getValueFromTheme(val, props.theme.typography.fontSize, val)};
		`);
const allTypographyRules = [typoHeightRules, typoWeightRules, typoSizeRules];
/**
 * Maps for testing and storybook
 */

const typoHeightMap = createPropMap('typoHeight', 'enum', typography.lineHeight, {
  'line-height': propVal => typography.lineHeight[propVal]
});
const typoWeightMap = createPropMap('typoWeight', 'enum', typography.fontWeight, {
  'font-weight': propVal => typography.fontWeight[propVal]
});
const typoSizeMap = createPropMap('typoSize', 'enum', typography.fontSize, {
  'font-size': propVal => typography.fontSize[propVal]
});
const map$d = { ...typoHeightMap,
  ...typoWeightMap,
  ...typoSizeMap
};
const anatomy$c = makeAnatomy(allTypographyRules, propTypes$a);
/**
 * The default export - the anatomy
 */

var index$e = styledAnatomyFactory(anatomy$c);

// @ts-nocheck
/**
 * Constants
 */

const DISPLAY_ENUMS = ['inline', 'block', 'contents', 'flex', // 'grid',
'inline-flex', 'inline-block', 'inline-flex', // 'inline-grid',
'inline-table', 'list-item', 'run-in', 'table', 'table-caption', 'table-column-group', 'table-header-group', 'table-footer-group', 'table-row-group', 'table-cell', 'table-column', 'table-row', 'none', 'initial', 'inherit'];
const displayDefaults = enumsToJSON(DISPLAY_ENUMS);
/**
 * PropTypes
 */

const propTypes$9 = {
  display: PropTypes.oneOfType([PropTypes.oneOf(DISPLAY_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(DISPLAY_ENUMS))])
};
/**
 * CSS Rules
 */

const displayRules = props => props.display && makeResponsiveStyles(props.theme.breakpoints, props.display, val => css`
			display: ${val};
		`);
const allDisplayRules = [displayRules];
/**
 * maps for Jest and Storybook
 */

const map$c = createPropMap('display', 'enum', displayDefaults, {
  display: propVal => propVal
});
const anatomy$b = makeAnatomy(allDisplayRules, propTypes$9);
/**
 * The actual anatomy
 */

var index$d = styledAnatomyFactory(anatomy$b);

// @ts-nocheck
/**
 * Constants
 */

const FLEX_WRAP_ENUMS = ['nowrap', 'wrap', 'wrap-reverse', 'initial', 'inherit'];
const FLEX_DIRECTION_ENUMS = ['row', 'row-reverse', 'column', 'column-reverse', 'initial', 'inherit'];
const ALIGN_CONTENT_ENUMS = ['stretch', 'center', 'flex-start', 'flex-end', 'space-between', 'space-around', 'initial', 'inherit'];
const ALIGN_ITEMS_ENUMS = ['stretch', 'center', 'flex-start', 'flex-end', 'baseline', 'initial', 'inherit'];
const ALIGN_SELF_ENUMS = ['auto', 'stretch', 'center', 'flex-start', 'flex-end', 'baseline', 'initial', 'inherit'];
const JUSTIFY_CONTENT_ENUMS = ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'initial', 'inherit'];
const defaults$3 = {
  flexWrap: enumsToJSON(FLEX_WRAP_ENUMS),
  flexDirection: enumsToJSON(FLEX_DIRECTION_ENUMS),
  alignContent: enumsToJSON(ALIGN_CONTENT_ENUMS),
  alignItems: enumsToJSON(ALIGN_ITEMS_ENUMS),
  alignSelf: enumsToJSON(ALIGN_SELF_ENUMS),
  justifyContent: enumsToJSON(JUSTIFY_CONTENT_ENUMS),
  flex: {
    '1 1': '1 1'
  },
  flexBasis: {
    '1': '1'
  },
  flexFlow: {
    'row nowrap': 'row nowrap'
  },
  flexGrow: {
    '1': '1'
  },
  flexShrink: {
    '0': '0'
  },
  flexOrder: {
    '0': '0'
  }
};
/**
 * PropTypes
 */

const flexStringProps = {
  flex: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  flexBasis: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  flexFlow: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  flexGrow: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  flexShrink: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
};
const flexEnumProps = {
  flexWrap: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(FLEX_WRAP_ENUMS)), PropTypes.oneOf(FLEX_WRAP_ENUMS)]),
  flexDirection: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(FLEX_DIRECTION_ENUMS)), PropTypes.oneOf(FLEX_DIRECTION_ENUMS)]),
  alignContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(ALIGN_CONTENT_ENUMS)), PropTypes.oneOf(ALIGN_CONTENT_ENUMS)]),
  alignItems: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(ALIGN_ITEMS_ENUMS)), PropTypes.oneOf(ALIGN_ITEMS_ENUMS)]),
  alignSelf: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(ALIGN_SELF_ENUMS)), PropTypes.oneOf(ALIGN_SELF_ENUMS)]),
  justifyContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOf(JUSTIFY_CONTENT_ENUMS)), PropTypes.oneOf(JUSTIFY_CONTENT_ENUMS)])
};
const propTypes$8 = { ...flexStringProps,
  ...flexEnumProps,
  flexOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};
/**
 * CSS Rules
 */

const flexRules = props => props.flex && makeResponsiveStyles(props.theme.breakpoints, props.flex, val => css`
			flex: ${val};
		`);
const flexBasisRules = props => props.flexBasis && makeResponsiveStyles(props.theme.breakpoints, props.flexBasis, val => css`
			flex-basis: ${val};
		`);
const flexFlowRules = props => props.flexFlow && makeResponsiveStyles(props.theme.breakpoints, props.flexFlow, val => css`
			flex-flow: ${val};
		`);
const flexGrowRules = props => props.flexGrow && makeResponsiveStyles(props.theme.breakpoints, props.flexGrow, val => css`
			flex-grow: ${val};
		`);
const flexShrinkRules = props => props.flexShrink && makeResponsiveStyles(props.theme.breakpoints, props.flexShrink, val => css`
			flex-shrink: ${val};
		`);
const flexWrapRules = props => props.flexWrap && makeResponsiveStyles(props.theme.breakpoints, props.flexWrap, val => css`
			flex-wrap: ${val};
		`);
const flexDirectionRules = props => props.flexDirection && makeResponsiveStyles(props.theme.breakpoints, props.flexDirection, val => css`
			flex-direction: ${val};
		`);
const alignContentRules = props => props.alignContent && makeResponsiveStyles(props.theme.breakpoints, props.alignContent, val => css`
			align-content: ${val};
		`);
const alignItemsRules = props => props.alignItems && makeResponsiveStyles(props.theme.breakpoints, props.alignItems, val => css`
			align-items: ${val};
		`);
const alignSelfRules = props => props.alignSelf && makeResponsiveStyles(props.theme.breakpoints, props.alignSelf, val => css`
			align-self: ${val};
		`);
const justifyContentRules = props => props.justifyContent && makeResponsiveStyles(props.theme.breakpoints, props.justifyContent, val => css`
			justify-content: ${val};
		`);
const flexOrderRules = props => props.flexOrder && makeResponsiveStyles(props.theme.breakpoints, props.flexOrder, val => css`
			order: ${val};
		`);
const allFlexRules = [flexRules, flexBasisRules, flexFlowRules, flexGrowRules, flexShrinkRules, flexWrapRules, flexDirectionRules, alignContentRules, alignItemsRules, alignSelfRules, justifyContentRules, flexOrderRules];
/**
 * maps for Jest and Storybook
 */

const mapperFunction$2 = prop => {
  const toFormat = prop === 'flexOrder' ? 'order' : prop;
  const cssProp = kebabCase(toFormat);
  return {
    propType: Object.keys(flexEnumProps).includes(prop) ? 'enum' : 'string',
    expectedValueMap: defaults$3[prop],
    cssPropGetters: {
      [cssProp]: propVal => defaults$3[prop][propVal]
    }
  };
};
const map$b = createPropMap2(propTypes$8)(mapperFunction$2);
const anatomy$a = makeAnatomy(allFlexRules, propTypes$8);
/**
 * The actual anatomy
 */

var index$c = styledAnatomyFactory(anatomy$a);

// @ts-nocheck
/**
 * PropTypes
 */

const propTypes$7 = {
  elWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  elHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  elMinWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  elMinHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  elMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  elMaxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};
/**
 * CSS Rules
 */

const widthRules = props => props.elWidth && makeResponsiveStyles(props.theme.breakpoints, props.elWidth, val => css`
				width: ${val};
			`);
const heightRules = props => props.elHeight && makeResponsiveStyles(props.theme.breakpoints, props.elHeight, val => css`
				height: ${val};
			`);
const minWidthRules = props => props.elMinWidth && makeResponsiveStyles(props.theme.breakpoints, props.elMinWidth, val => css`
				min-width: ${val};
			`);
const minHeightRules = props => props.elMinHeight && makeResponsiveStyles(props.theme.breakpoints, props.elMinHeight, val => css`
				min-height: ${val};
			`);
const maxWidthRules = props => props.elMaxWidth && makeResponsiveStyles(props.theme.breakpoints, props.elMaxWidth, val => css`
				max-width: ${val};
			`);
const maxHeightRules = props => props.elMaxHeight && makeResponsiveStyles(props.theme.breakpoints, props.elMaxHeight, val => css`
				max-height: ${val};
			`);
const allDimensionsRules = [widthRules, heightRules, minWidthRules, minHeightRules, maxWidthRules, maxHeightRules];
const mapperFunction$1 = prop => {
  const cssProp = prop.replace('el', '').replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  return {
    propType: 'strings',
    expectedValueMap: {
      '100px': '100px'
    },
    cssPropGetters: {
      [cssProp]: () => '100px'
    }
  };
};
/**
 * maps for Jest and Storybook
 */

const map$a = createPropMap2(propTypes$7)(mapperFunction$1);
const anatomy$9 = makeAnatomy(allDimensionsRules, propTypes$7);
/**
 * The actual anatomy
 */

var index$b = styledAnatomyFactory(anatomy$9);

// @ts-nocheck
/**
 * Constants
 */

const BORDER_STYLE_ENUMS = ['none', 'hidden', 'dotted', 'dashed', 'solid', 'double', 'groove', 'ridge', 'inset', 'outset', 'initial', 'inherit'];
const BORDER_OVERRIDES_ENUMS = ['none', 'default'];
const defaults$2 = {
  border: {
    default: '1px solid',
    '1px solid': '1px solid'
  },
  borderStyle: enumsToJSON(BORDER_STYLE_ENUMS),
  borderWidth: {
    '1px': '1px'
  },
  borderTop: {
    none: '0',
    default: '1px solid'
  },
  borderBottom: {
    none: '0',
    default: '1px solid'
  },
  borderLeft: {
    none: '0',
    default: '1px solid'
  },
  borderRight: {
    none: '0',
    default: '1px solid'
  },
  borderRadius: {
    none: '0',
    default: '16px'
  },
  borderTopRightRadius: {
    none: '0',
    default: '16px'
  },
  borderTopLeftRadius: {
    none: '0',
    default: '16px'
  },
  borderBottomRightRadius: {
    none: '0',
    default: '16px'
  },
  borderBottomLeftRadius: {
    none: '0',
    default: '16px'
  }
};
/**
 * PropTypes
 */

const propTypes$6 = {
  /** shorthand for css border property */
  border: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  borderWidth: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  borderStyle: PropTypes.oneOfType([PropTypes.oneOf(BORDER_STYLE_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_STYLE_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderRadius: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderTop: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderBottom: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderLeft: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderRight: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderTopRightRadius: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderTopLeftRadius: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderBottomRightRadius: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))]),

  /** passing 'none' will override the theme default radius with 0 */
  borderBottomLeftRadius: PropTypes.oneOfType([PropTypes.oneOf(BORDER_OVERRIDES_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(BORDER_OVERRIDES_ENUMS))])
};
/**
 * CSS Rules
 */

/**
 * @todo specify default borders based on theme
 * @param {*} props props from component
 */

const borderRules = props => props.border && makeResponsiveStyles(props.theme.breakpoints, props.border, val => css`
				border: ${getValueFromTheme(val, props.theme.borders, val)};
				/* border-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)}; */
			`);
const borderWidthRules = props => props.borderWidth && makeResponsiveStyles(props.theme.breakpoints, props.borderWidth, val => css`
				border-width: ${val};
			`);
const borderStyleRules = props => props.borderStyle && makeResponsiveStyles(props.theme.breakpoints, props.borderStyle, val => css`
				border-style: ${val};
			`);
const borderRadiusRules = props => props.borderRadius && makeResponsiveStyles(props.theme.breakpoints, props.borderRadius, val => css`
			border-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)};
		`);
const borderTopRules = props => props.borderTop && makeResponsiveStyles(props.theme.breakpoints, props.borderTop, val => css`
			border-top: ${getValueFromTheme(val, props.theme.borders, val)};
		`);
const borderBottomRules = props => props.borderBottom && makeResponsiveStyles(props.theme.breakpoints, props.borderBottom, val => css`
			border-bottom: ${getValueFromTheme(val, props.theme.borders, val)};
		`);
const borderLeftRules = props => props.borderLeft && makeResponsiveStyles(props.theme.breakpoints, props.borderLeft, val => css`
			border-left: ${getValueFromTheme(val, props.theme.borders, val)};
		`);
const borderRightRules = props => props.borderRight && makeResponsiveStyles(props.theme.breakpoints, props.borderRight, val => css`
			border-right: ${getValueFromTheme(val, props.theme.borders, val)};
		`);
/** passing 'none' will override the theme default radius with 0 */

const borderTopRightRadiusRules = props => props.borderTopRightRadius && makeResponsiveStyles(props.theme.breakpoints, props.borderTopRightRadius, val => css`
			border-top-right-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)};
		`);
/** passing 'none' will override the theme default radius with 0 */

const borderTopLeftRadiusRules = props => props.borderTopLeftRadius && makeResponsiveStyles(props.theme.breakpoints, props.borderTopLeftRadius, val => css`
			border-top-left-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)};
		`);
/** passing 'none' will override the theme default radius with 0 */

const borderBottomRightRadiusRules = props => props.borderBottomRightRadius && makeResponsiveStyles(props.theme.breakpoints, props.borderBottomRightRadius, val => css`
			border-bottom-right-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)};
		`);
/** passing 'none' will override the theme default radius with 0 */

const borderBottomLeftRadiusRules = props => props.borderBottomLeftRadius && makeResponsiveStyles(props.theme.breakpoints, props.borderBottomLeftRadius, val => css`
			border-bottom-left-radius: ${getValueFromTheme(val, props.theme.borders.radius, val)};
		`);
/**
 * maps for Jest and Storybook
 */

const mapperFunction = prop => {
  const isStringProp = prop === 'border' || prop === 'borderWidth';
  const cssProp = kebabCase(prop).toLowerCase();
  const expectedValueMap = defaults$2[prop];
  return {
    expectedValueMap,
    propType: isStringProp ? 'string' : 'enum',
    cssPropGetters: {
      [cssProp]: propVal => expectedValueMap[propVal]
    }
  };
};

const map$9 = createPropMap2(propTypes$6)(mapperFunction);
const allBorderRules = [borderRules, borderWidthRules, borderStyleRules, borderRadiusRules, borderTopRules, borderBottomRules, borderLeftRules, borderRightRules, borderTopRightRadiusRules, borderTopLeftRadiusRules, borderBottomRightRadiusRules, borderBottomLeftRadiusRules];
/**
 * The actual anatomy
 */

const anatomy$8 = makeAnatomy(allBorderRules, propTypes$6);
/**
 * The styled anatomy
 */

var index$a = styledAnatomyFactory(anatomy$8);

// @ts-nocheck
/**
 * Constants
 */

const SHADOW_ENUMS = Object.keys(boxShadows);
/**
 * PropTypes
 */

const propTypes$5 = {
  shadow: PropTypes.oneOfType([PropTypes.oneOf(SHADOW_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(SHADOW_ENUMS))])
};
/**
 * CSS Rules
 */

const boxShadowRules = props => props.shadow && makeResponsiveStyles(props.theme.breakpoints, props.shadow, val => css`
			box-shadow: ${getValueFromTheme(val, props.theme.boxShadows, val)};
		`);
/**
 * maps for Jest and Storybook
 */

const mapperFn$5 = prop => {
  const expectedValueMap = boxShadows;
  return {
    expectedValueMap,
    propType: 'enum',
    cssPropGetters: {
      'box-shadow': propVal => expectedValueMap[propVal]
    }
  };
};

const map$8 = createPropMap2(propTypes$5)(mapperFn$5);
const allShadowsRules = [boxShadowRules];
/**
 * The actual anatomy
 */

const anatomy$7 = makeAnatomy(allShadowsRules, propTypes$5);
/**
 * The styled anatomy
 */

var index$9 = styledAnatomyFactory(anatomy$7);

// @ts-nocheck
/**
 * Constants
 */

const OVERFLOW_ENUMS = ['visible', 'hidden', 'scroll', 'auto'];
/**
 * PropTypes
 */

const propTypes$4 = {
  overflow: PropTypes.oneOfType([PropTypes.oneOf(OVERFLOW_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(OVERFLOW_ENUMS))]),
  overflowX: PropTypes.oneOfType([PropTypes.oneOf(OVERFLOW_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(OVERFLOW_ENUMS))]),
  overflowY: PropTypes.oneOfType([PropTypes.oneOf(OVERFLOW_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(OVERFLOW_ENUMS))])
};
/**
 * CSS Rules
 */

const overflow = ({
  theme,
  overflow
}) => overflow && makeResponsiveStyles(theme.breakpoints, overflow, val => css`
			overflow: ${val};
		`);

const overflowX = ({
  theme,
  overflowX
}) => overflowX && makeResponsiveStyles(theme.breakpoints, overflowX, val => css`
			overflow-x: ${val};
		`);

const overflowY = ({
  theme,
  overflowY
}) => overflowY && makeResponsiveStyles(theme.breakpoints, overflowY, val => css`
			overflow-y: ${val};
		`);

const allOverflowRules = [overflow, overflowY, overflowX];
/**
 * maps for Jest and Storybook
 */

const mapperFn$4 = prop => {
  const propType = 'enum';
  const cssProp = kebabCase(prop).toLowerCase();
  const expectedValueMap = enumsToJSON(OVERFLOW_ENUMS);
  return {
    expectedValueMap,
    propType,
    cssPropGetters: {
      [cssProp]: propVal => expectedValueMap[propVal]
    }
  };
};

const map$7 = createPropMap2(propTypes$4)(mapperFn$4);
/**
 * The actual anatomy
 */

const anatomy$6 = makeAnatomy(allOverflowRules, propTypes$4);
/**
 * The styled anatomy
 */

var index$8 = styledAnatomyFactory(anatomy$6);

// @ts-nocheck
/**
 * Constants
 */

const TEXT_ALIGN_ENUMS = ['left', 'right', 'center'];
const TEXT_DECORAION_ENUMS = ['none', 'underline', 'overline', 'line-through'];
const TEXT_TRANSFORM_ENUMS = ['uppercase', 'capitalize', 'lowercase', 'none', 'full-width', 'full-size-kana'];
/**
 * PropTypes
 *
 * @note it probably seems dumb to see oneOf type string or enum (it could just be string)
 * but I wanted to write it this way in case it helps with type checking
 */

const propTypes$3 = {
  textAlign: PropTypes.oneOfType([PropTypes.oneOf(TEXT_ALIGN_ENUMS), PropTypes.arrayOf(PropTypes.oneOf(TEXT_ALIGN_ENUMS))]),
  textDecoration: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(TEXT_DECORAION_ENUMS), PropTypes.string]), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(TEXT_DECORAION_ENUMS), PropTypes.string]))]),
  textTransform: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(TEXT_TRANSFORM_ENUMS), PropTypes.string]), PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(TEXT_TRANSFORM_ENUMS), PropTypes.string]))])
};
/**
 * CSS Rules
 */

const textAlignRules = ({
  theme,
  textAlign
}) => textAlign && makeResponsiveStyles(theme.breakpoints, textAlign, val => css`
			text-align: ${val};
		`);

const textDecorationRules = ({
  theme,
  textDecoration
}) => textDecoration && makeResponsiveStyles(theme.breakpoints, textDecoration, val => css`
			text-decoration: ${val};
		`);

const textTransformRules = ({
  theme,
  textTransform
}) => textTransform && makeResponsiveStyles(theme.breakpoints, textTransform, val => css`
			text-transform: ${val};
		`);
/**
 * maps for Jest and Storybook
 */


const testVals$1 = {
  textAlign: enumsToJSON(TEXT_ALIGN_ENUMS),
  textTransform: enumsToJSON(TEXT_TRANSFORM_ENUMS),
  textDecoration: { ...enumsToJSON(TEXT_DECORAION_ENUMS),
    'underline overline wavy': 'underline overline wavy'
  }
};

const mapperFn$3 = prop => {
  const cssProp = kebabCase(prop);
  return {
    propType: 'enum',
    expectedValueMap: testVals$1[prop],
    cssPropGetters: {
      [cssProp]: propVal => testVals$1[prop][propVal]
    }
  };
};

const map$6 = createPropMap2(propTypes$3)(mapperFn$3);
const allTextFormatRules = [textAlignRules, textDecorationRules, textTransformRules];
/**
 * The actual anatomy
 */

const anatomy$5 = makeAnatomy(allTextFormatRules, propTypes$3);
/**
 * The styled anatomy
 */

var index$7 = styledAnatomyFactory(anatomy$5);

// @ts-nocheck
/**
 * Constants
 */

/**
 * PropTypes
 */

const propTypes$2 = {
  optimistic: PropTypes.bool
};
/**
 * CSS Rules
 */

const shimmer = keyframes`
	0% {
		background-position: top left;
	}
	100% {
		background-position: top right;
	}
`;

const optimisticRules = ({
  theme,
  optimistic
}) => optimistic && css`
		background-image: linear-gradient(
			to right,
			${theme.colors.ambient} 0%,
			${theme.colors.ambient} 33%,
			${theme.colors.ambientDark} 50%,
			${theme.colors.ambient} 66%,
			${theme.colors.ambient} 100%
		);
		background-repeat: no-repeat;
		background-size: 1000px 100%;
		animation: ${shimmer} 1000ms ease-out infinite forwards;
	`;
/**
 * maps for Jest and Storybook
 */


const expectedValueMap = {
  'background-repeat': 'no-repeat',
  'background-size': '1000px 100%'
};

const mapperFn$2 = prop => ({
  propType: 'boolean',
  expectedValueMap: {
    true: expectedValueMap
  },
  cssPropGetters: {
    'background-repeat': () => expectedValueMap['background-repeat'],
    'background-size': () => expectedValueMap['background-size']
  }
});

const map$5 = createPropMap2(propTypes$2)(mapperFn$2);
const allOptimisticRules = [optimisticRules];
/**
 * The actual anatomy
 */

const anatomy$4 = makeAnatomy(allOptimisticRules, propTypes$2);
/**
 * The styled anatomy
 */

var index$6 = styledAnatomyFactory(anatomy$4);

// @ts-nocheck
/**
 * Constants
 */

const ENUMS = Object.keys(zIndex);
/**
 * PropTypes
 */

const propTypes$1 = {
  zIndex: PropTypes.oneOfType([PropTypes.oneOf(ENUMS), PropTypes.arrayOf(PropTypes.oneOf(ENUMS))])
};
/**
 * CSS Rules
 */

const zIndexRules = ({
  zIndex,
  theme
}) => zIndex && makeResponsiveStyles(theme.breakpoints, zIndex, val => css`
			z-index: ${getValueFromTheme(val, theme.zIndex, val)};
		`);
/**
 * maps for Jest and Storybook
 */

const mapperFn$1 = props => {
  const expectedValueMap = zIndex;
  return {
    expectedValueMap,
    propType: 'enum',
    cssPropGetters: {
      'z-index': propVal => expectedValueMap[propVal]
    }
  };
};

const map$4 = createPropMap2(propTypes$1)(mapperFn$1);
const allZDepthRules = [zIndexRules];
/**
 * The actual anatomy
 */

const anatomy$3 = makeAnatomy(allZDepthRules, propTypes$1);
/**
 * The styled anatomy
 */

var index$5 = styledAnatomyFactory(anatomy$3);

// @ts-nocheck
/**
 * Constants
 */

/**
 * PropTypes
 */

const propTypes = {
  // for containers
  grid: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.bool), PropTypes.oneOf(['true', 'false'])]),
  gridColumns: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  gridRows: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  gridColumnGap: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  gridRowGap: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  gridGap: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  // for items in a grid
  gridColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  gridRow: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
};
/**
 * CSS Rules
 * @todo find a way to include fallback styles and to wrap these all in a @ supports: grid
 */

const gridRules = ({
  theme,
  grid
}) => grid && makeResponsiveStyles(theme.breakpoints, grid, val => css`
				display: ${grid ? 'grid' : val};
			`);
/**
 * If the browser does not support grid, default to flex, single-column fallback.  Essentially a mobile view.
 * @param {*} props the component props
 */

const gridRulesFallback = ({
  theme,
  grid
}) => grid && makeResponsiveStyles(theme.breakpoints, grid, val => css`
				display: ${grid ? 'flex' : val};
				flex-direction: column;
			`);
const gridRowsRules = ({
  theme,
  grid,
  gridRows
}) => grid && gridRows && makeResponsiveStyles(theme.breakpoints, gridRows, val => css`
			grid-template-rows: ${val};
		`);
const gridColumnsRules = ({
  theme,
  grid,
  gridColumns
}) => grid && gridColumns && makeResponsiveStyles(theme.breakpoints, gridColumns, val => css`
			grid-template-columns: ${val};
		`);
const gridColumnGapRules = ({
  theme,
  grid,
  gridColumnGap
}) => grid && gridColumnGap && makeResponsiveStyles(theme.breakpoints, gridColumnGap, val => css`
			grid-column-gap: ${val};
		`);
const gridRowGapRules = ({
  theme,
  grid,
  gridRowGap
}) => grid && gridRowGap && makeResponsiveStyles(theme.breakpoints, gridRowGap, val => css`
			grid-row-gap: ${val};
		`);
const gridGapRules = ({
  theme,
  grid,
  gridGap
}) => grid && gridGap && makeResponsiveStyles(theme.breakpoints, gridGap, val => css`
			grid-gap: ${val};
		`);
const gridRowRules = ({
  theme,
  gridRow
}) => gridRow && makeResponsiveStyles(theme.breakpoints, gridRow, val => css`
			grid-row: ${val};
		`);
const gridColumnRules = ({
  theme,
  gridColumn
}) => gridColumn && makeResponsiveStyles(theme.breakpoints, gridColumn, val => css`
			grid-column: ${val};
		`);
const composedGridRules = props => css`
	${gridRulesFallback};
	@supports (display: grid) {
		${composeStyleRules(gridRules, gridRowsRules, gridColumnsRules, gridColumnGapRules, gridRowGapRules, gridGapRules, gridRowRules, gridColumnRules)(props)};
	}
`;
/**
 * maps for Jest and Storybook
 */

const propToCSSAttrMap = {
  grid: 'display',
  gridRows: 'grid-template-rows',
  gridColumns: 'grid-template-columns',
  gridColumnGap: 'grid-column-gap',
  gridRowGap: 'grid-row-gap',
  gridGap: 'grid-gap',
  gridRow: 'grid-row',
  gridColumn: 'grid-column'
};
const testVals = {
  grid: {
    true: 'grid'
  },
  gridRows: {
    'repeat(3,1fr)': 'repeat(3,1fr)'
  },
  gridColumns: {
    'repeat(3,1fr)': 'repeat(3,1fr)'
  },
  gridRowGap: {
    '16px': '16px'
  },
  gridColumnGap: {
    '16px': '16px'
  },
  gridGap: {
    '16px': '16px'
  },
  gridRow: {
    '1 / 3': '1 / 3'
  },
  gridColumn: {
    '1 / 3': '1 / 3'
  }
};

const mapperFn = prop => {
  const cssProp = propToCSSAttrMap[prop];
  return {
    propType: prop === 'grid' ? 'boolean' : 'string',
    expectedValueMap: testVals[prop],
    cssPropGetters: {
      [cssProp]: propVal => testVals[prop][propVal]
    }
  };
};

const map$3 = createPropMap2(propTypes)(mapperFn);
const allGridRules = [composedGridRules];
/**
 * The actual anatomy
 */

const anatomy$2 = makeAnatomy(allGridRules, propTypes);
/**
 * The styled anatomy
 */

var index$4 = styledAnatomyFactory(anatomy$2);

// @ts-nocheck
/**
 * The actual anatomy
 */

const anatomy$1 = mergeAnatomies(anatomy$8, anatomy$f, anatomy$7, anatomy$e, anatomy$c, anatomy$3);
/**
 * PropTypes
 */

({ ...anatomy$1.propTypes
});
anatomy$1.rules;
/**
 * maps for Jest and Storybook
 */

const map$2 = mergePropMaps(map$9, map$g, map$8, map$f, map$d, map$4);
/**
 * The styled anatomy
 */

var index$3 = styledAnatomyFactory(anatomy$1);

// @ts-nocheck
/**
 * The actual anatomy
 */

const anatomy = mergeAnatomies(anatomy$9, anatomy$b, anatomy$a, anatomy$2, anatomy$4, anatomy$6, anatomy$d, anatomy$5);
/**
 * PropTypes
 */

({ ...anatomy.propTypes
});
anatomy.rules;
/**
 * maps for Jest and Storybook
 */

const map$1 = mergePropMaps(map$a, map$c, map$b, map$3, map$5, map$7, map$e, map$6);
/**
 * The styled anatomy
 */

var index$2 = styledAnatomyFactory(anatomy);

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
	if (n.__esModule) return n;
	var a = Object.defineProperty({}, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var dist$2 = {exports: {}};

var check$4 = function (it) {
  return it && it.Math == Math && it;
}; // https://github.com/zloirock/core-js/issues/86#issuecomment-115759028


var global$1x = // eslint-disable-next-line es/no-global-this -- safe
check$4(typeof globalThis == 'object' && globalThis) || check$4(typeof window == 'object' && window) || // eslint-disable-next-line no-restricted-globals -- safe
check$4(typeof self == 'object' && self) || check$4(typeof commonjsGlobal == 'object' && commonjsGlobal) || // eslint-disable-next-line no-new-func -- fallback
function () {
  return this;
}() || Function('return this')();

var objectGetOwnPropertyDescriptor$4 = {};

var fails$1r = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$1q = fails$1r; // Detect IE8's incomplete defineProperty implementation


var descriptors$4 = !fails$1q(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, {
    get: function () {
      return 7;
    }
  })[1] != 7;
});

var objectPropertyIsEnumerable$4 = {};

var $propertyIsEnumerable$7 = {}.propertyIsEnumerable; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe

var getOwnPropertyDescriptor$d = Object.getOwnPropertyDescriptor; // Nashorn ~ JDK8 bug

var NASHORN_BUG$4 = getOwnPropertyDescriptor$d && !$propertyIsEnumerable$7.call({
  1: 2
}, 1); // `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable

objectPropertyIsEnumerable$4.f = NASHORN_BUG$4 ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$d(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable$7;

var createPropertyDescriptor$q = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var toString$i = {}.toString;

var classofRaw$9 = function (it) {
  return toString$i.call(it).slice(8, -1);
};

var fails$1p = fails$1r;

var classof$x = classofRaw$9;

var split$4 = ''.split; // fallback for non-array-like ES3 and non-enumerable old V8 strings

var indexedObject$4 = fails$1p(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$x(it) == 'String' ? split$4.call(it, '') : Object(it);
} : Object;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$r = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$c = indexedObject$4;

var requireObjectCoercible$q = requireObjectCoercible$r;

var toIndexedObject$B = function (it) {
  return IndexedObject$c(requireObjectCoercible$q(it));
};

var isObject$1k = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};

var isObject$1j = isObject$1k; // `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string


var toPrimitive$n = function (input, PREFERRED_STRING) {
  if (!isObject$1j(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$1j(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject$1j(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$1j(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};

var requireObjectCoercible$p = requireObjectCoercible$r; // `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject


var toObject$D = function (argument) {
  return Object(requireObjectCoercible$p(argument));
};

var toObject$C = toObject$D;

var hasOwnProperty$q = {}.hasOwnProperty;

var has$11 = function hasOwn(it, key) {
  return hasOwnProperty$q.call(toObject$C(it), key);
};

var global$1w = global$1x;

var isObject$1i = isObject$1k;

var document$a = global$1w.document; // typeof document.createElement is 'object' in old IE

var EXISTS$4 = isObject$1i(document$a) && isObject$1i(document$a.createElement);

var documentCreateElement$8 = function (it) {
  return EXISTS$4 ? document$a.createElement(it) : {};
};

var DESCRIPTORS$N = descriptors$4;

var fails$1o = fails$1r;

var createElement$7 = documentCreateElement$8; // Thank's IE8 for his funny defineProperty


var ie8DomDefine$4 = !DESCRIPTORS$N && !fails$1o(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement$7('div'), 'a', {
    get: function () {
      return 7;
    }
  }).a != 7;
});

var DESCRIPTORS$M = descriptors$4;

var propertyIsEnumerableModule$a = objectPropertyIsEnumerable$4;

var createPropertyDescriptor$p = createPropertyDescriptor$q;

var toIndexedObject$A = toIndexedObject$B;

var toPrimitive$m = toPrimitive$n;

var has$10 = has$11;

var IE8_DOM_DEFINE$9 = ie8DomDefine$4; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe


var $getOwnPropertyDescriptor$7 = Object.getOwnPropertyDescriptor; // `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor

objectGetOwnPropertyDescriptor$4.f = DESCRIPTORS$M ? $getOwnPropertyDescriptor$7 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$A(O);
  P = toPrimitive$m(P, true);
  if (IE8_DOM_DEFINE$9) try {
    return $getOwnPropertyDescriptor$7(O, P);
  } catch (error) {
    /* empty */
  }
  if (has$10(O, P)) return createPropertyDescriptor$p(!propertyIsEnumerableModule$a.f.call(O, P), O[P]);
};

var objectDefineProperty$4 = {};

var isObject$1h = isObject$1k;

var anObject$W = function (it) {
  if (!isObject$1h(it)) {
    throw TypeError(String(it) + ' is not an object');
  }

  return it;
};

var DESCRIPTORS$L = descriptors$4;

var IE8_DOM_DEFINE$8 = ie8DomDefine$4;

var anObject$V = anObject$W;

var toPrimitive$l = toPrimitive$n; // eslint-disable-next-line es/no-object-defineproperty -- safe


var $defineProperty$7 = Object.defineProperty; // `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty

objectDefineProperty$4.f = DESCRIPTORS$L ? $defineProperty$7 : function defineProperty(O, P, Attributes) {
  anObject$V(O);
  P = toPrimitive$l(P, true);
  anObject$V(Attributes);
  if (IE8_DOM_DEFINE$8) try {
    return $defineProperty$7(O, P, Attributes);
  } catch (error) {
    /* empty */
  }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$K = descriptors$4;

var definePropertyModule$t = objectDefineProperty$4;

var createPropertyDescriptor$o = createPropertyDescriptor$q;

var createNonEnumerableProperty$K = DESCRIPTORS$K ? function (object, key, value) {
  return definePropertyModule$t.f(object, key, createPropertyDescriptor$o(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var redefine$E = {exports: {}};

var global$1v = global$1x;

var createNonEnumerableProperty$J = createNonEnumerableProperty$K;

var setGlobal$j = function (key, value) {
  try {
    createNonEnumerableProperty$J(global$1v, key, value);
  } catch (error) {
    global$1v[key] = value;
  }

  return value;
};

var global$1u = global$1x;

var setGlobal$i = setGlobal$j;

var SHARED$4 = '__core-js_shared__';
var store$j = global$1u[SHARED$4] || setGlobal$i(SHARED$4, {});
var sharedStore$4 = store$j;

var store$i = sharedStore$4;

var functionToString$5 = Function.toString; // this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper

if (typeof store$i.inspectSource != 'function') {
  store$i.inspectSource = function (it) {
    return functionToString$5.call(it);
  };
}

var inspectSource$g = store$i.inspectSource;

var global$1t = global$1x;

var inspectSource$f = inspectSource$g;

var WeakMap$e = global$1t.WeakMap;
var nativeWeakMap$4 = typeof WeakMap$e === 'function' && /native code/.test(inspectSource$f(WeakMap$e));

var shared$r = {exports: {}};

var store$h = sharedStore$4;

(shared$r.exports = function (key, value) {
  return store$h[key] || (store$h[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.12.1',
  mode: 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});

var id$a = 0;
var postfix$4 = Math.random();

var uid$m = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id$a + postfix$4).toString(36);
};

var shared$q = shared$r.exports;

var uid$l = uid$m;

var keys$d = shared$q('keys');

var sharedKey$k = function (key) {
  return keys$d[key] || (keys$d[key] = uid$l(key));
};

var hiddenKeys$v = {};

var NATIVE_WEAK_MAP$5 = nativeWeakMap$4;

var global$1s = global$1x;

var isObject$1g = isObject$1k;

var createNonEnumerableProperty$I = createNonEnumerableProperty$K;

var objectHas$4 = has$11;

var shared$p = sharedStore$4;

var sharedKey$j = sharedKey$k;

var hiddenKeys$u = hiddenKeys$v;

var OBJECT_ALREADY_INITIALIZED$4 = 'Object already initialized';
var WeakMap$d = global$1s.WeakMap;
var set$7, get$6, has$$;

var enforce$4 = function (it) {
  return has$$(it) ? get$6(it) : set$7(it, {});
};

var getterFor$4 = function (TYPE) {
  return function (it) {
    var state;

    if (!isObject$1g(it) || (state = get$6(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    }

    return state;
  };
};

if (NATIVE_WEAK_MAP$5 || shared$p.state) {
  var store$g = shared$p.state || (shared$p.state = new WeakMap$d());
  var wmget$4 = store$g.get;
  var wmhas$4 = store$g.has;
  var wmset$4 = store$g.set;

  set$7 = function (it, metadata) {
    if (wmhas$4.call(store$g, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$4);
    metadata.facade = it;
    wmset$4.call(store$g, it, metadata);
    return metadata;
  };

  get$6 = function (it) {
    return wmget$4.call(store$g, it) || {};
  };

  has$$ = function (it) {
    return wmhas$4.call(store$g, it);
  };
} else {
  var STATE$4 = sharedKey$j('state');
  hiddenKeys$u[STATE$4] = true;

  set$7 = function (it, metadata) {
    if (objectHas$4(it, STATE$4)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$4);
    metadata.facade = it;
    createNonEnumerableProperty$I(it, STATE$4, metadata);
    return metadata;
  };

  get$6 = function (it) {
    return objectHas$4(it, STATE$4) ? it[STATE$4] : {};
  };

  has$$ = function (it) {
    return objectHas$4(it, STATE$4);
  };
}

var internalState$4 = {
  set: set$7,
  get: get$6,
  has: has$$,
  enforce: enforce$4,
  getterFor: getterFor$4
};

var global$1r = global$1x;

var createNonEnumerableProperty$H = createNonEnumerableProperty$K;

var has$_ = has$11;

var setGlobal$h = setGlobal$j;

var inspectSource$e = inspectSource$g;

var InternalStateModule$m = internalState$4;

var getInternalState$h = InternalStateModule$m.get;
var enforceInternalState$6 = InternalStateModule$m.enforce;
var TEMPLATE$4 = String(String).split('String');
(redefine$E.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var state;

  if (typeof value == 'function') {
    if (typeof key == 'string' && !has$_(value, 'name')) {
      createNonEnumerableProperty$H(value, 'name', key);
    }

    state = enforceInternalState$6(value);

    if (!state.source) {
      state.source = TEMPLATE$4.join(typeof key == 'string' ? key : '');
    }
  }

  if (O === global$1r) {
    if (simple) O[key] = value;else setGlobal$h(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }

  if (simple) O[key] = value;else createNonEnumerableProperty$H(O, key, value); // add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState$h(this).source || inspectSource$e(this);
});

var global$1q = global$1x;

var path$c = global$1q;

var path$b = path$c;

var global$1p = global$1x;

var aFunction$l = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

var getBuiltIn$s = function (namespace, method) {
  return arguments.length < 2 ? aFunction$l(path$b[namespace]) || aFunction$l(global$1p[namespace]) : path$b[namespace] && path$b[namespace][method] || global$1p[namespace] && global$1p[namespace][method];
};

var objectGetOwnPropertyNames$4 = {};

var ceil$4 = Math.ceil;
var floor$6 = Math.floor; // `ToInteger` abstract operation
// https://tc39.es/ecma262/#sec-tointeger

var toInteger$k = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor$6 : ceil$4)(argument);
};

var toInteger$j = toInteger$k;

var min$d = Math.min; // `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength

var toLength$y = function (argument) {
  return argument > 0 ? min$d(toInteger$j(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toInteger$i = toInteger$k;

var max$a = Math.max;
var min$c = Math.min; // Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).

var toAbsoluteIndex$e = function (index, length) {
  var integer = toInteger$i(index);
  return integer < 0 ? max$a(integer + length, 0) : min$c(integer, length);
};

var toIndexedObject$z = toIndexedObject$B;

var toLength$x = toLength$y;

var toAbsoluteIndex$d = toAbsoluteIndex$e; // `Array.prototype.{ indexOf, includes }` methods implementation


var createMethod$g = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$z($this);
    var length = toLength$x(O.length);
    var index = toAbsoluteIndex$d(fromIndex, length);
    var value; // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check

    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++]; // eslint-disable-next-line no-self-compare -- NaN check

      if (value != value) return true; // Array#indexOf ignores holes, Array#includes - not
    } else for (; length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    }
    return !IS_INCLUDES && -1;
  };
};

var arrayIncludes$4 = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$g(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$g(false)
};

var has$Z = has$11;

var toIndexedObject$y = toIndexedObject$B;

var indexOf$5 = arrayIncludes$4.indexOf;

var hiddenKeys$t = hiddenKeys$v;

var objectKeysInternal$4 = function (object, names) {
  var O = toIndexedObject$y(object);
  var i = 0;
  var result = [];
  var key;

  for (key in O) !has$Z(hiddenKeys$t, key) && has$Z(O, key) && result.push(key); // Don't enum bug & hidden keys


  while (names.length > i) if (has$Z(O, key = names[i++])) {
    ~indexOf$5(result, key) || result.push(key);
  }

  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$i = ['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf'];

var internalObjectKeys$9 = objectKeysInternal$4;

var enumBugKeys$h = enumBugKeys$i;

var hiddenKeys$s = enumBugKeys$h.concat('length', 'prototype'); // `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe

objectGetOwnPropertyNames$4.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$9(O, hiddenKeys$s);
};

var objectGetOwnPropertySymbols$4 = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols$4.f = Object.getOwnPropertySymbols;

var getBuiltIn$r = getBuiltIn$s;

var getOwnPropertyNamesModule$7 = objectGetOwnPropertyNames$4;

var getOwnPropertySymbolsModule$a = objectGetOwnPropertySymbols$4;

var anObject$U = anObject$W; // all object keys, includes non-enumerable and symbols


var ownKeys$9 = getBuiltIn$r('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule$7.f(anObject$U(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$a.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};

var has$Y = has$11;

var ownKeys$8 = ownKeys$9;

var getOwnPropertyDescriptorModule$7 = objectGetOwnPropertyDescriptor$4;

var definePropertyModule$s = objectDefineProperty$4;

var copyConstructorProperties$c = function (target, source) {
  var keys = ownKeys$8(source);
  var defineProperty = definePropertyModule$s.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule$7.f;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has$Y(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};

var fails$1n = fails$1r;

var replacement$4 = /#|\.prototype\./;

var isForced$i = function (feature, detection) {
  var value = data$4[normalize$4(feature)];
  return value == POLYFILL$4 ? true : value == NATIVE$4 ? false : typeof detection == 'function' ? fails$1n(detection) : !!detection;
};

var normalize$4 = isForced$i.normalize = function (string) {
  return String(string).replace(replacement$4, '.').toLowerCase();
};

var data$4 = isForced$i.data = {};
var NATIVE$4 = isForced$i.NATIVE = 'N';
var POLYFILL$4 = isForced$i.POLYFILL = 'P';
var isForced_1$4 = isForced$i;

var global$1o = global$1x;

var getOwnPropertyDescriptor$c = objectGetOwnPropertyDescriptor$4.f;

var createNonEnumerableProperty$G = createNonEnumerableProperty$K;

var redefine$D = redefine$E.exports;

var setGlobal$g = setGlobal$j;

var copyConstructorProperties$b = copyConstructorProperties$c;

var isForced$h = isForced_1$4;
/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/


var _export$4 = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;

  if (GLOBAL) {
    target = global$1o;
  } else if (STATIC) {
    target = global$1o[TARGET] || setGlobal$g(TARGET, {});
  } else {
    target = (global$1o[TARGET] || {}).prototype;
  }

  if (target) for (key in source) {
    sourceProperty = source[key];

    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor$c(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];

    FORCED = isForced$h(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced); // contained in target

    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties$b(sourceProperty, targetProperty);
    } // add a flag to not completely full polyfills


    if (options.sham || targetProperty && targetProperty.sham) {
      createNonEnumerableProperty$G(sourceProperty, 'sham', true);
    } // extend global


    redefine$D(target, key, sourceProperty, options);
  }
};

var internalObjectKeys$8 = objectKeysInternal$4;

var enumBugKeys$g = enumBugKeys$i; // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe


var objectKeys$h = Object.keys || function keys(O) {
  return internalObjectKeys$8(O, enumBugKeys$g);
};

var DESCRIPTORS$J = descriptors$4;

var fails$1m = fails$1r;

var objectKeys$g = objectKeys$h;

var getOwnPropertySymbolsModule$9 = objectGetOwnPropertySymbols$4;

var propertyIsEnumerableModule$9 = objectPropertyIsEnumerable$4;

var toObject$B = toObject$D;

var IndexedObject$b = indexedObject$4; // eslint-disable-next-line es/no-object-assign -- safe


var $assign$2 = Object.assign; // eslint-disable-next-line es/no-object-defineproperty -- required for testing

var defineProperty$u = Object.defineProperty; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign

var objectAssign$2 = !$assign$2 || fails$1m(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS$J && $assign$2({
    b: 1
  }, $assign$2(defineProperty$u({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty$u(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), {
    b: 2
  })).b !== 1) return true; // should work with symbols and should have deterministic property order (V8 bug)

  var A = {};
  var B = {}; // eslint-disable-next-line es/no-symbol -- safe

  var symbol = Symbol();
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) {
    B[chr] = chr;
  });
  return $assign$2({}, A)[symbol] != 7 || objectKeys$g($assign$2({}, B)).join('') != alphabet;
}) ? function assign(target, source) {
  // eslint-disable-line no-unused-vars -- required for `.length`
  var T = toObject$B(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$9.f;
  var propertyIsEnumerable = propertyIsEnumerableModule$9.f;

  while (argumentsLength > index) {
    var S = IndexedObject$b(arguments[index++]);
    var keys = getOwnPropertySymbols ? objectKeys$g(S).concat(getOwnPropertySymbols(S)) : objectKeys$g(S);
    var length = keys.length;
    var j = 0;
    var key;

    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS$J || propertyIsEnumerable.call(S, key)) T[key] = S[key];
    }
  }

  return T;
} : $assign$2;

var $$12 = _export$4;

var assign$3 = objectAssign$2; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
// eslint-disable-next-line es/no-object-assign -- required for testing


$$12({
  target: 'Object',
  stat: true,
  forced: Object.assign !== assign$3
}, {
  assign: assign$3
});

var getBuiltIn$q = getBuiltIn$s;

var engineUserAgent$4 = getBuiltIn$q('navigator', 'userAgent') || '';

var global$1n = global$1x;

var userAgent$9 = engineUserAgent$4;

var process$c = global$1n.process;
var versions$4 = process$c && process$c.versions;
var v8$4 = versions$4 && versions$4.v8;
var match$5, version$4;

if (v8$4) {
  match$5 = v8$4.split('.');
  version$4 = match$5[0] < 4 ? 1 : match$5[0] + match$5[1];
} else if (userAgent$9) {
  match$5 = userAgent$9.match(/Edge\/(\d+)/);

  if (!match$5 || match$5[1] >= 74) {
    match$5 = userAgent$9.match(/Chrome\/(\d+)/);
    if (match$5) version$4 = match$5[1];
  }
}

var engineV8Version$4 = version$4 && +version$4;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION$f = engineV8Version$4;

var fails$1l = fails$1r; // eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing


var nativeSymbol$4 = !!Object.getOwnPropertySymbols && !fails$1l(function () {
  return !String(Symbol()) || // Chrome 38 Symbol has incorrect toString conversion
  // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
  !Symbol.sham && V8_VERSION$f && V8_VERSION$f < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$c = nativeSymbol$4;

var useSymbolAsUid$4 = NATIVE_SYMBOL$c && !Symbol.sham && typeof Symbol.iterator == 'symbol';

var global$1m = global$1x;

var shared$o = shared$r.exports;

var has$X = has$11;

var uid$k = uid$m;

var NATIVE_SYMBOL$b = nativeSymbol$4;

var USE_SYMBOL_AS_UID$7 = useSymbolAsUid$4;

var WellKnownSymbolsStore$7 = shared$o('wks');
var Symbol$j = global$1m.Symbol;
var createWellKnownSymbol$4 = USE_SYMBOL_AS_UID$7 ? Symbol$j : Symbol$j && Symbol$j.withoutSetter || uid$k;

var wellKnownSymbol$1o = function (name) {
  if (!has$X(WellKnownSymbolsStore$7, name) || !(NATIVE_SYMBOL$b || typeof WellKnownSymbolsStore$7[name] == 'string')) {
    if (NATIVE_SYMBOL$b && has$X(Symbol$j, name)) {
      WellKnownSymbolsStore$7[name] = Symbol$j[name];
    } else {
      WellKnownSymbolsStore$7[name] = createWellKnownSymbol$4('Symbol.' + name);
    }
  }

  return WellKnownSymbolsStore$7[name];
};

var wellKnownSymbol$1n = wellKnownSymbol$1o;

var TO_STRING_TAG$h = wellKnownSymbol$1n('toStringTag');
var test$5 = {};
test$5[TO_STRING_TAG$h] = 'z';
var toStringTagSupport$4 = String(test$5) === '[object z]';

var TO_STRING_TAG_SUPPORT$e = toStringTagSupport$4;

var classofRaw$8 = classofRaw$9;

var wellKnownSymbol$1m = wellKnownSymbol$1o;

var TO_STRING_TAG$g = wellKnownSymbol$1m('toStringTag'); // ES3 wrong here

var CORRECT_ARGUMENTS$4 = classofRaw$8(function () {
  return arguments;
}()) == 'Arguments'; // fallback for IE11 Script Access Denied error

var tryGet$4 = function (it, key) {
  try {
    return it[key];
  } catch (error) {
    /* empty */
  }
}; // getting tag from ES6+ `Object.prototype.toString`


var classof$w = TO_STRING_TAG_SUPPORT$e ? classofRaw$8 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null' // @@toStringTag case
  : typeof (tag = tryGet$4(O = Object(it), TO_STRING_TAG$g)) == 'string' ? tag // builtinTag case
  : CORRECT_ARGUMENTS$4 ? classofRaw$8(O) // ES3 arguments fallback
  : (result = classofRaw$8(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};

var TO_STRING_TAG_SUPPORT$d = toStringTagSupport$4;

var classof$v = classof$w; // `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring


var objectToString$b = TO_STRING_TAG_SUPPORT$d ? {}.toString : function toString() {
  return '[object ' + classof$v(this) + ']';
};

var TO_STRING_TAG_SUPPORT$c = toStringTagSupport$4;

var redefine$C = redefine$E.exports;

var toString$h = objectToString$b; // `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring


if (!TO_STRING_TAG_SUPPORT$c) {
  redefine$C(Object.prototype, 'toString', toString$h, {
    unsafe: true
  });
}

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables$4 = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

var aFunction$k = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  }

  return it;
};

var aFunction$j = aFunction$k; // optional / simple context binding


var functionBindContext$4 = function (fn, that, length) {
  aFunction$j(fn);
  if (that === undefined) return fn;

  switch (length) {
    case 0:
      return function () {
        return fn.call(that);
      };

    case 1:
      return function (a) {
        return fn.call(that, a);
      };

    case 2:
      return function (a, b) {
        return fn.call(that, a, b);
      };

    case 3:
      return function (a, b, c) {
        return fn.call(that, a, b, c);
      };
  }

  return function ()
  /* ...args */
  {
    return fn.apply(that, arguments);
  };
};

var classof$u = classofRaw$9; // `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe


var isArray$D = Array.isArray || function isArray(arg) {
  return classof$u(arg) == 'Array';
};

var isObject$1f = isObject$1k;

var isArray$C = isArray$D;

var wellKnownSymbol$1l = wellKnownSymbol$1o;

var SPECIES$o = wellKnownSymbol$1l('species'); // `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate

var arraySpeciesCreate$c = function (originalArray, length) {
  var C;

  if (isArray$C(originalArray)) {
    C = originalArray.constructor; // cross-realm fallback

    if (typeof C == 'function' && (C === Array || isArray$C(C.prototype))) C = undefined;else if (isObject$1f(C)) {
      C = C[SPECIES$o];
      if (C === null) C = undefined;
    }
  }

  return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};

var bind$l = functionBindContext$4;

var IndexedObject$a = indexedObject$4;

var toObject$A = toObject$D;

var toLength$w = toLength$y;

var arraySpeciesCreate$b = arraySpeciesCreate$c;

var push$4 = [].push; // `Array.prototype.{ forEach, map, filter, some, every, find, findIndex, filterOut }` methods implementation

var createMethod$f = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var IS_FILTER_OUT = TYPE == 7;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject$A($this);
    var self = IndexedObject$a(O);
    var boundFunction = bind$l(callbackfn, that, 3);
    var length = toLength$w(self.length);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate$b;
    var target = IS_MAP ? create($this, length) : IS_FILTER || IS_FILTER_OUT ? create($this, 0) : undefined;
    var value, result;

    for (; length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);

      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
            case 3:
              return true;
            // some

            case 5:
              return value;
            // find

            case 6:
              return index;
            // findIndex

            case 2:
              push$4.call(target, value);
            // filter
          } else switch (TYPE) {
            case 4:
              return false;
            // every

            case 7:
              push$4.call(target, value);
            // filterOut
          }
      }
    }

    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

var arrayIteration$3 = {
  // `Array.prototype.forEach` method
  // https://tc39.es/ecma262/#sec-array.prototype.foreach
  forEach: createMethod$f(0),
  // `Array.prototype.map` method
  // https://tc39.es/ecma262/#sec-array.prototype.map
  map: createMethod$f(1),
  // `Array.prototype.filter` method
  // https://tc39.es/ecma262/#sec-array.prototype.filter
  filter: createMethod$f(2),
  // `Array.prototype.some` method
  // https://tc39.es/ecma262/#sec-array.prototype.some
  some: createMethod$f(3),
  // `Array.prototype.every` method
  // https://tc39.es/ecma262/#sec-array.prototype.every
  every: createMethod$f(4),
  // `Array.prototype.find` method
  // https://tc39.es/ecma262/#sec-array.prototype.find
  find: createMethod$f(5),
  // `Array.prototype.findIndex` method
  // https://tc39.es/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod$f(6),
  // `Array.prototype.filterOut` method
  // https://github.com/tc39/proposal-array-filtering
  filterOut: createMethod$f(7)
};

var fails$1k = fails$1r;

var arrayMethodIsStrict$9 = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails$1k(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal -- required for testing
    method.call(null, argument || function () {
      throw 1;
    }, 1);
  });
};

var $forEach$6 = arrayIteration$3.forEach;

var arrayMethodIsStrict$8 = arrayMethodIsStrict$9;

var STRICT_METHOD$5 = arrayMethodIsStrict$8('forEach'); // `Array.prototype.forEach` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.foreach

var arrayForEach$3 = !STRICT_METHOD$5 ? function forEach(callbackfn
/* , thisArg */
) {
  return $forEach$6(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined); // eslint-disable-next-line es/no-array-prototype-foreach -- safe
} : [].forEach;

var global$1l = global$1x;

var DOMIterables$7 = domIterables$4;

var forEach$3 = arrayForEach$3;

var createNonEnumerableProperty$F = createNonEnumerableProperty$K;

for (var COLLECTION_NAME$7 in DOMIterables$7) {
  var Collection$7 = global$1l[COLLECTION_NAME$7];
  var CollectionPrototype$7 = Collection$7 && Collection$7.prototype; // some Chrome versions have non-configurable methods on DOMTokenList

  if (CollectionPrototype$7 && CollectionPrototype$7.forEach !== forEach$3) try {
    createNonEnumerableProperty$F(CollectionPrototype$7, 'forEach', forEach$3);
  } catch (error) {
    CollectionPrototype$7.forEach = forEach$3;
  }
}

var DESCRIPTORS$I = descriptors$4;

var objectKeys$f = objectKeys$h;

var toIndexedObject$x = toIndexedObject$B;

var propertyIsEnumerable$6 = objectPropertyIsEnumerable$4.f; // `Object.{ entries, values }` methods implementation


var createMethod$e = function (TO_ENTRIES) {
  return function (it) {
    var O = toIndexedObject$x(it);
    var keys = objectKeys$f(O);
    var length = keys.length;
    var i = 0;
    var result = [];
    var key;

    while (length > i) {
      key = keys[i++];

      if (!DESCRIPTORS$I || propertyIsEnumerable$6.call(O, key)) {
        result.push(TO_ENTRIES ? [key, O[key]] : O[key]);
      }
    }

    return result;
  };
};

var objectToArray$2 = {
  // `Object.entries` method
  // https://tc39.es/ecma262/#sec-object.entries
  entries: createMethod$e(true),
  // `Object.values` method
  // https://tc39.es/ecma262/#sec-object.values
  values: createMethod$e(false)
};

var $$11 = _export$4;

var $values$2 = objectToArray$2.values; // `Object.values` method
// https://tc39.es/ecma262/#sec-object.values


$$11({
  target: 'Object',
  stat: true
}, {
  values: function values(O) {
    return $values$2(O);
  }
});

var global$1k = global$1x;

var nativePromiseConstructor$1 = global$1k.Promise;

var redefine$B = redefine$E.exports;

var redefineAll$b = function (target, src, options) {
  for (var key in src) redefine$B(target, key, src[key], options);

  return target;
};

var isObject$1e = isObject$1k;

var aPossiblePrototype$7 = function (it) {
  if (!isObject$1e(it) && it !== null) {
    throw TypeError("Can't set " + String(it) + ' as a prototype');
  }

  return it;
};

/* eslint-disable no-proto -- safe */

var anObject$T = anObject$W;

var aPossiblePrototype$6 = aPossiblePrototype$7; // `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe


var objectSetPrototypeOf$3 = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;

  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    setter = Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set;
    setter.call(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) {
    /* empty */
  }

  return function setPrototypeOf(O, proto) {
    anObject$T(O);
    aPossiblePrototype$6(proto);
    if (CORRECT_SETTER) setter.call(O, proto);else O.__proto__ = proto;
    return O;
  };
}() : undefined);

var defineProperty$t = objectDefineProperty$4.f;

var has$W = has$11;

var wellKnownSymbol$1k = wellKnownSymbol$1o;

var TO_STRING_TAG$f = wellKnownSymbol$1k('toStringTag');

var setToStringTag$k = function (it, TAG, STATIC) {
  if (it && !has$W(it = STATIC ? it : it.prototype, TO_STRING_TAG$f)) {
    defineProperty$t(it, TO_STRING_TAG$f, {
      configurable: true,
      value: TAG
    });
  }
};

var getBuiltIn$p = getBuiltIn$s;

var definePropertyModule$r = objectDefineProperty$4;

var wellKnownSymbol$1j = wellKnownSymbol$1o;

var DESCRIPTORS$H = descriptors$4;

var SPECIES$n = wellKnownSymbol$1j('species');

var setSpecies$b = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn$p(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule$r.f;

  if (DESCRIPTORS$H && Constructor && !Constructor[SPECIES$n]) {
    defineProperty(Constructor, SPECIES$n, {
      configurable: true,
      get: function () {
        return this;
      }
    });
  }
};

var anInstance$e = function (it, Constructor, name) {
  if (!(it instanceof Constructor)) {
    throw TypeError('Incorrect ' + (name ? name + ' ' : '') + 'invocation');
  }

  return it;
};

var iterators$3 = {};

var wellKnownSymbol$1i = wellKnownSymbol$1o;

var Iterators$j = iterators$3;

var ITERATOR$n = wellKnownSymbol$1i('iterator');
var ArrayPrototype$7 = Array.prototype; // check on default Array iterator

var isArrayIteratorMethod$a = function (it) {
  return it !== undefined && (Iterators$j.Array === it || ArrayPrototype$7[ITERATOR$n] === it);
};

var classof$t = classof$w;

var Iterators$i = iterators$3;

var wellKnownSymbol$1h = wellKnownSymbol$1o;

var ITERATOR$m = wellKnownSymbol$1h('iterator');

var getIteratorMethod$a = function (it) {
  if (it != undefined) return it[ITERATOR$m] || it['@@iterator'] || Iterators$i[classof$t(it)];
};

var anObject$S = anObject$W;

var iteratorClose$a = function (iterator) {
  var returnMethod = iterator['return'];

  if (returnMethod !== undefined) {
    return anObject$S(returnMethod.call(iterator)).value;
  }
};

var anObject$R = anObject$W;

var isArrayIteratorMethod$9 = isArrayIteratorMethod$a;

var toLength$v = toLength$y;

var bind$k = functionBindContext$4;

var getIteratorMethod$9 = getIteratorMethod$a;

var iteratorClose$9 = iteratorClose$a;

var Result$3 = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var iterate$e = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind$k(unboundFunction, that, 1 + AS_ENTRIES + INTERRUPTED);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose$9(iterator);
    return new Result$3(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject$R(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    }

    return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod$9(iterable);
    if (typeof iterFn != 'function') throw TypeError('Target is not iterable'); // optimisation for array iterators

    if (isArrayIteratorMethod$9(iterFn)) {
      for (index = 0, length = toLength$v(iterable.length); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && result instanceof Result$3) return result;
      }

      return new Result$3(false);
    }

    iterator = iterFn.call(iterable);
  }

  next = iterator.next;

  while (!(step = next.call(iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose$9(iterator);
      throw error;
    }

    if (typeof result == 'object' && result && result instanceof Result$3) return result;
  }

  return new Result$3(false);
};

var wellKnownSymbol$1g = wellKnownSymbol$1o;

var ITERATOR$l = wellKnownSymbol$1g('iterator');
var SAFE_CLOSING$3 = false;

try {
  var called$3 = 0;
  var iteratorWithReturn$3 = {
    next: function () {
      return {
        done: !!called$3++
      };
    },
    'return': function () {
      SAFE_CLOSING$3 = true;
    }
  };

  iteratorWithReturn$3[ITERATOR$l] = function () {
    return this;
  }; // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing


  Array.from(iteratorWithReturn$3, function () {
    throw 2;
  });
} catch (error) {
  /* empty */
}

var checkCorrectnessOfIteration$c = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING$3) return false;
  var ITERATION_SUPPORT = false;

  try {
    var object = {};

    object[ITERATOR$l] = function () {
      return {
        next: function () {
          return {
            done: ITERATION_SUPPORT = true
          };
        }
      };
    };

    exec(object);
  } catch (error) {
    /* empty */
  }

  return ITERATION_SUPPORT;
};

var anObject$Q = anObject$W;

var aFunction$i = aFunction$k;

var wellKnownSymbol$1f = wellKnownSymbol$1o;

var SPECIES$m = wellKnownSymbol$1f('species'); // `SpeciesConstructor` abstract operation
// https://tc39.es/ecma262/#sec-speciesconstructor

var speciesConstructor$4 = function (O, defaultConstructor) {
  var C = anObject$Q(O).constructor;
  var S;
  return C === undefined || (S = anObject$Q(C)[SPECIES$m]) == undefined ? defaultConstructor : aFunction$i(S);
};

var getBuiltIn$o = getBuiltIn$s;

var html$b = getBuiltIn$o('document', 'documentElement');

var userAgent$8 = engineUserAgent$4;

var engineIsIos$2 = /(?:iphone|ipod|ipad).*applewebkit/i.test(userAgent$8);

var classof$s = classofRaw$9;

var global$1j = global$1x;

var engineIsNode$2 = classof$s(global$1j.process) == 'process';

var global$1i = global$1x;

var fails$1j = fails$1r;

var bind$j = functionBindContext$4;

var html$a = html$b;

var createElement$6 = documentCreateElement$8;

var IS_IOS$4 = engineIsIos$2;

var IS_NODE$6 = engineIsNode$2;

var location$2 = global$1i.location;
var set$6 = global$1i.setImmediate;
var clear$2 = global$1i.clearImmediate;
var process$b = global$1i.process;
var MessageChannel$2 = global$1i.MessageChannel;
var Dispatch$2 = global$1i.Dispatch;
var counter$2 = 0;
var queue$2 = {};
var ONREADYSTATECHANGE$2 = 'onreadystatechange';
var defer$2, channel$2, port$2;

var run$2 = function (id) {
  // eslint-disable-next-line no-prototype-builtins -- safe
  if (queue$2.hasOwnProperty(id)) {
    var fn = queue$2[id];
    delete queue$2[id];
    fn();
  }
};

var runner$2 = function (id) {
  return function () {
    run$2(id);
  };
};

var listener$2 = function (event) {
  run$2(event.data);
};

var post$2 = function (id) {
  // old engines have not location.origin
  global$1i.postMessage(id + '', location$2.protocol + '//' + location$2.host);
}; // Node.js 0.9+ & IE10+ has setImmediate, otherwise:


if (!set$6 || !clear$2) {
  set$6 = function setImmediate(fn) {
    var args = [];
    var i = 1;

    while (arguments.length > i) args.push(arguments[i++]);

    queue$2[++counter$2] = function () {
      // eslint-disable-next-line no-new-func -- spec requirement
      (typeof fn == 'function' ? fn : Function(fn)).apply(undefined, args);
    };

    defer$2(counter$2);
    return counter$2;
  };

  clear$2 = function clearImmediate(id) {
    delete queue$2[id];
  }; // Node.js 0.8-


  if (IS_NODE$6) {
    defer$2 = function (id) {
      process$b.nextTick(runner$2(id));
    }; // Sphere (JS game engine) Dispatch API

  } else if (Dispatch$2 && Dispatch$2.now) {
    defer$2 = function (id) {
      Dispatch$2.now(runner$2(id));
    }; // Browsers with MessageChannel, includes WebWorkers
    // except iOS - https://github.com/zloirock/core-js/issues/624

  } else if (MessageChannel$2 && !IS_IOS$4) {
    channel$2 = new MessageChannel$2();
    port$2 = channel$2.port2;
    channel$2.port1.onmessage = listener$2;
    defer$2 = bind$j(port$2.postMessage, port$2, 1); // Browsers with postMessage, skip WebWorkers
    // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (global$1i.addEventListener && typeof postMessage == 'function' && !global$1i.importScripts && location$2 && location$2.protocol !== 'file:' && !fails$1j(post$2)) {
    defer$2 = post$2;
    global$1i.addEventListener('message', listener$2, false); // IE8-
  } else if (ONREADYSTATECHANGE$2 in createElement$6('script')) {
    defer$2 = function (id) {
      html$a.appendChild(createElement$6('script'))[ONREADYSTATECHANGE$2] = function () {
        html$a.removeChild(this);
        run$2(id);
      };
    }; // Rest old browsers

  } else {
    defer$2 = function (id) {
      setTimeout(runner$2(id), 0);
    };
  }
}

var task$5 = {
  set: set$6,
  clear: clear$2
};

var userAgent$7 = engineUserAgent$4;

var engineIsWebosWebkit$1 = /web0s(?!.*chrome)/i.test(userAgent$7);

var global$1h = global$1x;

var getOwnPropertyDescriptor$b = objectGetOwnPropertyDescriptor$4.f;

var macrotask$1 = task$5.set;

var IS_IOS$3 = engineIsIos$2;

var IS_WEBOS_WEBKIT$1 = engineIsWebosWebkit$1;

var IS_NODE$5 = engineIsNode$2;

var MutationObserver$1 = global$1h.MutationObserver || global$1h.WebKitMutationObserver;
var document$9 = global$1h.document;
var process$a = global$1h.process;
var Promise$6 = global$1h.Promise; // Node.js 11 shows ExperimentalWarning on getting `queueMicrotask`

var queueMicrotaskDescriptor$1 = getOwnPropertyDescriptor$b(global$1h, 'queueMicrotask');
var queueMicrotask$1 = queueMicrotaskDescriptor$1 && queueMicrotaskDescriptor$1.value;
var flush$1, head$1, last$1, notify$3, toggle$1, node$2, promise$1, then$1; // modern engines have queueMicrotask method

if (!queueMicrotask$1) {
  flush$1 = function () {
    var parent, fn;
    if (IS_NODE$5 && (parent = process$a.domain)) parent.exit();

    while (head$1) {
      fn = head$1.fn;
      head$1 = head$1.next;

      try {
        fn();
      } catch (error) {
        if (head$1) notify$3();else last$1 = undefined;
        throw error;
      }
    }

    last$1 = undefined;
    if (parent) parent.enter();
  }; // browsers with MutationObserver, except iOS - https://github.com/zloirock/core-js/issues/339
  // also except WebOS Webkit https://github.com/zloirock/core-js/issues/898


  if (!IS_IOS$3 && !IS_NODE$5 && !IS_WEBOS_WEBKIT$1 && MutationObserver$1 && document$9) {
    toggle$1 = true;
    node$2 = document$9.createTextNode('');
    new MutationObserver$1(flush$1).observe(node$2, {
      characterData: true
    });

    notify$3 = function () {
      node$2.data = toggle$1 = !toggle$1;
    }; // environments with maybe non-completely correct, but existent Promise

  } else if (Promise$6 && Promise$6.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    promise$1 = Promise$6.resolve(undefined); // workaround of WebKit ~ iOS Safari 10.1 bug

    promise$1.constructor = Promise$6;
    then$1 = promise$1.then;

    notify$3 = function () {
      then$1.call(promise$1, flush$1);
    }; // Node.js without promises

  } else if (IS_NODE$5) {
    notify$3 = function () {
      process$a.nextTick(flush$1);
    }; // for other environments - macrotask based on:
    // - setImmediate
    // - MessageChannel
    // - window.postMessag
    // - onreadystatechange
    // - setTimeout

  } else {
    notify$3 = function () {
      // strange IE + webpack dev server bug - use .call(global)
      macrotask$1.call(global$1h, flush$1);
    };
  }
}

var microtask$3 = queueMicrotask$1 || function (fn) {
  var task = {
    fn: fn,
    next: undefined
  };
  if (last$1) last$1.next = task;

  if (!head$1) {
    head$1 = task;
    notify$3();
  }

  last$1 = task;
};

var newPromiseCapability$5 = {};

var aFunction$h = aFunction$k;

var PromiseCapability$1 = function (C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw TypeError('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aFunction$h(resolve);
  this.reject = aFunction$h(reject);
}; // 25.4.1.5 NewPromiseCapability(C)


newPromiseCapability$5.f = function (C) {
  return new PromiseCapability$1(C);
};

var anObject$P = anObject$W;

var isObject$1d = isObject$1k;

var newPromiseCapability$4 = newPromiseCapability$5;

var promiseResolve$3 = function (C, x) {
  anObject$P(C);
  if (isObject$1d(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability$4.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};

var global$1g = global$1x;

var hostReportErrors$3 = function (a, b) {
  var console = global$1g.console;

  if (console && console.error) {
    arguments.length === 1 ? console.error(a) : console.error(a, b);
  }
};

var perform$3 = function (exec) {
  try {
    return {
      error: false,
      value: exec()
    };
  } catch (error) {
    return {
      error: true,
      value: error
    };
  }
};

var engineIsBrowser$1 = typeof window == 'object';

var $$10 = _export$4;

var global$1f = global$1x;

var getBuiltIn$n = getBuiltIn$s;

var NativePromise$1 = nativePromiseConstructor$1;

var redefine$A = redefine$E.exports;

var redefineAll$a = redefineAll$b;

var setPrototypeOf$9 = objectSetPrototypeOf$3;

var setToStringTag$j = setToStringTag$k;

var setSpecies$a = setSpecies$b;

var isObject$1c = isObject$1k;

var aFunction$g = aFunction$k;

var anInstance$d = anInstance$e;

var inspectSource$d = inspectSource$g;

var iterate$d = iterate$e;

var checkCorrectnessOfIteration$b = checkCorrectnessOfIteration$c;

var speciesConstructor$3 = speciesConstructor$4;

var task$4 = task$5.set;

var microtask$2 = microtask$3;

var promiseResolve$2 = promiseResolve$3;

var hostReportErrors$2 = hostReportErrors$3;

var newPromiseCapabilityModule$1 = newPromiseCapability$5;

var perform$2 = perform$3;

var InternalStateModule$l = internalState$4;

var isForced$g = isForced_1$4;

var wellKnownSymbol$1e = wellKnownSymbol$1o;

var IS_BROWSER$1 = engineIsBrowser$1;

var IS_NODE$4 = engineIsNode$2;

var V8_VERSION$e = engineV8Version$4;

var SPECIES$l = wellKnownSymbol$1e('species');
var PROMISE$1 = 'Promise';
var getInternalState$g = InternalStateModule$l.get;
var setInternalState$h = InternalStateModule$l.set;
var getInternalPromiseState$1 = InternalStateModule$l.getterFor(PROMISE$1);
var NativePromisePrototype$1 = NativePromise$1 && NativePromise$1.prototype;
var PromiseConstructor$1 = NativePromise$1;
var PromiseConstructorPrototype$1 = NativePromisePrototype$1;
var TypeError$2 = global$1f.TypeError;
var document$8 = global$1f.document;
var process$9 = global$1f.process;
var newPromiseCapability$3 = newPromiseCapabilityModule$1.f;
var newGenericPromiseCapability$1 = newPromiseCapability$3;
var DISPATCH_EVENT$1 = !!(document$8 && document$8.createEvent && global$1f.dispatchEvent);
var NATIVE_REJECTION_EVENT$1 = typeof PromiseRejectionEvent == 'function';
var UNHANDLED_REJECTION$1 = 'unhandledrejection';
var REJECTION_HANDLED$1 = 'rejectionhandled';
var PENDING$2 = 0;
var FULFILLED$1 = 1;
var REJECTED$2 = 2;
var HANDLED$1 = 1;
var UNHANDLED$1 = 2;
var SUBCLASSING$1 = false;
var Internal$1, OwnPromiseCapability$1, PromiseWrapper$1, nativeThen$1;
var FORCED$9 = isForced$g(PROMISE$1, function () {
  var GLOBAL_CORE_JS_PROMISE = inspectSource$d(PromiseConstructor$1) !== String(PromiseConstructor$1); // V8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
  // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
  // We can't detect it synchronously, so just check versions

  if (!GLOBAL_CORE_JS_PROMISE && V8_VERSION$e === 66) return true; // We need Promise#finally in the pure version for preventing prototype pollution
  // deoptimization and performance degradation
  // https://github.com/zloirock/core-js/issues/679

  if (V8_VERSION$e >= 51 && /native code/.test(PromiseConstructor$1)) return false; // Detect correctness of subclassing with @@species support

  var promise = new PromiseConstructor$1(function (resolve) {
    resolve(1);
  });

  var FakePromise = function (exec) {
    exec(function () {
      /* empty */
    }, function () {
      /* empty */
    });
  };

  var constructor = promise.constructor = {};
  constructor[SPECIES$l] = FakePromise;
  SUBCLASSING$1 = promise.then(function () {
    /* empty */
  }) instanceof FakePromise;
  if (!SUBCLASSING$1) return true; // Unhandled rejections tracking support, NodeJS Promise without it fails @@species test

  return !GLOBAL_CORE_JS_PROMISE && IS_BROWSER$1 && !NATIVE_REJECTION_EVENT$1;
});
var INCORRECT_ITERATION$4 = FORCED$9 || !checkCorrectnessOfIteration$b(function (iterable) {
  PromiseConstructor$1.all(iterable)['catch'](function () {
    /* empty */
  });
}); // helpers

var isThenable$1 = function (it) {
  var then;
  return isObject$1c(it) && typeof (then = it.then) == 'function' ? then : false;
};

var notify$2 = function (state, isReject) {
  if (state.notified) return;
  state.notified = true;
  var chain = state.reactions;
  microtask$2(function () {
    var value = state.value;
    var ok = state.state == FULFILLED$1;
    var index = 0; // variable length - can't use forEach

    while (chain.length > index) {
      var reaction = chain[index++];
      var handler = ok ? reaction.ok : reaction.fail;
      var resolve = reaction.resolve;
      var reject = reaction.reject;
      var domain = reaction.domain;
      var result, then, exited;

      try {
        if (handler) {
          if (!ok) {
            if (state.rejection === UNHANDLED$1) onHandleUnhandled$1(state);
            state.rejection = HANDLED$1;
          }

          if (handler === true) result = value;else {
            if (domain) domain.enter();
            result = handler(value); // can throw

            if (domain) {
              domain.exit();
              exited = true;
            }
          }

          if (result === reaction.promise) {
            reject(TypeError$2('Promise-chain cycle'));
          } else if (then = isThenable$1(result)) {
            then.call(result, resolve, reject);
          } else resolve(result);
        } else reject(value);
      } catch (error) {
        if (domain && !exited) domain.exit();
        reject(error);
      }
    }

    state.reactions = [];
    state.notified = false;
    if (isReject && !state.rejection) onUnhandled$1(state);
  });
};

var dispatchEvent$1 = function (name, promise, reason) {
  var event, handler;

  if (DISPATCH_EVENT$1) {
    event = document$8.createEvent('Event');
    event.promise = promise;
    event.reason = reason;
    event.initEvent(name, false, true);
    global$1f.dispatchEvent(event);
  } else event = {
    promise: promise,
    reason: reason
  };

  if (!NATIVE_REJECTION_EVENT$1 && (handler = global$1f['on' + name])) handler(event);else if (name === UNHANDLED_REJECTION$1) hostReportErrors$2('Unhandled promise rejection', reason);
};

var onUnhandled$1 = function (state) {
  task$4.call(global$1f, function () {
    var promise = state.facade;
    var value = state.value;
    var IS_UNHANDLED = isUnhandled$1(state);
    var result;

    if (IS_UNHANDLED) {
      result = perform$2(function () {
        if (IS_NODE$4) {
          process$9.emit('unhandledRejection', value, promise);
        } else dispatchEvent$1(UNHANDLED_REJECTION$1, promise, value);
      }); // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should

      state.rejection = IS_NODE$4 || isUnhandled$1(state) ? UNHANDLED$1 : HANDLED$1;
      if (result.error) throw result.value;
    }
  });
};

var isUnhandled$1 = function (state) {
  return state.rejection !== HANDLED$1 && !state.parent;
};

var onHandleUnhandled$1 = function (state) {
  task$4.call(global$1f, function () {
    var promise = state.facade;

    if (IS_NODE$4) {
      process$9.emit('rejectionHandled', promise);
    } else dispatchEvent$1(REJECTION_HANDLED$1, promise, state.value);
  });
};

var bind$i = function (fn, state, unwrap) {
  return function (value) {
    fn(state, value, unwrap);
  };
};

var internalReject$1 = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  state.value = value;
  state.state = REJECTED$2;
  notify$2(state, true);
};

var internalResolve$1 = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;

  try {
    if (state.facade === value) throw TypeError$2("Promise can't be resolved itself");
    var then = isThenable$1(value);

    if (then) {
      microtask$2(function () {
        var wrapper = {
          done: false
        };

        try {
          then.call(value, bind$i(internalResolve$1, wrapper, state), bind$i(internalReject$1, wrapper, state));
        } catch (error) {
          internalReject$1(wrapper, error, state);
        }
      });
    } else {
      state.value = value;
      state.state = FULFILLED$1;
      notify$2(state, false);
    }
  } catch (error) {
    internalReject$1({
      done: false
    }, error, state);
  }
}; // constructor polyfill


if (FORCED$9) {
  // 25.4.3.1 Promise(executor)
  PromiseConstructor$1 = function Promise(executor) {
    anInstance$d(this, PromiseConstructor$1, PROMISE$1);
    aFunction$g(executor);
    Internal$1.call(this);
    var state = getInternalState$g(this);

    try {
      executor(bind$i(internalResolve$1, state), bind$i(internalReject$1, state));
    } catch (error) {
      internalReject$1(state, error);
    }
  };

  PromiseConstructorPrototype$1 = PromiseConstructor$1.prototype; // eslint-disable-next-line no-unused-vars -- required for `.length`

  Internal$1 = function Promise(executor) {
    setInternalState$h(this, {
      type: PROMISE$1,
      done: false,
      notified: false,
      parent: false,
      reactions: [],
      rejection: false,
      state: PENDING$2,
      value: undefined
    });
  };

  Internal$1.prototype = redefineAll$a(PromiseConstructorPrototype$1, {
    // `Promise.prototype.then` method
    // https://tc39.es/ecma262/#sec-promise.prototype.then
    then: function then(onFulfilled, onRejected) {
      var state = getInternalPromiseState$1(this);
      var reaction = newPromiseCapability$3(speciesConstructor$3(this, PromiseConstructor$1));
      reaction.ok = typeof onFulfilled == 'function' ? onFulfilled : true;
      reaction.fail = typeof onRejected == 'function' && onRejected;
      reaction.domain = IS_NODE$4 ? process$9.domain : undefined;
      state.parent = true;
      state.reactions.push(reaction);
      if (state.state != PENDING$2) notify$2(state, false);
      return reaction.promise;
    },
    // `Promise.prototype.catch` method
    // https://tc39.es/ecma262/#sec-promise.prototype.catch
    'catch': function (onRejected) {
      return this.then(undefined, onRejected);
    }
  });

  OwnPromiseCapability$1 = function () {
    var promise = new Internal$1();
    var state = getInternalState$g(promise);
    this.promise = promise;
    this.resolve = bind$i(internalResolve$1, state);
    this.reject = bind$i(internalReject$1, state);
  };

  newPromiseCapabilityModule$1.f = newPromiseCapability$3 = function (C) {
    return C === PromiseConstructor$1 || C === PromiseWrapper$1 ? new OwnPromiseCapability$1(C) : newGenericPromiseCapability$1(C);
  };

  if (typeof NativePromise$1 == 'function' && NativePromisePrototype$1 !== Object.prototype) {
    nativeThen$1 = NativePromisePrototype$1.then;

    if (!SUBCLASSING$1) {
      // make `Promise#then` return a polyfilled `Promise` for native promise-based APIs
      redefine$A(NativePromisePrototype$1, 'then', function then(onFulfilled, onRejected) {
        var that = this;
        return new PromiseConstructor$1(function (resolve, reject) {
          nativeThen$1.call(that, resolve, reject);
        }).then(onFulfilled, onRejected); // https://github.com/zloirock/core-js/issues/640
      }, {
        unsafe: true
      }); // makes sure that native promise-based APIs `Promise#catch` properly works with patched `Promise#then`

      redefine$A(NativePromisePrototype$1, 'catch', PromiseConstructorPrototype$1['catch'], {
        unsafe: true
      });
    } // make `.constructor === Promise` work for native promise-based APIs


    try {
      delete NativePromisePrototype$1.constructor;
    } catch (error) {}
    /* empty */
    // make `instanceof Promise` work for native promise-based APIs


    if (setPrototypeOf$9) {
      setPrototypeOf$9(NativePromisePrototype$1, PromiseConstructorPrototype$1);
    }
  }
}

$$10({
  global: true,
  wrap: true,
  forced: FORCED$9
}, {
  Promise: PromiseConstructor$1
});
setToStringTag$j(PromiseConstructor$1, PROMISE$1, false);
setSpecies$a(PROMISE$1);
PromiseWrapper$1 = getBuiltIn$n(PROMISE$1); // statics

$$10({
  target: PROMISE$1,
  stat: true,
  forced: FORCED$9
}, {
  // `Promise.reject` method
  // https://tc39.es/ecma262/#sec-promise.reject
  reject: function reject(r) {
    var capability = newPromiseCapability$3(this);
    capability.reject.call(undefined, r);
    return capability.promise;
  }
});
$$10({
  target: PROMISE$1,
  stat: true,
  forced: FORCED$9
}, {
  // `Promise.resolve` method
  // https://tc39.es/ecma262/#sec-promise.resolve
  resolve: function resolve(x) {
    return promiseResolve$2(this, x);
  }
});
$$10({
  target: PROMISE$1,
  stat: true,
  forced: INCORRECT_ITERATION$4
}, {
  // `Promise.all` method
  // https://tc39.es/ecma262/#sec-promise.all
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapability$3(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform$2(function () {
      var $promiseResolve = aFunction$g(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate$d(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        values.push(undefined);
        remaining++;
        $promiseResolve.call(C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  },
  // `Promise.race` method
  // https://tc39.es/ecma262/#sec-promise.race
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapability$3(C);
    var reject = capability.reject;
    var result = perform$2(function () {
      var $promiseResolve = aFunction$g(C.resolve);
      iterate$d(iterable, function (promise) {
        $promiseResolve.call(C, promise).then(capability.resolve, reject);
      });
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});

var win;

if (typeof window !== "undefined") {
  win = window;
} else if (typeof commonjsGlobal !== "undefined") {
  win = commonjsGlobal;
} else if (typeof self !== "undefined") {
  win = self;
} else {
  win = {};
}

var window_1 = win;

var check$3 = function (it) {
  return it && it.Math == Math && it;
}; // https://github.com/zloirock/core-js/issues/86#issuecomment-115759028


var global$1e = // eslint-disable-next-line es/no-global-this -- safe
check$3(typeof globalThis == 'object' && globalThis) || check$3(typeof window == 'object' && window) || // eslint-disable-next-line no-restricted-globals -- safe
check$3(typeof self == 'object' && self) || check$3(typeof commonjsGlobal == 'object' && commonjsGlobal) || // eslint-disable-next-line no-new-func -- fallback
function () {
  return this;
}() || Function('return this')();

var objectGetOwnPropertyDescriptor$3 = {};

var fails$1i = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$1h = fails$1i; // Detect IE8's incomplete defineProperty implementation


var descriptors$3 = !fails$1h(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, {
    get: function () {
      return 7;
    }
  })[1] != 7;
});

var objectPropertyIsEnumerable$3 = {};

var $propertyIsEnumerable$6 = {}.propertyIsEnumerable; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe

var getOwnPropertyDescriptor$a = Object.getOwnPropertyDescriptor; // Nashorn ~ JDK8 bug

var NASHORN_BUG$3 = getOwnPropertyDescriptor$a && !$propertyIsEnumerable$6.call({
  1: 2
}, 1); // `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable

objectPropertyIsEnumerable$3.f = NASHORN_BUG$3 ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$a(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable$6;

var createPropertyDescriptor$n = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var toString$g = {}.toString;

var classofRaw$7 = function (it) {
  return toString$g.call(it).slice(8, -1);
};

var fails$1g = fails$1i;

var classof$r = classofRaw$7;

var split$3 = ''.split; // fallback for non-array-like ES3 and non-enumerable old V8 strings

var indexedObject$3 = fails$1g(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$r(it) == 'String' ? split$3.call(it, '') : Object(it);
} : Object;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$o = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$9 = indexedObject$3;

var requireObjectCoercible$n = requireObjectCoercible$o;

var toIndexedObject$w = function (it) {
  return IndexedObject$9(requireObjectCoercible$n(it));
};

var isObject$1b = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};

var isObject$1a = isObject$1b; // `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string


var toPrimitive$k = function (input, PREFERRED_STRING) {
  if (!isObject$1a(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$1a(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject$1a(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$1a(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};

var requireObjectCoercible$m = requireObjectCoercible$o; // `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject


var toObject$z = function (argument) {
  return Object(requireObjectCoercible$m(argument));
};

var toObject$y = toObject$z;

var hasOwnProperty$p = {}.hasOwnProperty;

var has$V = function hasOwn(it, key) {
  return hasOwnProperty$p.call(toObject$y(it), key);
};

var global$1d = global$1e;

var isObject$19 = isObject$1b;

var document$7 = global$1d.document; // typeof document.createElement is 'object' in old IE

var EXISTS$3 = isObject$19(document$7) && isObject$19(document$7.createElement);

var documentCreateElement$7 = function (it) {
  return EXISTS$3 ? document$7.createElement(it) : {};
};

var DESCRIPTORS$G = descriptors$3;

var fails$1f = fails$1i;

var createElement$5 = documentCreateElement$7; // Thank's IE8 for his funny defineProperty


var ie8DomDefine$3 = !DESCRIPTORS$G && !fails$1f(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement$5('div'), 'a', {
    get: function () {
      return 7;
    }
  }).a != 7;
});

var DESCRIPTORS$F = descriptors$3;

var propertyIsEnumerableModule$8 = objectPropertyIsEnumerable$3;

var createPropertyDescriptor$m = createPropertyDescriptor$n;

var toIndexedObject$v = toIndexedObject$w;

var toPrimitive$j = toPrimitive$k;

var has$U = has$V;

var IE8_DOM_DEFINE$7 = ie8DomDefine$3; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe


var $getOwnPropertyDescriptor$6 = Object.getOwnPropertyDescriptor; // `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor

objectGetOwnPropertyDescriptor$3.f = DESCRIPTORS$F ? $getOwnPropertyDescriptor$6 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$v(O);
  P = toPrimitive$j(P, true);
  if (IE8_DOM_DEFINE$7) try {
    return $getOwnPropertyDescriptor$6(O, P);
  } catch (error) {
    /* empty */
  }
  if (has$U(O, P)) return createPropertyDescriptor$m(!propertyIsEnumerableModule$8.f.call(O, P), O[P]);
};

var objectDefineProperty$3 = {};

var isObject$18 = isObject$1b;

var anObject$O = function (it) {
  if (!isObject$18(it)) {
    throw TypeError(String(it) + ' is not an object');
  }

  return it;
};

var DESCRIPTORS$E = descriptors$3;

var IE8_DOM_DEFINE$6 = ie8DomDefine$3;

var anObject$N = anObject$O;

var toPrimitive$i = toPrimitive$k; // eslint-disable-next-line es/no-object-defineproperty -- safe


var $defineProperty$6 = Object.defineProperty; // `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty

objectDefineProperty$3.f = DESCRIPTORS$E ? $defineProperty$6 : function defineProperty(O, P, Attributes) {
  anObject$N(O);
  P = toPrimitive$i(P, true);
  anObject$N(Attributes);
  if (IE8_DOM_DEFINE$6) try {
    return $defineProperty$6(O, P, Attributes);
  } catch (error) {
    /* empty */
  }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$D = descriptors$3;

var definePropertyModule$q = objectDefineProperty$3;

var createPropertyDescriptor$l = createPropertyDescriptor$n;

var createNonEnumerableProperty$E = DESCRIPTORS$D ? function (object, key, value) {
  return definePropertyModule$q.f(object, key, createPropertyDescriptor$l(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var redefine$z = {exports: {}};

var global$1c = global$1e;

var createNonEnumerableProperty$D = createNonEnumerableProperty$E;

var setGlobal$f = function (key, value) {
  try {
    createNonEnumerableProperty$D(global$1c, key, value);
  } catch (error) {
    global$1c[key] = value;
  }

  return value;
};

var global$1b = global$1e;

var setGlobal$e = setGlobal$f;

var SHARED$3 = '__core-js_shared__';
var store$f = global$1b[SHARED$3] || setGlobal$e(SHARED$3, {});
var sharedStore$3 = store$f;

var store$e = sharedStore$3;

var functionToString$4 = Function.toString; // this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper

if (typeof store$e.inspectSource != 'function') {
  store$e.inspectSource = function (it) {
    return functionToString$4.call(it);
  };
}

var inspectSource$c = store$e.inspectSource;

var global$1a = global$1e;

var inspectSource$b = inspectSource$c;

var WeakMap$c = global$1a.WeakMap;
var nativeWeakMap$3 = typeof WeakMap$c === 'function' && /native code/.test(inspectSource$b(WeakMap$c));

var shared$n = {exports: {}};

var store$d = sharedStore$3;

(shared$n.exports = function (key, value) {
  return store$d[key] || (store$d[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.12.1',
  mode: 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});

var id$9 = 0;
var postfix$3 = Math.random();

var uid$j = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id$9 + postfix$3).toString(36);
};

var shared$m = shared$n.exports;

var uid$i = uid$j;

var keys$c = shared$m('keys');

var sharedKey$i = function (key) {
  return keys$c[key] || (keys$c[key] = uid$i(key));
};

var hiddenKeys$r = {};

var NATIVE_WEAK_MAP$4 = nativeWeakMap$3;

var global$19 = global$1e;

var isObject$17 = isObject$1b;

var createNonEnumerableProperty$C = createNonEnumerableProperty$E;

var objectHas$3 = has$V;

var shared$l = sharedStore$3;

var sharedKey$h = sharedKey$i;

var hiddenKeys$q = hiddenKeys$r;

var OBJECT_ALREADY_INITIALIZED$3 = 'Object already initialized';
var WeakMap$b = global$19.WeakMap;
var set$5, get$5, has$T;

var enforce$3 = function (it) {
  return has$T(it) ? get$5(it) : set$5(it, {});
};

var getterFor$3 = function (TYPE) {
  return function (it) {
    var state;

    if (!isObject$17(it) || (state = get$5(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    }

    return state;
  };
};

if (NATIVE_WEAK_MAP$4 || shared$l.state) {
  var store$c = shared$l.state || (shared$l.state = new WeakMap$b());
  var wmget$3 = store$c.get;
  var wmhas$3 = store$c.has;
  var wmset$3 = store$c.set;

  set$5 = function (it, metadata) {
    if (wmhas$3.call(store$c, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$3);
    metadata.facade = it;
    wmset$3.call(store$c, it, metadata);
    return metadata;
  };

  get$5 = function (it) {
    return wmget$3.call(store$c, it) || {};
  };

  has$T = function (it) {
    return wmhas$3.call(store$c, it);
  };
} else {
  var STATE$3 = sharedKey$h('state');
  hiddenKeys$q[STATE$3] = true;

  set$5 = function (it, metadata) {
    if (objectHas$3(it, STATE$3)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$3);
    metadata.facade = it;
    createNonEnumerableProperty$C(it, STATE$3, metadata);
    return metadata;
  };

  get$5 = function (it) {
    return objectHas$3(it, STATE$3) ? it[STATE$3] : {};
  };

  has$T = function (it) {
    return objectHas$3(it, STATE$3);
  };
}

var internalState$3 = {
  set: set$5,
  get: get$5,
  has: has$T,
  enforce: enforce$3,
  getterFor: getterFor$3
};

var global$18 = global$1e;

var createNonEnumerableProperty$B = createNonEnumerableProperty$E;

var has$S = has$V;

var setGlobal$d = setGlobal$f;

var inspectSource$a = inspectSource$c;

var InternalStateModule$k = internalState$3;

var getInternalState$f = InternalStateModule$k.get;
var enforceInternalState$5 = InternalStateModule$k.enforce;
var TEMPLATE$3 = String(String).split('String');
(redefine$z.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var state;

  if (typeof value == 'function') {
    if (typeof key == 'string' && !has$S(value, 'name')) {
      createNonEnumerableProperty$B(value, 'name', key);
    }

    state = enforceInternalState$5(value);

    if (!state.source) {
      state.source = TEMPLATE$3.join(typeof key == 'string' ? key : '');
    }
  }

  if (O === global$18) {
    if (simple) O[key] = value;else setGlobal$d(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }

  if (simple) O[key] = value;else createNonEnumerableProperty$B(O, key, value); // add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState$f(this).source || inspectSource$a(this);
});

var global$17 = global$1e;

var path$a = global$17;

var path$9 = path$a;

var global$16 = global$1e;

var aFunction$f = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

var getBuiltIn$m = function (namespace, method) {
  return arguments.length < 2 ? aFunction$f(path$9[namespace]) || aFunction$f(global$16[namespace]) : path$9[namespace] && path$9[namespace][method] || global$16[namespace] && global$16[namespace][method];
};

var objectGetOwnPropertyNames$3 = {};

var ceil$3 = Math.ceil;
var floor$5 = Math.floor; // `ToInteger` abstract operation
// https://tc39.es/ecma262/#sec-tointeger

var toInteger$h = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor$5 : ceil$3)(argument);
};

var toInteger$g = toInteger$h;

var min$b = Math.min; // `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength

var toLength$u = function (argument) {
  return argument > 0 ? min$b(toInteger$g(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toInteger$f = toInteger$h;

var max$9 = Math.max;
var min$a = Math.min; // Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).

var toAbsoluteIndex$c = function (index, length) {
  var integer = toInteger$f(index);
  return integer < 0 ? max$9(integer + length, 0) : min$a(integer, length);
};

var toIndexedObject$u = toIndexedObject$w;

var toLength$t = toLength$u;

var toAbsoluteIndex$b = toAbsoluteIndex$c; // `Array.prototype.{ indexOf, includes }` methods implementation


var createMethod$d = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$u($this);
    var length = toLength$t(O.length);
    var index = toAbsoluteIndex$b(fromIndex, length);
    var value; // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check

    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++]; // eslint-disable-next-line no-self-compare -- NaN check

      if (value != value) return true; // Array#indexOf ignores holes, Array#includes - not
    } else for (; length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    }
    return !IS_INCLUDES && -1;
  };
};

var arrayIncludes$3 = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$d(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$d(false)
};

var has$R = has$V;

var toIndexedObject$t = toIndexedObject$w;

var indexOf$4 = arrayIncludes$3.indexOf;

var hiddenKeys$p = hiddenKeys$r;

var objectKeysInternal$3 = function (object, names) {
  var O = toIndexedObject$t(object);
  var i = 0;
  var result = [];
  var key;

  for (key in O) !has$R(hiddenKeys$p, key) && has$R(O, key) && result.push(key); // Don't enum bug & hidden keys


  while (names.length > i) if (has$R(O, key = names[i++])) {
    ~indexOf$4(result, key) || result.push(key);
  }

  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$f = ['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf'];

var internalObjectKeys$7 = objectKeysInternal$3;

var enumBugKeys$e = enumBugKeys$f;

var hiddenKeys$o = enumBugKeys$e.concat('length', 'prototype'); // `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe

objectGetOwnPropertyNames$3.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$7(O, hiddenKeys$o);
};

var objectGetOwnPropertySymbols$3 = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols$3.f = Object.getOwnPropertySymbols;

var getBuiltIn$l = getBuiltIn$m;

var getOwnPropertyNamesModule$6 = objectGetOwnPropertyNames$3;

var getOwnPropertySymbolsModule$8 = objectGetOwnPropertySymbols$3;

var anObject$M = anObject$O; // all object keys, includes non-enumerable and symbols


var ownKeys$7 = getBuiltIn$l('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule$6.f(anObject$M(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$8.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};

var has$Q = has$V;

var ownKeys$6 = ownKeys$7;

var getOwnPropertyDescriptorModule$6 = objectGetOwnPropertyDescriptor$3;

var definePropertyModule$p = objectDefineProperty$3;

var copyConstructorProperties$a = function (target, source) {
  var keys = ownKeys$6(source);
  var defineProperty = definePropertyModule$p.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule$6.f;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has$Q(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};

var fails$1e = fails$1i;

var replacement$3 = /#|\.prototype\./;

var isForced$f = function (feature, detection) {
  var value = data$3[normalize$3(feature)];
  return value == POLYFILL$3 ? true : value == NATIVE$3 ? false : typeof detection == 'function' ? fails$1e(detection) : !!detection;
};

var normalize$3 = isForced$f.normalize = function (string) {
  return String(string).replace(replacement$3, '.').toLowerCase();
};

var data$3 = isForced$f.data = {};
var NATIVE$3 = isForced$f.NATIVE = 'N';
var POLYFILL$3 = isForced$f.POLYFILL = 'P';
var isForced_1$3 = isForced$f;

var global$15 = global$1e;

var getOwnPropertyDescriptor$9 = objectGetOwnPropertyDescriptor$3.f;

var createNonEnumerableProperty$A = createNonEnumerableProperty$E;

var redefine$y = redefine$z.exports;

var setGlobal$c = setGlobal$f;

var copyConstructorProperties$9 = copyConstructorProperties$a;

var isForced$e = isForced_1$3;
/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/


var _export$3 = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;

  if (GLOBAL) {
    target = global$15;
  } else if (STATIC) {
    target = global$15[TARGET] || setGlobal$c(TARGET, {});
  } else {
    target = (global$15[TARGET] || {}).prototype;
  }

  if (target) for (key in source) {
    sourceProperty = source[key];

    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor$9(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];

    FORCED = isForced$e(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced); // contained in target

    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties$9(sourceProperty, targetProperty);
    } // add a flag to not completely full polyfills


    if (options.sham || targetProperty && targetProperty.sham) {
      createNonEnumerableProperty$A(sourceProperty, 'sham', true);
    } // extend global


    redefine$y(target, key, sourceProperty, options);
  }
};

var fails$1d = fails$1i;

var freezing$4 = !fails$1d(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});

var internalMetadata$4 = {exports: {}};

var hiddenKeys$n = hiddenKeys$r;

var isObject$16 = isObject$1b;

var has$P = has$V;

var defineProperty$s = objectDefineProperty$3.f;

var uid$h = uid$j;

var FREEZING$7 = freezing$4;

var METADATA$4 = uid$h('meta');
var id$8 = 0; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible$5 = Object.isExtensible || function () {
  return true;
};

var setMetadata$4 = function (it) {
  defineProperty$s(it, METADATA$4, {
    value: {
      objectID: 'O' + ++id$8,
      // object ID
      weakData: {} // weak collections IDs

    }
  });
};

var fastKey$8 = function (it, create) {
  // return a primitive with prefix
  if (!isObject$16(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;

  if (!has$P(it, METADATA$4)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$5(it)) return 'F'; // not necessary to add metadata

    if (!create) return 'E'; // add missing metadata

    setMetadata$4(it); // return object ID
  }

  return it[METADATA$4].objectID;
};

var getWeakData$5 = function (it, create) {
  if (!has$P(it, METADATA$4)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$5(it)) return true; // not necessary to add metadata

    if (!create) return false; // add missing metadata

    setMetadata$4(it); // return the store of weak collections IDs
  }

  return it[METADATA$4].weakData;
}; // add metadata on freeze-family methods calling


var onFreeze$7 = function (it) {
  if (FREEZING$7 && meta$4.REQUIRED && isExtensible$5(it) && !has$P(it, METADATA$4)) setMetadata$4(it);
  return it;
};

var meta$4 = internalMetadata$4.exports = {
  REQUIRED: false,
  fastKey: fastKey$8,
  getWeakData: getWeakData$5,
  onFreeze: onFreeze$7
};
hiddenKeys$n[METADATA$4] = true;

var $$$ = _export$3;

var FREEZING$6 = freezing$4;

var fails$1c = fails$1i;

var isObject$15 = isObject$1b;

var onFreeze$6 = internalMetadata$4.exports.onFreeze; // eslint-disable-next-line es/no-object-freeze -- safe


var $freeze$2 = Object.freeze;
var FAILS_ON_PRIMITIVES$7 = fails$1c(function () {
  $freeze$2(1);
}); // `Object.freeze` method
// https://tc39.es/ecma262/#sec-object.freeze

$$$({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$7,
  sham: !FREEZING$6
}, {
  freeze: function freeze(it) {
    return $freeze$2 && isObject$15(it) ? $freeze$2(onFreeze$6(it)) : it;
  }
});

var classof$q = classofRaw$7; // `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe


var isArray$B = Array.isArray || function isArray(arg) {
  return classof$q(arg) == 'Array';
};

var toPrimitive$h = toPrimitive$k;

var definePropertyModule$o = objectDefineProperty$3;

var createPropertyDescriptor$k = createPropertyDescriptor$n;

var createProperty$f = function (object, key, value) {
  var propertyKey = toPrimitive$h(key);
  if (propertyKey in object) definePropertyModule$o.f(object, propertyKey, createPropertyDescriptor$k(0, value));else object[propertyKey] = value;
};

var getBuiltIn$k = getBuiltIn$m;

var engineUserAgent$3 = getBuiltIn$k('navigator', 'userAgent') || '';

var global$14 = global$1e;

var userAgent$6 = engineUserAgent$3;

var process$8 = global$14.process;
var versions$3 = process$8 && process$8.versions;
var v8$3 = versions$3 && versions$3.v8;
var match$4, version$3;

if (v8$3) {
  match$4 = v8$3.split('.');
  version$3 = match$4[0] < 4 ? 1 : match$4[0] + match$4[1];
} else if (userAgent$6) {
  match$4 = userAgent$6.match(/Edge\/(\d+)/);

  if (!match$4 || match$4[1] >= 74) {
    match$4 = userAgent$6.match(/Chrome\/(\d+)/);
    if (match$4) version$3 = match$4[1];
  }
}

var engineV8Version$3 = version$3 && +version$3;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION$d = engineV8Version$3;

var fails$1b = fails$1i; // eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing


var nativeSymbol$3 = !!Object.getOwnPropertySymbols && !fails$1b(function () {
  return !String(Symbol()) || // Chrome 38 Symbol has incorrect toString conversion
  // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
  !Symbol.sham && V8_VERSION$d && V8_VERSION$d < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$a = nativeSymbol$3;

var useSymbolAsUid$3 = NATIVE_SYMBOL$a && !Symbol.sham && typeof Symbol.iterator == 'symbol';

var global$13 = global$1e;

var shared$k = shared$n.exports;

var has$O = has$V;

var uid$g = uid$j;

var NATIVE_SYMBOL$9 = nativeSymbol$3;

var USE_SYMBOL_AS_UID$6 = useSymbolAsUid$3;

var WellKnownSymbolsStore$6 = shared$k('wks');
var Symbol$i = global$13.Symbol;
var createWellKnownSymbol$3 = USE_SYMBOL_AS_UID$6 ? Symbol$i : Symbol$i && Symbol$i.withoutSetter || uid$g;

var wellKnownSymbol$1d = function (name) {
  if (!has$O(WellKnownSymbolsStore$6, name) || !(NATIVE_SYMBOL$9 || typeof WellKnownSymbolsStore$6[name] == 'string')) {
    if (NATIVE_SYMBOL$9 && has$O(Symbol$i, name)) {
      WellKnownSymbolsStore$6[name] = Symbol$i[name];
    } else {
      WellKnownSymbolsStore$6[name] = createWellKnownSymbol$3('Symbol.' + name);
    }
  }

  return WellKnownSymbolsStore$6[name];
};

var fails$1a = fails$1i;

var wellKnownSymbol$1c = wellKnownSymbol$1d;

var V8_VERSION$c = engineV8Version$3;

var SPECIES$k = wellKnownSymbol$1c('species');

var arrayMethodHasSpeciesSupport$i = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION$c >= 51 || !fails$1a(function () {
    var array = [];
    var constructor = array.constructor = {};

    constructor[SPECIES$k] = function () {
      return {
        foo: 1
      };
    };

    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};

var $$_ = _export$3;

var isObject$14 = isObject$1b;

var isArray$A = isArray$B;

var toAbsoluteIndex$a = toAbsoluteIndex$c;

var toLength$s = toLength$u;

var toIndexedObject$s = toIndexedObject$w;

var createProperty$e = createProperty$f;

var wellKnownSymbol$1b = wellKnownSymbol$1d;

var arrayMethodHasSpeciesSupport$h = arrayMethodHasSpeciesSupport$i;

var HAS_SPECIES_SUPPORT$9 = arrayMethodHasSpeciesSupport$h('slice');
var SPECIES$j = wellKnownSymbol$1b('species');
var nativeSlice$3 = [].slice;
var max$8 = Math.max; // `Array.prototype.slice` method
// https://tc39.es/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects

$$_({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$9
}, {
  slice: function slice(start, end) {
    var O = toIndexedObject$s(this);
    var length = toLength$s(O.length);
    var k = toAbsoluteIndex$a(start, length);
    var fin = toAbsoluteIndex$a(end === undefined ? length : end, length); // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible

    var Constructor, result, n;

    if (isArray$A(O)) {
      Constructor = O.constructor; // cross-realm fallback

      if (typeof Constructor == 'function' && (Constructor === Array || isArray$A(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject$14(Constructor)) {
        Constructor = Constructor[SPECIES$j];
        if (Constructor === null) Constructor = undefined;
      }

      if (Constructor === Array || Constructor === undefined) {
        return nativeSlice$3.call(O, k, fin);
      }
    }

    result = new (Constructor === undefined ? Array : Constructor)(max$8(fin - k, 0));

    for (n = 0; k < fin; k++, n++) if (k in O) createProperty$e(result, n, O[k]);

    result.length = n;
    return result;
  }
});

var wellKnownSymbol$1a = wellKnownSymbol$1d;

var TO_STRING_TAG$e = wellKnownSymbol$1a('toStringTag');
var test$4 = {};
test$4[TO_STRING_TAG$e] = 'z';
var toStringTagSupport$3 = String(test$4) === '[object z]';

var TO_STRING_TAG_SUPPORT$b = toStringTagSupport$3;

var classofRaw$6 = classofRaw$7;

var wellKnownSymbol$19 = wellKnownSymbol$1d;

var TO_STRING_TAG$d = wellKnownSymbol$19('toStringTag'); // ES3 wrong here

var CORRECT_ARGUMENTS$3 = classofRaw$6(function () {
  return arguments;
}()) == 'Arguments'; // fallback for IE11 Script Access Denied error

var tryGet$3 = function (it, key) {
  try {
    return it[key];
  } catch (error) {
    /* empty */
  }
}; // getting tag from ES6+ `Object.prototype.toString`


var classof$p = TO_STRING_TAG_SUPPORT$b ? classofRaw$6 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null' // @@toStringTag case
  : typeof (tag = tryGet$3(O = Object(it), TO_STRING_TAG$d)) == 'string' ? tag // builtinTag case
  : CORRECT_ARGUMENTS$3 ? classofRaw$6(O) // ES3 arguments fallback
  : (result = classofRaw$6(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};

var TO_STRING_TAG_SUPPORT$a = toStringTagSupport$3;

var classof$o = classof$p; // `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring


var objectToString$a = TO_STRING_TAG_SUPPORT$a ? {}.toString : function toString() {
  return '[object ' + classof$o(this) + ']';
};

var TO_STRING_TAG_SUPPORT$9 = toStringTagSupport$3;

var redefine$x = redefine$z.exports;

var toString$f = objectToString$a; // `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring


if (!TO_STRING_TAG_SUPPORT$9) {
  redefine$x(Object.prototype, 'toString', toString$f, {
    unsafe: true
  });
}

var anObject$L = anObject$O; // `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags


var regexpFlags$8 = function () {
  var that = anObject$L(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};

var redefine$w = redefine$z.exports;

var anObject$K = anObject$O;

var fails$19 = fails$1i;

var flags$2 = regexpFlags$8;

var TO_STRING$2 = 'toString';
var RegExpPrototype$7 = RegExp.prototype;
var nativeToString$2 = RegExpPrototype$7[TO_STRING$2];
var NOT_GENERIC$2 = fails$19(function () {
  return nativeToString$2.call({
    source: 'a',
    flags: 'b'
  }) != '/a/b';
}); // FF44- RegExp#toString has a wrong name

var INCORRECT_NAME$2 = nativeToString$2.name != TO_STRING$2; // `RegExp.prototype.toString` method
// https://tc39.es/ecma262/#sec-regexp.prototype.tostring

if (NOT_GENERIC$2 || INCORRECT_NAME$2) {
  redefine$w(RegExp.prototype, TO_STRING$2, function toString() {
    var R = anObject$K(this);
    var p = String(R.source);
    var rf = R.flags;
    var f = String(rf === undefined && R instanceof RegExp && !('flags' in RegExpPrototype$7) ? flags$2.call(R) : rf);
    return '/' + p + '/' + f;
  }, {
    unsafe: true
  });
}

var aFunction$e = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  }

  return it;
};

var aFunction$d = aFunction$e; // optional / simple context binding


var functionBindContext$3 = function (fn, that, length) {
  aFunction$d(fn);
  if (that === undefined) return fn;

  switch (length) {
    case 0:
      return function () {
        return fn.call(that);
      };

    case 1:
      return function (a) {
        return fn.call(that, a);
      };

    case 2:
      return function (a, b) {
        return fn.call(that, a, b);
      };

    case 3:
      return function (a, b, c) {
        return fn.call(that, a, b, c);
      };
  }

  return function ()
  /* ...args */
  {
    return fn.apply(that, arguments);
  };
};

var getBuiltIn$j = getBuiltIn$m;

var html$9 = getBuiltIn$j('document', 'documentElement');

var userAgent$5 = engineUserAgent$3;

var engineIsIos$1 = /(?:iphone|ipod|ipad).*applewebkit/i.test(userAgent$5);

var classof$n = classofRaw$7;

var global$12 = global$1e;

var engineIsNode$1 = classof$n(global$12.process) == 'process';

var global$11 = global$1e;

var fails$18 = fails$1i;

var bind$h = functionBindContext$3;

var html$8 = html$9;

var createElement$4 = documentCreateElement$7;

var IS_IOS$2 = engineIsIos$1;

var IS_NODE$3 = engineIsNode$1;

var location$1 = global$11.location;
var set$4 = global$11.setImmediate;
var clear$1 = global$11.clearImmediate;
var process$7 = global$11.process;
var MessageChannel$1 = global$11.MessageChannel;
var Dispatch$1 = global$11.Dispatch;
var counter$1 = 0;
var queue$1 = {};
var ONREADYSTATECHANGE$1 = 'onreadystatechange';
var defer$1, channel$1, port$1;

var run$1 = function (id) {
  // eslint-disable-next-line no-prototype-builtins -- safe
  if (queue$1.hasOwnProperty(id)) {
    var fn = queue$1[id];
    delete queue$1[id];
    fn();
  }
};

var runner$1 = function (id) {
  return function () {
    run$1(id);
  };
};

var listener$1 = function (event) {
  run$1(event.data);
};

var post$1 = function (id) {
  // old engines have not location.origin
  global$11.postMessage(id + '', location$1.protocol + '//' + location$1.host);
}; // Node.js 0.9+ & IE10+ has setImmediate, otherwise:


if (!set$4 || !clear$1) {
  set$4 = function setImmediate(fn) {
    var args = [];
    var i = 1;

    while (arguments.length > i) args.push(arguments[i++]);

    queue$1[++counter$1] = function () {
      // eslint-disable-next-line no-new-func -- spec requirement
      (typeof fn == 'function' ? fn : Function(fn)).apply(undefined, args);
    };

    defer$1(counter$1);
    return counter$1;
  };

  clear$1 = function clearImmediate(id) {
    delete queue$1[id];
  }; // Node.js 0.8-


  if (IS_NODE$3) {
    defer$1 = function (id) {
      process$7.nextTick(runner$1(id));
    }; // Sphere (JS game engine) Dispatch API

  } else if (Dispatch$1 && Dispatch$1.now) {
    defer$1 = function (id) {
      Dispatch$1.now(runner$1(id));
    }; // Browsers with MessageChannel, includes WebWorkers
    // except iOS - https://github.com/zloirock/core-js/issues/624

  } else if (MessageChannel$1 && !IS_IOS$2) {
    channel$1 = new MessageChannel$1();
    port$1 = channel$1.port2;
    channel$1.port1.onmessage = listener$1;
    defer$1 = bind$h(port$1.postMessage, port$1, 1); // Browsers with postMessage, skip WebWorkers
    // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (global$11.addEventListener && typeof postMessage == 'function' && !global$11.importScripts && location$1 && location$1.protocol !== 'file:' && !fails$18(post$1)) {
    defer$1 = post$1;
    global$11.addEventListener('message', listener$1, false); // IE8-
  } else if (ONREADYSTATECHANGE$1 in createElement$4('script')) {
    defer$1 = function (id) {
      html$8.appendChild(createElement$4('script'))[ONREADYSTATECHANGE$1] = function () {
        html$8.removeChild(this);
        run$1(id);
      };
    }; // Rest old browsers

  } else {
    defer$1 = function (id) {
      setTimeout(runner$1(id), 0);
    };
  }
}

var task$3 = {
  set: set$4,
  clear: clear$1
};

var $$Z = _export$3;

var global$10 = global$1e;

var task$2 = task$3;

var FORCED$8 = !global$10.setImmediate || !global$10.clearImmediate; // http://w3c.github.io/setImmediate/

$$Z({
  global: true,
  bind: true,
  enumerable: true,
  forced: FORCED$8
}, {
  // `setImmediate` method
  // http://w3c.github.io/setImmediate/#si-setImmediate
  setImmediate: task$2.set,
  // `clearImmediate` method
  // http://w3c.github.io/setImmediate/#si-clearImmediate
  clearImmediate: task$2.clear
});

var internalObjectKeys$6 = objectKeysInternal$3;

var enumBugKeys$d = enumBugKeys$f; // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe


var objectKeys$e = Object.keys || function keys(O) {
  return internalObjectKeys$6(O, enumBugKeys$d);
};

var $$Y = _export$3;

var toObject$x = toObject$z;

var nativeKeys$7 = objectKeys$e;

var fails$17 = fails$1i;

var FAILS_ON_PRIMITIVES$6 = fails$17(function () {
  nativeKeys$7(1);
}); // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys

$$Y({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$6
}, {
  keys: function keys(it) {
    return nativeKeys$7(toObject$x(it));
  }
});

var isObject$13 = isObject$1b;

var isArray$z = isArray$B;

var wellKnownSymbol$18 = wellKnownSymbol$1d;

var SPECIES$i = wellKnownSymbol$18('species'); // `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate

var arraySpeciesCreate$a = function (originalArray, length) {
  var C;

  if (isArray$z(originalArray)) {
    C = originalArray.constructor; // cross-realm fallback

    if (typeof C == 'function' && (C === Array || isArray$z(C.prototype))) C = undefined;else if (isObject$13(C)) {
      C = C[SPECIES$i];
      if (C === null) C = undefined;
    }
  }

  return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};

var bind$g = functionBindContext$3;

var IndexedObject$8 = indexedObject$3;

var toObject$w = toObject$z;

var toLength$r = toLength$u;

var arraySpeciesCreate$9 = arraySpeciesCreate$a;

var push$3 = [].push; // `Array.prototype.{ forEach, map, filter, some, every, find, findIndex, filterOut }` methods implementation

var createMethod$c = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var IS_FILTER_OUT = TYPE == 7;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject$w($this);
    var self = IndexedObject$8(O);
    var boundFunction = bind$g(callbackfn, that, 3);
    var length = toLength$r(self.length);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate$9;
    var target = IS_MAP ? create($this, length) : IS_FILTER || IS_FILTER_OUT ? create($this, 0) : undefined;
    var value, result;

    for (; length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);

      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
            case 3:
              return true;
            // some

            case 5:
              return value;
            // find

            case 6:
              return index;
            // findIndex

            case 2:
              push$3.call(target, value);
            // filter
          } else switch (TYPE) {
            case 4:
              return false;
            // every

            case 7:
              push$3.call(target, value);
            // filterOut
          }
      }
    }

    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

var arrayIteration$2 = {
  // `Array.prototype.forEach` method
  // https://tc39.es/ecma262/#sec-array.prototype.foreach
  forEach: createMethod$c(0),
  // `Array.prototype.map` method
  // https://tc39.es/ecma262/#sec-array.prototype.map
  map: createMethod$c(1),
  // `Array.prototype.filter` method
  // https://tc39.es/ecma262/#sec-array.prototype.filter
  filter: createMethod$c(2),
  // `Array.prototype.some` method
  // https://tc39.es/ecma262/#sec-array.prototype.some
  some: createMethod$c(3),
  // `Array.prototype.every` method
  // https://tc39.es/ecma262/#sec-array.prototype.every
  every: createMethod$c(4),
  // `Array.prototype.find` method
  // https://tc39.es/ecma262/#sec-array.prototype.find
  find: createMethod$c(5),
  // `Array.prototype.findIndex` method
  // https://tc39.es/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod$c(6),
  // `Array.prototype.filterOut` method
  // https://github.com/tc39/proposal-array-filtering
  filterOut: createMethod$c(7)
};

var $$X = _export$3;

var $filter$2 = arrayIteration$2.filter;

var arrayMethodHasSpeciesSupport$g = arrayMethodHasSpeciesSupport$i;

var HAS_SPECIES_SUPPORT$8 = arrayMethodHasSpeciesSupport$g('filter'); // `Array.prototype.filter` method
// https://tc39.es/ecma262/#sec-array.prototype.filter
// with adding support of @@species

$$X({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$8
}, {
  filter: function filter(callbackfn
  /* , thisArg */
  ) {
    return $filter$2(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables$3 = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

var fails$16 = fails$1i;

var arrayMethodIsStrict$7 = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails$16(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal -- required for testing
    method.call(null, argument || function () {
      throw 1;
    }, 1);
  });
};

var $forEach$5 = arrayIteration$2.forEach;

var arrayMethodIsStrict$6 = arrayMethodIsStrict$7;

var STRICT_METHOD$4 = arrayMethodIsStrict$6('forEach'); // `Array.prototype.forEach` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.foreach

var arrayForEach$2 = !STRICT_METHOD$4 ? function forEach(callbackfn
/* , thisArg */
) {
  return $forEach$5(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined); // eslint-disable-next-line es/no-array-prototype-foreach -- safe
} : [].forEach;

var global$$ = global$1e;

var DOMIterables$6 = domIterables$3;

var forEach$2 = arrayForEach$2;

var createNonEnumerableProperty$z = createNonEnumerableProperty$E;

for (var COLLECTION_NAME$6 in DOMIterables$6) {
  var Collection$6 = global$$[COLLECTION_NAME$6];
  var CollectionPrototype$6 = Collection$6 && Collection$6.prototype; // some Chrome versions have non-configurable methods on DOMTokenList

  if (CollectionPrototype$6 && CollectionPrototype$6.forEach !== forEach$2) try {
    createNonEnumerableProperty$z(CollectionPrototype$6, 'forEach', forEach$2);
  } catch (error) {
    CollectionPrototype$6.forEach = forEach$2;
  }
}

/**
 * For Node.js, simply re-export the core `util.deprecate` function.
 */

var node$1 = require$$0$2.deprecate;

var dist$1 = {};

Object.defineProperty(dist$1, "__esModule", {
  value: true
});
dist$1.dedent = void 0;

function dedent(templ) {
  var values = [];

  for (var _i = 1; _i < arguments.length; _i++) {
    values[_i - 1] = arguments[_i];
  }

  var strings = Array.from(typeof templ === 'string' ? [templ] : templ);
  strings[strings.length - 1] = strings[strings.length - 1].replace(/\r?\n([\t ]*)$/, '');
  var indentLengths = strings.reduce(function (arr, str) {
    var matches = str.match(/\n([\t ]+|(?!\s).)/g);

    if (matches) {
      return arr.concat(matches.map(function (match) {
        var _a, _b;

        return (_b = (_a = match.match(/[\t ]/g)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
      }));
    }

    return arr;
  }, []);

  if (indentLengths.length) {
    var pattern_1 = new RegExp("\n[\t ]{" + Math.min.apply(Math, indentLengths) + "}", 'g');
    strings = strings.map(function (str) {
      return str.replace(pattern_1, '\n');
    });
  }

  strings[0] = strings[0].replace(/^\r?\n/, '');
  var string = strings[0];
  values.forEach(function (value, i) {
    string += value + strings[i + 1];
  });
  return string;
}

dist$1.dedent = dedent;
var _default = dist$1.default = dedent;

var _templateObject$a;

function _taggedTemplateLiteral$a(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _classCallCheck$8(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$8(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$8(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$8(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$8(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

var generateRandomId = function generateRandomId() {
  // generates a random 13 character string
  return Math.random().toString(16).slice(2);
};

var Channel =
/*#__PURE__*/
function () {
  function Channel() {
    var _this = this;

    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        transport = _ref.transport,
        _ref$async = _ref.async,
        async = _ref$async === void 0 ? false : _ref$async;

    _classCallCheck$8(this, Channel);

    this.isAsync = void 0;
    this.sender = generateRandomId();
    this.events = {};
    this.data = {};
    this.transport = undefined;
    this.addPeerListener = node$1(function (eventName, listener) {
      _this.addListener(eventName, listener);
    }, _default(_templateObject$a || (_templateObject$a = _taggedTemplateLiteral$a(["\n      channel.addPeerListener is deprecated\n    "]))));
    this.isAsync = async;

    if (transport) {
      this.transport = transport;
      this.transport.setHandler(function (event) {
        return _this.handleEvent(event);
      });
    }
  }

  _createClass$8(Channel, [{
    key: "hasTransport",
    get: function get() {
      return !!this.transport;
    }
  }, {
    key: "addListener",
    value: function addListener(eventName, listener) {
      this.events[eventName] = this.events[eventName] || [];
      this.events[eventName].push(listener);
    }
  }, {
    key: "emit",
    value: function emit(eventName) {
      var _this2 = this;

      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      var event = {
        type: eventName,
        args: args,
        from: this.sender
      };
      var options = {};

      if (args.length >= 1 && args[0] && args[0].options) {
        options = args[0].options;
      }

      var handler = function handler() {
        if (_this2.transport) {
          _this2.transport.send(event, options);
        }

        _this2.handleEvent(event);
      };

      if (this.isAsync) {
        // todo I'm not sure how to test this
        setImmediate(handler);
      } else {
        handler();
      }
    }
  }, {
    key: "last",
    value: function last(eventName) {
      return this.data[eventName];
    }
  }, {
    key: "eventNames",
    value: function eventNames() {
      return Object.keys(this.events);
    }
  }, {
    key: "listenerCount",
    value: function listenerCount(eventName) {
      var listeners = this.listeners(eventName);
      return listeners ? listeners.length : 0;
    }
  }, {
    key: "listeners",
    value: function listeners(eventName) {
      var listeners = this.events[eventName];
      return listeners || undefined;
    }
  }, {
    key: "once",
    value: function once(eventName, listener) {
      var onceListener = this.onceListener(eventName, listener);
      this.addListener(eventName, onceListener);
    }
  }, {
    key: "removeAllListeners",
    value: function removeAllListeners(eventName) {
      if (!eventName) {
        this.events = {};
      } else if (this.events[eventName]) {
        delete this.events[eventName];
      }
    }
  }, {
    key: "removeListener",
    value: function removeListener(eventName, listener) {
      var listeners = this.listeners(eventName);

      if (listeners) {
        this.events[eventName] = listeners.filter(function (l) {
          return l !== listener;
        });
      }
    }
  }, {
    key: "on",
    value: function on(eventName, listener) {
      this.addListener(eventName, listener);
    }
  }, {
    key: "off",
    value: function off(eventName, listener) {
      this.removeListener(eventName, listener);
    }
  }, {
    key: "handleEvent",
    value: function handleEvent(event) {
      var listeners = this.listeners(event.type);

      if (listeners && listeners.length) {
        listeners.forEach(function (fn) {
          fn.apply(event, event.args);
        });
      }

      this.data[event.type] = event.args;
    }
  }, {
    key: "onceListener",
    value: function onceListener(eventName, listener) {
      var _this3 = this;

      var onceListener = function onceListener() {
        _this3.removeListener(eventName, onceListener);

        return listener.apply(void 0, arguments);
      };

      return onceListener;
    }
  }]);

  return Channel;
}();

var check$2 = function (it) {
  return it && it.Math == Math && it;
}; // https://github.com/zloirock/core-js/issues/86#issuecomment-115759028


var global$_ = // eslint-disable-next-line es/no-global-this -- safe
check$2(typeof globalThis == 'object' && globalThis) || check$2(typeof window == 'object' && window) || // eslint-disable-next-line no-restricted-globals -- safe
check$2(typeof self == 'object' && self) || check$2(typeof commonjsGlobal == 'object' && commonjsGlobal) || // eslint-disable-next-line no-new-func -- fallback
function () {
  return this;
}() || Function('return this')();

var objectGetOwnPropertyDescriptor$2 = {};

var fails$15 = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$14 = fails$15; // Detect IE8's incomplete defineProperty implementation


var descriptors$2 = !fails$14(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, {
    get: function () {
      return 7;
    }
  })[1] != 7;
});

var objectPropertyIsEnumerable$2 = {};

var $propertyIsEnumerable$5 = {}.propertyIsEnumerable; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe

var getOwnPropertyDescriptor$8 = Object.getOwnPropertyDescriptor; // Nashorn ~ JDK8 bug

var NASHORN_BUG$2 = getOwnPropertyDescriptor$8 && !$propertyIsEnumerable$5.call({
  1: 2
}, 1); // `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable

objectPropertyIsEnumerable$2.f = NASHORN_BUG$2 ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$8(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable$5;

var createPropertyDescriptor$j = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var toString$e = {}.toString;

var classofRaw$5 = function (it) {
  return toString$e.call(it).slice(8, -1);
};

var fails$13 = fails$15;

var classof$m = classofRaw$5;

var split$2 = ''.split; // fallback for non-array-like ES3 and non-enumerable old V8 strings

var indexedObject$2 = fails$13(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$m(it) == 'String' ? split$2.call(it, '') : Object(it);
} : Object;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$l = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$7 = indexedObject$2;

var requireObjectCoercible$k = requireObjectCoercible$l;

var toIndexedObject$r = function (it) {
  return IndexedObject$7(requireObjectCoercible$k(it));
};

var isObject$12 = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};

var isObject$11 = isObject$12; // `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string


var toPrimitive$g = function (input, PREFERRED_STRING) {
  if (!isObject$11(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$11(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject$11(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$11(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};

var requireObjectCoercible$j = requireObjectCoercible$l; // `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject


var toObject$v = function (argument) {
  return Object(requireObjectCoercible$j(argument));
};

var toObject$u = toObject$v;

var hasOwnProperty$o = {}.hasOwnProperty;

var has$N = function hasOwn(it, key) {
  return hasOwnProperty$o.call(toObject$u(it), key);
};

var global$Z = global$_;

var isObject$10 = isObject$12;

var document$6 = global$Z.document; // typeof document.createElement is 'object' in old IE

var EXISTS$2 = isObject$10(document$6) && isObject$10(document$6.createElement);

var documentCreateElement$6 = function (it) {
  return EXISTS$2 ? document$6.createElement(it) : {};
};

var DESCRIPTORS$C = descriptors$2;

var fails$12 = fails$15;

var createElement$3 = documentCreateElement$6; // Thank's IE8 for his funny defineProperty


var ie8DomDefine$2 = !DESCRIPTORS$C && !fails$12(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement$3('div'), 'a', {
    get: function () {
      return 7;
    }
  }).a != 7;
});

var DESCRIPTORS$B = descriptors$2;

var propertyIsEnumerableModule$7 = objectPropertyIsEnumerable$2;

var createPropertyDescriptor$i = createPropertyDescriptor$j;

var toIndexedObject$q = toIndexedObject$r;

var toPrimitive$f = toPrimitive$g;

var has$M = has$N;

var IE8_DOM_DEFINE$5 = ie8DomDefine$2; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe


var $getOwnPropertyDescriptor$5 = Object.getOwnPropertyDescriptor; // `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor

objectGetOwnPropertyDescriptor$2.f = DESCRIPTORS$B ? $getOwnPropertyDescriptor$5 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$q(O);
  P = toPrimitive$f(P, true);
  if (IE8_DOM_DEFINE$5) try {
    return $getOwnPropertyDescriptor$5(O, P);
  } catch (error) {
    /* empty */
  }
  if (has$M(O, P)) return createPropertyDescriptor$i(!propertyIsEnumerableModule$7.f.call(O, P), O[P]);
};

var objectDefineProperty$2 = {};

var isObject$$ = isObject$12;

var anObject$J = function (it) {
  if (!isObject$$(it)) {
    throw TypeError(String(it) + ' is not an object');
  }

  return it;
};

var DESCRIPTORS$A = descriptors$2;

var IE8_DOM_DEFINE$4 = ie8DomDefine$2;

var anObject$I = anObject$J;

var toPrimitive$e = toPrimitive$g; // eslint-disable-next-line es/no-object-defineproperty -- safe


var $defineProperty$5 = Object.defineProperty; // `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty

objectDefineProperty$2.f = DESCRIPTORS$A ? $defineProperty$5 : function defineProperty(O, P, Attributes) {
  anObject$I(O);
  P = toPrimitive$e(P, true);
  anObject$I(Attributes);
  if (IE8_DOM_DEFINE$4) try {
    return $defineProperty$5(O, P, Attributes);
  } catch (error) {
    /* empty */
  }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$z = descriptors$2;

var definePropertyModule$n = objectDefineProperty$2;

var createPropertyDescriptor$h = createPropertyDescriptor$j;

var createNonEnumerableProperty$y = DESCRIPTORS$z ? function (object, key, value) {
  return definePropertyModule$n.f(object, key, createPropertyDescriptor$h(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var redefine$v = {exports: {}};

var global$Y = global$_;

var createNonEnumerableProperty$x = createNonEnumerableProperty$y;

var setGlobal$b = function (key, value) {
  try {
    createNonEnumerableProperty$x(global$Y, key, value);
  } catch (error) {
    global$Y[key] = value;
  }

  return value;
};

var global$X = global$_;

var setGlobal$a = setGlobal$b;

var SHARED$2 = '__core-js_shared__';
var store$b = global$X[SHARED$2] || setGlobal$a(SHARED$2, {});
var sharedStore$2 = store$b;

var store$a = sharedStore$2;

var functionToString$3 = Function.toString; // this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper

if (typeof store$a.inspectSource != 'function') {
  store$a.inspectSource = function (it) {
    return functionToString$3.call(it);
  };
}

var inspectSource$9 = store$a.inspectSource;

var global$W = global$_;

var inspectSource$8 = inspectSource$9;

var WeakMap$a = global$W.WeakMap;
var nativeWeakMap$2 = typeof WeakMap$a === 'function' && /native code/.test(inspectSource$8(WeakMap$a));

var shared$j = {exports: {}};

var store$9 = sharedStore$2;

(shared$j.exports = function (key, value) {
  return store$9[key] || (store$9[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.12.1',
  mode: 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});

var id$7 = 0;
var postfix$2 = Math.random();

var uid$f = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id$7 + postfix$2).toString(36);
};

var shared$i = shared$j.exports;

var uid$e = uid$f;

var keys$b = shared$i('keys');

var sharedKey$g = function (key) {
  return keys$b[key] || (keys$b[key] = uid$e(key));
};

var hiddenKeys$m = {};

var NATIVE_WEAK_MAP$3 = nativeWeakMap$2;

var global$V = global$_;

var isObject$_ = isObject$12;

var createNonEnumerableProperty$w = createNonEnumerableProperty$y;

var objectHas$2 = has$N;

var shared$h = sharedStore$2;

var sharedKey$f = sharedKey$g;

var hiddenKeys$l = hiddenKeys$m;

var OBJECT_ALREADY_INITIALIZED$2 = 'Object already initialized';
var WeakMap$9 = global$V.WeakMap;
var set$3, get$4, has$L;

var enforce$2 = function (it) {
  return has$L(it) ? get$4(it) : set$3(it, {});
};

var getterFor$2 = function (TYPE) {
  return function (it) {
    var state;

    if (!isObject$_(it) || (state = get$4(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    }

    return state;
  };
};

if (NATIVE_WEAK_MAP$3 || shared$h.state) {
  var store$8 = shared$h.state || (shared$h.state = new WeakMap$9());
  var wmget$2 = store$8.get;
  var wmhas$2 = store$8.has;
  var wmset$2 = store$8.set;

  set$3 = function (it, metadata) {
    if (wmhas$2.call(store$8, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$2);
    metadata.facade = it;
    wmset$2.call(store$8, it, metadata);
    return metadata;
  };

  get$4 = function (it) {
    return wmget$2.call(store$8, it) || {};
  };

  has$L = function (it) {
    return wmhas$2.call(store$8, it);
  };
} else {
  var STATE$2 = sharedKey$f('state');
  hiddenKeys$l[STATE$2] = true;

  set$3 = function (it, metadata) {
    if (objectHas$2(it, STATE$2)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$2);
    metadata.facade = it;
    createNonEnumerableProperty$w(it, STATE$2, metadata);
    return metadata;
  };

  get$4 = function (it) {
    return objectHas$2(it, STATE$2) ? it[STATE$2] : {};
  };

  has$L = function (it) {
    return objectHas$2(it, STATE$2);
  };
}

var internalState$2 = {
  set: set$3,
  get: get$4,
  has: has$L,
  enforce: enforce$2,
  getterFor: getterFor$2
};

var global$U = global$_;

var createNonEnumerableProperty$v = createNonEnumerableProperty$y;

var has$K = has$N;

var setGlobal$9 = setGlobal$b;

var inspectSource$7 = inspectSource$9;

var InternalStateModule$j = internalState$2;

var getInternalState$e = InternalStateModule$j.get;
var enforceInternalState$4 = InternalStateModule$j.enforce;
var TEMPLATE$2 = String(String).split('String');
(redefine$v.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var state;

  if (typeof value == 'function') {
    if (typeof key == 'string' && !has$K(value, 'name')) {
      createNonEnumerableProperty$v(value, 'name', key);
    }

    state = enforceInternalState$4(value);

    if (!state.source) {
      state.source = TEMPLATE$2.join(typeof key == 'string' ? key : '');
    }
  }

  if (O === global$U) {
    if (simple) O[key] = value;else setGlobal$9(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }

  if (simple) O[key] = value;else createNonEnumerableProperty$v(O, key, value); // add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState$e(this).source || inspectSource$7(this);
});

var global$T = global$_;

var path$8 = global$T;

var path$7 = path$8;

var global$S = global$_;

var aFunction$c = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

var getBuiltIn$i = function (namespace, method) {
  return arguments.length < 2 ? aFunction$c(path$7[namespace]) || aFunction$c(global$S[namespace]) : path$7[namespace] && path$7[namespace][method] || global$S[namespace] && global$S[namespace][method];
};

var objectGetOwnPropertyNames$2 = {};

var ceil$2 = Math.ceil;
var floor$4 = Math.floor; // `ToInteger` abstract operation
// https://tc39.es/ecma262/#sec-tointeger

var toInteger$e = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor$4 : ceil$2)(argument);
};

var toInteger$d = toInteger$e;

var min$9 = Math.min; // `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength

var toLength$q = function (argument) {
  return argument > 0 ? min$9(toInteger$d(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toInteger$c = toInteger$e;

var max$7 = Math.max;
var min$8 = Math.min; // Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).

var toAbsoluteIndex$9 = function (index, length) {
  var integer = toInteger$c(index);
  return integer < 0 ? max$7(integer + length, 0) : min$8(integer, length);
};

var toIndexedObject$p = toIndexedObject$r;

var toLength$p = toLength$q;

var toAbsoluteIndex$8 = toAbsoluteIndex$9; // `Array.prototype.{ indexOf, includes }` methods implementation


var createMethod$b = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$p($this);
    var length = toLength$p(O.length);
    var index = toAbsoluteIndex$8(fromIndex, length);
    var value; // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check

    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++]; // eslint-disable-next-line no-self-compare -- NaN check

      if (value != value) return true; // Array#indexOf ignores holes, Array#includes - not
    } else for (; length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    }
    return !IS_INCLUDES && -1;
  };
};

var arrayIncludes$2 = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$b(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$b(false)
};

var has$J = has$N;

var toIndexedObject$o = toIndexedObject$r;

var indexOf$3 = arrayIncludes$2.indexOf;

var hiddenKeys$k = hiddenKeys$m;

var objectKeysInternal$2 = function (object, names) {
  var O = toIndexedObject$o(object);
  var i = 0;
  var result = [];
  var key;

  for (key in O) !has$J(hiddenKeys$k, key) && has$J(O, key) && result.push(key); // Don't enum bug & hidden keys


  while (names.length > i) if (has$J(O, key = names[i++])) {
    ~indexOf$3(result, key) || result.push(key);
  }

  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$c = ['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf'];

var internalObjectKeys$5 = objectKeysInternal$2;

var enumBugKeys$b = enumBugKeys$c;

var hiddenKeys$j = enumBugKeys$b.concat('length', 'prototype'); // `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe

objectGetOwnPropertyNames$2.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$5(O, hiddenKeys$j);
};

var objectGetOwnPropertySymbols$2 = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols$2.f = Object.getOwnPropertySymbols;

var getBuiltIn$h = getBuiltIn$i;

var getOwnPropertyNamesModule$5 = objectGetOwnPropertyNames$2;

var getOwnPropertySymbolsModule$7 = objectGetOwnPropertySymbols$2;

var anObject$H = anObject$J; // all object keys, includes non-enumerable and symbols


var ownKeys$5 = getBuiltIn$h('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule$5.f(anObject$H(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$7.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};

var has$I = has$N;

var ownKeys$4 = ownKeys$5;

var getOwnPropertyDescriptorModule$5 = objectGetOwnPropertyDescriptor$2;

var definePropertyModule$m = objectDefineProperty$2;

var copyConstructorProperties$8 = function (target, source) {
  var keys = ownKeys$4(source);
  var defineProperty = definePropertyModule$m.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule$5.f;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has$I(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};

var fails$11 = fails$15;

var replacement$2 = /#|\.prototype\./;

var isForced$d = function (feature, detection) {
  var value = data$2[normalize$2(feature)];
  return value == POLYFILL$2 ? true : value == NATIVE$2 ? false : typeof detection == 'function' ? fails$11(detection) : !!detection;
};

var normalize$2 = isForced$d.normalize = function (string) {
  return String(string).replace(replacement$2, '.').toLowerCase();
};

var data$2 = isForced$d.data = {};
var NATIVE$2 = isForced$d.NATIVE = 'N';
var POLYFILL$2 = isForced$d.POLYFILL = 'P';
var isForced_1$2 = isForced$d;

var global$R = global$_;

var getOwnPropertyDescriptor$7 = objectGetOwnPropertyDescriptor$2.f;

var createNonEnumerableProperty$u = createNonEnumerableProperty$y;

var redefine$u = redefine$v.exports;

var setGlobal$8 = setGlobal$b;

var copyConstructorProperties$7 = copyConstructorProperties$8;

var isForced$c = isForced_1$2;
/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/


var _export$2 = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;

  if (GLOBAL) {
    target = global$R;
  } else if (STATIC) {
    target = global$R[TARGET] || setGlobal$8(TARGET, {});
  } else {
    target = (global$R[TARGET] || {}).prototype;
  }

  if (target) for (key in source) {
    sourceProperty = source[key];

    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor$7(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];

    FORCED = isForced$c(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced); // contained in target

    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties$7(sourceProperty, targetProperty);
    } // add a flag to not completely full polyfills


    if (options.sham || targetProperty && targetProperty.sham) {
      createNonEnumerableProperty$u(sourceProperty, 'sham', true);
    } // extend global


    redefine$u(target, key, sourceProperty, options);
  }
};

var classof$l = classofRaw$5; // `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe


var isArray$y = Array.isArray || function isArray(arg) {
  return classof$l(arg) == 'Array';
};

var toPrimitive$d = toPrimitive$g;

var definePropertyModule$l = objectDefineProperty$2;

var createPropertyDescriptor$g = createPropertyDescriptor$j;

var createProperty$d = function (object, key, value) {
  var propertyKey = toPrimitive$d(key);
  if (propertyKey in object) definePropertyModule$l.f(object, propertyKey, createPropertyDescriptor$g(0, value));else object[propertyKey] = value;
};

var getBuiltIn$g = getBuiltIn$i;

var engineUserAgent$2 = getBuiltIn$g('navigator', 'userAgent') || '';

var global$Q = global$_;

var userAgent$4 = engineUserAgent$2;

var process$6 = global$Q.process;
var versions$2 = process$6 && process$6.versions;
var v8$2 = versions$2 && versions$2.v8;
var match$3, version$2;

if (v8$2) {
  match$3 = v8$2.split('.');
  version$2 = match$3[0] < 4 ? 1 : match$3[0] + match$3[1];
} else if (userAgent$4) {
  match$3 = userAgent$4.match(/Edge\/(\d+)/);

  if (!match$3 || match$3[1] >= 74) {
    match$3 = userAgent$4.match(/Chrome\/(\d+)/);
    if (match$3) version$2 = match$3[1];
  }
}

var engineV8Version$2 = version$2 && +version$2;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION$b = engineV8Version$2;

var fails$10 = fails$15; // eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing


var nativeSymbol$2 = !!Object.getOwnPropertySymbols && !fails$10(function () {
  return !String(Symbol()) || // Chrome 38 Symbol has incorrect toString conversion
  // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
  !Symbol.sham && V8_VERSION$b && V8_VERSION$b < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$8 = nativeSymbol$2;

var useSymbolAsUid$2 = NATIVE_SYMBOL$8 && !Symbol.sham && typeof Symbol.iterator == 'symbol';

var global$P = global$_;

var shared$g = shared$j.exports;

var has$H = has$N;

var uid$d = uid$f;

var NATIVE_SYMBOL$7 = nativeSymbol$2;

var USE_SYMBOL_AS_UID$5 = useSymbolAsUid$2;

var WellKnownSymbolsStore$5 = shared$g('wks');
var Symbol$h = global$P.Symbol;
var createWellKnownSymbol$2 = USE_SYMBOL_AS_UID$5 ? Symbol$h : Symbol$h && Symbol$h.withoutSetter || uid$d;

var wellKnownSymbol$17 = function (name) {
  if (!has$H(WellKnownSymbolsStore$5, name) || !(NATIVE_SYMBOL$7 || typeof WellKnownSymbolsStore$5[name] == 'string')) {
    if (NATIVE_SYMBOL$7 && has$H(Symbol$h, name)) {
      WellKnownSymbolsStore$5[name] = Symbol$h[name];
    } else {
      WellKnownSymbolsStore$5[name] = createWellKnownSymbol$2('Symbol.' + name);
    }
  }

  return WellKnownSymbolsStore$5[name];
};

var isObject$Z = isObject$12;

var isArray$x = isArray$y;

var wellKnownSymbol$16 = wellKnownSymbol$17;

var SPECIES$h = wellKnownSymbol$16('species'); // `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate

var arraySpeciesCreate$8 = function (originalArray, length) {
  var C;

  if (isArray$x(originalArray)) {
    C = originalArray.constructor; // cross-realm fallback

    if (typeof C == 'function' && (C === Array || isArray$x(C.prototype))) C = undefined;else if (isObject$Z(C)) {
      C = C[SPECIES$h];
      if (C === null) C = undefined;
    }
  }

  return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};

var fails$$ = fails$15;

var wellKnownSymbol$15 = wellKnownSymbol$17;

var V8_VERSION$a = engineV8Version$2;

var SPECIES$g = wellKnownSymbol$15('species');

var arrayMethodHasSpeciesSupport$f = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION$a >= 51 || !fails$$(function () {
    var array = [];
    var constructor = array.constructor = {};

    constructor[SPECIES$g] = function () {
      return {
        foo: 1
      };
    };

    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};

var $$W = _export$2;

var fails$_ = fails$15;

var isArray$w = isArray$y;

var isObject$Y = isObject$12;

var toObject$t = toObject$v;

var toLength$o = toLength$q;

var createProperty$c = createProperty$d;

var arraySpeciesCreate$7 = arraySpeciesCreate$8;

var arrayMethodHasSpeciesSupport$e = arrayMethodHasSpeciesSupport$f;

var wellKnownSymbol$14 = wellKnownSymbol$17;

var V8_VERSION$9 = engineV8Version$2;

var IS_CONCAT_SPREADABLE$3 = wellKnownSymbol$14('isConcatSpreadable');
var MAX_SAFE_INTEGER$7 = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED$3 = 'Maximum allowed index exceeded'; // We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679

var IS_CONCAT_SPREADABLE_SUPPORT$3 = V8_VERSION$9 >= 51 || !fails$_(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE$3] = false;
  return array.concat()[0] !== array;
});
var SPECIES_SUPPORT$3 = arrayMethodHasSpeciesSupport$e('concat');

var isConcatSpreadable$3 = function (O) {
  if (!isObject$Y(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE$3];
  return spreadable !== undefined ? !!spreadable : isArray$w(O);
};

var FORCED$7 = !IS_CONCAT_SPREADABLE_SUPPORT$3 || !SPECIES_SUPPORT$3; // `Array.prototype.concat` method
// https://tc39.es/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species

$$W({
  target: 'Array',
  proto: true,
  forced: FORCED$7
}, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  concat: function concat(arg) {
    var O = toObject$t(this);
    var A = arraySpeciesCreate$7(O, 0);
    var n = 0;
    var i, k, length, len, E;

    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];

      if (isConcatSpreadable$3(E)) {
        len = toLength$o(E.length);
        if (n + len > MAX_SAFE_INTEGER$7) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$3);

        for (k = 0; k < len; k++, n++) if (k in E) createProperty$c(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER$7) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$3);
        createProperty$c(A, n++, E);
      }
    }

    A.length = n;
    return A;
  }
});

var internalObjectKeys$4 = objectKeysInternal$2;

var enumBugKeys$a = enumBugKeys$c; // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe


var objectKeys$d = Object.keys || function keys(O) {
  return internalObjectKeys$4(O, enumBugKeys$a);
};

var DESCRIPTORS$y = descriptors$2;

var definePropertyModule$k = objectDefineProperty$2;

var anObject$G = anObject$J;

var objectKeys$c = objectKeys$d; // `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe


var objectDefineProperties$3 = DESCRIPTORS$y ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$G(O);
  var keys = objectKeys$c(Properties);
  var length = keys.length;
  var index = 0;
  var key;

  while (length > index) definePropertyModule$k.f(O, key = keys[index++], Properties[key]);

  return O;
};

var getBuiltIn$f = getBuiltIn$i;

var html$7 = getBuiltIn$f('document', 'documentElement');

var anObject$F = anObject$J;

var defineProperties$3 = objectDefineProperties$3;

var enumBugKeys$9 = enumBugKeys$c;

var hiddenKeys$i = hiddenKeys$m;

var html$6 = html$7;

var documentCreateElement$5 = documentCreateElement$6;

var sharedKey$e = sharedKey$g;

var GT$3 = '>';
var LT$3 = '<';
var PROTOTYPE$6 = 'prototype';
var SCRIPT$3 = 'script';
var IE_PROTO$7 = sharedKey$e('IE_PROTO');

var EmptyConstructor$3 = function () {
  /* empty */
};

var scriptTag$3 = function (content) {
  return LT$3 + SCRIPT$3 + GT$3 + content + LT$3 + '/' + SCRIPT$3 + GT$3;
}; // Create object with fake `null` prototype: use ActiveX Object with cleared prototype


var NullProtoObjectViaActiveX$3 = function (activeXDocument) {
  activeXDocument.write(scriptTag$3(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak

  return temp;
}; // Create object with fake `null` prototype: use iframe Object with cleared prototype


var NullProtoObjectViaIFrame$3 = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement$5('iframe');
  var JS = 'java' + SCRIPT$3 + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html$6.appendChild(iframe); // https://github.com/zloirock/core-js/issues/475

  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag$3('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
}; // Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug


var activeXDocument$3;

var NullProtoObject$3 = function () {
  try {
    /* global ActiveXObject -- old IE */
    activeXDocument$3 = document.domain && new ActiveXObject('htmlfile');
  } catch (error) {
    /* ignore */
  }

  NullProtoObject$3 = activeXDocument$3 ? NullProtoObjectViaActiveX$3(activeXDocument$3) : NullProtoObjectViaIFrame$3();
  var length = enumBugKeys$9.length;

  while (length--) delete NullProtoObject$3[PROTOTYPE$6][enumBugKeys$9[length]];

  return NullProtoObject$3();
};

hiddenKeys$i[IE_PROTO$7] = true; // `Object.create` method
// https://tc39.es/ecma262/#sec-object.create

var objectCreate$3 = Object.create || function create(O, Properties) {
  var result;

  if (O !== null) {
    EmptyConstructor$3[PROTOTYPE$6] = anObject$F(O);
    result = new EmptyConstructor$3();
    EmptyConstructor$3[PROTOTYPE$6] = null; // add "__proto__" for Object.getPrototypeOf polyfill

    result[IE_PROTO$7] = O;
  } else result = NullProtoObject$3();

  return Properties === undefined ? result : defineProperties$3(result, Properties);
};

var wellKnownSymbol$13 = wellKnownSymbol$17;

var create$c = objectCreate$3;

var definePropertyModule$j = objectDefineProperty$2;

var UNSCOPABLES$3 = wellKnownSymbol$13('unscopables');
var ArrayPrototype$6 = Array.prototype; // Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

if (ArrayPrototype$6[UNSCOPABLES$3] == undefined) {
  definePropertyModule$j.f(ArrayPrototype$6, UNSCOPABLES$3, {
    configurable: true,
    value: create$c(null)
  });
} // add a key to Array.prototype[@@unscopables]


var addToUnscopables$d = function (key) {
  ArrayPrototype$6[UNSCOPABLES$3][key] = true;
};

var iterators$2 = {};

var fails$Z = fails$15;

var correctPrototypeGetter$3 = !fails$Z(function () {
  function F() {
    /* empty */
  }

  F.prototype.constructor = null; // eslint-disable-next-line es/no-object-getprototypeof -- required for testing

  return Object.getPrototypeOf(new F()) !== F.prototype;
});

var has$G = has$N;

var toObject$s = toObject$v;

var sharedKey$d = sharedKey$g;

var CORRECT_PROTOTYPE_GETTER$4 = correctPrototypeGetter$3;

var IE_PROTO$6 = sharedKey$d('IE_PROTO');
var ObjectPrototype$6 = Object.prototype; // `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
// eslint-disable-next-line es/no-object-getprototypeof -- safe

var objectGetPrototypeOf$3 = CORRECT_PROTOTYPE_GETTER$4 ? Object.getPrototypeOf : function (O) {
  O = toObject$s(O);
  if (has$G(O, IE_PROTO$6)) return O[IE_PROTO$6];

  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  }

  return O instanceof Object ? ObjectPrototype$6 : null;
};

var fails$Y = fails$15;

var getPrototypeOf$7 = objectGetPrototypeOf$3;

var createNonEnumerableProperty$t = createNonEnumerableProperty$y;

var has$F = has$N;

var wellKnownSymbol$12 = wellKnownSymbol$17;

var ITERATOR$k = wellKnownSymbol$12('iterator');
var BUGGY_SAFARI_ITERATORS$7 = false;

var returnThis$b = function () {
  return this;
}; // `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object


var IteratorPrototype$b, PrototypeOfArrayIteratorPrototype$3, arrayIterator$3;
/* eslint-disable es/no-array-prototype-keys -- safe */

if ([].keys) {
  arrayIterator$3 = [].keys(); // Safari 8 has buggy iterators w/o `next`

  if (!('next' in arrayIterator$3)) BUGGY_SAFARI_ITERATORS$7 = true;else {
    PrototypeOfArrayIteratorPrototype$3 = getPrototypeOf$7(getPrototypeOf$7(arrayIterator$3));
    if (PrototypeOfArrayIteratorPrototype$3 !== Object.prototype) IteratorPrototype$b = PrototypeOfArrayIteratorPrototype$3;
  }
}

var NEW_ITERATOR_PROTOTYPE$3 = IteratorPrototype$b == undefined || fails$Y(function () {
  var test = {}; // FF44- legacy iterators case

  return IteratorPrototype$b[ITERATOR$k].call(test) !== test;
});
if (NEW_ITERATOR_PROTOTYPE$3) IteratorPrototype$b = {}; // 25.1.2.1.1 %IteratorPrototype%[@@iterator]()

if (!has$F(IteratorPrototype$b, ITERATOR$k)) {
  createNonEnumerableProperty$t(IteratorPrototype$b, ITERATOR$k, returnThis$b);
}

var iteratorsCore$3 = {
  IteratorPrototype: IteratorPrototype$b,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS$7
};

var defineProperty$r = objectDefineProperty$2.f;

var has$E = has$N;

var wellKnownSymbol$11 = wellKnownSymbol$17;

var TO_STRING_TAG$c = wellKnownSymbol$11('toStringTag');

var setToStringTag$i = function (it, TAG, STATIC) {
  if (it && !has$E(it = STATIC ? it : it.prototype, TO_STRING_TAG$c)) {
    defineProperty$r(it, TO_STRING_TAG$c, {
      configurable: true,
      value: TAG
    });
  }
};

var IteratorPrototype$a = iteratorsCore$3.IteratorPrototype;

var create$b = objectCreate$3;

var createPropertyDescriptor$f = createPropertyDescriptor$j;

var setToStringTag$h = setToStringTag$i;

var Iterators$h = iterators$2;

var returnThis$a = function () {
  return this;
};

var createIteratorConstructor$7 = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create$b(IteratorPrototype$a, {
    next: createPropertyDescriptor$f(1, next)
  });
  setToStringTag$h(IteratorConstructor, TO_STRING_TAG, false);
  Iterators$h[TO_STRING_TAG] = returnThis$a;
  return IteratorConstructor;
};

var isObject$X = isObject$12;

var aPossiblePrototype$5 = function (it) {
  if (!isObject$X(it) && it !== null) {
    throw TypeError("Can't set " + String(it) + ' as a prototype');
  }

  return it;
};

/* eslint-disable no-proto -- safe */

var anObject$E = anObject$J;

var aPossiblePrototype$4 = aPossiblePrototype$5; // `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe


var objectSetPrototypeOf$2 = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;

  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    setter = Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set;
    setter.call(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) {
    /* empty */
  }

  return function setPrototypeOf(O, proto) {
    anObject$E(O);
    aPossiblePrototype$4(proto);
    if (CORRECT_SETTER) setter.call(O, proto);else O.__proto__ = proto;
    return O;
  };
}() : undefined);

var $$V = _export$2;

var createIteratorConstructor$6 = createIteratorConstructor$7;

var getPrototypeOf$6 = objectGetPrototypeOf$3;

var setPrototypeOf$8 = objectSetPrototypeOf$2;

var setToStringTag$g = setToStringTag$i;

var createNonEnumerableProperty$s = createNonEnumerableProperty$y;

var redefine$t = redefine$v.exports;

var wellKnownSymbol$10 = wellKnownSymbol$17;

var Iterators$g = iterators$2;

var IteratorsCore$3 = iteratorsCore$3;

var IteratorPrototype$9 = IteratorsCore$3.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS$6 = IteratorsCore$3.BUGGY_SAFARI_ITERATORS;
var ITERATOR$j = wellKnownSymbol$10('iterator');
var KEYS$3 = 'keys';
var VALUES$3 = 'values';
var ENTRIES$3 = 'entries';

var returnThis$9 = function () {
  return this;
};

var defineIterator$f = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor$6(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS$6 && KIND in IterablePrototype) return IterablePrototype[KIND];

    switch (KIND) {
      case KEYS$3:
        return function keys() {
          return new IteratorConstructor(this, KIND);
        };

      case VALUES$3:
        return function values() {
          return new IteratorConstructor(this, KIND);
        };

      case ENTRIES$3:
        return function entries() {
          return new IteratorConstructor(this, KIND);
        };
    }

    return function () {
      return new IteratorConstructor(this);
    };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR$j] || IterablePrototype['@@iterator'] || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS$6 && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY; // fix native

  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf$6(anyNativeIterator.call(new Iterable()));

    if (IteratorPrototype$9 !== Object.prototype && CurrentIteratorPrototype.next) {
      if (getPrototypeOf$6(CurrentIteratorPrototype) !== IteratorPrototype$9) {
        if (setPrototypeOf$8) {
          setPrototypeOf$8(CurrentIteratorPrototype, IteratorPrototype$9);
        } else if (typeof CurrentIteratorPrototype[ITERATOR$j] != 'function') {
          createNonEnumerableProperty$s(CurrentIteratorPrototype, ITERATOR$j, returnThis$9);
        }
      } // Set @@toStringTag to native iterators


      setToStringTag$g(CurrentIteratorPrototype, TO_STRING_TAG, true);
    }
  } // fix Array#{values, @@iterator}.name in V8 / FF


  if (DEFAULT == VALUES$3 && nativeIterator && nativeIterator.name !== VALUES$3) {
    INCORRECT_VALUES_NAME = true;

    defaultIterator = function values() {
      return nativeIterator.call(this);
    };
  } // define iterator


  if (IterablePrototype[ITERATOR$j] !== defaultIterator) {
    createNonEnumerableProperty$s(IterablePrototype, ITERATOR$j, defaultIterator);
  }

  Iterators$g[NAME] = defaultIterator; // export additional methods

  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES$3),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS$3),
      entries: getIterationMethod(ENTRIES$3)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS$6 || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine$t(IterablePrototype, KEY, methods[KEY]);
      }
    } else $$V({
      target: NAME,
      proto: true,
      forced: BUGGY_SAFARI_ITERATORS$6 || INCORRECT_VALUES_NAME
    }, methods);
  }

  return methods;
};

var toIndexedObject$n = toIndexedObject$r;

var addToUnscopables$c = addToUnscopables$d;

var Iterators$f = iterators$2;

var InternalStateModule$i = internalState$2;

var defineIterator$e = defineIterator$f;

var ARRAY_ITERATOR$3 = 'Array Iterator';
var setInternalState$g = InternalStateModule$i.set;
var getInternalState$d = InternalStateModule$i.getterFor(ARRAY_ITERATOR$3); // `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator

var es_array_iterator$3 = defineIterator$e(Array, 'Array', function (iterated, kind) {
  setInternalState$g(this, {
    type: ARRAY_ITERATOR$3,
    target: toIndexedObject$n(iterated),
    // target
    index: 0,
    // next index
    kind: kind // kind

  }); // `%ArrayIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState$d(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;

  if (!target || index >= target.length) {
    state.target = undefined;
    return {
      value: undefined,
      done: true
    };
  }

  if (kind == 'keys') return {
    value: index,
    done: false
  };
  if (kind == 'values') return {
    value: target[index],
    done: false
  };
  return {
    value: [index, target[index]],
    done: false
  };
}, 'values'); // argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject

Iterators$f.Arguments = Iterators$f.Array; // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$c('keys');
addToUnscopables$c('values');
addToUnscopables$c('entries');

var wellKnownSymbol$$ = wellKnownSymbol$17;

var TO_STRING_TAG$b = wellKnownSymbol$$('toStringTag');
var test$3 = {};
test$3[TO_STRING_TAG$b] = 'z';
var toStringTagSupport$2 = String(test$3) === '[object z]';

var TO_STRING_TAG_SUPPORT$8 = toStringTagSupport$2;

var classofRaw$4 = classofRaw$5;

var wellKnownSymbol$_ = wellKnownSymbol$17;

var TO_STRING_TAG$a = wellKnownSymbol$_('toStringTag'); // ES3 wrong here

var CORRECT_ARGUMENTS$2 = classofRaw$4(function () {
  return arguments;
}()) == 'Arguments'; // fallback for IE11 Script Access Denied error

var tryGet$2 = function (it, key) {
  try {
    return it[key];
  } catch (error) {
    /* empty */
  }
}; // getting tag from ES6+ `Object.prototype.toString`


var classof$k = TO_STRING_TAG_SUPPORT$8 ? classofRaw$4 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null' // @@toStringTag case
  : typeof (tag = tryGet$2(O = Object(it), TO_STRING_TAG$a)) == 'string' ? tag // builtinTag case
  : CORRECT_ARGUMENTS$2 ? classofRaw$4(O) // ES3 arguments fallback
  : (result = classofRaw$4(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};

var TO_STRING_TAG_SUPPORT$7 = toStringTagSupport$2;

var classof$j = classof$k; // `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring


var objectToString$9 = TO_STRING_TAG_SUPPORT$7 ? {}.toString : function toString() {
  return '[object ' + classof$j(this) + ']';
};

var TO_STRING_TAG_SUPPORT$6 = toStringTagSupport$2;

var redefine$s = redefine$v.exports;

var toString$d = objectToString$9; // `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring


if (!TO_STRING_TAG_SUPPORT$6) {
  redefine$s(Object.prototype, 'toString', toString$d, {
    unsafe: true
  });
}

var internalMetadata$3 = {exports: {}};

var fails$X = fails$15;

var freezing$3 = !fails$X(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});

var hiddenKeys$h = hiddenKeys$m;

var isObject$W = isObject$12;

var has$D = has$N;

var defineProperty$q = objectDefineProperty$2.f;

var uid$c = uid$f;

var FREEZING$5 = freezing$3;

var METADATA$3 = uid$c('meta');
var id$6 = 0; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible$4 = Object.isExtensible || function () {
  return true;
};

var setMetadata$3 = function (it) {
  defineProperty$q(it, METADATA$3, {
    value: {
      objectID: 'O' + ++id$6,
      // object ID
      weakData: {} // weak collections IDs

    }
  });
};

var fastKey$7 = function (it, create) {
  // return a primitive with prefix
  if (!isObject$W(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;

  if (!has$D(it, METADATA$3)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$4(it)) return 'F'; // not necessary to add metadata

    if (!create) return 'E'; // add missing metadata

    setMetadata$3(it); // return object ID
  }

  return it[METADATA$3].objectID;
};

var getWeakData$4 = function (it, create) {
  if (!has$D(it, METADATA$3)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$4(it)) return true; // not necessary to add metadata

    if (!create) return false; // add missing metadata

    setMetadata$3(it); // return the store of weak collections IDs
  }

  return it[METADATA$3].weakData;
}; // add metadata on freeze-family methods calling


var onFreeze$5 = function (it) {
  if (FREEZING$5 && meta$3.REQUIRED && isExtensible$4(it) && !has$D(it, METADATA$3)) setMetadata$3(it);
  return it;
};

var meta$3 = internalMetadata$3.exports = {
  REQUIRED: false,
  fastKey: fastKey$7,
  getWeakData: getWeakData$4,
  onFreeze: onFreeze$5
};
hiddenKeys$h[METADATA$3] = true;

var wellKnownSymbol$Z = wellKnownSymbol$17;

var Iterators$e = iterators$2;

var ITERATOR$i = wellKnownSymbol$Z('iterator');
var ArrayPrototype$5 = Array.prototype; // check on default Array iterator

var isArrayIteratorMethod$8 = function (it) {
  return it !== undefined && (Iterators$e.Array === it || ArrayPrototype$5[ITERATOR$i] === it);
};

var aFunction$b = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  }

  return it;
};

var aFunction$a = aFunction$b; // optional / simple context binding


var functionBindContext$2 = function (fn, that, length) {
  aFunction$a(fn);
  if (that === undefined) return fn;

  switch (length) {
    case 0:
      return function () {
        return fn.call(that);
      };

    case 1:
      return function (a) {
        return fn.call(that, a);
      };

    case 2:
      return function (a, b) {
        return fn.call(that, a, b);
      };

    case 3:
      return function (a, b, c) {
        return fn.call(that, a, b, c);
      };
  }

  return function ()
  /* ...args */
  {
    return fn.apply(that, arguments);
  };
};

var classof$i = classof$k;

var Iterators$d = iterators$2;

var wellKnownSymbol$Y = wellKnownSymbol$17;

var ITERATOR$h = wellKnownSymbol$Y('iterator');

var getIteratorMethod$8 = function (it) {
  if (it != undefined) return it[ITERATOR$h] || it['@@iterator'] || Iterators$d[classof$i(it)];
};

var anObject$D = anObject$J;

var iteratorClose$8 = function (iterator) {
  var returnMethod = iterator['return'];

  if (returnMethod !== undefined) {
    return anObject$D(returnMethod.call(iterator)).value;
  }
};

var anObject$C = anObject$J;

var isArrayIteratorMethod$7 = isArrayIteratorMethod$8;

var toLength$n = toLength$q;

var bind$f = functionBindContext$2;

var getIteratorMethod$7 = getIteratorMethod$8;

var iteratorClose$7 = iteratorClose$8;

var Result$2 = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var iterate$c = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind$f(unboundFunction, that, 1 + AS_ENTRIES + INTERRUPTED);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose$7(iterator);
    return new Result$2(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject$C(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    }

    return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod$7(iterable);
    if (typeof iterFn != 'function') throw TypeError('Target is not iterable'); // optimisation for array iterators

    if (isArrayIteratorMethod$7(iterFn)) {
      for (index = 0, length = toLength$n(iterable.length); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && result instanceof Result$2) return result;
      }

      return new Result$2(false);
    }

    iterator = iterFn.call(iterable);
  }

  next = iterator.next;

  while (!(step = next.call(iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose$7(iterator);
      throw error;
    }

    if (typeof result == 'object' && result && result instanceof Result$2) return result;
  }

  return new Result$2(false);
};

var anInstance$c = function (it, Constructor, name) {
  if (!(it instanceof Constructor)) {
    throw TypeError('Incorrect ' + (name ? name + ' ' : '') + 'invocation');
  }

  return it;
};

var wellKnownSymbol$X = wellKnownSymbol$17;

var ITERATOR$g = wellKnownSymbol$X('iterator');
var SAFE_CLOSING$2 = false;

try {
  var called$2 = 0;
  var iteratorWithReturn$2 = {
    next: function () {
      return {
        done: !!called$2++
      };
    },
    'return': function () {
      SAFE_CLOSING$2 = true;
    }
  };

  iteratorWithReturn$2[ITERATOR$g] = function () {
    return this;
  }; // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing


  Array.from(iteratorWithReturn$2, function () {
    throw 2;
  });
} catch (error) {
  /* empty */
}

var checkCorrectnessOfIteration$a = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING$2) return false;
  var ITERATION_SUPPORT = false;

  try {
    var object = {};

    object[ITERATOR$g] = function () {
      return {
        next: function () {
          return {
            done: ITERATION_SUPPORT = true
          };
        }
      };
    };

    exec(object);
  } catch (error) {
    /* empty */
  }

  return ITERATION_SUPPORT;
};

var isObject$V = isObject$12;

var setPrototypeOf$7 = objectSetPrototypeOf$2; // makes subclassing work correct for wrapped built-ins


var inheritIfRequired$a = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if ( // it can work only with native `setPrototypeOf`
  setPrototypeOf$7 && // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
  typeof (NewTarget = dummy.constructor) == 'function' && NewTarget !== Wrapper && isObject$V(NewTargetPrototype = NewTarget.prototype) && NewTargetPrototype !== Wrapper.prototype) setPrototypeOf$7($this, NewTargetPrototype);
  return $this;
};

var $$U = _export$2;

var global$O = global$_;

var isForced$b = isForced_1$2;

var redefine$r = redefine$v.exports;

var InternalMetadataModule$4 = internalMetadata$3.exports;

var iterate$b = iterate$c;

var anInstance$b = anInstance$c;

var isObject$U = isObject$12;

var fails$W = fails$15;

var checkCorrectnessOfIteration$9 = checkCorrectnessOfIteration$a;

var setToStringTag$f = setToStringTag$i;

var inheritIfRequired$9 = inheritIfRequired$a;

var collection$8 = function (CONSTRUCTOR_NAME, wrapper, common) {
  var IS_MAP = CONSTRUCTOR_NAME.indexOf('Map') !== -1;
  var IS_WEAK = CONSTRUCTOR_NAME.indexOf('Weak') !== -1;
  var ADDER = IS_MAP ? 'set' : 'add';
  var NativeConstructor = global$O[CONSTRUCTOR_NAME];
  var NativePrototype = NativeConstructor && NativeConstructor.prototype;
  var Constructor = NativeConstructor;
  var exported = {};

  var fixMethod = function (KEY) {
    var nativeMethod = NativePrototype[KEY];
    redefine$r(NativePrototype, KEY, KEY == 'add' ? function add(value) {
      nativeMethod.call(this, value === 0 ? 0 : value);
      return this;
    } : KEY == 'delete' ? function (key) {
      return IS_WEAK && !isObject$U(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'get' ? function get(key) {
      return IS_WEAK && !isObject$U(key) ? undefined : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'has' ? function has(key) {
      return IS_WEAK && !isObject$U(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : function set(key, value) {
      nativeMethod.call(this, key === 0 ? 0 : key, value);
      return this;
    });
  };

  var REPLACE = isForced$b(CONSTRUCTOR_NAME, typeof NativeConstructor != 'function' || !(IS_WEAK || NativePrototype.forEach && !fails$W(function () {
    new NativeConstructor().entries().next();
  })));

  if (REPLACE) {
    // create collection constructor
    Constructor = common.getConstructor(wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER);
    InternalMetadataModule$4.REQUIRED = true;
  } else if (isForced$b(CONSTRUCTOR_NAME, true)) {
    var instance = new Constructor(); // early implementations not supports chaining

    var HASNT_CHAINING = instance[ADDER](IS_WEAK ? {} : -0, 1) != instance; // V8 ~ Chromium 40- weak-collections throws on primitives, but should return false

    var THROWS_ON_PRIMITIVES = fails$W(function () {
      instance.has(1);
    }); // most early implementations doesn't supports iterables, most modern - not close it correctly
    // eslint-disable-next-line no-new -- required for testing

    var ACCEPT_ITERABLES = checkCorrectnessOfIteration$9(function (iterable) {
      new NativeConstructor(iterable);
    }); // for early implementations -0 and +0 not the same

    var BUGGY_ZERO = !IS_WEAK && fails$W(function () {
      // V8 ~ Chromium 42- fails only with 5+ elements
      var $instance = new NativeConstructor();
      var index = 5;

      while (index--) $instance[ADDER](index, index);

      return !$instance.has(-0);
    });

    if (!ACCEPT_ITERABLES) {
      Constructor = wrapper(function (dummy, iterable) {
        anInstance$b(dummy, Constructor, CONSTRUCTOR_NAME);
        var that = inheritIfRequired$9(new NativeConstructor(), dummy, Constructor);
        if (iterable != undefined) iterate$b(iterable, that[ADDER], {
          that: that,
          AS_ENTRIES: IS_MAP
        });
        return that;
      });
      Constructor.prototype = NativePrototype;
      NativePrototype.constructor = Constructor;
    }

    if (THROWS_ON_PRIMITIVES || BUGGY_ZERO) {
      fixMethod('delete');
      fixMethod('has');
      IS_MAP && fixMethod('get');
    }

    if (BUGGY_ZERO || HASNT_CHAINING) fixMethod(ADDER); // weak collections should not contains .clear method

    if (IS_WEAK && NativePrototype.clear) delete NativePrototype.clear;
  }

  exported[CONSTRUCTOR_NAME] = Constructor;
  $$U({
    global: true,
    forced: Constructor != NativeConstructor
  }, exported);
  setToStringTag$f(Constructor, CONSTRUCTOR_NAME);
  if (!IS_WEAK) common.setStrong(Constructor, CONSTRUCTOR_NAME, IS_MAP);
  return Constructor;
};

var redefine$q = redefine$v.exports;

var redefineAll$9 = function (target, src, options) {
  for (var key in src) redefine$q(target, key, src[key], options);

  return target;
};

var getBuiltIn$e = getBuiltIn$i;

var definePropertyModule$i = objectDefineProperty$2;

var wellKnownSymbol$W = wellKnownSymbol$17;

var DESCRIPTORS$x = descriptors$2;

var SPECIES$f = wellKnownSymbol$W('species');

var setSpecies$9 = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn$e(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule$i.f;

  if (DESCRIPTORS$x && Constructor && !Constructor[SPECIES$f]) {
    defineProperty(Constructor, SPECIES$f, {
      configurable: true,
      get: function () {
        return this;
      }
    });
  }
};

var defineProperty$p = objectDefineProperty$2.f;

var create$a = objectCreate$3;

var redefineAll$8 = redefineAll$9;

var bind$e = functionBindContext$2;

var anInstance$a = anInstance$c;

var iterate$a = iterate$c;

var defineIterator$d = defineIterator$f;

var setSpecies$8 = setSpecies$9;

var DESCRIPTORS$w = descriptors$2;

var fastKey$6 = internalMetadata$3.exports.fastKey;

var InternalStateModule$h = internalState$2;

var setInternalState$f = InternalStateModule$h.set;
var internalStateGetterFor$4 = InternalStateModule$h.getterFor;
var collectionStrong$7 = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance$a(that, C, CONSTRUCTOR_NAME);
      setInternalState$f(that, {
        type: CONSTRUCTOR_NAME,
        index: create$a(null),
        first: undefined,
        last: undefined,
        size: 0
      });
      if (!DESCRIPTORS$w) that.size = 0;
      if (iterable != undefined) iterate$a(iterable, that[ADDER], {
        that: that,
        AS_ENTRIES: IS_MAP
      });
    });
    var getInternalState = internalStateGetterFor$4(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var entry = getEntry(that, key);
      var previous, index; // change existing entry

      if (entry) {
        entry.value = value; // create new entry
      } else {
        state.last = entry = {
          index: index = fastKey$6(key, true),
          key: key,
          value: value,
          previous: previous = state.last,
          next: undefined,
          removed: false
        };
        if (!state.first) state.first = entry;
        if (previous) previous.next = entry;
        if (DESCRIPTORS$w) state.size++;else that.size++; // add to index

        if (index !== 'F') state.index[index] = entry;
      }

      return that;
    };

    var getEntry = function (that, key) {
      var state = getInternalState(that); // fast case

      var index = fastKey$6(key);
      var entry;
      if (index !== 'F') return state.index[index]; // frozen object case

      for (entry = state.first; entry; entry = entry.next) {
        if (entry.key == key) return entry;
      }
    };

    redefineAll$8(C.prototype, {
      // 23.1.3.1 Map.prototype.clear()
      // 23.2.3.2 Set.prototype.clear()
      clear: function clear() {
        var that = this;
        var state = getInternalState(that);
        var data = state.index;
        var entry = state.first;

        while (entry) {
          entry.removed = true;
          if (entry.previous) entry.previous = entry.previous.next = undefined;
          delete data[entry.index];
          entry = entry.next;
        }

        state.first = state.last = undefined;
        if (DESCRIPTORS$w) state.size = 0;else that.size = 0;
      },
      // 23.1.3.3 Map.prototype.delete(key)
      // 23.2.3.4 Set.prototype.delete(value)
      'delete': function (key) {
        var that = this;
        var state = getInternalState(that);
        var entry = getEntry(that, key);

        if (entry) {
          var next = entry.next;
          var prev = entry.previous;
          delete state.index[entry.index];
          entry.removed = true;
          if (prev) prev.next = next;
          if (next) next.previous = prev;
          if (state.first == entry) state.first = next;
          if (state.last == entry) state.last = prev;
          if (DESCRIPTORS$w) state.size--;else that.size--;
        }

        return !!entry;
      },
      // 23.2.3.6 Set.prototype.forEach(callbackfn, thisArg = undefined)
      // 23.1.3.5 Map.prototype.forEach(callbackfn, thisArg = undefined)
      forEach: function forEach(callbackfn
      /* , that = undefined */
      ) {
        var state = getInternalState(this);
        var boundFunction = bind$e(callbackfn, arguments.length > 1 ? arguments[1] : undefined, 3);
        var entry;

        while (entry = entry ? entry.next : state.first) {
          boundFunction(entry.value, entry.key, this); // revert to the last existing entry

          while (entry && entry.removed) entry = entry.previous;
        }
      },
      // 23.1.3.7 Map.prototype.has(key)
      // 23.2.3.7 Set.prototype.has(value)
      has: function has(key) {
        return !!getEntry(this, key);
      }
    });
    redefineAll$8(C.prototype, IS_MAP ? {
      // 23.1.3.6 Map.prototype.get(key)
      get: function get(key) {
        var entry = getEntry(this, key);
        return entry && entry.value;
      },
      // 23.1.3.9 Map.prototype.set(key, value)
      set: function set(key, value) {
        return define(this, key === 0 ? 0 : key, value);
      }
    } : {
      // 23.2.3.1 Set.prototype.add(value)
      add: function add(value) {
        return define(this, value = value === 0 ? 0 : value, value);
      }
    });
    if (DESCRIPTORS$w) defineProperty$p(C.prototype, 'size', {
      get: function () {
        return getInternalState(this).size;
      }
    });
    return C;
  },
  setStrong: function (C, CONSTRUCTOR_NAME, IS_MAP) {
    var ITERATOR_NAME = CONSTRUCTOR_NAME + ' Iterator';
    var getInternalCollectionState = internalStateGetterFor$4(CONSTRUCTOR_NAME);
    var getInternalIteratorState = internalStateGetterFor$4(ITERATOR_NAME); // add .keys, .values, .entries, [@@iterator]
    // 23.1.3.4, 23.1.3.8, 23.1.3.11, 23.1.3.12, 23.2.3.5, 23.2.3.8, 23.2.3.10, 23.2.3.11

    defineIterator$d(C, CONSTRUCTOR_NAME, function (iterated, kind) {
      setInternalState$f(this, {
        type: ITERATOR_NAME,
        target: iterated,
        state: getInternalCollectionState(iterated),
        kind: kind,
        last: undefined
      });
    }, function () {
      var state = getInternalIteratorState(this);
      var kind = state.kind;
      var entry = state.last; // revert to the last existing entry

      while (entry && entry.removed) entry = entry.previous; // get next entry


      if (!state.target || !(state.last = entry = entry ? entry.next : state.state.first)) {
        // or finish the iteration
        state.target = undefined;
        return {
          value: undefined,
          done: true
        };
      } // return step by kind


      if (kind == 'keys') return {
        value: entry.key,
        done: false
      };
      if (kind == 'values') return {
        value: entry.value,
        done: false
      };
      return {
        value: [entry.key, entry.value],
        done: false
      };
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true); // add [@@species], 23.1.2.2, 23.2.2.2

    setSpecies$8(CONSTRUCTOR_NAME);
  }
};

var collection$7 = collection$8;

var collectionStrong$6 = collectionStrong$7; // `Set` constructor
// https://tc39.es/ecma262/#sec-set-objects


collection$7('Set', function (init) {
  return function Set() {
    return init(this, arguments.length ? arguments[0] : undefined);
  };
}, collectionStrong$6);

var toInteger$b = toInteger$e;

var requireObjectCoercible$i = requireObjectCoercible$l; // `String.prototype.{ codePointAt, at }` methods implementation


var createMethod$a = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = String(requireObjectCoercible$i($this));
    var position = toInteger$b(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = S.charCodeAt(position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size || (second = S.charCodeAt(position + 1)) < 0xDC00 || second > 0xDFFF ? CONVERT_TO_STRING ? S.charAt(position) : first : CONVERT_TO_STRING ? S.slice(position, position + 2) : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

var stringMultibyte$3 = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod$a(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod$a(true)
};

var charAt$5 = stringMultibyte$3.charAt;

var InternalStateModule$g = internalState$2;

var defineIterator$c = defineIterator$f;

var STRING_ITERATOR$3 = 'String Iterator';
var setInternalState$e = InternalStateModule$g.set;
var getInternalState$c = InternalStateModule$g.getterFor(STRING_ITERATOR$3); // `String.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-string.prototype-@@iterator

defineIterator$c(String, 'String', function (iterated) {
  setInternalState$e(this, {
    type: STRING_ITERATOR$3,
    string: String(iterated),
    index: 0
  }); // `%StringIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState$c(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return {
    value: undefined,
    done: true
  };
  point = charAt$5(string, index);
  state.index += point.length;
  return {
    value: point,
    done: false
  };
});

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables$2 = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

var global$N = global$_;

var DOMIterables$5 = domIterables$2;

var ArrayIteratorMethods$3 = es_array_iterator$3;

var createNonEnumerableProperty$r = createNonEnumerableProperty$y;

var wellKnownSymbol$V = wellKnownSymbol$17;

var ITERATOR$f = wellKnownSymbol$V('iterator');
var TO_STRING_TAG$9 = wellKnownSymbol$V('toStringTag');
var ArrayValues$3 = ArrayIteratorMethods$3.values;

for (var COLLECTION_NAME$5 in DOMIterables$5) {
  var Collection$5 = global$N[COLLECTION_NAME$5];
  var CollectionPrototype$5 = Collection$5 && Collection$5.prototype;

  if (CollectionPrototype$5) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype$5[ITERATOR$f] !== ArrayValues$3) try {
      createNonEnumerableProperty$r(CollectionPrototype$5, ITERATOR$f, ArrayValues$3);
    } catch (error) {
      CollectionPrototype$5[ITERATOR$f] = ArrayValues$3;
    }

    if (!CollectionPrototype$5[TO_STRING_TAG$9]) {
      createNonEnumerableProperty$r(CollectionPrototype$5, TO_STRING_TAG$9, COLLECTION_NAME$5);
    }

    if (DOMIterables$5[COLLECTION_NAME$5]) for (var METHOD_NAME$3 in ArrayIteratorMethods$3) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype$5[METHOD_NAME$3] !== ArrayIteratorMethods$3[METHOD_NAME$3]) try {
        createNonEnumerableProperty$r(CollectionPrototype$5, METHOD_NAME$3, ArrayIteratorMethods$3[METHOD_NAME$3]);
      } catch (error) {
        CollectionPrototype$5[METHOD_NAME$3] = ArrayIteratorMethods$3[METHOD_NAME$3];
      }
    }
  }
}

var anObject$B = anObject$J; // `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags


var regexpFlags$7 = function () {
  var that = anObject$B(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};

var regexpStickyHelpers$3 = {};

var fails$V = fails$15; // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError,
// so we use an intermediate function.


function RE$3(s, f) {
  return RegExp(s, f);
}

regexpStickyHelpers$3.UNSUPPORTED_Y = fails$V(function () {
  // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
  var re = RE$3('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});
regexpStickyHelpers$3.BROKEN_CARET = fails$V(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = RE$3('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});

/* eslint-disable regexp/no-assertion-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */

/* eslint-disable regexp/no-useless-quantifier -- testing */

var regexpFlags$6 = regexpFlags$7;

var stickyHelpers$6 = regexpStickyHelpers$3;

var shared$f = shared$j.exports;

var nativeExec$3 = RegExp.prototype.exec;
var nativeReplace$3 = shared$f('native-string-replace', String.prototype.replace);
var patchedExec$3 = nativeExec$3;

var UPDATES_LAST_INDEX_WRONG$3 = function () {
  var re1 = /a/;
  var re2 = /b*/g;
  nativeExec$3.call(re1, 'a');
  nativeExec$3.call(re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
}();

var UNSUPPORTED_Y$6 = stickyHelpers$6.UNSUPPORTED_Y || stickyHelpers$6.BROKEN_CARET; // nonparticipating capturing group, copied from es5-shim's String#split patch.

var NPCG_INCLUDED$3 = /()??/.exec('')[1] !== undefined;
var PATCH$3 = UPDATES_LAST_INDEX_WRONG$3 || NPCG_INCLUDED$3 || UNSUPPORTED_Y$6;

if (PATCH$3) {
  patchedExec$3 = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;
    var sticky = UNSUPPORTED_Y$6 && re.sticky;
    var flags = regexpFlags$6.call(re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = flags.replace('y', '');

      if (flags.indexOf('g') === -1) {
        flags += 'g';
      }

      strCopy = String(str).slice(re.lastIndex); // Support anchored sticky behavior.

      if (re.lastIndex > 0 && (!re.multiline || re.multiline && str[re.lastIndex - 1] !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      } // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.


      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED$3) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }

    if (UPDATES_LAST_INDEX_WRONG$3) lastIndex = re.lastIndex;
    match = nativeExec$3.call(sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = match.input.slice(charsAdded);
        match[0] = match[0].slice(charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG$3 && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }

    if (NPCG_INCLUDED$3 && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      nativeReplace$3.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

var regexpExec$a = patchedExec$3;

var $$T = _export$2;

var exec$3 = regexpExec$a; // `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec


$$T({
  target: 'RegExp',
  proto: true,
  forced: /./.exec !== exec$3
}, {
  exec: exec$3
});

var redefine$p = redefine$v.exports;

var regexpExec$9 = regexpExec$a;

var fails$U = fails$15;

var wellKnownSymbol$U = wellKnownSymbol$17;

var createNonEnumerableProperty$q = createNonEnumerableProperty$y;

var SPECIES$e = wellKnownSymbol$U('species');
var RegExpPrototype$6 = RegExp.prototype;
var REPLACE_SUPPORTS_NAMED_GROUPS$2 = !fails$U(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;

  re.exec = function () {
    var result = [];
    result.groups = {
      a: '7'
    };
    return result;
  };

  return ''.replace(re, '$<a>') !== '7';
}); // IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0

var REPLACE_KEEPS_$0$2 = function () {
  // eslint-disable-next-line regexp/prefer-escape-replacement-dollar-char -- required for testing
  return 'a'.replace(/./, '$0') === '$0';
}();

var REPLACE$2 = wellKnownSymbol$U('replace'); // Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string

var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$2 = function () {
  if (/./[REPLACE$2]) {
    return /./[REPLACE$2]('a', '$0') === '';
  }

  return false;
}(); // Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
// Weex JS has frozen built-in prototypes, so use try / catch wrapper


var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC$2 = !fails$U(function () {
  // eslint-disable-next-line regexp/no-empty-group -- required for testing
  var re = /(?:)/;
  var originalExec = re.exec;

  re.exec = function () {
    return originalExec.apply(this, arguments);
  };

  var result = 'ab'.split(re);
  return result.length !== 2 || result[0] !== 'a' || result[1] !== 'b';
});

var fixRegexpWellKnownSymbolLogic$2 = function (KEY, length, exec, sham) {
  var SYMBOL = wellKnownSymbol$U(KEY);
  var DELEGATES_TO_SYMBOL = !fails$U(function () {
    // String methods call symbol-named RegEp methods
    var O = {};

    O[SYMBOL] = function () {
      return 7;
    };

    return ''[KEY](O) != 7;
  });
  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails$U(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {}; // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.

      re.constructor = {};

      re.constructor[SPECIES$e] = function () {
        return re;
      };

      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () {
      execCalled = true;
      return null;
    };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (!DELEGATES_TO_SYMBOL || !DELEGATES_TO_EXEC || KEY === 'replace' && !(REPLACE_SUPPORTS_NAMED_GROUPS$2 && REPLACE_KEEPS_$0$2 && !REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$2) || KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC$2) {
    var nativeRegExpMethod = /./[SYMBOL];
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      var $exec = regexp.exec;

      if ($exec === regexpExec$9 || $exec === RegExpPrototype$6.exec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return {
            done: true,
            value: nativeRegExpMethod.call(regexp, str, arg2)
          };
        }

        return {
          done: true,
          value: nativeMethod.call(str, regexp, arg2)
        };
      }

      return {
        done: false
      };
    }, {
      REPLACE_KEEPS_$0: REPLACE_KEEPS_$0$2,
      REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE: REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$2
    });
    var stringMethod = methods[0];
    var regexMethod = methods[1];
    redefine$p(String.prototype, KEY, stringMethod);
    redefine$p(RegExpPrototype$6, SYMBOL, length == 2 // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
    // 21.2.5.11 RegExp.prototype[@@split](string, limit)
    ? function (string, arg) {
      return regexMethod.call(string, this, arg);
    } // 21.2.5.6 RegExp.prototype[@@match](string)
    // 21.2.5.9 RegExp.prototype[@@search](string)
    : function (string) {
      return regexMethod.call(string, this);
    });
  }

  if (sham) createNonEnumerableProperty$q(RegExpPrototype$6[SYMBOL], 'sham', true);
};

var charAt$4 = stringMultibyte$3.charAt; // `AdvanceStringIndex` abstract operation
// https://tc39.es/ecma262/#sec-advancestringindex


var advanceStringIndex$5 = function (S, index, unicode) {
  return index + (unicode ? charAt$4(S, index).length : 1);
};

var toObject$r = toObject$v;

var floor$3 = Math.floor;
var replace$2 = ''.replace;
var SUBSTITUTION_SYMBOLS$1 = /\$([$&'`]|\d{1,2}|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED$1 = /\$([$&'`]|\d{1,2})/g; // https://tc39.es/ecma262/#sec-getsubstitution

var getSubstitution$3 = function (matched, str, position, captures, namedCaptures, replacement) {
  var tailPos = position + matched.length;
  var m = captures.length;
  var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED$1;

  if (namedCaptures !== undefined) {
    namedCaptures = toObject$r(namedCaptures);
    symbols = SUBSTITUTION_SYMBOLS$1;
  }

  return replace$2.call(replacement, symbols, function (match, ch) {
    var capture;

    switch (ch.charAt(0)) {
      case '$':
        return '$';

      case '&':
        return matched;

      case '`':
        return str.slice(0, position);

      case "'":
        return str.slice(tailPos);

      case '<':
        capture = namedCaptures[ch.slice(1, -1)];
        break;

      default:
        // \d\d?
        var n = +ch;
        if (n === 0) return match;

        if (n > m) {
          var f = floor$3(n / 10);
          if (f === 0) return match;
          if (f <= m) return captures[f - 1] === undefined ? ch.charAt(1) : captures[f - 1] + ch.charAt(1);
          return match;
        }

        capture = captures[n - 1];
    }

    return capture === undefined ? '' : capture;
  });
};

var classof$h = classofRaw$5;

var regexpExec$8 = regexpExec$a; // `RegExpExec` abstract operation
// https://tc39.es/ecma262/#sec-regexpexec


var regexpExecAbstract$2 = function (R, S) {
  var exec = R.exec;

  if (typeof exec === 'function') {
    var result = exec.call(R, S);

    if (typeof result !== 'object') {
      throw TypeError('RegExp exec method returned something other than an Object or null');
    }

    return result;
  }

  if (classof$h(R) !== 'RegExp') {
    throw TypeError('RegExp#exec called on incompatible receiver');
  }

  return regexpExec$8.call(R, S);
};

var fixRegExpWellKnownSymbolLogic$4 = fixRegexpWellKnownSymbolLogic$2;

var anObject$A = anObject$J;

var toLength$m = toLength$q;

var toInteger$a = toInteger$e;

var requireObjectCoercible$h = requireObjectCoercible$l;

var advanceStringIndex$4 = advanceStringIndex$5;

var getSubstitution$2 = getSubstitution$3;

var regExpExec$3 = regexpExecAbstract$2;

var max$6 = Math.max;
var min$7 = Math.min;

var maybeToString$1 = function (it) {
  return it === undefined ? it : String(it);
}; // @@replace logic


fixRegExpWellKnownSymbolLogic$4('replace', 2, function (REPLACE, nativeReplace, maybeCallNative, reason) {
  var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = reason.REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE;
  var REPLACE_KEEPS_$0 = reason.REPLACE_KEEPS_$0;
  var UNSAFE_SUBSTITUTE = REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE ? '$' : '$0';
  return [// `String.prototype.replace` method
  // https://tc39.es/ecma262/#sec-string.prototype.replace
  function replace(searchValue, replaceValue) {
    var O = requireObjectCoercible$h(this);
    var replacer = searchValue == undefined ? undefined : searchValue[REPLACE];
    return replacer !== undefined ? replacer.call(searchValue, O, replaceValue) : nativeReplace.call(String(O), searchValue, replaceValue);
  }, // `RegExp.prototype[@@replace]` method
  // https://tc39.es/ecma262/#sec-regexp.prototype-@@replace
  function (regexp, replaceValue) {
    if (!REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE && REPLACE_KEEPS_$0 || typeof replaceValue === 'string' && replaceValue.indexOf(UNSAFE_SUBSTITUTE) === -1) {
      var res = maybeCallNative(nativeReplace, regexp, this, replaceValue);
      if (res.done) return res.value;
    }

    var rx = anObject$A(regexp);
    var S = String(this);
    var functionalReplace = typeof replaceValue === 'function';
    if (!functionalReplace) replaceValue = String(replaceValue);
    var global = rx.global;

    if (global) {
      var fullUnicode = rx.unicode;
      rx.lastIndex = 0;
    }

    var results = [];

    while (true) {
      var result = regExpExec$3(rx, S);
      if (result === null) break;
      results.push(result);
      if (!global) break;
      var matchStr = String(result[0]);
      if (matchStr === '') rx.lastIndex = advanceStringIndex$4(S, toLength$m(rx.lastIndex), fullUnicode);
    }

    var accumulatedResult = '';
    var nextSourcePosition = 0;

    for (var i = 0; i < results.length; i++) {
      result = results[i];
      var matched = String(result[0]);
      var position = max$6(min$7(toInteger$a(result.index), S.length), 0);
      var captures = []; // NOTE: This is equivalent to
      //   captures = result.slice(1).map(maybeToString)
      // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
      // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
      // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.

      for (var j = 1; j < result.length; j++) captures.push(maybeToString$1(result[j]));

      var namedCaptures = result.groups;

      if (functionalReplace) {
        var replacerArgs = [matched].concat(captures, position, S);
        if (namedCaptures !== undefined) replacerArgs.push(namedCaptures);
        var replacement = String(replaceValue.apply(undefined, replacerArgs));
      } else {
        replacement = getSubstitution$2(matched, S, position, captures, namedCaptures, replaceValue);
      }

      if (position >= nextSourcePosition) {
        accumulatedResult += S.slice(nextSourcePosition, position) + replacement;
        nextSourcePosition = position + matched.length;
      }
    }

    return accumulatedResult + S.slice(nextSourcePosition);
  }];
});

var LOGLEVEL = window_1.LOGLEVEL,
    console$1 = window_1.console;
var levels = {
  trace: 1,
  debug: 2,
  info: 3,
  warn: 4,
  error: 5,
  silent: 10
};
var currentLogLevelString = LOGLEVEL;
var currentLogLevelNumber = levels[currentLogLevelString] || levels.info;
var logger = {
  trace: function trace(message) {
    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }

    return currentLogLevelNumber <= levels.trace && console$1.trace.apply(console$1, [message].concat(rest));
  },
  debug: function debug(message) {
    for (var _len2 = arguments.length, rest = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      rest[_key2 - 1] = arguments[_key2];
    }

    return currentLogLevelNumber <= levels.debug && console$1.debug.apply(console$1, [message].concat(rest));
  },
  info: function info(message) {
    for (var _len3 = arguments.length, rest = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      rest[_key3 - 1] = arguments[_key3];
    }

    return currentLogLevelNumber <= levels.info && console$1.info.apply(console$1, [message].concat(rest));
  },
  warn: function warn(message) {
    for (var _len4 = arguments.length, rest = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
      rest[_key4 - 1] = arguments[_key4];
    }

    return currentLogLevelNumber <= levels.warn && console$1.warn.apply(console$1, [message].concat(rest));
  },
  error: function error(message) {
    for (var _len5 = arguments.length, rest = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
      rest[_key5 - 1] = arguments[_key5];
    }

    return currentLogLevelNumber <= levels.error && console$1.error.apply(console$1, [message].concat(rest));
  },
  log: function log(message) {
    for (var _len6 = arguments.length, rest = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
      rest[_key6 - 1] = arguments[_key6];
    }

    return currentLogLevelNumber < levels.silent && console$1.log.apply(console$1, [message].concat(rest));
  }
};
var logged = new Set();
var once = function once(type) {
  return function (message) {
    if (logged.has(message)) return undefined;
    logged.add(message);

    for (var _len7 = arguments.length, rest = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
      rest[_key7 - 1] = arguments[_key7];
    }

    return logger[type].apply(logger, [message].concat(rest));
  };
};

once.clear = function () {
  return logged.clear();
};

once.trace = once('trace');
once.debug = once('debug');
once.info = once('info');
once.warn = once('warn');
once.error = once('error');
once.log = once('log');

function mockChannel() {
  var transport = {
    setHandler: function setHandler() {},
    send: function send() {}
  };
  return new Channel({
    transport: transport
  });
}

var DESCRIPTORS$v = descriptors$4;

var definePropertyModule$h = objectDefineProperty$4;

var anObject$z = anObject$W;

var objectKeys$b = objectKeys$h; // `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe


var objectDefineProperties$2 = DESCRIPTORS$v ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$z(O);
  var keys = objectKeys$b(Properties);
  var length = keys.length;
  var index = 0;
  var key;

  while (length > index) definePropertyModule$h.f(O, key = keys[index++], Properties[key]);

  return O;
};

var anObject$y = anObject$W;

var defineProperties$2 = objectDefineProperties$2;

var enumBugKeys$8 = enumBugKeys$i;

var hiddenKeys$g = hiddenKeys$v;

var html$5 = html$b;

var documentCreateElement$4 = documentCreateElement$8;

var sharedKey$c = sharedKey$k;

var GT$2 = '>';
var LT$2 = '<';
var PROTOTYPE$5 = 'prototype';
var SCRIPT$2 = 'script';
var IE_PROTO$5 = sharedKey$c('IE_PROTO');

var EmptyConstructor$2 = function () {
  /* empty */
};

var scriptTag$2 = function (content) {
  return LT$2 + SCRIPT$2 + GT$2 + content + LT$2 + '/' + SCRIPT$2 + GT$2;
}; // Create object with fake `null` prototype: use ActiveX Object with cleared prototype


var NullProtoObjectViaActiveX$2 = function (activeXDocument) {
  activeXDocument.write(scriptTag$2(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak

  return temp;
}; // Create object with fake `null` prototype: use iframe Object with cleared prototype


var NullProtoObjectViaIFrame$2 = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement$4('iframe');
  var JS = 'java' + SCRIPT$2 + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html$5.appendChild(iframe); // https://github.com/zloirock/core-js/issues/475

  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag$2('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
}; // Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug


var activeXDocument$2;

var NullProtoObject$2 = function () {
  try {
    /* global ActiveXObject -- old IE */
    activeXDocument$2 = document.domain && new ActiveXObject('htmlfile');
  } catch (error) {
    /* ignore */
  }

  NullProtoObject$2 = activeXDocument$2 ? NullProtoObjectViaActiveX$2(activeXDocument$2) : NullProtoObjectViaIFrame$2();
  var length = enumBugKeys$8.length;

  while (length--) delete NullProtoObject$2[PROTOTYPE$5][enumBugKeys$8[length]];

  return NullProtoObject$2();
};

hiddenKeys$g[IE_PROTO$5] = true; // `Object.create` method
// https://tc39.es/ecma262/#sec-object.create

var objectCreate$2 = Object.create || function create(O, Properties) {
  var result;

  if (O !== null) {
    EmptyConstructor$2[PROTOTYPE$5] = anObject$y(O);
    result = new EmptyConstructor$2();
    EmptyConstructor$2[PROTOTYPE$5] = null; // add "__proto__" for Object.getPrototypeOf polyfill

    result[IE_PROTO$5] = O;
  } else result = NullProtoObject$2();

  return Properties === undefined ? result : defineProperties$2(result, Properties);
};

var wellKnownSymbol$T = wellKnownSymbol$1o;

var create$9 = objectCreate$2;

var definePropertyModule$g = objectDefineProperty$4;

var UNSCOPABLES$2 = wellKnownSymbol$T('unscopables');
var ArrayPrototype$4 = Array.prototype; // Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

if (ArrayPrototype$4[UNSCOPABLES$2] == undefined) {
  definePropertyModule$g.f(ArrayPrototype$4, UNSCOPABLES$2, {
    configurable: true,
    value: create$9(null)
  });
} // add a key to Array.prototype[@@unscopables]


var addToUnscopables$b = function (key) {
  ArrayPrototype$4[UNSCOPABLES$2][key] = true;
};

var $$S = _export$4;

var $find$1 = arrayIteration$3.find;

var addToUnscopables$a = addToUnscopables$b;

var FIND$1 = 'find';
var SKIPS_HOLES$2 = true; // Shouldn't skip holes

if (FIND$1 in []) Array(1)[FIND$1](function () {
  SKIPS_HOLES$2 = false;
}); // `Array.prototype.find` method
// https://tc39.es/ecma262/#sec-array.prototype.find

$$S({
  target: 'Array',
  proto: true,
  forced: SKIPS_HOLES$2
}, {
  find: function find(callbackfn
  /* , that = undefined */
  ) {
    return $find$1(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$a(FIND$1);

// by @storybook/csf, with the simpler form retained for backwards compatibility.
// We will likely start exporting the more complex <StoryFnReturnType> based types in 7.0
// The `any` here is the story store's `StoreItem` record. Ideally we should probably only
// pass a defined subset of that full data, but we pass it all so far :shrug:

var types;

(function (types) {
  types["TAB"] = "tab";
  types["PANEL"] = "panel";
  types["TOOL"] = "tool";
  types["TOOLEXTRA"] = "toolextra";
  types["PREVIEW"] = "preview";
  types["NOTES_ELEMENT"] = "notes-element";
})(types || (types = {}));

function isSupportedType(type) {
  return !!Object.values(types).find(function (typeVal) {
    return typeVal === type;
  });
}

function _defineProperties$7(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$7(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$7(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$7(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _classCallCheck$7(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
var AddonStore =
/*#__PURE__*/
_createClass$7(function AddonStore() {
  var _this = this;

  _classCallCheck$7(this, AddonStore);

  this.loaders = {};
  this.elements = {};
  this.config = {};
  this.channel = void 0;
  this.serverChannel = void 0;
  this.promise = void 0;
  this.resolve = void 0;

  this.getChannel = function () {
    // this.channel should get overwritten by setChannel. If it wasn't called (e.g. in non-browser environment), set a mock instead.
    if (!_this.channel) {
      _this.setChannel(mockChannel());
    }

    return _this.channel;
  };

  this.getServerChannel = function () {
    if (!_this.serverChannel) {
      throw new Error('Accessing non-existent serverChannel');
    }

    return _this.serverChannel;
  };

  this.ready = function () {
    return _this.promise;
  };

  this.hasChannel = function () {
    return !!_this.channel;
  };

  this.hasServerChannel = function () {
    return !!_this.serverChannel;
  };

  this.setChannel = function (channel) {
    _this.channel = channel;

    _this.resolve();
  };

  this.setServerChannel = function (channel) {
    _this.serverChannel = channel;
  };

  this.getElements = function (type) {
    if (!_this.elements[type]) {
      _this.elements[type] = {};
    }

    return _this.elements[type];
  };

  this.addPanel = function (name, options) {
    _this.add(name, Object.assign({
      type: types.PANEL
    }, options));
  };

  this.add = function (name, addon) {
    var type = addon.type;

    var collection = _this.getElements(type);

    collection[name] = Object.assign({
      id: name
    }, addon);
  };

  this.setConfig = function (value) {
    Object.assign(_this.config, value);
  };

  this.getConfig = function () {
    return _this.config;
  };

  this.register = function (name, registerCallback) {
    if (_this.loaders[name]) {
      logger.warn("".concat(name, " was loaded twice, this could have bad side-effects"));
    }

    _this.loaders[name] = registerCallback;
  };

  this.loadAddons = function (api) {
    Object.values(_this.loaders).forEach(function (value) {
      return value(api);
    });
  };

  this.promise = new Promise(function (res) {
    _this.resolve = function () {
      return res(_this.getChannel());
    };
  });
}); // Enforce addons store to be a singleton

var KEY = '__STORYBOOK_ADDONS';

function getAddonsStore() {
  if (!window_1[KEY]) {
    window_1[KEY] = new AddonStore();
  }

  return window_1[KEY];
} // Exporting this twice in order to to be able to import it like { addons } instead of 'addons'
// prefer import { addons } from '@storybook/addons' over import addons from '@storybook/addons'
//
// See public_api.ts


var addons = getAddonsStore();

var DESCRIPTORS$u = descriptors$4;

var defineProperty$o = objectDefineProperty$4.f;

var FunctionPrototype$2 = Function.prototype;
var FunctionPrototypeToString$2 = FunctionPrototype$2.toString;
var nameRE$2 = /^\s*function ([^ (]*)/;
var NAME$2 = 'name'; // Function instances `.name` property
// https://tc39.es/ecma262/#sec-function-instances-name

if (DESCRIPTORS$u && !(NAME$2 in FunctionPrototype$2)) {
  defineProperty$o(FunctionPrototype$2, NAME$2, {
    configurable: true,
    get: function () {
      try {
        return FunctionPrototypeToString$2.call(this).match(nameRE$2)[1];
      } catch (error) {
        return '';
      }
    }
  });
}

var toPrimitive$c = toPrimitive$n;

var definePropertyModule$f = objectDefineProperty$4;

var createPropertyDescriptor$e = createPropertyDescriptor$q;

var createProperty$b = function (object, key, value) {
  var propertyKey = toPrimitive$c(key);
  if (propertyKey in object) definePropertyModule$f.f(object, propertyKey, createPropertyDescriptor$e(0, value));else object[propertyKey] = value;
};

var fails$T = fails$1r;

var wellKnownSymbol$S = wellKnownSymbol$1o;

var V8_VERSION$8 = engineV8Version$4;

var SPECIES$d = wellKnownSymbol$S('species');

var arrayMethodHasSpeciesSupport$d = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION$8 >= 51 || !fails$T(function () {
    var array = [];
    var constructor = array.constructor = {};

    constructor[SPECIES$d] = function () {
      return {
        foo: 1
      };
    };

    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};

var $$R = _export$4;

var fails$S = fails$1r;

var isArray$v = isArray$D;

var isObject$T = isObject$1k;

var toObject$q = toObject$D;

var toLength$l = toLength$y;

var createProperty$a = createProperty$b;

var arraySpeciesCreate$6 = arraySpeciesCreate$c;

var arrayMethodHasSpeciesSupport$c = arrayMethodHasSpeciesSupport$d;

var wellKnownSymbol$R = wellKnownSymbol$1o;

var V8_VERSION$7 = engineV8Version$4;

var IS_CONCAT_SPREADABLE$2 = wellKnownSymbol$R('isConcatSpreadable');
var MAX_SAFE_INTEGER$6 = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED$2 = 'Maximum allowed index exceeded'; // We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679

var IS_CONCAT_SPREADABLE_SUPPORT$2 = V8_VERSION$7 >= 51 || !fails$S(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE$2] = false;
  return array.concat()[0] !== array;
});
var SPECIES_SUPPORT$2 = arrayMethodHasSpeciesSupport$c('concat');

var isConcatSpreadable$2 = function (O) {
  if (!isObject$T(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE$2];
  return spreadable !== undefined ? !!spreadable : isArray$v(O);
};

var FORCED$6 = !IS_CONCAT_SPREADABLE_SUPPORT$2 || !SPECIES_SUPPORT$2; // `Array.prototype.concat` method
// https://tc39.es/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species

$$R({
  target: 'Array',
  proto: true,
  forced: FORCED$6
}, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  concat: function concat(arg) {
    var O = toObject$q(this);
    var A = arraySpeciesCreate$6(O, 0);
    var n = 0;
    var i, k, length, len, E;

    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];

      if (isConcatSpreadable$2(E)) {
        len = toLength$l(E.length);
        if (n + len > MAX_SAFE_INTEGER$6) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$2);

        for (k = 0; k < len; k++, n++) if (k in E) createProperty$a(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER$6) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$2);
        createProperty$a(A, n++, E);
      }
    }

    A.length = n;
    return A;
  }
});

var makeDecorator = function makeDecorator(_ref) {
  var name = _ref.name,
      parameterName = _ref.parameterName,
      wrapper = _ref.wrapper,
      _ref$skipIfNoParamete = _ref.skipIfNoParametersOrOptions,
      skipIfNoParametersOrOptions = _ref$skipIfNoParamete === void 0 ? false : _ref$skipIfNoParamete;

  var decorator = function decorator(options) {
    return function (storyFn, context) {
      var parameters = context.parameters && context.parameters[parameterName];

      if (parameters && parameters.disable) {
        return storyFn(context);
      }

      if (skipIfNoParametersOrOptions && !options && !parameters) {
        return storyFn(context);
      }

      return wrapper(storyFn, context, {
        options: options,
        parameters: parameters
      });
    };
  };

  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    } // Used without options as .addDecorator(decorator)


    if (typeof args[0] === 'function') {
      return decorator().apply(void 0, args);
    }

    return function () {
      // Used as [.]addDecorator(decorator(options))
      if (arguments.length > 1) {
        // Used as [.]addDecorator(decorator(option1, option2))
        if (args.length > 1) {
          return decorator(args).apply(void 0, arguments);
        }

        return decorator.apply(void 0, args).apply(void 0, arguments);
      }

      throw new Error("Passing stories directly into ".concat(name, "() is not allowed,\n        instead use addDecorator(").concat(name, ") and pass options with the '").concat(parameterName, "' parameter"));
    };
  };
};

var fails$R = fails$1r;

var correctPrototypeGetter$2 = !fails$R(function () {
  function F() {
    /* empty */
  }

  F.prototype.constructor = null; // eslint-disable-next-line es/no-object-getprototypeof -- required for testing

  return Object.getPrototypeOf(new F()) !== F.prototype;
});

var has$C = has$11;

var toObject$p = toObject$D;

var sharedKey$b = sharedKey$k;

var CORRECT_PROTOTYPE_GETTER$3 = correctPrototypeGetter$2;

var IE_PROTO$4 = sharedKey$b('IE_PROTO');
var ObjectPrototype$5 = Object.prototype; // `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
// eslint-disable-next-line es/no-object-getprototypeof -- safe

var objectGetPrototypeOf$2 = CORRECT_PROTOTYPE_GETTER$3 ? Object.getPrototypeOf : function (O) {
  O = toObject$p(O);
  if (has$C(O, IE_PROTO$4)) return O[IE_PROTO$4];

  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  }

  return O instanceof Object ? ObjectPrototype$5 : null;
};

var fails$Q = fails$1r;

var getPrototypeOf$5 = objectGetPrototypeOf$2;

var createNonEnumerableProperty$p = createNonEnumerableProperty$K;

var has$B = has$11;

var wellKnownSymbol$Q = wellKnownSymbol$1o;

var ITERATOR$e = wellKnownSymbol$Q('iterator');
var BUGGY_SAFARI_ITERATORS$5 = false;

var returnThis$8 = function () {
  return this;
}; // `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object


var IteratorPrototype$8, PrototypeOfArrayIteratorPrototype$2, arrayIterator$2;
/* eslint-disable es/no-array-prototype-keys -- safe */

if ([].keys) {
  arrayIterator$2 = [].keys(); // Safari 8 has buggy iterators w/o `next`

  if (!('next' in arrayIterator$2)) BUGGY_SAFARI_ITERATORS$5 = true;else {
    PrototypeOfArrayIteratorPrototype$2 = getPrototypeOf$5(getPrototypeOf$5(arrayIterator$2));
    if (PrototypeOfArrayIteratorPrototype$2 !== Object.prototype) IteratorPrototype$8 = PrototypeOfArrayIteratorPrototype$2;
  }
}

var NEW_ITERATOR_PROTOTYPE$2 = IteratorPrototype$8 == undefined || fails$Q(function () {
  var test = {}; // FF44- legacy iterators case

  return IteratorPrototype$8[ITERATOR$e].call(test) !== test;
});
if (NEW_ITERATOR_PROTOTYPE$2) IteratorPrototype$8 = {}; // 25.1.2.1.1 %IteratorPrototype%[@@iterator]()

if (!has$B(IteratorPrototype$8, ITERATOR$e)) {
  createNonEnumerableProperty$p(IteratorPrototype$8, ITERATOR$e, returnThis$8);
}

var iteratorsCore$2 = {
  IteratorPrototype: IteratorPrototype$8,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS$5
};

var IteratorPrototype$7 = iteratorsCore$2.IteratorPrototype;

var create$8 = objectCreate$2;

var createPropertyDescriptor$d = createPropertyDescriptor$q;

var setToStringTag$e = setToStringTag$k;

var Iterators$c = iterators$3;

var returnThis$7 = function () {
  return this;
};

var createIteratorConstructor$5 = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create$8(IteratorPrototype$7, {
    next: createPropertyDescriptor$d(1, next)
  });
  setToStringTag$e(IteratorConstructor, TO_STRING_TAG, false);
  Iterators$c[TO_STRING_TAG] = returnThis$7;
  return IteratorConstructor;
};

var $$Q = _export$4;

var createIteratorConstructor$4 = createIteratorConstructor$5;

var getPrototypeOf$4 = objectGetPrototypeOf$2;

var setPrototypeOf$6 = objectSetPrototypeOf$3;

var setToStringTag$d = setToStringTag$k;

var createNonEnumerableProperty$o = createNonEnumerableProperty$K;

var redefine$o = redefine$E.exports;

var wellKnownSymbol$P = wellKnownSymbol$1o;

var Iterators$b = iterators$3;

var IteratorsCore$2 = iteratorsCore$2;

var IteratorPrototype$6 = IteratorsCore$2.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS$4 = IteratorsCore$2.BUGGY_SAFARI_ITERATORS;
var ITERATOR$d = wellKnownSymbol$P('iterator');
var KEYS$2 = 'keys';
var VALUES$2 = 'values';
var ENTRIES$2 = 'entries';

var returnThis$6 = function () {
  return this;
};

var defineIterator$b = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor$4(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS$4 && KIND in IterablePrototype) return IterablePrototype[KIND];

    switch (KIND) {
      case KEYS$2:
        return function keys() {
          return new IteratorConstructor(this, KIND);
        };

      case VALUES$2:
        return function values() {
          return new IteratorConstructor(this, KIND);
        };

      case ENTRIES$2:
        return function entries() {
          return new IteratorConstructor(this, KIND);
        };
    }

    return function () {
      return new IteratorConstructor(this);
    };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR$d] || IterablePrototype['@@iterator'] || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS$4 && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY; // fix native

  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf$4(anyNativeIterator.call(new Iterable()));

    if (IteratorPrototype$6 !== Object.prototype && CurrentIteratorPrototype.next) {
      if (getPrototypeOf$4(CurrentIteratorPrototype) !== IteratorPrototype$6) {
        if (setPrototypeOf$6) {
          setPrototypeOf$6(CurrentIteratorPrototype, IteratorPrototype$6);
        } else if (typeof CurrentIteratorPrototype[ITERATOR$d] != 'function') {
          createNonEnumerableProperty$o(CurrentIteratorPrototype, ITERATOR$d, returnThis$6);
        }
      } // Set @@toStringTag to native iterators


      setToStringTag$d(CurrentIteratorPrototype, TO_STRING_TAG, true);
    }
  } // fix Array#{values, @@iterator}.name in V8 / FF


  if (DEFAULT == VALUES$2 && nativeIterator && nativeIterator.name !== VALUES$2) {
    INCORRECT_VALUES_NAME = true;

    defaultIterator = function values() {
      return nativeIterator.call(this);
    };
  } // define iterator


  if (IterablePrototype[ITERATOR$d] !== defaultIterator) {
    createNonEnumerableProperty$o(IterablePrototype, ITERATOR$d, defaultIterator);
  }

  Iterators$b[NAME] = defaultIterator; // export additional methods

  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES$2),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS$2),
      entries: getIterationMethod(ENTRIES$2)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS$4 || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine$o(IterablePrototype, KEY, methods[KEY]);
      }
    } else $$Q({
      target: NAME,
      proto: true,
      forced: BUGGY_SAFARI_ITERATORS$4 || INCORRECT_VALUES_NAME
    }, methods);
  }

  return methods;
};

var toIndexedObject$m = toIndexedObject$B;

var addToUnscopables$9 = addToUnscopables$b;

var Iterators$a = iterators$3;

var InternalStateModule$f = internalState$4;

var defineIterator$a = defineIterator$b;

var ARRAY_ITERATOR$2 = 'Array Iterator';
var setInternalState$d = InternalStateModule$f.set;
var getInternalState$b = InternalStateModule$f.getterFor(ARRAY_ITERATOR$2); // `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator

var es_array_iterator$2 = defineIterator$a(Array, 'Array', function (iterated, kind) {
  setInternalState$d(this, {
    type: ARRAY_ITERATOR$2,
    target: toIndexedObject$m(iterated),
    // target
    index: 0,
    // next index
    kind: kind // kind

  }); // `%ArrayIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState$b(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;

  if (!target || index >= target.length) {
    state.target = undefined;
    return {
      value: undefined,
      done: true
    };
  }

  if (kind == 'keys') return {
    value: index,
    done: false
  };
  if (kind == 'values') return {
    value: target[index],
    done: false
  };
  return {
    value: [index, target[index]],
    done: false
  };
}, 'values'); // argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject

Iterators$a.Arguments = Iterators$a.Array; // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$9('keys');
addToUnscopables$9('values');
addToUnscopables$9('entries');

var toInteger$9 = toInteger$k;

var requireObjectCoercible$g = requireObjectCoercible$r; // `String.prototype.{ codePointAt, at }` methods implementation


var createMethod$9 = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = String(requireObjectCoercible$g($this));
    var position = toInteger$9(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = S.charCodeAt(position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size || (second = S.charCodeAt(position + 1)) < 0xDC00 || second > 0xDFFF ? CONVERT_TO_STRING ? S.charAt(position) : first : CONVERT_TO_STRING ? S.slice(position, position + 2) : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

var stringMultibyte$2 = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod$9(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod$9(true)
};

var charAt$3 = stringMultibyte$2.charAt;

var InternalStateModule$e = internalState$4;

var defineIterator$9 = defineIterator$b;

var STRING_ITERATOR$2 = 'String Iterator';
var setInternalState$c = InternalStateModule$e.set;
var getInternalState$a = InternalStateModule$e.getterFor(STRING_ITERATOR$2); // `String.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-string.prototype-@@iterator

defineIterator$9(String, 'String', function (iterated) {
  setInternalState$c(this, {
    type: STRING_ITERATOR$2,
    string: String(iterated),
    index: 0
  }); // `%StringIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState$a(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return {
    value: undefined,
    done: true
  };
  point = charAt$3(string, index);
  state.index += point.length;
  return {
    value: point,
    done: false
  };
});

var internalMetadata$2 = {exports: {}};

var fails$P = fails$1r;

var freezing$2 = !fails$P(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});

var hiddenKeys$f = hiddenKeys$v;

var isObject$S = isObject$1k;

var has$A = has$11;

var defineProperty$n = objectDefineProperty$4.f;

var uid$b = uid$m;

var FREEZING$4 = freezing$2;

var METADATA$2 = uid$b('meta');
var id$5 = 0; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible$3 = Object.isExtensible || function () {
  return true;
};

var setMetadata$2 = function (it) {
  defineProperty$n(it, METADATA$2, {
    value: {
      objectID: 'O' + ++id$5,
      // object ID
      weakData: {} // weak collections IDs

    }
  });
};

var fastKey$5 = function (it, create) {
  // return a primitive with prefix
  if (!isObject$S(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;

  if (!has$A(it, METADATA$2)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$3(it)) return 'F'; // not necessary to add metadata

    if (!create) return 'E'; // add missing metadata

    setMetadata$2(it); // return object ID
  }

  return it[METADATA$2].objectID;
};

var getWeakData$3 = function (it, create) {
  if (!has$A(it, METADATA$2)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$3(it)) return true; // not necessary to add metadata

    if (!create) return false; // add missing metadata

    setMetadata$2(it); // return the store of weak collections IDs
  }

  return it[METADATA$2].weakData;
}; // add metadata on freeze-family methods calling


var onFreeze$4 = function (it) {
  if (FREEZING$4 && meta$2.REQUIRED && isExtensible$3(it) && !has$A(it, METADATA$2)) setMetadata$2(it);
  return it;
};

var meta$2 = internalMetadata$2.exports = {
  REQUIRED: false,
  fastKey: fastKey$5,
  getWeakData: getWeakData$3,
  onFreeze: onFreeze$4
};
hiddenKeys$f[METADATA$2] = true;

var isObject$R = isObject$1k;

var setPrototypeOf$5 = objectSetPrototypeOf$3; // makes subclassing work correct for wrapped built-ins


var inheritIfRequired$8 = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if ( // it can work only with native `setPrototypeOf`
  setPrototypeOf$5 && // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
  typeof (NewTarget = dummy.constructor) == 'function' && NewTarget !== Wrapper && isObject$R(NewTargetPrototype = NewTarget.prototype) && NewTargetPrototype !== Wrapper.prototype) setPrototypeOf$5($this, NewTargetPrototype);
  return $this;
};

var $$P = _export$4;

var global$M = global$1x;

var isForced$a = isForced_1$4;

var redefine$n = redefine$E.exports;

var InternalMetadataModule$3 = internalMetadata$2.exports;

var iterate$9 = iterate$e;

var anInstance$9 = anInstance$e;

var isObject$Q = isObject$1k;

var fails$O = fails$1r;

var checkCorrectnessOfIteration$8 = checkCorrectnessOfIteration$c;

var setToStringTag$c = setToStringTag$k;

var inheritIfRequired$7 = inheritIfRequired$8;

var collection$6 = function (CONSTRUCTOR_NAME, wrapper, common) {
  var IS_MAP = CONSTRUCTOR_NAME.indexOf('Map') !== -1;
  var IS_WEAK = CONSTRUCTOR_NAME.indexOf('Weak') !== -1;
  var ADDER = IS_MAP ? 'set' : 'add';
  var NativeConstructor = global$M[CONSTRUCTOR_NAME];
  var NativePrototype = NativeConstructor && NativeConstructor.prototype;
  var Constructor = NativeConstructor;
  var exported = {};

  var fixMethod = function (KEY) {
    var nativeMethod = NativePrototype[KEY];
    redefine$n(NativePrototype, KEY, KEY == 'add' ? function add(value) {
      nativeMethod.call(this, value === 0 ? 0 : value);
      return this;
    } : KEY == 'delete' ? function (key) {
      return IS_WEAK && !isObject$Q(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'get' ? function get(key) {
      return IS_WEAK && !isObject$Q(key) ? undefined : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'has' ? function has(key) {
      return IS_WEAK && !isObject$Q(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : function set(key, value) {
      nativeMethod.call(this, key === 0 ? 0 : key, value);
      return this;
    });
  };

  var REPLACE = isForced$a(CONSTRUCTOR_NAME, typeof NativeConstructor != 'function' || !(IS_WEAK || NativePrototype.forEach && !fails$O(function () {
    new NativeConstructor().entries().next();
  })));

  if (REPLACE) {
    // create collection constructor
    Constructor = common.getConstructor(wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER);
    InternalMetadataModule$3.REQUIRED = true;
  } else if (isForced$a(CONSTRUCTOR_NAME, true)) {
    var instance = new Constructor(); // early implementations not supports chaining

    var HASNT_CHAINING = instance[ADDER](IS_WEAK ? {} : -0, 1) != instance; // V8 ~ Chromium 40- weak-collections throws on primitives, but should return false

    var THROWS_ON_PRIMITIVES = fails$O(function () {
      instance.has(1);
    }); // most early implementations doesn't supports iterables, most modern - not close it correctly
    // eslint-disable-next-line no-new -- required for testing

    var ACCEPT_ITERABLES = checkCorrectnessOfIteration$8(function (iterable) {
      new NativeConstructor(iterable);
    }); // for early implementations -0 and +0 not the same

    var BUGGY_ZERO = !IS_WEAK && fails$O(function () {
      // V8 ~ Chromium 42- fails only with 5+ elements
      var $instance = new NativeConstructor();
      var index = 5;

      while (index--) $instance[ADDER](index, index);

      return !$instance.has(-0);
    });

    if (!ACCEPT_ITERABLES) {
      Constructor = wrapper(function (dummy, iterable) {
        anInstance$9(dummy, Constructor, CONSTRUCTOR_NAME);
        var that = inheritIfRequired$7(new NativeConstructor(), dummy, Constructor);
        if (iterable != undefined) iterate$9(iterable, that[ADDER], {
          that: that,
          AS_ENTRIES: IS_MAP
        });
        return that;
      });
      Constructor.prototype = NativePrototype;
      NativePrototype.constructor = Constructor;
    }

    if (THROWS_ON_PRIMITIVES || BUGGY_ZERO) {
      fixMethod('delete');
      fixMethod('has');
      IS_MAP && fixMethod('get');
    }

    if (BUGGY_ZERO || HASNT_CHAINING) fixMethod(ADDER); // weak collections should not contains .clear method

    if (IS_WEAK && NativePrototype.clear) delete NativePrototype.clear;
  }

  exported[CONSTRUCTOR_NAME] = Constructor;
  $$P({
    global: true,
    forced: Constructor != NativeConstructor
  }, exported);
  setToStringTag$c(Constructor, CONSTRUCTOR_NAME);
  if (!IS_WEAK) common.setStrong(Constructor, CONSTRUCTOR_NAME, IS_MAP);
  return Constructor;
};

var redefineAll$7 = redefineAll$b;

var getWeakData$2 = internalMetadata$2.exports.getWeakData;

var anObject$x = anObject$W;

var isObject$P = isObject$1k;

var anInstance$8 = anInstance$e;

var iterate$8 = iterate$e;

var ArrayIterationModule = arrayIteration$3;

var $has = has$11;

var InternalStateModule$d = internalState$4;

var setInternalState$b = InternalStateModule$d.set;
var internalStateGetterFor$3 = InternalStateModule$d.getterFor;
var find = ArrayIterationModule.find;
var findIndex = ArrayIterationModule.findIndex;
var id$4 = 0; // fallback for uncaught frozen keys

var uncaughtFrozenStore = function (store) {
  return store.frozen || (store.frozen = new UncaughtFrozenStore());
};

var UncaughtFrozenStore = function () {
  this.entries = [];
};

var findUncaughtFrozen = function (store, key) {
  return find(store.entries, function (it) {
    return it[0] === key;
  });
};

UncaughtFrozenStore.prototype = {
  get: function (key) {
    var entry = findUncaughtFrozen(this, key);
    if (entry) return entry[1];
  },
  has: function (key) {
    return !!findUncaughtFrozen(this, key);
  },
  set: function (key, value) {
    var entry = findUncaughtFrozen(this, key);
    if (entry) entry[1] = value;else this.entries.push([key, value]);
  },
  'delete': function (key) {
    var index = findIndex(this.entries, function (it) {
      return it[0] === key;
    });
    if (~index) this.entries.splice(index, 1);
    return !!~index;
  }
};
var collectionWeak$1 = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance$8(that, C, CONSTRUCTOR_NAME);
      setInternalState$b(that, {
        type: CONSTRUCTOR_NAME,
        id: id$4++,
        frozen: undefined
      });
      if (iterable != undefined) iterate$8(iterable, that[ADDER], {
        that: that,
        AS_ENTRIES: IS_MAP
      });
    });
    var getInternalState = internalStateGetterFor$3(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var data = getWeakData$2(anObject$x(key), true);
      if (data === true) uncaughtFrozenStore(state).set(key, value);else data[state.id] = value;
      return that;
    };

    redefineAll$7(C.prototype, {
      // 23.3.3.2 WeakMap.prototype.delete(key)
      // 23.4.3.3 WeakSet.prototype.delete(value)
      'delete': function (key) {
        var state = getInternalState(this);
        if (!isObject$P(key)) return false;
        var data = getWeakData$2(key);
        if (data === true) return uncaughtFrozenStore(state)['delete'](key);
        return data && $has(data, state.id) && delete data[state.id];
      },
      // 23.3.3.4 WeakMap.prototype.has(key)
      // 23.4.3.4 WeakSet.prototype.has(value)
      has: function has(key) {
        var state = getInternalState(this);
        if (!isObject$P(key)) return false;
        var data = getWeakData$2(key);
        if (data === true) return uncaughtFrozenStore(state).has(key);
        return data && $has(data, state.id);
      }
    });
    redefineAll$7(C.prototype, IS_MAP ? {
      // 23.3.3.3 WeakMap.prototype.get(key)
      get: function get(key) {
        var state = getInternalState(this);

        if (isObject$P(key)) {
          var data = getWeakData$2(key);
          if (data === true) return uncaughtFrozenStore(state).get(key);
          return data ? data[state.id] : undefined;
        }
      },
      // 23.3.3.5 WeakMap.prototype.set(key, value)
      set: function set(key, value) {
        return define(this, key, value);
      }
    } : {
      // 23.4.3.1 WeakSet.prototype.add(value)
      add: function add(value) {
        return define(this, value, true);
      }
    });
    return C;
  }
};

var global$L = global$1x;

var redefineAll$6 = redefineAll$b;

var InternalMetadataModule$2 = internalMetadata$2.exports;

var collection$5 = collection$6;

var collectionWeak = collectionWeak$1;

var isObject$O = isObject$1k;

var enforceIternalState = internalState$4.enforce;

var NATIVE_WEAK_MAP$2 = nativeWeakMap$4;

var IS_IE11 = !global$L.ActiveXObject && 'ActiveXObject' in global$L; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible$2 = Object.isExtensible;
var InternalWeakMap;

var wrapper = function (init) {
  return function WeakMap() {
    return init(this, arguments.length ? arguments[0] : undefined);
  };
}; // `WeakMap` constructor
// https://tc39.es/ecma262/#sec-weakmap-constructor


var $WeakMap$1 = collection$5('WeakMap', wrapper, collectionWeak); // IE11 WeakMap frozen keys fix
// We can't use feature detection because it crash some old IE builds
// https://github.com/zloirock/core-js/issues/485

if (NATIVE_WEAK_MAP$2 && IS_IE11) {
  InternalWeakMap = collectionWeak.getConstructor(wrapper, 'WeakMap', true);
  InternalMetadataModule$2.REQUIRED = true;
  var WeakMapPrototype = $WeakMap$1.prototype;
  var nativeDelete = WeakMapPrototype['delete'];
  var nativeHas = WeakMapPrototype.has;
  var nativeGet = WeakMapPrototype.get;
  var nativeSet = WeakMapPrototype.set;
  redefineAll$6(WeakMapPrototype, {
    'delete': function (key) {
      if (isObject$O(key) && !isExtensible$2(key)) {
        var state = enforceIternalState(this);
        if (!state.frozen) state.frozen = new InternalWeakMap();
        return nativeDelete.call(this, key) || state.frozen['delete'](key);
      }

      return nativeDelete.call(this, key);
    },
    has: function has(key) {
      if (isObject$O(key) && !isExtensible$2(key)) {
        var state = enforceIternalState(this);
        if (!state.frozen) state.frozen = new InternalWeakMap();
        return nativeHas.call(this, key) || state.frozen.has(key);
      }

      return nativeHas.call(this, key);
    },
    get: function get(key) {
      if (isObject$O(key) && !isExtensible$2(key)) {
        var state = enforceIternalState(this);
        if (!state.frozen) state.frozen = new InternalWeakMap();
        return nativeHas.call(this, key) ? nativeGet.call(this, key) : state.frozen.get(key);
      }

      return nativeGet.call(this, key);
    },
    set: function set(key, value) {
      if (isObject$O(key) && !isExtensible$2(key)) {
        var state = enforceIternalState(this);
        if (!state.frozen) state.frozen = new InternalWeakMap();
        nativeHas.call(this, key) ? nativeSet.call(this, key, value) : state.frozen.set(key, value);
      } else nativeSet.call(this, key, value);

      return this;
    }
  });
}

var global$K = global$1x;

var DOMIterables$4 = domIterables$4;

var ArrayIteratorMethods$2 = es_array_iterator$2;

var createNonEnumerableProperty$n = createNonEnumerableProperty$K;

var wellKnownSymbol$O = wellKnownSymbol$1o;

var ITERATOR$c = wellKnownSymbol$O('iterator');
var TO_STRING_TAG$8 = wellKnownSymbol$O('toStringTag');
var ArrayValues$2 = ArrayIteratorMethods$2.values;

for (var COLLECTION_NAME$4 in DOMIterables$4) {
  var Collection$4 = global$K[COLLECTION_NAME$4];
  var CollectionPrototype$4 = Collection$4 && Collection$4.prototype;

  if (CollectionPrototype$4) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype$4[ITERATOR$c] !== ArrayValues$2) try {
      createNonEnumerableProperty$n(CollectionPrototype$4, ITERATOR$c, ArrayValues$2);
    } catch (error) {
      CollectionPrototype$4[ITERATOR$c] = ArrayValues$2;
    }

    if (!CollectionPrototype$4[TO_STRING_TAG$8]) {
      createNonEnumerableProperty$n(CollectionPrototype$4, TO_STRING_TAG$8, COLLECTION_NAME$4);
    }

    if (DOMIterables$4[COLLECTION_NAME$4]) for (var METHOD_NAME$2 in ArrayIteratorMethods$2) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype$4[METHOD_NAME$2] !== ArrayIteratorMethods$2[METHOD_NAME$2]) try {
        createNonEnumerableProperty$n(CollectionPrototype$4, METHOD_NAME$2, ArrayIteratorMethods$2[METHOD_NAME$2]);
      } catch (error) {
        CollectionPrototype$4[METHOD_NAME$2] = ArrayIteratorMethods$2[METHOD_NAME$2];
      }
    }
  }
}

var defineProperty$m = objectDefineProperty$4.f;

var create$7 = objectCreate$2;

var redefineAll$5 = redefineAll$b;

var bind$d = functionBindContext$4;

var anInstance$7 = anInstance$e;

var iterate$7 = iterate$e;

var defineIterator$8 = defineIterator$b;

var setSpecies$7 = setSpecies$b;

var DESCRIPTORS$t = descriptors$4;

var fastKey$4 = internalMetadata$2.exports.fastKey;

var InternalStateModule$c = internalState$4;

var setInternalState$a = InternalStateModule$c.set;
var internalStateGetterFor$2 = InternalStateModule$c.getterFor;
var collectionStrong$5 = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance$7(that, C, CONSTRUCTOR_NAME);
      setInternalState$a(that, {
        type: CONSTRUCTOR_NAME,
        index: create$7(null),
        first: undefined,
        last: undefined,
        size: 0
      });
      if (!DESCRIPTORS$t) that.size = 0;
      if (iterable != undefined) iterate$7(iterable, that[ADDER], {
        that: that,
        AS_ENTRIES: IS_MAP
      });
    });
    var getInternalState = internalStateGetterFor$2(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var entry = getEntry(that, key);
      var previous, index; // change existing entry

      if (entry) {
        entry.value = value; // create new entry
      } else {
        state.last = entry = {
          index: index = fastKey$4(key, true),
          key: key,
          value: value,
          previous: previous = state.last,
          next: undefined,
          removed: false
        };
        if (!state.first) state.first = entry;
        if (previous) previous.next = entry;
        if (DESCRIPTORS$t) state.size++;else that.size++; // add to index

        if (index !== 'F') state.index[index] = entry;
      }

      return that;
    };

    var getEntry = function (that, key) {
      var state = getInternalState(that); // fast case

      var index = fastKey$4(key);
      var entry;
      if (index !== 'F') return state.index[index]; // frozen object case

      for (entry = state.first; entry; entry = entry.next) {
        if (entry.key == key) return entry;
      }
    };

    redefineAll$5(C.prototype, {
      // 23.1.3.1 Map.prototype.clear()
      // 23.2.3.2 Set.prototype.clear()
      clear: function clear() {
        var that = this;
        var state = getInternalState(that);
        var data = state.index;
        var entry = state.first;

        while (entry) {
          entry.removed = true;
          if (entry.previous) entry.previous = entry.previous.next = undefined;
          delete data[entry.index];
          entry = entry.next;
        }

        state.first = state.last = undefined;
        if (DESCRIPTORS$t) state.size = 0;else that.size = 0;
      },
      // 23.1.3.3 Map.prototype.delete(key)
      // 23.2.3.4 Set.prototype.delete(value)
      'delete': function (key) {
        var that = this;
        var state = getInternalState(that);
        var entry = getEntry(that, key);

        if (entry) {
          var next = entry.next;
          var prev = entry.previous;
          delete state.index[entry.index];
          entry.removed = true;
          if (prev) prev.next = next;
          if (next) next.previous = prev;
          if (state.first == entry) state.first = next;
          if (state.last == entry) state.last = prev;
          if (DESCRIPTORS$t) state.size--;else that.size--;
        }

        return !!entry;
      },
      // 23.2.3.6 Set.prototype.forEach(callbackfn, thisArg = undefined)
      // 23.1.3.5 Map.prototype.forEach(callbackfn, thisArg = undefined)
      forEach: function forEach(callbackfn
      /* , that = undefined */
      ) {
        var state = getInternalState(this);
        var boundFunction = bind$d(callbackfn, arguments.length > 1 ? arguments[1] : undefined, 3);
        var entry;

        while (entry = entry ? entry.next : state.first) {
          boundFunction(entry.value, entry.key, this); // revert to the last existing entry

          while (entry && entry.removed) entry = entry.previous;
        }
      },
      // 23.1.3.7 Map.prototype.has(key)
      // 23.2.3.7 Set.prototype.has(value)
      has: function has(key) {
        return !!getEntry(this, key);
      }
    });
    redefineAll$5(C.prototype, IS_MAP ? {
      // 23.1.3.6 Map.prototype.get(key)
      get: function get(key) {
        var entry = getEntry(this, key);
        return entry && entry.value;
      },
      // 23.1.3.9 Map.prototype.set(key, value)
      set: function set(key, value) {
        return define(this, key === 0 ? 0 : key, value);
      }
    } : {
      // 23.2.3.1 Set.prototype.add(value)
      add: function add(value) {
        return define(this, value = value === 0 ? 0 : value, value);
      }
    });
    if (DESCRIPTORS$t) defineProperty$m(C.prototype, 'size', {
      get: function () {
        return getInternalState(this).size;
      }
    });
    return C;
  },
  setStrong: function (C, CONSTRUCTOR_NAME, IS_MAP) {
    var ITERATOR_NAME = CONSTRUCTOR_NAME + ' Iterator';
    var getInternalCollectionState = internalStateGetterFor$2(CONSTRUCTOR_NAME);
    var getInternalIteratorState = internalStateGetterFor$2(ITERATOR_NAME); // add .keys, .values, .entries, [@@iterator]
    // 23.1.3.4, 23.1.3.8, 23.1.3.11, 23.1.3.12, 23.2.3.5, 23.2.3.8, 23.2.3.10, 23.2.3.11

    defineIterator$8(C, CONSTRUCTOR_NAME, function (iterated, kind) {
      setInternalState$a(this, {
        type: ITERATOR_NAME,
        target: iterated,
        state: getInternalCollectionState(iterated),
        kind: kind,
        last: undefined
      });
    }, function () {
      var state = getInternalIteratorState(this);
      var kind = state.kind;
      var entry = state.last; // revert to the last existing entry

      while (entry && entry.removed) entry = entry.previous; // get next entry


      if (!state.target || !(state.last = entry = entry ? entry.next : state.state.first)) {
        // or finish the iteration
        state.target = undefined;
        return {
          value: undefined,
          done: true
        };
      } // return step by kind


      if (kind == 'keys') return {
        value: entry.key,
        done: false
      };
      if (kind == 'values') return {
        value: entry.value,
        done: false
      };
      return {
        value: [entry.key, entry.value],
        done: false
      };
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true); // add [@@species], 23.1.2.2, 23.2.2.2

    setSpecies$7(CONSTRUCTOR_NAME);
  }
};

var collection$4 = collection$6;

var collectionStrong$4 = collectionStrong$5; // `Set` constructor
// https://tc39.es/ecma262/#sec-set-objects


collection$4('Set', function (init) {
  return function Set() {
    return init(this, arguments.length ? arguments[0] : undefined);
  };
}, collectionStrong$4);

var $$O = _export$4;

var $includes$1 = arrayIncludes$4.includes;

var addToUnscopables$8 = addToUnscopables$b; // `Array.prototype.includes` method
// https://tc39.es/ecma262/#sec-array.prototype.includes


$$O({
  target: 'Array',
  proto: true
}, {
  includes: function includes(el
  /* , fromIndex = 0 */
  ) {
    return $includes$1(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$8('includes');

var isObject$N = isObject$1k;

var classof$g = classofRaw$9;

var wellKnownSymbol$N = wellKnownSymbol$1o;

var MATCH$6 = wellKnownSymbol$N('match'); // `IsRegExp` abstract operation
// https://tc39.es/ecma262/#sec-isregexp

var isRegexp$2 = function (it) {
  var isRegExp;
  return isObject$N(it) && ((isRegExp = it[MATCH$6]) !== undefined ? !!isRegExp : classof$g(it) == 'RegExp');
};

var isRegExp$6 = isRegexp$2;

var notARegexp$1 = function (it) {
  if (isRegExp$6(it)) {
    throw TypeError("The method doesn't accept regular expressions");
  }

  return it;
};

var wellKnownSymbol$M = wellKnownSymbol$1o;

var MATCH$5 = wellKnownSymbol$M('match');

var correctIsRegexpLogic$1 = function (METHOD_NAME) {
  var regexp = /./;

  try {
    '/./'[METHOD_NAME](regexp);
  } catch (error1) {
    try {
      regexp[MATCH$5] = false;
      return '/./'[METHOD_NAME](regexp);
    } catch (error2) {
      /* empty */
    }
  }

  return false;
};

var $$N = _export$4;

var notARegExp$2 = notARegexp$1;

var requireObjectCoercible$f = requireObjectCoercible$r;

var correctIsRegExpLogic$2 = correctIsRegexpLogic$1; // `String.prototype.includes` method
// https://tc39.es/ecma262/#sec-string.prototype.includes


$$N({
  target: 'String',
  proto: true,
  forced: !correctIsRegExpLogic$2('includes')
}, {
  includes: function includes(searchString
  /* , position = 0 */
  ) {
    return !!~String(requireObjectCoercible$f(this)).indexOf(notARegExp$2(searchString), arguments.length > 1 ? arguments[1] : undefined);
  }
});

var $$M = _export$4;

var $map$2 = arrayIteration$3.map;

var arrayMethodHasSpeciesSupport$b = arrayMethodHasSpeciesSupport$d;

var HAS_SPECIES_SUPPORT$7 = arrayMethodHasSpeciesSupport$b('map'); // `Array.prototype.map` method
// https://tc39.es/ecma262/#sec-array.prototype.map
// with adding support of @@species

$$M({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$7
}, {
  map: function map(callbackfn
  /* , thisArg */
  ) {
    return $map$2(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

var $$L = _export$4;

var $entries$2 = objectToArray$2.entries; // `Object.entries` method
// https://tc39.es/ecma262/#sec-object.entries


$$L({
  target: 'Object',
  stat: true
}, {
  entries: function entries(O) {
    return $entries$2(O);
  }
});

var $$K = _export$4;

var toObject$o = toObject$D;

var nativeKeys$6 = objectKeys$h;

var fails$N = fails$1r;

var FAILS_ON_PRIMITIVES$5 = fails$N(function () {
  nativeKeys$6(1);
}); // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys

$$K({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$5
}, {
  keys: function keys(it) {
    return nativeKeys$6(toObject$o(it));
  }
});

var objectGetOwnPropertyNamesExternal$2 = {};

/* eslint-disable es/no-object-getownpropertynames -- safe */

var toIndexedObject$l = toIndexedObject$B;

var $getOwnPropertyNames$5 = objectGetOwnPropertyNames$4.f;

var toString$c = {}.toString;
var windowNames$2 = typeof window == 'object' && window && Object.getOwnPropertyNames ? Object.getOwnPropertyNames(window) : [];

var getWindowNames$2 = function (it) {
  try {
    return $getOwnPropertyNames$5(it);
  } catch (error) {
    return windowNames$2.slice();
  }
}; // fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window


objectGetOwnPropertyNamesExternal$2.f = function getOwnPropertyNames(it) {
  return windowNames$2 && toString$c.call(it) == '[object Window]' ? getWindowNames$2(it) : $getOwnPropertyNames$5(toIndexedObject$l(it));
};

var wellKnownSymbolWrapped$2 = {};

var wellKnownSymbol$L = wellKnownSymbol$1o;

wellKnownSymbolWrapped$2.f = wellKnownSymbol$L;

var path$6 = path$c;

var has$z = has$11;

var wrappedWellKnownSymbolModule$5 = wellKnownSymbolWrapped$2;

var defineProperty$l = objectDefineProperty$4.f;

var defineWellKnownSymbol$8 = function (NAME) {
  var Symbol = path$6.Symbol || (path$6.Symbol = {});
  if (!has$z(Symbol, NAME)) defineProperty$l(Symbol, NAME, {
    value: wrappedWellKnownSymbolModule$5.f(NAME)
  });
};

var $$J = _export$4;

var global$J = global$1x;

var getBuiltIn$d = getBuiltIn$s;

var DESCRIPTORS$s = descriptors$4;

var NATIVE_SYMBOL$6 = nativeSymbol$4;

var USE_SYMBOL_AS_UID$4 = useSymbolAsUid$4;

var fails$M = fails$1r;

var has$y = has$11;

var isArray$u = isArray$D;

var isObject$M = isObject$1k;

var anObject$w = anObject$W;

var toObject$n = toObject$D;

var toIndexedObject$k = toIndexedObject$B;

var toPrimitive$b = toPrimitive$n;

var createPropertyDescriptor$c = createPropertyDescriptor$q;

var nativeObjectCreate$2 = objectCreate$2;

var objectKeys$a = objectKeys$h;

var getOwnPropertyNamesModule$4 = objectGetOwnPropertyNames$4;

var getOwnPropertyNamesExternal$2 = objectGetOwnPropertyNamesExternal$2;

var getOwnPropertySymbolsModule$6 = objectGetOwnPropertySymbols$4;

var getOwnPropertyDescriptorModule$4 = objectGetOwnPropertyDescriptor$4;

var definePropertyModule$e = objectDefineProperty$4;

var propertyIsEnumerableModule$6 = objectPropertyIsEnumerable$4;

var createNonEnumerableProperty$m = createNonEnumerableProperty$K;

var redefine$m = redefine$E.exports;

var shared$e = shared$r.exports;

var sharedKey$a = sharedKey$k;

var hiddenKeys$e = hiddenKeys$v;

var uid$a = uid$m;

var wellKnownSymbol$K = wellKnownSymbol$1o;

var wrappedWellKnownSymbolModule$4 = wellKnownSymbolWrapped$2;

var defineWellKnownSymbol$7 = defineWellKnownSymbol$8;

var setToStringTag$b = setToStringTag$k;

var InternalStateModule$b = internalState$4;

var $forEach$4 = arrayIteration$3.forEach;

var HIDDEN$2 = sharedKey$a('hidden');
var SYMBOL$2 = 'Symbol';
var PROTOTYPE$4 = 'prototype';
var TO_PRIMITIVE$2 = wellKnownSymbol$K('toPrimitive');
var setInternalState$9 = InternalStateModule$b.set;
var getInternalState$9 = InternalStateModule$b.getterFor(SYMBOL$2);
var ObjectPrototype$4 = Object[PROTOTYPE$4];
var $Symbol$2 = global$J.Symbol;
var $stringify$2 = getBuiltIn$d('JSON', 'stringify');
var nativeGetOwnPropertyDescriptor$2 = getOwnPropertyDescriptorModule$4.f;
var nativeDefineProperty$2 = definePropertyModule$e.f;
var nativeGetOwnPropertyNames$2 = getOwnPropertyNamesExternal$2.f;
var nativePropertyIsEnumerable$2 = propertyIsEnumerableModule$6.f;
var AllSymbols$2 = shared$e('symbols');
var ObjectPrototypeSymbols$2 = shared$e('op-symbols');
var StringToSymbolRegistry$2 = shared$e('string-to-symbol-registry');
var SymbolToStringRegistry$2 = shared$e('symbol-to-string-registry');
var WellKnownSymbolsStore$4 = shared$e('wks');
var QObject$2 = global$J.QObject; // Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173

var USE_SETTER$2 = !QObject$2 || !QObject$2[PROTOTYPE$4] || !QObject$2[PROTOTYPE$4].findChild; // fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687

var setSymbolDescriptor$2 = DESCRIPTORS$s && fails$M(function () {
  return nativeObjectCreate$2(nativeDefineProperty$2({}, 'a', {
    get: function () {
      return nativeDefineProperty$2(this, 'a', {
        value: 7
      }).a;
    }
  })).a != 7;
}) ? function (O, P, Attributes) {
  var ObjectPrototypeDescriptor = nativeGetOwnPropertyDescriptor$2(ObjectPrototype$4, P);
  if (ObjectPrototypeDescriptor) delete ObjectPrototype$4[P];
  nativeDefineProperty$2(O, P, Attributes);

  if (ObjectPrototypeDescriptor && O !== ObjectPrototype$4) {
    nativeDefineProperty$2(ObjectPrototype$4, P, ObjectPrototypeDescriptor);
  }
} : nativeDefineProperty$2;

var wrap$2 = function (tag, description) {
  var symbol = AllSymbols$2[tag] = nativeObjectCreate$2($Symbol$2[PROTOTYPE$4]);
  setInternalState$9(symbol, {
    type: SYMBOL$2,
    tag: tag,
    description: description
  });
  if (!DESCRIPTORS$s) symbol.description = description;
  return symbol;
};

var isSymbol$b = USE_SYMBOL_AS_UID$4 ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  return Object(it) instanceof $Symbol$2;
};

var $defineProperty$4 = function defineProperty(O, P, Attributes) {
  if (O === ObjectPrototype$4) $defineProperty$4(ObjectPrototypeSymbols$2, P, Attributes);
  anObject$w(O);
  var key = toPrimitive$b(P, true);
  anObject$w(Attributes);

  if (has$y(AllSymbols$2, key)) {
    if (!Attributes.enumerable) {
      if (!has$y(O, HIDDEN$2)) nativeDefineProperty$2(O, HIDDEN$2, createPropertyDescriptor$c(1, {}));
      O[HIDDEN$2][key] = true;
    } else {
      if (has$y(O, HIDDEN$2) && O[HIDDEN$2][key]) O[HIDDEN$2][key] = false;
      Attributes = nativeObjectCreate$2(Attributes, {
        enumerable: createPropertyDescriptor$c(0, false)
      });
    }

    return setSymbolDescriptor$2(O, key, Attributes);
  }

  return nativeDefineProperty$2(O, key, Attributes);
};

var $defineProperties$2 = function defineProperties(O, Properties) {
  anObject$w(O);
  var properties = toIndexedObject$k(Properties);
  var keys = objectKeys$a(properties).concat($getOwnPropertySymbols$2(properties));
  $forEach$4(keys, function (key) {
    if (!DESCRIPTORS$s || $propertyIsEnumerable$4.call(properties, key)) $defineProperty$4(O, key, properties[key]);
  });
  return O;
};

var $create$2 = function create(O, Properties) {
  return Properties === undefined ? nativeObjectCreate$2(O) : $defineProperties$2(nativeObjectCreate$2(O), Properties);
};

var $propertyIsEnumerable$4 = function propertyIsEnumerable(V) {
  var P = toPrimitive$b(V, true);
  var enumerable = nativePropertyIsEnumerable$2.call(this, P);
  if (this === ObjectPrototype$4 && has$y(AllSymbols$2, P) && !has$y(ObjectPrototypeSymbols$2, P)) return false;
  return enumerable || !has$y(this, P) || !has$y(AllSymbols$2, P) || has$y(this, HIDDEN$2) && this[HIDDEN$2][P] ? enumerable : true;
};

var $getOwnPropertyDescriptor$4 = function getOwnPropertyDescriptor(O, P) {
  var it = toIndexedObject$k(O);
  var key = toPrimitive$b(P, true);
  if (it === ObjectPrototype$4 && has$y(AllSymbols$2, key) && !has$y(ObjectPrototypeSymbols$2, key)) return;
  var descriptor = nativeGetOwnPropertyDescriptor$2(it, key);

  if (descriptor && has$y(AllSymbols$2, key) && !(has$y(it, HIDDEN$2) && it[HIDDEN$2][key])) {
    descriptor.enumerable = true;
  }

  return descriptor;
};

var $getOwnPropertyNames$4 = function getOwnPropertyNames(O) {
  var names = nativeGetOwnPropertyNames$2(toIndexedObject$k(O));
  var result = [];
  $forEach$4(names, function (key) {
    if (!has$y(AllSymbols$2, key) && !has$y(hiddenKeys$e, key)) result.push(key);
  });
  return result;
};

var $getOwnPropertySymbols$2 = function getOwnPropertySymbols(O) {
  var IS_OBJECT_PROTOTYPE = O === ObjectPrototype$4;
  var names = nativeGetOwnPropertyNames$2(IS_OBJECT_PROTOTYPE ? ObjectPrototypeSymbols$2 : toIndexedObject$k(O));
  var result = [];
  $forEach$4(names, function (key) {
    if (has$y(AllSymbols$2, key) && (!IS_OBJECT_PROTOTYPE || has$y(ObjectPrototype$4, key))) {
      result.push(AllSymbols$2[key]);
    }
  });
  return result;
}; // `Symbol` constructor
// https://tc39.es/ecma262/#sec-symbol-constructor


if (!NATIVE_SYMBOL$6) {
  $Symbol$2 = function Symbol() {
    if (this instanceof $Symbol$2) throw TypeError('Symbol is not a constructor');
    var description = !arguments.length || arguments[0] === undefined ? undefined : String(arguments[0]);
    var tag = uid$a(description);

    var setter = function (value) {
      if (this === ObjectPrototype$4) setter.call(ObjectPrototypeSymbols$2, value);
      if (has$y(this, HIDDEN$2) && has$y(this[HIDDEN$2], tag)) this[HIDDEN$2][tag] = false;
      setSymbolDescriptor$2(this, tag, createPropertyDescriptor$c(1, value));
    };

    if (DESCRIPTORS$s && USE_SETTER$2) setSymbolDescriptor$2(ObjectPrototype$4, tag, {
      configurable: true,
      set: setter
    });
    return wrap$2(tag, description);
  };

  redefine$m($Symbol$2[PROTOTYPE$4], 'toString', function toString() {
    return getInternalState$9(this).tag;
  });
  redefine$m($Symbol$2, 'withoutSetter', function (description) {
    return wrap$2(uid$a(description), description);
  });
  propertyIsEnumerableModule$6.f = $propertyIsEnumerable$4;
  definePropertyModule$e.f = $defineProperty$4;
  getOwnPropertyDescriptorModule$4.f = $getOwnPropertyDescriptor$4;
  getOwnPropertyNamesModule$4.f = getOwnPropertyNamesExternal$2.f = $getOwnPropertyNames$4;
  getOwnPropertySymbolsModule$6.f = $getOwnPropertySymbols$2;

  wrappedWellKnownSymbolModule$4.f = function (name) {
    return wrap$2(wellKnownSymbol$K(name), name);
  };

  if (DESCRIPTORS$s) {
    // https://github.com/tc39/proposal-Symbol-description
    nativeDefineProperty$2($Symbol$2[PROTOTYPE$4], 'description', {
      configurable: true,
      get: function description() {
        return getInternalState$9(this).description;
      }
    });

    {
      redefine$m(ObjectPrototype$4, 'propertyIsEnumerable', $propertyIsEnumerable$4, {
        unsafe: true
      });
    }
  }
}

$$J({
  global: true,
  wrap: true,
  forced: !NATIVE_SYMBOL$6,
  sham: !NATIVE_SYMBOL$6
}, {
  Symbol: $Symbol$2
});
$forEach$4(objectKeys$a(WellKnownSymbolsStore$4), function (name) {
  defineWellKnownSymbol$7(name);
});
$$J({
  target: SYMBOL$2,
  stat: true,
  forced: !NATIVE_SYMBOL$6
}, {
  // `Symbol.for` method
  // https://tc39.es/ecma262/#sec-symbol.for
  'for': function (key) {
    var string = String(key);
    if (has$y(StringToSymbolRegistry$2, string)) return StringToSymbolRegistry$2[string];
    var symbol = $Symbol$2(string);
    StringToSymbolRegistry$2[string] = symbol;
    SymbolToStringRegistry$2[symbol] = string;
    return symbol;
  },
  // `Symbol.keyFor` method
  // https://tc39.es/ecma262/#sec-symbol.keyfor
  keyFor: function keyFor(sym) {
    if (!isSymbol$b(sym)) throw TypeError(sym + ' is not a symbol');
    if (has$y(SymbolToStringRegistry$2, sym)) return SymbolToStringRegistry$2[sym];
  },
  useSetter: function () {
    USE_SETTER$2 = true;
  },
  useSimple: function () {
    USE_SETTER$2 = false;
  }
});
$$J({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL$6,
  sham: !DESCRIPTORS$s
}, {
  // `Object.create` method
  // https://tc39.es/ecma262/#sec-object.create
  create: $create$2,
  // `Object.defineProperty` method
  // https://tc39.es/ecma262/#sec-object.defineproperty
  defineProperty: $defineProperty$4,
  // `Object.defineProperties` method
  // https://tc39.es/ecma262/#sec-object.defineproperties
  defineProperties: $defineProperties$2,
  // `Object.getOwnPropertyDescriptor` method
  // https://tc39.es/ecma262/#sec-object.getownpropertydescriptors
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor$4
});
$$J({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL$6
}, {
  // `Object.getOwnPropertyNames` method
  // https://tc39.es/ecma262/#sec-object.getownpropertynames
  getOwnPropertyNames: $getOwnPropertyNames$4,
  // `Object.getOwnPropertySymbols` method
  // https://tc39.es/ecma262/#sec-object.getownpropertysymbols
  getOwnPropertySymbols: $getOwnPropertySymbols$2
}); // Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443

$$J({
  target: 'Object',
  stat: true,
  forced: fails$M(function () {
    getOwnPropertySymbolsModule$6.f(1);
  })
}, {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return getOwnPropertySymbolsModule$6.f(toObject$n(it));
  }
}); // `JSON.stringify` method behavior with symbols
// https://tc39.es/ecma262/#sec-json.stringify

if ($stringify$2) {
  var FORCED_JSON_STRINGIFY$2 = !NATIVE_SYMBOL$6 || fails$M(function () {
    var symbol = $Symbol$2(); // MS Edge converts symbol values to JSON as {}

    return $stringify$2([symbol]) != '[null]' // WebKit converts symbol values to JSON as null
    || $stringify$2({
      a: symbol
    }) != '{}' // V8 throws on boxed symbols
    || $stringify$2(Object(symbol)) != '{}';
  });
  $$J({
    target: 'JSON',
    stat: true,
    forced: FORCED_JSON_STRINGIFY$2
  }, {
    // eslint-disable-next-line no-unused-vars -- required for `.length`
    stringify: function stringify(it, replacer, space) {
      var args = [it];
      var index = 1;
      var $replacer;

      while (arguments.length > index) args.push(arguments[index++]);

      $replacer = replacer;
      if (!isObject$M(replacer) && it === undefined || isSymbol$b(it)) return; // IE8 returns string on undefined

      if (!isArray$u(replacer)) replacer = function (key, value) {
        if (typeof $replacer == 'function') value = $replacer.call(this, key, value);
        if (!isSymbol$b(value)) return value;
      };
      args[1] = replacer;
      return $stringify$2.apply(null, args);
    }
  });
} // `Symbol.prototype[@@toPrimitive]` method
// https://tc39.es/ecma262/#sec-symbol.prototype-@@toprimitive


if (!$Symbol$2[PROTOTYPE$4][TO_PRIMITIVE$2]) {
  createNonEnumerableProperty$m($Symbol$2[PROTOTYPE$4], TO_PRIMITIVE$2, $Symbol$2[PROTOTYPE$4].valueOf);
} // `Symbol.prototype[@@toStringTag]` property
// https://tc39.es/ecma262/#sec-symbol.prototype-@@tostringtag


setToStringTag$b($Symbol$2, SYMBOL$2);
hiddenKeys$e[HIDDEN$2] = true;

var $$I = _export$4;

var DESCRIPTORS$r = descriptors$4;

var global$I = global$1x;

var has$x = has$11;

var isObject$L = isObject$1k;

var defineProperty$k = objectDefineProperty$4.f;

var copyConstructorProperties$6 = copyConstructorProperties$c;

var NativeSymbol$2 = global$I.Symbol;

if (DESCRIPTORS$r && typeof NativeSymbol$2 == 'function' && (!('description' in NativeSymbol$2.prototype) || // Safari 12 bug
NativeSymbol$2().description !== undefined)) {
  var EmptyStringDescriptionStore$2 = {}; // wrap Symbol constructor for correct work with undefined description

  var SymbolWrapper$2 = function Symbol() {
    var description = arguments.length < 1 || arguments[0] === undefined ? undefined : String(arguments[0]);
    var result = this instanceof SymbolWrapper$2 ? new NativeSymbol$2(description) // in Edge 13, String(Symbol(undefined)) === 'Symbol(undefined)'
    : description === undefined ? NativeSymbol$2() : NativeSymbol$2(description);
    if (description === '') EmptyStringDescriptionStore$2[result] = true;
    return result;
  };

  copyConstructorProperties$6(SymbolWrapper$2, NativeSymbol$2);
  var symbolPrototype$2 = SymbolWrapper$2.prototype = NativeSymbol$2.prototype;
  symbolPrototype$2.constructor = SymbolWrapper$2;
  var symbolToString$4 = symbolPrototype$2.toString;
  var native$2 = String(NativeSymbol$2('test')) == 'Symbol(test)';
  var regexp$2 = /^Symbol\((.*)\)[^)]+$/;
  defineProperty$k(symbolPrototype$2, 'description', {
    configurable: true,
    get: function description() {
      var symbol = isObject$L(this) ? this.valueOf() : this;
      var string = symbolToString$4.call(symbol);
      if (has$x(EmptyStringDescriptionStore$2, symbol)) return '';
      var desc = native$2 ? string.slice(7, -1) : string.replace(regexp$2, '$1');
      return desc === '' ? undefined : desc;
    }
  });
  $$I({
    global: true,
    forced: true
  }, {
    Symbol: SymbolWrapper$2
  });
}

var defineWellKnownSymbol$6 = defineWellKnownSymbol$8; // `Symbol.iterator` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.iterator


defineWellKnownSymbol$6('iterator');

var anObject$v = anObject$W;

var iteratorClose$6 = iteratorClose$a; // call something on iterator step with safe closing on error


var callWithSafeIterationClosing$5 = function (iterator, fn, value, ENTRIES) {
  try {
    return ENTRIES ? fn(anObject$v(value)[0], value[1]) : fn(value); // 7.4.6 IteratorClose(iterator, completion)
  } catch (error) {
    iteratorClose$6(iterator);
    throw error;
  }
};

var bind$c = functionBindContext$4;

var toObject$m = toObject$D;

var callWithSafeIterationClosing$4 = callWithSafeIterationClosing$5;

var isArrayIteratorMethod$6 = isArrayIteratorMethod$a;

var toLength$k = toLength$y;

var createProperty$9 = createProperty$b;

var getIteratorMethod$6 = getIteratorMethod$a; // `Array.from` method implementation
// https://tc39.es/ecma262/#sec-array.from


var arrayFrom$2 = function from(arrayLike
/* , mapfn = undefined, thisArg = undefined */
) {
  var O = toObject$m(arrayLike);
  var C = typeof this == 'function' ? this : Array;
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod$6(O);
  var index = 0;
  var length, result, step, iterator, next, value;
  if (mapping) mapfn = bind$c(mapfn, argumentsLength > 2 ? arguments[2] : undefined, 2); // if the target is not iterable or it's an array with the default iterator - use a simple case

  if (iteratorMethod != undefined && !(C == Array && isArrayIteratorMethod$6(iteratorMethod))) {
    iterator = iteratorMethod.call(O);
    next = iterator.next;
    result = new C();

    for (; !(step = next.call(iterator)).done; index++) {
      value = mapping ? callWithSafeIterationClosing$4(iterator, mapfn, [step.value, index], true) : step.value;
      createProperty$9(result, index, value);
    }
  } else {
    length = toLength$k(O.length);
    result = new C(length);

    for (; length > index; index++) {
      value = mapping ? mapfn(O[index], index) : O[index];
      createProperty$9(result, index, value);
    }
  }

  result.length = index;
  return result;
};

var $$H = _export$4;

var from$2 = arrayFrom$2;

var checkCorrectnessOfIteration$7 = checkCorrectnessOfIteration$c;

var INCORRECT_ITERATION$3 = !checkCorrectnessOfIteration$7(function (iterable) {
  // eslint-disable-next-line es/no-array-from -- required for testing
  Array.from(iterable);
}); // `Array.from` method
// https://tc39.es/ecma262/#sec-array.from

$$H({
  target: 'Array',
  stat: true,
  forced: INCORRECT_ITERATION$3
}, {
  from: from$2
});

var $$G = _export$4;

var isObject$K = isObject$1k;

var isArray$t = isArray$D;

var toAbsoluteIndex$7 = toAbsoluteIndex$e;

var toLength$j = toLength$y;

var toIndexedObject$j = toIndexedObject$B;

var createProperty$8 = createProperty$b;

var wellKnownSymbol$J = wellKnownSymbol$1o;

var arrayMethodHasSpeciesSupport$a = arrayMethodHasSpeciesSupport$d;

var HAS_SPECIES_SUPPORT$6 = arrayMethodHasSpeciesSupport$a('slice');
var SPECIES$c = wellKnownSymbol$J('species');
var nativeSlice$2 = [].slice;
var max$5 = Math.max; // `Array.prototype.slice` method
// https://tc39.es/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects

$$G({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$6
}, {
  slice: function slice(start, end) {
    var O = toIndexedObject$j(this);
    var length = toLength$j(O.length);
    var k = toAbsoluteIndex$7(start, length);
    var fin = toAbsoluteIndex$7(end === undefined ? length : end, length); // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible

    var Constructor, result, n;

    if (isArray$t(O)) {
      Constructor = O.constructor; // cross-realm fallback

      if (typeof Constructor == 'function' && (Constructor === Array || isArray$t(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject$K(Constructor)) {
        Constructor = Constructor[SPECIES$c];
        if (Constructor === null) Constructor = undefined;
      }

      if (Constructor === Array || Constructor === undefined) {
        return nativeSlice$2.call(O, k, fin);
      }
    }

    result = new (Constructor === undefined ? Array : Constructor)(max$5(fin - k, 0));

    for (n = 0; k < fin; k++, n++) if (k in O) createProperty$8(result, n, O[k]);

    result.length = n;
    return result;
  }
});

var anObject$u = anObject$W; // `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags


var regexpFlags$5 = function () {
  var that = anObject$u(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};

var regexpStickyHelpers$2 = {};

var fails$L = fails$1r; // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError,
// so we use an intermediate function.


function RE$2(s, f) {
  return RegExp(s, f);
}

regexpStickyHelpers$2.UNSUPPORTED_Y = fails$L(function () {
  // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
  var re = RE$2('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});
regexpStickyHelpers$2.BROKEN_CARET = fails$L(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = RE$2('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});

/* eslint-disable regexp/no-assertion-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */

/* eslint-disable regexp/no-useless-quantifier -- testing */

var regexpFlags$4 = regexpFlags$5;

var stickyHelpers$5 = regexpStickyHelpers$2;

var shared$d = shared$r.exports;

var nativeExec$2 = RegExp.prototype.exec;
var nativeReplace$2 = shared$d('native-string-replace', String.prototype.replace);
var patchedExec$2 = nativeExec$2;

var UPDATES_LAST_INDEX_WRONG$2 = function () {
  var re1 = /a/;
  var re2 = /b*/g;
  nativeExec$2.call(re1, 'a');
  nativeExec$2.call(re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
}();

var UNSUPPORTED_Y$5 = stickyHelpers$5.UNSUPPORTED_Y || stickyHelpers$5.BROKEN_CARET; // nonparticipating capturing group, copied from es5-shim's String#split patch.

var NPCG_INCLUDED$2 = /()??/.exec('')[1] !== undefined;
var PATCH$2 = UPDATES_LAST_INDEX_WRONG$2 || NPCG_INCLUDED$2 || UNSUPPORTED_Y$5;

if (PATCH$2) {
  patchedExec$2 = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;
    var sticky = UNSUPPORTED_Y$5 && re.sticky;
    var flags = regexpFlags$4.call(re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = flags.replace('y', '');

      if (flags.indexOf('g') === -1) {
        flags += 'g';
      }

      strCopy = String(str).slice(re.lastIndex); // Support anchored sticky behavior.

      if (re.lastIndex > 0 && (!re.multiline || re.multiline && str[re.lastIndex - 1] !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      } // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.


      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED$2) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }

    if (UPDATES_LAST_INDEX_WRONG$2) lastIndex = re.lastIndex;
    match = nativeExec$2.call(sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = match.input.slice(charsAdded);
        match[0] = match[0].slice(charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG$2 && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }

    if (NPCG_INCLUDED$2 && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      nativeReplace$2.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

var regexpExec$7 = patchedExec$2;

var $$F = _export$4;

var exec$2 = regexpExec$7; // `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec


$$F({
  target: 'RegExp',
  proto: true,
  forced: /./.exec !== exec$2
}, {
  exec: exec$2
});

var events; // Enables: `import Events from ...`

(function (events) {
  events["CHANNEL_CREATED"] = "channelCreated";
  events["CONFIG_ERROR"] = "configError";
  events["STORY_INDEX_INVALIDATED"] = "storyIndexInvalidated";
  events["STORY_SPECIFIED"] = "storySpecified";
  events["SET_STORIES"] = "setStories";
  events["SET_CURRENT_STORY"] = "setCurrentStory";
  events["CURRENT_STORY_WAS_SET"] = "currentStoryWasSet";
  events["FORCE_RE_RENDER"] = "forceReRender";
  events["FORCE_REMOUNT"] = "forceRemount";
  events["PRELOAD_STORIES"] = "preloadStories";
  events["STORY_PREPARED"] = "storyPrepared";
  events["STORY_CHANGED"] = "storyChanged";
  events["STORY_UNCHANGED"] = "storyUnchanged";
  events["STORY_RENDERED"] = "storyRendered";
  events["STORY_MISSING"] = "storyMissing";
  events["STORY_ERRORED"] = "storyErrored";
  events["STORY_THREW_EXCEPTION"] = "storyThrewException";
  events["STORY_RENDER_PHASE_CHANGED"] = "storyRenderPhaseChanged";
  events["UPDATE_STORY_ARGS"] = "updateStoryArgs";
  events["STORY_ARGS_UPDATED"] = "storyArgsUpdated";
  events["RESET_STORY_ARGS"] = "resetStoryArgs";
  events["SET_GLOBALS"] = "setGlobals";
  events["UPDATE_GLOBALS"] = "updateGlobals";
  events["GLOBALS_UPDATED"] = "globalsUpdated";
  events["REGISTER_SUBSCRIPTION"] = "registerSubscription";
  events["PREVIEW_KEYDOWN"] = "previewKeydown";
  events["SELECT_STORY"] = "selectStory";
  events["STORIES_COLLAPSE_ALL"] = "storiesCollapseAll";
  events["STORIES_EXPAND_ALL"] = "storiesExpandAll";
  events["DOCS_RENDERED"] = "docsRendered";
  events["SHARED_STATE_CHANGED"] = "sharedStateChanged";
  events["SHARED_STATE_SET"] = "sharedStateSet";
  events["NAVIGATE_URL"] = "navigateUrl";
  events["UPDATE_QUERY_PARAMS"] = "updateQueryParams";
})(events || (events = {}));

var events$1 = events; // Enables: `import * as Events from ...` or `import { CHANNEL_CREATED } as Events from ...`
// This is the preferred method

var CHANNEL_CREATED = events.CHANNEL_CREATED,
    CONFIG_ERROR = events.CONFIG_ERROR,
    STORY_INDEX_INVALIDATED = events.STORY_INDEX_INVALIDATED,
    STORY_SPECIFIED = events.STORY_SPECIFIED,
    SET_STORIES = events.SET_STORIES,
    SET_CURRENT_STORY = events.SET_CURRENT_STORY,
    CURRENT_STORY_WAS_SET = events.CURRENT_STORY_WAS_SET,
    FORCE_RE_RENDER = events.FORCE_RE_RENDER,
    FORCE_REMOUNT = events.FORCE_REMOUNT,
    STORY_PREPARED = events.STORY_PREPARED,
    STORY_CHANGED = events.STORY_CHANGED,
    STORY_UNCHANGED = events.STORY_UNCHANGED,
    PRELOAD_STORIES = events.PRELOAD_STORIES,
    STORY_RENDERED = events.STORY_RENDERED,
    STORY_MISSING = events.STORY_MISSING,
    STORY_ERRORED = events.STORY_ERRORED,
    STORY_THREW_EXCEPTION = events.STORY_THREW_EXCEPTION,
    STORY_RENDER_PHASE_CHANGED = events.STORY_RENDER_PHASE_CHANGED,
    UPDATE_STORY_ARGS = events.UPDATE_STORY_ARGS,
    STORY_ARGS_UPDATED = events.STORY_ARGS_UPDATED,
    RESET_STORY_ARGS = events.RESET_STORY_ARGS,
    SET_GLOBALS = events.SET_GLOBALS,
    UPDATE_GLOBALS = events.UPDATE_GLOBALS,
    GLOBALS_UPDATED = events.GLOBALS_UPDATED,
    REGISTER_SUBSCRIPTION = events.REGISTER_SUBSCRIPTION,
    PREVIEW_KEYDOWN = events.PREVIEW_KEYDOWN,
    SELECT_STORY = events.SELECT_STORY,
    STORIES_COLLAPSE_ALL = events.STORIES_COLLAPSE_ALL,
    STORIES_EXPAND_ALL = events.STORIES_EXPAND_ALL,
    DOCS_RENDERED = events.DOCS_RENDERED,
    SHARED_STATE_CHANGED = events.SHARED_STATE_CHANGED,
    SHARED_STATE_SET = events.SHARED_STATE_SET,
    NAVIGATE_URL = events.NAVIGATE_URL,
    UPDATE_QUERY_PARAMS = events.UPDATE_QUERY_PARAMS; // Used to break out of the current render without showing a redbox
var IGNORED_EXCEPTION = new Error('ignoredException');

var esm$1 = /*#__PURE__*/Object.freeze({
	__proto__: null,
	'default': events$1,
	CHANNEL_CREATED: CHANNEL_CREATED,
	CONFIG_ERROR: CONFIG_ERROR,
	STORY_INDEX_INVALIDATED: STORY_INDEX_INVALIDATED,
	STORY_SPECIFIED: STORY_SPECIFIED,
	SET_STORIES: SET_STORIES,
	SET_CURRENT_STORY: SET_CURRENT_STORY,
	CURRENT_STORY_WAS_SET: CURRENT_STORY_WAS_SET,
	FORCE_RE_RENDER: FORCE_RE_RENDER,
	FORCE_REMOUNT: FORCE_REMOUNT,
	STORY_PREPARED: STORY_PREPARED,
	STORY_CHANGED: STORY_CHANGED,
	STORY_UNCHANGED: STORY_UNCHANGED,
	PRELOAD_STORIES: PRELOAD_STORIES,
	STORY_RENDERED: STORY_RENDERED,
	STORY_MISSING: STORY_MISSING,
	STORY_ERRORED: STORY_ERRORED,
	STORY_THREW_EXCEPTION: STORY_THREW_EXCEPTION,
	STORY_RENDER_PHASE_CHANGED: STORY_RENDER_PHASE_CHANGED,
	UPDATE_STORY_ARGS: UPDATE_STORY_ARGS,
	STORY_ARGS_UPDATED: STORY_ARGS_UPDATED,
	RESET_STORY_ARGS: RESET_STORY_ARGS,
	SET_GLOBALS: SET_GLOBALS,
	UPDATE_GLOBALS: UPDATE_GLOBALS,
	GLOBALS_UPDATED: GLOBALS_UPDATED,
	REGISTER_SUBSCRIPTION: REGISTER_SUBSCRIPTION,
	PREVIEW_KEYDOWN: PREVIEW_KEYDOWN,
	SELECT_STORY: SELECT_STORY,
	STORIES_COLLAPSE_ALL: STORIES_COLLAPSE_ALL,
	STORIES_EXPAND_ALL: STORIES_EXPAND_ALL,
	DOCS_RENDERED: DOCS_RENDERED,
	SHARED_STATE_CHANGED: SHARED_STATE_CHANGED,
	SHARED_STATE_SET: SHARED_STATE_SET,
	NAVIGATE_URL: NAVIGATE_URL,
	UPDATE_QUERY_PARAMS: UPDATE_QUERY_PARAMS,
	IGNORED_EXCEPTION: IGNORED_EXCEPTION
});

function _slicedToArray$b(arr, i) {
  return _arrayWithHoles$c(arr) || _iterableToArrayLimit$b(arr, i) || _unsupportedIterableToArray$f(arr, i) || _nonIterableRest$c();
}

function _nonIterableRest$c() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _iterableToArrayLimit$b(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$c(arr) {
  if (Array.isArray(arr)) return arr;
}

function _toConsumableArray$8(arr) {
  return _arrayWithoutHoles$8(arr) || _iterableToArray$8(arr) || _unsupportedIterableToArray$f(arr) || _nonIterableSpread$8();
}

function _nonIterableSpread$8() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$f(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$f(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$f(o, minLen);
}

function _iterableToArray$8(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$8(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$f(arr);
}

function _arrayLikeToArray$f(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _classCallCheck$6(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$6(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$6(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$6(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$6(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
var globalWindow = window_1.window;
var HooksContext =
/*#__PURE__*/
function () {
  function HooksContext() {
    var _this = this;

    _classCallCheck$6(this, HooksContext);

    this.hookListsMap = void 0;
    this.mountedDecorators = void 0;
    this.prevMountedDecorators = void 0;
    this.currentHooks = void 0;
    this.nextHookIndex = void 0;
    this.currentPhase = void 0;
    this.currentEffects = void 0;
    this.prevEffects = void 0;
    this.currentDecoratorName = void 0;
    this.hasUpdates = void 0;
    this.currentContext = void 0;

    this.renderListener = function (storyId) {
      if (storyId !== _this.currentContext.id) return;

      _this.triggerEffects();

      _this.currentContext = null;

      _this.removeRenderListeners();
    };

    this.init();
  }

  _createClass$6(HooksContext, [{
    key: "init",
    value: function init() {
      this.hookListsMap = new WeakMap();
      this.mountedDecorators = new Set();
      this.prevMountedDecorators = this.mountedDecorators;
      this.currentHooks = [];
      this.nextHookIndex = 0;
      this.currentPhase = 'NONE';
      this.currentEffects = [];
      this.prevEffects = [];
      this.currentDecoratorName = null;
      this.hasUpdates = false;
      this.currentContext = null;
    }
  }, {
    key: "clean",
    value: function clean() {
      this.prevEffects.forEach(function (effect) {
        if (effect.destroy) {
          effect.destroy();
        }
      });
      this.init();
      this.removeRenderListeners();
    }
  }, {
    key: "getNextHook",
    value: function getNextHook() {
      var hook = this.currentHooks[this.nextHookIndex];
      this.nextHookIndex += 1;
      return hook;
    }
  }, {
    key: "triggerEffects",
    value: function triggerEffects() {
      var _this2 = this; // destroy removed effects


      this.prevEffects.forEach(function (effect) {
        if (!_this2.currentEffects.includes(effect) && effect.destroy) {
          effect.destroy();
        }
      }); // trigger added effects

      this.currentEffects.forEach(function (effect) {
        if (!_this2.prevEffects.includes(effect)) {
          // eslint-disable-next-line no-param-reassign
          effect.destroy = effect.create();
        }
      });
      this.prevEffects = this.currentEffects;
      this.currentEffects = [];
    }
  }, {
    key: "addRenderListeners",
    value: function addRenderListeners() {
      this.removeRenderListeners();
      var channel = addons.getChannel();
      channel.on(STORY_RENDERED, this.renderListener);
    }
  }, {
    key: "removeRenderListeners",
    value: function removeRenderListeners() {
      var channel = addons.getChannel();
      channel.removeListener(STORY_RENDERED, this.renderListener);
    }
  }]);

  return HooksContext;
}();

function hookify(fn) {
  return function () {
    var _ref = typeof (arguments.length <= 0 ? undefined : arguments[0]) === 'function' ? arguments.length <= 1 ? undefined : arguments[1] : arguments.length <= 0 ? undefined : arguments[0],
        hooks = _ref.hooks;

    var prevPhase = hooks.currentPhase;
    var prevHooks = hooks.currentHooks;
    var prevNextHookIndex = hooks.nextHookIndex;
    var prevDecoratorName = hooks.currentDecoratorName;
    hooks.currentDecoratorName = fn.name;

    if (hooks.prevMountedDecorators.has(fn)) {
      hooks.currentPhase = 'UPDATE';
      hooks.currentHooks = hooks.hookListsMap.get(fn) || [];
    } else {
      hooks.currentPhase = 'MOUNT';
      hooks.currentHooks = [];
      hooks.hookListsMap.set(fn, hooks.currentHooks);
      hooks.prevMountedDecorators.add(fn);
    }

    hooks.nextHookIndex = 0;
    var prevContext = globalWindow.STORYBOOK_HOOKS_CONTEXT;
    globalWindow.STORYBOOK_HOOKS_CONTEXT = hooks;
    var result = fn.apply(void 0, arguments);
    globalWindow.STORYBOOK_HOOKS_CONTEXT = prevContext;

    if (hooks.currentPhase === 'UPDATE' && hooks.getNextHook() != null) {
      throw new Error('Rendered fewer hooks than expected. This may be caused by an accidental early return statement.');
    }

    hooks.currentPhase = prevPhase;
    hooks.currentHooks = prevHooks;
    hooks.nextHookIndex = prevNextHookIndex;
    hooks.currentDecoratorName = prevDecoratorName;
    return result;
  };
} // Counter to prevent infinite loops.


var numberOfRenders = 0;
var RENDER_LIMIT = 25;
var applyHooks = function applyHooks(applyDecorators) {
  return function (storyFn, decorators) {
    var decorated = applyDecorators(hookify(storyFn), decorators.map(function (decorator) {
      return hookify(decorator);
    }));
    return function (context) {
      var _ref2 = context,
          hooks = _ref2.hooks;
      hooks.prevMountedDecorators = hooks.mountedDecorators;
      hooks.mountedDecorators = new Set([storyFn].concat(_toConsumableArray$8(decorators)));
      hooks.currentContext = context;
      hooks.hasUpdates = false;
      var result = decorated(context);
      numberOfRenders = 1;

      while (hooks.hasUpdates) {
        hooks.hasUpdates = false;
        hooks.currentEffects = [];
        result = decorated(context);
        numberOfRenders += 1;

        if (numberOfRenders > RENDER_LIMIT) {
          throw new Error('Too many re-renders. Storybook limits the number of renders to prevent an infinite loop.');
        }
      }

      hooks.addRenderListeners();
      return result;
    };
  };
};

var areDepsEqual = function areDepsEqual(deps, nextDeps) {
  return deps.length === nextDeps.length && deps.every(function (dep, i) {
    return dep === nextDeps[i];
  });
};

var invalidHooksError = function invalidHooksError() {
  return new Error('Storybook preview hooks can only be called inside decorators and story functions.');
};

function getHooksContextOrNull() {
  return globalWindow.STORYBOOK_HOOKS_CONTEXT || null;
}

function getHooksContextOrThrow() {
  var hooks = getHooksContextOrNull();

  if (hooks == null) {
    throw invalidHooksError();
  }

  return hooks;
}

function useHook(name, callback, deps) {
  var hooks = getHooksContextOrThrow();

  if (hooks.currentPhase === 'MOUNT') {
    if (deps != null && !Array.isArray(deps)) {
      logger.warn("".concat(name, " received a final argument that is not an array (instead, received ").concat(deps, "). When specified, the final argument must be an array."));
    }

    var _hook = {
      name: name,
      deps: deps
    };
    hooks.currentHooks.push(_hook);
    callback(_hook);
    return _hook;
  }

  if (hooks.currentPhase === 'UPDATE') {
    var _hook2 = hooks.getNextHook();

    if (_hook2 == null) {
      throw new Error('Rendered more hooks than during the previous render.');
    }

    if (_hook2.name !== name) {
      logger.warn("Storybook has detected a change in the order of Hooks".concat(hooks.currentDecoratorName ? " called by ".concat(hooks.currentDecoratorName) : '', ". This will lead to bugs and errors if not fixed."));
    }

    if (deps != null && _hook2.deps == null) {
      logger.warn("".concat(name, " received a final argument during this render, but not during the previous render. Even though the final argument is optional, its type cannot change between renders."));
    }

    if (deps != null && _hook2.deps != null && deps.length !== _hook2.deps.length) {
      logger.warn("The final argument passed to ".concat(name, " changed size between renders. The order and size of this array must remain constant.\nPrevious: ").concat(_hook2.deps, "\nIncoming: ").concat(deps));
    }

    if (deps == null || _hook2.deps == null || !areDepsEqual(deps, _hook2.deps)) {
      callback(_hook2);
      _hook2.deps = deps;
    }

    return _hook2;
  }

  throw invalidHooksError();
}

function useMemoLike(name, nextCreate, deps) {
  var _useHook = useHook(name, function (hook) {
    // eslint-disable-next-line no-param-reassign
    hook.memoizedState = nextCreate();
  }, deps),
      memoizedState = _useHook.memoizedState;

  return memoizedState;
}
/* Returns a memoized value, see https://reactjs.org/docs/hooks-reference.html#usememo */


function useMemo(nextCreate, deps) {
  return useMemoLike('useMemo', nextCreate, deps);
}
/* Returns a memoized callback, see https://reactjs.org/docs/hooks-reference.html#usecallback */

function useCallback(callback, deps) {
  return useMemoLike('useCallback', function () {
    return callback;
  }, deps);
}

function useRefLike(name, initialValue) {
  return useMemoLike(name, function () {
    return {
      current: initialValue
    };
  }, []);
}
/* Returns a mutable ref object, see https://reactjs.org/docs/hooks-reference.html#useref */


function useRef(initialValue) {
  return useRefLike('useRef', initialValue);
}

function triggerUpdate() {
  var hooks = getHooksContextOrNull(); // Rerun storyFn if updates were triggered synchronously, force rerender otherwise

  if (hooks != null && hooks.currentPhase !== 'NONE') {
    hooks.hasUpdates = true;
  } else {
    try {
      addons.getChannel().emit(FORCE_RE_RENDER);
    } catch (e) {
      logger.warn('State updates of Storybook preview hooks work only in browser');
    }
  }
}

function useStateLike(name, initialState) {
  var stateRef = useRefLike(name, // @ts-ignore S type should never be function, but there's no way to tell that to TypeScript
  typeof initialState === 'function' ? initialState() : initialState);

  var setState = function setState(update) {
    // @ts-ignore S type should never be function, but there's no way to tell that to TypeScript
    stateRef.current = typeof update === 'function' ? update(stateRef.current) : update;
    triggerUpdate();
  };

  return [stateRef.current, setState];
}
/* Returns a stateful value, and a function to update it, see https://reactjs.org/docs/hooks-reference.html#usestate */


function useState(initialState) {
  return useStateLike('useState', initialState);
}
/* A redux-like alternative to useState, see https://reactjs.org/docs/hooks-reference.html#usereducer */

function useReducer(reducer, initialArg, init) {
  var initialState = init != null ? function () {
    return init(initialArg);
  } : initialArg;

  var _useStateLike = useStateLike('useReducer', initialState),
      _useStateLike2 = _slicedToArray$b(_useStateLike, 2),
      state = _useStateLike2[0],
      setState = _useStateLike2[1];

  var dispatch = function dispatch(action) {
    return setState(function (prevState) {
      return reducer(prevState, action);
    });
  };

  return [state, dispatch];
}
/*
  Triggers a side effect, see https://reactjs.org/docs/hooks-reference.html#usestate
  Effects are triggered synchronously after rendering the story
*/

function useEffect(create, deps) {
  var hooks = getHooksContextOrThrow();
  var effect = useMemoLike('useEffect', function () {
    return {
      create: create
    };
  }, deps);

  if (!hooks.currentEffects.includes(effect)) {
    hooks.currentEffects.push(effect);
  }
}
/* Accepts a map of Storybook channel event listeners, returns an emit function */

function useChannel(eventMap) {
  var deps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var channel = addons.getChannel();
  useEffect(function () {
    Object.entries(eventMap).forEach(function (_ref3) {
      var _ref4 = _slicedToArray$b(_ref3, 2),
          type = _ref4[0],
          listener = _ref4[1];

      return channel.on(type, listener);
    });
    return function () {
      Object.entries(eventMap).forEach(function (_ref5) {
        var _ref6 = _slicedToArray$b(_ref5, 2),
            type = _ref6[0],
            listener = _ref6[1];

        return channel.removeListener(type, listener);
      });
    };
  }, [].concat(_toConsumableArray$8(Object.keys(eventMap)), _toConsumableArray$8(deps)));
  return useCallback(channel.emit.bind(channel), [channel]);
}
/* Returns current story context */

function useStoryContext() {
  var _getHooksContextOrThr = getHooksContextOrThrow(),
      currentContext = _getHooksContextOrThr.currentContext;

  if (currentContext == null) {
    throw invalidHooksError();
  }

  return currentContext;
}
/* Returns current value of a story parameter */

function useParameter(parameterKey, defaultValue) {
  var _useStoryContext = useStoryContext(),
      parameters = _useStoryContext.parameters;

  if (parameterKey) {
    var _parameters$parameter;

    return (_parameters$parameter = parameters[parameterKey]) !== null && _parameters$parameter !== void 0 ? _parameters$parameter : defaultValue;
  }

  return undefined;
}
/* Returns current value of story args */

function useArgs() {
  var channel = addons.getChannel();

  var _useStoryContext2 = useStoryContext(),
      storyId = _useStoryContext2.id,
      args = _useStoryContext2.args;

  var updateArgs = useCallback(function (updatedArgs) {
    return channel.emit(UPDATE_STORY_ARGS, {
      storyId: storyId,
      updatedArgs: updatedArgs
    });
  }, [channel, storyId]);
  var resetArgs = useCallback(function (argNames) {
    return channel.emit(RESET_STORY_ARGS, {
      storyId: storyId,
      argNames: argNames
    });
  }, [channel, storyId]);
  return [args, updateArgs, resetArgs];
}
/* Returns current value of global args */

function useGlobals() {
  var channel = addons.getChannel();

  var _useStoryContext3 = useStoryContext(),
      globals = _useStoryContext3.globals;

  var updateGlobals = useCallback(function (newGlobals) {
    return channel.emit(UPDATE_GLOBALS, {
      globals: newGlobals
    });
  }, [channel]);
  return [globals, updateGlobals];
}

// There can only be 1 default export per entry point and it has to be directly from public_api

var public_api = /*#__PURE__*/Object.freeze({
	__proto__: null,
	'default': addons,
	makeDecorator: makeDecorator,
	Channel: Channel,
	AddonStore: AddonStore,
	addons: addons,
	get types () { return types; },
	isSupportedType: isSupportedType,
	mockChannel: mockChannel,
	HooksContext: HooksContext,
	applyHooks: applyHooks,
	useMemo: useMemo,
	useCallback: useCallback,
	useRef: useRef,
	useState: useState,
	useReducer: useReducer,
	useEffect: useEffect,
	useChannel: useChannel,
	useStoryContext: useStoryContext,
	useParameter: useParameter,
	useArgs: useArgs,
	useGlobals: useGlobals
});

var require$$0$1 = /*@__PURE__*/getAugmentedNamespace(public_api);

var shared$c = {};

Object.defineProperty(shared$c, "__esModule", {
  value: true
});
shared$c.CLICK = shared$c.SET_OPTIONS = shared$c.CHANGE = shared$c.SET = shared$c.RESET = shared$c.PANEL_ID = shared$c.ADDON_ID = shared$c.PARAM_KEY = void 0; // addons, panels and events get unique names using a prefix

var PARAM_KEY = 'knobs';
shared$c.PARAM_KEY = PARAM_KEY;
var ADDON_ID = 'storybookjs/knobs';
shared$c.ADDON_ID = ADDON_ID;
var PANEL_ID = "".concat(ADDON_ID, "/panel");
shared$c.PANEL_ID = PANEL_ID;
var RESET = "".concat(ADDON_ID, "/reset");
shared$c.RESET = RESET;
var SET = "".concat(ADDON_ID, "/set");
shared$c.SET = SET;
var CHANGE = "".concat(ADDON_ID, "/change");
shared$c.CHANGE = CHANGE;
var SET_OPTIONS = "".concat(ADDON_ID, "/set-options");
shared$c.SET_OPTIONS = SET_OPTIONS;
var CLICK = "".concat(ADDON_ID, "/click");
shared$c.CLICK = CLICK;

var registerKnobs$1 = {};

var check$1 = function (it) {
  return it && it.Math == Math && it;
}; // https://github.com/zloirock/core-js/issues/86#issuecomment-115759028


var global$H = // eslint-disable-next-line es/no-global-this -- safe
check$1(typeof globalThis == 'object' && globalThis) || check$1(typeof window == 'object' && window) || // eslint-disable-next-line no-restricted-globals -- safe
check$1(typeof self == 'object' && self) || check$1(typeof commonjsGlobal == 'object' && commonjsGlobal) || // eslint-disable-next-line no-new-func -- fallback
function () {
  return this;
}() || Function('return this')();

var objectGetOwnPropertyDescriptor$1 = {};

var fails$K = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$J = fails$K; // Detect IE8's incomplete defineProperty implementation


var descriptors$1 = !fails$J(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, {
    get: function () {
      return 7;
    }
  })[1] != 7;
});

var objectPropertyIsEnumerable$1 = {};

var $propertyIsEnumerable$3 = {}.propertyIsEnumerable; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe

var getOwnPropertyDescriptor$6 = Object.getOwnPropertyDescriptor; // Nashorn ~ JDK8 bug

var NASHORN_BUG$1 = getOwnPropertyDescriptor$6 && !$propertyIsEnumerable$3.call({
  1: 2
}, 1); // `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable

objectPropertyIsEnumerable$1.f = NASHORN_BUG$1 ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$6(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable$3;

var createPropertyDescriptor$b = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var toString$b = {}.toString;

var classofRaw$3 = function (it) {
  return toString$b.call(it).slice(8, -1);
};

var fails$I = fails$K;

var classof$f = classofRaw$3;

var split$1 = ''.split; // fallback for non-array-like ES3 and non-enumerable old V8 strings

var indexedObject$1 = fails$I(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$f(it) == 'String' ? split$1.call(it, '') : Object(it);
} : Object;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$e = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$6 = indexedObject$1;

var requireObjectCoercible$d = requireObjectCoercible$e;

var toIndexedObject$i = function (it) {
  return IndexedObject$6(requireObjectCoercible$d(it));
};

var isObject$J = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};

var isObject$I = isObject$J; // `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string


var toPrimitive$a = function (input, PREFERRED_STRING) {
  if (!isObject$I(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$I(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject$I(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$I(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};

var requireObjectCoercible$c = requireObjectCoercible$e; // `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject


var toObject$l = function (argument) {
  return Object(requireObjectCoercible$c(argument));
};

var toObject$k = toObject$l;

var hasOwnProperty$n = {}.hasOwnProperty;

var has$w = function hasOwn(it, key) {
  return hasOwnProperty$n.call(toObject$k(it), key);
};

var global$G = global$H;

var isObject$H = isObject$J;

var document$5 = global$G.document; // typeof document.createElement is 'object' in old IE

var EXISTS$1 = isObject$H(document$5) && isObject$H(document$5.createElement);

var documentCreateElement$3 = function (it) {
  return EXISTS$1 ? document$5.createElement(it) : {};
};

var DESCRIPTORS$q = descriptors$1;

var fails$H = fails$K;

var createElement$2 = documentCreateElement$3; // Thank's IE8 for his funny defineProperty


var ie8DomDefine$1 = !DESCRIPTORS$q && !fails$H(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement$2('div'), 'a', {
    get: function () {
      return 7;
    }
  }).a != 7;
});

var DESCRIPTORS$p = descriptors$1;

var propertyIsEnumerableModule$5 = objectPropertyIsEnumerable$1;

var createPropertyDescriptor$a = createPropertyDescriptor$b;

var toIndexedObject$h = toIndexedObject$i;

var toPrimitive$9 = toPrimitive$a;

var has$v = has$w;

var IE8_DOM_DEFINE$3 = ie8DomDefine$1; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe


var $getOwnPropertyDescriptor$3 = Object.getOwnPropertyDescriptor; // `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor

objectGetOwnPropertyDescriptor$1.f = DESCRIPTORS$p ? $getOwnPropertyDescriptor$3 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$h(O);
  P = toPrimitive$9(P, true);
  if (IE8_DOM_DEFINE$3) try {
    return $getOwnPropertyDescriptor$3(O, P);
  } catch (error) {
    /* empty */
  }
  if (has$v(O, P)) return createPropertyDescriptor$a(!propertyIsEnumerableModule$5.f.call(O, P), O[P]);
};

var objectDefineProperty$1 = {};

var isObject$G = isObject$J;

var anObject$t = function (it) {
  if (!isObject$G(it)) {
    throw TypeError(String(it) + ' is not an object');
  }

  return it;
};

var DESCRIPTORS$o = descriptors$1;

var IE8_DOM_DEFINE$2 = ie8DomDefine$1;

var anObject$s = anObject$t;

var toPrimitive$8 = toPrimitive$a; // eslint-disable-next-line es/no-object-defineproperty -- safe


var $defineProperty$3 = Object.defineProperty; // `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty

objectDefineProperty$1.f = DESCRIPTORS$o ? $defineProperty$3 : function defineProperty(O, P, Attributes) {
  anObject$s(O);
  P = toPrimitive$8(P, true);
  anObject$s(Attributes);
  if (IE8_DOM_DEFINE$2) try {
    return $defineProperty$3(O, P, Attributes);
  } catch (error) {
    /* empty */
  }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$n = descriptors$1;

var definePropertyModule$d = objectDefineProperty$1;

var createPropertyDescriptor$9 = createPropertyDescriptor$b;

var createNonEnumerableProperty$l = DESCRIPTORS$n ? function (object, key, value) {
  return definePropertyModule$d.f(object, key, createPropertyDescriptor$9(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var redefine$l = {exports: {}};

var global$F = global$H;

var createNonEnumerableProperty$k = createNonEnumerableProperty$l;

var setGlobal$7 = function (key, value) {
  try {
    createNonEnumerableProperty$k(global$F, key, value);
  } catch (error) {
    global$F[key] = value;
  }

  return value;
};

var global$E = global$H;

var setGlobal$6 = setGlobal$7;

var SHARED$1 = '__core-js_shared__';
var store$7 = global$E[SHARED$1] || setGlobal$6(SHARED$1, {});
var sharedStore$1 = store$7;

var store$6 = sharedStore$1;

var functionToString$2 = Function.toString; // this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper

if (typeof store$6.inspectSource != 'function') {
  store$6.inspectSource = function (it) {
    return functionToString$2.call(it);
  };
}

var inspectSource$6 = store$6.inspectSource;

var global$D = global$H;

var inspectSource$5 = inspectSource$6;

var WeakMap$8 = global$D.WeakMap;
var nativeWeakMap$1 = typeof WeakMap$8 === 'function' && /native code/.test(inspectSource$5(WeakMap$8));

var shared$b = {exports: {}};

var store$5 = sharedStore$1;

(shared$b.exports = function (key, value) {
  return store$5[key] || (store$5[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.12.1',
  mode: 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});

var id$3 = 0;
var postfix$1 = Math.random();

var uid$9 = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id$3 + postfix$1).toString(36);
};

var shared$a = shared$b.exports;

var uid$8 = uid$9;

var keys$a = shared$a('keys');

var sharedKey$9 = function (key) {
  return keys$a[key] || (keys$a[key] = uid$8(key));
};

var hiddenKeys$d = {};

var NATIVE_WEAK_MAP$1 = nativeWeakMap$1;

var global$C = global$H;

var isObject$F = isObject$J;

var createNonEnumerableProperty$j = createNonEnumerableProperty$l;

var objectHas$1 = has$w;

var shared$9 = sharedStore$1;

var sharedKey$8 = sharedKey$9;

var hiddenKeys$c = hiddenKeys$d;

var OBJECT_ALREADY_INITIALIZED$1 = 'Object already initialized';
var WeakMap$7 = global$C.WeakMap;
var set$2, get$3, has$u;

var enforce$1 = function (it) {
  return has$u(it) ? get$3(it) : set$2(it, {});
};

var getterFor$1 = function (TYPE) {
  return function (it) {
    var state;

    if (!isObject$F(it) || (state = get$3(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    }

    return state;
  };
};

if (NATIVE_WEAK_MAP$1 || shared$9.state) {
  var store$4 = shared$9.state || (shared$9.state = new WeakMap$7());
  var wmget$1 = store$4.get;
  var wmhas$1 = store$4.has;
  var wmset$1 = store$4.set;

  set$2 = function (it, metadata) {
    if (wmhas$1.call(store$4, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$1);
    metadata.facade = it;
    wmset$1.call(store$4, it, metadata);
    return metadata;
  };

  get$3 = function (it) {
    return wmget$1.call(store$4, it) || {};
  };

  has$u = function (it) {
    return wmhas$1.call(store$4, it);
  };
} else {
  var STATE$1 = sharedKey$8('state');
  hiddenKeys$c[STATE$1] = true;

  set$2 = function (it, metadata) {
    if (objectHas$1(it, STATE$1)) throw new TypeError(OBJECT_ALREADY_INITIALIZED$1);
    metadata.facade = it;
    createNonEnumerableProperty$j(it, STATE$1, metadata);
    return metadata;
  };

  get$3 = function (it) {
    return objectHas$1(it, STATE$1) ? it[STATE$1] : {};
  };

  has$u = function (it) {
    return objectHas$1(it, STATE$1);
  };
}

var internalState$1 = {
  set: set$2,
  get: get$3,
  has: has$u,
  enforce: enforce$1,
  getterFor: getterFor$1
};

var global$B = global$H;

var createNonEnumerableProperty$i = createNonEnumerableProperty$l;

var has$t = has$w;

var setGlobal$5 = setGlobal$7;

var inspectSource$4 = inspectSource$6;

var InternalStateModule$a = internalState$1;

var getInternalState$8 = InternalStateModule$a.get;
var enforceInternalState$3 = InternalStateModule$a.enforce;
var TEMPLATE$1 = String(String).split('String');
(redefine$l.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var state;

  if (typeof value == 'function') {
    if (typeof key == 'string' && !has$t(value, 'name')) {
      createNonEnumerableProperty$i(value, 'name', key);
    }

    state = enforceInternalState$3(value);

    if (!state.source) {
      state.source = TEMPLATE$1.join(typeof key == 'string' ? key : '');
    }
  }

  if (O === global$B) {
    if (simple) O[key] = value;else setGlobal$5(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }

  if (simple) O[key] = value;else createNonEnumerableProperty$i(O, key, value); // add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState$8(this).source || inspectSource$4(this);
});

var global$A = global$H;

var path$5 = global$A;

var path$4 = path$5;

var global$z = global$H;

var aFunction$9 = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

var getBuiltIn$c = function (namespace, method) {
  return arguments.length < 2 ? aFunction$9(path$4[namespace]) || aFunction$9(global$z[namespace]) : path$4[namespace] && path$4[namespace][method] || global$z[namespace] && global$z[namespace][method];
};

var objectGetOwnPropertyNames$1 = {};

var ceil$1 = Math.ceil;
var floor$2 = Math.floor; // `ToInteger` abstract operation
// https://tc39.es/ecma262/#sec-tointeger

var toInteger$8 = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor$2 : ceil$1)(argument);
};

var toInteger$7 = toInteger$8;

var min$6 = Math.min; // `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength

var toLength$i = function (argument) {
  return argument > 0 ? min$6(toInteger$7(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toInteger$6 = toInteger$8;

var max$4 = Math.max;
var min$5 = Math.min; // Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).

var toAbsoluteIndex$6 = function (index, length) {
  var integer = toInteger$6(index);
  return integer < 0 ? max$4(integer + length, 0) : min$5(integer, length);
};

var toIndexedObject$g = toIndexedObject$i;

var toLength$h = toLength$i;

var toAbsoluteIndex$5 = toAbsoluteIndex$6; // `Array.prototype.{ indexOf, includes }` methods implementation


var createMethod$8 = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$g($this);
    var length = toLength$h(O.length);
    var index = toAbsoluteIndex$5(fromIndex, length);
    var value; // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check

    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++]; // eslint-disable-next-line no-self-compare -- NaN check

      if (value != value) return true; // Array#indexOf ignores holes, Array#includes - not
    } else for (; length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    }
    return !IS_INCLUDES && -1;
  };
};

var arrayIncludes$1 = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$8(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$8(false)
};

var has$s = has$w;

var toIndexedObject$f = toIndexedObject$i;

var indexOf$2 = arrayIncludes$1.indexOf;

var hiddenKeys$b = hiddenKeys$d;

var objectKeysInternal$1 = function (object, names) {
  var O = toIndexedObject$f(object);
  var i = 0;
  var result = [];
  var key;

  for (key in O) !has$s(hiddenKeys$b, key) && has$s(O, key) && result.push(key); // Don't enum bug & hidden keys


  while (names.length > i) if (has$s(O, key = names[i++])) {
    ~indexOf$2(result, key) || result.push(key);
  }

  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$7 = ['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf'];

var internalObjectKeys$3 = objectKeysInternal$1;

var enumBugKeys$6 = enumBugKeys$7;

var hiddenKeys$a = enumBugKeys$6.concat('length', 'prototype'); // `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe

objectGetOwnPropertyNames$1.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$3(O, hiddenKeys$a);
};

var objectGetOwnPropertySymbols$1 = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols$1.f = Object.getOwnPropertySymbols;

var getBuiltIn$b = getBuiltIn$c;

var getOwnPropertyNamesModule$3 = objectGetOwnPropertyNames$1;

var getOwnPropertySymbolsModule$5 = objectGetOwnPropertySymbols$1;

var anObject$r = anObject$t; // all object keys, includes non-enumerable and symbols


var ownKeys$3 = getBuiltIn$b('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule$3.f(anObject$r(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$5.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};

var has$r = has$w;

var ownKeys$2 = ownKeys$3;

var getOwnPropertyDescriptorModule$3 = objectGetOwnPropertyDescriptor$1;

var definePropertyModule$c = objectDefineProperty$1;

var copyConstructorProperties$5 = function (target, source) {
  var keys = ownKeys$2(source);
  var defineProperty = definePropertyModule$c.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule$3.f;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has$r(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};

var fails$G = fails$K;

var replacement$1 = /#|\.prototype\./;

var isForced$9 = function (feature, detection) {
  var value = data$1[normalize$1(feature)];
  return value == POLYFILL$1 ? true : value == NATIVE$1 ? false : typeof detection == 'function' ? fails$G(detection) : !!detection;
};

var normalize$1 = isForced$9.normalize = function (string) {
  return String(string).replace(replacement$1, '.').toLowerCase();
};

var data$1 = isForced$9.data = {};
var NATIVE$1 = isForced$9.NATIVE = 'N';
var POLYFILL$1 = isForced$9.POLYFILL = 'P';
var isForced_1$1 = isForced$9;

var global$y = global$H;

var getOwnPropertyDescriptor$5 = objectGetOwnPropertyDescriptor$1.f;

var createNonEnumerableProperty$h = createNonEnumerableProperty$l;

var redefine$k = redefine$l.exports;

var setGlobal$4 = setGlobal$7;

var copyConstructorProperties$4 = copyConstructorProperties$5;

var isForced$8 = isForced_1$1;
/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/


var _export$1 = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;

  if (GLOBAL) {
    target = global$y;
  } else if (STATIC) {
    target = global$y[TARGET] || setGlobal$4(TARGET, {});
  } else {
    target = (global$y[TARGET] || {}).prototype;
  }

  if (target) for (key in source) {
    sourceProperty = source[key];

    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor$5(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];

    FORCED = isForced$8(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced); // contained in target

    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties$4(sourceProperty, targetProperty);
    } // add a flag to not completely full polyfills


    if (options.sham || targetProperty && targetProperty.sham) {
      createNonEnumerableProperty$h(sourceProperty, 'sham', true);
    } // extend global


    redefine$k(target, key, sourceProperty, options);
  }
};

var classof$e = classofRaw$3; // `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe


var isArray$s = Array.isArray || function isArray(arg) {
  return classof$e(arg) == 'Array';
};

var toPrimitive$7 = toPrimitive$a;

var definePropertyModule$b = objectDefineProperty$1;

var createPropertyDescriptor$8 = createPropertyDescriptor$b;

var createProperty$7 = function (object, key, value) {
  var propertyKey = toPrimitive$7(key);
  if (propertyKey in object) definePropertyModule$b.f(object, propertyKey, createPropertyDescriptor$8(0, value));else object[propertyKey] = value;
};

var getBuiltIn$a = getBuiltIn$c;

var engineUserAgent$1 = getBuiltIn$a('navigator', 'userAgent') || '';

var global$x = global$H;

var userAgent$3 = engineUserAgent$1;

var process$5 = global$x.process;
var versions$1 = process$5 && process$5.versions;
var v8$1 = versions$1 && versions$1.v8;
var match$2, version$1;

if (v8$1) {
  match$2 = v8$1.split('.');
  version$1 = match$2[0] < 4 ? 1 : match$2[0] + match$2[1];
} else if (userAgent$3) {
  match$2 = userAgent$3.match(/Edge\/(\d+)/);

  if (!match$2 || match$2[1] >= 74) {
    match$2 = userAgent$3.match(/Chrome\/(\d+)/);
    if (match$2) version$1 = match$2[1];
  }
}

var engineV8Version$1 = version$1 && +version$1;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION$6 = engineV8Version$1;

var fails$F = fails$K; // eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing


var nativeSymbol$1 = !!Object.getOwnPropertySymbols && !fails$F(function () {
  return !String(Symbol()) || // Chrome 38 Symbol has incorrect toString conversion
  // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
  !Symbol.sham && V8_VERSION$6 && V8_VERSION$6 < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$5 = nativeSymbol$1;

var useSymbolAsUid$1 = NATIVE_SYMBOL$5 && !Symbol.sham && typeof Symbol.iterator == 'symbol';

var global$w = global$H;

var shared$8 = shared$b.exports;

var has$q = has$w;

var uid$7 = uid$9;

var NATIVE_SYMBOL$4 = nativeSymbol$1;

var USE_SYMBOL_AS_UID$3 = useSymbolAsUid$1;

var WellKnownSymbolsStore$3 = shared$8('wks');
var Symbol$g = global$w.Symbol;
var createWellKnownSymbol$1 = USE_SYMBOL_AS_UID$3 ? Symbol$g : Symbol$g && Symbol$g.withoutSetter || uid$7;

var wellKnownSymbol$I = function (name) {
  if (!has$q(WellKnownSymbolsStore$3, name) || !(NATIVE_SYMBOL$4 || typeof WellKnownSymbolsStore$3[name] == 'string')) {
    if (NATIVE_SYMBOL$4 && has$q(Symbol$g, name)) {
      WellKnownSymbolsStore$3[name] = Symbol$g[name];
    } else {
      WellKnownSymbolsStore$3[name] = createWellKnownSymbol$1('Symbol.' + name);
    }
  }

  return WellKnownSymbolsStore$3[name];
};

var fails$E = fails$K;

var wellKnownSymbol$H = wellKnownSymbol$I;

var V8_VERSION$5 = engineV8Version$1;

var SPECIES$b = wellKnownSymbol$H('species');

var arrayMethodHasSpeciesSupport$9 = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION$5 >= 51 || !fails$E(function () {
    var array = [];
    var constructor = array.constructor = {};

    constructor[SPECIES$b] = function () {
      return {
        foo: 1
      };
    };

    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};

var $$E = _export$1;

var isObject$E = isObject$J;

var isArray$r = isArray$s;

var toAbsoluteIndex$4 = toAbsoluteIndex$6;

var toLength$g = toLength$i;

var toIndexedObject$e = toIndexedObject$i;

var createProperty$6 = createProperty$7;

var wellKnownSymbol$G = wellKnownSymbol$I;

var arrayMethodHasSpeciesSupport$8 = arrayMethodHasSpeciesSupport$9;

var HAS_SPECIES_SUPPORT$5 = arrayMethodHasSpeciesSupport$8('slice');
var SPECIES$a = wellKnownSymbol$G('species');
var nativeSlice$1 = [].slice;
var max$3 = Math.max; // `Array.prototype.slice` method
// https://tc39.es/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects

$$E({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$5
}, {
  slice: function slice(start, end) {
    var O = toIndexedObject$e(this);
    var length = toLength$g(O.length);
    var k = toAbsoluteIndex$4(start, length);
    var fin = toAbsoluteIndex$4(end === undefined ? length : end, length); // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible

    var Constructor, result, n;

    if (isArray$r(O)) {
      Constructor = O.constructor; // cross-realm fallback

      if (typeof Constructor == 'function' && (Constructor === Array || isArray$r(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject$E(Constructor)) {
        Constructor = Constructor[SPECIES$a];
        if (Constructor === null) Constructor = undefined;
      }

      if (Constructor === Array || Constructor === undefined) {
        return nativeSlice$1.call(O, k, fin);
      }
    }

    result = new (Constructor === undefined ? Array : Constructor)(max$3(fin - k, 0));

    for (n = 0; k < fin; k++, n++) if (k in O) createProperty$6(result, n, O[k]);

    result.length = n;
    return result;
  }
});

var fails$D = fails$K;

var freezing$1 = !fails$D(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});

var internalMetadata$1 = {exports: {}};

var hiddenKeys$9 = hiddenKeys$d;

var isObject$D = isObject$J;

var has$p = has$w;

var defineProperty$j = objectDefineProperty$1.f;

var uid$6 = uid$9;

var FREEZING$3 = freezing$1;

var METADATA$1 = uid$6('meta');
var id$2 = 0; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible$1 = Object.isExtensible || function () {
  return true;
};

var setMetadata$1 = function (it) {
  defineProperty$j(it, METADATA$1, {
    value: {
      objectID: 'O' + ++id$2,
      // object ID
      weakData: {} // weak collections IDs

    }
  });
};

var fastKey$3 = function (it, create) {
  // return a primitive with prefix
  if (!isObject$D(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;

  if (!has$p(it, METADATA$1)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$1(it)) return 'F'; // not necessary to add metadata

    if (!create) return 'E'; // add missing metadata

    setMetadata$1(it); // return object ID
  }

  return it[METADATA$1].objectID;
};

var getWeakData$1 = function (it, create) {
  if (!has$p(it, METADATA$1)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible$1(it)) return true; // not necessary to add metadata

    if (!create) return false; // add missing metadata

    setMetadata$1(it); // return the store of weak collections IDs
  }

  return it[METADATA$1].weakData;
}; // add metadata on freeze-family methods calling


var onFreeze$3 = function (it) {
  if (FREEZING$3 && meta$1.REQUIRED && isExtensible$1(it) && !has$p(it, METADATA$1)) setMetadata$1(it);
  return it;
};

var meta$1 = internalMetadata$1.exports = {
  REQUIRED: false,
  fastKey: fastKey$3,
  getWeakData: getWeakData$1,
  onFreeze: onFreeze$3
};
hiddenKeys$9[METADATA$1] = true;

var $$D = _export$1;

var FREEZING$2 = freezing$1;

var fails$C = fails$K;

var isObject$C = isObject$J;

var onFreeze$2 = internalMetadata$1.exports.onFreeze; // eslint-disable-next-line es/no-object-freeze -- safe


var $freeze$1 = Object.freeze;
var FAILS_ON_PRIMITIVES$4 = fails$C(function () {
  $freeze$1(1);
}); // `Object.freeze` method
// https://tc39.es/ecma262/#sec-object.freeze

$$D({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$4,
  sham: !FREEZING$2
}, {
  freeze: function freeze(it) {
    return $freeze$1 && isObject$C(it) ? $freeze$1(onFreeze$2(it)) : it;
  }
});

var internalObjectKeys$2 = objectKeysInternal$1;

var enumBugKeys$5 = enumBugKeys$7; // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe


var objectKeys$9 = Object.keys || function keys(O) {
  return internalObjectKeys$2(O, enumBugKeys$5);
};

var DESCRIPTORS$m = descriptors$1;

var definePropertyModule$a = objectDefineProperty$1;

var anObject$q = anObject$t;

var objectKeys$8 = objectKeys$9; // `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe


var objectDefineProperties$1 = DESCRIPTORS$m ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$q(O);
  var keys = objectKeys$8(Properties);
  var length = keys.length;
  var index = 0;
  var key;

  while (length > index) definePropertyModule$a.f(O, key = keys[index++], Properties[key]);

  return O;
};

var getBuiltIn$9 = getBuiltIn$c;

var html$4 = getBuiltIn$9('document', 'documentElement');

var anObject$p = anObject$t;

var defineProperties$1 = objectDefineProperties$1;

var enumBugKeys$4 = enumBugKeys$7;

var hiddenKeys$8 = hiddenKeys$d;

var html$3 = html$4;

var documentCreateElement$2 = documentCreateElement$3;

var sharedKey$7 = sharedKey$9;

var GT$1 = '>';
var LT$1 = '<';
var PROTOTYPE$3 = 'prototype';
var SCRIPT$1 = 'script';
var IE_PROTO$3 = sharedKey$7('IE_PROTO');

var EmptyConstructor$1 = function () {
  /* empty */
};

var scriptTag$1 = function (content) {
  return LT$1 + SCRIPT$1 + GT$1 + content + LT$1 + '/' + SCRIPT$1 + GT$1;
}; // Create object with fake `null` prototype: use ActiveX Object with cleared prototype


var NullProtoObjectViaActiveX$1 = function (activeXDocument) {
  activeXDocument.write(scriptTag$1(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak

  return temp;
}; // Create object with fake `null` prototype: use iframe Object with cleared prototype


var NullProtoObjectViaIFrame$1 = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement$2('iframe');
  var JS = 'java' + SCRIPT$1 + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html$3.appendChild(iframe); // https://github.com/zloirock/core-js/issues/475

  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag$1('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
}; // Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug


var activeXDocument$1;

var NullProtoObject$1 = function () {
  try {
    /* global ActiveXObject -- old IE */
    activeXDocument$1 = document.domain && new ActiveXObject('htmlfile');
  } catch (error) {
    /* ignore */
  }

  NullProtoObject$1 = activeXDocument$1 ? NullProtoObjectViaActiveX$1(activeXDocument$1) : NullProtoObjectViaIFrame$1();
  var length = enumBugKeys$4.length;

  while (length--) delete NullProtoObject$1[PROTOTYPE$3][enumBugKeys$4[length]];

  return NullProtoObject$1();
};

hiddenKeys$8[IE_PROTO$3] = true; // `Object.create` method
// https://tc39.es/ecma262/#sec-object.create

var objectCreate$1 = Object.create || function create(O, Properties) {
  var result;

  if (O !== null) {
    EmptyConstructor$1[PROTOTYPE$3] = anObject$p(O);
    result = new EmptyConstructor$1();
    EmptyConstructor$1[PROTOTYPE$3] = null; // add "__proto__" for Object.getPrototypeOf polyfill

    result[IE_PROTO$3] = O;
  } else result = NullProtoObject$1();

  return Properties === undefined ? result : defineProperties$1(result, Properties);
};

var objectGetOwnPropertyNamesExternal$1 = {};

/* eslint-disable es/no-object-getownpropertynames -- safe */

var toIndexedObject$d = toIndexedObject$i;

var $getOwnPropertyNames$3 = objectGetOwnPropertyNames$1.f;

var toString$a = {}.toString;
var windowNames$1 = typeof window == 'object' && window && Object.getOwnPropertyNames ? Object.getOwnPropertyNames(window) : [];

var getWindowNames$1 = function (it) {
  try {
    return $getOwnPropertyNames$3(it);
  } catch (error) {
    return windowNames$1.slice();
  }
}; // fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window


objectGetOwnPropertyNamesExternal$1.f = function getOwnPropertyNames(it) {
  return windowNames$1 && toString$a.call(it) == '[object Window]' ? getWindowNames$1(it) : $getOwnPropertyNames$3(toIndexedObject$d(it));
};

var wellKnownSymbolWrapped$1 = {};

var wellKnownSymbol$F = wellKnownSymbol$I;

wellKnownSymbolWrapped$1.f = wellKnownSymbol$F;

var path$3 = path$5;

var has$o = has$w;

var wrappedWellKnownSymbolModule$3 = wellKnownSymbolWrapped$1;

var defineProperty$i = objectDefineProperty$1.f;

var defineWellKnownSymbol$5 = function (NAME) {
  var Symbol = path$3.Symbol || (path$3.Symbol = {});
  if (!has$o(Symbol, NAME)) defineProperty$i(Symbol, NAME, {
    value: wrappedWellKnownSymbolModule$3.f(NAME)
  });
};

var defineProperty$h = objectDefineProperty$1.f;

var has$n = has$w;

var wellKnownSymbol$E = wellKnownSymbol$I;

var TO_STRING_TAG$7 = wellKnownSymbol$E('toStringTag');

var setToStringTag$a = function (it, TAG, STATIC) {
  if (it && !has$n(it = STATIC ? it : it.prototype, TO_STRING_TAG$7)) {
    defineProperty$h(it, TO_STRING_TAG$7, {
      configurable: true,
      value: TAG
    });
  }
};

var aFunction$8 = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  }

  return it;
};

var aFunction$7 = aFunction$8; // optional / simple context binding


var functionBindContext$1 = function (fn, that, length) {
  aFunction$7(fn);
  if (that === undefined) return fn;

  switch (length) {
    case 0:
      return function () {
        return fn.call(that);
      };

    case 1:
      return function (a) {
        return fn.call(that, a);
      };

    case 2:
      return function (a, b) {
        return fn.call(that, a, b);
      };

    case 3:
      return function (a, b, c) {
        return fn.call(that, a, b, c);
      };
  }

  return function ()
  /* ...args */
  {
    return fn.apply(that, arguments);
  };
};

var isObject$B = isObject$J;

var isArray$q = isArray$s;

var wellKnownSymbol$D = wellKnownSymbol$I;

var SPECIES$9 = wellKnownSymbol$D('species'); // `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate

var arraySpeciesCreate$5 = function (originalArray, length) {
  var C;

  if (isArray$q(originalArray)) {
    C = originalArray.constructor; // cross-realm fallback

    if (typeof C == 'function' && (C === Array || isArray$q(C.prototype))) C = undefined;else if (isObject$B(C)) {
      C = C[SPECIES$9];
      if (C === null) C = undefined;
    }
  }

  return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};

var bind$b = functionBindContext$1;

var IndexedObject$5 = indexedObject$1;

var toObject$j = toObject$l;

var toLength$f = toLength$i;

var arraySpeciesCreate$4 = arraySpeciesCreate$5;

var push$2 = [].push; // `Array.prototype.{ forEach, map, filter, some, every, find, findIndex, filterOut }` methods implementation

var createMethod$7 = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var IS_FILTER_OUT = TYPE == 7;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject$j($this);
    var self = IndexedObject$5(O);
    var boundFunction = bind$b(callbackfn, that, 3);
    var length = toLength$f(self.length);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate$4;
    var target = IS_MAP ? create($this, length) : IS_FILTER || IS_FILTER_OUT ? create($this, 0) : undefined;
    var value, result;

    for (; length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);

      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
            case 3:
              return true;
            // some

            case 5:
              return value;
            // find

            case 6:
              return index;
            // findIndex

            case 2:
              push$2.call(target, value);
            // filter
          } else switch (TYPE) {
            case 4:
              return false;
            // every

            case 7:
              push$2.call(target, value);
            // filterOut
          }
      }
    }

    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

var arrayIteration$1 = {
  // `Array.prototype.forEach` method
  // https://tc39.es/ecma262/#sec-array.prototype.foreach
  forEach: createMethod$7(0),
  // `Array.prototype.map` method
  // https://tc39.es/ecma262/#sec-array.prototype.map
  map: createMethod$7(1),
  // `Array.prototype.filter` method
  // https://tc39.es/ecma262/#sec-array.prototype.filter
  filter: createMethod$7(2),
  // `Array.prototype.some` method
  // https://tc39.es/ecma262/#sec-array.prototype.some
  some: createMethod$7(3),
  // `Array.prototype.every` method
  // https://tc39.es/ecma262/#sec-array.prototype.every
  every: createMethod$7(4),
  // `Array.prototype.find` method
  // https://tc39.es/ecma262/#sec-array.prototype.find
  find: createMethod$7(5),
  // `Array.prototype.findIndex` method
  // https://tc39.es/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod$7(6),
  // `Array.prototype.filterOut` method
  // https://github.com/tc39/proposal-array-filtering
  filterOut: createMethod$7(7)
};

var $$C = _export$1;

var global$v = global$H;

var getBuiltIn$8 = getBuiltIn$c;

var DESCRIPTORS$l = descriptors$1;

var NATIVE_SYMBOL$3 = nativeSymbol$1;

var USE_SYMBOL_AS_UID$2 = useSymbolAsUid$1;

var fails$B = fails$K;

var has$m = has$w;

var isArray$p = isArray$s;

var isObject$A = isObject$J;

var anObject$o = anObject$t;

var toObject$i = toObject$l;

var toIndexedObject$c = toIndexedObject$i;

var toPrimitive$6 = toPrimitive$a;

var createPropertyDescriptor$7 = createPropertyDescriptor$b;

var nativeObjectCreate$1 = objectCreate$1;

var objectKeys$7 = objectKeys$9;

var getOwnPropertyNamesModule$2 = objectGetOwnPropertyNames$1;

var getOwnPropertyNamesExternal$1 = objectGetOwnPropertyNamesExternal$1;

var getOwnPropertySymbolsModule$4 = objectGetOwnPropertySymbols$1;

var getOwnPropertyDescriptorModule$2 = objectGetOwnPropertyDescriptor$1;

var definePropertyModule$9 = objectDefineProperty$1;

var propertyIsEnumerableModule$4 = objectPropertyIsEnumerable$1;

var createNonEnumerableProperty$g = createNonEnumerableProperty$l;

var redefine$j = redefine$l.exports;

var shared$7 = shared$b.exports;

var sharedKey$6 = sharedKey$9;

var hiddenKeys$7 = hiddenKeys$d;

var uid$5 = uid$9;

var wellKnownSymbol$C = wellKnownSymbol$I;

var wrappedWellKnownSymbolModule$2 = wellKnownSymbolWrapped$1;

var defineWellKnownSymbol$4 = defineWellKnownSymbol$5;

var setToStringTag$9 = setToStringTag$a;

var InternalStateModule$9 = internalState$1;

var $forEach$3 = arrayIteration$1.forEach;

var HIDDEN$1 = sharedKey$6('hidden');
var SYMBOL$1 = 'Symbol';
var PROTOTYPE$2 = 'prototype';
var TO_PRIMITIVE$1 = wellKnownSymbol$C('toPrimitive');
var setInternalState$8 = InternalStateModule$9.set;
var getInternalState$7 = InternalStateModule$9.getterFor(SYMBOL$1);
var ObjectPrototype$3 = Object[PROTOTYPE$2];
var $Symbol$1 = global$v.Symbol;
var $stringify$1 = getBuiltIn$8('JSON', 'stringify');
var nativeGetOwnPropertyDescriptor$1 = getOwnPropertyDescriptorModule$2.f;
var nativeDefineProperty$1 = definePropertyModule$9.f;
var nativeGetOwnPropertyNames$1 = getOwnPropertyNamesExternal$1.f;
var nativePropertyIsEnumerable$1 = propertyIsEnumerableModule$4.f;
var AllSymbols$1 = shared$7('symbols');
var ObjectPrototypeSymbols$1 = shared$7('op-symbols');
var StringToSymbolRegistry$1 = shared$7('string-to-symbol-registry');
var SymbolToStringRegistry$1 = shared$7('symbol-to-string-registry');
var WellKnownSymbolsStore$2 = shared$7('wks');
var QObject$1 = global$v.QObject; // Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173

var USE_SETTER$1 = !QObject$1 || !QObject$1[PROTOTYPE$2] || !QObject$1[PROTOTYPE$2].findChild; // fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687

var setSymbolDescriptor$1 = DESCRIPTORS$l && fails$B(function () {
  return nativeObjectCreate$1(nativeDefineProperty$1({}, 'a', {
    get: function () {
      return nativeDefineProperty$1(this, 'a', {
        value: 7
      }).a;
    }
  })).a != 7;
}) ? function (O, P, Attributes) {
  var ObjectPrototypeDescriptor = nativeGetOwnPropertyDescriptor$1(ObjectPrototype$3, P);
  if (ObjectPrototypeDescriptor) delete ObjectPrototype$3[P];
  nativeDefineProperty$1(O, P, Attributes);

  if (ObjectPrototypeDescriptor && O !== ObjectPrototype$3) {
    nativeDefineProperty$1(ObjectPrototype$3, P, ObjectPrototypeDescriptor);
  }
} : nativeDefineProperty$1;

var wrap$1 = function (tag, description) {
  var symbol = AllSymbols$1[tag] = nativeObjectCreate$1($Symbol$1[PROTOTYPE$2]);
  setInternalState$8(symbol, {
    type: SYMBOL$1,
    tag: tag,
    description: description
  });
  if (!DESCRIPTORS$l) symbol.description = description;
  return symbol;
};

var isSymbol$a = USE_SYMBOL_AS_UID$2 ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  return Object(it) instanceof $Symbol$1;
};

var $defineProperty$2 = function defineProperty(O, P, Attributes) {
  if (O === ObjectPrototype$3) $defineProperty$2(ObjectPrototypeSymbols$1, P, Attributes);
  anObject$o(O);
  var key = toPrimitive$6(P, true);
  anObject$o(Attributes);

  if (has$m(AllSymbols$1, key)) {
    if (!Attributes.enumerable) {
      if (!has$m(O, HIDDEN$1)) nativeDefineProperty$1(O, HIDDEN$1, createPropertyDescriptor$7(1, {}));
      O[HIDDEN$1][key] = true;
    } else {
      if (has$m(O, HIDDEN$1) && O[HIDDEN$1][key]) O[HIDDEN$1][key] = false;
      Attributes = nativeObjectCreate$1(Attributes, {
        enumerable: createPropertyDescriptor$7(0, false)
      });
    }

    return setSymbolDescriptor$1(O, key, Attributes);
  }

  return nativeDefineProperty$1(O, key, Attributes);
};

var $defineProperties$1 = function defineProperties(O, Properties) {
  anObject$o(O);
  var properties = toIndexedObject$c(Properties);
  var keys = objectKeys$7(properties).concat($getOwnPropertySymbols$1(properties));
  $forEach$3(keys, function (key) {
    if (!DESCRIPTORS$l || $propertyIsEnumerable$2.call(properties, key)) $defineProperty$2(O, key, properties[key]);
  });
  return O;
};

var $create$1 = function create(O, Properties) {
  return Properties === undefined ? nativeObjectCreate$1(O) : $defineProperties$1(nativeObjectCreate$1(O), Properties);
};

var $propertyIsEnumerable$2 = function propertyIsEnumerable(V) {
  var P = toPrimitive$6(V, true);
  var enumerable = nativePropertyIsEnumerable$1.call(this, P);
  if (this === ObjectPrototype$3 && has$m(AllSymbols$1, P) && !has$m(ObjectPrototypeSymbols$1, P)) return false;
  return enumerable || !has$m(this, P) || !has$m(AllSymbols$1, P) || has$m(this, HIDDEN$1) && this[HIDDEN$1][P] ? enumerable : true;
};

var $getOwnPropertyDescriptor$2 = function getOwnPropertyDescriptor(O, P) {
  var it = toIndexedObject$c(O);
  var key = toPrimitive$6(P, true);
  if (it === ObjectPrototype$3 && has$m(AllSymbols$1, key) && !has$m(ObjectPrototypeSymbols$1, key)) return;
  var descriptor = nativeGetOwnPropertyDescriptor$1(it, key);

  if (descriptor && has$m(AllSymbols$1, key) && !(has$m(it, HIDDEN$1) && it[HIDDEN$1][key])) {
    descriptor.enumerable = true;
  }

  return descriptor;
};

var $getOwnPropertyNames$2 = function getOwnPropertyNames(O) {
  var names = nativeGetOwnPropertyNames$1(toIndexedObject$c(O));
  var result = [];
  $forEach$3(names, function (key) {
    if (!has$m(AllSymbols$1, key) && !has$m(hiddenKeys$7, key)) result.push(key);
  });
  return result;
};

var $getOwnPropertySymbols$1 = function getOwnPropertySymbols(O) {
  var IS_OBJECT_PROTOTYPE = O === ObjectPrototype$3;
  var names = nativeGetOwnPropertyNames$1(IS_OBJECT_PROTOTYPE ? ObjectPrototypeSymbols$1 : toIndexedObject$c(O));
  var result = [];
  $forEach$3(names, function (key) {
    if (has$m(AllSymbols$1, key) && (!IS_OBJECT_PROTOTYPE || has$m(ObjectPrototype$3, key))) {
      result.push(AllSymbols$1[key]);
    }
  });
  return result;
}; // `Symbol` constructor
// https://tc39.es/ecma262/#sec-symbol-constructor


if (!NATIVE_SYMBOL$3) {
  $Symbol$1 = function Symbol() {
    if (this instanceof $Symbol$1) throw TypeError('Symbol is not a constructor');
    var description = !arguments.length || arguments[0] === undefined ? undefined : String(arguments[0]);
    var tag = uid$5(description);

    var setter = function (value) {
      if (this === ObjectPrototype$3) setter.call(ObjectPrototypeSymbols$1, value);
      if (has$m(this, HIDDEN$1) && has$m(this[HIDDEN$1], tag)) this[HIDDEN$1][tag] = false;
      setSymbolDescriptor$1(this, tag, createPropertyDescriptor$7(1, value));
    };

    if (DESCRIPTORS$l && USE_SETTER$1) setSymbolDescriptor$1(ObjectPrototype$3, tag, {
      configurable: true,
      set: setter
    });
    return wrap$1(tag, description);
  };

  redefine$j($Symbol$1[PROTOTYPE$2], 'toString', function toString() {
    return getInternalState$7(this).tag;
  });
  redefine$j($Symbol$1, 'withoutSetter', function (description) {
    return wrap$1(uid$5(description), description);
  });
  propertyIsEnumerableModule$4.f = $propertyIsEnumerable$2;
  definePropertyModule$9.f = $defineProperty$2;
  getOwnPropertyDescriptorModule$2.f = $getOwnPropertyDescriptor$2;
  getOwnPropertyNamesModule$2.f = getOwnPropertyNamesExternal$1.f = $getOwnPropertyNames$2;
  getOwnPropertySymbolsModule$4.f = $getOwnPropertySymbols$1;

  wrappedWellKnownSymbolModule$2.f = function (name) {
    return wrap$1(wellKnownSymbol$C(name), name);
  };

  if (DESCRIPTORS$l) {
    // https://github.com/tc39/proposal-Symbol-description
    nativeDefineProperty$1($Symbol$1[PROTOTYPE$2], 'description', {
      configurable: true,
      get: function description() {
        return getInternalState$7(this).description;
      }
    });

    {
      redefine$j(ObjectPrototype$3, 'propertyIsEnumerable', $propertyIsEnumerable$2, {
        unsafe: true
      });
    }
  }
}

$$C({
  global: true,
  wrap: true,
  forced: !NATIVE_SYMBOL$3,
  sham: !NATIVE_SYMBOL$3
}, {
  Symbol: $Symbol$1
});
$forEach$3(objectKeys$7(WellKnownSymbolsStore$2), function (name) {
  defineWellKnownSymbol$4(name);
});
$$C({
  target: SYMBOL$1,
  stat: true,
  forced: !NATIVE_SYMBOL$3
}, {
  // `Symbol.for` method
  // https://tc39.es/ecma262/#sec-symbol.for
  'for': function (key) {
    var string = String(key);
    if (has$m(StringToSymbolRegistry$1, string)) return StringToSymbolRegistry$1[string];
    var symbol = $Symbol$1(string);
    StringToSymbolRegistry$1[string] = symbol;
    SymbolToStringRegistry$1[symbol] = string;
    return symbol;
  },
  // `Symbol.keyFor` method
  // https://tc39.es/ecma262/#sec-symbol.keyfor
  keyFor: function keyFor(sym) {
    if (!isSymbol$a(sym)) throw TypeError(sym + ' is not a symbol');
    if (has$m(SymbolToStringRegistry$1, sym)) return SymbolToStringRegistry$1[sym];
  },
  useSetter: function () {
    USE_SETTER$1 = true;
  },
  useSimple: function () {
    USE_SETTER$1 = false;
  }
});
$$C({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL$3,
  sham: !DESCRIPTORS$l
}, {
  // `Object.create` method
  // https://tc39.es/ecma262/#sec-object.create
  create: $create$1,
  // `Object.defineProperty` method
  // https://tc39.es/ecma262/#sec-object.defineproperty
  defineProperty: $defineProperty$2,
  // `Object.defineProperties` method
  // https://tc39.es/ecma262/#sec-object.defineproperties
  defineProperties: $defineProperties$1,
  // `Object.getOwnPropertyDescriptor` method
  // https://tc39.es/ecma262/#sec-object.getownpropertydescriptors
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor$2
});
$$C({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL$3
}, {
  // `Object.getOwnPropertyNames` method
  // https://tc39.es/ecma262/#sec-object.getownpropertynames
  getOwnPropertyNames: $getOwnPropertyNames$2,
  // `Object.getOwnPropertySymbols` method
  // https://tc39.es/ecma262/#sec-object.getownpropertysymbols
  getOwnPropertySymbols: $getOwnPropertySymbols$1
}); // Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443

$$C({
  target: 'Object',
  stat: true,
  forced: fails$B(function () {
    getOwnPropertySymbolsModule$4.f(1);
  })
}, {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return getOwnPropertySymbolsModule$4.f(toObject$i(it));
  }
}); // `JSON.stringify` method behavior with symbols
// https://tc39.es/ecma262/#sec-json.stringify

if ($stringify$1) {
  var FORCED_JSON_STRINGIFY$1 = !NATIVE_SYMBOL$3 || fails$B(function () {
    var symbol = $Symbol$1(); // MS Edge converts symbol values to JSON as {}

    return $stringify$1([symbol]) != '[null]' // WebKit converts symbol values to JSON as null
    || $stringify$1({
      a: symbol
    }) != '{}' // V8 throws on boxed symbols
    || $stringify$1(Object(symbol)) != '{}';
  });
  $$C({
    target: 'JSON',
    stat: true,
    forced: FORCED_JSON_STRINGIFY$1
  }, {
    // eslint-disable-next-line no-unused-vars -- required for `.length`
    stringify: function stringify(it, replacer, space) {
      var args = [it];
      var index = 1;
      var $replacer;

      while (arguments.length > index) args.push(arguments[index++]);

      $replacer = replacer;
      if (!isObject$A(replacer) && it === undefined || isSymbol$a(it)) return; // IE8 returns string on undefined

      if (!isArray$p(replacer)) replacer = function (key, value) {
        if (typeof $replacer == 'function') value = $replacer.call(this, key, value);
        if (!isSymbol$a(value)) return value;
      };
      args[1] = replacer;
      return $stringify$1.apply(null, args);
    }
  });
} // `Symbol.prototype[@@toPrimitive]` method
// https://tc39.es/ecma262/#sec-symbol.prototype-@@toprimitive


if (!$Symbol$1[PROTOTYPE$2][TO_PRIMITIVE$1]) {
  createNonEnumerableProperty$g($Symbol$1[PROTOTYPE$2], TO_PRIMITIVE$1, $Symbol$1[PROTOTYPE$2].valueOf);
} // `Symbol.prototype[@@toStringTag]` property
// https://tc39.es/ecma262/#sec-symbol.prototype-@@tostringtag


setToStringTag$9($Symbol$1, SYMBOL$1);
hiddenKeys$7[HIDDEN$1] = true;

var $$B = _export$1;

var DESCRIPTORS$k = descriptors$1;

var global$u = global$H;

var has$l = has$w;

var isObject$z = isObject$J;

var defineProperty$g = objectDefineProperty$1.f;

var copyConstructorProperties$3 = copyConstructorProperties$5;

var NativeSymbol$1 = global$u.Symbol;

if (DESCRIPTORS$k && typeof NativeSymbol$1 == 'function' && (!('description' in NativeSymbol$1.prototype) || // Safari 12 bug
NativeSymbol$1().description !== undefined)) {
  var EmptyStringDescriptionStore$1 = {}; // wrap Symbol constructor for correct work with undefined description

  var SymbolWrapper$1 = function Symbol() {
    var description = arguments.length < 1 || arguments[0] === undefined ? undefined : String(arguments[0]);
    var result = this instanceof SymbolWrapper$1 ? new NativeSymbol$1(description) // in Edge 13, String(Symbol(undefined)) === 'Symbol(undefined)'
    : description === undefined ? NativeSymbol$1() : NativeSymbol$1(description);
    if (description === '') EmptyStringDescriptionStore$1[result] = true;
    return result;
  };

  copyConstructorProperties$3(SymbolWrapper$1, NativeSymbol$1);
  var symbolPrototype$1 = SymbolWrapper$1.prototype = NativeSymbol$1.prototype;
  symbolPrototype$1.constructor = SymbolWrapper$1;
  var symbolToString$3 = symbolPrototype$1.toString;
  var native$1 = String(NativeSymbol$1('test')) == 'Symbol(test)';
  var regexp$1 = /^Symbol\((.*)\)[^)]+$/;
  defineProperty$g(symbolPrototype$1, 'description', {
    configurable: true,
    get: function description() {
      var symbol = isObject$z(this) ? this.valueOf() : this;
      var string = symbolToString$3.call(symbol);
      if (has$l(EmptyStringDescriptionStore$1, symbol)) return '';
      var desc = native$1 ? string.slice(7, -1) : string.replace(regexp$1, '$1');
      return desc === '' ? undefined : desc;
    }
  });
  $$B({
    global: true,
    forced: true
  }, {
    Symbol: SymbolWrapper$1
  });
}

var defineWellKnownSymbol$3 = defineWellKnownSymbol$5; // `Symbol.iterator` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.iterator


defineWellKnownSymbol$3('iterator');

var anObject$n = anObject$t;

var iteratorClose$5 = function (iterator) {
  var returnMethod = iterator['return'];

  if (returnMethod !== undefined) {
    return anObject$n(returnMethod.call(iterator)).value;
  }
};

var anObject$m = anObject$t;

var iteratorClose$4 = iteratorClose$5; // call something on iterator step with safe closing on error


var callWithSafeIterationClosing$3 = function (iterator, fn, value, ENTRIES) {
  try {
    return ENTRIES ? fn(anObject$m(value)[0], value[1]) : fn(value); // 7.4.6 IteratorClose(iterator, completion)
  } catch (error) {
    iteratorClose$4(iterator);
    throw error;
  }
};

var iterators$1 = {};

var wellKnownSymbol$B = wellKnownSymbol$I;

var Iterators$9 = iterators$1;

var ITERATOR$b = wellKnownSymbol$B('iterator');
var ArrayPrototype$3 = Array.prototype; // check on default Array iterator

var isArrayIteratorMethod$5 = function (it) {
  return it !== undefined && (Iterators$9.Array === it || ArrayPrototype$3[ITERATOR$b] === it);
};

var wellKnownSymbol$A = wellKnownSymbol$I;

var TO_STRING_TAG$6 = wellKnownSymbol$A('toStringTag');
var test$2 = {};
test$2[TO_STRING_TAG$6] = 'z';
var toStringTagSupport$1 = String(test$2) === '[object z]';

var TO_STRING_TAG_SUPPORT$5 = toStringTagSupport$1;

var classofRaw$2 = classofRaw$3;

var wellKnownSymbol$z = wellKnownSymbol$I;

var TO_STRING_TAG$5 = wellKnownSymbol$z('toStringTag'); // ES3 wrong here

var CORRECT_ARGUMENTS$1 = classofRaw$2(function () {
  return arguments;
}()) == 'Arguments'; // fallback for IE11 Script Access Denied error

var tryGet$1 = function (it, key) {
  try {
    return it[key];
  } catch (error) {
    /* empty */
  }
}; // getting tag from ES6+ `Object.prototype.toString`


var classof$d = TO_STRING_TAG_SUPPORT$5 ? classofRaw$2 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null' // @@toStringTag case
  : typeof (tag = tryGet$1(O = Object(it), TO_STRING_TAG$5)) == 'string' ? tag // builtinTag case
  : CORRECT_ARGUMENTS$1 ? classofRaw$2(O) // ES3 arguments fallback
  : (result = classofRaw$2(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};

var classof$c = classof$d;

var Iterators$8 = iterators$1;

var wellKnownSymbol$y = wellKnownSymbol$I;

var ITERATOR$a = wellKnownSymbol$y('iterator');

var getIteratorMethod$5 = function (it) {
  if (it != undefined) return it[ITERATOR$a] || it['@@iterator'] || Iterators$8[classof$c(it)];
};

var bind$a = functionBindContext$1;

var toObject$h = toObject$l;

var callWithSafeIterationClosing$2 = callWithSafeIterationClosing$3;

var isArrayIteratorMethod$4 = isArrayIteratorMethod$5;

var toLength$e = toLength$i;

var createProperty$5 = createProperty$7;

var getIteratorMethod$4 = getIteratorMethod$5; // `Array.from` method implementation
// https://tc39.es/ecma262/#sec-array.from


var arrayFrom$1 = function from(arrayLike
/* , mapfn = undefined, thisArg = undefined */
) {
  var O = toObject$h(arrayLike);
  var C = typeof this == 'function' ? this : Array;
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod$4(O);
  var index = 0;
  var length, result, step, iterator, next, value;
  if (mapping) mapfn = bind$a(mapfn, argumentsLength > 2 ? arguments[2] : undefined, 2); // if the target is not iterable or it's an array with the default iterator - use a simple case

  if (iteratorMethod != undefined && !(C == Array && isArrayIteratorMethod$4(iteratorMethod))) {
    iterator = iteratorMethod.call(O);
    next = iterator.next;
    result = new C();

    for (; !(step = next.call(iterator)).done; index++) {
      value = mapping ? callWithSafeIterationClosing$2(iterator, mapfn, [step.value, index], true) : step.value;
      createProperty$5(result, index, value);
    }
  } else {
    length = toLength$e(O.length);
    result = new C(length);

    for (; length > index; index++) {
      value = mapping ? mapfn(O[index], index) : O[index];
      createProperty$5(result, index, value);
    }
  }

  result.length = index;
  return result;
};

var wellKnownSymbol$x = wellKnownSymbol$I;

var ITERATOR$9 = wellKnownSymbol$x('iterator');
var SAFE_CLOSING$1 = false;

try {
  var called$1 = 0;
  var iteratorWithReturn$1 = {
    next: function () {
      return {
        done: !!called$1++
      };
    },
    'return': function () {
      SAFE_CLOSING$1 = true;
    }
  };

  iteratorWithReturn$1[ITERATOR$9] = function () {
    return this;
  }; // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing


  Array.from(iteratorWithReturn$1, function () {
    throw 2;
  });
} catch (error) {
  /* empty */
}

var checkCorrectnessOfIteration$6 = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING$1) return false;
  var ITERATION_SUPPORT = false;

  try {
    var object = {};

    object[ITERATOR$9] = function () {
      return {
        next: function () {
          return {
            done: ITERATION_SUPPORT = true
          };
        }
      };
    };

    exec(object);
  } catch (error) {
    /* empty */
  }

  return ITERATION_SUPPORT;
};

var $$A = _export$1;

var from$1 = arrayFrom$1;

var checkCorrectnessOfIteration$5 = checkCorrectnessOfIteration$6;

var INCORRECT_ITERATION$2 = !checkCorrectnessOfIteration$5(function (iterable) {
  // eslint-disable-next-line es/no-array-from -- required for testing
  Array.from(iterable);
}); // `Array.from` method
// https://tc39.es/ecma262/#sec-array.from

$$A({
  target: 'Array',
  stat: true,
  forced: INCORRECT_ITERATION$2
}, {
  from: from$1
});

var anObject$l = anObject$t; // `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags


var regexpFlags$3 = function () {
  var that = anObject$l(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};

var regexpStickyHelpers$1 = {};

var fails$A = fails$K; // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError,
// so we use an intermediate function.


function RE$1(s, f) {
  return RegExp(s, f);
}

regexpStickyHelpers$1.UNSUPPORTED_Y = fails$A(function () {
  // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
  var re = RE$1('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});
regexpStickyHelpers$1.BROKEN_CARET = fails$A(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = RE$1('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});

/* eslint-disable regexp/no-assertion-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */

/* eslint-disable regexp/no-useless-quantifier -- testing */

var regexpFlags$2 = regexpFlags$3;

var stickyHelpers$4 = regexpStickyHelpers$1;

var shared$6 = shared$b.exports;

var nativeExec$1 = RegExp.prototype.exec;
var nativeReplace$1 = shared$6('native-string-replace', String.prototype.replace);
var patchedExec$1 = nativeExec$1;

var UPDATES_LAST_INDEX_WRONG$1 = function () {
  var re1 = /a/;
  var re2 = /b*/g;
  nativeExec$1.call(re1, 'a');
  nativeExec$1.call(re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
}();

var UNSUPPORTED_Y$4 = stickyHelpers$4.UNSUPPORTED_Y || stickyHelpers$4.BROKEN_CARET; // nonparticipating capturing group, copied from es5-shim's String#split patch.

var NPCG_INCLUDED$1 = /()??/.exec('')[1] !== undefined;
var PATCH$1 = UPDATES_LAST_INDEX_WRONG$1 || NPCG_INCLUDED$1 || UNSUPPORTED_Y$4;

if (PATCH$1) {
  patchedExec$1 = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;
    var sticky = UNSUPPORTED_Y$4 && re.sticky;
    var flags = regexpFlags$2.call(re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = flags.replace('y', '');

      if (flags.indexOf('g') === -1) {
        flags += 'g';
      }

      strCopy = String(str).slice(re.lastIndex); // Support anchored sticky behavior.

      if (re.lastIndex > 0 && (!re.multiline || re.multiline && str[re.lastIndex - 1] !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      } // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.


      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED$1) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }

    if (UPDATES_LAST_INDEX_WRONG$1) lastIndex = re.lastIndex;
    match = nativeExec$1.call(sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = match.input.slice(charsAdded);
        match[0] = match[0].slice(charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG$1 && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }

    if (NPCG_INCLUDED$1 && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      nativeReplace$1.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

var regexpExec$6 = patchedExec$1;

var $$z = _export$1;

var exec$1 = regexpExec$6; // `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec


$$z({
  target: 'RegExp',
  proto: true,
  forced: /./.exec !== exec$1
}, {
  exec: exec$1
});

var wellKnownSymbol$w = wellKnownSymbol$I;

var create$6 = objectCreate$1;

var definePropertyModule$8 = objectDefineProperty$1;

var UNSCOPABLES$1 = wellKnownSymbol$w('unscopables');
var ArrayPrototype$2 = Array.prototype; // Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

if (ArrayPrototype$2[UNSCOPABLES$1] == undefined) {
  definePropertyModule$8.f(ArrayPrototype$2, UNSCOPABLES$1, {
    configurable: true,
    value: create$6(null)
  });
} // add a key to Array.prototype[@@unscopables]


var addToUnscopables$7 = function (key) {
  ArrayPrototype$2[UNSCOPABLES$1][key] = true;
};

var fails$z = fails$K;

var correctPrototypeGetter$1 = !fails$z(function () {
  function F() {
    /* empty */
  }

  F.prototype.constructor = null; // eslint-disable-next-line es/no-object-getprototypeof -- required for testing

  return Object.getPrototypeOf(new F()) !== F.prototype;
});

var has$k = has$w;

var toObject$g = toObject$l;

var sharedKey$5 = sharedKey$9;

var CORRECT_PROTOTYPE_GETTER$2 = correctPrototypeGetter$1;

var IE_PROTO$2 = sharedKey$5('IE_PROTO');
var ObjectPrototype$2 = Object.prototype; // `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
// eslint-disable-next-line es/no-object-getprototypeof -- safe

var objectGetPrototypeOf$1 = CORRECT_PROTOTYPE_GETTER$2 ? Object.getPrototypeOf : function (O) {
  O = toObject$g(O);
  if (has$k(O, IE_PROTO$2)) return O[IE_PROTO$2];

  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  }

  return O instanceof Object ? ObjectPrototype$2 : null;
};

var fails$y = fails$K;

var getPrototypeOf$3 = objectGetPrototypeOf$1;

var createNonEnumerableProperty$f = createNonEnumerableProperty$l;

var has$j = has$w;

var wellKnownSymbol$v = wellKnownSymbol$I;

var ITERATOR$8 = wellKnownSymbol$v('iterator');
var BUGGY_SAFARI_ITERATORS$3 = false;

var returnThis$5 = function () {
  return this;
}; // `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object


var IteratorPrototype$5, PrototypeOfArrayIteratorPrototype$1, arrayIterator$1;
/* eslint-disable es/no-array-prototype-keys -- safe */

if ([].keys) {
  arrayIterator$1 = [].keys(); // Safari 8 has buggy iterators w/o `next`

  if (!('next' in arrayIterator$1)) BUGGY_SAFARI_ITERATORS$3 = true;else {
    PrototypeOfArrayIteratorPrototype$1 = getPrototypeOf$3(getPrototypeOf$3(arrayIterator$1));
    if (PrototypeOfArrayIteratorPrototype$1 !== Object.prototype) IteratorPrototype$5 = PrototypeOfArrayIteratorPrototype$1;
  }
}

var NEW_ITERATOR_PROTOTYPE$1 = IteratorPrototype$5 == undefined || fails$y(function () {
  var test = {}; // FF44- legacy iterators case

  return IteratorPrototype$5[ITERATOR$8].call(test) !== test;
});
if (NEW_ITERATOR_PROTOTYPE$1) IteratorPrototype$5 = {}; // 25.1.2.1.1 %IteratorPrototype%[@@iterator]()

if (!has$j(IteratorPrototype$5, ITERATOR$8)) {
  createNonEnumerableProperty$f(IteratorPrototype$5, ITERATOR$8, returnThis$5);
}

var iteratorsCore$1 = {
  IteratorPrototype: IteratorPrototype$5,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS$3
};

var IteratorPrototype$4 = iteratorsCore$1.IteratorPrototype;

var create$5 = objectCreate$1;

var createPropertyDescriptor$6 = createPropertyDescriptor$b;

var setToStringTag$8 = setToStringTag$a;

var Iterators$7 = iterators$1;

var returnThis$4 = function () {
  return this;
};

var createIteratorConstructor$3 = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create$5(IteratorPrototype$4, {
    next: createPropertyDescriptor$6(1, next)
  });
  setToStringTag$8(IteratorConstructor, TO_STRING_TAG, false);
  Iterators$7[TO_STRING_TAG] = returnThis$4;
  return IteratorConstructor;
};

var isObject$y = isObject$J;

var aPossiblePrototype$3 = function (it) {
  if (!isObject$y(it) && it !== null) {
    throw TypeError("Can't set " + String(it) + ' as a prototype');
  }

  return it;
};

/* eslint-disable no-proto -- safe */

var anObject$k = anObject$t;

var aPossiblePrototype$2 = aPossiblePrototype$3; // `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe


var objectSetPrototypeOf$1 = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;

  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    setter = Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set;
    setter.call(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) {
    /* empty */
  }

  return function setPrototypeOf(O, proto) {
    anObject$k(O);
    aPossiblePrototype$2(proto);
    if (CORRECT_SETTER) setter.call(O, proto);else O.__proto__ = proto;
    return O;
  };
}() : undefined);

var $$y = _export$1;

var createIteratorConstructor$2 = createIteratorConstructor$3;

var getPrototypeOf$2 = objectGetPrototypeOf$1;

var setPrototypeOf$4 = objectSetPrototypeOf$1;

var setToStringTag$7 = setToStringTag$a;

var createNonEnumerableProperty$e = createNonEnumerableProperty$l;

var redefine$i = redefine$l.exports;

var wellKnownSymbol$u = wellKnownSymbol$I;

var Iterators$6 = iterators$1;

var IteratorsCore$1 = iteratorsCore$1;

var IteratorPrototype$3 = IteratorsCore$1.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS$2 = IteratorsCore$1.BUGGY_SAFARI_ITERATORS;
var ITERATOR$7 = wellKnownSymbol$u('iterator');
var KEYS$1 = 'keys';
var VALUES$1 = 'values';
var ENTRIES$1 = 'entries';

var returnThis$3 = function () {
  return this;
};

var defineIterator$7 = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor$2(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS$2 && KIND in IterablePrototype) return IterablePrototype[KIND];

    switch (KIND) {
      case KEYS$1:
        return function keys() {
          return new IteratorConstructor(this, KIND);
        };

      case VALUES$1:
        return function values() {
          return new IteratorConstructor(this, KIND);
        };

      case ENTRIES$1:
        return function entries() {
          return new IteratorConstructor(this, KIND);
        };
    }

    return function () {
      return new IteratorConstructor(this);
    };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR$7] || IterablePrototype['@@iterator'] || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS$2 && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY; // fix native

  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf$2(anyNativeIterator.call(new Iterable()));

    if (IteratorPrototype$3 !== Object.prototype && CurrentIteratorPrototype.next) {
      if (getPrototypeOf$2(CurrentIteratorPrototype) !== IteratorPrototype$3) {
        if (setPrototypeOf$4) {
          setPrototypeOf$4(CurrentIteratorPrototype, IteratorPrototype$3);
        } else if (typeof CurrentIteratorPrototype[ITERATOR$7] != 'function') {
          createNonEnumerableProperty$e(CurrentIteratorPrototype, ITERATOR$7, returnThis$3);
        }
      } // Set @@toStringTag to native iterators


      setToStringTag$7(CurrentIteratorPrototype, TO_STRING_TAG, true);
    }
  } // fix Array#{values, @@iterator}.name in V8 / FF


  if (DEFAULT == VALUES$1 && nativeIterator && nativeIterator.name !== VALUES$1) {
    INCORRECT_VALUES_NAME = true;

    defaultIterator = function values() {
      return nativeIterator.call(this);
    };
  } // define iterator


  if (IterablePrototype[ITERATOR$7] !== defaultIterator) {
    createNonEnumerableProperty$e(IterablePrototype, ITERATOR$7, defaultIterator);
  }

  Iterators$6[NAME] = defaultIterator; // export additional methods

  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES$1),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS$1),
      entries: getIterationMethod(ENTRIES$1)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS$2 || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine$i(IterablePrototype, KEY, methods[KEY]);
      }
    } else $$y({
      target: NAME,
      proto: true,
      forced: BUGGY_SAFARI_ITERATORS$2 || INCORRECT_VALUES_NAME
    }, methods);
  }

  return methods;
};

var toIndexedObject$b = toIndexedObject$i;

var addToUnscopables$6 = addToUnscopables$7;

var Iterators$5 = iterators$1;

var InternalStateModule$8 = internalState$1;

var defineIterator$6 = defineIterator$7;

var ARRAY_ITERATOR$1 = 'Array Iterator';
var setInternalState$7 = InternalStateModule$8.set;
var getInternalState$6 = InternalStateModule$8.getterFor(ARRAY_ITERATOR$1); // `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator

var es_array_iterator$1 = defineIterator$6(Array, 'Array', function (iterated, kind) {
  setInternalState$7(this, {
    type: ARRAY_ITERATOR$1,
    target: toIndexedObject$b(iterated),
    // target
    index: 0,
    // next index
    kind: kind // kind

  }); // `%ArrayIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState$6(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;

  if (!target || index >= target.length) {
    state.target = undefined;
    return {
      value: undefined,
      done: true
    };
  }

  if (kind == 'keys') return {
    value: index,
    done: false
  };
  if (kind == 'values') return {
    value: target[index],
    done: false
  };
  return {
    value: [index, target[index]],
    done: false
  };
}, 'values'); // argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject

Iterators$5.Arguments = Iterators$5.Array; // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$6('keys');
addToUnscopables$6('values');
addToUnscopables$6('entries');

var TO_STRING_TAG_SUPPORT$4 = toStringTagSupport$1;

var classof$b = classof$d; // `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring


var objectToString$8 = TO_STRING_TAG_SUPPORT$4 ? {}.toString : function toString() {
  return '[object ' + classof$b(this) + ']';
};

var TO_STRING_TAG_SUPPORT$3 = toStringTagSupport$1;

var redefine$h = redefine$l.exports;

var toString$9 = objectToString$8; // `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring


if (!TO_STRING_TAG_SUPPORT$3) {
  redefine$h(Object.prototype, 'toString', toString$9, {
    unsafe: true
  });
}

var anObject$j = anObject$t;

var isArrayIteratorMethod$3 = isArrayIteratorMethod$5;

var toLength$d = toLength$i;

var bind$9 = functionBindContext$1;

var getIteratorMethod$3 = getIteratorMethod$5;

var iteratorClose$3 = iteratorClose$5;

var Result$1 = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var iterate$6 = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind$9(unboundFunction, that, 1 + AS_ENTRIES + INTERRUPTED);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose$3(iterator);
    return new Result$1(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject$j(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    }

    return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod$3(iterable);
    if (typeof iterFn != 'function') throw TypeError('Target is not iterable'); // optimisation for array iterators

    if (isArrayIteratorMethod$3(iterFn)) {
      for (index = 0, length = toLength$d(iterable.length); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && result instanceof Result$1) return result;
      }

      return new Result$1(false);
    }

    iterator = iterFn.call(iterable);
  }

  next = iterator.next;

  while (!(step = next.call(iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose$3(iterator);
      throw error;
    }

    if (typeof result == 'object' && result && result instanceof Result$1) return result;
  }

  return new Result$1(false);
};

var anInstance$6 = function (it, Constructor, name) {
  if (!(it instanceof Constructor)) {
    throw TypeError('Incorrect ' + (name ? name + ' ' : '') + 'invocation');
  }

  return it;
};

var isObject$x = isObject$J;

var setPrototypeOf$3 = objectSetPrototypeOf$1; // makes subclassing work correct for wrapped built-ins


var inheritIfRequired$6 = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if ( // it can work only with native `setPrototypeOf`
  setPrototypeOf$3 && // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
  typeof (NewTarget = dummy.constructor) == 'function' && NewTarget !== Wrapper && isObject$x(NewTargetPrototype = NewTarget.prototype) && NewTargetPrototype !== Wrapper.prototype) setPrototypeOf$3($this, NewTargetPrototype);
  return $this;
};

var $$x = _export$1;

var global$t = global$H;

var isForced$7 = isForced_1$1;

var redefine$g = redefine$l.exports;

var InternalMetadataModule$1 = internalMetadata$1.exports;

var iterate$5 = iterate$6;

var anInstance$5 = anInstance$6;

var isObject$w = isObject$J;

var fails$x = fails$K;

var checkCorrectnessOfIteration$4 = checkCorrectnessOfIteration$6;

var setToStringTag$6 = setToStringTag$a;

var inheritIfRequired$5 = inheritIfRequired$6;

var collection$3 = function (CONSTRUCTOR_NAME, wrapper, common) {
  var IS_MAP = CONSTRUCTOR_NAME.indexOf('Map') !== -1;
  var IS_WEAK = CONSTRUCTOR_NAME.indexOf('Weak') !== -1;
  var ADDER = IS_MAP ? 'set' : 'add';
  var NativeConstructor = global$t[CONSTRUCTOR_NAME];
  var NativePrototype = NativeConstructor && NativeConstructor.prototype;
  var Constructor = NativeConstructor;
  var exported = {};

  var fixMethod = function (KEY) {
    var nativeMethod = NativePrototype[KEY];
    redefine$g(NativePrototype, KEY, KEY == 'add' ? function add(value) {
      nativeMethod.call(this, value === 0 ? 0 : value);
      return this;
    } : KEY == 'delete' ? function (key) {
      return IS_WEAK && !isObject$w(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'get' ? function get(key) {
      return IS_WEAK && !isObject$w(key) ? undefined : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'has' ? function has(key) {
      return IS_WEAK && !isObject$w(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : function set(key, value) {
      nativeMethod.call(this, key === 0 ? 0 : key, value);
      return this;
    });
  };

  var REPLACE = isForced$7(CONSTRUCTOR_NAME, typeof NativeConstructor != 'function' || !(IS_WEAK || NativePrototype.forEach && !fails$x(function () {
    new NativeConstructor().entries().next();
  })));

  if (REPLACE) {
    // create collection constructor
    Constructor = common.getConstructor(wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER);
    InternalMetadataModule$1.REQUIRED = true;
  } else if (isForced$7(CONSTRUCTOR_NAME, true)) {
    var instance = new Constructor(); // early implementations not supports chaining

    var HASNT_CHAINING = instance[ADDER](IS_WEAK ? {} : -0, 1) != instance; // V8 ~ Chromium 40- weak-collections throws on primitives, but should return false

    var THROWS_ON_PRIMITIVES = fails$x(function () {
      instance.has(1);
    }); // most early implementations doesn't supports iterables, most modern - not close it correctly
    // eslint-disable-next-line no-new -- required for testing

    var ACCEPT_ITERABLES = checkCorrectnessOfIteration$4(function (iterable) {
      new NativeConstructor(iterable);
    }); // for early implementations -0 and +0 not the same

    var BUGGY_ZERO = !IS_WEAK && fails$x(function () {
      // V8 ~ Chromium 42- fails only with 5+ elements
      var $instance = new NativeConstructor();
      var index = 5;

      while (index--) $instance[ADDER](index, index);

      return !$instance.has(-0);
    });

    if (!ACCEPT_ITERABLES) {
      Constructor = wrapper(function (dummy, iterable) {
        anInstance$5(dummy, Constructor, CONSTRUCTOR_NAME);
        var that = inheritIfRequired$5(new NativeConstructor(), dummy, Constructor);
        if (iterable != undefined) iterate$5(iterable, that[ADDER], {
          that: that,
          AS_ENTRIES: IS_MAP
        });
        return that;
      });
      Constructor.prototype = NativePrototype;
      NativePrototype.constructor = Constructor;
    }

    if (THROWS_ON_PRIMITIVES || BUGGY_ZERO) {
      fixMethod('delete');
      fixMethod('has');
      IS_MAP && fixMethod('get');
    }

    if (BUGGY_ZERO || HASNT_CHAINING) fixMethod(ADDER); // weak collections should not contains .clear method

    if (IS_WEAK && NativePrototype.clear) delete NativePrototype.clear;
  }

  exported[CONSTRUCTOR_NAME] = Constructor;
  $$x({
    global: true,
    forced: Constructor != NativeConstructor
  }, exported);
  setToStringTag$6(Constructor, CONSTRUCTOR_NAME);
  if (!IS_WEAK) common.setStrong(Constructor, CONSTRUCTOR_NAME, IS_MAP);
  return Constructor;
};

var redefine$f = redefine$l.exports;

var redefineAll$4 = function (target, src, options) {
  for (var key in src) redefine$f(target, key, src[key], options);

  return target;
};

var getBuiltIn$7 = getBuiltIn$c;

var definePropertyModule$7 = objectDefineProperty$1;

var wellKnownSymbol$t = wellKnownSymbol$I;

var DESCRIPTORS$j = descriptors$1;

var SPECIES$8 = wellKnownSymbol$t('species');

var setSpecies$6 = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn$7(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule$7.f;

  if (DESCRIPTORS$j && Constructor && !Constructor[SPECIES$8]) {
    defineProperty(Constructor, SPECIES$8, {
      configurable: true,
      get: function () {
        return this;
      }
    });
  }
};

var defineProperty$f = objectDefineProperty$1.f;

var create$4 = objectCreate$1;

var redefineAll$3 = redefineAll$4;

var bind$8 = functionBindContext$1;

var anInstance$4 = anInstance$6;

var iterate$4 = iterate$6;

var defineIterator$5 = defineIterator$7;

var setSpecies$5 = setSpecies$6;

var DESCRIPTORS$i = descriptors$1;

var fastKey$2 = internalMetadata$1.exports.fastKey;

var InternalStateModule$7 = internalState$1;

var setInternalState$6 = InternalStateModule$7.set;
var internalStateGetterFor$1 = InternalStateModule$7.getterFor;
var collectionStrong$3 = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance$4(that, C, CONSTRUCTOR_NAME);
      setInternalState$6(that, {
        type: CONSTRUCTOR_NAME,
        index: create$4(null),
        first: undefined,
        last: undefined,
        size: 0
      });
      if (!DESCRIPTORS$i) that.size = 0;
      if (iterable != undefined) iterate$4(iterable, that[ADDER], {
        that: that,
        AS_ENTRIES: IS_MAP
      });
    });
    var getInternalState = internalStateGetterFor$1(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var entry = getEntry(that, key);
      var previous, index; // change existing entry

      if (entry) {
        entry.value = value; // create new entry
      } else {
        state.last = entry = {
          index: index = fastKey$2(key, true),
          key: key,
          value: value,
          previous: previous = state.last,
          next: undefined,
          removed: false
        };
        if (!state.first) state.first = entry;
        if (previous) previous.next = entry;
        if (DESCRIPTORS$i) state.size++;else that.size++; // add to index

        if (index !== 'F') state.index[index] = entry;
      }

      return that;
    };

    var getEntry = function (that, key) {
      var state = getInternalState(that); // fast case

      var index = fastKey$2(key);
      var entry;
      if (index !== 'F') return state.index[index]; // frozen object case

      for (entry = state.first; entry; entry = entry.next) {
        if (entry.key == key) return entry;
      }
    };

    redefineAll$3(C.prototype, {
      // 23.1.3.1 Map.prototype.clear()
      // 23.2.3.2 Set.prototype.clear()
      clear: function clear() {
        var that = this;
        var state = getInternalState(that);
        var data = state.index;
        var entry = state.first;

        while (entry) {
          entry.removed = true;
          if (entry.previous) entry.previous = entry.previous.next = undefined;
          delete data[entry.index];
          entry = entry.next;
        }

        state.first = state.last = undefined;
        if (DESCRIPTORS$i) state.size = 0;else that.size = 0;
      },
      // 23.1.3.3 Map.prototype.delete(key)
      // 23.2.3.4 Set.prototype.delete(value)
      'delete': function (key) {
        var that = this;
        var state = getInternalState(that);
        var entry = getEntry(that, key);

        if (entry) {
          var next = entry.next;
          var prev = entry.previous;
          delete state.index[entry.index];
          entry.removed = true;
          if (prev) prev.next = next;
          if (next) next.previous = prev;
          if (state.first == entry) state.first = next;
          if (state.last == entry) state.last = prev;
          if (DESCRIPTORS$i) state.size--;else that.size--;
        }

        return !!entry;
      },
      // 23.2.3.6 Set.prototype.forEach(callbackfn, thisArg = undefined)
      // 23.1.3.5 Map.prototype.forEach(callbackfn, thisArg = undefined)
      forEach: function forEach(callbackfn
      /* , that = undefined */
      ) {
        var state = getInternalState(this);
        var boundFunction = bind$8(callbackfn, arguments.length > 1 ? arguments[1] : undefined, 3);
        var entry;

        while (entry = entry ? entry.next : state.first) {
          boundFunction(entry.value, entry.key, this); // revert to the last existing entry

          while (entry && entry.removed) entry = entry.previous;
        }
      },
      // 23.1.3.7 Map.prototype.has(key)
      // 23.2.3.7 Set.prototype.has(value)
      has: function has(key) {
        return !!getEntry(this, key);
      }
    });
    redefineAll$3(C.prototype, IS_MAP ? {
      // 23.1.3.6 Map.prototype.get(key)
      get: function get(key) {
        var entry = getEntry(this, key);
        return entry && entry.value;
      },
      // 23.1.3.9 Map.prototype.set(key, value)
      set: function set(key, value) {
        return define(this, key === 0 ? 0 : key, value);
      }
    } : {
      // 23.2.3.1 Set.prototype.add(value)
      add: function add(value) {
        return define(this, value = value === 0 ? 0 : value, value);
      }
    });
    if (DESCRIPTORS$i) defineProperty$f(C.prototype, 'size', {
      get: function () {
        return getInternalState(this).size;
      }
    });
    return C;
  },
  setStrong: function (C, CONSTRUCTOR_NAME, IS_MAP) {
    var ITERATOR_NAME = CONSTRUCTOR_NAME + ' Iterator';
    var getInternalCollectionState = internalStateGetterFor$1(CONSTRUCTOR_NAME);
    var getInternalIteratorState = internalStateGetterFor$1(ITERATOR_NAME); // add .keys, .values, .entries, [@@iterator]
    // 23.1.3.4, 23.1.3.8, 23.1.3.11, 23.1.3.12, 23.2.3.5, 23.2.3.8, 23.2.3.10, 23.2.3.11

    defineIterator$5(C, CONSTRUCTOR_NAME, function (iterated, kind) {
      setInternalState$6(this, {
        type: ITERATOR_NAME,
        target: iterated,
        state: getInternalCollectionState(iterated),
        kind: kind,
        last: undefined
      });
    }, function () {
      var state = getInternalIteratorState(this);
      var kind = state.kind;
      var entry = state.last; // revert to the last existing entry

      while (entry && entry.removed) entry = entry.previous; // get next entry


      if (!state.target || !(state.last = entry = entry ? entry.next : state.state.first)) {
        // or finish the iteration
        state.target = undefined;
        return {
          value: undefined,
          done: true
        };
      } // return step by kind


      if (kind == 'keys') return {
        value: entry.key,
        done: false
      };
      if (kind == 'values') return {
        value: entry.value,
        done: false
      };
      return {
        value: [entry.key, entry.value],
        done: false
      };
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true); // add [@@species], 23.1.2.2, 23.2.2.2

    setSpecies$5(CONSTRUCTOR_NAME);
  }
};

var collection$2 = collection$3;

var collectionStrong$2 = collectionStrong$3; // `Set` constructor
// https://tc39.es/ecma262/#sec-set-objects


collection$2('Set', function (init) {
  return function Set() {
    return init(this, arguments.length ? arguments[0] : undefined);
  };
}, collectionStrong$2);

var toInteger$5 = toInteger$8;

var requireObjectCoercible$b = requireObjectCoercible$e; // `String.prototype.{ codePointAt, at }` methods implementation


var createMethod$6 = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = String(requireObjectCoercible$b($this));
    var position = toInteger$5(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = S.charCodeAt(position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size || (second = S.charCodeAt(position + 1)) < 0xDC00 || second > 0xDFFF ? CONVERT_TO_STRING ? S.charAt(position) : first : CONVERT_TO_STRING ? S.slice(position, position + 2) : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

var stringMultibyte$1 = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod$6(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod$6(true)
};

var charAt$2 = stringMultibyte$1.charAt;

var InternalStateModule$6 = internalState$1;

var defineIterator$4 = defineIterator$7;

var STRING_ITERATOR$1 = 'String Iterator';
var setInternalState$5 = InternalStateModule$6.set;
var getInternalState$5 = InternalStateModule$6.getterFor(STRING_ITERATOR$1); // `String.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-string.prototype-@@iterator

defineIterator$4(String, 'String', function (iterated) {
  setInternalState$5(this, {
    type: STRING_ITERATOR$1,
    string: String(iterated),
    index: 0
  }); // `%StringIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState$5(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return {
    value: undefined,
    done: true
  };
  point = charAt$2(string, index);
  state.index += point.length;
  return {
    value: point,
    done: false
  };
});

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables$1 = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

var global$s = global$H;

var DOMIterables$3 = domIterables$1;

var ArrayIteratorMethods$1 = es_array_iterator$1;

var createNonEnumerableProperty$d = createNonEnumerableProperty$l;

var wellKnownSymbol$s = wellKnownSymbol$I;

var ITERATOR$6 = wellKnownSymbol$s('iterator');
var TO_STRING_TAG$4 = wellKnownSymbol$s('toStringTag');
var ArrayValues$1 = ArrayIteratorMethods$1.values;

for (var COLLECTION_NAME$3 in DOMIterables$3) {
  var Collection$3 = global$s[COLLECTION_NAME$3];
  var CollectionPrototype$3 = Collection$3 && Collection$3.prototype;

  if (CollectionPrototype$3) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype$3[ITERATOR$6] !== ArrayValues$1) try {
      createNonEnumerableProperty$d(CollectionPrototype$3, ITERATOR$6, ArrayValues$1);
    } catch (error) {
      CollectionPrototype$3[ITERATOR$6] = ArrayValues$1;
    }

    if (!CollectionPrototype$3[TO_STRING_TAG$4]) {
      createNonEnumerableProperty$d(CollectionPrototype$3, TO_STRING_TAG$4, COLLECTION_NAME$3);
    }

    if (DOMIterables$3[COLLECTION_NAME$3]) for (var METHOD_NAME$1 in ArrayIteratorMethods$1) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype$3[METHOD_NAME$1] !== ArrayIteratorMethods$1[METHOD_NAME$1]) try {
        createNonEnumerableProperty$d(CollectionPrototype$3, METHOD_NAME$1, ArrayIteratorMethods$1[METHOD_NAME$1]);
      } catch (error) {
        CollectionPrototype$3[METHOD_NAME$1] = ArrayIteratorMethods$1[METHOD_NAME$1];
      }
    }
  }
}

var DESCRIPTORS$h = descriptors$1;

var fails$w = fails$K;

var objectKeys$6 = objectKeys$9;

var getOwnPropertySymbolsModule$3 = objectGetOwnPropertySymbols$1;

var propertyIsEnumerableModule$3 = objectPropertyIsEnumerable$1;

var toObject$f = toObject$l;

var IndexedObject$4 = indexedObject$1; // eslint-disable-next-line es/no-object-assign -- safe


var $assign$1 = Object.assign; // eslint-disable-next-line es/no-object-defineproperty -- required for testing

var defineProperty$e = Object.defineProperty; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign

var objectAssign$1 = !$assign$1 || fails$w(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS$h && $assign$1({
    b: 1
  }, $assign$1(defineProperty$e({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty$e(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), {
    b: 2
  })).b !== 1) return true; // should work with symbols and should have deterministic property order (V8 bug)

  var A = {};
  var B = {}; // eslint-disable-next-line es/no-symbol -- safe

  var symbol = Symbol();
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) {
    B[chr] = chr;
  });
  return $assign$1({}, A)[symbol] != 7 || objectKeys$6($assign$1({}, B)).join('') != alphabet;
}) ? function assign(target, source) {
  // eslint-disable-line no-unused-vars -- required for `.length`
  var T = toObject$f(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$3.f;
  var propertyIsEnumerable = propertyIsEnumerableModule$3.f;

  while (argumentsLength > index) {
    var S = IndexedObject$4(arguments[index++]);
    var keys = getOwnPropertySymbols ? objectKeys$6(S).concat(getOwnPropertySymbols(S)) : objectKeys$6(S);
    var length = keys.length;
    var j = 0;
    var key;

    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS$h || propertyIsEnumerable.call(S, key)) T[key] = S[key];
    }
  }

  return T;
} : $assign$1;

var $$w = _export$1;

var assign$2 = objectAssign$1; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
// eslint-disable-next-line es/no-object-assign -- required for testing


$$w({
  target: 'Object',
  stat: true,
  forced: Object.assign !== assign$2
}, {
  assign: assign$2
});

var $$v = _export$1;

var fails$v = fails$K;

var toObject$e = toObject$l;

var nativeGetPrototypeOf = objectGetPrototypeOf$1;

var CORRECT_PROTOTYPE_GETTER$1 = correctPrototypeGetter$1;

var FAILS_ON_PRIMITIVES$3 = fails$v(function () {
  nativeGetPrototypeOf(1);
}); // `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof

$$v({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$3,
  sham: !CORRECT_PROTOTYPE_GETTER$1
}, {
  getPrototypeOf: function getPrototypeOf(it) {
    return nativeGetPrototypeOf(toObject$e(it));
  }
});

var redefine$e = redefine$l.exports;

var anObject$i = anObject$t;

var fails$u = fails$K;

var flags$1 = regexpFlags$3;

var TO_STRING$1 = 'toString';
var RegExpPrototype$5 = RegExp.prototype;
var nativeToString$1 = RegExpPrototype$5[TO_STRING$1];
var NOT_GENERIC$1 = fails$u(function () {
  return nativeToString$1.call({
    source: 'a',
    flags: 'b'
  }) != '/a/b';
}); // FF44- RegExp#toString has a wrong name

var INCORRECT_NAME$1 = nativeToString$1.name != TO_STRING$1; // `RegExp.prototype.toString` method
// https://tc39.es/ecma262/#sec-regexp.prototype.tostring

if (NOT_GENERIC$1 || INCORRECT_NAME$1) {
  redefine$e(RegExp.prototype, TO_STRING$1, function toString() {
    var R = anObject$i(this);
    var p = String(R.source);
    var rf = R.flags;
    var f = String(rf === undefined && R instanceof RegExp && !('flags' in RegExpPrototype$5) ? flags$1.call(R) : rf);
    return '/' + p + '/' + f;
  }, {
    unsafe: true
  });
}

var $$u = _export$1;

var toObject$d = toObject$l;

var nativeKeys$5 = objectKeys$9;

var fails$t = fails$K;

var FAILS_ON_PRIMITIVES$2 = fails$t(function () {
  nativeKeys$5(1);
}); // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys

$$u({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$2
}, {
  keys: function keys(it) {
    return nativeKeys$5(toObject$d(it));
  }
});

var $$t = _export$1;

var fails$s = fails$K;

var isArray$o = isArray$s;

var isObject$v = isObject$J;

var toObject$c = toObject$l;

var toLength$c = toLength$i;

var createProperty$4 = createProperty$7;

var arraySpeciesCreate$3 = arraySpeciesCreate$5;

var arrayMethodHasSpeciesSupport$7 = arrayMethodHasSpeciesSupport$9;

var wellKnownSymbol$r = wellKnownSymbol$I;

var V8_VERSION$4 = engineV8Version$1;

var IS_CONCAT_SPREADABLE$1 = wellKnownSymbol$r('isConcatSpreadable');
var MAX_SAFE_INTEGER$5 = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED$1 = 'Maximum allowed index exceeded'; // We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679

var IS_CONCAT_SPREADABLE_SUPPORT$1 = V8_VERSION$4 >= 51 || !fails$s(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE$1] = false;
  return array.concat()[0] !== array;
});
var SPECIES_SUPPORT$1 = arrayMethodHasSpeciesSupport$7('concat');

var isConcatSpreadable$1 = function (O) {
  if (!isObject$v(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE$1];
  return spreadable !== undefined ? !!spreadable : isArray$o(O);
};

var FORCED$5 = !IS_CONCAT_SPREADABLE_SUPPORT$1 || !SPECIES_SUPPORT$1; // `Array.prototype.concat` method
// https://tc39.es/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species

$$t({
  target: 'Array',
  proto: true,
  forced: FORCED$5
}, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  concat: function concat(arg) {
    var O = toObject$c(this);
    var A = arraySpeciesCreate$3(O, 0);
    var n = 0;
    var i, k, length, len, E;

    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];

      if (isConcatSpreadable$1(E)) {
        len = toLength$c(E.length);
        if (n + len > MAX_SAFE_INTEGER$5) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$1);

        for (k = 0; k < len; k++, n++) if (k in E) createProperty$4(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER$5) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED$1);
        createProperty$4(A, n++, E);
      }
    }

    A.length = n;
    return A;
  }
});

var fails$r = fails$K;

var arrayMethodIsStrict$5 = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails$r(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal -- required for testing
    method.call(null, argument || function () {
      throw 1;
    }, 1);
  });
};

var $forEach$2 = arrayIteration$1.forEach;

var arrayMethodIsStrict$4 = arrayMethodIsStrict$5;

var STRICT_METHOD$3 = arrayMethodIsStrict$4('forEach'); // `Array.prototype.forEach` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.foreach

var arrayForEach$1 = !STRICT_METHOD$3 ? function forEach(callbackfn
/* , thisArg */
) {
  return $forEach$2(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined); // eslint-disable-next-line es/no-array-prototype-foreach -- safe
} : [].forEach;

var global$r = global$H;

var DOMIterables$2 = domIterables$1;

var forEach$1 = arrayForEach$1;

var createNonEnumerableProperty$c = createNonEnumerableProperty$l;

for (var COLLECTION_NAME$2 in DOMIterables$2) {
  var Collection$2 = global$r[COLLECTION_NAME$2];
  var CollectionPrototype$2 = Collection$2 && Collection$2.prototype; // some Chrome versions have non-configurable methods on DOMTokenList

  if (CollectionPrototype$2 && CollectionPrototype$2.forEach !== forEach$1) try {
    createNonEnumerableProperty$c(CollectionPrototype$2, 'forEach', forEach$1);
  } catch (error) {
    CollectionPrototype$2.forEach = forEach$1;
  }
}

var DESCRIPTORS$g = descriptors$1;

var objectKeys$5 = objectKeys$9;

var toIndexedObject$a = toIndexedObject$i;

var propertyIsEnumerable$5 = objectPropertyIsEnumerable$1.f; // `Object.{ entries, values }` methods implementation


var createMethod$5 = function (TO_ENTRIES) {
  return function (it) {
    var O = toIndexedObject$a(it);
    var keys = objectKeys$5(O);
    var length = keys.length;
    var i = 0;
    var result = [];
    var key;

    while (length > i) {
      key = keys[i++];

      if (!DESCRIPTORS$g || propertyIsEnumerable$5.call(O, key)) {
        result.push(TO_ENTRIES ? [key, O[key]] : O[key]);
      }
    }

    return result;
  };
};

var objectToArray$1 = {
  // `Object.entries` method
  // https://tc39.es/ecma262/#sec-object.entries
  entries: createMethod$5(true),
  // `Object.values` method
  // https://tc39.es/ecma262/#sec-object.values
  values: createMethod$5(false)
};

var $$s = _export$1;

var $entries$1 = objectToArray$1.entries; // `Object.entries` method
// https://tc39.es/ecma262/#sec-object.entries


$$s({
  target: 'Object',
  stat: true
}, {
  entries: function entries(O) {
    return $entries$1(O);
  }
});

var DESCRIPTORS$f = descriptors$1;

var defineProperty$d = objectDefineProperty$1.f;

var FunctionPrototype$1 = Function.prototype;
var FunctionPrototypeToString$1 = FunctionPrototype$1.toString;
var nameRE$1 = /^\s*function ([^ (]*)/;
var NAME$1 = 'name'; // Function instances `.name` property
// https://tc39.es/ecma262/#sec-function-instances-name

if (DESCRIPTORS$f && !(NAME$1 in FunctionPrototype$1)) {
  defineProperty$d(FunctionPrototype$1, NAME$1, {
    configurable: true,
    get: function () {
      try {
        return FunctionPrototypeToString$1.call(this).match(nameRE$1)[1];
      } catch (error) {
        return '';
      }
    }
  });
}

var $$r = _export$1;

var $values$1 = objectToArray$1.values; // `Object.values` method
// https://tc39.es/ecma262/#sec-object.values


$$r({
  target: 'Object',
  stat: true
}, {
  values: function values(O) {
    return $values$1(O);
  }
});

var dist = {};

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */

function arrayReduce$1(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array == null ? 0 : array.length;

  if (initAccum && length) {
    accumulator = array[++index];
  }

  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }

  return accumulator;
}

var _arrayReduce = arrayReduce$1;

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */

function basePropertyOf$1(object) {
  return function (key) {
    return object == null ? undefined : object[key];
  };
}

var _basePropertyOf = basePropertyOf$1;

var basePropertyOf = _basePropertyOf;
/** Used to map Latin Unicode letters to basic Latin letters. */


var deburredLetters = {
  // Latin-1 Supplement block.
  '\xc0': 'A',
  '\xc1': 'A',
  '\xc2': 'A',
  '\xc3': 'A',
  '\xc4': 'A',
  '\xc5': 'A',
  '\xe0': 'a',
  '\xe1': 'a',
  '\xe2': 'a',
  '\xe3': 'a',
  '\xe4': 'a',
  '\xe5': 'a',
  '\xc7': 'C',
  '\xe7': 'c',
  '\xd0': 'D',
  '\xf0': 'd',
  '\xc8': 'E',
  '\xc9': 'E',
  '\xca': 'E',
  '\xcb': 'E',
  '\xe8': 'e',
  '\xe9': 'e',
  '\xea': 'e',
  '\xeb': 'e',
  '\xcc': 'I',
  '\xcd': 'I',
  '\xce': 'I',
  '\xcf': 'I',
  '\xec': 'i',
  '\xed': 'i',
  '\xee': 'i',
  '\xef': 'i',
  '\xd1': 'N',
  '\xf1': 'n',
  '\xd2': 'O',
  '\xd3': 'O',
  '\xd4': 'O',
  '\xd5': 'O',
  '\xd6': 'O',
  '\xd8': 'O',
  '\xf2': 'o',
  '\xf3': 'o',
  '\xf4': 'o',
  '\xf5': 'o',
  '\xf6': 'o',
  '\xf8': 'o',
  '\xd9': 'U',
  '\xda': 'U',
  '\xdb': 'U',
  '\xdc': 'U',
  '\xf9': 'u',
  '\xfa': 'u',
  '\xfb': 'u',
  '\xfc': 'u',
  '\xdd': 'Y',
  '\xfd': 'y',
  '\xff': 'y',
  '\xc6': 'Ae',
  '\xe6': 'ae',
  '\xde': 'Th',
  '\xfe': 'th',
  '\xdf': 'ss',
  // Latin Extended-A block.
  '\u0100': 'A',
  '\u0102': 'A',
  '\u0104': 'A',
  '\u0101': 'a',
  '\u0103': 'a',
  '\u0105': 'a',
  '\u0106': 'C',
  '\u0108': 'C',
  '\u010a': 'C',
  '\u010c': 'C',
  '\u0107': 'c',
  '\u0109': 'c',
  '\u010b': 'c',
  '\u010d': 'c',
  '\u010e': 'D',
  '\u0110': 'D',
  '\u010f': 'd',
  '\u0111': 'd',
  '\u0112': 'E',
  '\u0114': 'E',
  '\u0116': 'E',
  '\u0118': 'E',
  '\u011a': 'E',
  '\u0113': 'e',
  '\u0115': 'e',
  '\u0117': 'e',
  '\u0119': 'e',
  '\u011b': 'e',
  '\u011c': 'G',
  '\u011e': 'G',
  '\u0120': 'G',
  '\u0122': 'G',
  '\u011d': 'g',
  '\u011f': 'g',
  '\u0121': 'g',
  '\u0123': 'g',
  '\u0124': 'H',
  '\u0126': 'H',
  '\u0125': 'h',
  '\u0127': 'h',
  '\u0128': 'I',
  '\u012a': 'I',
  '\u012c': 'I',
  '\u012e': 'I',
  '\u0130': 'I',
  '\u0129': 'i',
  '\u012b': 'i',
  '\u012d': 'i',
  '\u012f': 'i',
  '\u0131': 'i',
  '\u0134': 'J',
  '\u0135': 'j',
  '\u0136': 'K',
  '\u0137': 'k',
  '\u0138': 'k',
  '\u0139': 'L',
  '\u013b': 'L',
  '\u013d': 'L',
  '\u013f': 'L',
  '\u0141': 'L',
  '\u013a': 'l',
  '\u013c': 'l',
  '\u013e': 'l',
  '\u0140': 'l',
  '\u0142': 'l',
  '\u0143': 'N',
  '\u0145': 'N',
  '\u0147': 'N',
  '\u014a': 'N',
  '\u0144': 'n',
  '\u0146': 'n',
  '\u0148': 'n',
  '\u014b': 'n',
  '\u014c': 'O',
  '\u014e': 'O',
  '\u0150': 'O',
  '\u014d': 'o',
  '\u014f': 'o',
  '\u0151': 'o',
  '\u0154': 'R',
  '\u0156': 'R',
  '\u0158': 'R',
  '\u0155': 'r',
  '\u0157': 'r',
  '\u0159': 'r',
  '\u015a': 'S',
  '\u015c': 'S',
  '\u015e': 'S',
  '\u0160': 'S',
  '\u015b': 's',
  '\u015d': 's',
  '\u015f': 's',
  '\u0161': 's',
  '\u0162': 'T',
  '\u0164': 'T',
  '\u0166': 'T',
  '\u0163': 't',
  '\u0165': 't',
  '\u0167': 't',
  '\u0168': 'U',
  '\u016a': 'U',
  '\u016c': 'U',
  '\u016e': 'U',
  '\u0170': 'U',
  '\u0172': 'U',
  '\u0169': 'u',
  '\u016b': 'u',
  '\u016d': 'u',
  '\u016f': 'u',
  '\u0171': 'u',
  '\u0173': 'u',
  '\u0174': 'W',
  '\u0175': 'w',
  '\u0176': 'Y',
  '\u0177': 'y',
  '\u0178': 'Y',
  '\u0179': 'Z',
  '\u017b': 'Z',
  '\u017d': 'Z',
  '\u017a': 'z',
  '\u017c': 'z',
  '\u017e': 'z',
  '\u0132': 'IJ',
  '\u0133': 'ij',
  '\u0152': 'Oe',
  '\u0153': 'oe',
  '\u0149': "'n",
  '\u017f': 's'
};
/**
 * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
 * letters to basic Latin letters.
 *
 * @private
 * @param {string} letter The matched letter to deburr.
 * @returns {string} Returns the deburred letter.
 */

var deburrLetter$1 = basePropertyOf(deburredLetters);
var _deburrLetter = deburrLetter$1;

/** Detect free variable `global` from Node.js. */

var freeGlobal$5 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal$2 = freeGlobal$5;

var freeGlobal$4 = _freeGlobal$2;
/** Detect free variable `self`. */


var freeSelf$2 = typeof self == 'object' && self && self.Object === Object && self;
/** Used as a reference to the global object. */

var root$k = freeGlobal$4 || freeSelf$2 || Function('return this')();
var _root$2 = root$k;

var root$j = _root$2;
/** Built-in value references. */


var Symbol$f = root$j.Symbol;
var _Symbol$2 = Symbol$f;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

function arrayMap$4(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }

  return result;
}

var _arrayMap$1 = arrayMap$4;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray$n = Array.isArray;
var isArray_1$1 = isArray$n;

var Symbol$e = _Symbol$2;
/** Used for built-in method references. */


var objectProto$s = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$m = objectProto$s.hasOwnProperty;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$5 = objectProto$s.toString;
/** Built-in value references. */

var symToStringTag$5 = Symbol$e ? Symbol$e.toStringTag : undefined;
/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */

function getRawTag$5(value) {
  var isOwn = hasOwnProperty$m.call(value, symToStringTag$5),
      tag = value[symToStringTag$5];

  try {
    value[symToStringTag$5] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$5.call(value);

  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$5] = tag;
    } else {
      delete value[symToStringTag$5];
    }
  }

  return result;
}

var _getRawTag$2 = getRawTag$5;

/** Used for built-in method references. */

var objectProto$r = Object.prototype;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$4 = objectProto$r.toString;
/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */

function objectToString$7(value) {
  return nativeObjectToString$4.call(value);
}

var _objectToString$2 = objectToString$7;

var Symbol$d = _Symbol$2,
    getRawTag$4 = _getRawTag$2,
    objectToString$6 = _objectToString$2;
/** `Object#toString` result references. */


var nullTag$2 = '[object Null]',
    undefinedTag$2 = '[object Undefined]';
/** Built-in value references. */

var symToStringTag$4 = Symbol$d ? Symbol$d.toStringTag : undefined;
/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

function baseGetTag$e(value) {
  if (value == null) {
    return value === undefined ? undefinedTag$2 : nullTag$2;
  }

  return symToStringTag$4 && symToStringTag$4 in Object(value) ? getRawTag$4(value) : objectToString$6(value);
}

var _baseGetTag$2 = baseGetTag$e;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$e(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1$2 = isObjectLike$e;

var baseGetTag$d = _baseGetTag$2,
    isObjectLike$d = isObjectLike_1$2;
/** `Object#toString` result references. */


var symbolTag$4 = '[object Symbol]';
/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */

function isSymbol$9(value) {
  return typeof value == 'symbol' || isObjectLike$d(value) && baseGetTag$d(value) == symbolTag$4;
}

var isSymbol_1$2 = isSymbol$9;

var Symbol$c = _Symbol$2,
    arrayMap$3 = _arrayMap$1,
    isArray$m = isArray_1$1,
    isSymbol$8 = isSymbol_1$2;
/** Used as references for various `Number` constants. */


var INFINITY$2 = 1 / 0;
/** Used to convert symbols to primitives and strings. */

var symbolProto$3 = Symbol$c ? Symbol$c.prototype : undefined,
    symbolToString$2 = symbolProto$3 ? symbolProto$3.toString : undefined;
/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */

function baseToString$3(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }

  if (isArray$m(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap$3(value, baseToString$3) + '';
  }

  if (isSymbol$8(value)) {
    return symbolToString$2 ? symbolToString$2.call(value) : '';
  }

  var result = value + '';
  return result == '0' && 1 / value == -INFINITY$2 ? '-0' : result;
}

var _baseToString$1 = baseToString$3;

var baseToString$2 = _baseToString$1;
/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */


function toString$8(value) {
  return value == null ? '' : baseToString$2(value);
}

var toString_1$1 = toString$8;

var deburrLetter = _deburrLetter,
    toString$7 = toString_1$1;
/** Used to match Latin Unicode letters (excluding mathematical operators). */


var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;
/** Used to compose unicode character classes. */

var rsComboMarksRange$3 = '\\u0300-\\u036f',
    reComboHalfMarksRange$3 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$3 = '\\u20d0-\\u20ff',
    rsComboRange$3 = rsComboMarksRange$3 + reComboHalfMarksRange$3 + rsComboSymbolsRange$3;
/** Used to compose unicode capture groups. */

var rsCombo$2 = '[' + rsComboRange$3 + ']';
/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */

var reComboMark = RegExp(rsCombo$2, 'g');
/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */

function deburr$1(string) {
  string = toString$7(string);
  return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
}

var deburr_1 = deburr$1;

/** Used to match words composed of alphanumeric characters. */

var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;
/**
 * Splits an ASCII `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */

function asciiWords$1(string) {
  return string.match(reAsciiWord) || [];
}

var _asciiWords = asciiWords$1;

/** Used to detect strings that need a more robust regexp to match words. */

var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;
/**
 * Checks if `string` contains a word composed of Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a word is found, else `false`.
 */

function hasUnicodeWord$1(string) {
  return reHasUnicodeWord.test(string);
}

var _hasUnicodeWord = hasUnicodeWord$1;

/** Used to compose unicode character classes. */

var rsAstralRange$2 = '\\ud800-\\udfff',
    rsComboMarksRange$2 = '\\u0300-\\u036f',
    reComboHalfMarksRange$2 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$2 = '\\u20d0-\\u20ff',
    rsComboRange$2 = rsComboMarksRange$2 + reComboHalfMarksRange$2 + rsComboSymbolsRange$2,
    rsDingbatRange = '\\u2700-\\u27bf',
    rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
    rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
    rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
    rsPunctuationRange = '\\u2000-\\u206f',
    rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
    rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
    rsVarRange$2 = '\\ufe0e\\ufe0f',
    rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;
/** Used to compose unicode capture groups. */

var rsApos$1 = "['\u2019]",
    rsBreak = '[' + rsBreakRange + ']',
    rsCombo$1 = '[' + rsComboRange$2 + ']',
    rsDigits = '\\d+',
    rsDingbat = '[' + rsDingbatRange + ']',
    rsLower = '[' + rsLowerRange + ']',
    rsMisc = '[^' + rsAstralRange$2 + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
    rsFitz$1 = '\\ud83c[\\udffb-\\udfff]',
    rsModifier$1 = '(?:' + rsCombo$1 + '|' + rsFitz$1 + ')',
    rsNonAstral$1 = '[^' + rsAstralRange$2 + ']',
    rsRegional$1 = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair$1 = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsUpper = '[' + rsUpperRange + ']',
    rsZWJ$2 = '\\u200d';
/** Used to compose unicode regexes. */

var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
    rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
    rsOptContrLower = '(?:' + rsApos$1 + '(?:d|ll|m|re|s|t|ve))?',
    rsOptContrUpper = '(?:' + rsApos$1 + '(?:D|LL|M|RE|S|T|VE))?',
    reOptMod$1 = rsModifier$1 + '?',
    rsOptVar$1 = '[' + rsVarRange$2 + ']?',
    rsOptJoin$1 = '(?:' + rsZWJ$2 + '(?:' + [rsNonAstral$1, rsRegional$1, rsSurrPair$1].join('|') + ')' + rsOptVar$1 + reOptMod$1 + ')*',
    rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
    rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
    rsSeq$1 = rsOptVar$1 + reOptMod$1 + rsOptJoin$1,
    rsEmoji = '(?:' + [rsDingbat, rsRegional$1, rsSurrPair$1].join('|') + ')' + rsSeq$1;
/** Used to match complex or compound words. */

var reUnicodeWord = RegExp([rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')', rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')', rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower, rsUpper + '+' + rsOptContrUpper, rsOrdUpper, rsOrdLower, rsDigits, rsEmoji].join('|'), 'g');
/**
 * Splits a Unicode `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */

function unicodeWords$1(string) {
  return string.match(reUnicodeWord) || [];
}

var _unicodeWords = unicodeWords$1;

var asciiWords = _asciiWords,
    hasUnicodeWord = _hasUnicodeWord,
    toString$6 = toString_1$1,
    unicodeWords = _unicodeWords;
/**
 * Splits `string` into an array of its words.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to inspect.
 * @param {RegExp|string} [pattern] The pattern to match words.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the words of `string`.
 * @example
 *
 * _.words('fred, barney, & pebbles');
 * // => ['fred', 'barney', 'pebbles']
 *
 * _.words('fred, barney, & pebbles', /[^, ]+/g);
 * // => ['fred', 'barney', '&', 'pebbles']
 */


function words$1(string, pattern, guard) {
  string = toString$6(string);
  pattern = guard ? undefined : pattern;

  if (pattern === undefined) {
    return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
  }

  return string.match(pattern) || [];
}

var words_1 = words$1;

var arrayReduce = _arrayReduce,
    deburr = deburr_1,
    words = words_1;
/** Used to compose unicode capture groups. */


var rsApos = "['\u2019]";
/** Used to match apostrophes. */

var reApos = RegExp(rsApos, 'g');
/**
 * Creates a function like `_.camelCase`.
 *
 * @private
 * @param {Function} callback The function to combine each word.
 * @returns {Function} Returns the new compounder function.
 */

function createCompounder$1(callback) {
  return function (string) {
    return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
  };
}

var _createCompounder = createCompounder$1;

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */

function baseSlice$1(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : length + start;
  }

  end = end > length ? length : end;

  if (end < 0) {
    end += length;
  }

  length = start > end ? 0 : end - start >>> 0;
  start >>>= 0;
  var result = Array(length);

  while (++index < length) {
    result[index] = array[index + start];
  }

  return result;
}

var _baseSlice = baseSlice$1;

var baseSlice = _baseSlice;
/**
 * Casts `array` to a slice if it's needed.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {number} start The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the cast slice.
 */


function castSlice$1(array, start, end) {
  var length = array.length;
  end = end === undefined ? length : end;
  return !start && end >= length ? array : baseSlice(array, start, end);
}

var _castSlice = castSlice$1;

/** Used to compose unicode character classes. */

var rsAstralRange$1 = '\\ud800-\\udfff',
    rsComboMarksRange$1 = '\\u0300-\\u036f',
    reComboHalfMarksRange$1 = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange$1 = '\\u20d0-\\u20ff',
    rsComboRange$1 = rsComboMarksRange$1 + reComboHalfMarksRange$1 + rsComboSymbolsRange$1,
    rsVarRange$1 = '\\ufe0e\\ufe0f';
/** Used to compose unicode capture groups. */

var rsZWJ$1 = '\\u200d';
/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */

var reHasUnicode = RegExp('[' + rsZWJ$1 + rsAstralRange$1 + rsComboRange$1 + rsVarRange$1 + ']');
/**
 * Checks if `string` contains Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
 */

function hasUnicode$2(string) {
  return reHasUnicode.test(string);
}

var _hasUnicode = hasUnicode$2;

/**
 * Converts an ASCII `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */

function asciiToArray$1(string) {
  return string.split('');
}

var _asciiToArray = asciiToArray$1;

/** Used to compose unicode character classes. */

var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange = '\\u0300-\\u036f',
    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
    rsComboSymbolsRange = '\\u20d0-\\u20ff',
    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
    rsVarRange = '\\ufe0e\\ufe0f';
/** Used to compose unicode capture groups. */

var rsAstral = '[' + rsAstralRange + ']',
    rsCombo = '[' + rsComboRange + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsZWJ = '\\u200d';
/** Used to compose unicode regexes. */

var reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange + ']?',
    rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';
/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */

var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');
/**
 * Converts a Unicode `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */

function unicodeToArray$1(string) {
  return string.match(reUnicode) || [];
}

var _unicodeToArray = unicodeToArray$1;

var asciiToArray = _asciiToArray,
    hasUnicode$1 = _hasUnicode,
    unicodeToArray = _unicodeToArray;
/**
 * Converts `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */


function stringToArray$1(string) {
  return hasUnicode$1(string) ? unicodeToArray(string) : asciiToArray(string);
}

var _stringToArray = stringToArray$1;

var castSlice = _castSlice,
    hasUnicode = _hasUnicode,
    stringToArray = _stringToArray,
    toString$5 = toString_1$1;
/**
 * Creates a function like `_.lowerFirst`.
 *
 * @private
 * @param {string} methodName The name of the `String` case method to use.
 * @returns {Function} Returns the new case function.
 */


function createCaseFirst$1(methodName) {
  return function (string) {
    string = toString$5(string);
    var strSymbols = hasUnicode(string) ? stringToArray(string) : undefined;
    var chr = strSymbols ? strSymbols[0] : string.charAt(0);
    var trailing = strSymbols ? castSlice(strSymbols, 1).join('') : string.slice(1);
    return chr[methodName]() + trailing;
  };
}

var _createCaseFirst = createCaseFirst$1;

var createCaseFirst = _createCaseFirst;
/**
 * Converts the first character of `string` to upper case.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.upperFirst('fred');
 * // => 'Fred'
 *
 * _.upperFirst('FRED');
 * // => 'FRED'
 */


var upperFirst$1 = createCaseFirst('toUpperCase');
var upperFirst_1 = upperFirst$1;

var createCompounder = _createCompounder,
    upperFirst = upperFirst_1;
/**
 * Converts `string` to
 * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
 *
 * @static
 * @memberOf _
 * @since 3.1.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the start cased string.
 * @example
 *
 * _.startCase('--foo-bar--');
 * // => 'Foo Bar'
 *
 * _.startCase('fooBar');
 * // => 'Foo Bar'
 *
 * _.startCase('__FOO_BAR__');
 * // => 'FOO BAR'
 */


var startCase = createCompounder(function (result, word, index) {
  return result + (index ? ' ' : '') + upperFirst(word);
});
var startCase_1 = startCase;

var includeConditionalArg$1 = {};

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */

function listCacheClear$3() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear$1 = listCacheClear$3;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */

function eq$6(value, other) {
  return value === other || value !== value && other !== other;
}

var eq_1$1 = eq$6;

var eq$5 = eq_1$1;
/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */


function assocIndexOf$9(array, key) {
  var length = array.length;

  while (length--) {
    if (eq$5(array[length][0], key)) {
      return length;
    }
  }

  return -1;
}

var _assocIndexOf$1 = assocIndexOf$9;

var assocIndexOf$8 = _assocIndexOf$1;
/** Used for built-in method references. */


var arrayProto$1 = Array.prototype;
/** Built-in value references. */

var splice$1 = arrayProto$1.splice;
/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function listCacheDelete$3(key) {
  var data = this.__data__,
      index = assocIndexOf$8(data, key);

  if (index < 0) {
    return false;
  }

  var lastIndex = data.length - 1;

  if (index == lastIndex) {
    data.pop();
  } else {
    splice$1.call(data, index, 1);
  }

  --this.size;
  return true;
}

var _listCacheDelete$1 = listCacheDelete$3;

var assocIndexOf$7 = _assocIndexOf$1;
/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */


function listCacheGet$3(key) {
  var data = this.__data__,
      index = assocIndexOf$7(data, key);
  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet$1 = listCacheGet$3;

var assocIndexOf$6 = _assocIndexOf$1;
/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */


function listCacheHas$3(key) {
  return assocIndexOf$6(this.__data__, key) > -1;
}

var _listCacheHas$1 = listCacheHas$3;

var assocIndexOf$5 = _assocIndexOf$1;
/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */


function listCacheSet$3(key, value) {
  var data = this.__data__,
      index = assocIndexOf$5(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }

  return this;
}

var _listCacheSet$1 = listCacheSet$3;

var listCacheClear$2 = _listCacheClear$1,
    listCacheDelete$2 = _listCacheDelete$1,
    listCacheGet$2 = _listCacheGet$1,
    listCacheHas$2 = _listCacheHas$1,
    listCacheSet$2 = _listCacheSet$1;
/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function ListCache$9(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `ListCache`.


ListCache$9.prototype.clear = listCacheClear$2;
ListCache$9.prototype['delete'] = listCacheDelete$2;
ListCache$9.prototype.get = listCacheGet$2;
ListCache$9.prototype.has = listCacheHas$2;
ListCache$9.prototype.set = listCacheSet$2;
var _ListCache$1 = ListCache$9;

var ListCache$8 = _ListCache$1;
/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */


function stackClear$3() {
  this.__data__ = new ListCache$8();
  this.size = 0;
}

var _stackClear$1 = stackClear$3;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function stackDelete$3(key) {
  var data = this.__data__,
      result = data['delete'](key);
  this.size = data.size;
  return result;
}

var _stackDelete$1 = stackDelete$3;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function stackGet$3(key) {
  return this.__data__.get(key);
}

var _stackGet$1 = stackGet$3;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function stackHas$3(key) {
  return this.__data__.has(key);
}

var _stackHas$1 = stackHas$3;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$u(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1$2 = isObject$u;

var baseGetTag$c = _baseGetTag$2,
    isObject$t = isObject_1$2;
/** `Object#toString` result references. */


var asyncTag$1 = '[object AsyncFunction]',
    funcTag$3 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    proxyTag$1 = '[object Proxy]';
/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */

function isFunction$5(value) {
  if (!isObject$t(value)) {
    return false;
  } // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.


  var tag = baseGetTag$c(value);
  return tag == funcTag$3 || tag == genTag$1 || tag == asyncTag$1 || tag == proxyTag$1;
}

var isFunction_1$1 = isFunction$5;

var root$i = _root$2;
/** Used to detect overreaching core-js shims. */


var coreJsData$3 = root$i['__core-js_shared__'];
var _coreJsData$1 = coreJsData$3;

var coreJsData$2 = _coreJsData$1;
/** Used to detect methods masquerading as native. */


var maskSrcKey$1 = function () {
  var uid = /[^.]+$/.exec(coreJsData$2 && coreJsData$2.keys && coreJsData$2.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();
/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */


function isMasked$3(func) {
  return !!maskSrcKey$1 && maskSrcKey$1 in func;
}

var _isMasked$1 = isMasked$3;

/** Used for built-in method references. */

var funcProto$4 = Function.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString$4 = funcProto$4.toString;
/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */

function toSource$5(func) {
  if (func != null) {
    try {
      return funcToString$4.call(func);
    } catch (e) {}

    try {
      return func + '';
    } catch (e) {}
  }

  return '';
}

var _toSource$1 = toSource$5;

var isFunction$4 = isFunction_1$1,
    isMasked$2 = _isMasked$1,
    isObject$s = isObject_1$2,
    toSource$4 = _toSource$1;
/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */


var reRegExpChar$1 = /[\\^$.*+?()[\]{}|]/g;
/** Used to detect host constructors (Safari). */

var reIsHostCtor$1 = /^\[object .+?Constructor\]$/;
/** Used for built-in method references. */

var funcProto$3 = Function.prototype,
    objectProto$q = Object.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString$3 = funcProto$3.toString;
/** Used to check objects for own properties. */

var hasOwnProperty$l = objectProto$q.hasOwnProperty;
/** Used to detect if a method is native. */

var reIsNative$1 = RegExp('^' + funcToString$3.call(hasOwnProperty$l).replace(reRegExpChar$1, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');
/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */

function baseIsNative$3(value) {
  if (!isObject$s(value) || isMasked$2(value)) {
    return false;
  }

  var pattern = isFunction$4(value) ? reIsNative$1 : reIsHostCtor$1;
  return pattern.test(toSource$4(value));
}

var _baseIsNative$1 = baseIsNative$3;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function getValue$3(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue$1 = getValue$3;

var baseIsNative$2 = _baseIsNative$1,
    getValue$2 = _getValue$1;
/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */


function getNative$e(object, key) {
  var value = getValue$2(object, key);
  return baseIsNative$2(value) ? value : undefined;
}

var _getNative$1 = getNative$e;

var getNative$d = _getNative$1,
    root$h = _root$2;
/* Built-in method references that are verified to be native. */


var Map$8 = getNative$d(root$h, 'Map');
var _Map$1 = Map$8;

var getNative$c = _getNative$1;
/* Built-in method references that are verified to be native. */


var nativeCreate$9 = getNative$c(Object, 'create');
var _nativeCreate$1 = nativeCreate$9;

var nativeCreate$8 = _nativeCreate$1;
/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */


function hashClear$3() {
  this.__data__ = nativeCreate$8 ? nativeCreate$8(null) : {};
  this.size = 0;
}

var _hashClear$1 = hashClear$3;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function hashDelete$3(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete$1 = hashDelete$3;

var nativeCreate$7 = _nativeCreate$1;
/** Used to stand-in for `undefined` hash values. */


var HASH_UNDEFINED$5 = '__lodash_hash_undefined__';
/** Used for built-in method references. */

var objectProto$p = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$k = objectProto$p.hasOwnProperty;
/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function hashGet$3(key) {
  var data = this.__data__;

  if (nativeCreate$7) {
    var result = data[key];
    return result === HASH_UNDEFINED$5 ? undefined : result;
  }

  return hasOwnProperty$k.call(data, key) ? data[key] : undefined;
}

var _hashGet$1 = hashGet$3;

var nativeCreate$6 = _nativeCreate$1;
/** Used for built-in method references. */


var objectProto$o = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$j = objectProto$o.hasOwnProperty;
/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function hashHas$3(key) {
  var data = this.__data__;
  return nativeCreate$6 ? data[key] !== undefined : hasOwnProperty$j.call(data, key);
}

var _hashHas$1 = hashHas$3;

var nativeCreate$5 = _nativeCreate$1;
/** Used to stand-in for `undefined` hash values. */


var HASH_UNDEFINED$4 = '__lodash_hash_undefined__';
/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */

function hashSet$3(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = nativeCreate$5 && value === undefined ? HASH_UNDEFINED$4 : value;
  return this;
}

var _hashSet$1 = hashSet$3;

var hashClear$2 = _hashClear$1,
    hashDelete$2 = _hashDelete$1,
    hashGet$2 = _hashGet$1,
    hashHas$2 = _hashHas$1,
    hashSet$2 = _hashSet$1;
/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function Hash$3(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `Hash`.


Hash$3.prototype.clear = hashClear$2;
Hash$3.prototype['delete'] = hashDelete$2;
Hash$3.prototype.get = hashGet$2;
Hash$3.prototype.has = hashHas$2;
Hash$3.prototype.set = hashSet$2;
var _Hash$1 = Hash$3;

var Hash$2 = _Hash$1,
    ListCache$7 = _ListCache$1,
    Map$7 = _Map$1;
/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */


function mapCacheClear$3() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash$2(),
    'map': new (Map$7 || ListCache$7)(),
    'string': new Hash$2()
  };
}

var _mapCacheClear$1 = mapCacheClear$3;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */

function isKeyable$3(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}

var _isKeyable$1 = isKeyable$3;

var isKeyable$2 = _isKeyable$1;
/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */


function getMapData$9(map, key) {
  var data = map.__data__;
  return isKeyable$2(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}

var _getMapData$1 = getMapData$9;

var getMapData$8 = _getMapData$1;
/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */


function mapCacheDelete$3(key) {
  var result = getMapData$8(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete$1 = mapCacheDelete$3;

var getMapData$7 = _getMapData$1;
/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */


function mapCacheGet$3(key) {
  return getMapData$7(this, key).get(key);
}

var _mapCacheGet$1 = mapCacheGet$3;

var getMapData$6 = _getMapData$1;
/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */


function mapCacheHas$3(key) {
  return getMapData$6(this, key).has(key);
}

var _mapCacheHas$1 = mapCacheHas$3;

var getMapData$5 = _getMapData$1;
/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */


function mapCacheSet$3(key, value) {
  var data = getMapData$5(this, key),
      size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet$1 = mapCacheSet$3;

var mapCacheClear$2 = _mapCacheClear$1,
    mapCacheDelete$2 = _mapCacheDelete$1,
    mapCacheGet$2 = _mapCacheGet$1,
    mapCacheHas$2 = _mapCacheHas$1,
    mapCacheSet$2 = _mapCacheSet$1;
/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function MapCache$6(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `MapCache`.


MapCache$6.prototype.clear = mapCacheClear$2;
MapCache$6.prototype['delete'] = mapCacheDelete$2;
MapCache$6.prototype.get = mapCacheGet$2;
MapCache$6.prototype.has = mapCacheHas$2;
MapCache$6.prototype.set = mapCacheSet$2;
var _MapCache$1 = MapCache$6;

var ListCache$6 = _ListCache$1,
    Map$6 = _Map$1,
    MapCache$5 = _MapCache$1;
/** Used as the size to enable large array optimizations. */


var LARGE_ARRAY_SIZE$1 = 200;
/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */

function stackSet$3(key, value) {
  var data = this.__data__;

  if (data instanceof ListCache$6) {
    var pairs = data.__data__;

    if (!Map$6 || pairs.length < LARGE_ARRAY_SIZE$1 - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }

    data = this.__data__ = new MapCache$5(pairs);
  }

  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet$1 = stackSet$3;

var ListCache$5 = _ListCache$1,
    stackClear$2 = _stackClear$1,
    stackDelete$2 = _stackDelete$1,
    stackGet$2 = _stackGet$1,
    stackHas$2 = _stackHas$1,
    stackSet$2 = _stackSet$1;
/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function Stack$4(entries) {
  var data = this.__data__ = new ListCache$5(entries);
  this.size = data.size;
} // Add methods to `Stack`.


Stack$4.prototype.clear = stackClear$2;
Stack$4.prototype['delete'] = stackDelete$2;
Stack$4.prototype.get = stackGet$2;
Stack$4.prototype.has = stackHas$2;
Stack$4.prototype.set = stackSet$2;
var _Stack$1 = Stack$4;

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED$3 = '__lodash_hash_undefined__';
/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */

function setCacheAdd$3(value) {
  this.__data__.set(value, HASH_UNDEFINED$3);

  return this;
}

var _setCacheAdd$1 = setCacheAdd$3;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */

function setCacheHas$3(value) {
  return this.__data__.has(value);
}

var _setCacheHas$1 = setCacheHas$3;

var MapCache$4 = _MapCache$1,
    setCacheAdd$2 = _setCacheAdd$1,
    setCacheHas$2 = _setCacheHas$1;
/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */


function SetCache$3(values) {
  var index = -1,
      length = values == null ? 0 : values.length;
  this.__data__ = new MapCache$4();

  while (++index < length) {
    this.add(values[index]);
  }
} // Add methods to `SetCache`.


SetCache$3.prototype.add = SetCache$3.prototype.push = setCacheAdd$2;
SetCache$3.prototype.has = setCacheHas$2;
var _SetCache$1 = SetCache$3;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */

function arraySome$3(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }

  return false;
}

var _arraySome$1 = arraySome$3;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function cacheHas$3(cache, key) {
  return cache.has(key);
}

var _cacheHas$1 = cacheHas$3;

var SetCache$2 = _SetCache$1,
    arraySome$2 = _arraySome$1,
    cacheHas$2 = _cacheHas$1;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$9 = 1,
    COMPARE_UNORDERED_FLAG$5 = 2;
/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */

function equalArrays$5(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$9,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  } // Assume cyclic values are equal.


  var stacked = stack.get(array);

  if (stacked && stack.get(other)) {
    return stacked == other;
  }

  var index = -1,
      result = true,
      seen = bitmask & COMPARE_UNORDERED_FLAG$5 ? new SetCache$2() : undefined;
  stack.set(array, other);
  stack.set(other, array); // Ignore non-index properties.

  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, arrValue, index, other, array, stack) : customizer(arrValue, othValue, index, array, other, stack);
    }

    if (compared !== undefined) {
      if (compared) {
        continue;
      }

      result = false;
      break;
    } // Recursively compare arrays (susceptible to call stack limits).


    if (seen) {
      if (!arraySome$2(other, function (othValue, othIndex) {
        if (!cacheHas$2(seen, othIndex) && (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
          return seen.push(othIndex);
        }
      })) {
        result = false;
        break;
      }
    } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
      result = false;
      break;
    }
  }

  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays$1 = equalArrays$5;

var root$g = _root$2;
/** Built-in value references. */


var Uint8Array$4 = root$g.Uint8Array;
var _Uint8Array$1 = Uint8Array$4;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */

function mapToArray$3(map) {
  var index = -1,
      result = Array(map.size);
  map.forEach(function (value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray$1 = mapToArray$3;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */

function setToArray$3(set) {
  var index = -1,
      result = Array(set.size);
  set.forEach(function (value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray$1 = setToArray$3;

var Symbol$b = _Symbol$2,
    Uint8Array$3 = _Uint8Array$1,
    eq$4 = eq_1$1,
    equalArrays$4 = _equalArrays$1,
    mapToArray$2 = _mapToArray$1,
    setToArray$2 = _setToArray$1;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$8 = 1,
    COMPARE_UNORDERED_FLAG$4 = 2;
/** `Object#toString` result references. */

var boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$3 = '[object Error]',
    mapTag$5 = '[object Map]',
    numberTag$3 = '[object Number]',
    regexpTag$3 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$3 = '[object String]',
    symbolTag$3 = '[object Symbol]';
var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$5 = '[object DataView]';
/** Used to convert symbols to primitives and strings. */

var symbolProto$2 = Symbol$b ? Symbol$b.prototype : undefined,
    symbolValueOf$1 = symbolProto$2 ? symbolProto$2.valueOf : undefined;
/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalByTag$3(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$5:
      if (object.byteLength != other.byteLength || object.byteOffset != other.byteOffset) {
        return false;
      }

      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag$3:
      if (object.byteLength != other.byteLength || !equalFunc(new Uint8Array$3(object), new Uint8Array$3(other))) {
        return false;
      }

      return true;

    case boolTag$3:
    case dateTag$3:
    case numberTag$3:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq$4(+object, +other);

    case errorTag$3:
      return object.name == other.name && object.message == other.message;

    case regexpTag$3:
    case stringTag$3:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == other + '';

    case mapTag$5:
      var convert = mapToArray$2;

    case setTag$5:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$8;
      convert || (convert = setToArray$2);

      if (object.size != other.size && !isPartial) {
        return false;
      } // Assume cyclic values are equal.


      var stacked = stack.get(object);

      if (stacked) {
        return stacked == other;
      }

      bitmask |= COMPARE_UNORDERED_FLAG$4; // Recursively compare objects (susceptible to call stack limits).

      stack.set(object, other);
      var result = equalArrays$4(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$3:
      if (symbolValueOf$1) {
        return symbolValueOf$1.call(object) == symbolValueOf$1.call(other);
      }

  }

  return false;
}

var _equalByTag$1 = equalByTag$3;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */

function arrayPush$6(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }

  return array;
}

var _arrayPush$1 = arrayPush$6;

var arrayPush$5 = _arrayPush$1,
    isArray$l = isArray_1$1;
/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */


function baseGetAllKeys$4(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$l(object) ? result : arrayPush$5(result, symbolsFunc(object));
}

var _baseGetAllKeys$1 = baseGetAllKeys$4;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */

function arrayFilter$3(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];

    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }

  return result;
}

var _arrayFilter$1 = arrayFilter$3;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */

function stubArray$4() {
  return [];
}

var stubArray_1$1 = stubArray$4;

var arrayFilter$2 = _arrayFilter$1,
    stubArray$3 = stubArray_1$1;
/** Used for built-in method references. */


var objectProto$n = Object.prototype;
/** Built-in value references. */

var propertyIsEnumerable$4 = objectProto$n.propertyIsEnumerable;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeGetSymbols$2 = Object.getOwnPropertySymbols;
/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */

var getSymbols$4 = !nativeGetSymbols$2 ? stubArray$3 : function (object) {
  if (object == null) {
    return [];
  }

  object = Object(object);
  return arrayFilter$2(nativeGetSymbols$2(object), function (symbol) {
    return propertyIsEnumerable$4.call(object, symbol);
  });
};
var _getSymbols$1 = getSymbols$4;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */

function baseTimes$3(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }

  return result;
}

var _baseTimes$1 = baseTimes$3;

var baseGetTag$b = _baseGetTag$2,
    isObjectLike$c = isObjectLike_1$2;
/** `Object#toString` result references. */


var argsTag$5 = '[object Arguments]';
/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */

function baseIsArguments$3(value) {
  return isObjectLike$c(value) && baseGetTag$b(value) == argsTag$5;
}

var _baseIsArguments$1 = baseIsArguments$3;

var baseIsArguments$2 = _baseIsArguments$1,
    isObjectLike$b = isObjectLike_1$2;
/** Used for built-in method references. */


var objectProto$m = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$i = objectProto$m.hasOwnProperty;
/** Built-in value references. */

var propertyIsEnumerable$3 = objectProto$m.propertyIsEnumerable;
/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */

var isArguments$5 = baseIsArguments$2(function () {
  return arguments;
}()) ? baseIsArguments$2 : function (value) {
  return isObjectLike$b(value) && hasOwnProperty$i.call(value, 'callee') && !propertyIsEnumerable$3.call(value, 'callee');
};
var isArguments_1$1 = isArguments$5;

var isBuffer$6 = {exports: {}};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */

function stubFalse$1() {
  return false;
}

var stubFalse_1$1 = stubFalse$1;

(function (module, exports) {
var root = _root$2,
    stubFalse = stubFalse_1$1;
/** Detect free variable `exports`. */


var freeExports = exports && !exports.nodeType && exports;
/** Detect free variable `module`. */

var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
/** Detect the popular CommonJS extension `module.exports`. */

var moduleExports = freeModule && freeModule.exports === freeExports;
/** Built-in value references. */

var Buffer = moduleExports ? root.Buffer : undefined;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;
/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */

var isBuffer = nativeIsBuffer || stubFalse;
module.exports = isBuffer;
}(isBuffer$6, isBuffer$6.exports));

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$4 = 9007199254740991;
/** Used to detect unsigned integer values. */

var reIsUint$1 = /^(?:0|[1-9]\d*)$/;
/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */

function isIndex$5(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$4 : length;
  return !!length && (type == 'number' || type != 'symbol' && reIsUint$1.test(value)) && value > -1 && value % 1 == 0 && value < length;
}

var _isIndex$1 = isIndex$5;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$3 = 9007199254740991;
/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */

function isLength$6(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$3;
}

var isLength_1$1 = isLength$6;

var baseGetTag$a = _baseGetTag$2,
    isLength$5 = isLength_1$1,
    isObjectLike$a = isObjectLike_1$2;
/** `Object#toString` result references. */


var argsTag$4 = '[object Arguments]',
    arrayTag$3 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$2 = '[object Error]',
    funcTag$2 = '[object Function]',
    mapTag$4 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$6 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    weakMapTag$3 = '[object WeakMap]';
var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';
/** Used to identify `toStringTag` values of typed arrays. */

var typedArrayTags$1 = {};
typedArrayTags$1[float32Tag$1] = typedArrayTags$1[float64Tag$1] = typedArrayTags$1[int8Tag$1] = typedArrayTags$1[int16Tag$1] = typedArrayTags$1[int32Tag$1] = typedArrayTags$1[uint8Tag$1] = typedArrayTags$1[uint8ClampedTag$1] = typedArrayTags$1[uint16Tag$1] = typedArrayTags$1[uint32Tag$1] = true;
typedArrayTags$1[argsTag$4] = typedArrayTags$1[arrayTag$3] = typedArrayTags$1[arrayBufferTag$2] = typedArrayTags$1[boolTag$2] = typedArrayTags$1[dataViewTag$4] = typedArrayTags$1[dateTag$2] = typedArrayTags$1[errorTag$2] = typedArrayTags$1[funcTag$2] = typedArrayTags$1[mapTag$4] = typedArrayTags$1[numberTag$2] = typedArrayTags$1[objectTag$6] = typedArrayTags$1[regexpTag$2] = typedArrayTags$1[setTag$4] = typedArrayTags$1[stringTag$2] = typedArrayTags$1[weakMapTag$3] = false;
/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */

function baseIsTypedArray$3(value) {
  return isObjectLike$a(value) && isLength$5(value.length) && !!typedArrayTags$1[baseGetTag$a(value)];
}

var _baseIsTypedArray$1 = baseIsTypedArray$3;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */

function baseUnary$3(func) {
  return function (value) {
    return func(value);
  };
}

var _baseUnary$1 = baseUnary$3;

var _nodeUtil$1 = {exports: {}};

(function (module, exports) {
var freeGlobal = _freeGlobal$2;
/** Detect free variable `exports`. */


var freeExports = exports && !exports.nodeType && exports;
/** Detect free variable `module`. */

var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
/** Detect the popular CommonJS extension `module.exports`. */

var moduleExports = freeModule && freeModule.exports === freeExports;
/** Detect free variable `process` from Node.js. */

var freeProcess = moduleExports && freeGlobal.process;
/** Used to access faster Node.js helpers. */

var nodeUtil = function () {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    } // Legacy `process.binding('util')` for Node.js < 10.


    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}();

module.exports = nodeUtil;
}(_nodeUtil$1, _nodeUtil$1.exports));

var baseIsTypedArray$2 = _baseIsTypedArray$1,
    baseUnary$2 = _baseUnary$1,
    nodeUtil$1 = _nodeUtil$1.exports;
/* Node.js helper references. */


var nodeIsTypedArray$1 = nodeUtil$1 && nodeUtil$1.isTypedArray;
/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */

var isTypedArray$5 = nodeIsTypedArray$1 ? baseUnary$2(nodeIsTypedArray$1) : baseIsTypedArray$2;
var isTypedArray_1$1 = isTypedArray$5;

var baseTimes$2 = _baseTimes$1,
    isArguments$4 = isArguments_1$1,
    isArray$k = isArray_1$1,
    isBuffer$5 = isBuffer$6.exports,
    isIndex$4 = _isIndex$1,
    isTypedArray$4 = isTypedArray_1$1;
/** Used for built-in method references. */


var objectProto$l = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$h = objectProto$l.hasOwnProperty;
/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */

function arrayLikeKeys$4(value, inherited) {
  var isArr = isArray$k(value),
      isArg = !isArr && isArguments$4(value),
      isBuff = !isArr && !isArg && isBuffer$5(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray$4(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes$2(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$h.call(value, key)) && !(skipIndexes && ( // Safari 9 has enumerable `arguments.length` in strict mode.
    key == 'length' || // Node.js 0.10 has enumerable non-index properties on buffers.
    isBuff && (key == 'offset' || key == 'parent') || // PhantomJS 2 has enumerable non-index properties on typed arrays.
    isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') || // Skip index properties.
    isIndex$4(key, length)))) {
      result.push(key);
    }
  }

  return result;
}

var _arrayLikeKeys$1 = arrayLikeKeys$4;

/** Used for built-in method references. */

var objectProto$k = Object.prototype;
/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */

function isPrototype$4(value) {
  var Ctor = value && value.constructor,
      proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$k;
  return value === proto;
}

var _isPrototype$1 = isPrototype$4;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */

function overArg$4(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}

var _overArg$1 = overArg$4;

var overArg$3 = _overArg$1;
/* Built-in method references for those with the same name as other `lodash` methods. */


var nativeKeys$4 = overArg$3(Object.keys, Object);
var _nativeKeys$1 = nativeKeys$4;

var isPrototype$3 = _isPrototype$1,
    nativeKeys$3 = _nativeKeys$1;
/** Used for built-in method references. */


var objectProto$j = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$g = objectProto$j.hasOwnProperty;
/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function baseKeys$3(object) {
  if (!isPrototype$3(object)) {
    return nativeKeys$3(object);
  }

  var result = [];

  for (var key in Object(object)) {
    if (hasOwnProperty$g.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }

  return result;
}

var _baseKeys$1 = baseKeys$3;

var isFunction$3 = isFunction_1$1,
    isLength$4 = isLength_1$1;
/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */


function isArrayLike$4(value) {
  return value != null && isLength$4(value.length) && !isFunction$3(value);
}

var isArrayLike_1$1 = isArrayLike$4;

var arrayLikeKeys$3 = _arrayLikeKeys$1,
    baseKeys$2 = _baseKeys$1,
    isArrayLike$3 = isArrayLike_1$1;
/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */


function keys$9(object) {
  return isArrayLike$3(object) ? arrayLikeKeys$3(object) : baseKeys$2(object);
}

var keys_1$1 = keys$9;

var baseGetAllKeys$3 = _baseGetAllKeys$1,
    getSymbols$3 = _getSymbols$1,
    keys$8 = keys_1$1;
/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */


function getAllKeys$3(object) {
  return baseGetAllKeys$3(object, keys$8, getSymbols$3);
}

var _getAllKeys$1 = getAllKeys$3;

var getAllKeys$2 = _getAllKeys$1;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$7 = 1;
/** Used for built-in method references. */

var objectProto$i = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$f = objectProto$i.hasOwnProperty;
/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalObjects$3(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$7,
      objProps = getAllKeys$2(object),
      objLength = objProps.length,
      othProps = getAllKeys$2(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }

  var index = objLength;

  while (index--) {
    var key = objProps[index];

    if (!(isPartial ? key in other : hasOwnProperty$f.call(other, key))) {
      return false;
    }
  } // Assume cyclic values are equal.


  var stacked = stack.get(object);

  if (stacked && stack.get(other)) {
    return stacked == other;
  }

  var result = true;
  stack.set(object, other);
  stack.set(other, object);
  var skipCtor = isPartial;

  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, objValue, key, other, object, stack) : customizer(objValue, othValue, key, object, other, stack);
    } // Recursively compare objects (susceptible to call stack limits).


    if (!(compared === undefined ? objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack) : compared)) {
      result = false;
      break;
    }

    skipCtor || (skipCtor = key == 'constructor');
  }

  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor; // Non `Object` object instances with different constructors are not equal.

    if (objCtor != othCtor && 'constructor' in object && 'constructor' in other && !(typeof objCtor == 'function' && objCtor instanceof objCtor && typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }

  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects$1 = equalObjects$3;

var getNative$b = _getNative$1,
    root$f = _root$2;
/* Built-in method references that are verified to be native. */


var DataView$4 = getNative$b(root$f, 'DataView');
var _DataView$1 = DataView$4;

var getNative$a = _getNative$1,
    root$e = _root$2;
/* Built-in method references that are verified to be native. */


var Promise$5 = getNative$a(root$e, 'Promise');
var _Promise$1 = Promise$5;

var getNative$9 = _getNative$1,
    root$d = _root$2;
/* Built-in method references that are verified to be native. */


var Set$4 = getNative$9(root$d, 'Set');
var _Set$1 = Set$4;

var getNative$8 = _getNative$1,
    root$c = _root$2;
/* Built-in method references that are verified to be native. */


var WeakMap$6 = getNative$8(root$c, 'WeakMap');
var _WeakMap$1 = WeakMap$6;

var DataView$3 = _DataView$1,
    Map$5 = _Map$1,
    Promise$4 = _Promise$1,
    Set$3 = _Set$1,
    WeakMap$5 = _WeakMap$1,
    baseGetTag$9 = _baseGetTag$2,
    toSource$3 = _toSource$1;
/** `Object#toString` result references. */


var mapTag$3 = '[object Map]',
    objectTag$5 = '[object Object]',
    promiseTag$1 = '[object Promise]',
    setTag$3 = '[object Set]',
    weakMapTag$2 = '[object WeakMap]';
var dataViewTag$3 = '[object DataView]';
/** Used to detect maps, sets, and weakmaps. */

var dataViewCtorString$1 = toSource$3(DataView$3),
    mapCtorString$1 = toSource$3(Map$5),
    promiseCtorString$1 = toSource$3(Promise$4),
    setCtorString$1 = toSource$3(Set$3),
    weakMapCtorString$1 = toSource$3(WeakMap$5);
/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

var getTag$3 = baseGetTag$9; // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.

if (DataView$3 && getTag$3(new DataView$3(new ArrayBuffer(1))) != dataViewTag$3 || Map$5 && getTag$3(new Map$5()) != mapTag$3 || Promise$4 && getTag$3(Promise$4.resolve()) != promiseTag$1 || Set$3 && getTag$3(new Set$3()) != setTag$3 || WeakMap$5 && getTag$3(new WeakMap$5()) != weakMapTag$2) {
  getTag$3 = function (value) {
    var result = baseGetTag$9(value),
        Ctor = result == objectTag$5 ? value.constructor : undefined,
        ctorString = Ctor ? toSource$3(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString$1:
          return dataViewTag$3;

        case mapCtorString$1:
          return mapTag$3;

        case promiseCtorString$1:
          return promiseTag$1;

        case setCtorString$1:
          return setTag$3;

        case weakMapCtorString$1:
          return weakMapTag$2;
      }
    }

    return result;
  };
}

var _getTag$1 = getTag$3;

var Stack$3 = _Stack$1,
    equalArrays$3 = _equalArrays$1,
    equalByTag$2 = _equalByTag$1,
    equalObjects$2 = _equalObjects$1,
    getTag$2 = _getTag$1,
    isArray$j = isArray_1$1,
    isBuffer$4 = isBuffer$6.exports,
    isTypedArray$3 = isTypedArray_1$1;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$6 = 1;
/** `Object#toString` result references. */

var argsTag$3 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    objectTag$4 = '[object Object]';
/** Used for built-in method references. */

var objectProto$h = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$e = objectProto$h.hasOwnProperty;
/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function baseIsEqualDeep$3(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$j(object),
      othIsArr = isArray$j(other),
      objTag = objIsArr ? arrayTag$2 : getTag$2(object),
      othTag = othIsArr ? arrayTag$2 : getTag$2(other);
  objTag = objTag == argsTag$3 ? objectTag$4 : objTag;
  othTag = othTag == argsTag$3 ? objectTag$4 : othTag;
  var objIsObj = objTag == objectTag$4,
      othIsObj = othTag == objectTag$4,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$4(object)) {
    if (!isBuffer$4(other)) {
      return false;
    }

    objIsArr = true;
    objIsObj = false;
  }

  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack$3());
    return objIsArr || isTypedArray$3(object) ? equalArrays$3(object, other, bitmask, customizer, equalFunc, stack) : equalByTag$2(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }

  if (!(bitmask & COMPARE_PARTIAL_FLAG$6)) {
    var objIsWrapped = objIsObj && hasOwnProperty$e.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$e.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;
      stack || (stack = new Stack$3());
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }

  if (!isSameTag) {
    return false;
  }

  stack || (stack = new Stack$3());
  return equalObjects$2(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep$1 = baseIsEqualDeep$3;

var baseIsEqualDeep$2 = _baseIsEqualDeep$1,
    isObjectLike$9 = isObjectLike_1$2;
/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */


function baseIsEqual$4(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }

  if (value == null || other == null || !isObjectLike$9(value) && !isObjectLike$9(other)) {
    return value !== value && other !== other;
  }

  return baseIsEqualDeep$2(value, other, bitmask, customizer, baseIsEqual$4, stack);
}

var _baseIsEqual$1 = baseIsEqual$4;

var baseIsEqual$3 = _baseIsEqual$1;
/**
 * Performs a deep comparison between two values to determine if they are
 * equivalent.
 *
 * **Note:** This method supports comparing arrays, array buffers, booleans,
 * date objects, error objects, maps, numbers, `Object` objects, regexes,
 * sets, strings, symbols, and typed arrays. `Object` objects are compared
 * by their own, not inherited, enumerable properties. Functions and DOM
 * nodes are compared by strict equality, i.e. `===`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.isEqual(object, other);
 * // => true
 *
 * object === other;
 * // => false
 */


function isEqual$1(value, other) {
  return baseIsEqual$3(value, other);
}

var isEqual_1 = isEqual$1;

Object.defineProperty(includeConditionalArg$1, "__esModule", {
  value: true
});
includeConditionalArg$1.includeConditionalArg = includeConditionalArg$1.testValue = void 0;

var _isEqual = _interopRequireDefault$1(isEqual_1);

function _interopRequireDefault$1(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

var count = function count(vals) {
  return vals.map(function (v) {
    return typeof v !== 'undefined';
  }).filter(Boolean).length;
};

var testValue = function testValue(cond, value) {
  var _ref = cond,
      exists = _ref.exists,
      eq = _ref.eq,
      neq = _ref.neq,
      truthy = _ref.truthy;

  if (count([exists, eq, neq, truthy]) > 1) {
    throw new Error("Invalid conditional test ".concat(JSON.stringify({
      exists: exists,
      eq: eq,
      neq: neq
    })));
  }

  if (typeof eq !== 'undefined') {
    return (0, _isEqual["default"])(value, eq);
  }

  if (typeof neq !== 'undefined') {
    return !(0, _isEqual["default"])(value, neq);
  }

  if (typeof exists !== 'undefined') {
    var valueExists = typeof value !== 'undefined';
    return exists ? valueExists : !valueExists;
  }

  var shouldBeTruthy = typeof truthy === 'undefined' ? true : truthy;
  return shouldBeTruthy ? !!value : !value;
};
/**
 * Helper function to include/exclude an arg based on the value of other other args
 * aka "conditional args"
 */


includeConditionalArg$1.testValue = testValue;

var includeConditionalArg = function includeConditionalArg(argType, args, globals) {
  if (!argType["if"]) return true;
  var _ref2 = argType["if"],
      arg = _ref2.arg,
      global = _ref2.global;

  if (count([arg, global]) !== 1) {
    throw new Error("Invalid conditional value ".concat(JSON.stringify({
      arg: arg,
      global: global
    })));
  }

  var value = arg ? args[arg] : globals[global];
  return testValue(argType["if"], value);
};

includeConditionalArg$1.includeConditionalArg = includeConditionalArg;

var story = {};

var SBType = /*#__PURE__*/Object.freeze({
	__proto__: null
});

var require$$0 = /*@__PURE__*/getAugmentedNamespace(SBType);

(function (exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});

var _SBType = require$$0;

Object.keys(_SBType).forEach(function (key) {
  if (key === "default" || key === "__esModule") return;
  Object.defineProperty(exports, key, {
    enumerable: true,
    get: function get() {
      return _SBType[key];
    }
  });
});
}(story));

(function (exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
var _exportNames = {
  sanitize: true,
  toId: true,
  storyNameFromExport: true,
  isExportStory: true,
  parseKind: true,
  includeConditionalArg: true
};
exports.isExportStory = isExportStory;
Object.defineProperty(exports, "includeConditionalArg", {
  enumerable: true,
  get: function get() {
    return _includeConditionalArg.includeConditionalArg;
  }
});
exports.parseKind = exports.storyNameFromExport = exports.toId = exports.sanitize = void 0;

var _startCase = _interopRequireDefault(startCase_1);

var _includeConditionalArg = includeConditionalArg$1;

var _story = story;

Object.keys(_story).forEach(function (key) {
  if (key === "default" || key === "__esModule") return;
  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
  Object.defineProperty(exports, key, {
    enumerable: true,
    get: function get() {
      return _story[key];
    }
  });
});

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * Remove punctuation and illegal characters from a story ID.
 *
 * See https://gist.github.com/davidjrice/9d2af51100e41c6c4b4a
 */


var sanitize = function sanitize(string) {
  return string.toLowerCase() // eslint-disable-next-line no-useless-escape
  .replace(/[ ’–—―′¿'`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '-').replace(/-+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
};

exports.sanitize = sanitize;

var sanitizeSafe = function sanitizeSafe(string, part) {
  var sanitized = sanitize(string);

  if (sanitized === '') {
    throw new Error("Invalid ".concat(part, " '").concat(string, "', must include alphanumeric characters"));
  }

  return sanitized;
};
/**
 * Generate a storybook ID from a component/kind and story name.
 */


var toId = function toId(kind, name) {
  return "".concat(sanitizeSafe(kind, 'kind')).concat(name ? "--".concat(sanitizeSafe(name, 'name')) : '');
};
/**
 * Transform a CSF named export into a readable story name
 */


exports.toId = toId;

var storyNameFromExport = function storyNameFromExport(key) {
  return (0, _startCase["default"])(key);
};

exports.storyNameFromExport = storyNameFromExport;

function matches(storyKey, arrayOrRegex) {
  if (Array.isArray(arrayOrRegex)) {
    return arrayOrRegex.includes(storyKey);
  }

  return storyKey.match(arrayOrRegex);
}
/**
 * Does a named export match CSF inclusion/exclusion options?
 */


function isExportStory(key, _ref) {
  var includeStories = _ref.includeStories,
      excludeStories = _ref.excludeStories;
  return (// https://babeljs.io/docs/en/babel-plugin-transform-modules-commonjs
    key !== '__esModule' && (!includeStories || matches(key, includeStories)) && (!excludeStories || !matches(key, excludeStories))
  );
}
/**
 * Parse out the component/kind name from a path, using the given separator config.
 */


var parseKind = function parseKind(kind, _ref2) {
  var rootSeparator = _ref2.rootSeparator,
      groupSeparator = _ref2.groupSeparator;

  var _kind$split = kind.split(rootSeparator, 2),
      _kind$split2 = _slicedToArray(_kind$split, 2),
      root = _kind$split2[0],
      remainder = _kind$split2[1];

  var groups = (remainder || kind).split(groupSeparator).filter(function (i) {
    return !!i;
  }); // when there's no remainder, it means the root wasn't found/split

  return {
    root: remainder ? root : null,
    groups: groups
  };
};

exports.parseKind = parseKind;
}(dist));

var runtime = {exports: {}};

/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

(function (module) {
var runtime = function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.

  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }

  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function (obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []); // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.

    generator._invoke = makeInvokeMethod(innerFn, self, context);
    return generator;
  }

  exports.wrap = wrap; // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.

  function tryCatch(fn, obj, arg) {
    try {
      return {
        type: "normal",
        arg: fn.call(obj, arg)
      };
    } catch (err) {
      return {
        type: "throw",
        arg: err
      };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed"; // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.

  var ContinueSentinel = {}; // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.

  function Generator() {}

  function GeneratorFunction() {}

  function GeneratorFunctionPrototype() {} // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.


  var IteratorPrototype = {};

  IteratorPrototype[iteratorSymbol] = function () {
    return this;
  };

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));

  if (NativeIteratorPrototype && NativeIteratorPrototype !== Op && hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = Gp.constructor = GeneratorFunctionPrototype;
  GeneratorFunctionPrototype.constructor = GeneratorFunction;
  GeneratorFunction.displayName = define(GeneratorFunctionPrototype, toStringTagSymbol, "GeneratorFunction"); // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.

  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function (method) {
      define(prototype, method, function (arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function (genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor ? ctor === GeneratorFunction || // For the native GeneratorFunction constructor, the best we can
    // do is to check its .name property.
    (ctor.displayName || ctor.name) === "GeneratorFunction" : false;
  };

  exports.mark = function (genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }

    genFun.prototype = Object.create(Gp);
    return genFun;
  }; // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.


  exports.awrap = function (arg) {
    return {
      __await: arg
    };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);

      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;

        if (value && typeof value === "object" && hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function (value) {
            invoke("next", value, resolve, reject);
          }, function (err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function (unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function (error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function (resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise = // If enqueue has been called before, then we want to wait until
      // all previous Promises have been resolved before calling invoke,
      // so that results are always delivered in the correct order. If
      // enqueue has not been called before, then it is important to
      // call invoke immediately, without waiting on a callback to fire,
      // so that the async generator function has the opportunity to do
      // any necessary setup in a predictable way. This predictability
      // is why the Promise constructor synchronously invokes its
      // executor callback, and why async functions synchronously
      // execute code before the first await. Since we implement simple
      // async functions in terms of async generators, it is especially
      // important to get this right, even though it requires care.
      previousPromise ? previousPromise.then(callInvokeWithMethodAndArg, // Avoid propagating failures to Promises returned by later
      // invocations of the iterator.
      callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
    } // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).


    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);

  AsyncIterator.prototype[asyncIteratorSymbol] = function () {
    return this;
  };

  exports.AsyncIterator = AsyncIterator; // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.

  exports.async = function (innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;
    var iter = new AsyncIterator(wrap(innerFn, outerFn, self, tryLocsList), PromiseImpl);
    return exports.isGeneratorFunction(outerFn) ? iter // If outerFn is a generator, return the full iterator.
    : iter.next().then(function (result) {
      return result.done ? result.value : iter.next();
    });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;
    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        } // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume


        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;

        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);

          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;
        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);
        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;
        var record = tryCatch(innerFn, self, context);

        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done ? GenStateCompleted : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };
        } else if (record.type === "throw") {
          state = GenStateCompleted; // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.

          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  } // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.


  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];

    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError("The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (!info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value; // Resume execution at the desired location (see delegateYield).

      context.next = delegate.nextLoc; // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.

      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }
    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    } // The delegate iterator is finished, so forget it and continue with
    // the outer generator.


    context.delegate = null;
    return ContinueSentinel;
  } // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.


  defineIteratorMethods(Gp);
  define(Gp, toStringTagSymbol, "Generator"); // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.

  Gp[iteratorSymbol] = function () {
    return this;
  };

  Gp.toString = function () {
    return "[object Generator]";
  };

  function pushTryEntry(locs) {
    var entry = {
      tryLoc: locs[0]
    };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{
      tryLoc: "root"
    }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function (object) {
    var keys = [];

    for (var key in object) {
      keys.push(key);
    }

    keys.reverse(); // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.

    return function next() {
      while (keys.length) {
        var key = keys.pop();

        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      } // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.


      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];

      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1,
            next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;
          return next;
        };

        return next.next = next;
      }
    } // Return an iterator with no values.


    return {
      next: doneResult
    };
  }

  exports.values = values;

  function doneResult() {
    return {
      value: undefined$1,
      done: true
    };
  }

  Context.prototype = {
    constructor: Context,
    reset: function (skipTempReset) {
      this.prev = 0;
      this.next = 0; // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.

      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;
      this.method = "next";
      this.arg = undefined$1;
      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" && hasOwn.call(this, name) && !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },
    stop: function () {
      this.done = true;
      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;

      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },
    dispatchException: function (exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;

      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !!caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }
          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }
          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }
          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },
    abrupt: function (type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc <= this.prev && hasOwn.call(entry, "finallyLoc") && this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry && (type === "break" || type === "continue") && finallyEntry.tryLoc <= arg && arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },
    complete: function (record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" || record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },
    finish: function (finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },
    "catch": function (tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;

          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }

          return thrown;
        }
      } // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.


      throw new Error("illegal catch attempt");
    },
    delegateYield: function (iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  }; // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.

  return exports;
}( // If this script is executing as a CommonJS module, use module.exports
// as the regeneratorRuntime namespace. Otherwise create a new empty
// object. Either way, the resulting object will be used to initialize
// the regeneratorRuntime variable at the top of this file.
module.exports );

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  Function("r", "regeneratorRuntime = r")(runtime);
}
}(runtime));

var check = function (it) {
  return it && it.Math == Math && it;
}; // https://github.com/zloirock/core-js/issues/86#issuecomment-115759028


var global$q = // eslint-disable-next-line es/no-global-this -- safe
check(typeof globalThis == 'object' && globalThis) || check(typeof window == 'object' && window) || // eslint-disable-next-line no-restricted-globals -- safe
check(typeof self == 'object' && self) || check(typeof commonjsGlobal == 'object' && commonjsGlobal) || // eslint-disable-next-line no-new-func -- fallback
function () {
  return this;
}() || Function('return this')();

var objectGetOwnPropertyDescriptor = {};

var fails$q = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$p = fails$q; // Detect IE8's incomplete defineProperty implementation


var descriptors = !fails$p(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, {
    get: function () {
      return 7;
    }
  })[1] != 7;
});

var objectPropertyIsEnumerable = {};

var $propertyIsEnumerable$1 = {}.propertyIsEnumerable; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe

var getOwnPropertyDescriptor$4 = Object.getOwnPropertyDescriptor; // Nashorn ~ JDK8 bug

var NASHORN_BUG = getOwnPropertyDescriptor$4 && !$propertyIsEnumerable$1.call({
  1: 2
}, 1); // `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable

objectPropertyIsEnumerable.f = NASHORN_BUG ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$4(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable$1;

var createPropertyDescriptor$5 = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var toString$4 = {}.toString;

var classofRaw$1 = function (it) {
  return toString$4.call(it).slice(8, -1);
};

var fails$o = fails$q;

var classof$a = classofRaw$1;

var split = ''.split; // fallback for non-array-like ES3 and non-enumerable old V8 strings

var indexedObject = fails$o(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$a(it) == 'String' ? split.call(it, '') : Object(it);
} : Object;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$a = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$3 = indexedObject;

var requireObjectCoercible$9 = requireObjectCoercible$a;

var toIndexedObject$9 = function (it) {
  return IndexedObject$3(requireObjectCoercible$9(it));
};

var isObject$r = function (it) {
  return typeof it === 'object' ? it !== null : typeof it === 'function';
};

var isObject$q = isObject$r; // `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
// instead of the ES6 spec version, we didn't implement @@toPrimitive case
// and the second argument - flag - preferred type is a string


var toPrimitive$5 = function (input, PREFERRED_STRING) {
  if (!isObject$q(input)) return input;
  var fn, val;
  if (PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$q(val = fn.call(input))) return val;
  if (typeof (fn = input.valueOf) == 'function' && !isObject$q(val = fn.call(input))) return val;
  if (!PREFERRED_STRING && typeof (fn = input.toString) == 'function' && !isObject$q(val = fn.call(input))) return val;
  throw TypeError("Can't convert object to primitive value");
};

var requireObjectCoercible$8 = requireObjectCoercible$a; // `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject


var toObject$b = function (argument) {
  return Object(requireObjectCoercible$8(argument));
};

var toObject$a = toObject$b;

var hasOwnProperty$d = {}.hasOwnProperty;

var has$i = function hasOwn(it, key) {
  return hasOwnProperty$d.call(toObject$a(it), key);
};

var global$p = global$q;

var isObject$p = isObject$r;

var document$4 = global$p.document; // typeof document.createElement is 'object' in old IE

var EXISTS = isObject$p(document$4) && isObject$p(document$4.createElement);

var documentCreateElement$1 = function (it) {
  return EXISTS ? document$4.createElement(it) : {};
};

var DESCRIPTORS$e = descriptors;

var fails$n = fails$q;

var createElement$1 = documentCreateElement$1; // Thank's IE8 for his funny defineProperty


var ie8DomDefine = !DESCRIPTORS$e && !fails$n(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement$1('div'), 'a', {
    get: function () {
      return 7;
    }
  }).a != 7;
});

var DESCRIPTORS$d = descriptors;

var propertyIsEnumerableModule$2 = objectPropertyIsEnumerable;

var createPropertyDescriptor$4 = createPropertyDescriptor$5;

var toIndexedObject$8 = toIndexedObject$9;

var toPrimitive$4 = toPrimitive$5;

var has$h = has$i;

var IE8_DOM_DEFINE$1 = ie8DomDefine; // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe


var $getOwnPropertyDescriptor$1 = Object.getOwnPropertyDescriptor; // `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor

objectGetOwnPropertyDescriptor.f = DESCRIPTORS$d ? $getOwnPropertyDescriptor$1 : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$8(O);
  P = toPrimitive$4(P, true);
  if (IE8_DOM_DEFINE$1) try {
    return $getOwnPropertyDescriptor$1(O, P);
  } catch (error) {
    /* empty */
  }
  if (has$h(O, P)) return createPropertyDescriptor$4(!propertyIsEnumerableModule$2.f.call(O, P), O[P]);
};

var objectDefineProperty = {};

var isObject$o = isObject$r;

var anObject$h = function (it) {
  if (!isObject$o(it)) {
    throw TypeError(String(it) + ' is not an object');
  }

  return it;
};

var DESCRIPTORS$c = descriptors;

var IE8_DOM_DEFINE = ie8DomDefine;

var anObject$g = anObject$h;

var toPrimitive$3 = toPrimitive$5; // eslint-disable-next-line es/no-object-defineproperty -- safe


var $defineProperty$1 = Object.defineProperty; // `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty

objectDefineProperty.f = DESCRIPTORS$c ? $defineProperty$1 : function defineProperty(O, P, Attributes) {
  anObject$g(O);
  P = toPrimitive$3(P, true);
  anObject$g(Attributes);
  if (IE8_DOM_DEFINE) try {
    return $defineProperty$1(O, P, Attributes);
  } catch (error) {
    /* empty */
  }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var DESCRIPTORS$b = descriptors;

var definePropertyModule$6 = objectDefineProperty;

var createPropertyDescriptor$3 = createPropertyDescriptor$5;

var createNonEnumerableProperty$b = DESCRIPTORS$b ? function (object, key, value) {
  return definePropertyModule$6.f(object, key, createPropertyDescriptor$3(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var redefine$d = {exports: {}};

var global$o = global$q;

var createNonEnumerableProperty$a = createNonEnumerableProperty$b;

var setGlobal$3 = function (key, value) {
  try {
    createNonEnumerableProperty$a(global$o, key, value);
  } catch (error) {
    global$o[key] = value;
  }

  return value;
};

var global$n = global$q;

var setGlobal$2 = setGlobal$3;

var SHARED = '__core-js_shared__';
var store$3 = global$n[SHARED] || setGlobal$2(SHARED, {});
var sharedStore = store$3;

var store$2 = sharedStore;

var functionToString$1 = Function.toString; // this helper broken in `3.4.1-3.4.4`, so we can't use `shared` helper

if (typeof store$2.inspectSource != 'function') {
  store$2.inspectSource = function (it) {
    return functionToString$1.call(it);
  };
}

var inspectSource$3 = store$2.inspectSource;

var global$m = global$q;

var inspectSource$2 = inspectSource$3;

var WeakMap$4 = global$m.WeakMap;
var nativeWeakMap = typeof WeakMap$4 === 'function' && /native code/.test(inspectSource$2(WeakMap$4));

var shared$5 = {exports: {}};

var store$1 = sharedStore;

(shared$5.exports = function (key, value) {
  return store$1[key] || (store$1[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.12.1',
  mode: 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});

var id$1 = 0;
var postfix = Math.random();

var uid$4 = function (key) {
  return 'Symbol(' + String(key === undefined ? '' : key) + ')_' + (++id$1 + postfix).toString(36);
};

var shared$4 = shared$5.exports;

var uid$3 = uid$4;

var keys$7 = shared$4('keys');

var sharedKey$4 = function (key) {
  return keys$7[key] || (keys$7[key] = uid$3(key));
};

var hiddenKeys$6 = {};

var NATIVE_WEAK_MAP = nativeWeakMap;

var global$l = global$q;

var isObject$n = isObject$r;

var createNonEnumerableProperty$9 = createNonEnumerableProperty$b;

var objectHas = has$i;

var shared$3 = sharedStore;

var sharedKey$3 = sharedKey$4;

var hiddenKeys$5 = hiddenKeys$6;

var OBJECT_ALREADY_INITIALIZED = 'Object already initialized';
var WeakMap$3 = global$l.WeakMap;
var set$1, get$2, has$g;

var enforce = function (it) {
  return has$g(it) ? get$2(it) : set$1(it, {});
};

var getterFor = function (TYPE) {
  return function (it) {
    var state;

    if (!isObject$n(it) || (state = get$2(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    }

    return state;
  };
};

if (NATIVE_WEAK_MAP || shared$3.state) {
  var store = shared$3.state || (shared$3.state = new WeakMap$3());
  var wmget = store.get;
  var wmhas = store.has;
  var wmset = store.set;

  set$1 = function (it, metadata) {
    if (wmhas.call(store, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    wmset.call(store, it, metadata);
    return metadata;
  };

  get$2 = function (it) {
    return wmget.call(store, it) || {};
  };

  has$g = function (it) {
    return wmhas.call(store, it);
  };
} else {
  var STATE = sharedKey$3('state');
  hiddenKeys$5[STATE] = true;

  set$1 = function (it, metadata) {
    if (objectHas(it, STATE)) throw new TypeError(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    createNonEnumerableProperty$9(it, STATE, metadata);
    return metadata;
  };

  get$2 = function (it) {
    return objectHas(it, STATE) ? it[STATE] : {};
  };

  has$g = function (it) {
    return objectHas(it, STATE);
  };
}

var internalState = {
  set: set$1,
  get: get$2,
  has: has$g,
  enforce: enforce,
  getterFor: getterFor
};

var global$k = global$q;

var createNonEnumerableProperty$8 = createNonEnumerableProperty$b;

var has$f = has$i;

var setGlobal$1 = setGlobal$3;

var inspectSource$1 = inspectSource$3;

var InternalStateModule$5 = internalState;

var getInternalState$4 = InternalStateModule$5.get;
var enforceInternalState$2 = InternalStateModule$5.enforce;
var TEMPLATE = String(String).split('String');
(redefine$d.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var state;

  if (typeof value == 'function') {
    if (typeof key == 'string' && !has$f(value, 'name')) {
      createNonEnumerableProperty$8(value, 'name', key);
    }

    state = enforceInternalState$2(value);

    if (!state.source) {
      state.source = TEMPLATE.join(typeof key == 'string' ? key : '');
    }
  }

  if (O === global$k) {
    if (simple) O[key] = value;else setGlobal$1(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }

  if (simple) O[key] = value;else createNonEnumerableProperty$8(O, key, value); // add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return typeof this == 'function' && getInternalState$4(this).source || inspectSource$1(this);
});

var global$j = global$q;

var path$2 = global$j;

var path$1 = path$2;

var global$i = global$q;

var aFunction$6 = function (variable) {
  return typeof variable == 'function' ? variable : undefined;
};

var getBuiltIn$6 = function (namespace, method) {
  return arguments.length < 2 ? aFunction$6(path$1[namespace]) || aFunction$6(global$i[namespace]) : path$1[namespace] && path$1[namespace][method] || global$i[namespace] && global$i[namespace][method];
};

var objectGetOwnPropertyNames = {};

var ceil = Math.ceil;
var floor$1 = Math.floor; // `ToInteger` abstract operation
// https://tc39.es/ecma262/#sec-tointeger

var toInteger$4 = function (argument) {
  return isNaN(argument = +argument) ? 0 : (argument > 0 ? floor$1 : ceil)(argument);
};

var toInteger$3 = toInteger$4;

var min$4 = Math.min; // `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength

var toLength$b = function (argument) {
  return argument > 0 ? min$4(toInteger$3(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toInteger$2 = toInteger$4;

var max$2 = Math.max;
var min$3 = Math.min; // Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).

var toAbsoluteIndex$3 = function (index, length) {
  var integer = toInteger$2(index);
  return integer < 0 ? max$2(integer + length, 0) : min$3(integer, length);
};

var toIndexedObject$7 = toIndexedObject$9;

var toLength$a = toLength$b;

var toAbsoluteIndex$2 = toAbsoluteIndex$3; // `Array.prototype.{ indexOf, includes }` methods implementation


var createMethod$4 = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$7($this);
    var length = toLength$a(O.length);
    var index = toAbsoluteIndex$2(fromIndex, length);
    var value; // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check

    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++]; // eslint-disable-next-line no-self-compare -- NaN check

      if (value != value) return true; // Array#indexOf ignores holes, Array#includes - not
    } else for (; length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    }
    return !IS_INCLUDES && -1;
  };
};

var arrayIncludes = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$4(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$4(false)
};

var has$e = has$i;

var toIndexedObject$6 = toIndexedObject$9;

var indexOf$1 = arrayIncludes.indexOf;

var hiddenKeys$4 = hiddenKeys$6;

var objectKeysInternal = function (object, names) {
  var O = toIndexedObject$6(object);
  var i = 0;
  var result = [];
  var key;

  for (key in O) !has$e(hiddenKeys$4, key) && has$e(O, key) && result.push(key); // Don't enum bug & hidden keys


  while (names.length > i) if (has$e(O, key = names[i++])) {
    ~indexOf$1(result, key) || result.push(key);
  }

  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$3 = ['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toLocaleString', 'toString', 'valueOf'];

var internalObjectKeys$1 = objectKeysInternal;

var enumBugKeys$2 = enumBugKeys$3;

var hiddenKeys$3 = enumBugKeys$2.concat('length', 'prototype'); // `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe

objectGetOwnPropertyNames.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys$1(O, hiddenKeys$3);
};

var objectGetOwnPropertySymbols = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols.f = Object.getOwnPropertySymbols;

var getBuiltIn$5 = getBuiltIn$6;

var getOwnPropertyNamesModule$1 = objectGetOwnPropertyNames;

var getOwnPropertySymbolsModule$2 = objectGetOwnPropertySymbols;

var anObject$f = anObject$h; // all object keys, includes non-enumerable and symbols


var ownKeys$1 = getBuiltIn$5('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule$1.f(anObject$f(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$2.f;
  return getOwnPropertySymbols ? keys.concat(getOwnPropertySymbols(it)) : keys;
};

var has$d = has$i;

var ownKeys = ownKeys$1;

var getOwnPropertyDescriptorModule$1 = objectGetOwnPropertyDescriptor;

var definePropertyModule$5 = objectDefineProperty;

var copyConstructorProperties$2 = function (target, source) {
  var keys = ownKeys(source);
  var defineProperty = definePropertyModule$5.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule$1.f;

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!has$d(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};

var fails$m = fails$q;

var replacement = /#|\.prototype\./;

var isForced$6 = function (feature, detection) {
  var value = data[normalize(feature)];
  return value == POLYFILL ? true : value == NATIVE ? false : typeof detection == 'function' ? fails$m(detection) : !!detection;
};

var normalize = isForced$6.normalize = function (string) {
  return String(string).replace(replacement, '.').toLowerCase();
};

var data = isForced$6.data = {};
var NATIVE = isForced$6.NATIVE = 'N';
var POLYFILL = isForced$6.POLYFILL = 'P';
var isForced_1 = isForced$6;

var global$h = global$q;

var getOwnPropertyDescriptor$3 = objectGetOwnPropertyDescriptor.f;

var createNonEnumerableProperty$7 = createNonEnumerableProperty$b;

var redefine$c = redefine$d.exports;

var setGlobal = setGlobal$3;

var copyConstructorProperties$1 = copyConstructorProperties$2;

var isForced$5 = isForced_1;
/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
*/


var _export = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;

  if (GLOBAL) {
    target = global$h;
  } else if (STATIC) {
    target = global$h[TARGET] || setGlobal(TARGET, {});
  } else {
    target = (global$h[TARGET] || {}).prototype;
  }

  if (target) for (key in source) {
    sourceProperty = source[key];

    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor$3(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];

    FORCED = isForced$5(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced); // contained in target

    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty === typeof targetProperty) continue;
      copyConstructorProperties$1(sourceProperty, targetProperty);
    } // add a flag to not completely full polyfills


    if (options.sham || targetProperty && targetProperty.sham) {
      createNonEnumerableProperty$7(sourceProperty, 'sham', true);
    } // extend global


    redefine$c(target, key, sourceProperty, options);
  }
};

var internalObjectKeys = objectKeysInternal;

var enumBugKeys$1 = enumBugKeys$3; // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe


var objectKeys$4 = Object.keys || function keys(O) {
  return internalObjectKeys(O, enumBugKeys$1);
};

var DESCRIPTORS$a = descriptors;

var fails$l = fails$q;

var objectKeys$3 = objectKeys$4;

var getOwnPropertySymbolsModule$1 = objectGetOwnPropertySymbols;

var propertyIsEnumerableModule$1 = objectPropertyIsEnumerable;

var toObject$9 = toObject$b;

var IndexedObject$2 = indexedObject; // eslint-disable-next-line es/no-object-assign -- safe


var $assign = Object.assign; // eslint-disable-next-line es/no-object-defineproperty -- required for testing

var defineProperty$c = Object.defineProperty; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign

var objectAssign = !$assign || fails$l(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS$a && $assign({
    b: 1
  }, $assign(defineProperty$c({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty$c(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), {
    b: 2
  })).b !== 1) return true; // should work with symbols and should have deterministic property order (V8 bug)

  var A = {};
  var B = {}; // eslint-disable-next-line es/no-symbol -- safe

  var symbol = Symbol();
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) {
    B[chr] = chr;
  });
  return $assign({}, A)[symbol] != 7 || objectKeys$3($assign({}, B)).join('') != alphabet;
}) ? function assign(target, source) {
  // eslint-disable-line no-unused-vars -- required for `.length`
  var T = toObject$9(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$1.f;
  var propertyIsEnumerable = propertyIsEnumerableModule$1.f;

  while (argumentsLength > index) {
    var S = IndexedObject$2(arguments[index++]);
    var keys = getOwnPropertySymbols ? objectKeys$3(S).concat(getOwnPropertySymbols(S)) : objectKeys$3(S);
    var length = keys.length;
    var j = 0;
    var key;

    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS$a || propertyIsEnumerable.call(S, key)) T[key] = S[key];
    }
  }

  return T;
} : $assign;

var $$q = _export;

var assign$1 = objectAssign; // `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
// eslint-disable-next-line es/no-object-assign -- required for testing


$$q({
  target: 'Object',
  stat: true,
  forced: Object.assign !== assign$1
}, {
  assign: assign$1
});

var DESCRIPTORS$9 = descriptors;

var defineProperty$b = objectDefineProperty.f;

var FunctionPrototype = Function.prototype;
var FunctionPrototypeToString = FunctionPrototype.toString;
var nameRE = /^\s*function ([^ (]*)/;
var NAME = 'name'; // Function instances `.name` property
// https://tc39.es/ecma262/#sec-function-instances-name

if (DESCRIPTORS$9 && !(NAME in FunctionPrototype)) {
  defineProperty$b(FunctionPrototype, NAME, {
    configurable: true,
    get: function () {
      try {
        return FunctionPrototypeToString.call(this).match(nameRE)[1];
      } catch (error) {
        return '';
      }
    }
  });
}

var getBuiltIn$4 = getBuiltIn$6;

var engineUserAgent = getBuiltIn$4('navigator', 'userAgent') || '';

var global$g = global$q;

var userAgent$2 = engineUserAgent;

var process$4 = global$g.process;
var versions = process$4 && process$4.versions;
var v8 = versions && versions.v8;
var match$1, version;

if (v8) {
  match$1 = v8.split('.');
  version = match$1[0] < 4 ? 1 : match$1[0] + match$1[1];
} else if (userAgent$2) {
  match$1 = userAgent$2.match(/Edge\/(\d+)/);

  if (!match$1 || match$1[1] >= 74) {
    match$1 = userAgent$2.match(/Chrome\/(\d+)/);
    if (match$1) version = match$1[1];
  }
}

var engineV8Version = version && +version;

/* eslint-disable es/no-symbol -- required for testing */

var V8_VERSION$3 = engineV8Version;

var fails$k = fails$q; // eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing


var nativeSymbol = !!Object.getOwnPropertySymbols && !fails$k(function () {
  return !String(Symbol()) || // Chrome 38 Symbol has incorrect toString conversion
  // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
  !Symbol.sham && V8_VERSION$3 && V8_VERSION$3 < 41;
});

/* eslint-disable es/no-symbol -- required for testing */

var NATIVE_SYMBOL$2 = nativeSymbol;

var useSymbolAsUid = NATIVE_SYMBOL$2 && !Symbol.sham && typeof Symbol.iterator == 'symbol';

var global$f = global$q;

var shared$2 = shared$5.exports;

var has$c = has$i;

var uid$2 = uid$4;

var NATIVE_SYMBOL$1 = nativeSymbol;

var USE_SYMBOL_AS_UID$1 = useSymbolAsUid;

var WellKnownSymbolsStore$1 = shared$2('wks');
var Symbol$a = global$f.Symbol;
var createWellKnownSymbol = USE_SYMBOL_AS_UID$1 ? Symbol$a : Symbol$a && Symbol$a.withoutSetter || uid$2;

var wellKnownSymbol$q = function (name) {
  if (!has$c(WellKnownSymbolsStore$1, name) || !(NATIVE_SYMBOL$1 || typeof WellKnownSymbolsStore$1[name] == 'string')) {
    if (NATIVE_SYMBOL$1 && has$c(Symbol$a, name)) {
      WellKnownSymbolsStore$1[name] = Symbol$a[name];
    } else {
      WellKnownSymbolsStore$1[name] = createWellKnownSymbol('Symbol.' + name);
    }
  }

  return WellKnownSymbolsStore$1[name];
};

var wellKnownSymbol$p = wellKnownSymbol$q;

var TO_STRING_TAG$3 = wellKnownSymbol$p('toStringTag');
var test$1 = {};
test$1[TO_STRING_TAG$3] = 'z';
var toStringTagSupport = String(test$1) === '[object z]';

var TO_STRING_TAG_SUPPORT$2 = toStringTagSupport;

var classofRaw = classofRaw$1;

var wellKnownSymbol$o = wellKnownSymbol$q;

var TO_STRING_TAG$2 = wellKnownSymbol$o('toStringTag'); // ES3 wrong here

var CORRECT_ARGUMENTS = classofRaw(function () {
  return arguments;
}()) == 'Arguments'; // fallback for IE11 Script Access Denied error

var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (error) {
    /* empty */
  }
}; // getting tag from ES6+ `Object.prototype.toString`


var classof$9 = TO_STRING_TAG_SUPPORT$2 ? classofRaw : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null' // @@toStringTag case
  : typeof (tag = tryGet(O = Object(it), TO_STRING_TAG$2)) == 'string' ? tag // builtinTag case
  : CORRECT_ARGUMENTS ? classofRaw(O) // ES3 arguments fallback
  : (result = classofRaw(O)) == 'Object' && typeof O.callee == 'function' ? 'Arguments' : result;
};

var TO_STRING_TAG_SUPPORT$1 = toStringTagSupport;

var classof$8 = classof$9; // `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring


var objectToString$5 = TO_STRING_TAG_SUPPORT$1 ? {}.toString : function toString() {
  return '[object ' + classof$8(this) + ']';
};

var TO_STRING_TAG_SUPPORT = toStringTagSupport;

var redefine$b = redefine$d.exports;

var toString$3 = objectToString$5; // `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring


if (!TO_STRING_TAG_SUPPORT) {
  redefine$b(Object.prototype, 'toString', toString$3, {
    unsafe: true
  });
}

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

var aFunction$5 = function (it) {
  if (typeof it != 'function') {
    throw TypeError(String(it) + ' is not a function');
  }

  return it;
};

var aFunction$4 = aFunction$5; // optional / simple context binding


var functionBindContext = function (fn, that, length) {
  aFunction$4(fn);
  if (that === undefined) return fn;

  switch (length) {
    case 0:
      return function () {
        return fn.call(that);
      };

    case 1:
      return function (a) {
        return fn.call(that, a);
      };

    case 2:
      return function (a, b) {
        return fn.call(that, a, b);
      };

    case 3:
      return function (a, b, c) {
        return fn.call(that, a, b, c);
      };
  }

  return function ()
  /* ...args */
  {
    return fn.apply(that, arguments);
  };
};

var classof$7 = classofRaw$1; // `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe


var isArray$i = Array.isArray || function isArray(arg) {
  return classof$7(arg) == 'Array';
};

var isObject$m = isObject$r;

var isArray$h = isArray$i;

var wellKnownSymbol$n = wellKnownSymbol$q;

var SPECIES$7 = wellKnownSymbol$n('species'); // `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate

var arraySpeciesCreate$2 = function (originalArray, length) {
  var C;

  if (isArray$h(originalArray)) {
    C = originalArray.constructor; // cross-realm fallback

    if (typeof C == 'function' && (C === Array || isArray$h(C.prototype))) C = undefined;else if (isObject$m(C)) {
      C = C[SPECIES$7];
      if (C === null) C = undefined;
    }
  }

  return new (C === undefined ? Array : C)(length === 0 ? 0 : length);
};

var bind$7 = functionBindContext;

var IndexedObject$1 = indexedObject;

var toObject$8 = toObject$b;

var toLength$9 = toLength$b;

var arraySpeciesCreate$1 = arraySpeciesCreate$2;

var push$1 = [].push; // `Array.prototype.{ forEach, map, filter, some, every, find, findIndex, filterOut }` methods implementation

var createMethod$3 = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var IS_FILTER_OUT = TYPE == 7;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject$8($this);
    var self = IndexedObject$1(O);
    var boundFunction = bind$7(callbackfn, that, 3);
    var length = toLength$9(self.length);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate$1;
    var target = IS_MAP ? create($this, length) : IS_FILTER || IS_FILTER_OUT ? create($this, 0) : undefined;
    var value, result;

    for (; length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);

      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
            case 3:
              return true;
            // some

            case 5:
              return value;
            // find

            case 6:
              return index;
            // findIndex

            case 2:
              push$1.call(target, value);
            // filter
          } else switch (TYPE) {
            case 4:
              return false;
            // every

            case 7:
              push$1.call(target, value);
            // filterOut
          }
      }
    }

    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

var arrayIteration = {
  // `Array.prototype.forEach` method
  // https://tc39.es/ecma262/#sec-array.prototype.foreach
  forEach: createMethod$3(0),
  // `Array.prototype.map` method
  // https://tc39.es/ecma262/#sec-array.prototype.map
  map: createMethod$3(1),
  // `Array.prototype.filter` method
  // https://tc39.es/ecma262/#sec-array.prototype.filter
  filter: createMethod$3(2),
  // `Array.prototype.some` method
  // https://tc39.es/ecma262/#sec-array.prototype.some
  some: createMethod$3(3),
  // `Array.prototype.every` method
  // https://tc39.es/ecma262/#sec-array.prototype.every
  every: createMethod$3(4),
  // `Array.prototype.find` method
  // https://tc39.es/ecma262/#sec-array.prototype.find
  find: createMethod$3(5),
  // `Array.prototype.findIndex` method
  // https://tc39.es/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod$3(6),
  // `Array.prototype.filterOut` method
  // https://github.com/tc39/proposal-array-filtering
  filterOut: createMethod$3(7)
};

var fails$j = fails$q;

var arrayMethodIsStrict$3 = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails$j(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal -- required for testing
    method.call(null, argument || function () {
      throw 1;
    }, 1);
  });
};

var $forEach$1 = arrayIteration.forEach;

var arrayMethodIsStrict$2 = arrayMethodIsStrict$3;

var STRICT_METHOD$2 = arrayMethodIsStrict$2('forEach'); // `Array.prototype.forEach` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.foreach

var arrayForEach = !STRICT_METHOD$2 ? function forEach(callbackfn
/* , thisArg */
) {
  return $forEach$1(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined); // eslint-disable-next-line es/no-array-prototype-foreach -- safe
} : [].forEach;

var global$e = global$q;

var DOMIterables$1 = domIterables;

var forEach = arrayForEach;

var createNonEnumerableProperty$6 = createNonEnumerableProperty$b;

for (var COLLECTION_NAME$1 in DOMIterables$1) {
  var Collection$1 = global$e[COLLECTION_NAME$1];
  var CollectionPrototype$1 = Collection$1 && Collection$1.prototype; // some Chrome versions have non-configurable methods on DOMTokenList

  if (CollectionPrototype$1 && CollectionPrototype$1.forEach !== forEach) try {
    createNonEnumerableProperty$6(CollectionPrototype$1, 'forEach', forEach);
  } catch (error) {
    CollectionPrototype$1.forEach = forEach;
  }
}

var DESCRIPTORS$8 = descriptors;

var objectKeys$2 = objectKeys$4;

var toIndexedObject$5 = toIndexedObject$9;

var propertyIsEnumerable$2 = objectPropertyIsEnumerable.f; // `Object.{ entries, values }` methods implementation


var createMethod$2 = function (TO_ENTRIES) {
  return function (it) {
    var O = toIndexedObject$5(it);
    var keys = objectKeys$2(O);
    var length = keys.length;
    var i = 0;
    var result = [];
    var key;

    while (length > i) {
      key = keys[i++];

      if (!DESCRIPTORS$8 || propertyIsEnumerable$2.call(O, key)) {
        result.push(TO_ENTRIES ? [key, O[key]] : O[key]);
      }
    }

    return result;
  };
};

var objectToArray = {
  // `Object.entries` method
  // https://tc39.es/ecma262/#sec-object.entries
  entries: createMethod$2(true),
  // `Object.values` method
  // https://tc39.es/ecma262/#sec-object.values
  values: createMethod$2(false)
};

var $$p = _export;

var $entries = objectToArray.entries; // `Object.entries` method
// https://tc39.es/ecma262/#sec-object.entries


$$p({
  target: 'Object',
  stat: true
}, {
  entries: function entries(O) {
    return $entries(O);
  }
});

var fails$i = fails$q;

var wellKnownSymbol$m = wellKnownSymbol$q;

var V8_VERSION$2 = engineV8Version;

var SPECIES$6 = wellKnownSymbol$m('species');

var arrayMethodHasSpeciesSupport$6 = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION$2 >= 51 || !fails$i(function () {
    var array = [];
    var constructor = array.constructor = {};

    constructor[SPECIES$6] = function () {
      return {
        foo: 1
      };
    };

    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};

var $$o = _export;

var $map$1 = arrayIteration.map;

var arrayMethodHasSpeciesSupport$5 = arrayMethodHasSpeciesSupport$6;

var HAS_SPECIES_SUPPORT$4 = arrayMethodHasSpeciesSupport$5('map'); // `Array.prototype.map` method
// https://tc39.es/ecma262/#sec-array.prototype.map
// with adding support of @@species

$$o({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$4
}, {
  map: function map(callbackfn
  /* , thisArg */
  ) {
    return $map$1(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

var $$n = _export;

var $filter$1 = arrayIteration.filter;

var arrayMethodHasSpeciesSupport$4 = arrayMethodHasSpeciesSupport$6;

var HAS_SPECIES_SUPPORT$3 = arrayMethodHasSpeciesSupport$4('filter'); // `Array.prototype.filter` method
// https://tc39.es/ecma262/#sec-array.prototype.filter
// with adding support of @@species

$$n({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$3
}, {
  filter: function filter(callbackfn
  /* , thisArg */
  ) {
    return $filter$1(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

var $$m = _export;

var toObject$7 = toObject$b;

var nativeKeys$2 = objectKeys$4;

var fails$h = fails$q;

var FAILS_ON_PRIMITIVES$1 = fails$h(function () {
  nativeKeys$2(1);
}); // `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys

$$m({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES$1
}, {
  keys: function keys(it) {
    return nativeKeys$2(toObject$7(it));
  }
});

var $$l = _export;

var aFunction$3 = aFunction$5;

var toObject$6 = toObject$b;

var fails$g = fails$q;

var arrayMethodIsStrict$1 = arrayMethodIsStrict$3;

var test = [];
var nativeSort = test.sort; // IE8-

var FAILS_ON_UNDEFINED = fails$g(function () {
  test.sort(undefined);
}); // V8 bug

var FAILS_ON_NULL = fails$g(function () {
  test.sort(null);
}); // Old WebKit

var STRICT_METHOD$1 = arrayMethodIsStrict$1('sort');
var FORCED$4 = FAILS_ON_UNDEFINED || !FAILS_ON_NULL || !STRICT_METHOD$1; // `Array.prototype.sort` method
// https://tc39.es/ecma262/#sec-array.prototype.sort

$$l({
  target: 'Array',
  proto: true,
  forced: FORCED$4
}, {
  sort: function sort(comparefn) {
    return comparefn === undefined ? nativeSort.call(toObject$6(this)) : nativeSort.call(toObject$6(this), aFunction$3(comparefn));
  }
});

var toPrimitive$2 = toPrimitive$5;

var definePropertyModule$4 = objectDefineProperty;

var createPropertyDescriptor$2 = createPropertyDescriptor$5;

var createProperty$3 = function (object, key, value) {
  var propertyKey = toPrimitive$2(key);
  if (propertyKey in object) definePropertyModule$4.f(object, propertyKey, createPropertyDescriptor$2(0, value));else object[propertyKey] = value;
};

var $$k = _export;

var isObject$l = isObject$r;

var isArray$g = isArray$i;

var toAbsoluteIndex$1 = toAbsoluteIndex$3;

var toLength$8 = toLength$b;

var toIndexedObject$4 = toIndexedObject$9;

var createProperty$2 = createProperty$3;

var wellKnownSymbol$l = wellKnownSymbol$q;

var arrayMethodHasSpeciesSupport$3 = arrayMethodHasSpeciesSupport$6;

var HAS_SPECIES_SUPPORT$2 = arrayMethodHasSpeciesSupport$3('slice');
var SPECIES$5 = wellKnownSymbol$l('species');
var nativeSlice = [].slice;
var max$1 = Math.max; // `Array.prototype.slice` method
// https://tc39.es/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects

$$k({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$2
}, {
  slice: function slice(start, end) {
    var O = toIndexedObject$4(this);
    var length = toLength$8(O.length);
    var k = toAbsoluteIndex$1(start, length);
    var fin = toAbsoluteIndex$1(end === undefined ? length : end, length); // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible

    var Constructor, result, n;

    if (isArray$g(O)) {
      Constructor = O.constructor; // cross-realm fallback

      if (typeof Constructor == 'function' && (Constructor === Array || isArray$g(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject$l(Constructor)) {
        Constructor = Constructor[SPECIES$5];
        if (Constructor === null) Constructor = undefined;
      }

      if (Constructor === Array || Constructor === undefined) {
        return nativeSlice.call(O, k, fin);
      }
    }

    result = new (Constructor === undefined ? Array : Constructor)(max$1(fin - k, 0));

    for (n = 0; k < fin; k++, n++) if (k in O) createProperty$2(result, n, O[k]);

    result.length = n;
    return result;
  }
});

var $$j = _export;

var $values = objectToArray.values; // `Object.values` method
// https://tc39.es/ecma262/#sec-object.values


$$j({
  target: 'Object',
  stat: true
}, {
  values: function values(O) {
    return $values(O);
  }
});

var global$d = global$q;

var nativePromiseConstructor = global$d.Promise;

var redefine$a = redefine$d.exports;

var redefineAll$2 = function (target, src, options) {
  for (var key in src) redefine$a(target, key, src[key], options);

  return target;
};

var isObject$k = isObject$r;

var aPossiblePrototype$1 = function (it) {
  if (!isObject$k(it) && it !== null) {
    throw TypeError("Can't set " + String(it) + ' as a prototype');
  }

  return it;
};

/* eslint-disable no-proto -- safe */

var anObject$e = anObject$h;

var aPossiblePrototype = aPossiblePrototype$1; // `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe


var objectSetPrototypeOf = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;

  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    setter = Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set;
    setter.call(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) {
    /* empty */
  }

  return function setPrototypeOf(O, proto) {
    anObject$e(O);
    aPossiblePrototype(proto);
    if (CORRECT_SETTER) setter.call(O, proto);else O.__proto__ = proto;
    return O;
  };
}() : undefined);

var defineProperty$a = objectDefineProperty.f;

var has$b = has$i;

var wellKnownSymbol$k = wellKnownSymbol$q;

var TO_STRING_TAG$1 = wellKnownSymbol$k('toStringTag');

var setToStringTag$5 = function (it, TAG, STATIC) {
  if (it && !has$b(it = STATIC ? it : it.prototype, TO_STRING_TAG$1)) {
    defineProperty$a(it, TO_STRING_TAG$1, {
      configurable: true,
      value: TAG
    });
  }
};

var getBuiltIn$3 = getBuiltIn$6;

var definePropertyModule$3 = objectDefineProperty;

var wellKnownSymbol$j = wellKnownSymbol$q;

var DESCRIPTORS$7 = descriptors;

var SPECIES$4 = wellKnownSymbol$j('species');

var setSpecies$4 = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn$3(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule$3.f;

  if (DESCRIPTORS$7 && Constructor && !Constructor[SPECIES$4]) {
    defineProperty(Constructor, SPECIES$4, {
      configurable: true,
      get: function () {
        return this;
      }
    });
  }
};

var anInstance$3 = function (it, Constructor, name) {
  if (!(it instanceof Constructor)) {
    throw TypeError('Incorrect ' + (name ? name + ' ' : '') + 'invocation');
  }

  return it;
};

var iterators = {};

var wellKnownSymbol$i = wellKnownSymbol$q;

var Iterators$4 = iterators;

var ITERATOR$5 = wellKnownSymbol$i('iterator');
var ArrayPrototype$1 = Array.prototype; // check on default Array iterator

var isArrayIteratorMethod$2 = function (it) {
  return it !== undefined && (Iterators$4.Array === it || ArrayPrototype$1[ITERATOR$5] === it);
};

var classof$6 = classof$9;

var Iterators$3 = iterators;

var wellKnownSymbol$h = wellKnownSymbol$q;

var ITERATOR$4 = wellKnownSymbol$h('iterator');

var getIteratorMethod$2 = function (it) {
  if (it != undefined) return it[ITERATOR$4] || it['@@iterator'] || Iterators$3[classof$6(it)];
};

var anObject$d = anObject$h;

var iteratorClose$2 = function (iterator) {
  var returnMethod = iterator['return'];

  if (returnMethod !== undefined) {
    return anObject$d(returnMethod.call(iterator)).value;
  }
};

var anObject$c = anObject$h;

var isArrayIteratorMethod$1 = isArrayIteratorMethod$2;

var toLength$7 = toLength$b;

var bind$6 = functionBindContext;

var getIteratorMethod$1 = getIteratorMethod$2;

var iteratorClose$1 = iteratorClose$2;

var Result = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var iterate$3 = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind$6(unboundFunction, that, 1 + AS_ENTRIES + INTERRUPTED);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose$1(iterator);
    return new Result(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject$c(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    }

    return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod$1(iterable);
    if (typeof iterFn != 'function') throw TypeError('Target is not iterable'); // optimisation for array iterators

    if (isArrayIteratorMethod$1(iterFn)) {
      for (index = 0, length = toLength$7(iterable.length); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && result instanceof Result) return result;
      }

      return new Result(false);
    }

    iterator = iterFn.call(iterable);
  }

  next = iterator.next;

  while (!(step = next.call(iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose$1(iterator);
      throw error;
    }

    if (typeof result == 'object' && result && result instanceof Result) return result;
  }

  return new Result(false);
};

var wellKnownSymbol$g = wellKnownSymbol$q;

var ITERATOR$3 = wellKnownSymbol$g('iterator');
var SAFE_CLOSING = false;

try {
  var called = 0;
  var iteratorWithReturn = {
    next: function () {
      return {
        done: !!called++
      };
    },
    'return': function () {
      SAFE_CLOSING = true;
    }
  };

  iteratorWithReturn[ITERATOR$3] = function () {
    return this;
  }; // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing


  Array.from(iteratorWithReturn, function () {
    throw 2;
  });
} catch (error) {
  /* empty */
}

var checkCorrectnessOfIteration$3 = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING) return false;
  var ITERATION_SUPPORT = false;

  try {
    var object = {};

    object[ITERATOR$3] = function () {
      return {
        next: function () {
          return {
            done: ITERATION_SUPPORT = true
          };
        }
      };
    };

    exec(object);
  } catch (error) {
    /* empty */
  }

  return ITERATION_SUPPORT;
};

var anObject$b = anObject$h;

var aFunction$2 = aFunction$5;

var wellKnownSymbol$f = wellKnownSymbol$q;

var SPECIES$3 = wellKnownSymbol$f('species'); // `SpeciesConstructor` abstract operation
// https://tc39.es/ecma262/#sec-speciesconstructor

var speciesConstructor$2 = function (O, defaultConstructor) {
  var C = anObject$b(O).constructor;
  var S;
  return C === undefined || (S = anObject$b(C)[SPECIES$3]) == undefined ? defaultConstructor : aFunction$2(S);
};

var getBuiltIn$2 = getBuiltIn$6;

var html$2 = getBuiltIn$2('document', 'documentElement');

var userAgent$1 = engineUserAgent;

var engineIsIos = /(?:iphone|ipod|ipad).*applewebkit/i.test(userAgent$1);

var classof$5 = classofRaw$1;

var global$c = global$q;

var engineIsNode = classof$5(global$c.process) == 'process';

var global$b = global$q;

var fails$f = fails$q;

var bind$5 = functionBindContext;

var html$1 = html$2;

var createElement = documentCreateElement$1;

var IS_IOS$1 = engineIsIos;

var IS_NODE$2 = engineIsNode;

var location = global$b.location;
var set = global$b.setImmediate;
var clear = global$b.clearImmediate;
var process$3 = global$b.process;
var MessageChannel = global$b.MessageChannel;
var Dispatch = global$b.Dispatch;
var counter = 0;
var queue = {};
var ONREADYSTATECHANGE = 'onreadystatechange';
var defer, channel, port;

var run = function (id) {
  // eslint-disable-next-line no-prototype-builtins -- safe
  if (queue.hasOwnProperty(id)) {
    var fn = queue[id];
    delete queue[id];
    fn();
  }
};

var runner = function (id) {
  return function () {
    run(id);
  };
};

var listener = function (event) {
  run(event.data);
};

var post = function (id) {
  // old engines have not location.origin
  global$b.postMessage(id + '', location.protocol + '//' + location.host);
}; // Node.js 0.9+ & IE10+ has setImmediate, otherwise:


if (!set || !clear) {
  set = function setImmediate(fn) {
    var args = [];
    var i = 1;

    while (arguments.length > i) args.push(arguments[i++]);

    queue[++counter] = function () {
      // eslint-disable-next-line no-new-func -- spec requirement
      (typeof fn == 'function' ? fn : Function(fn)).apply(undefined, args);
    };

    defer(counter);
    return counter;
  };

  clear = function clearImmediate(id) {
    delete queue[id];
  }; // Node.js 0.8-


  if (IS_NODE$2) {
    defer = function (id) {
      process$3.nextTick(runner(id));
    }; // Sphere (JS game engine) Dispatch API

  } else if (Dispatch && Dispatch.now) {
    defer = function (id) {
      Dispatch.now(runner(id));
    }; // Browsers with MessageChannel, includes WebWorkers
    // except iOS - https://github.com/zloirock/core-js/issues/624

  } else if (MessageChannel && !IS_IOS$1) {
    channel = new MessageChannel();
    port = channel.port2;
    channel.port1.onmessage = listener;
    defer = bind$5(port.postMessage, port, 1); // Browsers with postMessage, skip WebWorkers
    // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (global$b.addEventListener && typeof postMessage == 'function' && !global$b.importScripts && location && location.protocol !== 'file:' && !fails$f(post)) {
    defer = post;
    global$b.addEventListener('message', listener, false); // IE8-
  } else if (ONREADYSTATECHANGE in createElement('script')) {
    defer = function (id) {
      html$1.appendChild(createElement('script'))[ONREADYSTATECHANGE] = function () {
        html$1.removeChild(this);
        run(id);
      };
    }; // Rest old browsers

  } else {
    defer = function (id) {
      setTimeout(runner(id), 0);
    };
  }
}

var task$1 = {
  set: set,
  clear: clear
};

var userAgent = engineUserAgent;

var engineIsWebosWebkit = /web0s(?!.*chrome)/i.test(userAgent);

var global$a = global$q;

var getOwnPropertyDescriptor$2 = objectGetOwnPropertyDescriptor.f;

var macrotask = task$1.set;

var IS_IOS = engineIsIos;

var IS_WEBOS_WEBKIT = engineIsWebosWebkit;

var IS_NODE$1 = engineIsNode;

var MutationObserver = global$a.MutationObserver || global$a.WebKitMutationObserver;
var document$3 = global$a.document;
var process$2 = global$a.process;
var Promise$3 = global$a.Promise; // Node.js 11 shows ExperimentalWarning on getting `queueMicrotask`

var queueMicrotaskDescriptor = getOwnPropertyDescriptor$2(global$a, 'queueMicrotask');
var queueMicrotask = queueMicrotaskDescriptor && queueMicrotaskDescriptor.value;
var flush, head, last, notify$1, toggle, node, promise, then; // modern engines have queueMicrotask method

if (!queueMicrotask) {
  flush = function () {
    var parent, fn;
    if (IS_NODE$1 && (parent = process$2.domain)) parent.exit();

    while (head) {
      fn = head.fn;
      head = head.next;

      try {
        fn();
      } catch (error) {
        if (head) notify$1();else last = undefined;
        throw error;
      }
    }

    last = undefined;
    if (parent) parent.enter();
  }; // browsers with MutationObserver, except iOS - https://github.com/zloirock/core-js/issues/339
  // also except WebOS Webkit https://github.com/zloirock/core-js/issues/898


  if (!IS_IOS && !IS_NODE$1 && !IS_WEBOS_WEBKIT && MutationObserver && document$3) {
    toggle = true;
    node = document$3.createTextNode('');
    new MutationObserver(flush).observe(node, {
      characterData: true
    });

    notify$1 = function () {
      node.data = toggle = !toggle;
    }; // environments with maybe non-completely correct, but existent Promise

  } else if (Promise$3 && Promise$3.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    promise = Promise$3.resolve(undefined); // workaround of WebKit ~ iOS Safari 10.1 bug

    promise.constructor = Promise$3;
    then = promise.then;

    notify$1 = function () {
      then.call(promise, flush);
    }; // Node.js without promises

  } else if (IS_NODE$1) {
    notify$1 = function () {
      process$2.nextTick(flush);
    }; // for other environments - macrotask based on:
    // - setImmediate
    // - MessageChannel
    // - window.postMessag
    // - onreadystatechange
    // - setTimeout

  } else {
    notify$1 = function () {
      // strange IE + webpack dev server bug - use .call(global)
      macrotask.call(global$a, flush);
    };
  }
}

var microtask$1 = queueMicrotask || function (fn) {
  var task = {
    fn: fn,
    next: undefined
  };
  if (last) last.next = task;

  if (!head) {
    head = task;
    notify$1();
  }

  last = task;
};

var newPromiseCapability$2 = {};

var aFunction$1 = aFunction$5;

var PromiseCapability = function (C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw TypeError('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aFunction$1(resolve);
  this.reject = aFunction$1(reject);
}; // 25.4.1.5 NewPromiseCapability(C)


newPromiseCapability$2.f = function (C) {
  return new PromiseCapability(C);
};

var anObject$a = anObject$h;

var isObject$j = isObject$r;

var newPromiseCapability$1 = newPromiseCapability$2;

var promiseResolve$1 = function (C, x) {
  anObject$a(C);
  if (isObject$j(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability$1.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};

var global$9 = global$q;

var hostReportErrors$1 = function (a, b) {
  var console = global$9.console;

  if (console && console.error) {
    arguments.length === 1 ? console.error(a) : console.error(a, b);
  }
};

var perform$1 = function (exec) {
  try {
    return {
      error: false,
      value: exec()
    };
  } catch (error) {
    return {
      error: true,
      value: error
    };
  }
};

var engineIsBrowser = typeof window == 'object';

var $$i = _export;

var global$8 = global$q;

var getBuiltIn$1 = getBuiltIn$6;

var NativePromise = nativePromiseConstructor;

var redefine$9 = redefine$d.exports;

var redefineAll$1 = redefineAll$2;

var setPrototypeOf$2 = objectSetPrototypeOf;

var setToStringTag$4 = setToStringTag$5;

var setSpecies$3 = setSpecies$4;

var isObject$i = isObject$r;

var aFunction = aFunction$5;

var anInstance$2 = anInstance$3;

var inspectSource = inspectSource$3;

var iterate$2 = iterate$3;

var checkCorrectnessOfIteration$2 = checkCorrectnessOfIteration$3;

var speciesConstructor$1 = speciesConstructor$2;

var task = task$1.set;

var microtask = microtask$1;

var promiseResolve = promiseResolve$1;

var hostReportErrors = hostReportErrors$1;

var newPromiseCapabilityModule = newPromiseCapability$2;

var perform = perform$1;

var InternalStateModule$4 = internalState;

var isForced$4 = isForced_1;

var wellKnownSymbol$e = wellKnownSymbol$q;

var IS_BROWSER = engineIsBrowser;

var IS_NODE = engineIsNode;

var V8_VERSION$1 = engineV8Version;

var SPECIES$2 = wellKnownSymbol$e('species');
var PROMISE = 'Promise';
var getInternalState$3 = InternalStateModule$4.get;
var setInternalState$4 = InternalStateModule$4.set;
var getInternalPromiseState = InternalStateModule$4.getterFor(PROMISE);
var NativePromisePrototype = NativePromise && NativePromise.prototype;
var PromiseConstructor = NativePromise;
var PromiseConstructorPrototype = NativePromisePrototype;
var TypeError$1 = global$8.TypeError;
var document$2 = global$8.document;
var process$1 = global$8.process;
var newPromiseCapability = newPromiseCapabilityModule.f;
var newGenericPromiseCapability = newPromiseCapability;
var DISPATCH_EVENT = !!(document$2 && document$2.createEvent && global$8.dispatchEvent);
var NATIVE_REJECTION_EVENT = typeof PromiseRejectionEvent == 'function';
var UNHANDLED_REJECTION = 'unhandledrejection';
var REJECTION_HANDLED = 'rejectionhandled';
var PENDING$1 = 0;
var FULFILLED = 1;
var REJECTED$1 = 2;
var HANDLED = 1;
var UNHANDLED = 2;
var SUBCLASSING = false;
var Internal, OwnPromiseCapability, PromiseWrapper, nativeThen;
var FORCED$3 = isForced$4(PROMISE, function () {
  var GLOBAL_CORE_JS_PROMISE = inspectSource(PromiseConstructor) !== String(PromiseConstructor); // V8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
  // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
  // We can't detect it synchronously, so just check versions

  if (!GLOBAL_CORE_JS_PROMISE && V8_VERSION$1 === 66) return true; // We need Promise#finally in the pure version for preventing prototype pollution
  // deoptimization and performance degradation
  // https://github.com/zloirock/core-js/issues/679

  if (V8_VERSION$1 >= 51 && /native code/.test(PromiseConstructor)) return false; // Detect correctness of subclassing with @@species support

  var promise = new PromiseConstructor(function (resolve) {
    resolve(1);
  });

  var FakePromise = function (exec) {
    exec(function () {
      /* empty */
    }, function () {
      /* empty */
    });
  };

  var constructor = promise.constructor = {};
  constructor[SPECIES$2] = FakePromise;
  SUBCLASSING = promise.then(function () {
    /* empty */
  }) instanceof FakePromise;
  if (!SUBCLASSING) return true; // Unhandled rejections tracking support, NodeJS Promise without it fails @@species test

  return !GLOBAL_CORE_JS_PROMISE && IS_BROWSER && !NATIVE_REJECTION_EVENT;
});
var INCORRECT_ITERATION$1 = FORCED$3 || !checkCorrectnessOfIteration$2(function (iterable) {
  PromiseConstructor.all(iterable)['catch'](function () {
    /* empty */
  });
}); // helpers

var isThenable = function (it) {
  var then;
  return isObject$i(it) && typeof (then = it.then) == 'function' ? then : false;
};

var notify = function (state, isReject) {
  if (state.notified) return;
  state.notified = true;
  var chain = state.reactions;
  microtask(function () {
    var value = state.value;
    var ok = state.state == FULFILLED;
    var index = 0; // variable length - can't use forEach

    while (chain.length > index) {
      var reaction = chain[index++];
      var handler = ok ? reaction.ok : reaction.fail;
      var resolve = reaction.resolve;
      var reject = reaction.reject;
      var domain = reaction.domain;
      var result, then, exited;

      try {
        if (handler) {
          if (!ok) {
            if (state.rejection === UNHANDLED) onHandleUnhandled(state);
            state.rejection = HANDLED;
          }

          if (handler === true) result = value;else {
            if (domain) domain.enter();
            result = handler(value); // can throw

            if (domain) {
              domain.exit();
              exited = true;
            }
          }

          if (result === reaction.promise) {
            reject(TypeError$1('Promise-chain cycle'));
          } else if (then = isThenable(result)) {
            then.call(result, resolve, reject);
          } else resolve(result);
        } else reject(value);
      } catch (error) {
        if (domain && !exited) domain.exit();
        reject(error);
      }
    }

    state.reactions = [];
    state.notified = false;
    if (isReject && !state.rejection) onUnhandled(state);
  });
};

var dispatchEvent = function (name, promise, reason) {
  var event, handler;

  if (DISPATCH_EVENT) {
    event = document$2.createEvent('Event');
    event.promise = promise;
    event.reason = reason;
    event.initEvent(name, false, true);
    global$8.dispatchEvent(event);
  } else event = {
    promise: promise,
    reason: reason
  };

  if (!NATIVE_REJECTION_EVENT && (handler = global$8['on' + name])) handler(event);else if (name === UNHANDLED_REJECTION) hostReportErrors('Unhandled promise rejection', reason);
};

var onUnhandled = function (state) {
  task.call(global$8, function () {
    var promise = state.facade;
    var value = state.value;
    var IS_UNHANDLED = isUnhandled(state);
    var result;

    if (IS_UNHANDLED) {
      result = perform(function () {
        if (IS_NODE) {
          process$1.emit('unhandledRejection', value, promise);
        } else dispatchEvent(UNHANDLED_REJECTION, promise, value);
      }); // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should

      state.rejection = IS_NODE || isUnhandled(state) ? UNHANDLED : HANDLED;
      if (result.error) throw result.value;
    }
  });
};

var isUnhandled = function (state) {
  return state.rejection !== HANDLED && !state.parent;
};

var onHandleUnhandled = function (state) {
  task.call(global$8, function () {
    var promise = state.facade;

    if (IS_NODE) {
      process$1.emit('rejectionHandled', promise);
    } else dispatchEvent(REJECTION_HANDLED, promise, state.value);
  });
};

var bind$4 = function (fn, state, unwrap) {
  return function (value) {
    fn(state, value, unwrap);
  };
};

var internalReject = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  state.value = value;
  state.state = REJECTED$1;
  notify(state, true);
};

var internalResolve = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;

  try {
    if (state.facade === value) throw TypeError$1("Promise can't be resolved itself");
    var then = isThenable(value);

    if (then) {
      microtask(function () {
        var wrapper = {
          done: false
        };

        try {
          then.call(value, bind$4(internalResolve, wrapper, state), bind$4(internalReject, wrapper, state));
        } catch (error) {
          internalReject(wrapper, error, state);
        }
      });
    } else {
      state.value = value;
      state.state = FULFILLED;
      notify(state, false);
    }
  } catch (error) {
    internalReject({
      done: false
    }, error, state);
  }
}; // constructor polyfill


if (FORCED$3) {
  // 25.4.3.1 Promise(executor)
  PromiseConstructor = function Promise(executor) {
    anInstance$2(this, PromiseConstructor, PROMISE);
    aFunction(executor);
    Internal.call(this);
    var state = getInternalState$3(this);

    try {
      executor(bind$4(internalResolve, state), bind$4(internalReject, state));
    } catch (error) {
      internalReject(state, error);
    }
  };

  PromiseConstructorPrototype = PromiseConstructor.prototype; // eslint-disable-next-line no-unused-vars -- required for `.length`

  Internal = function Promise(executor) {
    setInternalState$4(this, {
      type: PROMISE,
      done: false,
      notified: false,
      parent: false,
      reactions: [],
      rejection: false,
      state: PENDING$1,
      value: undefined
    });
  };

  Internal.prototype = redefineAll$1(PromiseConstructorPrototype, {
    // `Promise.prototype.then` method
    // https://tc39.es/ecma262/#sec-promise.prototype.then
    then: function then(onFulfilled, onRejected) {
      var state = getInternalPromiseState(this);
      var reaction = newPromiseCapability(speciesConstructor$1(this, PromiseConstructor));
      reaction.ok = typeof onFulfilled == 'function' ? onFulfilled : true;
      reaction.fail = typeof onRejected == 'function' && onRejected;
      reaction.domain = IS_NODE ? process$1.domain : undefined;
      state.parent = true;
      state.reactions.push(reaction);
      if (state.state != PENDING$1) notify(state, false);
      return reaction.promise;
    },
    // `Promise.prototype.catch` method
    // https://tc39.es/ecma262/#sec-promise.prototype.catch
    'catch': function (onRejected) {
      return this.then(undefined, onRejected);
    }
  });

  OwnPromiseCapability = function () {
    var promise = new Internal();
    var state = getInternalState$3(promise);
    this.promise = promise;
    this.resolve = bind$4(internalResolve, state);
    this.reject = bind$4(internalReject, state);
  };

  newPromiseCapabilityModule.f = newPromiseCapability = function (C) {
    return C === PromiseConstructor || C === PromiseWrapper ? new OwnPromiseCapability(C) : newGenericPromiseCapability(C);
  };

  if (typeof NativePromise == 'function' && NativePromisePrototype !== Object.prototype) {
    nativeThen = NativePromisePrototype.then;

    if (!SUBCLASSING) {
      // make `Promise#then` return a polyfilled `Promise` for native promise-based APIs
      redefine$9(NativePromisePrototype, 'then', function then(onFulfilled, onRejected) {
        var that = this;
        return new PromiseConstructor(function (resolve, reject) {
          nativeThen.call(that, resolve, reject);
        }).then(onFulfilled, onRejected); // https://github.com/zloirock/core-js/issues/640
      }, {
        unsafe: true
      }); // makes sure that native promise-based APIs `Promise#catch` properly works with patched `Promise#then`

      redefine$9(NativePromisePrototype, 'catch', PromiseConstructorPrototype['catch'], {
        unsafe: true
      });
    } // make `.constructor === Promise` work for native promise-based APIs


    try {
      delete NativePromisePrototype.constructor;
    } catch (error) {}
    /* empty */
    // make `instanceof Promise` work for native promise-based APIs


    if (setPrototypeOf$2) {
      setPrototypeOf$2(NativePromisePrototype, PromiseConstructorPrototype);
    }
  }
}

$$i({
  global: true,
  wrap: true,
  forced: FORCED$3
}, {
  Promise: PromiseConstructor
});
setToStringTag$4(PromiseConstructor, PROMISE, false);
setSpecies$3(PROMISE);
PromiseWrapper = getBuiltIn$1(PROMISE); // statics

$$i({
  target: PROMISE,
  stat: true,
  forced: FORCED$3
}, {
  // `Promise.reject` method
  // https://tc39.es/ecma262/#sec-promise.reject
  reject: function reject(r) {
    var capability = newPromiseCapability(this);
    capability.reject.call(undefined, r);
    return capability.promise;
  }
});
$$i({
  target: PROMISE,
  stat: true,
  forced: FORCED$3
}, {
  // `Promise.resolve` method
  // https://tc39.es/ecma262/#sec-promise.resolve
  resolve: function resolve(x) {
    return promiseResolve(this, x);
  }
});
$$i({
  target: PROMISE,
  stat: true,
  forced: INCORRECT_ITERATION$1
}, {
  // `Promise.all` method
  // https://tc39.es/ecma262/#sec-promise.all
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aFunction(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate$2(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        values.push(undefined);
        remaining++;
        $promiseResolve.call(C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  },
  // `Promise.race` method
  // https://tc39.es/ecma262/#sec-promise.race
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aFunction(C.resolve);
      iterate$2(iterable, function (promise) {
        $promiseResolve.call(C, promise).then(capability.resolve, reject);
      });
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});

var DESCRIPTORS$6 = descriptors;

var definePropertyModule$2 = objectDefineProperty;

var anObject$9 = anObject$h;

var objectKeys$1 = objectKeys$4; // `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe


var objectDefineProperties = DESCRIPTORS$6 ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$9(O);
  var keys = objectKeys$1(Properties);
  var length = keys.length;
  var index = 0;
  var key;

  while (length > index) definePropertyModule$2.f(O, key = keys[index++], Properties[key]);

  return O;
};

var anObject$8 = anObject$h;

var defineProperties = objectDefineProperties;

var enumBugKeys = enumBugKeys$3;

var hiddenKeys$2 = hiddenKeys$6;

var html = html$2;

var documentCreateElement = documentCreateElement$1;

var sharedKey$2 = sharedKey$4;

var GT = '>';
var LT = '<';
var PROTOTYPE$1 = 'prototype';
var SCRIPT = 'script';
var IE_PROTO$1 = sharedKey$2('IE_PROTO');

var EmptyConstructor = function () {
  /* empty */
};

var scriptTag = function (content) {
  return LT + SCRIPT + GT + content + LT + '/' + SCRIPT + GT;
}; // Create object with fake `null` prototype: use ActiveX Object with cleared prototype


var NullProtoObjectViaActiveX = function (activeXDocument) {
  activeXDocument.write(scriptTag(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak

  return temp;
}; // Create object with fake `null` prototype: use iframe Object with cleared prototype


var NullProtoObjectViaIFrame = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement('iframe');
  var JS = 'java' + SCRIPT + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html.appendChild(iframe); // https://github.com/zloirock/core-js/issues/475

  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
}; // Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug


var activeXDocument;

var NullProtoObject = function () {
  try {
    /* global ActiveXObject -- old IE */
    activeXDocument = document.domain && new ActiveXObject('htmlfile');
  } catch (error) {
    /* ignore */
  }

  NullProtoObject = activeXDocument ? NullProtoObjectViaActiveX(activeXDocument) : NullProtoObjectViaIFrame();
  var length = enumBugKeys.length;

  while (length--) delete NullProtoObject[PROTOTYPE$1][enumBugKeys[length]];

  return NullProtoObject();
};

hiddenKeys$2[IE_PROTO$1] = true; // `Object.create` method
// https://tc39.es/ecma262/#sec-object.create

var objectCreate = Object.create || function create(O, Properties) {
  var result;

  if (O !== null) {
    EmptyConstructor[PROTOTYPE$1] = anObject$8(O);
    result = new EmptyConstructor();
    EmptyConstructor[PROTOTYPE$1] = null; // add "__proto__" for Object.getPrototypeOf polyfill

    result[IE_PROTO$1] = O;
  } else result = NullProtoObject();

  return Properties === undefined ? result : defineProperties(result, Properties);
};

var objectGetOwnPropertyNamesExternal = {};

/* eslint-disable es/no-object-getownpropertynames -- safe */

var toIndexedObject$3 = toIndexedObject$9;

var $getOwnPropertyNames$1 = objectGetOwnPropertyNames.f;

var toString$2 = {}.toString;
var windowNames = typeof window == 'object' && window && Object.getOwnPropertyNames ? Object.getOwnPropertyNames(window) : [];

var getWindowNames = function (it) {
  try {
    return $getOwnPropertyNames$1(it);
  } catch (error) {
    return windowNames.slice();
  }
}; // fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window


objectGetOwnPropertyNamesExternal.f = function getOwnPropertyNames(it) {
  return windowNames && toString$2.call(it) == '[object Window]' ? getWindowNames(it) : $getOwnPropertyNames$1(toIndexedObject$3(it));
};

var wellKnownSymbolWrapped = {};

var wellKnownSymbol$d = wellKnownSymbol$q;

wellKnownSymbolWrapped.f = wellKnownSymbol$d;

var path = path$2;

var has$a = has$i;

var wrappedWellKnownSymbolModule$1 = wellKnownSymbolWrapped;

var defineProperty$9 = objectDefineProperty.f;

var defineWellKnownSymbol$2 = function (NAME) {
  var Symbol = path.Symbol || (path.Symbol = {});
  if (!has$a(Symbol, NAME)) defineProperty$9(Symbol, NAME, {
    value: wrappedWellKnownSymbolModule$1.f(NAME)
  });
};

var $$h = _export;

var global$7 = global$q;

var getBuiltIn = getBuiltIn$6;

var DESCRIPTORS$5 = descriptors;

var NATIVE_SYMBOL = nativeSymbol;

var USE_SYMBOL_AS_UID = useSymbolAsUid;

var fails$e = fails$q;

var has$9 = has$i;

var isArray$f = isArray$i;

var isObject$h = isObject$r;

var anObject$7 = anObject$h;

var toObject$5 = toObject$b;

var toIndexedObject$2 = toIndexedObject$9;

var toPrimitive$1 = toPrimitive$5;

var createPropertyDescriptor$1 = createPropertyDescriptor$5;

var nativeObjectCreate = objectCreate;

var objectKeys = objectKeys$4;

var getOwnPropertyNamesModule = objectGetOwnPropertyNames;

var getOwnPropertyNamesExternal = objectGetOwnPropertyNamesExternal;

var getOwnPropertySymbolsModule = objectGetOwnPropertySymbols;

var getOwnPropertyDescriptorModule = objectGetOwnPropertyDescriptor;

var definePropertyModule$1 = objectDefineProperty;

var propertyIsEnumerableModule = objectPropertyIsEnumerable;

var createNonEnumerableProperty$5 = createNonEnumerableProperty$b;

var redefine$8 = redefine$d.exports;

var shared$1 = shared$5.exports;

var sharedKey$1 = sharedKey$4;

var hiddenKeys$1 = hiddenKeys$6;

var uid$1 = uid$4;

var wellKnownSymbol$c = wellKnownSymbol$q;

var wrappedWellKnownSymbolModule = wellKnownSymbolWrapped;

var defineWellKnownSymbol$1 = defineWellKnownSymbol$2;

var setToStringTag$3 = setToStringTag$5;

var InternalStateModule$3 = internalState;

var $forEach = arrayIteration.forEach;

var HIDDEN = sharedKey$1('hidden');
var SYMBOL = 'Symbol';
var PROTOTYPE = 'prototype';
var TO_PRIMITIVE = wellKnownSymbol$c('toPrimitive');
var setInternalState$3 = InternalStateModule$3.set;
var getInternalState$2 = InternalStateModule$3.getterFor(SYMBOL);
var ObjectPrototype$1 = Object[PROTOTYPE];
var $Symbol = global$7.Symbol;
var $stringify = getBuiltIn('JSON', 'stringify');
var nativeGetOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
var nativeDefineProperty = definePropertyModule$1.f;
var nativeGetOwnPropertyNames = getOwnPropertyNamesExternal.f;
var nativePropertyIsEnumerable = propertyIsEnumerableModule.f;
var AllSymbols = shared$1('symbols');
var ObjectPrototypeSymbols = shared$1('op-symbols');
var StringToSymbolRegistry = shared$1('string-to-symbol-registry');
var SymbolToStringRegistry = shared$1('symbol-to-string-registry');
var WellKnownSymbolsStore = shared$1('wks');
var QObject = global$7.QObject; // Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173

var USE_SETTER = !QObject || !QObject[PROTOTYPE] || !QObject[PROTOTYPE].findChild; // fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687

var setSymbolDescriptor = DESCRIPTORS$5 && fails$e(function () {
  return nativeObjectCreate(nativeDefineProperty({}, 'a', {
    get: function () {
      return nativeDefineProperty(this, 'a', {
        value: 7
      }).a;
    }
  })).a != 7;
}) ? function (O, P, Attributes) {
  var ObjectPrototypeDescriptor = nativeGetOwnPropertyDescriptor(ObjectPrototype$1, P);
  if (ObjectPrototypeDescriptor) delete ObjectPrototype$1[P];
  nativeDefineProperty(O, P, Attributes);

  if (ObjectPrototypeDescriptor && O !== ObjectPrototype$1) {
    nativeDefineProperty(ObjectPrototype$1, P, ObjectPrototypeDescriptor);
  }
} : nativeDefineProperty;

var wrap = function (tag, description) {
  var symbol = AllSymbols[tag] = nativeObjectCreate($Symbol[PROTOTYPE]);
  setInternalState$3(symbol, {
    type: SYMBOL,
    tag: tag,
    description: description
  });
  if (!DESCRIPTORS$5) symbol.description = description;
  return symbol;
};

var isSymbol$7 = USE_SYMBOL_AS_UID ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  return Object(it) instanceof $Symbol;
};

var $defineProperty = function defineProperty(O, P, Attributes) {
  if (O === ObjectPrototype$1) $defineProperty(ObjectPrototypeSymbols, P, Attributes);
  anObject$7(O);
  var key = toPrimitive$1(P, true);
  anObject$7(Attributes);

  if (has$9(AllSymbols, key)) {
    if (!Attributes.enumerable) {
      if (!has$9(O, HIDDEN)) nativeDefineProperty(O, HIDDEN, createPropertyDescriptor$1(1, {}));
      O[HIDDEN][key] = true;
    } else {
      if (has$9(O, HIDDEN) && O[HIDDEN][key]) O[HIDDEN][key] = false;
      Attributes = nativeObjectCreate(Attributes, {
        enumerable: createPropertyDescriptor$1(0, false)
      });
    }

    return setSymbolDescriptor(O, key, Attributes);
  }

  return nativeDefineProperty(O, key, Attributes);
};

var $defineProperties = function defineProperties(O, Properties) {
  anObject$7(O);
  var properties = toIndexedObject$2(Properties);
  var keys = objectKeys(properties).concat($getOwnPropertySymbols(properties));
  $forEach(keys, function (key) {
    if (!DESCRIPTORS$5 || $propertyIsEnumerable.call(properties, key)) $defineProperty(O, key, properties[key]);
  });
  return O;
};

var $create = function create(O, Properties) {
  return Properties === undefined ? nativeObjectCreate(O) : $defineProperties(nativeObjectCreate(O), Properties);
};

var $propertyIsEnumerable = function propertyIsEnumerable(V) {
  var P = toPrimitive$1(V, true);
  var enumerable = nativePropertyIsEnumerable.call(this, P);
  if (this === ObjectPrototype$1 && has$9(AllSymbols, P) && !has$9(ObjectPrototypeSymbols, P)) return false;
  return enumerable || !has$9(this, P) || !has$9(AllSymbols, P) || has$9(this, HIDDEN) && this[HIDDEN][P] ? enumerable : true;
};

var $getOwnPropertyDescriptor = function getOwnPropertyDescriptor(O, P) {
  var it = toIndexedObject$2(O);
  var key = toPrimitive$1(P, true);
  if (it === ObjectPrototype$1 && has$9(AllSymbols, key) && !has$9(ObjectPrototypeSymbols, key)) return;
  var descriptor = nativeGetOwnPropertyDescriptor(it, key);

  if (descriptor && has$9(AllSymbols, key) && !(has$9(it, HIDDEN) && it[HIDDEN][key])) {
    descriptor.enumerable = true;
  }

  return descriptor;
};

var $getOwnPropertyNames = function getOwnPropertyNames(O) {
  var names = nativeGetOwnPropertyNames(toIndexedObject$2(O));
  var result = [];
  $forEach(names, function (key) {
    if (!has$9(AllSymbols, key) && !has$9(hiddenKeys$1, key)) result.push(key);
  });
  return result;
};

var $getOwnPropertySymbols = function getOwnPropertySymbols(O) {
  var IS_OBJECT_PROTOTYPE = O === ObjectPrototype$1;
  var names = nativeGetOwnPropertyNames(IS_OBJECT_PROTOTYPE ? ObjectPrototypeSymbols : toIndexedObject$2(O));
  var result = [];
  $forEach(names, function (key) {
    if (has$9(AllSymbols, key) && (!IS_OBJECT_PROTOTYPE || has$9(ObjectPrototype$1, key))) {
      result.push(AllSymbols[key]);
    }
  });
  return result;
}; // `Symbol` constructor
// https://tc39.es/ecma262/#sec-symbol-constructor


if (!NATIVE_SYMBOL) {
  $Symbol = function Symbol() {
    if (this instanceof $Symbol) throw TypeError('Symbol is not a constructor');
    var description = !arguments.length || arguments[0] === undefined ? undefined : String(arguments[0]);
    var tag = uid$1(description);

    var setter = function (value) {
      if (this === ObjectPrototype$1) setter.call(ObjectPrototypeSymbols, value);
      if (has$9(this, HIDDEN) && has$9(this[HIDDEN], tag)) this[HIDDEN][tag] = false;
      setSymbolDescriptor(this, tag, createPropertyDescriptor$1(1, value));
    };

    if (DESCRIPTORS$5 && USE_SETTER) setSymbolDescriptor(ObjectPrototype$1, tag, {
      configurable: true,
      set: setter
    });
    return wrap(tag, description);
  };

  redefine$8($Symbol[PROTOTYPE], 'toString', function toString() {
    return getInternalState$2(this).tag;
  });
  redefine$8($Symbol, 'withoutSetter', function (description) {
    return wrap(uid$1(description), description);
  });
  propertyIsEnumerableModule.f = $propertyIsEnumerable;
  definePropertyModule$1.f = $defineProperty;
  getOwnPropertyDescriptorModule.f = $getOwnPropertyDescriptor;
  getOwnPropertyNamesModule.f = getOwnPropertyNamesExternal.f = $getOwnPropertyNames;
  getOwnPropertySymbolsModule.f = $getOwnPropertySymbols;

  wrappedWellKnownSymbolModule.f = function (name) {
    return wrap(wellKnownSymbol$c(name), name);
  };

  if (DESCRIPTORS$5) {
    // https://github.com/tc39/proposal-Symbol-description
    nativeDefineProperty($Symbol[PROTOTYPE], 'description', {
      configurable: true,
      get: function description() {
        return getInternalState$2(this).description;
      }
    });

    {
      redefine$8(ObjectPrototype$1, 'propertyIsEnumerable', $propertyIsEnumerable, {
        unsafe: true
      });
    }
  }
}

$$h({
  global: true,
  wrap: true,
  forced: !NATIVE_SYMBOL,
  sham: !NATIVE_SYMBOL
}, {
  Symbol: $Symbol
});
$forEach(objectKeys(WellKnownSymbolsStore), function (name) {
  defineWellKnownSymbol$1(name);
});
$$h({
  target: SYMBOL,
  stat: true,
  forced: !NATIVE_SYMBOL
}, {
  // `Symbol.for` method
  // https://tc39.es/ecma262/#sec-symbol.for
  'for': function (key) {
    var string = String(key);
    if (has$9(StringToSymbolRegistry, string)) return StringToSymbolRegistry[string];
    var symbol = $Symbol(string);
    StringToSymbolRegistry[string] = symbol;
    SymbolToStringRegistry[symbol] = string;
    return symbol;
  },
  // `Symbol.keyFor` method
  // https://tc39.es/ecma262/#sec-symbol.keyfor
  keyFor: function keyFor(sym) {
    if (!isSymbol$7(sym)) throw TypeError(sym + ' is not a symbol');
    if (has$9(SymbolToStringRegistry, sym)) return SymbolToStringRegistry[sym];
  },
  useSetter: function () {
    USE_SETTER = true;
  },
  useSimple: function () {
    USE_SETTER = false;
  }
});
$$h({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL,
  sham: !DESCRIPTORS$5
}, {
  // `Object.create` method
  // https://tc39.es/ecma262/#sec-object.create
  create: $create,
  // `Object.defineProperty` method
  // https://tc39.es/ecma262/#sec-object.defineproperty
  defineProperty: $defineProperty,
  // `Object.defineProperties` method
  // https://tc39.es/ecma262/#sec-object.defineproperties
  defineProperties: $defineProperties,
  // `Object.getOwnPropertyDescriptor` method
  // https://tc39.es/ecma262/#sec-object.getownpropertydescriptors
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor
});
$$h({
  target: 'Object',
  stat: true,
  forced: !NATIVE_SYMBOL
}, {
  // `Object.getOwnPropertyNames` method
  // https://tc39.es/ecma262/#sec-object.getownpropertynames
  getOwnPropertyNames: $getOwnPropertyNames,
  // `Object.getOwnPropertySymbols` method
  // https://tc39.es/ecma262/#sec-object.getownpropertysymbols
  getOwnPropertySymbols: $getOwnPropertySymbols
}); // Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443

$$h({
  target: 'Object',
  stat: true,
  forced: fails$e(function () {
    getOwnPropertySymbolsModule.f(1);
  })
}, {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return getOwnPropertySymbolsModule.f(toObject$5(it));
  }
}); // `JSON.stringify` method behavior with symbols
// https://tc39.es/ecma262/#sec-json.stringify

if ($stringify) {
  var FORCED_JSON_STRINGIFY = !NATIVE_SYMBOL || fails$e(function () {
    var symbol = $Symbol(); // MS Edge converts symbol values to JSON as {}

    return $stringify([symbol]) != '[null]' // WebKit converts symbol values to JSON as null
    || $stringify({
      a: symbol
    }) != '{}' // V8 throws on boxed symbols
    || $stringify(Object(symbol)) != '{}';
  });
  $$h({
    target: 'JSON',
    stat: true,
    forced: FORCED_JSON_STRINGIFY
  }, {
    // eslint-disable-next-line no-unused-vars -- required for `.length`
    stringify: function stringify(it, replacer, space) {
      var args = [it];
      var index = 1;
      var $replacer;

      while (arguments.length > index) args.push(arguments[index++]);

      $replacer = replacer;
      if (!isObject$h(replacer) && it === undefined || isSymbol$7(it)) return; // IE8 returns string on undefined

      if (!isArray$f(replacer)) replacer = function (key, value) {
        if (typeof $replacer == 'function') value = $replacer.call(this, key, value);
        if (!isSymbol$7(value)) return value;
      };
      args[1] = replacer;
      return $stringify.apply(null, args);
    }
  });
} // `Symbol.prototype[@@toPrimitive]` method
// https://tc39.es/ecma262/#sec-symbol.prototype-@@toprimitive


if (!$Symbol[PROTOTYPE][TO_PRIMITIVE]) {
  createNonEnumerableProperty$5($Symbol[PROTOTYPE], TO_PRIMITIVE, $Symbol[PROTOTYPE].valueOf);
} // `Symbol.prototype[@@toStringTag]` property
// https://tc39.es/ecma262/#sec-symbol.prototype-@@tostringtag


setToStringTag$3($Symbol, SYMBOL);
hiddenKeys$1[HIDDEN] = true;

var $$g = _export;

var DESCRIPTORS$4 = descriptors;

var global$6 = global$q;

var has$8 = has$i;

var isObject$g = isObject$r;

var defineProperty$8 = objectDefineProperty.f;

var copyConstructorProperties = copyConstructorProperties$2;

var NativeSymbol = global$6.Symbol;

if (DESCRIPTORS$4 && typeof NativeSymbol == 'function' && (!('description' in NativeSymbol.prototype) || // Safari 12 bug
NativeSymbol().description !== undefined)) {
  var EmptyStringDescriptionStore = {}; // wrap Symbol constructor for correct work with undefined description

  var SymbolWrapper = function Symbol() {
    var description = arguments.length < 1 || arguments[0] === undefined ? undefined : String(arguments[0]);
    var result = this instanceof SymbolWrapper ? new NativeSymbol(description) // in Edge 13, String(Symbol(undefined)) === 'Symbol(undefined)'
    : description === undefined ? NativeSymbol() : NativeSymbol(description);
    if (description === '') EmptyStringDescriptionStore[result] = true;
    return result;
  };

  copyConstructorProperties(SymbolWrapper, NativeSymbol);
  var symbolPrototype = SymbolWrapper.prototype = NativeSymbol.prototype;
  symbolPrototype.constructor = SymbolWrapper;
  var symbolToString$1 = symbolPrototype.toString;
  var native = String(NativeSymbol('test')) == 'Symbol(test)';
  var regexp = /^Symbol\((.*)\)[^)]+$/;
  defineProperty$8(symbolPrototype, 'description', {
    configurable: true,
    get: function description() {
      var symbol = isObject$g(this) ? this.valueOf() : this;
      var string = symbolToString$1.call(symbol);
      if (has$8(EmptyStringDescriptionStore, symbol)) return '';
      var desc = native ? string.slice(7, -1) : string.replace(regexp, '$1');
      return desc === '' ? undefined : desc;
    }
  });
  $$g({
    global: true,
    forced: true
  }, {
    Symbol: SymbolWrapper
  });
}

var defineWellKnownSymbol = defineWellKnownSymbol$2; // `Symbol.iterator` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.iterator


defineWellKnownSymbol('iterator');

var wellKnownSymbol$b = wellKnownSymbol$q;

var create$3 = objectCreate;

var definePropertyModule = objectDefineProperty;

var UNSCOPABLES = wellKnownSymbol$b('unscopables');
var ArrayPrototype = Array.prototype; // Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

if (ArrayPrototype[UNSCOPABLES] == undefined) {
  definePropertyModule.f(ArrayPrototype, UNSCOPABLES, {
    configurable: true,
    value: create$3(null)
  });
} // add a key to Array.prototype[@@unscopables]


var addToUnscopables$5 = function (key) {
  ArrayPrototype[UNSCOPABLES][key] = true;
};

var fails$d = fails$q;

var correctPrototypeGetter = !fails$d(function () {
  function F() {
    /* empty */
  }

  F.prototype.constructor = null; // eslint-disable-next-line es/no-object-getprototypeof -- required for testing

  return Object.getPrototypeOf(new F()) !== F.prototype;
});

var has$7 = has$i;

var toObject$4 = toObject$b;

var sharedKey = sharedKey$4;

var CORRECT_PROTOTYPE_GETTER = correctPrototypeGetter;

var IE_PROTO = sharedKey('IE_PROTO');
var ObjectPrototype = Object.prototype; // `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
// eslint-disable-next-line es/no-object-getprototypeof -- safe

var objectGetPrototypeOf = CORRECT_PROTOTYPE_GETTER ? Object.getPrototypeOf : function (O) {
  O = toObject$4(O);
  if (has$7(O, IE_PROTO)) return O[IE_PROTO];

  if (typeof O.constructor == 'function' && O instanceof O.constructor) {
    return O.constructor.prototype;
  }

  return O instanceof Object ? ObjectPrototype : null;
};

var fails$c = fails$q;

var getPrototypeOf$1 = objectGetPrototypeOf;

var createNonEnumerableProperty$4 = createNonEnumerableProperty$b;

var has$6 = has$i;

var wellKnownSymbol$a = wellKnownSymbol$q;

var ITERATOR$2 = wellKnownSymbol$a('iterator');
var BUGGY_SAFARI_ITERATORS$1 = false;

var returnThis$2 = function () {
  return this;
}; // `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object


var IteratorPrototype$2, PrototypeOfArrayIteratorPrototype, arrayIterator;
/* eslint-disable es/no-array-prototype-keys -- safe */

if ([].keys) {
  arrayIterator = [].keys(); // Safari 8 has buggy iterators w/o `next`

  if (!('next' in arrayIterator)) BUGGY_SAFARI_ITERATORS$1 = true;else {
    PrototypeOfArrayIteratorPrototype = getPrototypeOf$1(getPrototypeOf$1(arrayIterator));
    if (PrototypeOfArrayIteratorPrototype !== Object.prototype) IteratorPrototype$2 = PrototypeOfArrayIteratorPrototype;
  }
}

var NEW_ITERATOR_PROTOTYPE = IteratorPrototype$2 == undefined || fails$c(function () {
  var test = {}; // FF44- legacy iterators case

  return IteratorPrototype$2[ITERATOR$2].call(test) !== test;
});
if (NEW_ITERATOR_PROTOTYPE) IteratorPrototype$2 = {}; // 25.1.2.1.1 %IteratorPrototype%[@@iterator]()

if (!has$6(IteratorPrototype$2, ITERATOR$2)) {
  createNonEnumerableProperty$4(IteratorPrototype$2, ITERATOR$2, returnThis$2);
}

var iteratorsCore = {
  IteratorPrototype: IteratorPrototype$2,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS$1
};

var IteratorPrototype$1 = iteratorsCore.IteratorPrototype;

var create$2 = objectCreate;

var createPropertyDescriptor = createPropertyDescriptor$5;

var setToStringTag$2 = setToStringTag$5;

var Iterators$2 = iterators;

var returnThis$1 = function () {
  return this;
};

var createIteratorConstructor$1 = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create$2(IteratorPrototype$1, {
    next: createPropertyDescriptor(1, next)
  });
  setToStringTag$2(IteratorConstructor, TO_STRING_TAG, false);
  Iterators$2[TO_STRING_TAG] = returnThis$1;
  return IteratorConstructor;
};

var $$f = _export;

var createIteratorConstructor = createIteratorConstructor$1;

var getPrototypeOf = objectGetPrototypeOf;

var setPrototypeOf$1 = objectSetPrototypeOf;

var setToStringTag$1 = setToStringTag$5;

var createNonEnumerableProperty$3 = createNonEnumerableProperty$b;

var redefine$7 = redefine$d.exports;

var wellKnownSymbol$9 = wellKnownSymbol$q;

var Iterators$1 = iterators;

var IteratorsCore = iteratorsCore;

var IteratorPrototype = IteratorsCore.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS = IteratorsCore.BUGGY_SAFARI_ITERATORS;
var ITERATOR$1 = wellKnownSymbol$9('iterator');
var KEYS = 'keys';
var VALUES = 'values';
var ENTRIES = 'entries';

var returnThis = function () {
  return this;
};

var defineIterator$3 = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS && KIND in IterablePrototype) return IterablePrototype[KIND];

    switch (KIND) {
      case KEYS:
        return function keys() {
          return new IteratorConstructor(this, KIND);
        };

      case VALUES:
        return function values() {
          return new IteratorConstructor(this, KIND);
        };

      case ENTRIES:
        return function entries() {
          return new IteratorConstructor(this, KIND);
        };
    }

    return function () {
      return new IteratorConstructor(this);
    };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR$1] || IterablePrototype['@@iterator'] || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY; // fix native

  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf(anyNativeIterator.call(new Iterable()));

    if (IteratorPrototype !== Object.prototype && CurrentIteratorPrototype.next) {
      if (getPrototypeOf(CurrentIteratorPrototype) !== IteratorPrototype) {
        if (setPrototypeOf$1) {
          setPrototypeOf$1(CurrentIteratorPrototype, IteratorPrototype);
        } else if (typeof CurrentIteratorPrototype[ITERATOR$1] != 'function') {
          createNonEnumerableProperty$3(CurrentIteratorPrototype, ITERATOR$1, returnThis);
        }
      } // Set @@toStringTag to native iterators


      setToStringTag$1(CurrentIteratorPrototype, TO_STRING_TAG, true);
    }
  } // fix Array#{values, @@iterator}.name in V8 / FF


  if (DEFAULT == VALUES && nativeIterator && nativeIterator.name !== VALUES) {
    INCORRECT_VALUES_NAME = true;

    defaultIterator = function values() {
      return nativeIterator.call(this);
    };
  } // define iterator


  if (IterablePrototype[ITERATOR$1] !== defaultIterator) {
    createNonEnumerableProperty$3(IterablePrototype, ITERATOR$1, defaultIterator);
  }

  Iterators$1[NAME] = defaultIterator; // export additional methods

  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS),
      entries: getIterationMethod(ENTRIES)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine$7(IterablePrototype, KEY, methods[KEY]);
      }
    } else $$f({
      target: NAME,
      proto: true,
      forced: BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME
    }, methods);
  }

  return methods;
};

var toIndexedObject$1 = toIndexedObject$9;

var addToUnscopables$4 = addToUnscopables$5;

var Iterators = iterators;

var InternalStateModule$2 = internalState;

var defineIterator$2 = defineIterator$3;

var ARRAY_ITERATOR = 'Array Iterator';
var setInternalState$2 = InternalStateModule$2.set;
var getInternalState$1 = InternalStateModule$2.getterFor(ARRAY_ITERATOR); // `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator

var es_array_iterator = defineIterator$2(Array, 'Array', function (iterated, kind) {
  setInternalState$2(this, {
    type: ARRAY_ITERATOR,
    target: toIndexedObject$1(iterated),
    // target
    index: 0,
    // next index
    kind: kind // kind

  }); // `%ArrayIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState$1(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;

  if (!target || index >= target.length) {
    state.target = undefined;
    return {
      value: undefined,
      done: true
    };
  }

  if (kind == 'keys') return {
    value: index,
    done: false
  };
  if (kind == 'values') return {
    value: target[index],
    done: false
  };
  return {
    value: [index, target[index]],
    done: false
  };
}, 'values'); // argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject

Iterators.Arguments = Iterators.Array; // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$4('keys');
addToUnscopables$4('values');
addToUnscopables$4('entries');

var toInteger$1 = toInteger$4;

var requireObjectCoercible$7 = requireObjectCoercible$a; // `String.prototype.{ codePointAt, at }` methods implementation


var createMethod$1 = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = String(requireObjectCoercible$7($this));
    var position = toInteger$1(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = S.charCodeAt(position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size || (second = S.charCodeAt(position + 1)) < 0xDC00 || second > 0xDFFF ? CONVERT_TO_STRING ? S.charAt(position) : first : CONVERT_TO_STRING ? S.slice(position, position + 2) : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

var stringMultibyte = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod$1(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod$1(true)
};

var charAt$1 = stringMultibyte.charAt;

var InternalStateModule$1 = internalState;

var defineIterator$1 = defineIterator$3;

var STRING_ITERATOR = 'String Iterator';
var setInternalState$1 = InternalStateModule$1.set;
var getInternalState = InternalStateModule$1.getterFor(STRING_ITERATOR); // `String.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-string.prototype-@@iterator

defineIterator$1(String, 'String', function (iterated) {
  setInternalState$1(this, {
    type: STRING_ITERATOR,
    string: String(iterated),
    index: 0
  }); // `%StringIteratorPrototype%.next` method
  // https://tc39.es/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return {
    value: undefined,
    done: true
  };
  point = charAt$1(string, index);
  state.index += point.length;
  return {
    value: point,
    done: false
  };
});

var global$5 = global$q;

var DOMIterables = domIterables;

var ArrayIteratorMethods = es_array_iterator;

var createNonEnumerableProperty$2 = createNonEnumerableProperty$b;

var wellKnownSymbol$8 = wellKnownSymbol$q;

var ITERATOR = wellKnownSymbol$8('iterator');
var TO_STRING_TAG = wellKnownSymbol$8('toStringTag');
var ArrayValues = ArrayIteratorMethods.values;

for (var COLLECTION_NAME in DOMIterables) {
  var Collection = global$5[COLLECTION_NAME];
  var CollectionPrototype = Collection && Collection.prototype;

  if (CollectionPrototype) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype[ITERATOR] !== ArrayValues) try {
      createNonEnumerableProperty$2(CollectionPrototype, ITERATOR, ArrayValues);
    } catch (error) {
      CollectionPrototype[ITERATOR] = ArrayValues;
    }

    if (!CollectionPrototype[TO_STRING_TAG]) {
      createNonEnumerableProperty$2(CollectionPrototype, TO_STRING_TAG, COLLECTION_NAME);
    }

    if (DOMIterables[COLLECTION_NAME]) for (var METHOD_NAME in ArrayIteratorMethods) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype[METHOD_NAME] !== ArrayIteratorMethods[METHOD_NAME]) try {
        createNonEnumerableProperty$2(CollectionPrototype, METHOD_NAME, ArrayIteratorMethods[METHOD_NAME]);
      } catch (error) {
        CollectionPrototype[METHOD_NAME] = ArrayIteratorMethods[METHOD_NAME];
      }
    }
  }
}

var anObject$6 = anObject$h;

var iteratorClose = iteratorClose$2; // call something on iterator step with safe closing on error


var callWithSafeIterationClosing$1 = function (iterator, fn, value, ENTRIES) {
  try {
    return ENTRIES ? fn(anObject$6(value)[0], value[1]) : fn(value); // 7.4.6 IteratorClose(iterator, completion)
  } catch (error) {
    iteratorClose(iterator);
    throw error;
  }
};

var bind$3 = functionBindContext;

var toObject$3 = toObject$b;

var callWithSafeIterationClosing = callWithSafeIterationClosing$1;

var isArrayIteratorMethod = isArrayIteratorMethod$2;

var toLength$6 = toLength$b;

var createProperty$1 = createProperty$3;

var getIteratorMethod = getIteratorMethod$2; // `Array.from` method implementation
// https://tc39.es/ecma262/#sec-array.from


var arrayFrom = function from(arrayLike
/* , mapfn = undefined, thisArg = undefined */
) {
  var O = toObject$3(arrayLike);
  var C = typeof this == 'function' ? this : Array;
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod(O);
  var index = 0;
  var length, result, step, iterator, next, value;
  if (mapping) mapfn = bind$3(mapfn, argumentsLength > 2 ? arguments[2] : undefined, 2); // if the target is not iterable or it's an array with the default iterator - use a simple case

  if (iteratorMethod != undefined && !(C == Array && isArrayIteratorMethod(iteratorMethod))) {
    iterator = iteratorMethod.call(O);
    next = iterator.next;
    result = new C();

    for (; !(step = next.call(iterator)).done; index++) {
      value = mapping ? callWithSafeIterationClosing(iterator, mapfn, [step.value, index], true) : step.value;
      createProperty$1(result, index, value);
    }
  } else {
    length = toLength$6(O.length);
    result = new C(length);

    for (; length > index; index++) {
      value = mapping ? mapfn(O[index], index) : O[index];
      createProperty$1(result, index, value);
    }
  }

  result.length = index;
  return result;
};

var $$e = _export;

var from = arrayFrom;

var checkCorrectnessOfIteration$1 = checkCorrectnessOfIteration$3;

var INCORRECT_ITERATION = !checkCorrectnessOfIteration$1(function (iterable) {
  // eslint-disable-next-line es/no-array-from -- required for testing
  Array.from(iterable);
}); // `Array.from` method
// https://tc39.es/ecma262/#sec-array.from

$$e({
  target: 'Array',
  stat: true,
  forced: INCORRECT_ITERATION
}, {
  from: from
});

var anObject$5 = anObject$h; // `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags


var regexpFlags$1 = function () {
  var that = anObject$5(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};

var regexpStickyHelpers = {};

var fails$b = fails$q; // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError,
// so we use an intermediate function.


function RE(s, f) {
  return RegExp(s, f);
}

regexpStickyHelpers.UNSUPPORTED_Y = fails$b(function () {
  // babel-minify transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
  var re = RE('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});
regexpStickyHelpers.BROKEN_CARET = fails$b(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = RE('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});

/* eslint-disable regexp/no-assertion-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */

/* eslint-disable regexp/no-useless-quantifier -- testing */

var regexpFlags = regexpFlags$1;

var stickyHelpers$3 = regexpStickyHelpers;

var shared = shared$5.exports;

var nativeExec = RegExp.prototype.exec;
var nativeReplace = shared('native-string-replace', String.prototype.replace);
var patchedExec = nativeExec;

var UPDATES_LAST_INDEX_WRONG = function () {
  var re1 = /a/;
  var re2 = /b*/g;
  nativeExec.call(re1, 'a');
  nativeExec.call(re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
}();

var UNSUPPORTED_Y$3 = stickyHelpers$3.UNSUPPORTED_Y || stickyHelpers$3.BROKEN_CARET; // nonparticipating capturing group, copied from es5-shim's String#split patch.

var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;
var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED || UNSUPPORTED_Y$3;

if (PATCH) {
  patchedExec = function exec(str) {
    var re = this;
    var lastIndex, reCopy, match, i;
    var sticky = UNSUPPORTED_Y$3 && re.sticky;
    var flags = regexpFlags.call(re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = flags.replace('y', '');

      if (flags.indexOf('g') === -1) {
        flags += 'g';
      }

      strCopy = String(str).slice(re.lastIndex); // Support anchored sticky behavior.

      if (re.lastIndex > 0 && (!re.multiline || re.multiline && str[re.lastIndex - 1] !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      } // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.


      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }

    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re.lastIndex;
    match = nativeExec.call(sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = match.input.slice(charsAdded);
        match[0] = match[0].slice(charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }

    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      nativeReplace.call(match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    return match;
  };
}

var regexpExec$5 = patchedExec;

var $$d = _export;

var exec = regexpExec$5; // `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec


$$d({
  target: 'RegExp',
  proto: true,
  forced: /./.exec !== exec
}, {
  exec: exec
});

function Similar() {
  this.list = [];
  this.lastItem = undefined;
  this.size = 0;
  return this;
}

Similar.prototype.get = function (key) {
  var index;

  if (this.lastItem && this.isEqual(this.lastItem.key, key)) {
    return this.lastItem.val;
  }

  index = this.indexOf(key);

  if (index >= 0) {
    this.lastItem = this.list[index];
    return this.list[index].val;
  }

  return undefined;
};

Similar.prototype.set = function (key, val) {
  var index;

  if (this.lastItem && this.isEqual(this.lastItem.key, key)) {
    this.lastItem.val = val;
    return this;
  }

  index = this.indexOf(key);

  if (index >= 0) {
    this.lastItem = this.list[index];
    this.list[index].val = val;
    return this;
  }

  this.lastItem = {
    key: key,
    val: val
  };
  this.list.push(this.lastItem);
  this.size++;
  return this;
};

Similar.prototype.delete = function (key) {
  var index;

  if (this.lastItem && this.isEqual(this.lastItem.key, key)) {
    this.lastItem = undefined;
  }

  index = this.indexOf(key);

  if (index >= 0) {
    this.size--;
    return this.list.splice(index, 1)[0];
  }

  return undefined;
}; // important that has() doesn't use get() in case an existing key has a falsy value, in which case has() would return false


Similar.prototype.has = function (key) {
  var index;

  if (this.lastItem && this.isEqual(this.lastItem.key, key)) {
    return true;
  }

  index = this.indexOf(key);

  if (index >= 0) {
    this.lastItem = this.list[index];
    return true;
  }

  return false;
};

Similar.prototype.forEach = function (callback, thisArg) {
  var i;

  for (i = 0; i < this.size; i++) {
    callback.call(thisArg || this, this.list[i].val, this.list[i].key, this);
  }
};

Similar.prototype.indexOf = function (key) {
  var i;

  for (i = 0; i < this.size; i++) {
    if (this.isEqual(this.list[i].key, key)) {
      return i;
    }
  }

  return -1;
}; // check if the numbers are equal, or whether they are both precisely NaN (isNaN returns true for all non-numbers)


Similar.prototype.isEqual = function (val1, val2) {
  return val1 === val2 || val1 !== val1 && val2 !== val2;
};

var similar = Similar;

var mapOrSimilar = function (forceSimilar) {
  if (typeof Map !== 'function' || forceSimilar) {
    var Similar = similar;

    return new Similar();
  } else {
    return new Map();
  }
};

var MapOrSimilar = mapOrSimilar;

var memoizerific = function (limit) {
  var cache = new MapOrSimilar(process.env.FORCE_SIMILAR_INSTEAD_OF_MAP === 'true'),
      lru = [];
  return function (fn) {
    var memoizerific = function () {
      var currentCache = cache,
          newMap,
          fnResult,
          argsLengthMinusOne = arguments.length - 1,
          lruPath = Array(argsLengthMinusOne + 1),
          isMemoized = true,
          i;

      if ((memoizerific.numArgs || memoizerific.numArgs === 0) && memoizerific.numArgs !== argsLengthMinusOne + 1) {
        throw new Error('Memoizerific functions should always be called with the same number of arguments');
      } // loop through each argument to traverse the map tree


      for (i = 0; i < argsLengthMinusOne; i++) {
        lruPath[i] = {
          cacheItem: currentCache,
          arg: arguments[i]
        }; // climb through the hierarchical map tree until the second-last argument has been found, or an argument is missing.
        // if all arguments up to the second-last have been found, this will potentially be a cache hit (determined later)

        if (currentCache.has(arguments[i])) {
          currentCache = currentCache.get(arguments[i]);
          continue;
        }

        isMemoized = false; // make maps until last value

        newMap = new MapOrSimilar(process.env.FORCE_SIMILAR_INSTEAD_OF_MAP === 'true');
        currentCache.set(arguments[i], newMap);
        currentCache = newMap;
      } // we are at the last arg, check if it is really memoized


      if (isMemoized) {
        if (currentCache.has(arguments[argsLengthMinusOne])) {
          fnResult = currentCache.get(arguments[argsLengthMinusOne]);
        } else {
          isMemoized = false;
        }
      } // if the result wasn't memoized, compute it and cache it


      if (!isMemoized) {
        fnResult = fn.apply(null, arguments);
        currentCache.set(arguments[argsLengthMinusOne], fnResult);
      } // if there is a cache limit, purge any extra results


      if (limit > 0) {
        lruPath[argsLengthMinusOne] = {
          cacheItem: currentCache,
          arg: arguments[argsLengthMinusOne]
        };

        if (isMemoized) {
          moveToMostRecentLru(lru, lruPath);
        } else {
          lru.push(lruPath);
        }

        if (lru.length > limit) {
          removeCachedResult(lru.shift());
        }
      }

      memoizerific.wasMemoized = isMemoized;
      memoizerific.numArgs = argsLengthMinusOne + 1;
      return fnResult;
    };

    memoizerific.limit = limit;
    memoizerific.wasMemoized = false;
    memoizerific.cache = cache;
    memoizerific.lru = lru;
    return memoizerific;
  };
}; // move current args to most recent position


function moveToMostRecentLru(lru, lruPath) {
  var lruLen = lru.length,
      lruPathLen = lruPath.length,
      isMatch,
      i,
      ii;

  for (i = 0; i < lruLen; i++) {
    isMatch = true;

    for (ii = 0; ii < lruPathLen; ii++) {
      if (!isEqual(lru[i][ii].arg, lruPath[ii].arg)) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      break;
    }
  }

  lru.push(lru.splice(i, 1)[0]);
} // remove least recently used cache item and all dead branches


function removeCachedResult(removedLru) {
  var removedLruLen = removedLru.length,
      currentLru = removedLru[removedLruLen - 1],
      tmp,
      i;
  currentLru.cacheItem.delete(currentLru.arg); // walk down the tree removing dead branches (size 0) along the way

  for (i = removedLruLen - 2; i >= 0; i--) {
    currentLru = removedLru[i];
    tmp = currentLru.cacheItem.get(currentLru.arg);

    if (!tmp || !tmp.size) {
      currentLru.cacheItem.delete(currentLru.arg);
    } else {
      break;
    }
  }
} // check if the numbers are equal, or whether they are both precisely NaN (isNaN returns true for all non-numbers)


function isEqual(val1, val2) {
  return val1 === val2 || val1 !== val1 && val2 !== val2;
}

/** Detect free variable `global` from Node.js. */

var freeGlobal$3 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal$1 = freeGlobal$3;

var freeGlobal$2 = _freeGlobal$1;
/** Detect free variable `self`. */


var freeSelf$1 = typeof self == 'object' && self && self.Object === Object && self;
/** Used as a reference to the global object. */

var root$b = freeGlobal$2 || freeSelf$1 || Function('return this')();
var _root$1 = root$b;

var root$a = _root$1;
/** Built-in value references. */


var Symbol$9 = root$a.Symbol;
var _Symbol$1 = Symbol$9;

var Symbol$8 = _Symbol$1;
/** Used for built-in method references. */


var objectProto$g = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$c = objectProto$g.hasOwnProperty;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$3 = objectProto$g.toString;
/** Built-in value references. */

var symToStringTag$3 = Symbol$8 ? Symbol$8.toStringTag : undefined;
/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */

function getRawTag$3(value) {
  var isOwn = hasOwnProperty$c.call(value, symToStringTag$3),
      tag = value[symToStringTag$3];

  try {
    value[symToStringTag$3] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$3.call(value);

  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$3] = tag;
    } else {
      delete value[symToStringTag$3];
    }
  }

  return result;
}

var _getRawTag$1 = getRawTag$3;

/** Used for built-in method references. */

var objectProto$f = Object.prototype;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$2 = objectProto$f.toString;
/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */

function objectToString$4(value) {
  return nativeObjectToString$2.call(value);
}

var _objectToString$1 = objectToString$4;

var Symbol$7 = _Symbol$1,
    getRawTag$2 = _getRawTag$1,
    objectToString$3 = _objectToString$1;
/** `Object#toString` result references. */


var nullTag$1 = '[object Null]',
    undefinedTag$1 = '[object Undefined]';
/** Built-in value references. */

var symToStringTag$2 = Symbol$7 ? Symbol$7.toStringTag : undefined;
/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

function baseGetTag$8(value) {
  if (value == null) {
    return value === undefined ? undefinedTag$1 : nullTag$1;
  }

  return symToStringTag$2 && symToStringTag$2 in Object(value) ? getRawTag$2(value) : objectToString$3(value);
}

var _baseGetTag$1 = baseGetTag$8;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$f(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1$1 = isObject$f;

var baseGetTag$7 = _baseGetTag$1,
    isObject$e = isObject_1$1;
/** `Object#toString` result references. */


var asyncTag = '[object AsyncFunction]',
    funcTag$1 = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';
/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */

function isFunction$2(value) {
  if (!isObject$e(value)) {
    return false;
  } // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.


  var tag = baseGetTag$7(value);
  return tag == funcTag$1 || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction$2;

var root$9 = _root$1;
/** Used to detect overreaching core-js shims. */


var coreJsData$1 = root$9['__core-js_shared__'];
var _coreJsData = coreJsData$1;

var coreJsData = _coreJsData;
/** Used to detect methods masquerading as native. */


var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();
/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */


function isMasked$1(func) {
  return !!maskSrcKey && maskSrcKey in func;
}

var _isMasked = isMasked$1;

/** Used for built-in method references. */

var funcProto$2 = Function.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString$2 = funcProto$2.toString;
/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */

function toSource$2(func) {
  if (func != null) {
    try {
      return funcToString$2.call(func);
    } catch (e) {}

    try {
      return func + '';
    } catch (e) {}
  }

  return '';
}

var _toSource = toSource$2;

var isFunction$1 = isFunction_1,
    isMasked = _isMasked,
    isObject$d = isObject_1$1,
    toSource$1 = _toSource;
/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */


var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
/** Used to detect host constructors (Safari). */

var reIsHostCtor = /^\[object .+?Constructor\]$/;
/** Used for built-in method references. */

var funcProto$1 = Function.prototype,
    objectProto$e = Object.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString$1 = funcProto$1.toString;
/** Used to check objects for own properties. */

var hasOwnProperty$b = objectProto$e.hasOwnProperty;
/** Used to detect if a method is native. */

var reIsNative = RegExp('^' + funcToString$1.call(hasOwnProperty$b).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');
/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */

function baseIsNative$1(value) {
  if (!isObject$d(value) || isMasked(value)) {
    return false;
  }

  var pattern = isFunction$1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource$1(value));
}

var _baseIsNative = baseIsNative$1;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function getValue$1(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue$1;

var baseIsNative = _baseIsNative,
    getValue = _getValue;
/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */


function getNative$7(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

var _getNative = getNative$7;

var getNative$6 = _getNative;

var defineProperty$7 = function () {
  try {
    var func = getNative$6(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}();

var _defineProperty$4 = defineProperty$7;

var defineProperty$6 = _defineProperty$4;
/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */


function baseAssignValue$2(object, key, value) {
  if (key == '__proto__' && defineProperty$6) {
    defineProperty$6(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue$2;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */

function createBaseFor$1(fromRight) {
  return function (object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];

      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }

    return object;
  };
}

var _createBaseFor = createBaseFor$1;

var createBaseFor = _createBaseFor;
/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */


var baseFor$1 = createBaseFor();
var _baseFor = baseFor$1;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */

function baseTimes$1(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }

  return result;
}

var _baseTimes = baseTimes$1;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$8(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1$1 = isObjectLike$8;

var baseGetTag$6 = _baseGetTag$1,
    isObjectLike$7 = isObjectLike_1$1;
/** `Object#toString` result references. */


var argsTag$2 = '[object Arguments]';
/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */

function baseIsArguments$1(value) {
  return isObjectLike$7(value) && baseGetTag$6(value) == argsTag$2;
}

var _baseIsArguments = baseIsArguments$1;

var baseIsArguments = _baseIsArguments,
    isObjectLike$6 = isObjectLike_1$1;
/** Used for built-in method references. */


var objectProto$d = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$a = objectProto$d.hasOwnProperty;
/** Built-in value references. */

var propertyIsEnumerable$1 = objectProto$d.propertyIsEnumerable;
/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */

var isArguments$3 = baseIsArguments(function () {
  return arguments;
}()) ? baseIsArguments : function (value) {
  return isObjectLike$6(value) && hasOwnProperty$a.call(value, 'callee') && !propertyIsEnumerable$1.call(value, 'callee');
};
var isArguments_1 = isArguments$3;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray$e = Array.isArray;
var isArray_1 = isArray$e;

var isBuffer$3 = {exports: {}};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */

function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

(function (module, exports) {
var root = _root$1,
    stubFalse = stubFalse_1;
/** Detect free variable `exports`. */


var freeExports = exports && !exports.nodeType && exports;
/** Detect free variable `module`. */

var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
/** Detect the popular CommonJS extension `module.exports`. */

var moduleExports = freeModule && freeModule.exports === freeExports;
/** Built-in value references. */

var Buffer = moduleExports ? root.Buffer : undefined;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;
/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */

var isBuffer = nativeIsBuffer || stubFalse;
module.exports = isBuffer;
}(isBuffer$3, isBuffer$3.exports));

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$2 = 9007199254740991;
/** Used to detect unsigned integer values. */

var reIsUint = /^(?:0|[1-9]\d*)$/;
/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */

function isIndex$3(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$2 : length;
  return !!length && (type == 'number' || type != 'symbol' && reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
}

var _isIndex = isIndex$3;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$1 = 9007199254740991;
/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */

function isLength$3(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength$3;

var baseGetTag$5 = _baseGetTag$1,
    isLength$2 = isLength_1,
    isObjectLike$5 = isObjectLike_1$1;
/** `Object#toString` result references. */


var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag = '[object Function]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag$3 = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    weakMapTag$1 = '[object WeakMap]';
var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';
/** Used to identify `toStringTag` values of typed arrays. */

var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] = typedArrayTags[int8Tag] = typedArrayTags[int16Tag] = typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] = typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] = typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag$1] = typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] = typedArrayTags[dataViewTag$2] = typedArrayTags[dateTag$1] = typedArrayTags[errorTag$1] = typedArrayTags[funcTag] = typedArrayTags[mapTag$2] = typedArrayTags[numberTag$1] = typedArrayTags[objectTag$3] = typedArrayTags[regexpTag$1] = typedArrayTags[setTag$2] = typedArrayTags[stringTag$1] = typedArrayTags[weakMapTag$1] = false;
/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */

function baseIsTypedArray$1(value) {
  return isObjectLike$5(value) && isLength$2(value.length) && !!typedArrayTags[baseGetTag$5(value)];
}

var _baseIsTypedArray = baseIsTypedArray$1;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */

function baseUnary$1(func) {
  return function (value) {
    return func(value);
  };
}

var _baseUnary = baseUnary$1;

var _nodeUtil = {exports: {}};

(function (module, exports) {
var freeGlobal = _freeGlobal$1;
/** Detect free variable `exports`. */


var freeExports = exports && !exports.nodeType && exports;
/** Detect free variable `module`. */

var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;
/** Detect the popular CommonJS extension `module.exports`. */

var moduleExports = freeModule && freeModule.exports === freeExports;
/** Detect free variable `process` from Node.js. */

var freeProcess = moduleExports && freeGlobal.process;
/** Used to access faster Node.js helpers. */

var nodeUtil = function () {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    } // Legacy `process.binding('util')` for Node.js < 10.


    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}();

module.exports = nodeUtil;
}(_nodeUtil, _nodeUtil.exports));

var baseIsTypedArray = _baseIsTypedArray,
    baseUnary = _baseUnary,
    nodeUtil = _nodeUtil.exports;
/* Node.js helper references. */


var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;
/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */

var isTypedArray$2 = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;
var isTypedArray_1 = isTypedArray$2;

var baseTimes = _baseTimes,
    isArguments$2 = isArguments_1,
    isArray$d = isArray_1,
    isBuffer$2 = isBuffer$3.exports,
    isIndex$2 = _isIndex,
    isTypedArray$1 = isTypedArray_1;
/** Used for built-in method references. */


var objectProto$c = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$9 = objectProto$c.hasOwnProperty;
/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */

function arrayLikeKeys$2(value, inherited) {
  var isArr = isArray$d(value),
      isArg = !isArr && isArguments$2(value),
      isBuff = !isArr && !isArg && isBuffer$2(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray$1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$9.call(value, key)) && !(skipIndexes && ( // Safari 9 has enumerable `arguments.length` in strict mode.
    key == 'length' || // Node.js 0.10 has enumerable non-index properties on buffers.
    isBuff && (key == 'offset' || key == 'parent') || // PhantomJS 2 has enumerable non-index properties on typed arrays.
    isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') || // Skip index properties.
    isIndex$2(key, length)))) {
      result.push(key);
    }
  }

  return result;
}

var _arrayLikeKeys = arrayLikeKeys$2;

/** Used for built-in method references. */

var objectProto$b = Object.prototype;
/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */

function isPrototype$2(value) {
  var Ctor = value && value.constructor,
      proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$b;
  return value === proto;
}

var _isPrototype = isPrototype$2;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */

function overArg$2(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg$2;

var overArg$1 = _overArg;
/* Built-in method references for those with the same name as other `lodash` methods. */


var nativeKeys$1 = overArg$1(Object.keys, Object);
var _nativeKeys = nativeKeys$1;

var isPrototype$1 = _isPrototype,
    nativeKeys = _nativeKeys;
/** Used for built-in method references. */


var objectProto$a = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$8 = objectProto$a.hasOwnProperty;
/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function baseKeys$1(object) {
  if (!isPrototype$1(object)) {
    return nativeKeys(object);
  }

  var result = [];

  for (var key in Object(object)) {
    if (hasOwnProperty$8.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }

  return result;
}

var _baseKeys = baseKeys$1;

var isFunction = isFunction_1,
    isLength$1 = isLength_1;
/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */


function isArrayLike$2(value) {
  return value != null && isLength$1(value.length) && !isFunction(value);
}

var isArrayLike_1 = isArrayLike$2;

var arrayLikeKeys$1 = _arrayLikeKeys,
    baseKeys = _baseKeys,
    isArrayLike$1 = isArrayLike_1;
/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */


function keys$6(object) {
  return isArrayLike$1(object) ? arrayLikeKeys$1(object) : baseKeys(object);
}

var keys_1 = keys$6;

var baseFor = _baseFor,
    keys$5 = keys_1;
/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */


function baseForOwn$1(object, iteratee) {
  return object && baseFor(object, iteratee, keys$5);
}

var _baseForOwn = baseForOwn$1;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */

function listCacheClear$1() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear$1;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */

function eq$3(value, other) {
  return value === other || value !== value && other !== other;
}

var eq_1 = eq$3;

var eq$2 = eq_1;
/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */


function assocIndexOf$4(array, key) {
  var length = array.length;

  while (length--) {
    if (eq$2(array[length][0], key)) {
      return length;
    }
  }

  return -1;
}

var _assocIndexOf = assocIndexOf$4;

var assocIndexOf$3 = _assocIndexOf;
/** Used for built-in method references. */


var arrayProto = Array.prototype;
/** Built-in value references. */

var splice = arrayProto.splice;
/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function listCacheDelete$1(key) {
  var data = this.__data__,
      index = assocIndexOf$3(data, key);

  if (index < 0) {
    return false;
  }

  var lastIndex = data.length - 1;

  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }

  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete$1;

var assocIndexOf$2 = _assocIndexOf;
/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */


function listCacheGet$1(key) {
  var data = this.__data__,
      index = assocIndexOf$2(data, key);
  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet$1;

var assocIndexOf$1 = _assocIndexOf;
/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */


function listCacheHas$1(key) {
  return assocIndexOf$1(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas$1;

var assocIndexOf = _assocIndexOf;
/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */


function listCacheSet$1(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }

  return this;
}

var _listCacheSet = listCacheSet$1;

var listCacheClear = _listCacheClear,
    listCacheDelete = _listCacheDelete,
    listCacheGet = _listCacheGet,
    listCacheHas = _listCacheHas,
    listCacheSet = _listCacheSet;
/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function ListCache$4(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `ListCache`.


ListCache$4.prototype.clear = listCacheClear;
ListCache$4.prototype['delete'] = listCacheDelete;
ListCache$4.prototype.get = listCacheGet;
ListCache$4.prototype.has = listCacheHas;
ListCache$4.prototype.set = listCacheSet;
var _ListCache = ListCache$4;

var ListCache$3 = _ListCache;
/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */


function stackClear$1() {
  this.__data__ = new ListCache$3();
  this.size = 0;
}

var _stackClear = stackClear$1;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function stackDelete$1(key) {
  var data = this.__data__,
      result = data['delete'](key);
  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete$1;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function stackGet$1(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet$1;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function stackHas$1(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas$1;

var getNative$5 = _getNative,
    root$8 = _root$1;
/* Built-in method references that are verified to be native. */


var Map$4 = getNative$5(root$8, 'Map');
var _Map = Map$4;

var getNative$4 = _getNative;
/* Built-in method references that are verified to be native. */


var nativeCreate$4 = getNative$4(Object, 'create');
var _nativeCreate = nativeCreate$4;

var nativeCreate$3 = _nativeCreate;
/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */


function hashClear$1() {
  this.__data__ = nativeCreate$3 ? nativeCreate$3(null) : {};
  this.size = 0;
}

var _hashClear = hashClear$1;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function hashDelete$1(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete$1;

var nativeCreate$2 = _nativeCreate;
/** Used to stand-in for `undefined` hash values. */


var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';
/** Used for built-in method references. */

var objectProto$9 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$7 = objectProto$9.hasOwnProperty;
/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function hashGet$1(key) {
  var data = this.__data__;

  if (nativeCreate$2) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }

  return hasOwnProperty$7.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet$1;

var nativeCreate$1 = _nativeCreate;
/** Used for built-in method references. */


var objectProto$8 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$6 = objectProto$8.hasOwnProperty;
/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function hashHas$1(key) {
  var data = this.__data__;
  return nativeCreate$1 ? data[key] !== undefined : hasOwnProperty$6.call(data, key);
}

var _hashHas = hashHas$1;

var nativeCreate = _nativeCreate;
/** Used to stand-in for `undefined` hash values. */


var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';
/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */

function hashSet$1(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = nativeCreate && value === undefined ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet$1;

var hashClear = _hashClear,
    hashDelete = _hashDelete,
    hashGet = _hashGet,
    hashHas = _hashHas,
    hashSet = _hashSet;
/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function Hash$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `Hash`.


Hash$1.prototype.clear = hashClear;
Hash$1.prototype['delete'] = hashDelete;
Hash$1.prototype.get = hashGet;
Hash$1.prototype.has = hashHas;
Hash$1.prototype.set = hashSet;
var _Hash = Hash$1;

var Hash = _Hash,
    ListCache$2 = _ListCache,
    Map$3 = _Map;
/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */


function mapCacheClear$1() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash(),
    'map': new (Map$3 || ListCache$2)(),
    'string': new Hash()
  };
}

var _mapCacheClear = mapCacheClear$1;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */

function isKeyable$1(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}

var _isKeyable = isKeyable$1;

var isKeyable = _isKeyable;
/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */


function getMapData$4(map, key) {
  var data = map.__data__;
  return isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}

var _getMapData = getMapData$4;

var getMapData$3 = _getMapData;
/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */


function mapCacheDelete$1(key) {
  var result = getMapData$3(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete$1;

var getMapData$2 = _getMapData;
/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */


function mapCacheGet$1(key) {
  return getMapData$2(this, key).get(key);
}

var _mapCacheGet = mapCacheGet$1;

var getMapData$1 = _getMapData;
/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */


function mapCacheHas$1(key) {
  return getMapData$1(this, key).has(key);
}

var _mapCacheHas = mapCacheHas$1;

var getMapData = _getMapData;
/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */


function mapCacheSet$1(key, value) {
  var data = getMapData(this, key),
      size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet$1;

var mapCacheClear = _mapCacheClear,
    mapCacheDelete = _mapCacheDelete,
    mapCacheGet = _mapCacheGet,
    mapCacheHas = _mapCacheHas,
    mapCacheSet = _mapCacheSet;
/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function MapCache$3(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;
  this.clear();

  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
} // Add methods to `MapCache`.


MapCache$3.prototype.clear = mapCacheClear;
MapCache$3.prototype['delete'] = mapCacheDelete;
MapCache$3.prototype.get = mapCacheGet;
MapCache$3.prototype.has = mapCacheHas;
MapCache$3.prototype.set = mapCacheSet;
var _MapCache = MapCache$3;

var ListCache$1 = _ListCache,
    Map$2 = _Map,
    MapCache$2 = _MapCache;
/** Used as the size to enable large array optimizations. */


var LARGE_ARRAY_SIZE = 200;
/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */

function stackSet$1(key, value) {
  var data = this.__data__;

  if (data instanceof ListCache$1) {
    var pairs = data.__data__;

    if (!Map$2 || pairs.length < LARGE_ARRAY_SIZE - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }

    data = this.__data__ = new MapCache$2(pairs);
  }

  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet$1;

var ListCache = _ListCache,
    stackClear = _stackClear,
    stackDelete = _stackDelete,
    stackGet = _stackGet,
    stackHas = _stackHas,
    stackSet = _stackSet;
/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */


function Stack$2(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
} // Add methods to `Stack`.


Stack$2.prototype.clear = stackClear;
Stack$2.prototype['delete'] = stackDelete;
Stack$2.prototype.get = stackGet;
Stack$2.prototype.has = stackHas;
Stack$2.prototype.set = stackSet;
var _Stack = Stack$2;

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED = '__lodash_hash_undefined__';
/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */

function setCacheAdd$1(value) {
  this.__data__.set(value, HASH_UNDEFINED);

  return this;
}

var _setCacheAdd = setCacheAdd$1;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */

function setCacheHas$1(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas$1;

var MapCache$1 = _MapCache,
    setCacheAdd = _setCacheAdd,
    setCacheHas = _setCacheHas;
/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */


function SetCache$1(values) {
  var index = -1,
      length = values == null ? 0 : values.length;
  this.__data__ = new MapCache$1();

  while (++index < length) {
    this.add(values[index]);
  }
} // Add methods to `SetCache`.


SetCache$1.prototype.add = SetCache$1.prototype.push = setCacheAdd;
SetCache$1.prototype.has = setCacheHas;
var _SetCache = SetCache$1;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */

function arraySome$1(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }

  return false;
}

var _arraySome = arraySome$1;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function cacheHas$1(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas$1;

var SetCache = _SetCache,
    arraySome = _arraySome,
    cacheHas = _cacheHas;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;
/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */

function equalArrays$2(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$5,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  } // Check that cyclic values are equal.


  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);

  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }

  var index = -1,
      result = true,
      seen = bitmask & COMPARE_UNORDERED_FLAG$3 ? new SetCache() : undefined;
  stack.set(array, other);
  stack.set(other, array); // Ignore non-index properties.

  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, arrValue, index, other, array, stack) : customizer(arrValue, othValue, index, array, other, stack);
    }

    if (compared !== undefined) {
      if (compared) {
        continue;
      }

      result = false;
      break;
    } // Recursively compare arrays (susceptible to call stack limits).


    if (seen) {
      if (!arraySome(other, function (othValue, othIndex) {
        if (!cacheHas(seen, othIndex) && (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
          return seen.push(othIndex);
        }
      })) {
        result = false;
        break;
      }
    } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
      result = false;
      break;
    }
  }

  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays$2;

var root$7 = _root$1;
/** Built-in value references. */


var Uint8Array$2 = root$7.Uint8Array;
var _Uint8Array = Uint8Array$2;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */

function mapToArray$1(map) {
  var index = -1,
      result = Array(map.size);
  map.forEach(function (value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray$1;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */

function setToArray$1(set) {
  var index = -1,
      result = Array(set.size);
  set.forEach(function (value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray$1;

var Symbol$6 = _Symbol$1,
    Uint8Array$1 = _Uint8Array,
    eq$1 = eq_1,
    equalArrays$1 = _equalArrays,
    mapToArray = _mapToArray,
    setToArray = _setToArray;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;
/** `Object#toString` result references. */

var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag$1 = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag = '[object String]',
    symbolTag$2 = '[object Symbol]';
var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]';
/** Used to convert symbols to primitives and strings. */

var symbolProto$1 = Symbol$6 ? Symbol$6.prototype : undefined,
    symbolValueOf = symbolProto$1 ? symbolProto$1.valueOf : undefined;
/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalByTag$1(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$1:
      if (object.byteLength != other.byteLength || object.byteOffset != other.byteOffset) {
        return false;
      }

      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if (object.byteLength != other.byteLength || !equalFunc(new Uint8Array$1(object), new Uint8Array$1(other))) {
        return false;
      }

      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq$1(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == other + '';

    case mapTag$1:
      var convert = mapToArray;

    case setTag$1:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$4;
      convert || (convert = setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      } // Assume cyclic values are equal.


      var stacked = stack.get(object);

      if (stacked) {
        return stacked == other;
      }

      bitmask |= COMPARE_UNORDERED_FLAG$2; // Recursively compare objects (susceptible to call stack limits).

      stack.set(object, other);
      var result = equalArrays$1(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$2:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }

  }

  return false;
}

var _equalByTag = equalByTag$1;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */

function arrayPush$4(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }

  return array;
}

var _arrayPush = arrayPush$4;

var arrayPush$3 = _arrayPush,
    isArray$c = isArray_1;
/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */


function baseGetAllKeys$2(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$c(object) ? result : arrayPush$3(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys$2;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */

function arrayFilter$1(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];

    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }

  return result;
}

var _arrayFilter = arrayFilter$1;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */

function stubArray$2() {
  return [];
}

var stubArray_1 = stubArray$2;

var arrayFilter = _arrayFilter,
    stubArray$1 = stubArray_1;
/** Used for built-in method references. */


var objectProto$7 = Object.prototype;
/** Built-in value references. */

var propertyIsEnumerable = objectProto$7.propertyIsEnumerable;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeGetSymbols$1 = Object.getOwnPropertySymbols;
/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */

var getSymbols$2 = !nativeGetSymbols$1 ? stubArray$1 : function (object) {
  if (object == null) {
    return [];
  }

  object = Object(object);
  return arrayFilter(nativeGetSymbols$1(object), function (symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};
var _getSymbols = getSymbols$2;

var baseGetAllKeys$1 = _baseGetAllKeys,
    getSymbols$1 = _getSymbols,
    keys$4 = keys_1;
/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */


function getAllKeys$1(object) {
  return baseGetAllKeys$1(object, keys$4, getSymbols$1);
}

var _getAllKeys = getAllKeys$1;

var getAllKeys = _getAllKeys;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$3 = 1;
/** Used for built-in method references. */

var objectProto$6 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$5 = objectProto$6.hasOwnProperty;
/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function equalObjects$1(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      objProps = getAllKeys(object),
      objLength = objProps.length,
      othProps = getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }

  var index = objLength;

  while (index--) {
    var key = objProps[index];

    if (!(isPartial ? key in other : hasOwnProperty$5.call(other, key))) {
      return false;
    }
  } // Check that cyclic values are equal.


  var objStacked = stack.get(object);
  var othStacked = stack.get(other);

  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }

  var result = true;
  stack.set(object, other);
  stack.set(other, object);
  var skipCtor = isPartial;

  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial ? customizer(othValue, objValue, key, other, object, stack) : customizer(objValue, othValue, key, object, other, stack);
    } // Recursively compare objects (susceptible to call stack limits).


    if (!(compared === undefined ? objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack) : compared)) {
      result = false;
      break;
    }

    skipCtor || (skipCtor = key == 'constructor');
  }

  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor; // Non `Object` object instances with different constructors are not equal.

    if (objCtor != othCtor && 'constructor' in object && 'constructor' in other && !(typeof objCtor == 'function' && objCtor instanceof objCtor && typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }

  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects$1;

var getNative$3 = _getNative,
    root$6 = _root$1;
/* Built-in method references that are verified to be native. */


var DataView$2 = getNative$3(root$6, 'DataView');
var _DataView = DataView$2;

var getNative$2 = _getNative,
    root$5 = _root$1;
/* Built-in method references that are verified to be native. */


var Promise$2 = getNative$2(root$5, 'Promise');
var _Promise = Promise$2;

var getNative$1 = _getNative,
    root$4 = _root$1;
/* Built-in method references that are verified to be native. */


var Set$2 = getNative$1(root$4, 'Set');
var _Set = Set$2;

var getNative = _getNative,
    root$3 = _root$1;
/* Built-in method references that are verified to be native. */


var WeakMap$2 = getNative(root$3, 'WeakMap');
var _WeakMap = WeakMap$2;

var DataView$1 = _DataView,
    Map$1 = _Map,
    Promise$1 = _Promise,
    Set$1 = _Set,
    WeakMap$1 = _WeakMap,
    baseGetTag$4 = _baseGetTag$1,
    toSource = _toSource;
/** `Object#toString` result references. */


var mapTag = '[object Map]',
    objectTag$2 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag = '[object Set]',
    weakMapTag = '[object WeakMap]';
var dataViewTag = '[object DataView]';
/** Used to detect maps, sets, and weakmaps. */

var dataViewCtorString = toSource(DataView$1),
    mapCtorString = toSource(Map$1),
    promiseCtorString = toSource(Promise$1),
    setCtorString = toSource(Set$1),
    weakMapCtorString = toSource(WeakMap$1);
/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

var getTag$1 = baseGetTag$4; // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.

if (DataView$1 && getTag$1(new DataView$1(new ArrayBuffer(1))) != dataViewTag || Map$1 && getTag$1(new Map$1()) != mapTag || Promise$1 && getTag$1(Promise$1.resolve()) != promiseTag || Set$1 && getTag$1(new Set$1()) != setTag || WeakMap$1 && getTag$1(new WeakMap$1()) != weakMapTag) {
  getTag$1 = function (value) {
    var result = baseGetTag$4(value),
        Ctor = result == objectTag$2 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString:
          return dataViewTag;

        case mapCtorString:
          return mapTag;

        case promiseCtorString:
          return promiseTag;

        case setCtorString:
          return setTag;

        case weakMapCtorString:
          return weakMapTag;
      }
    }

    return result;
  };
}

var _getTag = getTag$1;

var Stack$1 = _Stack,
    equalArrays = _equalArrays,
    equalByTag = _equalByTag,
    equalObjects = _equalObjects,
    getTag = _getTag,
    isArray$b = isArray_1,
    isBuffer$1 = isBuffer$3.exports,
    isTypedArray = isTypedArray_1;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$2 = 1;
/** `Object#toString` result references. */

var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    objectTag$1 = '[object Object]';
/** Used for built-in method references. */

var objectProto$5 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$4 = objectProto$5.hasOwnProperty;
/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */

function baseIsEqualDeep$1(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$b(object),
      othIsArr = isArray$b(other),
      objTag = objIsArr ? arrayTag : getTag(object),
      othTag = othIsArr ? arrayTag : getTag(other);
  objTag = objTag == argsTag ? objectTag$1 : objTag;
  othTag = othTag == argsTag ? objectTag$1 : othTag;
  var objIsObj = objTag == objectTag$1,
      othIsObj = othTag == objectTag$1,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$1(object)) {
    if (!isBuffer$1(other)) {
      return false;
    }

    objIsArr = true;
    objIsObj = false;
  }

  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack$1());
    return objIsArr || isTypedArray(object) ? equalArrays(object, other, bitmask, customizer, equalFunc, stack) : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }

  if (!(bitmask & COMPARE_PARTIAL_FLAG$2)) {
    var objIsWrapped = objIsObj && hasOwnProperty$4.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$4.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;
      stack || (stack = new Stack$1());
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }

  if (!isSameTag) {
    return false;
  }

  stack || (stack = new Stack$1());
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep$1;

var baseIsEqualDeep = _baseIsEqualDeep,
    isObjectLike$4 = isObjectLike_1$1;
/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */


function baseIsEqual$2(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }

  if (value == null || other == null || !isObjectLike$4(value) && !isObjectLike$4(other)) {
    return value !== value && other !== other;
  }

  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual$2, stack);
}

var _baseIsEqual = baseIsEqual$2;

var Stack = _Stack,
    baseIsEqual$1 = _baseIsEqual;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;
/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */

function baseIsMatch$1(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }

  object = Object(object);

  while (index--) {
    var data = matchData[index];

    if (noCustomizer && data[2] ? data[1] !== object[data[0]] : !(data[0] in object)) {
      return false;
    }
  }

  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack();

      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }

      if (!(result === undefined ? baseIsEqual$1(srcValue, objValue, COMPARE_PARTIAL_FLAG$1 | COMPARE_UNORDERED_FLAG$1, customizer, stack) : result)) {
        return false;
      }
    }
  }

  return true;
}

var _baseIsMatch = baseIsMatch$1;

var isObject$c = isObject_1$1;
/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */


function isStrictComparable$2(value) {
  return value === value && !isObject$c(value);
}

var _isStrictComparable = isStrictComparable$2;

var isStrictComparable$1 = _isStrictComparable,
    keys$3 = keys_1;
/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */


function getMatchData$1(object) {
  var result = keys$3(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];
    result[length] = [key, value, isStrictComparable$1(value)];
  }

  return result;
}

var _getMatchData = getMatchData$1;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */

function matchesStrictComparable$2(key, srcValue) {
  return function (object) {
    if (object == null) {
      return false;
    }

    return object[key] === srcValue && (srcValue !== undefined || key in Object(object));
  };
}

var _matchesStrictComparable = matchesStrictComparable$2;

var baseIsMatch = _baseIsMatch,
    getMatchData = _getMatchData,
    matchesStrictComparable$1 = _matchesStrictComparable;
/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */


function baseMatches$1(source) {
  var matchData = getMatchData(source);

  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable$1(matchData[0][0], matchData[0][1]);
  }

  return function (object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches$1;

var baseGetTag$3 = _baseGetTag$1,
    isObjectLike$3 = isObjectLike_1$1;
/** `Object#toString` result references. */


var symbolTag$1 = '[object Symbol]';
/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */

function isSymbol$6(value) {
  return typeof value == 'symbol' || isObjectLike$3(value) && baseGetTag$3(value) == symbolTag$1;
}

var isSymbol_1$1 = isSymbol$6;

var isArray$a = isArray_1,
    isSymbol$5 = isSymbol_1$1;
/** Used to match property names within property paths. */


var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;
/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */

function isKey$3(value, object) {
  if (isArray$a(value)) {
    return false;
  }

  var type = typeof value;

  if (type == 'number' || type == 'symbol' || type == 'boolean' || value == null || isSymbol$5(value)) {
    return true;
  }

  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) || object != null && value in Object(object);
}

var _isKey = isKey$3;

var MapCache = _MapCache;
/** Error message constants. */


var FUNC_ERROR_TEXT$1 = 'Expected a function';
/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */

function memoize$1(func, resolver) {
  if (typeof func != 'function' || resolver != null && typeof resolver != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }

  var memoized = function () {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }

    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };

  memoized.cache = new (memoize$1.Cache || MapCache)();
  return memoized;
} // Expose `MapCache`.


memoize$1.Cache = MapCache;
var memoize_1 = memoize$1;

var memoize = memoize_1;
/** Used as the maximum memoize cache size. */


var MAX_MEMOIZE_SIZE = 500;
/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */

function memoizeCapped$1(func) {
  var result = memoize(func, function (key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }

    return key;
  });
  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped$1;

var memoizeCapped = _memoizeCapped;
/** Used to match property names within property paths. */


var rePropName$1 = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;
/** Used to match backslashes in property paths. */

var reEscapeChar$1 = /\\(\\)?/g;
/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */

var stringToPath$2 = memoizeCapped(function (string) {
  var result = [];

  if (string.charCodeAt(0) === 46
  /* . */
  ) {
      result.push('');
    }

  string.replace(rePropName$1, function (match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar$1, '$1') : number || match);
  });
  return result;
});
var _stringToPath = stringToPath$2;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

function arrayMap$2(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }

  return result;
}

var _arrayMap = arrayMap$2;

var Symbol$5 = _Symbol$1,
    arrayMap$1 = _arrayMap,
    isArray$9 = isArray_1,
    isSymbol$4 = isSymbol_1$1;
/** Used as references for various `Number` constants. */


var INFINITY$1 = 1 / 0;
/** Used to convert symbols to primitives and strings. */

var symbolProto = Symbol$5 ? Symbol$5.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;
/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */

function baseToString$1(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }

  if (isArray$9(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap$1(value, baseToString$1) + '';
  }

  if (isSymbol$4(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }

  var result = value + '';
  return result == '0' && 1 / value == -INFINITY$1 ? '-0' : result;
}

var _baseToString = baseToString$1;

var baseToString = _baseToString;
/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */


function toString$1(value) {
  return value == null ? '' : baseToString(value);
}

var toString_1 = toString$1;

var isArray$8 = isArray_1,
    isKey$2 = _isKey,
    stringToPath$1 = _stringToPath,
    toString = toString_1;
/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */


function castPath$4(value, object) {
  if (isArray$8(value)) {
    return value;
  }

  return isKey$2(value, object) ? [value] : stringToPath$1(toString(value));
}

var _castPath = castPath$4;

var isSymbol$3 = isSymbol_1$1;
/** Used as references for various `Number` constants. */


var INFINITY = 1 / 0;
/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */

function toKey$5(value) {
  if (typeof value == 'string' || isSymbol$3(value)) {
    return value;
  }

  var result = value + '';
  return result == '0' && 1 / value == -INFINITY ? '-0' : result;
}

var _toKey = toKey$5;

var castPath$3 = _castPath,
    toKey$4 = _toKey;
/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */


function baseGet$3(object, path) {
  path = castPath$3(path, object);
  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey$4(path[index++])];
  }

  return index && index == length ? object : undefined;
}

var _baseGet = baseGet$3;

var baseGet$2 = _baseGet;
/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */


function get$1(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet$2(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get$1;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */

function baseHasIn$1(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn$1;

var castPath$2 = _castPath,
    isArguments$1 = isArguments_1,
    isArray$7 = isArray_1,
    isIndex$1 = _isIndex,
    isLength = isLength_1,
    toKey$3 = _toKey;
/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */


function hasPath$1(object, path, hasFunc) {
  path = castPath$2(path, object);
  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey$3(path[index]);

    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }

    object = object[key];
  }

  if (result || ++index != length) {
    return result;
  }

  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex$1(key, length) && (isArray$7(object) || isArguments$1(object));
}

var _hasPath = hasPath$1;

var baseHasIn = _baseHasIn,
    hasPath = _hasPath;
/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */


function hasIn$2(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

var hasIn_1 = hasIn$2;

var baseIsEqual = _baseIsEqual,
    get = get_1,
    hasIn$1 = hasIn_1,
    isKey$1 = _isKey,
    isStrictComparable = _isStrictComparable,
    matchesStrictComparable = _matchesStrictComparable,
    toKey$2 = _toKey;
/** Used to compose bitmasks for value comparisons. */


var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;
/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */

function baseMatchesProperty$1(path, srcValue) {
  if (isKey$1(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey$2(path), srcValue);
  }

  return function (object) {
    var objValue = get(object, path);
    return objValue === undefined && objValue === srcValue ? hasIn$1(object, path) : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
  };
}

var _baseMatchesProperty = baseMatchesProperty$1;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */

function identity$2(value) {
  return value;
}

var identity_1 = identity$2;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */

function baseProperty$1(key) {
  return function (object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty$1;

var baseGet$1 = _baseGet;
/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */


function basePropertyDeep$1(path) {
  return function (object) {
    return baseGet$1(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep$1;

var baseProperty = _baseProperty,
    basePropertyDeep = _basePropertyDeep,
    isKey = _isKey,
    toKey$1 = _toKey;
/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */


function property$1(path) {
  return isKey(path) ? baseProperty(toKey$1(path)) : basePropertyDeep(path);
}

var property_1 = property$1;

var baseMatches = _baseMatches,
    baseMatchesProperty = _baseMatchesProperty,
    identity$1 = identity_1,
    isArray$6 = isArray_1,
    property = property_1;
/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */


function baseIteratee$2(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }

  if (value == null) {
    return identity$1;
  }

  if (typeof value == 'object') {
    return isArray$6(value) ? baseMatchesProperty(value[0], value[1]) : baseMatches(value);
  }

  return property(value);
}

var _baseIteratee = baseIteratee$2;

var baseAssignValue$1 = _baseAssignValue,
    baseForOwn = _baseForOwn,
    baseIteratee$1 = _baseIteratee;
/**
 * Creates an object with the same keys as `object` and values generated
 * by running each own enumerable string keyed property of `object` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, key, object).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Object
 * @param {Object} object The object to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Object} Returns the new mapped object.
 * @see _.mapKeys
 * @example
 *
 * var users = {
 *   'fred':    { 'user': 'fred',    'age': 40 },
 *   'pebbles': { 'user': 'pebbles', 'age': 1 }
 * };
 *
 * _.mapValues(users, function(o) { return o.age; });
 * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
 *
 * // The `_.property` iteratee shorthand.
 * _.mapValues(users, 'age');
 * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
 */


function mapValues(object, iteratee) {
  var result = {};
  iteratee = baseIteratee$1(iteratee);
  baseForOwn(object, function (value, key, object) {
    baseAssignValue$1(result, key, iteratee(value, key, object));
  });
  return result;
}

var mapValues_1 = mapValues;

var baseAssignValue = _baseAssignValue,
    eq = eq_1;
/** Used for built-in method references. */


var objectProto$4 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$3 = objectProto$4.hasOwnProperty;
/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */

function assignValue$1(object, key, value) {
  var objValue = object[key];

  if (!(hasOwnProperty$3.call(object, key) && eq(objValue, value)) || value === undefined && !(key in object)) {
    baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue$1;

var assignValue = _assignValue,
    castPath$1 = _castPath,
    isIndex = _isIndex,
    isObject$b = isObject_1$1,
    toKey = _toKey;
/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */


function baseSet$1(object, path, value, customizer) {
  if (!isObject$b(object)) {
    return object;
  }

  path = castPath$1(path, object);
  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = toKey(path[index]),
        newValue = value;

    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return object;
    }

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;

      if (newValue === undefined) {
        newValue = isObject$b(objValue) ? objValue : isIndex(path[index + 1]) ? [] : {};
      }
    }

    assignValue(nested, key, newValue);
    nested = nested[key];
  }

  return object;
}

var _baseSet = baseSet$1;

var baseGet = _baseGet,
    baseSet = _baseSet,
    castPath = _castPath;
/**
 * The base implementation of  `_.pickBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The source object.
 * @param {string[]} paths The property paths to pick.
 * @param {Function} predicate The function invoked per property.
 * @returns {Object} Returns the new object.
 */


function basePickBy$2(object, paths, predicate) {
  var index = -1,
      length = paths.length,
      result = {};

  while (++index < length) {
    var path = paths[index],
        value = baseGet(object, path);

    if (predicate(value, path)) {
      baseSet(result, castPath(path, object), value);
    }
  }

  return result;
}

var _basePickBy = basePickBy$2;

var basePickBy$1 = _basePickBy,
    hasIn = hasIn_1;
/**
 * The base implementation of `_.pick` without support for individual
 * property identifiers.
 *
 * @private
 * @param {Object} object The source object.
 * @param {string[]} paths The property paths to pick.
 * @returns {Object} Returns the new object.
 */


function basePick$1(object, paths) {
  return basePickBy$1(object, paths, function (value, path) {
    return hasIn(object, path);
  });
}

var _basePick = basePick$1;

var Symbol$4 = _Symbol$1,
    isArguments = isArguments_1,
    isArray$5 = isArray_1;
/** Built-in value references. */


var spreadableSymbol = Symbol$4 ? Symbol$4.isConcatSpreadable : undefined;
/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */

function isFlattenable$1(value) {
  return isArray$5(value) || isArguments(value) || !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable$1;

var arrayPush$2 = _arrayPush,
    isFlattenable = _isFlattenable;
/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */


function baseFlatten$1(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;
  predicate || (predicate = isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];

    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten$1(value, depth - 1, predicate, isStrict, result);
      } else {
        arrayPush$2(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }

  return result;
}

var _baseFlatten = baseFlatten$1;

var baseFlatten = _baseFlatten;
/**
 * Flattens `array` a single level deep.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to flatten.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 */


function flatten$1(array) {
  var length = array == null ? 0 : array.length;
  return length ? baseFlatten(array, 1) : [];
}

var flatten_1 = flatten$1;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */

function apply$1(func, thisArg, args) {
  switch (args.length) {
    case 0:
      return func.call(thisArg);

    case 1:
      return func.call(thisArg, args[0]);

    case 2:
      return func.call(thisArg, args[0], args[1]);

    case 3:
      return func.call(thisArg, args[0], args[1], args[2]);
  }

  return func.apply(thisArg, args);
}

var _apply = apply$1;

var apply = _apply;
/* Built-in method references for those with the same name as other `lodash` methods. */


var nativeMax$1 = Math.max;
/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */

function overRest$1(func, start, transform) {
  start = nativeMax$1(start === undefined ? func.length - 1 : start, 0);
  return function () {
    var args = arguments,
        index = -1,
        length = nativeMax$1(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }

    index = -1;
    var otherArgs = Array(start + 1);

    while (++index < start) {
      otherArgs[index] = args[index];
    }

    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

var _overRest = overRest$1;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */

function constant$1(value) {
  return function () {
    return value;
  };
}

var constant_1 = constant$1;

var constant = constant_1,
    defineProperty$5 = _defineProperty$4,
    identity = identity_1;
/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */


var baseSetToString$1 = !defineProperty$5 ? identity : function (func, string) {
  return defineProperty$5(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};
var _baseSetToString = baseSetToString$1;

/** Used to detect hot functions by number of calls within a span of milliseconds. */

var HOT_COUNT = 800,
    HOT_SPAN = 16;
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeNow = Date.now;
/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */

function shortOut$1(func) {
  var count = 0,
      lastCalled = 0;
  return function () {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);
    lastCalled = stamp;

    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }

    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut$1;

var baseSetToString = _baseSetToString,
    shortOut = _shortOut;
/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */


var setToString$1 = shortOut(baseSetToString);
var _setToString = setToString$1;

var flatten = flatten_1,
    overRest = _overRest,
    setToString = _setToString;
/**
 * A specialized version of `baseRest` which flattens the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @returns {Function} Returns the new function.
 */


function flatRest$1(func) {
  return setToString(overRest(func, undefined, flatten), func + '');
}

var _flatRest = flatRest$1;

var basePick = _basePick,
    flatRest = _flatRest;
/**
 * Creates an object composed of the picked `object` properties.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The source object.
 * @param {...(string|string[])} [paths] The property paths to pick.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pick(object, ['a', 'c']);
 * // => { 'a': 1, 'c': 3 }
 */


var pick = flatRest(function (object, paths) {
  return object == null ? {} : basePick(object, paths);
});
var pick_1 = pick;

/* jshint node: true */

function makeArrayFrom(obj) {
  return Array.prototype.slice.apply(obj);
}

var PENDING = "pending",
    RESOLVED = "resolved",
    REJECTED = "rejected";

function SynchronousPromise(handler) {
  this.status = PENDING;
  this._continuations = [];
  this._parent = null;
  this._paused = false;

  if (handler) {
    handler.call(this, this._continueWith.bind(this), this._failWith.bind(this));
  }
}

function looksLikeAPromise(obj) {
  return obj && typeof obj.then === "function";
}

function passThrough(value) {
  return value;
}

SynchronousPromise.prototype = {
  then: function (nextFn, catchFn) {
    var next = SynchronousPromise.unresolved()._setParent(this);

    if (this._isRejected()) {
      if (this._paused) {
        this._continuations.push({
          promise: next,
          nextFn: nextFn,
          catchFn: catchFn
        });

        return next;
      }

      if (catchFn) {
        try {
          var catchResult = catchFn(this._error);

          if (looksLikeAPromise(catchResult)) {
            this._chainPromiseData(catchResult, next);

            return next;
          } else {
            return SynchronousPromise.resolve(catchResult)._setParent(this);
          }
        } catch (e) {
          return SynchronousPromise.reject(e)._setParent(this);
        }
      }

      return SynchronousPromise.reject(this._error)._setParent(this);
    }

    this._continuations.push({
      promise: next,
      nextFn: nextFn,
      catchFn: catchFn
    });

    this._runResolutions();

    return next;
  },
  catch: function (handler) {
    if (this._isResolved()) {
      return SynchronousPromise.resolve(this._data)._setParent(this);
    }

    var next = SynchronousPromise.unresolved()._setParent(this);

    this._continuations.push({
      promise: next,
      catchFn: handler
    });

    this._runRejections();

    return next;
  },
  finally: function (callback) {
    var ran = false;

    function runFinally(result, err) {
      if (!ran) {
        ran = true;

        if (!callback) {
          callback = passThrough;
        }

        var callbackResult = callback(result);

        if (looksLikeAPromise(callbackResult)) {
          return callbackResult.then(function () {
            if (err) {
              throw err;
            }

            return result;
          });
        } else {
          return result;
        }
      }
    }

    return this.then(function (result) {
      return runFinally(result);
    }).catch(function (err) {
      return runFinally(null, err);
    });
  },
  pause: function () {
    this._paused = true;
    return this;
  },
  resume: function () {
    var firstPaused = this._findFirstPaused();

    if (firstPaused) {
      firstPaused._paused = false;

      firstPaused._runResolutions();

      firstPaused._runRejections();
    }

    return this;
  },
  _findAncestry: function () {
    return this._continuations.reduce(function (acc, cur) {
      if (cur.promise) {
        var node = {
          promise: cur.promise,
          children: cur.promise._findAncestry()
        };
        acc.push(node);
      }

      return acc;
    }, []);
  },
  _setParent: function (parent) {
    if (this._parent) {
      throw new Error("parent already set");
    }

    this._parent = parent;
    return this;
  },
  _continueWith: function (data) {
    var firstPending = this._findFirstPending();

    if (firstPending) {
      firstPending._data = data;

      firstPending._setResolved();
    }
  },
  _findFirstPending: function () {
    return this._findFirstAncestor(function (test) {
      return test._isPending && test._isPending();
    });
  },
  _findFirstPaused: function () {
    return this._findFirstAncestor(function (test) {
      return test._paused;
    });
  },
  _findFirstAncestor: function (matching) {
    var test = this;
    var result;

    while (test) {
      if (matching(test)) {
        result = test;
      }

      test = test._parent;
    }

    return result;
  },
  _failWith: function (error) {
    var firstRejected = this._findFirstPending();

    if (firstRejected) {
      firstRejected._error = error;

      firstRejected._setRejected();
    }
  },
  _takeContinuations: function () {
    return this._continuations.splice(0, this._continuations.length);
  },
  _runRejections: function () {
    if (this._paused || !this._isRejected()) {
      return;
    }

    var error = this._error,
        continuations = this._takeContinuations(),
        self = this;

    continuations.forEach(function (cont) {
      if (cont.catchFn) {
        try {
          var catchResult = cont.catchFn(error);

          self._handleUserFunctionResult(catchResult, cont.promise);
        } catch (e) {
          cont.promise.reject(e);
        }
      } else {
        cont.promise.reject(error);
      }
    });
  },
  _runResolutions: function () {
    if (this._paused || !this._isResolved() || this._isPending()) {
      return;
    }

    var continuations = this._takeContinuations();

    if (looksLikeAPromise(this._data)) {
      return this._handleWhenResolvedDataIsPromise(this._data);
    }

    var data = this._data;
    var self = this;
    continuations.forEach(function (cont) {
      if (cont.nextFn) {
        try {
          var result = cont.nextFn(data);

          self._handleUserFunctionResult(result, cont.promise);
        } catch (e) {
          self._handleResolutionError(e, cont);
        }
      } else if (cont.promise) {
        cont.promise.resolve(data);
      }
    });
  },
  _handleResolutionError: function (e, continuation) {
    this._setRejected();

    if (continuation.catchFn) {
      try {
        continuation.catchFn(e);
        return;
      } catch (e2) {
        e = e2;
      }
    }

    if (continuation.promise) {
      continuation.promise.reject(e);
    }
  },
  _handleWhenResolvedDataIsPromise: function (data) {
    var self = this;
    return data.then(function (result) {
      self._data = result;

      self._runResolutions();
    }).catch(function (error) {
      self._error = error;

      self._setRejected();

      self._runRejections();
    });
  },
  _handleUserFunctionResult: function (data, nextSynchronousPromise) {
    if (looksLikeAPromise(data)) {
      this._chainPromiseData(data, nextSynchronousPromise);
    } else {
      nextSynchronousPromise.resolve(data);
    }
  },
  _chainPromiseData: function (promiseData, nextSynchronousPromise) {
    promiseData.then(function (newData) {
      nextSynchronousPromise.resolve(newData);
    }).catch(function (newError) {
      nextSynchronousPromise.reject(newError);
    });
  },
  _setResolved: function () {
    this.status = RESOLVED;

    if (!this._paused) {
      this._runResolutions();
    }
  },
  _setRejected: function () {
    this.status = REJECTED;

    if (!this._paused) {
      this._runRejections();
    }
  },
  _isPending: function () {
    return this.status === PENDING;
  },
  _isResolved: function () {
    return this.status === RESOLVED;
  },
  _isRejected: function () {
    return this.status === REJECTED;
  }
};

SynchronousPromise.resolve = function (result) {
  return new SynchronousPromise(function (resolve, reject) {
    if (looksLikeAPromise(result)) {
      result.then(function (newResult) {
        resolve(newResult);
      }).catch(function (error) {
        reject(error);
      });
    } else {
      resolve(result);
    }
  });
};

SynchronousPromise.reject = function (result) {
  return new SynchronousPromise(function (resolve, reject) {
    reject(result);
  });
};

SynchronousPromise.unresolved = function () {
  return new SynchronousPromise(function (resolve, reject) {
    this.resolve = resolve;
    this.reject = reject;
  });
};

SynchronousPromise.all = function () {
  var args = makeArrayFrom(arguments);

  if (Array.isArray(args[0])) {
    args = args[0];
  }

  if (!args.length) {
    return SynchronousPromise.resolve([]);
  }

  return new SynchronousPromise(function (resolve, reject) {
    var allData = [],
        numResolved = 0,
        doResolve = function () {
      if (numResolved === args.length) {
        resolve(allData);
      }
    },
        rejected = false,
        doReject = function (err) {
      if (rejected) {
        return;
      }

      rejected = true;
      reject(err);
    };

    args.forEach(function (arg, idx) {
      SynchronousPromise.resolve(arg).then(function (thisResult) {
        allData[idx] = thisResult;
        numResolved += 1;
        doResolve();
      }).catch(function (err) {
        doReject(err);
      });
    });
  });
};

function createAggregateErrorFrom(errors) {
  /* jshint ignore:start */
  if (typeof window !== "undefined" && "AggregateError" in window) {
    return new window.AggregateError(errors);
  }
  /* jshint ignore:end */


  return {
    errors: errors
  };
}

SynchronousPromise.any = function () {
  var args = makeArrayFrom(arguments);

  if (Array.isArray(args[0])) {
    args = args[0];
  }

  if (!args.length) {
    return SynchronousPromise.reject(createAggregateErrorFrom([]));
  }

  return new SynchronousPromise(function (resolve, reject) {
    var allErrors = [],
        numRejected = 0,
        doReject = function () {
      if (numRejected === args.length) {
        reject(createAggregateErrorFrom(allErrors));
      }
    },
        resolved = false,
        doResolve = function (result) {
      if (resolved) {
        return;
      }

      resolved = true;
      resolve(result);
    };

    args.forEach(function (arg, idx) {
      SynchronousPromise.resolve(arg).then(function (thisResult) {
        doResolve(thisResult);
      }).catch(function (err) {
        allErrors[idx] = err;
        numRejected += 1;
        doReject();
      });
    });
  });
};

SynchronousPromise.allSettled = function () {
  var args = makeArrayFrom(arguments);

  if (Array.isArray(args[0])) {
    args = args[0];
  }

  if (!args.length) {
    return SynchronousPromise.resolve([]);
  }

  return new SynchronousPromise(function (resolve) {
    var allData = [],
        numSettled = 0,
        doSettled = function () {
      numSettled += 1;

      if (numSettled === args.length) {
        resolve(allData);
      }
    };

    args.forEach(function (arg, idx) {
      SynchronousPromise.resolve(arg).then(function (thisResult) {
        allData[idx] = {
          status: "fulfilled",
          value: thisResult
        };
        doSettled();
      }).catch(function (err) {
        allData[idx] = {
          status: "rejected",
          reason: err
        };
        doSettled();
      });
    });
  });
};
/* jshint ignore:start */


if (Promise === SynchronousPromise) {
  throw new Error("Please use SynchronousPromise.installGlobally() to install globally");
}

var RealPromise = Promise;

SynchronousPromise.installGlobally = function (__awaiter) {
  if (Promise === SynchronousPromise) {
    return __awaiter;
  }

  var result = patchAwaiterIfRequired(__awaiter);
  Promise = SynchronousPromise;
  return result;
};

SynchronousPromise.uninstallGlobally = function () {
  if (Promise === SynchronousPromise) {
    Promise = RealPromise;
  }
};

function patchAwaiterIfRequired(__awaiter) {
  if (typeof __awaiter === "undefined" || __awaiter.__patched) {
    return __awaiter;
  }

  var originalAwaiter = __awaiter;

  __awaiter = function () {
    originalAwaiter.apply(this, makeArrayFrom(arguments));
  };

  __awaiter.__patched = true;
  return __awaiter;
}
/* jshint ignore:end */


var synchronousPromise = {
  SynchronousPromise: SynchronousPromise
};

var $$c = _export;

var $find = arrayIteration.find;

var addToUnscopables$3 = addToUnscopables$5;

var FIND = 'find';
var SKIPS_HOLES$1 = true; // Shouldn't skip holes

if (FIND in []) Array(1)[FIND](function () {
  SKIPS_HOLES$1 = false;
}); // `Array.prototype.find` method
// https://tc39.es/ecma262/#sec-array.prototype.find

$$c({
  target: 'Array',
  proto: true,
  forced: SKIPS_HOLES$1
}, {
  find: function find(callbackfn
  /* , that = undefined */
  ) {
    return $find(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$3(FIND);

var isObject$a = isObject$r;

var classof$4 = classofRaw$1;

var wellKnownSymbol$7 = wellKnownSymbol$q;

var MATCH$4 = wellKnownSymbol$7('match'); // `IsRegExp` abstract operation
// https://tc39.es/ecma262/#sec-isregexp

var isRegexp$1 = function (it) {
  var isRegExp;
  return isObject$a(it) && ((isRegExp = it[MATCH$4]) !== undefined ? !!isRegExp : classof$4(it) == 'RegExp');
};

var isRegExp$5 = isRegexp$1;

var notARegexp = function (it) {
  if (isRegExp$5(it)) {
    throw TypeError("The method doesn't accept regular expressions");
  }

  return it;
};

var wellKnownSymbol$6 = wellKnownSymbol$q;

var MATCH$3 = wellKnownSymbol$6('match');

var correctIsRegexpLogic = function (METHOD_NAME) {
  var regexp = /./;

  try {
    '/./'[METHOD_NAME](regexp);
  } catch (error1) {
    try {
      regexp[MATCH$3] = false;
      return '/./'[METHOD_NAME](regexp);
    } catch (error2) {
      /* empty */
    }
  }

  return false;
};

var $$b = _export;

var getOwnPropertyDescriptor$1 = objectGetOwnPropertyDescriptor.f;

var toLength$5 = toLength$b;

var notARegExp$1 = notARegexp;

var requireObjectCoercible$6 = requireObjectCoercible$a;

var correctIsRegExpLogic$1 = correctIsRegexpLogic;


var $startsWith = ''.startsWith;
var min$2 = Math.min;
var CORRECT_IS_REGEXP_LOGIC = correctIsRegExpLogic$1('startsWith'); // https://github.com/zloirock/core-js/pull/702

var MDN_POLYFILL_BUG = !CORRECT_IS_REGEXP_LOGIC && !!function () {
  var descriptor = getOwnPropertyDescriptor$1(String.prototype, 'startsWith');
  return descriptor && !descriptor.writable;
}(); // `String.prototype.startsWith` method
// https://tc39.es/ecma262/#sec-string.prototype.startswith

$$b({
  target: 'String',
  proto: true,
  forced: !MDN_POLYFILL_BUG && !CORRECT_IS_REGEXP_LOGIC
}, {
  startsWith: function startsWith(searchString
  /* , position = 0 */
  ) {
    var that = String(requireObjectCoercible$6(this));
    notARegExp$1(searchString);
    var index = toLength$5(min$2(arguments.length > 1 ? arguments[1] : undefined, that.length));
    var search = String(searchString);
    return $startsWith ? $startsWith.call(that, search, index) : that.slice(index, index + search.length) === search;
  }
});

var fails$a = fails$q;

var freezing = !fails$a(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});

var internalMetadata = {exports: {}};

var hiddenKeys = hiddenKeys$6;

var isObject$9 = isObject$r;

var has$5 = has$i;

var defineProperty$4 = objectDefineProperty.f;

var uid = uid$4;

var FREEZING$1 = freezing;

var METADATA = uid('meta');
var id = 0; // eslint-disable-next-line es/no-object-isextensible -- safe

var isExtensible = Object.isExtensible || function () {
  return true;
};

var setMetadata = function (it) {
  defineProperty$4(it, METADATA, {
    value: {
      objectID: 'O' + ++id,
      // object ID
      weakData: {} // weak collections IDs

    }
  });
};

var fastKey$1 = function (it, create) {
  // return a primitive with prefix
  if (!isObject$9(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;

  if (!has$5(it, METADATA)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return 'F'; // not necessary to add metadata

    if (!create) return 'E'; // add missing metadata

    setMetadata(it); // return object ID
  }

  return it[METADATA].objectID;
};

var getWeakData = function (it, create) {
  if (!has$5(it, METADATA)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return true; // not necessary to add metadata

    if (!create) return false; // add missing metadata

    setMetadata(it); // return the store of weak collections IDs
  }

  return it[METADATA].weakData;
}; // add metadata on freeze-family methods calling


var onFreeze$1 = function (it) {
  if (FREEZING$1 && meta.REQUIRED && isExtensible(it) && !has$5(it, METADATA)) setMetadata(it);
  return it;
};

var meta = internalMetadata.exports = {
  REQUIRED: false,
  fastKey: fastKey$1,
  getWeakData: getWeakData,
  onFreeze: onFreeze$1
};
hiddenKeys[METADATA] = true;

var $$a = _export;

var FREEZING = freezing;

var fails$9 = fails$q;

var isObject$8 = isObject$r;

var onFreeze = internalMetadata.exports.onFreeze; // eslint-disable-next-line es/no-object-freeze -- safe


var $freeze = Object.freeze;
var FAILS_ON_PRIMITIVES = fails$9(function () {
  $freeze(1);
}); // `Object.freeze` method
// https://tc39.es/ecma262/#sec-object.freeze

$$a({
  target: 'Object',
  stat: true,
  forced: FAILS_ON_PRIMITIVES,
  sham: !FREEZING
}, {
  freeze: function freeze(it) {
    return $freeze && isObject$8(it) ? $freeze(onFreeze(it)) : it;
  }
});

var _templateObject$9;

function _taggedTemplateLiteral$9(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray$a(arr, i) {
  return _arrayWithHoles$b(arr) || _iterableToArrayLimit$a(arr, i) || _unsupportedIterableToArray$e(arr, i) || _nonIterableRest$b();
}

function _nonIterableRest$b() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$e(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$e(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$e(o, minLen);
}

function _arrayLikeToArray$e(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$a(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$b(arr) {
  if (Array.isArray(arr)) return arr;
}

function _classCallCheck$5(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$5(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$5(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$5(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$5(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
var StoryIndexStore =
/*#__PURE__*/
function () {
  function StoryIndexStore() {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      v: 3,
      stories: {}
    },
        stories = _ref.stories;

    _classCallCheck$5(this, StoryIndexStore);

    this.channel = void 0;
    this.stories = void 0;
    this.stories = stories;
  }

  _createClass$5(StoryIndexStore, [{
    key: "storyIdFromSpecifier",
    value: function storyIdFromSpecifier(specifier) {
      var storyIds = Object.keys(this.stories);

      if (specifier === '*') {
        // '*' means select the first story. If there is none, we have no selection.
        return storyIds[0];
      }

      if (typeof specifier === 'string') {
        // Find the story with the exact id that matches the specifier (see #11571)
        if (storyIds.indexOf(specifier) >= 0) {
          return specifier;
        } // Fallback to the first story that starts with the specifier


        return storyIds.find(function (storyId) {
          return storyId.startsWith(specifier);
        });
      } // Try and find a story matching the name/kind, setting no selection if they don't exist.


      var name = specifier.name,
          title = specifier.title;
      var match = Object.entries(this.stories).find(function (_ref2) {
        var _ref3 = _slicedToArray$a(_ref2, 2);
            _ref3[0];
            var story = _ref3[1];

        return story.name === name && story.title === title;
      });
      return match && match[0];
    }
  }, {
    key: "storyIdToEntry",
    value: function storyIdToEntry(storyId) {
      var storyEntry = this.stories[storyId];

      if (!storyEntry) {
        throw new Error(_default(_templateObject$9 || (_templateObject$9 = _taggedTemplateLiteral$9(["Couldn't find story matching '", "' after HMR.\n      - Did you remove it from your CSF file?\n      - Are you sure a story with that id exists?\n      - Please check your stories field of your main.js config.\n      - Also check the browser console and terminal for error messages."])), storyId));
      }

      return storyEntry;
    }
  }]);

  return StoryIndexStore;
}();

var isObject$7 = isObject$r;

var setPrototypeOf = objectSetPrototypeOf; // makes subclassing work correct for wrapped built-ins


var inheritIfRequired$4 = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if ( // it can work only with native `setPrototypeOf`
  setPrototypeOf && // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
  typeof (NewTarget = dummy.constructor) == 'function' && NewTarget !== Wrapper && isObject$7(NewTargetPrototype = NewTarget.prototype) && NewTargetPrototype !== Wrapper.prototype) setPrototypeOf($this, NewTargetPrototype);
  return $this;
};

// a string of all valid unicode whitespaces
var whitespaces$2 = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u2000\u2001\u2002' + '\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';

var requireObjectCoercible$5 = requireObjectCoercible$a;

var whitespaces$1 = whitespaces$2;

var whitespace = '[' + whitespaces$1 + ']';
var ltrim = RegExp('^' + whitespace + whitespace + '*');
var rtrim = RegExp(whitespace + whitespace + '*$'); // `String.prototype.{ trim, trimStart, trimEnd, trimLeft, trimRight }` methods implementation

var createMethod = function (TYPE) {
  return function ($this) {
    var string = String(requireObjectCoercible$5($this));
    if (TYPE & 1) string = string.replace(ltrim, '');
    if (TYPE & 2) string = string.replace(rtrim, '');
    return string;
  };
};

var stringTrim = {
  // `String.prototype.{ trimLeft, trimStart }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimstart
  start: createMethod(1),
  // `String.prototype.{ trimRight, trimEnd }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimend
  end: createMethod(2),
  // `String.prototype.trim` method
  // https://tc39.es/ecma262/#sec-string.prototype.trim
  trim: createMethod(3)
};

var DESCRIPTORS$3 = descriptors;

var global$4 = global$q;

var isForced$3 = isForced_1;

var redefine$6 = redefine$d.exports;

var has$4 = has$i;

var classof$3 = classofRaw$1;

var inheritIfRequired$3 = inheritIfRequired$4;

var toPrimitive = toPrimitive$5;

var fails$8 = fails$q;

var create$1 = objectCreate;

var getOwnPropertyNames$2 = objectGetOwnPropertyNames.f;

var getOwnPropertyDescriptor = objectGetOwnPropertyDescriptor.f;

var defineProperty$3 = objectDefineProperty.f;

var trim = stringTrim.trim;

var NUMBER = 'Number';
var NativeNumber = global$4[NUMBER];
var NumberPrototype = NativeNumber.prototype; // Opera ~12 has broken Object#toString

var BROKEN_CLASSOF = classof$3(create$1(NumberPrototype)) == NUMBER; // `ToNumber` abstract operation
// https://tc39.es/ecma262/#sec-tonumber

var toNumber$2 = function (argument) {
  var it = toPrimitive(argument, false);
  var first, third, radix, maxCode, digits, length, index, code;

  if (typeof it == 'string' && it.length > 2) {
    it = trim(it);
    first = it.charCodeAt(0);

    if (first === 43 || first === 45) {
      third = it.charCodeAt(2);
      if (third === 88 || third === 120) return NaN; // Number('+0x1') should be NaN, old V8 fix
    } else if (first === 48) {
      switch (it.charCodeAt(1)) {
        case 66:
        case 98:
          radix = 2;
          maxCode = 49;
          break;
        // fast equal of /^0b[01]+$/i

        case 79:
        case 111:
          radix = 8;
          maxCode = 55;
          break;
        // fast equal of /^0o[0-7]+$/i

        default:
          return +it;
      }

      digits = it.slice(2);
      length = digits.length;

      for (index = 0; index < length; index++) {
        code = digits.charCodeAt(index); // parseInt parses a string to a first unavailable symbol
        // but ToNumber should return NaN if a string contains unavailable symbols

        if (code < 48 || code > maxCode) return NaN;
      }

      return parseInt(digits, radix);
    }
  }

  return +it;
}; // `Number` constructor
// https://tc39.es/ecma262/#sec-number-constructor


if (isForced$3(NUMBER, !NativeNumber(' 0o1') || !NativeNumber('0b1') || NativeNumber('+0x1'))) {
  var NumberWrapper = function Number(value) {
    var it = arguments.length < 1 ? 0 : value;
    var dummy = this;
    return dummy instanceof NumberWrapper // check on 1..constructor(foo) case
    && (BROKEN_CLASSOF ? fails$8(function () {
      NumberPrototype.valueOf.call(dummy);
    }) : classof$3(dummy) != NUMBER) ? inheritIfRequired$3(new NativeNumber(toNumber$2(it)), dummy, NumberWrapper) : toNumber$2(it);
  };

  for (var keys$2 = DESCRIPTORS$3 ? getOwnPropertyNames$2(NativeNumber) : ( // ES3:
  'MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,' + // ES2015 (in case, if modules with ES2015 Number statics required before):
  'EPSILON,isFinite,isInteger,isNaN,isSafeInteger,MAX_SAFE_INTEGER,' + 'MIN_SAFE_INTEGER,parseFloat,parseInt,isInteger,' + // ESNext
  'fromString,range').split(','), j = 0, key; keys$2.length > j; j++) {
    if (has$4(NativeNumber, key = keys$2[j]) && !has$4(NumberWrapper, key)) {
      defineProperty$3(NumberWrapper, key, getOwnPropertyDescriptor(NativeNumber, key));
    }
  }

  NumberWrapper.prototype = NumberPrototype;
  NumberPrototype.constructor = NumberWrapper;
  redefine$6(global$4, NUMBER, NumberWrapper);
}

var $$9 = _export;

var $includes = arrayIncludes.includes;

var addToUnscopables$2 = addToUnscopables$5; // `Array.prototype.includes` method
// https://tc39.es/ecma262/#sec-array.prototype.includes


$$9({
  target: 'Array',
  proto: true
}, {
  includes: function includes(el
  /* , fromIndex = 0 */
  ) {
    return $includes(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$2('includes');

var $$8 = _export;

var $findIndex = arrayIteration.findIndex;

var addToUnscopables$1 = addToUnscopables$5;

var FIND_INDEX = 'findIndex';
var SKIPS_HOLES = true; // Shouldn't skip holes

if (FIND_INDEX in []) Array(1)[FIND_INDEX](function () {
  SKIPS_HOLES = false;
}); // `Array.prototype.findIndex` method
// https://tc39.es/ecma262/#sec-array.prototype.findindex

$$8({
  target: 'Array',
  proto: true,
  forced: SKIPS_HOLES
}, {
  findIndex: function findIndex(callbackfn
  /* , that = undefined */
  ) {
    return $findIndex(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables$1(FIND_INDEX);

var $$7 = _export;

var notARegExp = notARegexp;

var requireObjectCoercible$4 = requireObjectCoercible$a;

var correctIsRegExpLogic = correctIsRegexpLogic; // `String.prototype.includes` method
// https://tc39.es/ecma262/#sec-string.prototype.includes


$$7({
  target: 'String',
  proto: true,
  forced: !correctIsRegExpLogic('includes')
}, {
  includes: function includes(searchString
  /* , position = 0 */
  ) {
    return !!~String(requireObjectCoercible$4(this)).indexOf(notARegExp(searchString), arguments.length > 1 ? arguments[1] : undefined);
  }
});

var $$6 = _export;

var fails$7 = fails$q;

var isArray$4 = isArray$i;

var isObject$6 = isObject$r;

var toObject$2 = toObject$b;

var toLength$4 = toLength$b;

var createProperty = createProperty$3;

var arraySpeciesCreate = arraySpeciesCreate$2;

var arrayMethodHasSpeciesSupport$2 = arrayMethodHasSpeciesSupport$6;

var wellKnownSymbol$5 = wellKnownSymbol$q;

var V8_VERSION = engineV8Version;

var IS_CONCAT_SPREADABLE = wellKnownSymbol$5('isConcatSpreadable');
var MAX_SAFE_INTEGER = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED = 'Maximum allowed index exceeded'; // We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679

var IS_CONCAT_SPREADABLE_SUPPORT = V8_VERSION >= 51 || !fails$7(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE] = false;
  return array.concat()[0] !== array;
});
var SPECIES_SUPPORT = arrayMethodHasSpeciesSupport$2('concat');

var isConcatSpreadable = function (O) {
  if (!isObject$6(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE];
  return spreadable !== undefined ? !!spreadable : isArray$4(O);
};

var FORCED$2 = !IS_CONCAT_SPREADABLE_SUPPORT || !SPECIES_SUPPORT; // `Array.prototype.concat` method
// https://tc39.es/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species

$$6({
  target: 'Array',
  proto: true,
  forced: FORCED$2
}, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  concat: function concat(arg) {
    var O = toObject$2(this);
    var A = arraySpeciesCreate(O, 0);
    var n = 0;
    var i, k, length, len, E;

    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];

      if (isConcatSpreadable(E)) {
        len = toLength$4(E.length);
        if (n + len > MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);

        for (k = 0; k < len; k++, n++) if (k in E) createProperty(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);
        createProperty(A, n++, E);
      }
    }

    A.length = n;
    return A;
  }
});

var $$5 = _export;

var IndexedObject = indexedObject;

var toIndexedObject = toIndexedObject$9;

var arrayMethodIsStrict = arrayMethodIsStrict$3;

var nativeJoin = [].join;
var ES3_STRINGS = IndexedObject != Object;
var STRICT_METHOD = arrayMethodIsStrict('join', ','); // `Array.prototype.join` method
// https://tc39.es/ecma262/#sec-array.prototype.join

$$5({
  target: 'Array',
  proto: true,
  forced: ES3_STRINGS || !STRICT_METHOD
}, {
  join: function join(separator) {
    return nativeJoin.call(toIndexedObject(this), separator === undefined ? ',' : separator);
  }
});

var toObject$1 = toObject$b;

var toAbsoluteIndex = toAbsoluteIndex$3;

var toLength$3 = toLength$b; // `Array.prototype.fill` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.fill


var arrayFill = function fill(value
/* , start = 0, end = @length */
) {
  var O = toObject$1(this);
  var length = toLength$3(O.length);
  var argumentsLength = arguments.length;
  var index = toAbsoluteIndex(argumentsLength > 1 ? arguments[1] : undefined, length);
  var end = argumentsLength > 2 ? arguments[2] : undefined;
  var endPos = end === undefined ? length : toAbsoluteIndex(end, length);

  while (endPos > index) O[index++] = value;

  return O;
};

var $$4 = _export;

var fill = arrayFill;

var addToUnscopables = addToUnscopables$5; // `Array.prototype.fill` method
// https://tc39.es/ecma262/#sec-array.prototype.fill


$$4({
  target: 'Array',
  proto: true
}, {
  fill: fill
}); // https://tc39.es/ecma262/#sec-array.prototype-@@unscopables

addToUnscopables('fill');

var fastDeepEqual = function equal(a, b) {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
    if (a.constructor !== b.constructor) return false;
    var length, i, keys;

    if (Array.isArray(a)) {
      length = a.length;
      if (length != b.length) return false;

      for (i = length; i-- !== 0;) if (!equal(a[i], b[i])) return false;

      return true;
    }

    if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0;) if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

    for (i = length; i-- !== 0;) {
      var key = keys[i];
      if (!equal(a[key], b[key])) return false;
    }

    return true;
  } // true if both NaN, false otherwise


  return a !== a && b !== b;
};

var overArg = _overArg;
/** Built-in value references. */


var getPrototype$2 = overArg(Object.getPrototypeOf, Object);
var _getPrototype = getPrototype$2;

var baseGetTag$2 = _baseGetTag$1,
    getPrototype$1 = _getPrototype,
    isObjectLike$2 = isObjectLike_1$1;
/** `Object#toString` result references. */


var objectTag = '[object Object]';
/** Used for built-in method references. */

var funcProto = Function.prototype,
    objectProto$3 = Object.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString = funcProto.toString;
/** Used to check objects for own properties. */

var hasOwnProperty$2 = objectProto$3.hasOwnProperty;
/** Used to infer the `Object` constructor. */

var objectCtorString = funcToString.call(Object);
/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */

function isPlainObject(value) {
  if (!isObjectLike$2(value) || baseGetTag$2(value) != objectTag) {
    return false;
  }

  var proto = getPrototype$1(value);

  if (proto === null) {
    return true;
  }

  var Ctor = hasOwnProperty$2.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor && funcToString.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject;

var _templateObject$8, _templateObject2$1;

function _taggedTemplateLiteral$8(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _toConsumableArray$7(arr) {
  return _arrayWithoutHoles$7(arr) || _iterableToArray$7(arr) || _unsupportedIterableToArray$d(arr) || _nonIterableSpread$7();
}

function _nonIterableSpread$7() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _iterableToArray$7(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$7(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$d(arr);
}

function _defineProperty$3(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray$9(arr, i) {
  return _arrayWithHoles$a(arr) || _iterableToArrayLimit$9(arr, i) || _unsupportedIterableToArray$d(arr, i) || _nonIterableRest$a();
}

function _nonIterableRest$a() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$d(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$d(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$d(o, minLen);
}

function _arrayLikeToArray$d(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$9(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$a(arr) {
  if (Array.isArray(arr)) return arr;
}

function _typeof$2(obj) {
  "@babel/helpers - typeof";

  return _typeof$2 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$2(obj);
}
var INCOMPATIBLE = Symbol('incompatible');

var map = function map(arg, argType) {
  var type = argType.type;
  if (arg === undefined || arg === null || !type) return arg;

  if (argType.mapping) {
    return arg;
  }

  switch (type.name) {
    case 'string':
      return String(arg);

    case 'enum':
      return arg;

    case 'number':
      return Number(arg);

    case 'boolean':
      return arg === 'true';

    case 'array':
      if (!type.value || !Array.isArray(arg)) return INCOMPATIBLE;
      return arg.reduce(function (acc, item, index) {
        var mapped = map(item, {
          type: type.value
        });
        if (mapped !== INCOMPATIBLE) acc[index] = mapped;
        return acc;
      }, new Array(arg.length));

    case 'object':
      if (typeof arg === 'string' || typeof arg === 'number') return arg;
      if (!type.value || _typeof$2(arg) !== 'object') return INCOMPATIBLE;
      return Object.entries(arg).reduce(function (acc, _ref) {
        var _ref2 = _slicedToArray$9(_ref, 2),
            key = _ref2[0],
            val = _ref2[1];

        var mapped = map(val, {
          type: type.value[key]
        });
        return mapped === INCOMPATIBLE ? acc : Object.assign(acc, _defineProperty$3({}, key, mapped));
      }, {});

    default:
      return INCOMPATIBLE;
  }
};

var mapArgsToTypes = function mapArgsToTypes(args, argTypes) {
  return Object.entries(args).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray$9(_ref3, 2),
        key = _ref4[0],
        value = _ref4[1];

    if (!argTypes[key]) return acc;
    var mapped = map(value, argTypes[key]);
    return mapped === INCOMPATIBLE ? acc : Object.assign(acc, _defineProperty$3({}, key, mapped));
  }, {});
};
var combineArgs = function combineArgs(value, update) {
  if (Array.isArray(value) && Array.isArray(update)) {
    return update.reduce(function (acc, upd, index) {
      acc[index] = combineArgs(value[index], update[index]);
      return acc;
    }, _toConsumableArray$7(value)).filter(function (v) {
      return v !== undefined;
    });
  }

  if (!isPlainObject_1(value) || !isPlainObject_1(update)) return update;
  return Object.keys(Object.assign({}, value, update)).reduce(function (acc, key) {
    if (key in update) {
      var combined = combineArgs(value[key], update[key]);
      if (combined !== undefined) acc[key] = combined;
    } else {
      acc[key] = value[key];
    }

    return acc;
  }, {});
};
var validateOptions = function validateOptions(args, argTypes) {
  return Object.entries(argTypes).reduce(function (acc, _ref5) {
    var _ref6 = _slicedToArray$9(_ref5, 2),
        key = _ref6[0],
        options = _ref6[1].options; // Don't set args that are not defined in `args` (they can be undefined in there)
    // see https://github.com/storybookjs/storybook/issues/15630 and
    //   https://github.com/storybookjs/storybook/issues/17063


    function allowArg() {
      if (key in args) {
        acc[key] = args[key];
      }

      return acc;
    }

    if (!options) return allowArg();

    if (!Array.isArray(options)) {
      once.error(_default(_templateObject$8 || (_templateObject$8 = _taggedTemplateLiteral$8(["\n        Invalid argType: '", ".options' should be an array.\n\n        More info: https://storybook.js.org/docs/react/api/argtypes\n      "])), key));
      return allowArg();
    }

    if (options.some(function (opt) {
      return opt && ['object', 'function'].includes(_typeof$2(opt));
    })) {
      once.error(_default(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral$8(["\n        Invalid argType: '", ".options' should only contain primitives. Use a 'mapping' for complex values.\n\n        More info: https://storybook.js.org/docs/react/writing-stories/args#mapping-to-complex-arg-values\n      "])), key));
      return allowArg();
    }

    var isArray = Array.isArray(args[key]);
    var invalidIndex = isArray && args[key].findIndex(function (val) {
      return !options.includes(val);
    });
    var isValidArray = isArray && invalidIndex === -1;

    if (args[key] === undefined || options.includes(args[key]) || isValidArray) {
      return allowArg();
    }

    var field = isArray ? "".concat(key, "[").concat(invalidIndex, "]") : key;
    var supportedOptions = options.map(function (opt) {
      return typeof opt === 'string' ? "'".concat(opt, "'") : String(opt);
    }).join(', ');
    once.warn("Received illegal value for '".concat(field, "'. Supported options: ").concat(supportedOptions));
    return acc;
  }, {});
}; // TODO -- copied from router, needs to be in a shared location

var DEEPLY_EQUAL = Symbol('Deeply equal');
var deepDiff = function deepDiff(value, update) {
  if (_typeof$2(value) !== _typeof$2(update)) return update;
  if (fastDeepEqual(value, update)) return DEEPLY_EQUAL;

  if (Array.isArray(value) && Array.isArray(update)) {
    var res = update.reduce(function (acc, upd, index) {
      var diff = deepDiff(value[index], upd);
      if (diff !== DEEPLY_EQUAL) acc[index] = diff;
      return acc;
    }, new Array(update.length));
    if (update.length >= value.length) return res;
    return res.concat(new Array(value.length - update.length).fill(undefined));
  }

  if (isPlainObject_1(value) && isPlainObject_1(update)) {
    return Object.keys(Object.assign({}, value, update)).reduce(function (acc, key) {
      var diff = deepDiff(value === null || value === void 0 ? void 0 : value[key], update === null || update === void 0 ? void 0 : update[key]);
      return diff === DEEPLY_EQUAL ? acc : Object.assign(acc, _defineProperty$3({}, key, diff));
    }, {});
  }

  return update;
};
var NO_TARGET_NAME = '';
function groupArgsByTarget(_ref7) {
  var args = _ref7.args,
      argTypes = _ref7.argTypes;
  var groupedArgs = {};
  Object.entries(args).forEach(function (_ref8) {
    var _ref9 = _slicedToArray$9(_ref8, 2),
        name = _ref9[0],
        value = _ref9[1];

    var _ref10 = argTypes[name] || {},
        _ref10$target = _ref10.target,
        target = _ref10$target === void 0 ? NO_TARGET_NAME : _ref10$target;

    groupedArgs[target] = groupedArgs[target] || {};
    groupedArgs[target][name] = value;
  });
  return groupedArgs;
}
function noTargetArgs(context) {
  return groupArgsByTarget(context)[NO_TARGET_NAME];
}

function _classCallCheck$4(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$4(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$4(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$4(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$4(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function deleteUndefined(obj) {
  // eslint-disable-next-line no-param-reassign
  Object.keys(obj).forEach(function (key) {
    return obj[key] === undefined && delete obj[key];
  });
  return obj;
}

var ArgsStore =
/*#__PURE__*/
function () {
  function ArgsStore() {
    _classCallCheck$4(this, ArgsStore);

    this.initialArgsByStoryId = {};
    this.argsByStoryId = {};
  }

  _createClass$4(ArgsStore, [{
    key: "get",
    value: function get(storyId) {
      if (!(storyId in this.argsByStoryId)) {
        throw new Error("No args known for ".concat(storyId, " -- has it been rendered yet?"));
      }

      return this.argsByStoryId[storyId];
    }
  }, {
    key: "setInitial",
    value: function setInitial(story) {
      if (!this.initialArgsByStoryId[story.id]) {
        this.initialArgsByStoryId[story.id] = story.initialArgs;
        this.argsByStoryId[story.id] = story.initialArgs;
      } else if (this.initialArgsByStoryId[story.id] !== story.initialArgs) {
        // When we get a new version of a story (with new initialArgs), we re-apply the same diff
        // that we had previously applied to the old version of the story
        var delta = deepDiff(this.initialArgsByStoryId[story.id], this.argsByStoryId[story.id]);
        this.initialArgsByStoryId[story.id] = story.initialArgs;
        this.argsByStoryId[story.id] = story.initialArgs;

        if (delta !== DEEPLY_EQUAL) {
          this.updateFromDelta(story, delta);
        }
      }
    }
  }, {
    key: "updateFromDelta",
    value: function updateFromDelta(story, delta) {
      // Use the argType to ensure we setting a type with defined options to something outside of that
      var validatedDelta = validateOptions(delta, story.argTypes); // NOTE: we use `combineArgs` here rather than `combineParameters` because changes to arg
      // array values are persisted in the URL as sparse arrays, and we have to take that into
      // account when overriding the initialArgs (e.g. we patch [,'changed'] over ['initial', 'val'])

      this.argsByStoryId[story.id] = combineArgs(this.argsByStoryId[story.id], validatedDelta);
    }
  }, {
    key: "updateFromPersisted",
    value: function updateFromPersisted(story, persisted) {
      // Use the argType to ensure we aren't persisting the wrong type of value to the type.
      // For instance you could try and set a string-valued arg to a number by changing the URL
      var mappedPersisted = mapArgsToTypes(persisted, story.argTypes);
      return this.updateFromDelta(story, mappedPersisted);
    }
  }, {
    key: "update",
    value: function update(storyId, argsUpdate) {
      if (!(storyId in this.argsByStoryId)) {
        throw new Error("No args known for ".concat(storyId, " -- has it been rendered yet?"));
      }

      this.argsByStoryId[storyId] = deleteUndefined(Object.assign({}, this.argsByStoryId[storyId], argsUpdate));
    }
  }]);

  return ArgsStore;
}();

var $$3 = _export;

var global$3 = global$q;

var isForced$2 = isForced_1;

var redefine$5 = redefine$d.exports;

var InternalMetadataModule = internalMetadata.exports;

var iterate$1 = iterate$3;

var anInstance$1 = anInstance$3;

var isObject$5 = isObject$r;

var fails$6 = fails$q;

var checkCorrectnessOfIteration = checkCorrectnessOfIteration$3;

var setToStringTag = setToStringTag$5;

var inheritIfRequired$2 = inheritIfRequired$4;

var collection$1 = function (CONSTRUCTOR_NAME, wrapper, common) {
  var IS_MAP = CONSTRUCTOR_NAME.indexOf('Map') !== -1;
  var IS_WEAK = CONSTRUCTOR_NAME.indexOf('Weak') !== -1;
  var ADDER = IS_MAP ? 'set' : 'add';
  var NativeConstructor = global$3[CONSTRUCTOR_NAME];
  var NativePrototype = NativeConstructor && NativeConstructor.prototype;
  var Constructor = NativeConstructor;
  var exported = {};

  var fixMethod = function (KEY) {
    var nativeMethod = NativePrototype[KEY];
    redefine$5(NativePrototype, KEY, KEY == 'add' ? function add(value) {
      nativeMethod.call(this, value === 0 ? 0 : value);
      return this;
    } : KEY == 'delete' ? function (key) {
      return IS_WEAK && !isObject$5(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'get' ? function get(key) {
      return IS_WEAK && !isObject$5(key) ? undefined : nativeMethod.call(this, key === 0 ? 0 : key);
    } : KEY == 'has' ? function has(key) {
      return IS_WEAK && !isObject$5(key) ? false : nativeMethod.call(this, key === 0 ? 0 : key);
    } : function set(key, value) {
      nativeMethod.call(this, key === 0 ? 0 : key, value);
      return this;
    });
  };

  var REPLACE = isForced$2(CONSTRUCTOR_NAME, typeof NativeConstructor != 'function' || !(IS_WEAK || NativePrototype.forEach && !fails$6(function () {
    new NativeConstructor().entries().next();
  })));

  if (REPLACE) {
    // create collection constructor
    Constructor = common.getConstructor(wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER);
    InternalMetadataModule.REQUIRED = true;
  } else if (isForced$2(CONSTRUCTOR_NAME, true)) {
    var instance = new Constructor(); // early implementations not supports chaining

    var HASNT_CHAINING = instance[ADDER](IS_WEAK ? {} : -0, 1) != instance; // V8 ~ Chromium 40- weak-collections throws on primitives, but should return false

    var THROWS_ON_PRIMITIVES = fails$6(function () {
      instance.has(1);
    }); // most early implementations doesn't supports iterables, most modern - not close it correctly
    // eslint-disable-next-line no-new -- required for testing

    var ACCEPT_ITERABLES = checkCorrectnessOfIteration(function (iterable) {
      new NativeConstructor(iterable);
    }); // for early implementations -0 and +0 not the same

    var BUGGY_ZERO = !IS_WEAK && fails$6(function () {
      // V8 ~ Chromium 42- fails only with 5+ elements
      var $instance = new NativeConstructor();
      var index = 5;

      while (index--) $instance[ADDER](index, index);

      return !$instance.has(-0);
    });

    if (!ACCEPT_ITERABLES) {
      Constructor = wrapper(function (dummy, iterable) {
        anInstance$1(dummy, Constructor, CONSTRUCTOR_NAME);
        var that = inheritIfRequired$2(new NativeConstructor(), dummy, Constructor);
        if (iterable != undefined) iterate$1(iterable, that[ADDER], {
          that: that,
          AS_ENTRIES: IS_MAP
        });
        return that;
      });
      Constructor.prototype = NativePrototype;
      NativePrototype.constructor = Constructor;
    }

    if (THROWS_ON_PRIMITIVES || BUGGY_ZERO) {
      fixMethod('delete');
      fixMethod('has');
      IS_MAP && fixMethod('get');
    }

    if (BUGGY_ZERO || HASNT_CHAINING) fixMethod(ADDER); // weak collections should not contains .clear method

    if (IS_WEAK && NativePrototype.clear) delete NativePrototype.clear;
  }

  exported[CONSTRUCTOR_NAME] = Constructor;
  $$3({
    global: true,
    forced: Constructor != NativeConstructor
  }, exported);
  setToStringTag(Constructor, CONSTRUCTOR_NAME);
  if (!IS_WEAK) common.setStrong(Constructor, CONSTRUCTOR_NAME, IS_MAP);
  return Constructor;
};

var defineProperty$2 = objectDefineProperty.f;

var create = objectCreate;

var redefineAll = redefineAll$2;

var bind$2 = functionBindContext;

var anInstance = anInstance$3;

var iterate = iterate$3;

var defineIterator = defineIterator$3;

var setSpecies$2 = setSpecies$4;

var DESCRIPTORS$2 = descriptors;

var fastKey = internalMetadata.exports.fastKey;

var InternalStateModule = internalState;

var setInternalState = InternalStateModule.set;
var internalStateGetterFor = InternalStateModule.getterFor;
var collectionStrong$1 = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var C = wrapper(function (that, iterable) {
      anInstance(that, C, CONSTRUCTOR_NAME);
      setInternalState(that, {
        type: CONSTRUCTOR_NAME,
        index: create(null),
        first: undefined,
        last: undefined,
        size: 0
      });
      if (!DESCRIPTORS$2) that.size = 0;
      if (iterable != undefined) iterate(iterable, that[ADDER], {
        that: that,
        AS_ENTRIES: IS_MAP
      });
    });
    var getInternalState = internalStateGetterFor(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var entry = getEntry(that, key);
      var previous, index; // change existing entry

      if (entry) {
        entry.value = value; // create new entry
      } else {
        state.last = entry = {
          index: index = fastKey(key, true),
          key: key,
          value: value,
          previous: previous = state.last,
          next: undefined,
          removed: false
        };
        if (!state.first) state.first = entry;
        if (previous) previous.next = entry;
        if (DESCRIPTORS$2) state.size++;else that.size++; // add to index

        if (index !== 'F') state.index[index] = entry;
      }

      return that;
    };

    var getEntry = function (that, key) {
      var state = getInternalState(that); // fast case

      var index = fastKey(key);
      var entry;
      if (index !== 'F') return state.index[index]; // frozen object case

      for (entry = state.first; entry; entry = entry.next) {
        if (entry.key == key) return entry;
      }
    };

    redefineAll(C.prototype, {
      // 23.1.3.1 Map.prototype.clear()
      // 23.2.3.2 Set.prototype.clear()
      clear: function clear() {
        var that = this;
        var state = getInternalState(that);
        var data = state.index;
        var entry = state.first;

        while (entry) {
          entry.removed = true;
          if (entry.previous) entry.previous = entry.previous.next = undefined;
          delete data[entry.index];
          entry = entry.next;
        }

        state.first = state.last = undefined;
        if (DESCRIPTORS$2) state.size = 0;else that.size = 0;
      },
      // 23.1.3.3 Map.prototype.delete(key)
      // 23.2.3.4 Set.prototype.delete(value)
      'delete': function (key) {
        var that = this;
        var state = getInternalState(that);
        var entry = getEntry(that, key);

        if (entry) {
          var next = entry.next;
          var prev = entry.previous;
          delete state.index[entry.index];
          entry.removed = true;
          if (prev) prev.next = next;
          if (next) next.previous = prev;
          if (state.first == entry) state.first = next;
          if (state.last == entry) state.last = prev;
          if (DESCRIPTORS$2) state.size--;else that.size--;
        }

        return !!entry;
      },
      // 23.2.3.6 Set.prototype.forEach(callbackfn, thisArg = undefined)
      // 23.1.3.5 Map.prototype.forEach(callbackfn, thisArg = undefined)
      forEach: function forEach(callbackfn
      /* , that = undefined */
      ) {
        var state = getInternalState(this);
        var boundFunction = bind$2(callbackfn, arguments.length > 1 ? arguments[1] : undefined, 3);
        var entry;

        while (entry = entry ? entry.next : state.first) {
          boundFunction(entry.value, entry.key, this); // revert to the last existing entry

          while (entry && entry.removed) entry = entry.previous;
        }
      },
      // 23.1.3.7 Map.prototype.has(key)
      // 23.2.3.7 Set.prototype.has(value)
      has: function has(key) {
        return !!getEntry(this, key);
      }
    });
    redefineAll(C.prototype, IS_MAP ? {
      // 23.1.3.6 Map.prototype.get(key)
      get: function get(key) {
        var entry = getEntry(this, key);
        return entry && entry.value;
      },
      // 23.1.3.9 Map.prototype.set(key, value)
      set: function set(key, value) {
        return define(this, key === 0 ? 0 : key, value);
      }
    } : {
      // 23.2.3.1 Set.prototype.add(value)
      add: function add(value) {
        return define(this, value = value === 0 ? 0 : value, value);
      }
    });
    if (DESCRIPTORS$2) defineProperty$2(C.prototype, 'size', {
      get: function () {
        return getInternalState(this).size;
      }
    });
    return C;
  },
  setStrong: function (C, CONSTRUCTOR_NAME, IS_MAP) {
    var ITERATOR_NAME = CONSTRUCTOR_NAME + ' Iterator';
    var getInternalCollectionState = internalStateGetterFor(CONSTRUCTOR_NAME);
    var getInternalIteratorState = internalStateGetterFor(ITERATOR_NAME); // add .keys, .values, .entries, [@@iterator]
    // 23.1.3.4, 23.1.3.8, 23.1.3.11, 23.1.3.12, 23.2.3.5, 23.2.3.8, 23.2.3.10, 23.2.3.11

    defineIterator(C, CONSTRUCTOR_NAME, function (iterated, kind) {
      setInternalState(this, {
        type: ITERATOR_NAME,
        target: iterated,
        state: getInternalCollectionState(iterated),
        kind: kind,
        last: undefined
      });
    }, function () {
      var state = getInternalIteratorState(this);
      var kind = state.kind;
      var entry = state.last; // revert to the last existing entry

      while (entry && entry.removed) entry = entry.previous; // get next entry


      if (!state.target || !(state.last = entry = entry ? entry.next : state.state.first)) {
        // or finish the iteration
        state.target = undefined;
        return {
          value: undefined,
          done: true
        };
      } // return step by kind


      if (kind == 'keys') return {
        value: entry.key,
        done: false
      };
      if (kind == 'values') return {
        value: entry.value,
        done: false
      };
      return {
        value: [entry.key, entry.value],
        done: false
      };
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true); // add [@@species], 23.1.2.2, 23.2.2.2

    setSpecies$2(CONSTRUCTOR_NAME);
  }
};

var collection = collection$1;

var collectionStrong = collectionStrong$1; // `Set` constructor
// https://tc39.es/ecma262/#sec-set-objects


collection('Set', function (init) {
  return function Set() {
    return init(this, arguments.length ? arguments[0] : undefined);
  };
}, collectionStrong);

function _slicedToArray$8(arr, i) {
  return _arrayWithHoles$9(arr) || _iterableToArrayLimit$8(arr, i) || _unsupportedIterableToArray$c(arr, i) || _nonIterableRest$9();
}

function _nonIterableRest$9() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$c(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$c(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$c(o, minLen);
}

function _arrayLikeToArray$c(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$8(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$9(arr) {
  if (Array.isArray(arr)) return arr;
}
var getValuesFromArgTypes = function getValuesFromArgTypes() {
  var argTypes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.entries(argTypes).reduce(function (acc, _ref) {
    var _ref2 = _slicedToArray$8(_ref, 2),
        arg = _ref2[0],
        defaultValue = _ref2[1].defaultValue;

    if (typeof defaultValue !== 'undefined') {
      acc[arg] = defaultValue;
    }

    return acc;
  }, {});
};

var _templateObject$7;

function _slicedToArray$7(arr, i) {
  return _arrayWithHoles$8(arr) || _iterableToArrayLimit$7(arr, i) || _unsupportedIterableToArray$b(arr, i) || _nonIterableRest$8();
}

function _nonIterableRest$8() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _iterableToArrayLimit$7(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$8(arr) {
  if (Array.isArray(arr)) return arr;
}

function _toConsumableArray$6(arr) {
  return _arrayWithoutHoles$6(arr) || _iterableToArray$6(arr) || _unsupportedIterableToArray$b(arr) || _nonIterableSpread$6();
}

function _nonIterableSpread$6() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$b(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$b(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$b(o, minLen);
}

function _iterableToArray$6(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$6(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$b(arr);
}

function _arrayLikeToArray$b(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _classCallCheck$3(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$3(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$3(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$3(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$3(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _taggedTemplateLiteral$7(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var setUndeclaredWarning = node$1(function () {}, _default(_templateObject$7 || (_templateObject$7 = _taggedTemplateLiteral$7(["\n    Setting a global value that is undeclared (i.e. not in the user's initial set of globals\n    or globalTypes) is deprecated and will have no effect in 7.0.\n  "]))));
var GlobalsStore =
/*#__PURE__*/
function () {
  function GlobalsStore() {
    _classCallCheck$3(this, GlobalsStore);

    this.allowedGlobalNames = void 0;
    this.initialGlobals = void 0;
    this.globals = {};
  }

  _createClass$3(GlobalsStore, [{
    key: "set",
    value: function set(_ref) {
      var _ref$globals = _ref.globals,
          globals = _ref$globals === void 0 ? {} : _ref$globals,
          _ref$globalTypes = _ref.globalTypes,
          globalTypes = _ref$globalTypes === void 0 ? {} : _ref$globalTypes;
      var delta = this.initialGlobals && deepDiff(this.initialGlobals, this.globals);
      this.allowedGlobalNames = new Set([].concat(_toConsumableArray$6(Object.keys(globals)), _toConsumableArray$6(Object.keys(globalTypes))));
      var defaultGlobals = getValuesFromArgTypes(globalTypes);
      this.initialGlobals = Object.assign({}, defaultGlobals, globals);
      this.globals = this.initialGlobals;

      if (delta && delta !== DEEPLY_EQUAL) {
        this.updateFromPersisted(delta);
      }
    }
  }, {
    key: "filterAllowedGlobals",
    value: function filterAllowedGlobals(globals) {
      var _this = this;

      return Object.entries(globals).reduce(function (acc, _ref2) {
        var _ref3 = _slicedToArray$7(_ref2, 2),
            key = _ref3[0],
            value = _ref3[1];

        if (_this.allowedGlobalNames.has(key)) acc[key] = value;
        return acc;
      }, {});
    }
  }, {
    key: "updateFromPersisted",
    value: function updateFromPersisted(persisted) {
      var allowedUrlGlobals = this.filterAllowedGlobals(persisted); // Note that unlike args, we do not have the same type information for globals to allow us
      // to type check them here, so we just set them naively

      this.globals = Object.assign({}, this.globals, allowedUrlGlobals);
    }
  }, {
    key: "get",
    value: function get() {
      return this.globals;
    }
  }, {
    key: "update",
    value: function update(newGlobals) {
      var _this2 = this;

      Object.keys(newGlobals).forEach(function (key) {
        if (!_this2.allowedGlobalNames.has(key)) {
          setUndeclaredWarning();
        }
      });
      this.globals = Object.assign({}, this.globals, newGlobals);
    }
  }]);

  return GlobalsStore;
}();

var _excluded$6 = ["type", "control"];

function _objectWithoutProperties$6(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$6(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$6(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var normalizeType = function normalizeType(type) {
  return typeof type === 'string' ? {
    name: type
  } : type;
};

var normalizeControl = function normalizeControl(control) {
  return typeof control === 'string' ? {
    type: control
  } : control;
};

var normalizeInputType = function normalizeInputType(inputType, key) {
  var type = inputType.type,
      control = inputType.control,
      rest = _objectWithoutProperties$6(inputType, _excluded$6);

  var normalized = Object.assign({
    name: key
  }, rest);
  if (type) normalized.type = normalizeType(type);

  if (control) {
    normalized.control = normalizeControl(control);
  } else if (control === false) {
    normalized.control = {
      disable: true
    };
  }

  return normalized;
};
var normalizeInputTypes = function normalizeInputTypes(inputTypes) {
  return mapValues_1(inputTypes, normalizeInputType);
};

var _templateObject$6;

function _toConsumableArray$5(arr) {
  return _arrayWithoutHoles$5(arr) || _iterableToArray$5(arr) || _unsupportedIterableToArray$a(arr) || _nonIterableSpread$5();
}

function _nonIterableSpread$5() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$a(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$a(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$a(o, minLen);
}

function _iterableToArray$5(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$5(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$a(arr);
}

function _arrayLikeToArray$a(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _taggedTemplateLiteral$6(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var deprecatedStoryAnnotation = _default(_templateObject$6 || (_templateObject$6 = _taggedTemplateLiteral$6(["\nCSF .story annotations deprecated; annotate story functions directly:\n- StoryFn.story.name => StoryFn.storyName\n- StoryFn.story.(parameters|decorators) => StoryFn.(parameters|decorators)\nSee https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#hoisted-csf-annotations for details and codemod.\n"])));
var deprecatedStoryAnnotationWarning = node$1(function () {}, deprecatedStoryAnnotation);
function normalizeStory(key, storyAnnotations, meta) {
  var userStoryFn;
  var storyObject;

  if (typeof storyAnnotations === 'function') {
    userStoryFn = storyAnnotations;
    storyObject = storyAnnotations;
  } else {
    storyObject = storyAnnotations;
  }

  var _storyObject = storyObject,
      story = _storyObject.story;

  if (story) {
    logger.debug('deprecated story', story);
    deprecatedStoryAnnotationWarning();
  }

  var exportName = dist.storyNameFromExport(key);
  var name = typeof storyObject !== 'function' && storyObject.name || storyObject.storyName || (story === null || story === void 0 ? void 0 : story.name) || exportName;
  var decorators = [].concat(_toConsumableArray$5(storyObject.decorators || []), _toConsumableArray$5((story === null || story === void 0 ? void 0 : story.decorators) || []));
  var parameters = Object.assign({}, story === null || story === void 0 ? void 0 : story.parameters, storyObject.parameters);
  var args = Object.assign({}, story === null || story === void 0 ? void 0 : story.args, storyObject.args);
  var argTypes = Object.assign({}, story === null || story === void 0 ? void 0 : story.argTypes, storyObject.argTypes);
  var loaders = [].concat(_toConsumableArray$5(storyObject.loaders || []), _toConsumableArray$5((story === null || story === void 0 ? void 0 : story.loaders) || []));
  var _storyObject2 = storyObject,
      render = _storyObject2.render,
      play = _storyObject2.play; // eslint-disable-next-line no-underscore-dangle

  var id = parameters.__id || dist.toId(meta.id || meta.title, exportName);
  return Object.assign({
    id: id,
    name: name,
    decorators: decorators,
    parameters: parameters,
    args: args,
    argTypes: normalizeInputTypes(argTypes),
    loaders: loaders
  }, render && {
    render: render
  }, userStoryFn && {
    userStoryFn: userStoryFn
  }, play && {
    play: play
  });
}

function normalizeComponentAnnotations(defaultExport) {
  var title = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultExport.title;
  var importPath = arguments.length > 2 ? arguments[2] : undefined;
  var id = defaultExport.id,
      argTypes = defaultExport.argTypes;
  return Object.assign({
    id: dist.sanitize(id || title)
  }, defaultExport, {
    title: title
  }, argTypes && {
    argTypes: normalizeInputTypes(argTypes)
  }, {
    parameters: Object.assign({
      fileName: importPath
    }, defaultExport.parameters)
  });
}

var _excluded$5 = ["default", "__namedExportsOrder"];

function _objectWithoutProperties$5(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$5(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$5(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var checkGlobals = function checkGlobals(parameters) {
  var globals = parameters.globals,
      globalTypes = parameters.globalTypes;

  if (globals || globalTypes) {
    logger.error('Global args/argTypes can only be set globally', JSON.stringify({
      globals: globals,
      globalTypes: globalTypes
    }));
  }
};

var checkStorySort = function checkStorySort(parameters) {
  var options = parameters.options;
  if (options !== null && options !== void 0 && options.storySort) logger.error('The storySort option parameter can only be set globally');
};

var checkDisallowedParameters = function checkDisallowedParameters(parameters) {
  if (!parameters) {
    return;
  }

  checkGlobals(parameters);
  checkStorySort(parameters);
}; // Given the raw exports of a CSF file, check and normalize it.


function processCSFFile(moduleExports, importPath, title) {
  var defaultExport = moduleExports.default;
      moduleExports.__namedExportsOrder;
      var namedExports = _objectWithoutProperties$5(moduleExports, _excluded$5);

  var meta = normalizeComponentAnnotations(defaultExport, title, importPath);
  checkDisallowedParameters(meta.parameters);
  var csfFile = {
    meta: meta,
    stories: {}
  };
  Object.keys(namedExports).forEach(function (key) {
    if (dist.isExportStory(key, meta)) {
      var storyMeta = normalizeStory(key, namedExports[key], meta);
      checkDisallowedParameters(storyMeta.parameters);
      csfFile.stories[storyMeta.id] = storyMeta;
    }
  });
  return csfFile;
}

function _toConsumableArray$4(arr) {
  return _arrayWithoutHoles$4(arr) || _iterableToArray$4(arr) || _unsupportedIterableToArray$9(arr) || _nonIterableSpread$4();
}

function _nonIterableSpread$4() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _iterableToArray$4(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$4(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$9(arr);
}

function _slicedToArray$6(arr, i) {
  return _arrayWithHoles$7(arr) || _iterableToArrayLimit$6(arr, i) || _unsupportedIterableToArray$9(arr, i) || _nonIterableRest$7();
}

function _nonIterableRest$7() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$9(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$9(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$9(o, minLen);
}

function _arrayLikeToArray$9(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$6(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$7(arr) {
  if (Array.isArray(arr)) return arr;
}
/**
 * Safely combine parameters recursively. Only copy objects when needed.
 * Algorithm = always overwrite the existing value UNLESS both values
 * are plain objects. In this case flag the key as "special" and handle
 * it with a heuristic.
 */

var combineParameters = function combineParameters() {
  for (var _len = arguments.length, parameterSets = new Array(_len), _key = 0; _key < _len; _key++) {
    parameterSets[_key] = arguments[_key];
  }

  var mergeKeys = {};
  var combined = parameterSets.filter(Boolean).reduce(function (acc, p) {
    Object.entries(p).forEach(function (_ref) {
      var _ref2 = _slicedToArray$6(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];

      var existing = acc[key];

      if (Array.isArray(value) || typeof existing === 'undefined') {
        acc[key] = value;
      } else if (isPlainObject_1(value) && isPlainObject_1(existing)) {
        // do nothing, we'll handle this later
        mergeKeys[key] = true;
      } else if (typeof value !== 'undefined') {
        acc[key] = value;
      }
    });
    return acc;
  }, {});
  Object.keys(mergeKeys).forEach(function (key) {
    var mergeValues = parameterSets.filter(Boolean).map(function (p) {
      return p[key];
    }).filter(function (value) {
      return typeof value !== 'undefined';
    });

    if (mergeValues.every(function (value) {
      return isPlainObject_1(value);
    })) {
      combined[key] = combineParameters.apply(void 0, _toConsumableArray$4(mergeValues));
    } else {
      combined[key] = mergeValues[mergeValues.length - 1];
    }
  });
  return combined;
};

function _defineProperty$2(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray$5(arr, i) {
  return _arrayWithHoles$6(arr) || _iterableToArrayLimit$5(arr, i) || _unsupportedIterableToArray$8(arr, i) || _nonIterableRest$6();
}

function _nonIterableRest$6() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$8(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$8(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$8(o, minLen);
}

function _arrayLikeToArray$8(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$5(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$6(arr) {
  if (Array.isArray(arr)) return arr;
}
function useSharedState(sharedId, defaultState) {
  var channel = addons.getChannel();

  var _ref = channel.last("".concat(SHARED_STATE_CHANGED, "-manager-").concat(sharedId)) || channel.last("".concat(SHARED_STATE_SET, "-manager-").concat(sharedId)) || [],
      _ref2 = _slicedToArray$5(_ref, 1),
      lastValue = _ref2[0];

  var _useState = useState(lastValue || defaultState),
      _useState2 = _slicedToArray$5(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var allListeners = useMemo(function () {
    var _ref3;

    return _ref3 = {}, _defineProperty$2(_ref3, "".concat(SHARED_STATE_CHANGED, "-manager-").concat(sharedId), function manager(s) {
      return setState(s);
    }), _defineProperty$2(_ref3, "".concat(SHARED_STATE_SET, "-manager-").concat(sharedId), function manager(s) {
      return setState(s);
    }), _ref3;
  }, [sharedId]);
  var emit = useChannel(allListeners, [sharedId]);
  useEffect(function () {
    // init
    if (defaultState !== undefined && !lastValue) {
      emit("".concat(SHARED_STATE_SET, "-client-").concat(sharedId), defaultState);
    }
  }, [sharedId]);
  return [state, function (s) {
    setState(s);
    emit("".concat(SHARED_STATE_CHANGED, "-client-").concat(sharedId), s);
  }];
}
function useAddonState(addonId, defaultState) {
  return useSharedState(addonId, defaultState);
}

var _excluded$4 = ["componentId", "title", "kind", "id", "name", "story", "parameters", "initialArgs", "argTypes"];

function _objectWithoutProperties$4(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$4(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$4(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function decorateStory(storyFn, decorator, bindWithContext) {
  // Bind the partially decorated storyFn so that when it is called it always knows about the story context,
  // no matter what it is passed directly. This is because we cannot guarantee a decorator will
  // pass the context down to the next decorated story in the chain.
  var boundStoryFunction = bindWithContext(storyFn);
  return function (context) {
    return decorator(boundStoryFunction, context);
  };
}
/**
 * Currently StoryContextUpdates are allowed to have any key in the type.
 * However, you cannot overwrite any of the build-it "static" keys.
 *
 * @param inputContextUpdate StoryContextUpdate
 * @returns StoryContextUpdate
 */

function sanitizeStoryContextUpdate() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  _ref.componentId;
      _ref.title;
      _ref.kind;
      _ref.id;
      _ref.name;
      _ref.story;
      _ref.parameters;
      _ref.initialArgs;
      _ref.argTypes;
      var update = _objectWithoutProperties$4(_ref, _excluded$4);

  return update;
}
function defaultDecorateStory(storyFn, decorators) {
  // We use a trick to avoid recreating the bound story function inside `decorateStory`.
  // Instead we pass it a context "getter", which is defined once (at "decoration time")
  // The getter reads a variable which is scoped to this call of `decorateStory`
  // (ie to this story), so there is no possibility of overlap.
  // This will break if you call the same story twice interleaved
  // (React might do it if you rendered the same story twice in the one ReactDom.render call, for instance)
  var contextStore = {};
  /**
   * When you call the story function inside a decorator, e.g.:
   *
   * ```jsx
   * <div>{storyFn({ foo: 'bar' })}</div>
   * ```
   *
   * This will override the `foo` property on the `innerContext`, which gets
   * merged in with the default context
   */

  var bindWithContext = function bindWithContext(decoratedStoryFn) {
    return function (update) {
      contextStore.value = Object.assign({}, contextStore.value, sanitizeStoryContextUpdate(update));
      return decoratedStoryFn(contextStore.value);
    };
  };

  var decoratedWithContextStore = decorators.reduce(function (story, decorator) {
    return decorateStory(story, decorator, bindWithContext);
  }, storyFn);
  return function (context) {
    contextStore.value = context;
    return decoratedWithContextStore(context); // Pass the context directly into the first decorator
  };
}

var _templateObject$5;

function _slicedToArray$4(arr, i) {
  return _arrayWithHoles$5(arr) || _iterableToArrayLimit$4(arr, i) || _unsupportedIterableToArray$7(arr, i) || _nonIterableRest$5();
}

function _nonIterableRest$5() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _iterableToArrayLimit$4(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$5(arr) {
  if (Array.isArray(arr)) return arr;
}

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator$1(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _toConsumableArray$3(arr) {
  return _arrayWithoutHoles$3(arr) || _iterableToArray$3(arr) || _unsupportedIterableToArray$7(arr) || _nonIterableSpread$3();
}

function _nonIterableSpread$3() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$7(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$7(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$7(o, minLen);
}

function _iterableToArray$3(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$3(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$7(arr);
}

function _arrayLikeToArray$7(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _taggedTemplateLiteral$5(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
var argTypeDefaultValueWarning = node$1(function () {}, _default(_templateObject$5 || (_templateObject$5 = _taggedTemplateLiteral$5(["\n  `argType.defaultValue` is deprecated and will be removed in Storybook 7.0.\n\n  https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#no-longer-inferring-default-values-of-args"], ["\n  \\`argType.defaultValue\\` is deprecated and will be removed in Storybook 7.0.\n\n  https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#no-longer-inferring-default-values-of-args"])))); // Combine all the metadata about a story (both direct and inherited from the component/global scope)
// into a "renderable" story function, with all decorators applied, parameters passed as context etc
//
// Note that this story function is *stateless* in the sense that it does not track args or globals
// Instead, it is expected these are tracked separately (if necessary) and are passed into each invocation.

function prepareStory(storyAnnotations, componentAnnotations, projectAnnotations) {
  var _global$FEATURES; // NOTE: in the current implementation we are doing everything once, up front, rather than doing
  // anything at render time. The assumption is that as we don't load all the stories at once, this
  // will have a limited cost. If this proves misguided, we can refactor it.


  var id = storyAnnotations.id,
      name = storyAnnotations.name;
  var title = componentAnnotations.title;
  var parameters = combineParameters(projectAnnotations.parameters, componentAnnotations.parameters, storyAnnotations.parameters);
  var decorators = [].concat(_toConsumableArray$3(storyAnnotations.decorators || []), _toConsumableArray$3(componentAnnotations.decorators || []), _toConsumableArray$3(projectAnnotations.decorators || [])); // Currently it is only possible to set these globally

  var _projectAnnotations$a = projectAnnotations.applyDecorators,
      applyDecorators = _projectAnnotations$a === void 0 ? defaultDecorateStory : _projectAnnotations$a,
      _projectAnnotations$a2 = projectAnnotations.argTypesEnhancers,
      argTypesEnhancers = _projectAnnotations$a2 === void 0 ? [] : _projectAnnotations$a2,
      _projectAnnotations$a3 = projectAnnotations.argsEnhancers,
      argsEnhancers = _projectAnnotations$a3 === void 0 ? [] : _projectAnnotations$a3;
  var loaders = [].concat(_toConsumableArray$3(projectAnnotations.loaders || []), _toConsumableArray$3(componentAnnotations.loaders || []), _toConsumableArray$3(storyAnnotations.loaders || [])); // The render function on annotations *has* to be an `ArgsStoryFn`, so when we normalize
  // CSFv1/2, we use a new field called `userStoryFn` so we know that it can be a LegacyStoryFn

  var render = storyAnnotations.userStoryFn || storyAnnotations.render || componentAnnotations.render || projectAnnotations.render;
  var passedArgTypes = combineParameters(projectAnnotations.argTypes, componentAnnotations.argTypes, storyAnnotations.argTypes);
  var _parameters$passArgsF = parameters.passArgsFirst,
      passArgsFirst = _parameters$passArgsF === void 0 ? true : _parameters$passArgsF; // eslint-disable-next-line no-underscore-dangle

  parameters.__isArgsStory = passArgsFirst && render.length > 0; // Pull out args[X] into initialArgs for argTypes enhancers

  var passedArgs = Object.assign({}, projectAnnotations.args, componentAnnotations.args, storyAnnotations.args);
  var contextForEnhancers = {
    componentId: componentAnnotations.id,
    title: title,
    kind: title,
    // Back compat
    id: id,
    name: name,
    story: name,
    // Back compat
    component: componentAnnotations.component,
    subcomponents: componentAnnotations.subcomponents,
    parameters: parameters,
    initialArgs: passedArgs,
    argTypes: passedArgTypes
  };
  contextForEnhancers.argTypes = argTypesEnhancers.reduce(function (accumulatedArgTypes, enhancer) {
    return enhancer(Object.assign({}, contextForEnhancers, {
      argTypes: accumulatedArgTypes
    }));
  }, contextForEnhancers.argTypes); // Add argTypes[X].defaultValue to initial args (note this deprecated)
  // We need to do this *after* the argTypesEnhancers as they may add defaultValues

  var defaultArgs = getValuesFromArgTypes(contextForEnhancers.argTypes);

  if (Object.keys(defaultArgs).length > 0) {
    argTypeDefaultValueWarning();
  }

  var initialArgsBeforeEnhancers = Object.assign({}, defaultArgs, passedArgs);
  contextForEnhancers.initialArgs = argsEnhancers.reduce(function (accumulatedArgs, enhancer) {
    return Object.assign({}, accumulatedArgs, enhancer(Object.assign({}, contextForEnhancers, {
      initialArgs: accumulatedArgs
    })));
  }, initialArgsBeforeEnhancers); // Add some of our metadata into parameters as we used to do this in 6.x and users may be relying on it

  if (!((_global$FEATURES = window_1.FEATURES) !== null && _global$FEATURES !== void 0 && _global$FEATURES.breakingChangesV7)) {
    contextForEnhancers.parameters = Object.assign({}, contextForEnhancers.parameters, {
      __id: id,
      globals: projectAnnotations.globals,
      globalTypes: projectAnnotations.globalTypes,
      args: contextForEnhancers.initialArgs,
      argTypes: contextForEnhancers.argTypes
    });
  }

  var applyLoaders =
  /*#__PURE__*/
  function () {
    var _ref = _asyncToGenerator$1(
    /*#__PURE__*/
    regeneratorRuntime.mark(function _callee(context) {
      var loadResults, loaded;
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all(loaders.map(function (loader) {
                return loader(context);
              }));

            case 2:
              loadResults = _context.sent;
              loaded = Object.assign.apply(Object, [{}].concat(_toConsumableArray$3(loadResults)));
              return _context.abrupt("return", Object.assign({}, context, {
                loaded: loaded
              }));

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function applyLoaders(_x) {
      return _ref.apply(this, arguments);
    };
  }();

  var undecoratedStoryFn = function undecoratedStoryFn(context) {
    var mappedArgs = Object.entries(context.args).reduce(function (acc, _ref2) {
      var _context$argTypes$key;

      var _ref3 = _slicedToArray$4(_ref2, 2),
          key = _ref3[0],
          val = _ref3[1];

      var mapping = (_context$argTypes$key = context.argTypes[key]) === null || _context$argTypes$key === void 0 ? void 0 : _context$argTypes$key.mapping;
      acc[key] = mapping && val in mapping ? mapping[val] : val;
      return acc;
    }, {});
    var includedArgs = Object.entries(mappedArgs).reduce(function (acc, _ref4) {
      var _ref5 = _slicedToArray$4(_ref4, 2),
          key = _ref5[0],
          val = _ref5[1];

      var argType = context.argTypes[key] || {};
      if (dist.includeConditionalArg(argType, mappedArgs, context.globals)) acc[key] = val;
      return acc;
    }, {});
    var includedContext = Object.assign({}, context, {
      args: includedArgs
    });
    var _context$parameters$p = context.parameters.passArgsFirst,
        renderTimePassArgsFirst = _context$parameters$p === void 0 ? true : _context$parameters$p;
    return renderTimePassArgsFirst ? render(includedContext.args, includedContext) : render(includedContext);
  };

  var decoratedStoryFn = applyHooks(applyDecorators)(undecoratedStoryFn, decorators);

  var unboundStoryFn = function unboundStoryFn(context) {
    var _global$FEATURES2;

    var finalContext = context;

    if ((_global$FEATURES2 = window_1.FEATURES) !== null && _global$FEATURES2 !== void 0 && _global$FEATURES2.argTypeTargetsV7) {
      var argsByTarget = groupArgsByTarget(Object.assign({
        args: context.args
      }, context));
      finalContext = Object.assign({}, context, {
        allArgs: context.args,
        argsByTarget: argsByTarget,
        args: argsByTarget[NO_TARGET_NAME] || {}
      });
    }

    return decoratedStoryFn(finalContext);
  };

  var playFunction = storyAnnotations.play;
  return Object.freeze(Object.assign({}, contextForEnhancers, {
    originalStoryFn: render,
    undecoratedStoryFn: undecoratedStoryFn,
    unboundStoryFn: unboundStoryFn,
    applyLoaders: applyLoaders,
    playFunction: playFunction
  }));
}

var _templateObject$4;

function _taggedTemplateLiteral$4(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _typeof$1(obj) {
  "@babel/helpers - typeof";

  return _typeof$1 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$1(obj);
}

var inferType = function inferType(value, name, visited) {
  var type = _typeof$1(value);

  switch (type) {
    case 'boolean':
    case 'string':
    case 'number':
    case 'function':
    case 'symbol':
      return {
        name: type
      };
  }

  if (value) {
    if (visited.has(value)) {
      logger.warn(_default(_templateObject$4 || (_templateObject$4 = _taggedTemplateLiteral$4(["\n        We've detected a cycle in arg '", "'. Args should be JSON-serializable.\n\n        Consider using the mapping feature or fully custom args:\n        - Mapping: https://storybook.js.org/docs/react/writing-stories/args#mapping-to-complex-arg-values\n        - Custom args: https://storybook.js.org/docs/react/essentials/controls#fully-custom-args\n      "])), name));
      return {
        name: 'other',
        value: 'cyclic object'
      };
    }

    visited.add(value);

    if (Array.isArray(value)) {
      var childType = value.length > 0 ? inferType(value[0], name, new Set(visited)) : {
        name: 'other',
        value: 'unknown'
      };
      return {
        name: 'array',
        value: childType
      };
    }

    var fieldTypes = mapValues_1(value, function (field) {
      return inferType(field, name, new Set(visited));
    });
    return {
      name: 'object',
      value: fieldTypes
    };
  }

  return {
    name: 'object',
    value: {}
  };
};

var inferArgTypes = function inferArgTypes(context) {
  var id = context.id,
      _context$argTypes = context.argTypes,
      userArgTypes = _context$argTypes === void 0 ? {} : _context$argTypes,
      _context$initialArgs = context.initialArgs,
      initialArgs = _context$initialArgs === void 0 ? {} : _context$initialArgs;
  var argTypes = mapValues_1(initialArgs, function (arg, key) {
    return {
      name: key,
      type: inferType(arg, "".concat(id, ".").concat(key), new Set())
    };
  });
  var userArgTypesNames = mapValues_1(userArgTypes, function (argType, key) {
    return {
      name: key
    };
  });
  return combineParameters(argTypes, userArgTypesNames, userArgTypes);
};
inferArgTypes.secondPass = true;

var redefine$4 = redefine$d.exports;

var regexpExec$4 = regexpExec$5;

var fails$5 = fails$q;

var wellKnownSymbol$4 = wellKnownSymbol$q;

var createNonEnumerableProperty$1 = createNonEnumerableProperty$b;

var SPECIES$1 = wellKnownSymbol$4('species');
var RegExpPrototype$4 = RegExp.prototype;
var REPLACE_SUPPORTS_NAMED_GROUPS$1 = !fails$5(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;

  re.exec = function () {
    var result = [];
    result.groups = {
      a: '7'
    };
    return result;
  };

  return ''.replace(re, '$<a>') !== '7';
}); // IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0

var REPLACE_KEEPS_$0$1 = function () {
  // eslint-disable-next-line regexp/prefer-escape-replacement-dollar-char -- required for testing
  return 'a'.replace(/./, '$0') === '$0';
}();

var REPLACE$1 = wellKnownSymbol$4('replace'); // Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string

var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$1 = function () {
  if (/./[REPLACE$1]) {
    return /./[REPLACE$1]('a', '$0') === '';
  }

  return false;
}(); // Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
// Weex JS has frozen built-in prototypes, so use try / catch wrapper


var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC$1 = !fails$5(function () {
  // eslint-disable-next-line regexp/no-empty-group -- required for testing
  var re = /(?:)/;
  var originalExec = re.exec;

  re.exec = function () {
    return originalExec.apply(this, arguments);
  };

  var result = 'ab'.split(re);
  return result.length !== 2 || result[0] !== 'a' || result[1] !== 'b';
});

var fixRegexpWellKnownSymbolLogic$1 = function (KEY, length, exec, sham) {
  var SYMBOL = wellKnownSymbol$4(KEY);
  var DELEGATES_TO_SYMBOL = !fails$5(function () {
    // String methods call symbol-named RegEp methods
    var O = {};

    O[SYMBOL] = function () {
      return 7;
    };

    return ''[KEY](O) != 7;
  });
  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails$5(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {}; // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.

      re.constructor = {};

      re.constructor[SPECIES$1] = function () {
        return re;
      };

      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () {
      execCalled = true;
      return null;
    };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (!DELEGATES_TO_SYMBOL || !DELEGATES_TO_EXEC || KEY === 'replace' && !(REPLACE_SUPPORTS_NAMED_GROUPS$1 && REPLACE_KEEPS_$0$1 && !REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$1) || KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC$1) {
    var nativeRegExpMethod = /./[SYMBOL];
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      var $exec = regexp.exec;

      if ($exec === regexpExec$4 || $exec === RegExpPrototype$4.exec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return {
            done: true,
            value: nativeRegExpMethod.call(regexp, str, arg2)
          };
        }

        return {
          done: true,
          value: nativeMethod.call(str, regexp, arg2)
        };
      }

      return {
        done: false
      };
    }, {
      REPLACE_KEEPS_$0: REPLACE_KEEPS_$0$1,
      REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE: REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE$1
    });
    var stringMethod = methods[0];
    var regexMethod = methods[1];
    redefine$4(String.prototype, KEY, stringMethod);
    redefine$4(RegExpPrototype$4, SYMBOL, length == 2 // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
    // 21.2.5.11 RegExp.prototype[@@split](string, limit)
    ? function (string, arg) {
      return regexMethod.call(string, this, arg);
    } // 21.2.5.6 RegExp.prototype[@@match](string)
    // 21.2.5.9 RegExp.prototype[@@search](string)
    : function (string) {
      return regexMethod.call(string, this);
    });
  }

  if (sham) createNonEnumerableProperty$1(RegExpPrototype$4[SYMBOL], 'sham', true);
};

var charAt = stringMultibyte.charAt; // `AdvanceStringIndex` abstract operation
// https://tc39.es/ecma262/#sec-advancestringindex


var advanceStringIndex$3 = function (S, index, unicode) {
  return index + (unicode ? charAt(S, index).length : 1);
};

var classof$2 = classofRaw$1;

var regexpExec$3 = regexpExec$5; // `RegExpExec` abstract operation
// https://tc39.es/ecma262/#sec-regexpexec


var regexpExecAbstract$1 = function (R, S) {
  var exec = R.exec;

  if (typeof exec === 'function') {
    var result = exec.call(R, S);

    if (typeof result !== 'object') {
      throw TypeError('RegExp exec method returned something other than an Object or null');
    }

    return result;
  }

  if (classof$2(R) !== 'RegExp') {
    throw TypeError('RegExp#exec called on incompatible receiver');
  }

  return regexpExec$3.call(R, S);
};

var fixRegExpWellKnownSymbolLogic$3 = fixRegexpWellKnownSymbolLogic$1;

var anObject$4 = anObject$h;

var toLength$2 = toLength$b;

var requireObjectCoercible$3 = requireObjectCoercible$a;

var advanceStringIndex$2 = advanceStringIndex$3;

var regExpExec$2 = regexpExecAbstract$1; // @@match logic


fixRegExpWellKnownSymbolLogic$3('match', 1, function (MATCH, nativeMatch, maybeCallNative) {
  return [// `String.prototype.match` method
  // https://tc39.es/ecma262/#sec-string.prototype.match
  function match(regexp) {
    var O = requireObjectCoercible$3(this);
    var matcher = regexp == undefined ? undefined : regexp[MATCH];
    return matcher !== undefined ? matcher.call(regexp, O) : new RegExp(regexp)[MATCH](String(O));
  }, // `RegExp.prototype[@@match]` method
  // https://tc39.es/ecma262/#sec-regexp.prototype-@@match
  function (regexp) {
    var res = maybeCallNative(nativeMatch, regexp, this);
    if (res.done) return res.value;
    var rx = anObject$4(regexp);
    var S = String(this);
    if (!rx.global) return regExpExec$2(rx, S);
    var fullUnicode = rx.unicode;
    rx.lastIndex = 0;
    var A = [];
    var n = 0;
    var result;

    while ((result = regExpExec$2(rx, S)) !== null) {
      var matchStr = String(result[0]);
      A[n] = matchStr;
      if (matchStr === '') rx.lastIndex = advanceStringIndex$2(S, toLength$2(rx.lastIndex), fullUnicode);
      n++;
    }

    return n === 0 ? null : A;
  }];
});

var arrayPush$1 = _arrayPush,
    getPrototype = _getPrototype,
    getSymbols = _getSymbols,
    stubArray = stubArray_1;
/* Built-in method references for those with the same name as other `lodash` methods. */


var nativeGetSymbols = Object.getOwnPropertySymbols;
/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */

var getSymbolsIn$1 = !nativeGetSymbols ? stubArray : function (object) {
  var result = [];

  while (object) {
    arrayPush$1(result, getSymbols(object));
    object = getPrototype(object);
  }

  return result;
};
var _getSymbolsIn = getSymbolsIn$1;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function nativeKeysIn$1(object) {
  var result = [];

  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }

  return result;
}

var _nativeKeysIn = nativeKeysIn$1;

var isObject$4 = isObject_1$1,
    isPrototype = _isPrototype,
    nativeKeysIn = _nativeKeysIn;
/** Used for built-in method references. */


var objectProto$2 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty$1 = objectProto$2.hasOwnProperty;
/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function baseKeysIn$1(object) {
  if (!isObject$4(object)) {
    return nativeKeysIn(object);
  }

  var isProto = isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$1.call(object, key)))) {
      result.push(key);
    }
  }

  return result;
}

var _baseKeysIn = baseKeysIn$1;

var arrayLikeKeys = _arrayLikeKeys,
    baseKeysIn = _baseKeysIn,
    isArrayLike = isArrayLike_1;
/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */


function keysIn$1(object) {
  return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}

var keysIn_1 = keysIn$1;

var baseGetAllKeys = _baseGetAllKeys,
    getSymbolsIn = _getSymbolsIn,
    keysIn = keysIn_1;
/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */


function getAllKeysIn$1(object) {
  return baseGetAllKeys(object, keysIn, getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn$1;

var arrayMap = _arrayMap,
    baseIteratee = _baseIteratee,
    basePickBy = _basePickBy,
    getAllKeysIn = _getAllKeysIn;
/**
 * Creates an object composed of the `object` properties `predicate` returns
 * truthy for. The predicate is invoked with two arguments: (value, key).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The source object.
 * @param {Function} [predicate=_.identity] The function invoked per property.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pickBy(object, _.isNumber);
 * // => { 'a': 1, 'c': 3 }
 */


function pickBy(object, predicate) {
  if (object == null) {
    return {};
  }

  var props = arrayMap(getAllKeysIn(object), function (prop) {
    return [prop];
  });
  predicate = baseIteratee(predicate);
  return basePickBy(object, props, function (value, path) {
    return predicate(value, path[0]);
  });
}

var pickBy_1 = pickBy;

var matches = function matches(name, descriptor) {
  return Array.isArray(descriptor) ? descriptor.includes(name) : name.match(descriptor);
};

var filterArgTypes = function filterArgTypes(argTypes, include, exclude) {
  if (!include && !exclude) {
    return argTypes;
  }

  return argTypes && pickBy_1(argTypes, function (argType, key) {
    var name = argType.name || key;
    return (!include || matches(name, include)) && (!exclude || !matches(name, exclude));
  });
};

var inferControl = function inferControl(argType, name, matchers) {
  var type = argType.type,
      options = argType.options;

  if (!type && !options) {
    return undefined;
  } // args that end with background or color e.g. iconColor


  if (matchers.color && matchers.color.test(name)) {
    var controlType = argType.type.name;

    if (controlType === 'string') {
      return {
        control: {
          type: 'color'
        }
      };
    }

    logger.warn("Addon controls: Control of type color only supports string, received \"".concat(controlType, "\" instead"));
  } // args that end with date e.g. purchaseDate


  if (matchers.date && matchers.date.test(name)) {
    return {
      control: {
        type: 'date'
      }
    };
  }

  switch (type.name) {
    case 'array':
      return {
        control: {
          type: 'object'
        }
      };

    case 'boolean':
      return {
        control: {
          type: 'boolean'
        }
      };

    case 'string':
      return {
        control: {
          type: 'text'
        }
      };

    case 'number':
      return {
        control: {
          type: 'number'
        }
      };

    case 'enum':
      {
        var _ref = type,
            value = _ref.value;
        return {
          control: {
            type: (value === null || value === void 0 ? void 0 : value.length) <= 5 ? 'radio' : 'select'
          },
          options: value
        };
      }

    case 'function':
    case 'symbol':
      return null;

    default:
      return {
        control: {
          type: options ? 'select' : 'object'
        }
      };
  }
};

var inferControls = function inferControls(context) {
  var argTypes = context.argTypes,
      _context$parameters = context.parameters,
      __isArgsStory = _context$parameters.__isArgsStory,
      _context$parameters$c = _context$parameters.controls;
  _context$parameters$c = _context$parameters$c === void 0 ? {} : _context$parameters$c;
  var _context$parameters$c2 = _context$parameters$c.include,
      include = _context$parameters$c2 === void 0 ? null : _context$parameters$c2,
      _context$parameters$c3 = _context$parameters$c.exclude,
      exclude = _context$parameters$c3 === void 0 ? null : _context$parameters$c3,
      _context$parameters$c4 = _context$parameters$c.matchers,
      matchers = _context$parameters$c4 === void 0 ? {} : _context$parameters$c4;
  if (!__isArgsStory) return argTypes;
  var filteredArgTypes = filterArgTypes(argTypes, include, exclude);
  var withControls = mapValues_1(filteredArgTypes, function (argType, name) {
    return (argType === null || argType === void 0 ? void 0 : argType.type) && inferControl(argType, name, matchers);
  });
  return combineParameters(withControls, filteredArgTypes);
};
inferControls.secondPass = true;

var _excluded$3 = ["argTypes", "globalTypes", "argTypesEnhancers"];

function _toConsumableArray$2(arr) {
  return _arrayWithoutHoles$2(arr) || _iterableToArray$2(arr) || _unsupportedIterableToArray$6(arr) || _nonIterableSpread$2();
}

function _nonIterableSpread$2() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$6(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$6(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$6(o, minLen);
}

function _iterableToArray$2(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$2(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$6(arr);
}

function _arrayLikeToArray$6(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _objectWithoutProperties$3(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$3(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$3(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
function normalizeProjectAnnotations(_ref) {
  var argTypes = _ref.argTypes,
      globalTypes = _ref.globalTypes,
      argTypesEnhancers = _ref.argTypesEnhancers,
      annotations = _objectWithoutProperties$3(_ref, _excluded$3);

  return Object.assign({}, argTypes && {
    argTypes: normalizeInputTypes(argTypes)
  }, globalTypes && {
    globalTypes: normalizeInputTypes(globalTypes)
  }, {
    argTypesEnhancers: [].concat(_toConsumableArray$2(argTypesEnhancers || []), [inferArgTypes, // inferControls technically should only run if the user is using the controls addon,
    // and so should be added by a preset there. However, as it seems some code relies on controls
    // annotations (in particular the angular implementation's `cleanArgsDecorator`), for backwards
    // compatibility reasons, we will leave this in the store until 7.0
    inferControls])
  }, annotations);
}

function _toConsumableArray$1(arr) {
  return _arrayWithoutHoles$1(arr) || _iterableToArray$1(arr) || _unsupportedIterableToArray$5(arr) || _nonIterableSpread$1();
}

function _nonIterableSpread$1() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$5(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$5(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$5(o, minLen);
}

function _iterableToArray$1(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles$1(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$5(arr);
}

function _arrayLikeToArray$5(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}
function getField(moduleExportList, field) {
  return moduleExportList.map(function (xs) {
    return xs[field];
  }).filter(Boolean);
}
function getArrayField(moduleExportList, field) {
  return getField(moduleExportList, field).reduce(function (a, b) {
    return [].concat(_toConsumableArray$1(a), _toConsumableArray$1(b));
  }, []);
}
function getObjectField(moduleExportList, field) {
  return Object.assign.apply(Object, [{}].concat(_toConsumableArray$1(getField(moduleExportList, field))));
}
function getSingletonField(moduleExportList, field) {
  return getField(moduleExportList, field).pop();
}
function composeConfigs(moduleExportList) {
  var allArgTypeEnhancers = getArrayField(moduleExportList, 'argTypesEnhancers');
  return {
    parameters: combineParameters.apply(void 0, _toConsumableArray$1(getField(moduleExportList, 'parameters'))),
    decorators: getArrayField(moduleExportList, 'decorators'),
    args: getObjectField(moduleExportList, 'args'),
    argsEnhancers: getArrayField(moduleExportList, 'argsEnhancers'),
    argTypes: getObjectField(moduleExportList, 'argTypes'),
    argTypesEnhancers: [].concat(_toConsumableArray$1(allArgTypeEnhancers.filter(function (e) {
      return !e.secondPass;
    })), _toConsumableArray$1(allArgTypeEnhancers.filter(function (e) {
      return e.secondPass;
    }))),
    globals: getObjectField(moduleExportList, 'globals'),
    globalTypes: getObjectField(moduleExportList, 'globalTypes'),
    loaders: getArrayField(moduleExportList, 'loaders'),
    render: getSingletonField(moduleExportList, 'render'),
    renderToDOM: getSingletonField(moduleExportList, 'renderToDOM'),
    applyDecorators: getSingletonField(moduleExportList, 'applyDecorators')
  };
}

var _excluded$2 = ["default", "__esModule", "__namedExportsOrder"];

function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray$3(arr, i) {
  return _arrayWithHoles$4(arr) || _iterableToArrayLimit$3(arr, i) || _unsupportedIterableToArray$4(arr, i) || _nonIterableRest$4();
}

function _nonIterableRest$4() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$4(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$4(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$4(o, minLen);
}

function _arrayLikeToArray$4(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$3(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$4(arr) {
  if (Array.isArray(arr)) return arr;
}

function _objectWithoutProperties$2(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$2(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$2(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}
var GLOBAL_STORYBOOK_PROJECT_ANNOTATIONS = {};
function setProjectAnnotations(projectAnnotations) {
  var annotations = Array.isArray(projectAnnotations) ? projectAnnotations : [projectAnnotations];
  GLOBAL_STORYBOOK_PROJECT_ANNOTATIONS = composeConfigs(annotations);
}
function composeStory(storyAnnotations, componentAnnotations) {
  var _componentAnnotations, _storyAnnotations$sto;

  var projectAnnotations = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : GLOBAL_STORYBOOK_PROJECT_ANNOTATIONS;
  var defaultConfig = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var exportsName = arguments.length > 4 ? arguments[4] : undefined;

  if (storyAnnotations === undefined) {
    throw new Error('Expected a story but received undefined.');
  } // @TODO: Support auto title
  // eslint-disable-next-line no-param-reassign


  componentAnnotations.title = (_componentAnnotations = componentAnnotations.title) !== null && _componentAnnotations !== void 0 ? _componentAnnotations : 'ComposedStory';
  var normalizedComponentAnnotations = normalizeComponentAnnotations(componentAnnotations);
  var storyName = exportsName || storyAnnotations.storyName || ((_storyAnnotations$sto = storyAnnotations.story) === null || _storyAnnotations$sto === void 0 ? void 0 : _storyAnnotations$sto.name) || storyAnnotations.name;
  var normalizedStory = normalizeStory(storyName, storyAnnotations, normalizedComponentAnnotations);
  var normalizedProjectAnnotations = normalizeProjectAnnotations(Object.assign({}, projectAnnotations, defaultConfig));
  var story = prepareStory(normalizedStory, normalizedComponentAnnotations, normalizedProjectAnnotations);
  var defaultGlobals = getValuesFromArgTypes(projectAnnotations.globalTypes);

  var composedStory = function composedStory(extraArgs) {
    var context = Object.assign({}, story, {
      hooks: new HooksContext(),
      globals: defaultGlobals,
      args: Object.assign({}, story.initialArgs, extraArgs)
    });
    return story.unboundStoryFn(context);
  };

  composedStory.storyName = storyName;
  composedStory.args = story.initialArgs;
  composedStory.play = story.playFunction;
  composedStory.parameters = story.parameters;
  return composedStory;
}
function composeStories(storiesImport, globalConfig, composeStoryFn) {
  var meta = storiesImport.default;
      storiesImport.__esModule;
      storiesImport.__namedExportsOrder;
      var stories = _objectWithoutProperties$2(storiesImport, _excluded$2);

  var composedStories = Object.entries(stories).reduce(function (storiesMap, _ref) {
    var _ref2 = _slicedToArray$3(_ref, 2),
        exportsName = _ref2[0],
        story = _ref2[1];

    if (!dist.isExportStory(exportsName, meta)) {
      return storiesMap;
    }

    var result = Object.assign(storiesMap, _defineProperty$1({}, exportsName, composeStoryFn(story, meta, globalConfig, exportsName)));
    return result;
  }, {});
  return composedStories;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray$2(arr, i) {
  return _arrayWithHoles$3(arr) || _iterableToArrayLimit$2(arr, i) || _unsupportedIterableToArray$3(arr, i) || _nonIterableRest$3();
}

function _nonIterableRest$3() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$3(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$3(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$3(o, minLen);
}

function _arrayLikeToArray$3(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$2(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$3(arr) {
  if (Array.isArray(arr)) return arr;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _classCallCheck$2(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$2(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$2(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$2(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$2(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

var CSF_CACHE_SIZE = 1000;
var STORY_CACHE_SIZE = 10000;
var StoryStore =
/*#__PURE__*/
function () {
  function StoryStore() {
    var _this = this;

    _classCallCheck$2(this, StoryStore);

    this.storyIndex = void 0;
    this.importFn = void 0;
    this.projectAnnotations = void 0;
    this.globals = void 0;
    this.args = void 0;
    this.hooks = void 0;
    this.cachedCSFFiles = void 0;
    this.processCSFFileWithCache = void 0;
    this.prepareStoryWithCache = void 0;
    this.initializationPromise = void 0;
    this.resolveInitializationPromise = void 0;

    this.getStoriesJsonData = function () {
      var value = _this.getSetStoriesPayload();

      var allowedParameters = ['fileName', 'docsOnly', 'framework', '__id', '__isArgsStory'];
      var stories = mapValues_1(value.stories, function (story) {
        var _global$FEATURES;

        return Object.assign({}, pick_1(story, ['id', 'name', 'title']), {
          importPath: _this.storyIndex.stories[story.id].importPath
        }, !((_global$FEATURES = window_1.FEATURES) !== null && _global$FEATURES !== void 0 && _global$FEATURES.breakingChangesV7) && {
          kind: story.title,
          story: story.name,
          parameters: Object.assign({}, pick_1(story.parameters, allowedParameters), {
            fileName: _this.storyIndex.stories[story.id].importPath
          })
        });
      });
      return {
        v: 3,
        stories: stories
      };
    };

    this.globals = new GlobalsStore();
    this.args = new ArgsStore();
    this.hooks = {}; // We use a cache for these two functions for two reasons:
    //  1. For performance
    //  2. To ensure that when the same story is prepared with the same inputs you get the same output

    this.processCSFFileWithCache = memoizerific(CSF_CACHE_SIZE)(processCSFFile);
    this.prepareStoryWithCache = memoizerific(STORY_CACHE_SIZE)(prepareStory); // We cannot call `loadStory()` until we've been initialized properly. But we can wait for it.

    this.initializationPromise = new synchronousPromise.SynchronousPromise(function (resolve) {
      _this.resolveInitializationPromise = resolve;
    });
  }

  _createClass$2(StoryStore, [{
    key: "setProjectAnnotations",
    value: function setProjectAnnotations(projectAnnotations) {
      // By changing `this.projectAnnotations, we implicitly invalidate the `prepareStoryWithCache`
      this.projectAnnotations = normalizeProjectAnnotations(projectAnnotations);
      var globals = projectAnnotations.globals,
          globalTypes = projectAnnotations.globalTypes;
      this.globals.set({
        globals: globals,
        globalTypes: globalTypes
      });
    }
  }, {
    key: "initialize",
    value: function initialize(_ref) {
      var storyIndex = _ref.storyIndex,
          importFn = _ref.importFn,
          _ref$cache = _ref.cache,
          cache = _ref$cache === void 0 ? false : _ref$cache;
      this.storyIndex = new StoryIndexStore(storyIndex);
      this.importFn = importFn; // We don't need the cache to be loaded to call `loadStory`, we just need the index ready

      this.resolveInitializationPromise();
      return cache ? this.cacheAllCSFFiles() : synchronousPromise.SynchronousPromise.resolve();
    } // This means that one of the CSF files has changed.
    // If the `importFn` has changed, we will invalidate both caches.
    // If the `storyIndex` data has changed, we may or may not invalidate the caches, depending
    // on whether we've loaded the relevant files yet.

  }, {
    key: "onStoriesChanged",
    value: function () {
      var _onStoriesChanged = _asyncToGenerator(
      /*#__PURE__*/
      regeneratorRuntime.mark(function _callee(_ref2) {
        var importFn, storyIndex;
        return regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                importFn = _ref2.importFn, storyIndex = _ref2.storyIndex;
                if (importFn) this.importFn = importFn;
                if (storyIndex) this.storyIndex.stories = storyIndex.stories;

                if (!this.cachedCSFFiles) {
                  _context.next = 6;
                  break;
                }

                _context.next = 6;
                return this.cacheAllCSFFiles();

              case 6:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      function onStoriesChanged(_x) {
        return _onStoriesChanged.apply(this, arguments);
      }

      return onStoriesChanged;
    }() // To load a single CSF file to service a story we need to look up the importPath in the index

  }, {
    key: "loadCSFFileByStoryId",
    value: function loadCSFFileByStoryId(storyId) {
      var _this2 = this;

      var _this$storyIndex$stor = this.storyIndex.storyIdToEntry(storyId),
          importPath = _this$storyIndex$stor.importPath,
          title = _this$storyIndex$stor.title;

      return this.importFn(importPath).then(function (moduleExports) {
        return (// We pass the title in here as it may have been generated by autoTitle on the server.
          _this2.processCSFFileWithCache(moduleExports, importPath, title)
        );
      });
    }
  }, {
    key: "loadAllCSFFiles",
    value: function loadAllCSFFiles() {
      var _this3 = this;

      var importPaths = {};
      Object.entries(this.storyIndex.stories).forEach(function (_ref3) {
        var _ref4 = _slicedToArray$2(_ref3, 2),
            storyId = _ref4[0],
            importPath = _ref4[1].importPath;

        importPaths[importPath] = storyId;
      });
      var csfFilePromiseList = Object.entries(importPaths).map(function (_ref5) {
        var _ref6 = _slicedToArray$2(_ref5, 2),
            importPath = _ref6[0],
            storyId = _ref6[1];

        return _this3.loadCSFFileByStoryId(storyId).then(function (csfFile) {
          return {
            importPath: importPath,
            csfFile: csfFile
          };
        });
      });
      return synchronousPromise.SynchronousPromise.all(csfFilePromiseList).then(function (list) {
        return list.reduce(function (acc, _ref7) {
          var importPath = _ref7.importPath,
              csfFile = _ref7.csfFile;
          acc[importPath] = csfFile;
          return acc;
        }, {});
      });
    }
  }, {
    key: "cacheAllCSFFiles",
    value: function cacheAllCSFFiles() {
      var _this4 = this;

      return this.initializationPromise.then(function () {
        return _this4.loadAllCSFFiles().then(function (csfFiles) {
          _this4.cachedCSFFiles = csfFiles;
        });
      });
    } // Load the CSF file for a story and prepare the story from it and the project annotations.

  }, {
    key: "loadStory",
    value: function () {
      var _loadStory = _asyncToGenerator(
      /*#__PURE__*/
      regeneratorRuntime.mark(function _callee2(_ref8) {
        var storyId, csfFile;
        return regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                storyId = _ref8.storyId;
                _context2.next = 3;
                return this.initializationPromise;

              case 3:
                _context2.next = 5;
                return this.loadCSFFileByStoryId(storyId);

              case 5:
                csfFile = _context2.sent;
                return _context2.abrupt("return", this.storyFromCSFFile({
                  storyId: storyId,
                  csfFile: csfFile
                }));

              case 7:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function loadStory(_x2) {
        return _loadStory.apply(this, arguments);
      }

      return loadStory;
    }() // This function is synchronous for convenience -- often times if you have a CSF file already
    // it is easier not to have to await `loadStory`.

  }, {
    key: "storyFromCSFFile",
    value: function storyFromCSFFile(_ref9) {
      var storyId = _ref9.storyId,
          csfFile = _ref9.csfFile;
      var storyAnnotations = csfFile.stories[storyId];

      if (!storyAnnotations) {
        throw new Error("Didn't find '".concat(storyId, "' in CSF file, this is unexpected"));
      }

      var componentAnnotations = csfFile.meta;
      var story = this.prepareStoryWithCache(storyAnnotations, componentAnnotations, this.projectAnnotations);
      this.args.setInitial(story);
      this.hooks[story.id] = this.hooks[story.id] || new HooksContext();
      return story;
    } // If we have a CSF file we can get all the stories from it synchronously

  }, {
    key: "componentStoriesFromCSFFile",
    value: function componentStoriesFromCSFFile(_ref10) {
      var _this5 = this;

      var csfFile = _ref10.csfFile;
      return Object.keys(this.storyIndex.stories).filter(function (storyId) {
        return !!csfFile.stories[storyId];
      }).map(function (storyId) {
        return _this5.storyFromCSFFile({
          storyId: storyId,
          csfFile: csfFile
        });
      });
    } // A prepared story does not include args, globals or hooks. These are stored in the story store
    // and updated separtely to the (immutable) story.

  }, {
    key: "getStoryContext",
    value: function getStoryContext(story) {
      return Object.assign({}, story, {
        args: this.args.get(story.id),
        globals: this.globals.get(),
        hooks: this.hooks[story.id]
      });
    }
  }, {
    key: "cleanupStory",
    value: function cleanupStory(story) {
      this.hooks[story.id].clean();
    }
  }, {
    key: "extract",
    value: function extract() {
      var _this6 = this;

      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        includeDocsOnly: false
      };

      if (!this.cachedCSFFiles) {
        throw new Error('Cannot call extract() unless you call cacheAllCSFFiles() first.');
      }

      return Object.entries(this.storyIndex.stories).reduce(function (acc, _ref11) {
        var _ref12 = _slicedToArray$2(_ref11, 2),
            storyId = _ref12[0],
            importPath = _ref12[1].importPath;

        var csfFile = _this6.cachedCSFFiles[importPath];

        var story = _this6.storyFromCSFFile({
          storyId: storyId,
          csfFile: csfFile
        });

        if (!options.includeDocsOnly && story.parameters.docsOnly) {
          return acc;
        }

        acc[storyId] = Object.entries(story).reduce(function (storyAcc, _ref13) {
          var _ref14 = _slicedToArray$2(_ref13, 2),
              key = _ref14[0],
              value = _ref14[1];

          if (typeof value === 'function') {
            return storyAcc;
          }

          if (Array.isArray(value)) {
            return Object.assign(storyAcc, _defineProperty({}, key, value.slice().sort()));
          }

          return Object.assign(storyAcc, _defineProperty({}, key, value));
        }, {
          args: story.initialArgs
        });
        return acc;
      }, {});
    }
  }, {
    key: "getSetStoriesPayload",
    value: function getSetStoriesPayload() {
      var stories = this.extract({
        includeDocsOnly: true
      });
      var kindParameters = Object.values(stories).reduce(function (acc, _ref15) {
        var title = _ref15.title;
        acc[title] = {};
        return acc;
      }, {});
      return {
        v: 2,
        globals: this.globals.get(),
        globalParameters: {},
        kindParameters: kindParameters,
        stories: stories
      };
    }
  }, {
    key: "raw",
    value: function raw() {
      var _this7 = this;

      return Object.values(this.extract()).map(function (_ref16) {
        var id = _ref16.id;
        return _this7.fromId(id);
      });
    }
  }, {
    key: "fromId",
    value: function fromId(storyId) {
      var _this8 = this;

      if (!this.cachedCSFFiles) {
        throw new Error('Cannot call fromId/raw() unless you call cacheAllCSFFiles() first.');
      }

      var importPath;

      try {
        var _this$storyIndex$stor2 = this.storyIndex.storyIdToEntry(storyId);

        importPath = _this$storyIndex$stor2.importPath;
      } catch (err) {
        return null;
      }

      var csfFile = this.cachedCSFFiles[importPath];
      var story = this.storyFromCSFFile({
        storyId: storyId,
        csfFile: csfFile
      });
      return Object.assign({}, story, {
        storyFn: function storyFn(update) {
          var context = Object.assign({}, _this8.getStoryContext(story), {
            viewMode: 'story'
          });
          return story.unboundStoryFn(Object.assign({}, context, update));
        }
      });
    }
  }]);

  return StoryStore;
}();

var DESCRIPTORS$1 = descriptors;

var global$2 = global$q;

var isForced$1 = isForced_1;

var inheritIfRequired$1 = inheritIfRequired$4;

var defineProperty$1 = objectDefineProperty.f;

var getOwnPropertyNames$1 = objectGetOwnPropertyNames.f;

var isRegExp$4 = isRegexp$1;

var getFlags$1 = regexpFlags$1;

var stickyHelpers$2 = regexpStickyHelpers;

var redefine$3 = redefine$d.exports;

var fails$4 = fails$q;

var enforceInternalState$1 = internalState.enforce;

var setSpecies$1 = setSpecies$4;

var wellKnownSymbol$3 = wellKnownSymbol$q;

var MATCH$2 = wellKnownSymbol$3('match');
var NativeRegExp$1 = global$2.RegExp;
var RegExpPrototype$3 = NativeRegExp$1.prototype;
var re1$1 = /a/g;
var re2$1 = /a/g; // "new" should create a new object, old webkit bug

var CORRECT_NEW$1 = new NativeRegExp$1(re1$1) !== re1$1;
var UNSUPPORTED_Y$2 = stickyHelpers$2.UNSUPPORTED_Y;
var FORCED$1 = DESCRIPTORS$1 && isForced$1('RegExp', !CORRECT_NEW$1 || UNSUPPORTED_Y$2 || fails$4(function () {
  re2$1[MATCH$2] = false; // RegExp constructor can alter flags and IsRegExp works correct with @@match

  return NativeRegExp$1(re1$1) != re1$1 || NativeRegExp$1(re2$1) == re2$1 || NativeRegExp$1(re1$1, 'i') != '/a/i';
})); // `RegExp` constructor
// https://tc39.es/ecma262/#sec-regexp-constructor

if (FORCED$1) {
  var RegExpWrapper$1 = function RegExp(pattern, flags) {
    var thisIsRegExp = this instanceof RegExpWrapper$1;
    var patternIsRegExp = isRegExp$4(pattern);
    var flagsAreUndefined = flags === undefined;
    var sticky;

    if (!thisIsRegExp && patternIsRegExp && pattern.constructor === RegExpWrapper$1 && flagsAreUndefined) {
      return pattern;
    }

    if (CORRECT_NEW$1) {
      if (patternIsRegExp && !flagsAreUndefined) pattern = pattern.source;
    } else if (pattern instanceof RegExpWrapper$1) {
      if (flagsAreUndefined) flags = getFlags$1.call(pattern);
      pattern = pattern.source;
    }

    if (UNSUPPORTED_Y$2) {
      sticky = !!flags && flags.indexOf('y') > -1;
      if (sticky) flags = flags.replace(/y/g, '');
    }

    var result = inheritIfRequired$1(CORRECT_NEW$1 ? new NativeRegExp$1(pattern, flags) : NativeRegExp$1(pattern, flags), thisIsRegExp ? this : RegExpPrototype$3, RegExpWrapper$1);

    if (UNSUPPORTED_Y$2 && sticky) {
      var state = enforceInternalState$1(result);
      state.sticky = true;
    }

    return result;
  };

  var proxy$1 = function (key) {
    key in RegExpWrapper$1 || defineProperty$1(RegExpWrapper$1, key, {
      configurable: true,
      get: function () {
        return NativeRegExp$1[key];
      },
      set: function (it) {
        NativeRegExp$1[key] = it;
      }
    });
  };

  var keys$1 = getOwnPropertyNames$1(NativeRegExp$1);
  var index$1 = 0;

  while (keys$1.length > index$1) proxy$1(keys$1[index$1++]);

  RegExpPrototype$3.constructor = RegExpWrapper$1;
  RegExpWrapper$1.prototype = RegExpPrototype$3;
  redefine$3(global$2, 'RegExp', RegExpWrapper$1);
} // https://tc39.es/ecma262/#sec-get-regexp-@@species


setSpecies$1('RegExp');

var redefine$2 = redefine$d.exports;

var anObject$3 = anObject$h;

var fails$3 = fails$q;

var flags = regexpFlags$1;

var TO_STRING = 'toString';
var RegExpPrototype$2 = RegExp.prototype;
var nativeToString = RegExpPrototype$2[TO_STRING];
var NOT_GENERIC = fails$3(function () {
  return nativeToString.call({
    source: 'a',
    flags: 'b'
  }) != '/a/b';
}); // FF44- RegExp#toString has a wrong name

var INCORRECT_NAME = nativeToString.name != TO_STRING; // `RegExp.prototype.toString` method
// https://tc39.es/ecma262/#sec-regexp.prototype.tostring

if (NOT_GENERIC || INCORRECT_NAME) {
  redefine$2(RegExp.prototype, TO_STRING, function toString() {
    var R = anObject$3(this);
    var p = String(R.source);
    var rf = R.flags;
    var f = String(rf === undefined && R instanceof RegExp && !('flags' in RegExpPrototype$2) ? flags.call(R) : rf);
    return '/' + p + '/' + f;
  }, {
    unsafe: true
  });
}

var toObject = toObject$b;

var floor = Math.floor;
var replace$1 = ''.replace;
var SUBSTITUTION_SYMBOLS = /\$([$&'`]|\d{1,2}|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&'`]|\d{1,2})/g; // https://tc39.es/ecma262/#sec-getsubstitution

var getSubstitution$1 = function (matched, str, position, captures, namedCaptures, replacement) {
  var tailPos = position + matched.length;
  var m = captures.length;
  var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;

  if (namedCaptures !== undefined) {
    namedCaptures = toObject(namedCaptures);
    symbols = SUBSTITUTION_SYMBOLS;
  }

  return replace$1.call(replacement, symbols, function (match, ch) {
    var capture;

    switch (ch.charAt(0)) {
      case '$':
        return '$';

      case '&':
        return matched;

      case '`':
        return str.slice(0, position);

      case "'":
        return str.slice(tailPos);

      case '<':
        capture = namedCaptures[ch.slice(1, -1)];
        break;

      default:
        // \d\d?
        var n = +ch;
        if (n === 0) return match;

        if (n > m) {
          var f = floor(n / 10);
          if (f === 0) return match;
          if (f <= m) return captures[f - 1] === undefined ? ch.charAt(1) : captures[f - 1] + ch.charAt(1);
          return match;
        }

        capture = captures[n - 1];
    }

    return capture === undefined ? '' : capture;
  });
};

var fixRegExpWellKnownSymbolLogic$2 = fixRegexpWellKnownSymbolLogic$1;

var anObject$2 = anObject$h;

var toLength$1 = toLength$b;

var toInteger = toInteger$4;

var requireObjectCoercible$2 = requireObjectCoercible$a;

var advanceStringIndex$1 = advanceStringIndex$3;

var getSubstitution = getSubstitution$1;

var regExpExec$1 = regexpExecAbstract$1;

var max = Math.max;
var min$1 = Math.min;

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
}; // @@replace logic


fixRegExpWellKnownSymbolLogic$2('replace', 2, function (REPLACE, nativeReplace, maybeCallNative, reason) {
  var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = reason.REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE;
  var REPLACE_KEEPS_$0 = reason.REPLACE_KEEPS_$0;
  var UNSAFE_SUBSTITUTE = REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE ? '$' : '$0';
  return [// `String.prototype.replace` method
  // https://tc39.es/ecma262/#sec-string.prototype.replace
  function replace(searchValue, replaceValue) {
    var O = requireObjectCoercible$2(this);
    var replacer = searchValue == undefined ? undefined : searchValue[REPLACE];
    return replacer !== undefined ? replacer.call(searchValue, O, replaceValue) : nativeReplace.call(String(O), searchValue, replaceValue);
  }, // `RegExp.prototype[@@replace]` method
  // https://tc39.es/ecma262/#sec-regexp.prototype-@@replace
  function (regexp, replaceValue) {
    if (!REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE && REPLACE_KEEPS_$0 || typeof replaceValue === 'string' && replaceValue.indexOf(UNSAFE_SUBSTITUTE) === -1) {
      var res = maybeCallNative(nativeReplace, regexp, this, replaceValue);
      if (res.done) return res.value;
    }

    var rx = anObject$2(regexp);
    var S = String(this);
    var functionalReplace = typeof replaceValue === 'function';
    if (!functionalReplace) replaceValue = String(replaceValue);
    var global = rx.global;

    if (global) {
      var fullUnicode = rx.unicode;
      rx.lastIndex = 0;
    }

    var results = [];

    while (true) {
      var result = regExpExec$1(rx, S);
      if (result === null) break;
      results.push(result);
      if (!global) break;
      var matchStr = String(result[0]);
      if (matchStr === '') rx.lastIndex = advanceStringIndex$1(S, toLength$1(rx.lastIndex), fullUnicode);
    }

    var accumulatedResult = '';
    var nextSourcePosition = 0;

    for (var i = 0; i < results.length; i++) {
      result = results[i];
      var matched = String(result[0]);
      var position = max(min$1(toInteger(result.index), S.length), 0);
      var captures = []; // NOTE: This is equivalent to
      //   captures = result.slice(1).map(maybeToString)
      // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
      // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
      // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.

      for (var j = 1; j < result.length; j++) captures.push(maybeToString(result[j]));

      var namedCaptures = result.groups;

      if (functionalReplace) {
        var replacerArgs = [matched].concat(captures, position, S);
        if (namedCaptures !== undefined) replacerArgs.push(namedCaptures);
        var replacement = String(replaceValue.apply(undefined, replacerArgs));
      } else {
        replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
      }

      if (position >= nextSourcePosition) {
        accumulatedResult += S.slice(nextSourcePosition, position) + replacement;
        nextSourcePosition = position + matched.length;
      }
    }

    return accumulatedResult + S.slice(nextSourcePosition);
  }];
});

var fixRegExpWellKnownSymbolLogic$1 = fixRegexpWellKnownSymbolLogic$1;

var isRegExp$3 = isRegexp$1;

var anObject$1 = anObject$h;

var requireObjectCoercible$1 = requireObjectCoercible$a;

var speciesConstructor = speciesConstructor$2;

var advanceStringIndex = advanceStringIndex$3;

var toLength = toLength$b;

var callRegExpExec = regexpExecAbstract$1;

var regexpExec$2 = regexpExec$5;

var stickyHelpers$1 = regexpStickyHelpers;

var UNSUPPORTED_Y$1 = stickyHelpers$1.UNSUPPORTED_Y;
var arrayPush = [].push;
var min = Math.min;
var MAX_UINT32 = 0xFFFFFFFF; // @@split logic

fixRegExpWellKnownSymbolLogic$1('split', 2, function (SPLIT, nativeSplit, maybeCallNative) {
  var internalSplit;

  if ('abbc'.split(/(b)*/)[1] == 'c' || // eslint-disable-next-line regexp/no-empty-group -- required for testing
  'test'.split(/(?:)/, -1).length != 4 || 'ab'.split(/(?:ab)*/).length != 2 || '.'.split(/(.?)(.?)/).length != 4 || // eslint-disable-next-line regexp/no-assertion-capturing-group, regexp/no-empty-group -- required for testing
  '.'.split(/()()/).length > 1 || ''.split(/.?/).length) {
    // based on es5-shim implementation, need to rework it
    internalSplit = function (separator, limit) {
      var string = String(requireObjectCoercible$1(this));
      var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
      if (lim === 0) return [];
      if (separator === undefined) return [string]; // If `separator` is not a regex, use native split

      if (!isRegExp$3(separator)) {
        return nativeSplit.call(string, separator, lim);
      }

      var output = [];
      var flags = (separator.ignoreCase ? 'i' : '') + (separator.multiline ? 'm' : '') + (separator.unicode ? 'u' : '') + (separator.sticky ? 'y' : '');
      var lastLastIndex = 0; // Make `global` and avoid `lastIndex` issues by working with a copy

      var separatorCopy = new RegExp(separator.source, flags + 'g');
      var match, lastIndex, lastLength;

      while (match = regexpExec$2.call(separatorCopy, string)) {
        lastIndex = separatorCopy.lastIndex;

        if (lastIndex > lastLastIndex) {
          output.push(string.slice(lastLastIndex, match.index));
          if (match.length > 1 && match.index < string.length) arrayPush.apply(output, match.slice(1));
          lastLength = match[0].length;
          lastLastIndex = lastIndex;
          if (output.length >= lim) break;
        }

        if (separatorCopy.lastIndex === match.index) separatorCopy.lastIndex++; // Avoid an infinite loop
      }

      if (lastLastIndex === string.length) {
        if (lastLength || !separatorCopy.test('')) output.push('');
      } else output.push(string.slice(lastLastIndex));

      return output.length > lim ? output.slice(0, lim) : output;
    }; // Chakra, V8

  } else if ('0'.split(undefined, 0).length) {
    internalSplit = function (separator, limit) {
      return separator === undefined && limit === 0 ? [] : nativeSplit.call(this, separator, limit);
    };
  } else internalSplit = nativeSplit;

  return [// `String.prototype.split` method
  // https://tc39.es/ecma262/#sec-string.prototype.split
  function split(separator, limit) {
    var O = requireObjectCoercible$1(this);
    var splitter = separator == undefined ? undefined : separator[SPLIT];
    return splitter !== undefined ? splitter.call(separator, O, limit) : internalSplit.call(String(O), separator, limit);
  }, // `RegExp.prototype[@@split]` method
  // https://tc39.es/ecma262/#sec-regexp.prototype-@@split
  //
  // NOTE: This cannot be properly polyfilled in engines that don't support
  // the 'y' flag.
  function (regexp, limit) {
    var res = maybeCallNative(internalSplit, regexp, this, limit, internalSplit !== nativeSplit);
    if (res.done) return res.value;
    var rx = anObject$1(regexp);
    var S = String(this);
    var C = speciesConstructor(rx, RegExp);
    var unicodeMatching = rx.unicode;
    var flags = (rx.ignoreCase ? 'i' : '') + (rx.multiline ? 'm' : '') + (rx.unicode ? 'u' : '') + (UNSUPPORTED_Y$1 ? 'g' : 'y'); // ^(? + rx + ) is needed, in combination with some S slicing, to
    // simulate the 'y' flag.

    var splitter = new C(UNSUPPORTED_Y$1 ? '^(?:' + rx.source + ')' : rx, flags);
    var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
    if (lim === 0) return [];
    if (S.length === 0) return callRegExpExec(splitter, S) === null ? [S] : [];
    var p = 0;
    var q = 0;
    var A = [];

    while (q < S.length) {
      splitter.lastIndex = UNSUPPORTED_Y$1 ? 0 : q;
      var z = callRegExpExec(splitter, UNSUPPORTED_Y$1 ? S.slice(q) : S);
      var e;

      if (z === null || (e = min(toLength(splitter.lastIndex + (UNSUPPORTED_Y$1 ? q : 0)), S.length)) === p) {
        q = advanceStringIndex(S, q, unicodeMatching);
      } else {
        A.push(S.slice(p, q));
        if (A.length === lim) return A;

        for (var i = 1; i <= z.length - 1; i++) {
          A.push(z[i]);
          if (A.length === lim) return A;
        }

        q = p = e;
      }
    }

    A.push(S.slice(p));
    return A;
  }];
}, UNSUPPORTED_Y$1);

var slash = path => {
  const isExtendedLengthPath = /^\\\\\?\\/.test(path);
  const hasNonAscii = /[^\u0000-\u0080]+/.test(path); // eslint-disable-line no-control-regex

  if (isExtendedLengthPath || hasNonAscii) {
    return path;
  }

  return path.replace(/\\/g, '/');
};

var _templateObject$3;

function _taggedTemplateLiteral$3(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _toArray(arr) {
  return _arrayWithHoles$2(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$2(arr) || _nonIterableRest$2();
}

function _nonIterableRest$2() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _arrayWithHoles$2(arr) {
  if (Array.isArray(arr)) return arr;
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray$2(arr) || _nonIterableSpread();
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$2(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$2(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$2(o, minLen);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray$2(arr);
}

function _arrayLikeToArray$2(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}
// removed when we remove v6 back-compat.

var stripExtension = function stripExtension(path) {
  var parts = _toConsumableArray(path);

  var last = parts[parts.length - 1];
  var dotIndex = last.indexOf('.');
  var stripped = dotIndex > 0 ? last.substr(0, dotIndex) : last;
  parts[parts.length - 1] = stripped;

  var _parts = parts,
      _parts2 = _toArray(_parts),
      first = _parts2[0],
      rest = _parts2.slice(1);

  if (first === '') {
    parts = rest;
  }

  return parts;
};

var indexRe = /^index$/i; // deal with files like "atoms/button/{button,index}.stories.js"

var removeRedundantFilename = function removeRedundantFilename(paths) {
  var prevVal;
  return paths.filter(function (val, index) {
    if (index === paths.length - 1 && (val === prevVal || indexRe.test(val))) {
      return false;
    }

    prevVal = val;
    return true;
  });
};
/**
 * Combines path parts together, without duplicating separators (slashes).  Used instead of `path.join`
 * because this code runs in the browser.
 *
 * @param paths array of paths to join together.
 * @returns joined path string, with single '/' between parts
 */


function pathJoin(paths) {
  var slashes = new RegExp('/{1,}', 'g');
  return paths.join('/').replace(slashes, '/');
}

var userOrAutoTitleFromSpecifier = function userOrAutoTitleFromSpecifier(fileName, entry, userTitle) {
  var _ref = entry || {},
      directory = _ref.directory,
      importPathMatcher = _ref.importPathMatcher,
      _ref$titlePrefix = _ref.titlePrefix,
      titlePrefix = _ref$titlePrefix === void 0 ? '' : _ref$titlePrefix; // On Windows, backslashes are used in paths, which can cause problems here
  // slash makes sure we always handle paths with unix-style forward slash


  if (typeof fileName === 'number') {
    once.warn(_default(_templateObject$3 || (_templateObject$3 = _taggedTemplateLiteral$3(["\n      CSF Auto-title received a numeric fileName. This typically happens when\n      webpack is mis-configured in production mode. To force webpack to produce\n      filenames, set optimization.moduleIds = \"named\" in your webpack config.\n    "]))));
  }

  var normalizedFileName = slash(String(fileName));

  if (importPathMatcher.exec(normalizedFileName)) {
    if (!userTitle) {
      var suffix = normalizedFileName.replace(directory, '');
      var titleAndSuffix = slash(pathJoin([titlePrefix, suffix]));
      var path = titleAndSuffix.split('/');
      path = stripExtension(path);
      path = removeRedundantFilename(path);
      return path.join('/');
    }

    if (!titlePrefix) {
      return userTitle;
    }

    return slash(pathJoin([titlePrefix, userTitle]));
  }

  return undefined;
};
var userOrAutoTitle = function userOrAutoTitle(fileName, storiesEntries, userTitle) {
  for (var i = 0; i < storiesEntries.length; i += 1) {
    var title = userOrAutoTitleFromSpecifier(fileName, storiesEntries[i], userTitle);
    if (title) return title;
  }

  return userTitle || undefined;
};

var stable$1 = {exports: {}};

(function (module, exports) {
//! stable.js 0.1.8, https://github.com/Two-Screen/stable
//! © 2018 Angry Bytes and contributors. MIT licensed.
(function (global, factory) {
  module.exports = factory() ;
})(commonjsGlobal, function () {
  // This is an implementation of merge sort, without recursion.

  var stable = function (arr, comp) {
    return exec(arr.slice(), comp);
  };

  stable.inplace = function (arr, comp) {
    var result = exec(arr, comp); // This simply copies back if the result isn't in the original array,
    // which happens on an odd number of passes.

    if (result !== arr) {
      pass(result, null, arr.length, arr);
    }

    return arr;
  }; // Execute the sort using the input array and a second buffer as work space.
  // Returns one of those two, containing the final result.


  function exec(arr, comp) {
    if (typeof comp !== 'function') {
      comp = function (a, b) {
        return String(a).localeCompare(b);
      };
    } // Short-circuit when there's nothing to sort.


    var len = arr.length;

    if (len <= 1) {
      return arr;
    } // Rather than dividing input, simply iterate chunks of 1, 2, 4, 8, etc.
    // Chunks are the size of the left or right hand in merge sort.
    // Stop when the left-hand covers all of the array.


    var buffer = new Array(len);

    for (var chk = 1; chk < len; chk *= 2) {
      pass(arr, comp, chk, buffer);
      var tmp = arr;
      arr = buffer;
      buffer = tmp;
    }

    return arr;
  } // Run a single pass with the given chunk size.


  var pass = function (arr, comp, chk, result) {
    var len = arr.length;
    var i = 0; // Step size / double chunk size.

    var dbl = chk * 2; // Bounds of the left and right chunks.

    var l, r, e; // Iterators over the left and right chunk.

    var li, ri; // Iterate over pairs of chunks.

    for (l = 0; l < len; l += dbl) {
      r = l + chk;
      e = r + chk;
      if (r > len) r = len;
      if (e > len) e = len; // Iterate both chunks in parallel.

      li = l;
      ri = r;

      while (true) {
        // Compare the chunks.
        if (li < r && ri < e) {
          // This works for a regular `sort()` compatible comparator,
          // but also for a simple comparator like: `a > b`
          if (comp(arr[li], arr[ri]) <= 0) {
            result[i++] = arr[li++];
          } else {
            result[i++] = arr[ri++];
          }
        } // Nothing to compare, just flush what's left.
        else if (li < r) {
            result[i++] = arr[li++];
          } else if (ri < e) {
            result[i++] = arr[ri++];
          } // Both iterators are at the chunk ends.
          else {
              break;
            }
      }
    }
  };

  return stable;
});
}(stable$1));

var stable = stable$1.exports;

var fails$2 = fails$q;

var whitespaces = whitespaces$2;

var non = '\u200B\u0085\u180E'; // check that a method works with the correct list
// of whitespaces and has a correct name

var stringTrimForced = function (METHOD_NAME) {
  return fails$2(function () {
    return !!whitespaces[METHOD_NAME]() || non[METHOD_NAME]() != non || whitespaces[METHOD_NAME].name !== METHOD_NAME;
  });
};

var $$2 = _export;

var $trim = stringTrim.trim;

var forcedStringTrimMethod = stringTrimForced; // `String.prototype.trim` method
// https://tc39.es/ecma262/#sec-string.prototype.trim


$$2({
  target: 'String',
  proto: true,
  forced: forcedStringTrimMethod('trim')
}, {
  trim: function trim() {
    return $trim(this);
  }
});

var STORY_KIND_PATH_SEPARATOR = /\s*\/\s*/;
var storySort = function storySort() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return function (a, b) {
    // If the two stories have the same story kind, then use the default
    // ordering, which is the order they are defined in the story file.
    // only when includeNames is falsy
    if (a.title === b.title && !options.includeNames) {
      return 0;
    } // Get the StorySortParameter options.


    var method = options.method || 'configure';
    var order = options.order || []; // Examine each part of the story title in turn.

    var storyTitleA = a.title.trim().split(STORY_KIND_PATH_SEPARATOR);
    var storyTitleB = b.title.trim().split(STORY_KIND_PATH_SEPARATOR);

    if (options.includeNames) {
      storyTitleA.push(a.name);
      storyTitleB.push(b.name);
    }

    var depth = 0;

    while (storyTitleA[depth] || storyTitleB[depth]) {
      // Stories with a shorter depth should go first.
      if (!storyTitleA[depth]) {
        return -1;
      }

      if (!storyTitleB[depth]) {
        return 1;
      } // Compare the next part of the story title.


      var nameA = storyTitleA[depth];
      var nameB = storyTitleB[depth];

      if (nameA !== nameB) {
        // Look for the names in the given `order` array.
        var indexA = order.indexOf(nameA);
        var indexB = order.indexOf(nameB);
        var indexWildcard = order.indexOf('*'); // If at least one of the names is found, sort by the `order` array.

        if (indexA !== -1 || indexB !== -1) {
          // If one of the names is not found and there is a wildcard, insert it at the wildcard position.
          // Otherwise, list it last.
          if (indexA === -1) {
            if (indexWildcard !== -1) {
              indexA = indexWildcard;
            } else {
              indexA = order.length;
            }
          }

          if (indexB === -1) {
            if (indexWildcard !== -1) {
              indexB = indexWildcard;
            } else {
              indexB = order.length;
            }
          }

          return indexA - indexB;
        } // Use the default configure() order.


        if (method === 'configure') {
          return 0;
        } // Otherwise, use alphabetical order.


        return nameA.localeCompare(nameB, options.locales ? options.locales : undefined, {
          numeric: true,
          sensitivity: 'accent'
        });
      } // If a nested array is provided for a name, use it for ordering.


      var index = order.indexOf(nameA);
      order = index !== -1 && Array.isArray(order[index + 1]) ? order[index + 1] : []; // We'll need to look at the next part of the name.

      depth += 1;
    } // Identical story titles. The shortcut at the start of this function prevents
    // this from ever being used.

    /* istanbul ignore next */


    return 0;
  };
};

var _templateObject$2;

function _taggedTemplateLiteral$2(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var sortStoriesCommon = function sortStoriesCommon(stories, storySortParameter, fileNameOrder) {
  if (storySortParameter) {
    var sortFn;

    if (typeof storySortParameter === 'function') {
      sortFn = storySortParameter;
    } else {
      sortFn = storySort(storySortParameter);
    }

    stable.inplace(stories, sortFn);
  } else {
    stable.inplace(stories, function (s1, s2) {
      return fileNameOrder.indexOf(s1.importPath) - fileNameOrder.indexOf(s2.importPath);
    });
  }

  return stories;
};

var sortStoriesV7 = function sortStoriesV7(stories, storySortParameter, fileNameOrder) {
  try {
    return sortStoriesCommon(stories, storySortParameter, fileNameOrder);
  } catch (err) {
    throw new Error(_default(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral$2(["\n    Error sorting stories with sort parameter ", ":\n\n    > ", "\n    \n    Are you using a V6-style sort function in V7 mode?\n\n    More info: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#v7-style-story-sort\n  "])), storySortParameter, err.message));
  }
};

var toIndexEntry = function toIndexEntry(story) {
  var id = story.id,
      title = story.title,
      name = story.name,
      parameters = story.parameters;
  return {
    id: id,
    title: title,
    name: name,
    importPath: parameters.fileName
  };
};

var sortStoriesV6 = function sortStoriesV6(stories, storySortParameter, fileNameOrder) {
  if (storySortParameter && typeof storySortParameter === 'function') {
    stable.inplace(stories, storySortParameter);
    return stories.map(function (s) {
      return toIndexEntry(s[1]);
    });
  }

  var storiesV7 = stories.map(function (s) {
    return toIndexEntry(s[1]);
  });
  return sortStoriesCommon(storiesV7, storySortParameter, fileNameOrder);
};

var $$1 = _export$1;

var $map = arrayIteration$1.map;

var arrayMethodHasSpeciesSupport$1 = arrayMethodHasSpeciesSupport$9;

var HAS_SPECIES_SUPPORT$1 = arrayMethodHasSpeciesSupport$1('map'); // `Array.prototype.map` method
// https://tc39.es/ecma262/#sec-array.prototype.map
// with adding support of @@species

$$1({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT$1
}, {
  map: function map(callbackfn
  /* , thisArg */
  ) {
    return $map(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

var isObject$3 = isObject$J;

var classof$1 = classofRaw$3;

var wellKnownSymbol$2 = wellKnownSymbol$I;

var MATCH$1 = wellKnownSymbol$2('match'); // `IsRegExp` abstract operation
// https://tc39.es/ecma262/#sec-isregexp

var isRegexp = function (it) {
  var isRegExp;
  return isObject$3(it) && ((isRegExp = it[MATCH$1]) !== undefined ? !!isRegExp : classof$1(it) == 'RegExp');
};

var DESCRIPTORS = descriptors$1;

var global$1 = global$H;

var isForced = isForced_1$1;

var inheritIfRequired = inheritIfRequired$6;

var defineProperty = objectDefineProperty$1.f;

var getOwnPropertyNames = objectGetOwnPropertyNames$1.f;

var isRegExp$2 = isRegexp;

var getFlags = regexpFlags$3;

var stickyHelpers = regexpStickyHelpers$1;

var redefine$1 = redefine$l.exports;

var fails$1 = fails$K;

var enforceInternalState = internalState$1.enforce;

var setSpecies = setSpecies$6;

var wellKnownSymbol$1 = wellKnownSymbol$I;

var MATCH = wellKnownSymbol$1('match');
var NativeRegExp = global$1.RegExp;
var RegExpPrototype$1 = NativeRegExp.prototype;
var re1 = /a/g;
var re2 = /a/g; // "new" should create a new object, old webkit bug

var CORRECT_NEW = new NativeRegExp(re1) !== re1;
var UNSUPPORTED_Y = stickyHelpers.UNSUPPORTED_Y;
var FORCED = DESCRIPTORS && isForced('RegExp', !CORRECT_NEW || UNSUPPORTED_Y || fails$1(function () {
  re2[MATCH] = false; // RegExp constructor can alter flags and IsRegExp works correct with @@match

  return NativeRegExp(re1) != re1 || NativeRegExp(re2) == re2 || NativeRegExp(re1, 'i') != '/a/i';
})); // `RegExp` constructor
// https://tc39.es/ecma262/#sec-regexp-constructor

if (FORCED) {
  var RegExpWrapper = function RegExp(pattern, flags) {
    var thisIsRegExp = this instanceof RegExpWrapper;
    var patternIsRegExp = isRegExp$2(pattern);
    var flagsAreUndefined = flags === undefined;
    var sticky;

    if (!thisIsRegExp && patternIsRegExp && pattern.constructor === RegExpWrapper && flagsAreUndefined) {
      return pattern;
    }

    if (CORRECT_NEW) {
      if (patternIsRegExp && !flagsAreUndefined) pattern = pattern.source;
    } else if (pattern instanceof RegExpWrapper) {
      if (flagsAreUndefined) flags = getFlags.call(pattern);
      pattern = pattern.source;
    }

    if (UNSUPPORTED_Y) {
      sticky = !!flags && flags.indexOf('y') > -1;
      if (sticky) flags = flags.replace(/y/g, '');
    }

    var result = inheritIfRequired(CORRECT_NEW ? new NativeRegExp(pattern, flags) : NativeRegExp(pattern, flags), thisIsRegExp ? this : RegExpPrototype$1, RegExpWrapper);

    if (UNSUPPORTED_Y && sticky) {
      var state = enforceInternalState(result);
      state.sticky = true;
    }

    return result;
  };

  var proxy = function (key) {
    key in RegExpWrapper || defineProperty(RegExpWrapper, key, {
      configurable: true,
      get: function () {
        return NativeRegExp[key];
      },
      set: function (it) {
        NativeRegExp[key] = it;
      }
    });
  };

  var keys = getOwnPropertyNames(NativeRegExp);
  var index = 0;

  while (keys.length > index) proxy(keys[index++]);

  RegExpPrototype$1.constructor = RegExpWrapper;
  RegExpWrapper.prototype = RegExpPrototype$1;
  redefine$1(global$1, 'RegExp', RegExpWrapper);
} // https://tc39.es/ecma262/#sec-get-regexp-@@species


setSpecies('RegExp');

var $ = _export$1;

var $filter = arrayIteration$1.filter;

var arrayMethodHasSpeciesSupport = arrayMethodHasSpeciesSupport$9;

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('filter'); // `Array.prototype.filter` method
// https://tc39.es/ecma262/#sec-array.prototype.filter
// with adding support of @@species

$({
  target: 'Array',
  proto: true,
  forced: !HAS_SPECIES_SUPPORT
}, {
  filter: function filter(callbackfn
  /* , thisArg */
  ) {
    return $filter(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

var _excluded$1 = ["default", "__namedExportsOrder"];

var _templateObject$1;

function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose$1(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteral$1(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray$1(arr, i) {
  return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1();
}

function _nonIterableRest$1() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray$1(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}

function _arrayLikeToArray$1(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit$1(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles$1(arr) {
  if (Array.isArray(arr)) return arr;
}

function _classCallCheck$1(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties$1(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass$1(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$1(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$1(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
var StoryStoreFacade =
/*#__PURE__*/
function () {
  function StoryStoreFacade() {
    _classCallCheck$1(this, StoryStoreFacade);

    this.projectAnnotations = void 0;
    this.stories = void 0;
    this.csfExports = void 0;
    this.projectAnnotations = {
      loaders: [],
      decorators: [],
      parameters: {},
      argsEnhancers: [],
      argTypesEnhancers: [],
      args: {},
      argTypes: {}
    };
    this.stories = {};
    this.csfExports = {};
  } // This doesn't actually import anything because the client-api loads fully
  // on startup, but this is a shim after all.


  _createClass$1(StoryStoreFacade, [{
    key: "importFn",
    value: function importFn(path) {
      var _this = this;

      return synchronousPromise.SynchronousPromise.resolve().then(function () {
        var moduleExports = _this.csfExports[path];
        if (!moduleExports) throw new Error("Unknown path: ".concat(path));
        return moduleExports;
      });
    }
  }, {
    key: "getStoryIndex",
    value: function getStoryIndex(store) {
      var _this$projectAnnotati,
          _this$projectAnnotati2,
          _this2 = this;

      var fileNameOrder = Object.keys(this.csfExports);
      var storySortParameter = (_this$projectAnnotati = this.projectAnnotations.parameters) === null || _this$projectAnnotati === void 0 ? void 0 : (_this$projectAnnotati2 = _this$projectAnnotati.options) === null || _this$projectAnnotati2 === void 0 ? void 0 : _this$projectAnnotati2.storySort;
      var storyEntries = Object.entries(this.stories); // Add the kind parameters and global parameters to each entry

      var sortableV6 = storyEntries.map(function (_ref) {
        var _ref2 = _slicedToArray$1(_ref, 2),
            storyId = _ref2[0],
            importPath = _ref2[1].importPath;

        var exports = _this2.csfExports[importPath];
        var csfFile = store.processCSFFileWithCache(exports, importPath, exports.default.title);
        return [storyId, store.storyFromCSFFile({
          storyId: storyId,
          csfFile: csfFile
        }), csfFile.meta.parameters, _this2.projectAnnotations.parameters];
      }); // NOTE: the sortStoriesV6 version returns the v7 data format. confusing but more convenient!

      var sortedV7;

      try {
        sortedV7 = sortStoriesV6(sortableV6, storySortParameter, fileNameOrder);
      } catch (err) {
        if (typeof storySortParameter === 'function') {
          throw new Error(_default(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral$1(["\n          Error sorting stories with sort parameter ", ":\n\n          > ", "\n          \n          Are you using a V7-style sort function in V6 compatibility mode?\n          \n          More info: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#v7-style-story-sort\n        "])), storySortParameter, err.message));
        }

        throw err;
      }

      var stories = sortedV7.reduce(function (acc, s) {
        // We use the original entry we stored in `this.stories` because it is possible that the CSF file itself
        // exports a `parameters.fileName` which can be different and mess up our `importFn`.
        // In fact, in Storyshots there is a Jest transformer that does exactly that.
        // NOTE: this doesn't actually change the story object, just the index.
        acc[s.id] = _this2.stories[s.id];
        return acc;
      }, {});
      return {
        v: 3,
        stories: stories
      };
    }
  }, {
    key: "clearFilenameExports",
    value: function clearFilenameExports(fileName) {
      var _this3 = this;

      if (!this.csfExports[fileName]) {
        return;
      } // Clear this module's stories from the storyList and existing exports


      Object.entries(this.stories).forEach(function (_ref3) {
        var _ref4 = _slicedToArray$1(_ref3, 2),
            id = _ref4[0],
            importPath = _ref4[1].importPath;

        if (importPath === fileName) {
          delete _this3.stories[id];
        }
      }); // We keep this as an empty record so we can use it to maintain component order

      this.csfExports[fileName] = {};
    } // NOTE: we could potentially share some of this code with the stories.json generation

  }, {
    key: "addStoriesFromExports",
    value: function addStoriesFromExports(fileName, fileExports) {
      var _this4 = this; // if the export haven't changed since last time we added them, this is a no-op


      if (this.csfExports[fileName] === fileExports) {
        return;
      } // OTOH, if they have changed, let's clear them out first


      this.clearFilenameExports(fileName);

      var defaultExport = fileExports.default,
          __namedExportsOrder = fileExports.__namedExportsOrder,
          namedExports = _objectWithoutProperties$1(fileExports, _excluded$1); // eslint-disable-next-line prefer-const


      var _ref5 = defaultExport || {},
          componentId = _ref5.id,
          title = _ref5.title;

      var specifiers = (window_1.STORIES || []).map(function (specifier) {
        return Object.assign({}, specifier, {
          importPathMatcher: new RegExp(specifier.importPathMatcher)
        });
      });
      title = userOrAutoTitle(fileName, specifiers, title);

      if (!title) {
        logger.info("Unexpected default export without title in '".concat(fileName, "': ").concat(JSON.stringify(fileExports.default)));
        return;
      }

      this.csfExports[fileName] = Object.assign({}, fileExports, {
        default: Object.assign({}, defaultExport, {
          title: title
        })
      });
      var sortedExports = namedExports; // prefer a user/loader provided `__namedExportsOrder` array if supplied
      // we do this as es module exports are always ordered alphabetically
      // see https://github.com/storybookjs/storybook/issues/9136

      if (Array.isArray(__namedExportsOrder)) {
        sortedExports = {};

        __namedExportsOrder.forEach(function (name) {
          var namedExport = namedExports[name];
          if (namedExport) sortedExports[name] = namedExport;
        });
      }

      Object.entries(sortedExports).filter(function (_ref6) {
        var _ref7 = _slicedToArray$1(_ref6, 1),
            key = _ref7[0];

        return dist.isExportStory(key, defaultExport);
      }).forEach(function (_ref8) {
        var _storyExport$paramete, _storyExport$story;

        var _ref9 = _slicedToArray$1(_ref8, 2),
            key = _ref9[0],
            storyExport = _ref9[1];

        var exportName = dist.storyNameFromExport(key);
        var id = ((_storyExport$paramete = storyExport.parameters) === null || _storyExport$paramete === void 0 ? void 0 : _storyExport$paramete.__id) || dist.toId(componentId || title, exportName);
        var name = typeof storyExport !== 'function' && storyExport.name || storyExport.storyName || ((_storyExport$story = storyExport.story) === null || _storyExport$story === void 0 ? void 0 : _storyExport$story.name) || exportName;
        _this4.stories[id] = {
          id: id,
          name: name,
          title: title,
          importPath: fileName
        };
      });
    }
  }]);

  return StoryStoreFacade;
}();

var _excluded = ["globals", "globalTypes"],
    _excluded2 = ["decorators", "loaders", "component", "args", "argTypes"],
    _excluded3 = ["component", "args", "argTypes"];

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}
// relevant framework instanciates them via `start.js`. The good news is this happens right away.

var singleton;
var warningAlternatives = {
  addDecorator: "Instead, use `export const decorators = [];` in your `preview.js`.",
  addParameters: "Instead, use `export const parameters = {};` in your `preview.js`.",
  addLoaders: "Instead, use `export const loaders = [];` in your `preview.js`."
};

var warningMessage = function warningMessage(method) {
  return node$1(function () {}, _default(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  `", "` is deprecated, and will be removed in Storybook 7.0.\n\n  ", "\n\n  Read more at https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-addparameters-and-adddecorator)."], ["\n  \\`", "\\` is deprecated, and will be removed in Storybook 7.0.\n\n  ", "\n\n  Read more at https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-addparameters-and-adddecorator)."])), method, warningAlternatives[method]));
};

var warnings = {
  addDecorator: warningMessage('addDecorator'),
  addParameters: warningMessage('addParameters'),
  addLoaders: warningMessage('addLoaders')
};

var checkMethod = function checkMethod(method, deprecationWarning) {
  var _global$FEATURES;

  if ((_global$FEATURES = window_1.FEATURES) !== null && _global$FEATURES !== void 0 && _global$FEATURES.storyStoreV7) {
    throw new Error(_default(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["You cannot use `", "` with the new Story Store.\n      \n      ", ""], ["You cannot use \\`", "\\` with the new Story Store.\n      \n      ", ""])), method, warningAlternatives[method]));
  }

  if (!singleton) {
    throw new Error("Singleton client API not yet initialized, cannot call `".concat(method, "`."));
  }

  if (deprecationWarning) {
    warnings[method]();
  }
};

var addDecorator = function addDecorator(decorator) {
  var deprecationWarning = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  checkMethod('addDecorator', deprecationWarning);
  singleton.addDecorator(decorator);
};
var addParameters = function addParameters(parameters) {
  var deprecationWarning = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  checkMethod('addParameters', deprecationWarning);
  singleton.addParameters(parameters);
};
var addLoader = function addLoader(loader) {
  var deprecationWarning = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  checkMethod('addLoader', deprecationWarning);
  singleton.addLoader(loader);
};
var addArgs = function addArgs(args) {
  checkMethod('addArgs', false);
  singleton.addArgs(args);
};
var addArgTypes = function addArgTypes(argTypes) {
  checkMethod('addArgTypes', false);
  singleton.addArgTypes(argTypes);
};
var addArgsEnhancer = function addArgsEnhancer(enhancer) {
  checkMethod('addArgsEnhancer', false);
  singleton.addArgsEnhancer(enhancer);
};
var addArgTypesEnhancer = function addArgTypesEnhancer(enhancer) {
  checkMethod('addArgTypesEnhancer', false);
  singleton.addArgTypesEnhancer(enhancer);
};
var setGlobalRender = function setGlobalRender(render) {
  checkMethod('setGlobalRender', false);
  singleton.facade.projectAnnotations.render = render;
};
var invalidStoryTypes = new Set(['string', 'number', 'boolean', 'symbol']);
var ClientApi =
/*#__PURE__*/
function () {
  // If we don't get passed modules so don't know filenames, we can
  // just use numeric indexes
  function ClientApi() {
    var _this = this;

    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        storyStore = _ref.storyStore;

    _classCallCheck(this, ClientApi);

    this.facade = void 0;
    this.storyStore = void 0;
    this.addons = void 0;
    this.onImportFnChanged = void 0;
    this.lastFileName = 0;
    this.setAddon = node$1(function (addon) {
      _this.addons = Object.assign({}, _this.addons, addon);
    }, _default(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      `setAddon` is deprecated and will be removed in Storybook 7.0.\n\n      https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-setaddon\n    "], ["\n      \\`setAddon\\` is deprecated and will be removed in Storybook 7.0.\n\n      https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-setaddon\n    "]))));

    this.addDecorator = function (decorator) {
      _this.facade.projectAnnotations.decorators.push(decorator);
    };

    this.clearDecorators = node$1(function () {
      _this.facade.projectAnnotations.decorators = [];
    }, _default(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      `clearDecorators` is deprecated and will be removed in Storybook 7.0.\n\n      https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-cleardecorators\n    "], ["\n      \\`clearDecorators\\` is deprecated and will be removed in Storybook 7.0.\n\n      https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#deprecated-cleardecorators\n    "]))));

    this.addParameters = function (_ref2) {
      var globals = _ref2.globals,
          globalTypes = _ref2.globalTypes,
          parameters = _objectWithoutProperties(_ref2, _excluded);

      _this.facade.projectAnnotations.parameters = combineParameters(_this.facade.projectAnnotations.parameters, parameters);

      if (globals) {
        _this.facade.projectAnnotations.globals = Object.assign({}, _this.facade.projectAnnotations.globals, globals);
      }

      if (globalTypes) {
        _this.facade.projectAnnotations.globalTypes = Object.assign({}, _this.facade.projectAnnotations.globalTypes, normalizeInputTypes(globalTypes));
      }
    };

    this.addLoader = function (loader) {
      _this.facade.projectAnnotations.loaders.push(loader);
    };

    this.addArgs = function (args) {
      _this.facade.projectAnnotations.args = Object.assign({}, _this.facade.projectAnnotations.args, args);
    };

    this.addArgTypes = function (argTypes) {
      _this.facade.projectAnnotations.argTypes = Object.assign({}, _this.facade.projectAnnotations.argTypes, normalizeInputTypes(argTypes));
    };

    this.addArgsEnhancer = function (enhancer) {
      _this.facade.projectAnnotations.argsEnhancers.push(enhancer);
    };

    this.addArgTypesEnhancer = function (enhancer) {
      _this.facade.projectAnnotations.argTypesEnhancers.push(enhancer);
    };

    this.storiesOf = function (kind, m) {
      if (!kind && typeof kind !== 'string') {
        throw new Error('Invalid or missing kind provided for stories, should be a string');
      }

      if (!m) {
        logger.warn("Missing 'module' parameter for story with a kind of '".concat(kind, "'. It will break your HMR"));
      }

      if (m) {
        var proto = Object.getPrototypeOf(m);

        if (proto.exports && proto.exports.default) {
          // FIXME: throw an error in SB6.0
          logger.error("Illegal mix of CSF default export and storiesOf calls in a single file: ".concat(proto.i));
        }
      } // eslint-disable-next-line no-plusplus


      var baseFilename = m && m.id ? "".concat(m.id) : (_this.lastFileName++).toString();
      var fileName = baseFilename;
      var i = 1; // Deal with `storiesOf()` being called twice in the same file.
      // On HMR, `this.csfExports[fileName]` will be reset to `{}`, so an empty object is due
      // to this export, not a second call of `storiesOf()`.

      while (_this.facade.csfExports[fileName] && Object.keys(_this.facade.csfExports[fileName]).length > 0) {
        i += 1;
        fileName = "".concat(baseFilename, "-").concat(i);
      }

      if (m && m.hot && m.hot.accept) {
        // This module used storiesOf(), so when it re-runs on HMR, it will reload
        // itself automatically without us needing to look at our imports
        m.hot.accept();
        m.hot.dispose(function () {
          _this.facade.clearFilenameExports(fileName); // We need to update the importFn as soon as the module re-evaluates
          // (and calls storiesOf() again, etc). We could call `onImportFnChanged()`
          // at the end of every setStories call (somehow), but then we'd need to
          // debounce it somehow for initial startup. Instead, we'll take advantage of
          // the fact that the evaluation of the module happens immediately in the same tick


          setTimeout(function () {
            var _this$onImportFnChang;

            (_this$onImportFnChang = _this.onImportFnChanged) === null || _this$onImportFnChang === void 0 ? void 0 : _this$onImportFnChang.call(_this, {
              importFn: _this.importFn.bind(_this)
            });
          }, 0);
        });
      }

      var hasAdded = false;
      var api = {
        kind: kind.toString(),
        add: function add() {
          return api;
        },
        addDecorator: function addDecorator() {
          return api;
        },
        addLoader: function addLoader() {
          return api;
        },
        addParameters: function addParameters() {
          return api;
        }
      }; // apply addons

      Object.keys(_this.addons).forEach(function (name) {
        var addon = _this.addons[name];

        api[name] = function () {
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }

          addon.apply(api, args);
          return api;
        };
      });
      var meta = {
        id: dist.sanitize(kind),
        title: kind,
        decorators: [],
        loaders: [],
        parameters: {}
      }; // We map these back to a simple default export, even though we have type guarantees at this point

      _this.facade.csfExports[fileName] = {
        default: meta
      };
      var counter = 0;

      api.add = function (storyName, storyFn) {
        var parameters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
        hasAdded = true;

        if (typeof storyName !== 'string') {
          throw new Error("Invalid or missing storyName provided for a \"".concat(kind, "\" story."));
        }

        if (!storyFn || Array.isArray(storyFn) || invalidStoryTypes.has(_typeof(storyFn))) {
          throw new Error("Cannot load story \"".concat(storyName, "\" in \"").concat(kind, "\" due to invalid format. Storybook expected a function/object but received ").concat(_typeof(storyFn), " instead."));
        }

        var decorators = parameters.decorators,
            loaders = parameters.loaders,
            component = parameters.component,
            args = parameters.args,
            argTypes = parameters.argTypes,
            storyParameters = _objectWithoutProperties(parameters, _excluded2); // eslint-disable-next-line no-underscore-dangle


        var storyId = parameters.__id || dist.toId(kind, storyName);
        var csfExports = _this.facade.csfExports[fileName]; // Whack a _ on the front incase it is "default"

        csfExports["story".concat(counter)] = {
          name: storyName,
          parameters: Object.assign({
            fileName: fileName,
            __id: storyId
          }, storyParameters),
          decorators: decorators,
          loaders: loaders,
          args: args,
          argTypes: argTypes,
          component: component,
          render: storyFn
        };
        counter += 1;
        _this.facade.stories[storyId] = {
          id: storyId,
          title: csfExports.default.title,
          name: storyName,
          importPath: fileName
        };
        return api;
      };

      api.addDecorator = function (decorator) {
        if (hasAdded) throw new Error("You cannot add a decorator after the first story for a kind.\nRead more here: https://github.com/storybookjs/storybook/blob/master/MIGRATION.md#can-no-longer-add-decoratorsparameters-after-stories");
        meta.decorators.push(decorator);
        return api;
      };

      api.addLoader = function (loader) {
        if (hasAdded) throw new Error("You cannot add a loader after the first story for a kind.");
        meta.loaders.push(loader);
        return api;
      };

      api.addParameters = function (_ref3) {
        var component = _ref3.component,
            args = _ref3.args,
            argTypes = _ref3.argTypes,
            parameters = _objectWithoutProperties(_ref3, _excluded3);

        if (hasAdded) throw new Error("You cannot add parameters after the first story for a kind.\nRead more here: https://github.com/storybookjs/storybook/blob/master/MIGRATION.md#can-no-longer-add-decoratorsparameters-after-stories");
        meta.parameters = combineParameters(meta.parameters, parameters);
        if (component) meta.component = component;
        if (args) meta.args = Object.assign({}, meta.args, args);
        if (argTypes) meta.argTypes = Object.assign({}, meta.argTypes, argTypes);
        return api;
      };

      return api;
    };

    this.getStorybook = function () {
      var stories = _this.storyStore.storyIndex.stories;
      var kinds = {};
      Object.entries(stories).forEach(function (_ref4) {
        var _ref5 = _slicedToArray(_ref4, 2),
            storyId = _ref5[0],
            _ref5$ = _ref5[1],
            title = _ref5$.title,
            name = _ref5$.name,
            importPath = _ref5$.importPath;

        if (!kinds[title]) {
          kinds[title] = {
            kind: title,
            fileName: importPath,
            stories: []
          };
        }

        var _this$storyStore$from = _this.storyStore.fromId(storyId),
            storyFn = _this$storyStore$from.storyFn;

        kinds[title].stories.push({
          name: name,
          render: storyFn
        });
      });
      return Object.values(kinds);
    };

    this.raw = function () {
      return _this.storyStore.raw();
    };

    this.facade = new StoryStoreFacade();
    this.addons = {};
    this.storyStore = storyStore;
    singleton = this;
  }

  _createClass(ClientApi, [{
    key: "importFn",
    value: function importFn(path) {
      return this.facade.importFn(path);
    }
  }, {
    key: "getStoryIndex",
    value: function getStoryIndex() {
      if (!this.storyStore) {
        throw new Error('Cannot get story index before setting storyStore');
      }

      return this.facade.getStoryIndex(this.storyStore);
    }
  }, {
    key: "_storyStore",
    get: // @deprecated
    function get() {
      return this.storyStore;
    }
  }]);

  return ClientApi;
}();

var redefine = redefine$l.exports;

var regexpExec$1 = regexpExec$6;

var fails = fails$K;

var wellKnownSymbol = wellKnownSymbol$I;

var createNonEnumerableProperty = createNonEnumerableProperty$l;

var SPECIES = wellKnownSymbol('species');
var RegExpPrototype = RegExp.prototype;
var REPLACE_SUPPORTS_NAMED_GROUPS = !fails(function () {
  // #replace needs built-in support for named groups.
  // #match works fine because it just return the exec results, even if it has
  // a "grops" property.
  var re = /./;

  re.exec = function () {
    var result = [];
    result.groups = {
      a: '7'
    };
    return result;
  };

  return ''.replace(re, '$<a>') !== '7';
}); // IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0

var REPLACE_KEEPS_$0 = function () {
  // eslint-disable-next-line regexp/prefer-escape-replacement-dollar-char -- required for testing
  return 'a'.replace(/./, '$0') === '$0';
}();

var REPLACE = wellKnownSymbol('replace'); // Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string

var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = function () {
  if (/./[REPLACE]) {
    return /./[REPLACE]('a', '$0') === '';
  }

  return false;
}(); // Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
// Weex JS has frozen built-in prototypes, so use try / catch wrapper


var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC = !fails(function () {
  // eslint-disable-next-line regexp/no-empty-group -- required for testing
  var re = /(?:)/;
  var originalExec = re.exec;

  re.exec = function () {
    return originalExec.apply(this, arguments);
  };

  var result = 'ab'.split(re);
  return result.length !== 2 || result[0] !== 'a' || result[1] !== 'b';
});

var fixRegexpWellKnownSymbolLogic = function (KEY, length, exec, sham) {
  var SYMBOL = wellKnownSymbol(KEY);
  var DELEGATES_TO_SYMBOL = !fails(function () {
    // String methods call symbol-named RegEp methods
    var O = {};

    O[SYMBOL] = function () {
      return 7;
    };

    return ''[KEY](O) != 7;
  });
  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {}; // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.

      re.constructor = {};

      re.constructor[SPECIES] = function () {
        return re;
      };

      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () {
      execCalled = true;
      return null;
    };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (!DELEGATES_TO_SYMBOL || !DELEGATES_TO_EXEC || KEY === 'replace' && !(REPLACE_SUPPORTS_NAMED_GROUPS && REPLACE_KEEPS_$0 && !REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE) || KEY === 'split' && !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC) {
    var nativeRegExpMethod = /./[SYMBOL];
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      var $exec = regexp.exec;

      if ($exec === regexpExec$1 || $exec === RegExpPrototype.exec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return {
            done: true,
            value: nativeRegExpMethod.call(regexp, str, arg2)
          };
        }

        return {
          done: true,
          value: nativeMethod.call(str, regexp, arg2)
        };
      }

      return {
        done: false
      };
    }, {
      REPLACE_KEEPS_$0: REPLACE_KEEPS_$0,
      REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE: REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE
    });
    var stringMethod = methods[0];
    var regexMethod = methods[1];
    redefine(String.prototype, KEY, stringMethod);
    redefine(RegExpPrototype, SYMBOL, length == 2 // 21.2.5.8 RegExp.prototype[@@replace](string, replaceValue)
    // 21.2.5.11 RegExp.prototype[@@split](string, limit)
    ? function (string, arg) {
      return regexMethod.call(string, this, arg);
    } // 21.2.5.6 RegExp.prototype[@@match](string)
    // 21.2.5.9 RegExp.prototype[@@search](string)
    : function (string) {
      return regexMethod.call(string, this);
    });
  }

  if (sham) createNonEnumerableProperty(RegExpPrototype[SYMBOL], 'sham', true);
};

// `SameValue` abstract operation
// https://tc39.es/ecma262/#sec-samevalue
// eslint-disable-next-line es/no-object-is -- safe
var sameValue$1 = Object.is || function is(x, y) {
  // eslint-disable-next-line no-self-compare -- NaN check
  return x === y ? x !== 0 || 1 / x === 1 / y : x != x && y != y;
};

var classof = classofRaw$3;

var regexpExec = regexpExec$6; // `RegExpExec` abstract operation
// https://tc39.es/ecma262/#sec-regexpexec


var regexpExecAbstract = function (R, S) {
  var exec = R.exec;

  if (typeof exec === 'function') {
    var result = exec.call(R, S);

    if (typeof result !== 'object') {
      throw TypeError('RegExp exec method returned something other than an Object or null');
    }

    return result;
  }

  if (classof(R) !== 'RegExp') {
    throw TypeError('RegExp#exec called on incompatible receiver');
  }

  return regexpExec.call(R, S);
};

var fixRegExpWellKnownSymbolLogic = fixRegexpWellKnownSymbolLogic;

var anObject = anObject$t;

var requireObjectCoercible = requireObjectCoercible$e;

var sameValue = sameValue$1;

var regExpExec = regexpExecAbstract; // @@search logic


fixRegExpWellKnownSymbolLogic('search', 1, function (SEARCH, nativeSearch, maybeCallNative) {
  return [// `String.prototype.search` method
  // https://tc39.es/ecma262/#sec-string.prototype.search
  function search(regexp) {
    var O = requireObjectCoercible(this);
    var searcher = regexp == undefined ? undefined : regexp[SEARCH];
    return searcher !== undefined ? searcher.call(regexp, O) : new RegExp(regexp)[SEARCH](String(O));
  }, // `RegExp.prototype[@@search]` method
  // https://tc39.es/ecma262/#sec-regexp.prototype-@@search
  function (regexp) {
    var res = maybeCallNative(nativeSearch, regexp, this);
    if (res.done) return res.value;
    var rx = anObject(regexp);
    var S = String(this);
    var previousLastIndex = rx.lastIndex;
    if (!sameValue(previousLastIndex, 0)) rx.lastIndex = 0;
    var result = regExpExec(rx, S);
    if (!sameValue(rx.lastIndex, previousLastIndex)) rx.lastIndex = previousLastIndex;
    return result === null ? -1 : result.index;
  }];
});

/* eslint complexity: [2, 18], max-statements: [2, 33] */

var shams = function hasSymbols() {
  if (typeof Symbol !== 'function' || typeof Object.getOwnPropertySymbols !== 'function') {
    return false;
  }

  if (typeof Symbol.iterator === 'symbol') {
    return true;
  }

  var obj = {};
  var sym = Symbol('test');
  var symObj = Object(sym);

  if (typeof sym === 'string') {
    return false;
  }

  if (Object.prototype.toString.call(sym) !== '[object Symbol]') {
    return false;
  }

  if (Object.prototype.toString.call(symObj) !== '[object Symbol]') {
    return false;
  } // temp disabled per https://github.com/ljharb/object.assign/issues/17
  // if (sym instanceof Symbol) { return false; }
  // temp disabled per https://github.com/WebReflection/get-own-property-symbols/issues/4
  // if (!(symObj instanceof Symbol)) { return false; }
  // if (typeof Symbol.prototype.toString !== 'function') { return false; }
  // if (String(sym) !== Symbol.prototype.toString.call(sym)) { return false; }


  var symVal = 42;
  obj[sym] = symVal;

  for (sym in obj) {
    return false;
  } // eslint-disable-line no-restricted-syntax


  if (typeof Object.keys === 'function' && Object.keys(obj).length !== 0) {
    return false;
  }

  if (typeof Object.getOwnPropertyNames === 'function' && Object.getOwnPropertyNames(obj).length !== 0) {
    return false;
  }

  var syms = Object.getOwnPropertySymbols(obj);

  if (syms.length !== 1 || syms[0] !== sym) {
    return false;
  }

  if (!Object.prototype.propertyIsEnumerable.call(obj, sym)) {
    return false;
  }

  if (typeof Object.getOwnPropertyDescriptor === 'function') {
    var descriptor = Object.getOwnPropertyDescriptor(obj, sym);

    if (descriptor.value !== symVal || descriptor.enumerable !== true) {
      return false;
    }
  }

  return true;
};

var origSymbol = commonjsGlobal.Symbol;

var hasSymbolSham = shams;

var hasSymbols$1 = function hasNativeSymbols() {
  if (typeof origSymbol !== 'function') {
    return false;
  }

  if (typeof Symbol !== 'function') {
    return false;
  }

  if (typeof origSymbol('foo') !== 'symbol') {
    return false;
  }

  if (typeof Symbol('bar') !== 'symbol') {
    return false;
  }

  return hasSymbolSham();
};

/* eslint no-invalid-this: 1 */

var ERROR_MESSAGE = 'Function.prototype.bind called on incompatible ';
var slice = Array.prototype.slice;
var toStr$1 = Object.prototype.toString;
var funcType = '[object Function]';

var implementation$1 = function bind(that) {
  var target = this;

  if (typeof target !== 'function' || toStr$1.call(target) !== funcType) {
    throw new TypeError(ERROR_MESSAGE + target);
  }

  var args = slice.call(arguments, 1);
  var bound;

  var binder = function () {
    if (this instanceof bound) {
      var result = target.apply(this, args.concat(slice.call(arguments)));

      if (Object(result) === result) {
        return result;
      }

      return this;
    } else {
      return target.apply(that, args.concat(slice.call(arguments)));
    }
  };

  var boundLength = Math.max(0, target.length - args.length);
  var boundArgs = [];

  for (var i = 0; i < boundLength; i++) {
    boundArgs.push('$' + i);
  }

  bound = Function('binder', 'return function (' + boundArgs.join(',') + '){ return binder.apply(this,arguments); }')(binder);

  if (target.prototype) {
    var Empty = function Empty() {};

    Empty.prototype = target.prototype;
    bound.prototype = new Empty();
    Empty.prototype = null;
  }

  return bound;
};

var implementation = implementation$1;

var functionBind = Function.prototype.bind || implementation;

var bind$1 = functionBind;

var src = bind$1.call(Function.call, Object.prototype.hasOwnProperty);

var undefined$1;
var $SyntaxError = SyntaxError;
var $Function = Function;
var $TypeError$1 = TypeError; // eslint-disable-next-line consistent-return

var getEvalledConstructor = function (expressionSyntax) {
  try {
    return $Function('"use strict"; return (' + expressionSyntax + ').constructor;')();
  } catch (e) {}
};

var $gOPD = Object.getOwnPropertyDescriptor;

if ($gOPD) {
  try {
    $gOPD({}, '');
  } catch (e) {
    $gOPD = null; // this is IE 8, which has a broken gOPD
  }
}

var throwTypeError = function () {
  throw new $TypeError$1();
};

var ThrowTypeError = $gOPD ? function () {
  try {
    // eslint-disable-next-line no-unused-expressions, no-caller, no-restricted-properties
    arguments.callee; // IE 8 does not throw here

    return throwTypeError;
  } catch (calleeThrows) {
    try {
      // IE 8 throws on Object.getOwnPropertyDescriptor(arguments, '')
      return $gOPD(arguments, 'callee').get;
    } catch (gOPDthrows) {
      return throwTypeError;
    }
  }
}() : throwTypeError;

var hasSymbols = hasSymbols$1();

var getProto = Object.getPrototypeOf || function (x) {
  return x.__proto__;
}; // eslint-disable-line no-proto


var needsEval = {};
var TypedArray = typeof Uint8Array === 'undefined' ? undefined$1 : getProto(Uint8Array);
var INTRINSICS = {
  '%AggregateError%': typeof AggregateError === 'undefined' ? undefined$1 : AggregateError,
  '%Array%': Array,
  '%ArrayBuffer%': typeof ArrayBuffer === 'undefined' ? undefined$1 : ArrayBuffer,
  '%ArrayIteratorPrototype%': hasSymbols ? getProto([][Symbol.iterator]()) : undefined$1,
  '%AsyncFromSyncIteratorPrototype%': undefined$1,
  '%AsyncFunction%': needsEval,
  '%AsyncGenerator%': needsEval,
  '%AsyncGeneratorFunction%': needsEval,
  '%AsyncIteratorPrototype%': needsEval,
  '%Atomics%': typeof Atomics === 'undefined' ? undefined$1 : Atomics,
  '%BigInt%': typeof BigInt === 'undefined' ? undefined$1 : BigInt,
  '%Boolean%': Boolean,
  '%DataView%': typeof DataView === 'undefined' ? undefined$1 : DataView,
  '%Date%': Date,
  '%decodeURI%': decodeURI,
  '%decodeURIComponent%': decodeURIComponent,
  '%encodeURI%': encodeURI,
  '%encodeURIComponent%': encodeURIComponent,
  '%Error%': Error,
  '%eval%': eval,
  // eslint-disable-line no-eval
  '%EvalError%': EvalError,
  '%Float32Array%': typeof Float32Array === 'undefined' ? undefined$1 : Float32Array,
  '%Float64Array%': typeof Float64Array === 'undefined' ? undefined$1 : Float64Array,
  '%FinalizationRegistry%': typeof FinalizationRegistry === 'undefined' ? undefined$1 : FinalizationRegistry,
  '%Function%': $Function,
  '%GeneratorFunction%': needsEval,
  '%Int8Array%': typeof Int8Array === 'undefined' ? undefined$1 : Int8Array,
  '%Int16Array%': typeof Int16Array === 'undefined' ? undefined$1 : Int16Array,
  '%Int32Array%': typeof Int32Array === 'undefined' ? undefined$1 : Int32Array,
  '%isFinite%': isFinite,
  '%isNaN%': isNaN,
  '%IteratorPrototype%': hasSymbols ? getProto(getProto([][Symbol.iterator]())) : undefined$1,
  '%JSON%': typeof JSON === 'object' ? JSON : undefined$1,
  '%Map%': typeof Map === 'undefined' ? undefined$1 : Map,
  '%MapIteratorPrototype%': typeof Map === 'undefined' || !hasSymbols ? undefined$1 : getProto(new Map()[Symbol.iterator]()),
  '%Math%': Math,
  '%Number%': Number,
  '%Object%': Object,
  '%parseFloat%': parseFloat,
  '%parseInt%': parseInt,
  '%Promise%': typeof Promise === 'undefined' ? undefined$1 : Promise,
  '%Proxy%': typeof Proxy === 'undefined' ? undefined$1 : Proxy,
  '%RangeError%': RangeError,
  '%ReferenceError%': ReferenceError,
  '%Reflect%': typeof Reflect === 'undefined' ? undefined$1 : Reflect,
  '%RegExp%': RegExp,
  '%Set%': typeof Set === 'undefined' ? undefined$1 : Set,
  '%SetIteratorPrototype%': typeof Set === 'undefined' || !hasSymbols ? undefined$1 : getProto(new Set()[Symbol.iterator]()),
  '%SharedArrayBuffer%': typeof SharedArrayBuffer === 'undefined' ? undefined$1 : SharedArrayBuffer,
  '%String%': String,
  '%StringIteratorPrototype%': hasSymbols ? getProto(''[Symbol.iterator]()) : undefined$1,
  '%Symbol%': hasSymbols ? Symbol : undefined$1,
  '%SyntaxError%': $SyntaxError,
  '%ThrowTypeError%': ThrowTypeError,
  '%TypedArray%': TypedArray,
  '%TypeError%': $TypeError$1,
  '%Uint8Array%': typeof Uint8Array === 'undefined' ? undefined$1 : Uint8Array,
  '%Uint8ClampedArray%': typeof Uint8ClampedArray === 'undefined' ? undefined$1 : Uint8ClampedArray,
  '%Uint16Array%': typeof Uint16Array === 'undefined' ? undefined$1 : Uint16Array,
  '%Uint32Array%': typeof Uint32Array === 'undefined' ? undefined$1 : Uint32Array,
  '%URIError%': URIError,
  '%WeakMap%': typeof WeakMap === 'undefined' ? undefined$1 : WeakMap,
  '%WeakRef%': typeof WeakRef === 'undefined' ? undefined$1 : WeakRef,
  '%WeakSet%': typeof WeakSet === 'undefined' ? undefined$1 : WeakSet
};

var doEval = function doEval(name) {
  var value;

  if (name === '%AsyncFunction%') {
    value = getEvalledConstructor('async function () {}');
  } else if (name === '%GeneratorFunction%') {
    value = getEvalledConstructor('function* () {}');
  } else if (name === '%AsyncGeneratorFunction%') {
    value = getEvalledConstructor('async function* () {}');
  } else if (name === '%AsyncGenerator%') {
    var fn = doEval('%AsyncGeneratorFunction%');

    if (fn) {
      value = fn.prototype;
    }
  } else if (name === '%AsyncIteratorPrototype%') {
    var gen = doEval('%AsyncGenerator%');

    if (gen) {
      value = getProto(gen.prototype);
    }
  }

  INTRINSICS[name] = value;
  return value;
};

var LEGACY_ALIASES = {
  '%ArrayBufferPrototype%': ['ArrayBuffer', 'prototype'],
  '%ArrayPrototype%': ['Array', 'prototype'],
  '%ArrayProto_entries%': ['Array', 'prototype', 'entries'],
  '%ArrayProto_forEach%': ['Array', 'prototype', 'forEach'],
  '%ArrayProto_keys%': ['Array', 'prototype', 'keys'],
  '%ArrayProto_values%': ['Array', 'prototype', 'values'],
  '%AsyncFunctionPrototype%': ['AsyncFunction', 'prototype'],
  '%AsyncGenerator%': ['AsyncGeneratorFunction', 'prototype'],
  '%AsyncGeneratorPrototype%': ['AsyncGeneratorFunction', 'prototype', 'prototype'],
  '%BooleanPrototype%': ['Boolean', 'prototype'],
  '%DataViewPrototype%': ['DataView', 'prototype'],
  '%DatePrototype%': ['Date', 'prototype'],
  '%ErrorPrototype%': ['Error', 'prototype'],
  '%EvalErrorPrototype%': ['EvalError', 'prototype'],
  '%Float32ArrayPrototype%': ['Float32Array', 'prototype'],
  '%Float64ArrayPrototype%': ['Float64Array', 'prototype'],
  '%FunctionPrototype%': ['Function', 'prototype'],
  '%Generator%': ['GeneratorFunction', 'prototype'],
  '%GeneratorPrototype%': ['GeneratorFunction', 'prototype', 'prototype'],
  '%Int8ArrayPrototype%': ['Int8Array', 'prototype'],
  '%Int16ArrayPrototype%': ['Int16Array', 'prototype'],
  '%Int32ArrayPrototype%': ['Int32Array', 'prototype'],
  '%JSONParse%': ['JSON', 'parse'],
  '%JSONStringify%': ['JSON', 'stringify'],
  '%MapPrototype%': ['Map', 'prototype'],
  '%NumberPrototype%': ['Number', 'prototype'],
  '%ObjectPrototype%': ['Object', 'prototype'],
  '%ObjProto_toString%': ['Object', 'prototype', 'toString'],
  '%ObjProto_valueOf%': ['Object', 'prototype', 'valueOf'],
  '%PromisePrototype%': ['Promise', 'prototype'],
  '%PromiseProto_then%': ['Promise', 'prototype', 'then'],
  '%Promise_all%': ['Promise', 'all'],
  '%Promise_reject%': ['Promise', 'reject'],
  '%Promise_resolve%': ['Promise', 'resolve'],
  '%RangeErrorPrototype%': ['RangeError', 'prototype'],
  '%ReferenceErrorPrototype%': ['ReferenceError', 'prototype'],
  '%RegExpPrototype%': ['RegExp', 'prototype'],
  '%SetPrototype%': ['Set', 'prototype'],
  '%SharedArrayBufferPrototype%': ['SharedArrayBuffer', 'prototype'],
  '%StringPrototype%': ['String', 'prototype'],
  '%SymbolPrototype%': ['Symbol', 'prototype'],
  '%SyntaxErrorPrototype%': ['SyntaxError', 'prototype'],
  '%TypedArrayPrototype%': ['TypedArray', 'prototype'],
  '%TypeErrorPrototype%': ['TypeError', 'prototype'],
  '%Uint8ArrayPrototype%': ['Uint8Array', 'prototype'],
  '%Uint8ClampedArrayPrototype%': ['Uint8ClampedArray', 'prototype'],
  '%Uint16ArrayPrototype%': ['Uint16Array', 'prototype'],
  '%Uint32ArrayPrototype%': ['Uint32Array', 'prototype'],
  '%URIErrorPrototype%': ['URIError', 'prototype'],
  '%WeakMapPrototype%': ['WeakMap', 'prototype'],
  '%WeakSetPrototype%': ['WeakSet', 'prototype']
};

var bind = functionBind;

var hasOwn$1 = src;

var $concat = bind.call(Function.call, Array.prototype.concat);
var $spliceApply = bind.call(Function.apply, Array.prototype.splice);
var $replace = bind.call(Function.call, String.prototype.replace);
var $strSlice = bind.call(Function.call, String.prototype.slice);
/* adapted from https://github.com/lodash/lodash/blob/4.17.15/dist/lodash.js#L6735-L6744 */

var rePropName = /[^%.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|%$))/g;
var reEscapeChar = /\\(\\)?/g;
/** Used to match backslashes in property paths. */

var stringToPath = function stringToPath(string) {
  var first = $strSlice(string, 0, 1);
  var last = $strSlice(string, -1);

  if (first === '%' && last !== '%') {
    throw new $SyntaxError('invalid intrinsic syntax, expected closing `%`');
  } else if (last === '%' && first !== '%') {
    throw new $SyntaxError('invalid intrinsic syntax, expected opening `%`');
  }

  var result = [];
  $replace(string, rePropName, function (match, number, quote, subString) {
    result[result.length] = quote ? $replace(subString, reEscapeChar, '$1') : number || match;
  });
  return result;
};
/* end adaptation */


var getBaseIntrinsic = function getBaseIntrinsic(name, allowMissing) {
  var intrinsicName = name;
  var alias;

  if (hasOwn$1(LEGACY_ALIASES, intrinsicName)) {
    alias = LEGACY_ALIASES[intrinsicName];
    intrinsicName = '%' + alias[0] + '%';
  }

  if (hasOwn$1(INTRINSICS, intrinsicName)) {
    var value = INTRINSICS[intrinsicName];

    if (value === needsEval) {
      value = doEval(intrinsicName);
    }

    if (typeof value === 'undefined' && !allowMissing) {
      throw new $TypeError$1('intrinsic ' + name + ' exists, but is not available. Please file an issue!');
    }

    return {
      alias: alias,
      name: intrinsicName,
      value: value
    };
  }

  throw new $SyntaxError('intrinsic ' + name + ' does not exist!');
};

var getIntrinsic = function GetIntrinsic(name, allowMissing) {
  if (typeof name !== 'string' || name.length === 0) {
    throw new $TypeError$1('intrinsic name must be a non-empty string');
  }

  if (arguments.length > 1 && typeof allowMissing !== 'boolean') {
    throw new $TypeError$1('"allowMissing" argument must be a boolean');
  }

  var parts = stringToPath(name);
  var intrinsicBaseName = parts.length > 0 ? parts[0] : '';
  var intrinsic = getBaseIntrinsic('%' + intrinsicBaseName + '%', allowMissing);
  var intrinsicRealName = intrinsic.name;
  var value = intrinsic.value;
  var skipFurtherCaching = false;
  var alias = intrinsic.alias;

  if (alias) {
    intrinsicBaseName = alias[0];
    $spliceApply(parts, $concat([0, 1], alias));
  }

  for (var i = 1, isOwn = true; i < parts.length; i += 1) {
    var part = parts[i];
    var first = $strSlice(part, 0, 1);
    var last = $strSlice(part, -1);

    if ((first === '"' || first === "'" || first === '`' || last === '"' || last === "'" || last === '`') && first !== last) {
      throw new $SyntaxError('property names with quotes must have matching quotes');
    }

    if (part === 'constructor' || !isOwn) {
      skipFurtherCaching = true;
    }

    intrinsicBaseName += '.' + part;
    intrinsicRealName = '%' + intrinsicBaseName + '%';

    if (hasOwn$1(INTRINSICS, intrinsicRealName)) {
      value = INTRINSICS[intrinsicRealName];
    } else if (value != null) {
      if (!(part in value)) {
        if (!allowMissing) {
          throw new $TypeError$1('base intrinsic for ' + name + ' exists, but the property is not available.');
        }

        return void undefined$1;
      }

      if ($gOPD && i + 1 >= parts.length) {
        var desc = $gOPD(value, part);
        isOwn = !!desc; // By convention, when a data property is converted to an accessor
        // property to emulate a data property that does not suffer from
        // the override mistake, that accessor's getter is marked with
        // an `originalValue` property. Here, when we detect this, we
        // uphold the illusion by pretending to see that original data
        // property, i.e., returning the value rather than the getter
        // itself.

        if (isOwn && 'get' in desc && !('originalValue' in desc.get)) {
          value = desc.get;
        } else {
          value = value[part];
        }
      } else {
        isOwn = hasOwn$1(value, part);
        value = value[part];
      }

      if (isOwn && !skipFurtherCaching) {
        INTRINSICS[intrinsicRealName] = value;
      }
    }
  }

  return value;
};

var callBind$1 = {exports: {}};

(function (module) {

var bind = functionBind;

var GetIntrinsic = getIntrinsic;

var $apply = GetIntrinsic('%Function.prototype.apply%');
var $call = GetIntrinsic('%Function.prototype.call%');
var $reflectApply = GetIntrinsic('%Reflect.apply%', true) || bind.call($call, $apply);
var $gOPD = GetIntrinsic('%Object.getOwnPropertyDescriptor%', true);
var $defineProperty = GetIntrinsic('%Object.defineProperty%', true);
var $max = GetIntrinsic('%Math.max%');

if ($defineProperty) {
  try {
    $defineProperty({}, 'a', {
      value: 1
    });
  } catch (e) {
    // IE 8 has a broken defineProperty
    $defineProperty = null;
  }
}

module.exports = function callBind(originalFunction) {
  var func = $reflectApply(bind, $call, arguments);

  if ($gOPD && $defineProperty) {
    var desc = $gOPD(func, 'length');

    if (desc.configurable) {
      // original length, plus the receiver, minus any additional arguments (after the receiver)
      $defineProperty(func, 'length', {
        value: 1 + $max(0, originalFunction.length - (arguments.length - 1))
      });
    }
  }

  return func;
};

var applyBind = function applyBind() {
  return $reflectApply(bind, $apply, arguments);
};

if ($defineProperty) {
  $defineProperty(module.exports, 'apply', {
    value: applyBind
  });
} else {
  module.exports.apply = applyBind;
}
}(callBind$1));

var GetIntrinsic$1 = getIntrinsic;

var callBind = callBind$1.exports;

var $indexOf = callBind(GetIntrinsic$1('String.prototype.indexOf'));

var callBound$1 = function callBoundIntrinsic(name, allowMissing) {
  var intrinsic = GetIntrinsic$1(name, !!allowMissing);

  if (typeof intrinsic === 'function' && $indexOf(name, '.prototype.') > -1) {
    return callBind(intrinsic);
  }

  return intrinsic;
};

var util_inspect = require$$0$2.inspect;

var hasMap = typeof Map === 'function' && Map.prototype;
var mapSizeDescriptor = Object.getOwnPropertyDescriptor && hasMap ? Object.getOwnPropertyDescriptor(Map.prototype, 'size') : null;
var mapSize = hasMap && mapSizeDescriptor && typeof mapSizeDescriptor.get === 'function' ? mapSizeDescriptor.get : null;
var mapForEach = hasMap && Map.prototype.forEach;
var hasSet = typeof Set === 'function' && Set.prototype;
var setSizeDescriptor = Object.getOwnPropertyDescriptor && hasSet ? Object.getOwnPropertyDescriptor(Set.prototype, 'size') : null;
var setSize = hasSet && setSizeDescriptor && typeof setSizeDescriptor.get === 'function' ? setSizeDescriptor.get : null;
var setForEach = hasSet && Set.prototype.forEach;
var hasWeakMap = typeof WeakMap === 'function' && WeakMap.prototype;
var weakMapHas = hasWeakMap ? WeakMap.prototype.has : null;
var hasWeakSet = typeof WeakSet === 'function' && WeakSet.prototype;
var weakSetHas = hasWeakSet ? WeakSet.prototype.has : null;
var hasWeakRef = typeof WeakRef === 'function' && WeakRef.prototype;
var weakRefDeref = hasWeakRef ? WeakRef.prototype.deref : null;
var booleanValueOf = Boolean.prototype.valueOf;
var objectToString$2 = Object.prototype.toString;
var functionToString = Function.prototype.toString;
var match = String.prototype.match;
var bigIntValueOf = typeof BigInt === 'function' ? BigInt.prototype.valueOf : null;
var gOPS = Object.getOwnPropertySymbols;
var symToString = typeof Symbol === 'function' && typeof Symbol.iterator === 'symbol' ? Symbol.prototype.toString : null;
var hasShammedSymbols = typeof Symbol === 'function' && typeof Symbol.iterator === 'object';
var isEnumerable = Object.prototype.propertyIsEnumerable;
var gPO = (typeof Reflect === 'function' ? Reflect.getPrototypeOf : Object.getPrototypeOf) || ([].__proto__ === Array.prototype // eslint-disable-line no-proto
? function (O) {
  return O.__proto__; // eslint-disable-line no-proto
} : null);

var inspectCustom = util_inspect.custom;

var inspectSymbol = inspectCustom && isSymbol$2(inspectCustom) ? inspectCustom : null;
var toStringTag = typeof Symbol === 'function' && typeof Symbol.toStringTag !== 'undefined' ? Symbol.toStringTag : null;

var objectInspect = function inspect_(obj, options, depth, seen) {
  var opts = options || {};

  if (has$3(opts, 'quoteStyle') && opts.quoteStyle !== 'single' && opts.quoteStyle !== 'double') {
    throw new TypeError('option "quoteStyle" must be "single" or "double"');
  }

  if (has$3(opts, 'maxStringLength') && (typeof opts.maxStringLength === 'number' ? opts.maxStringLength < 0 && opts.maxStringLength !== Infinity : opts.maxStringLength !== null)) {
    throw new TypeError('option "maxStringLength", if provided, must be a positive integer, Infinity, or `null`');
  }

  var customInspect = has$3(opts, 'customInspect') ? opts.customInspect : true;

  if (typeof customInspect !== 'boolean') {
    throw new TypeError('option "customInspect", if provided, must be `true` or `false`');
  }

  if (has$3(opts, 'indent') && opts.indent !== null && opts.indent !== '\t' && !(parseInt(opts.indent, 10) === opts.indent && opts.indent > 0)) {
    throw new TypeError('options "indent" must be "\\t", an integer > 0, or `null`');
  }

  if (typeof obj === 'undefined') {
    return 'undefined';
  }

  if (obj === null) {
    return 'null';
  }

  if (typeof obj === 'boolean') {
    return obj ? 'true' : 'false';
  }

  if (typeof obj === 'string') {
    return inspectString(obj, opts);
  }

  if (typeof obj === 'number') {
    if (obj === 0) {
      return Infinity / obj > 0 ? '0' : '-0';
    }

    return String(obj);
  }

  if (typeof obj === 'bigint') {
    return String(obj) + 'n';
  }

  var maxDepth = typeof opts.depth === 'undefined' ? 5 : opts.depth;

  if (typeof depth === 'undefined') {
    depth = 0;
  }

  if (depth >= maxDepth && maxDepth > 0 && typeof obj === 'object') {
    return isArray$3(obj) ? '[Array]' : '[Object]';
  }

  var indent = getIndent(opts, depth);

  if (typeof seen === 'undefined') {
    seen = [];
  } else if (indexOf(seen, obj) >= 0) {
    return '[Circular]';
  }

  function inspect(value, from, noIndent) {
    if (from) {
      seen = seen.slice();
      seen.push(from);
    }

    if (noIndent) {
      var newOpts = {
        depth: opts.depth
      };

      if (has$3(opts, 'quoteStyle')) {
        newOpts.quoteStyle = opts.quoteStyle;
      }

      return inspect_(value, newOpts, depth + 1, seen);
    }

    return inspect_(value, opts, depth + 1, seen);
  }

  if (typeof obj === 'function') {
    var name = nameOf(obj);
    var keys = arrObjKeys(obj, inspect);
    return '[Function' + (name ? ': ' + name : ' (anonymous)') + ']' + (keys.length > 0 ? ' { ' + keys.join(', ') + ' }' : '');
  }

  if (isSymbol$2(obj)) {
    var symString = hasShammedSymbols ? String(obj).replace(/^(Symbol\(.*\))_[^)]*$/, '$1') : symToString.call(obj);
    return typeof obj === 'object' && !hasShammedSymbols ? markBoxed(symString) : symString;
  }

  if (isElement(obj)) {
    var s = '<' + String(obj.nodeName).toLowerCase();
    var attrs = obj.attributes || [];

    for (var i = 0; i < attrs.length; i++) {
      s += ' ' + attrs[i].name + '=' + wrapQuotes(quote(attrs[i].value), 'double', opts);
    }

    s += '>';

    if (obj.childNodes && obj.childNodes.length) {
      s += '...';
    }

    s += '</' + String(obj.nodeName).toLowerCase() + '>';
    return s;
  }

  if (isArray$3(obj)) {
    if (obj.length === 0) {
      return '[]';
    }

    var xs = arrObjKeys(obj, inspect);

    if (indent && !singleLineValues(xs)) {
      return '[' + indentedJoin(xs, indent) + ']';
    }

    return '[ ' + xs.join(', ') + ' ]';
  }

  if (isError(obj)) {
    var parts = arrObjKeys(obj, inspect);

    if (parts.length === 0) {
      return '[' + String(obj) + ']';
    }

    return '{ [' + String(obj) + '] ' + parts.join(', ') + ' }';
  }

  if (typeof obj === 'object' && customInspect) {
    if (inspectSymbol && typeof obj[inspectSymbol] === 'function') {
      return obj[inspectSymbol]();
    } else if (typeof obj.inspect === 'function') {
      return obj.inspect();
    }
  }

  if (isMap(obj)) {
    var mapParts = [];
    mapForEach.call(obj, function (value, key) {
      mapParts.push(inspect(key, obj, true) + ' => ' + inspect(value, obj));
    });
    return collectionOf('Map', mapSize.call(obj), mapParts, indent);
  }

  if (isSet(obj)) {
    var setParts = [];
    setForEach.call(obj, function (value) {
      setParts.push(inspect(value, obj));
    });
    return collectionOf('Set', setSize.call(obj), setParts, indent);
  }

  if (isWeakMap(obj)) {
    return weakCollectionOf('WeakMap');
  }

  if (isWeakSet(obj)) {
    return weakCollectionOf('WeakSet');
  }

  if (isWeakRef(obj)) {
    return weakCollectionOf('WeakRef');
  }

  if (isNumber(obj)) {
    return markBoxed(inspect(Number(obj)));
  }

  if (isBigInt(obj)) {
    return markBoxed(inspect(bigIntValueOf.call(obj)));
  }

  if (isBoolean(obj)) {
    return markBoxed(booleanValueOf.call(obj));
  }

  if (isString(obj)) {
    return markBoxed(inspect(String(obj)));
  }

  if (!isDate(obj) && !isRegExp$1(obj)) {
    var ys = arrObjKeys(obj, inspect);
    var isPlainObject = gPO ? gPO(obj) === Object.prototype : obj instanceof Object || obj.constructor === Object;
    var protoTag = obj instanceof Object ? '' : 'null prototype';
    var stringTag = !isPlainObject && toStringTag && Object(obj) === obj && toStringTag in obj ? toStr(obj).slice(8, -1) : protoTag ? 'Object' : '';
    var constructorTag = isPlainObject || typeof obj.constructor !== 'function' ? '' : obj.constructor.name ? obj.constructor.name + ' ' : '';
    var tag = constructorTag + (stringTag || protoTag ? '[' + [].concat(stringTag || [], protoTag || []).join(': ') + '] ' : '');

    if (ys.length === 0) {
      return tag + '{}';
    }

    if (indent) {
      return tag + '{' + indentedJoin(ys, indent) + '}';
    }

    return tag + '{ ' + ys.join(', ') + ' }';
  }

  return String(obj);
};

function wrapQuotes(s, defaultStyle, opts) {
  var quoteChar = (opts.quoteStyle || defaultStyle) === 'double' ? '"' : "'";
  return quoteChar + s + quoteChar;
}

function quote(s) {
  return String(s).replace(/"/g, '&quot;');
}

function isArray$3(obj) {
  return toStr(obj) === '[object Array]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isDate(obj) {
  return toStr(obj) === '[object Date]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isRegExp$1(obj) {
  return toStr(obj) === '[object RegExp]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isError(obj) {
  return toStr(obj) === '[object Error]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isString(obj) {
  return toStr(obj) === '[object String]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isNumber(obj) {
  return toStr(obj) === '[object Number]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
}

function isBoolean(obj) {
  return toStr(obj) === '[object Boolean]' && (!toStringTag || !(typeof obj === 'object' && toStringTag in obj));
} // Symbol and BigInt do have Symbol.toStringTag by spec, so that can't be used to eliminate false positives


function isSymbol$2(obj) {
  if (hasShammedSymbols) {
    return obj && typeof obj === 'object' && obj instanceof Symbol;
  }

  if (typeof obj === 'symbol') {
    return true;
  }

  if (!obj || typeof obj !== 'object' || !symToString) {
    return false;
  }

  try {
    symToString.call(obj);
    return true;
  } catch (e) {}

  return false;
}

function isBigInt(obj) {
  if (!obj || typeof obj !== 'object' || !bigIntValueOf) {
    return false;
  }

  try {
    bigIntValueOf.call(obj);
    return true;
  } catch (e) {}

  return false;
}

var hasOwn = Object.prototype.hasOwnProperty || function (key) {
  return key in this;
};

function has$3(obj, key) {
  return hasOwn.call(obj, key);
}

function toStr(obj) {
  return objectToString$2.call(obj);
}

function nameOf(f) {
  if (f.name) {
    return f.name;
  }

  var m = match.call(functionToString.call(f), /^function\s*([\w$]+)/);

  if (m) {
    return m[1];
  }

  return null;
}

function indexOf(xs, x) {
  if (xs.indexOf) {
    return xs.indexOf(x);
  }

  for (var i = 0, l = xs.length; i < l; i++) {
    if (xs[i] === x) {
      return i;
    }
  }

  return -1;
}

function isMap(x) {
  if (!mapSize || !x || typeof x !== 'object') {
    return false;
  }

  try {
    mapSize.call(x);

    try {
      setSize.call(x);
    } catch (s) {
      return true;
    }

    return x instanceof Map; // core-js workaround, pre-v2.5.0
  } catch (e) {}

  return false;
}

function isWeakMap(x) {
  if (!weakMapHas || !x || typeof x !== 'object') {
    return false;
  }

  try {
    weakMapHas.call(x, weakMapHas);

    try {
      weakSetHas.call(x, weakSetHas);
    } catch (s) {
      return true;
    }

    return x instanceof WeakMap; // core-js workaround, pre-v2.5.0
  } catch (e) {}

  return false;
}

function isWeakRef(x) {
  if (!weakRefDeref || !x || typeof x !== 'object') {
    return false;
  }

  try {
    weakRefDeref.call(x);
    return true;
  } catch (e) {}

  return false;
}

function isSet(x) {
  if (!setSize || !x || typeof x !== 'object') {
    return false;
  }

  try {
    setSize.call(x);

    try {
      mapSize.call(x);
    } catch (m) {
      return true;
    }

    return x instanceof Set; // core-js workaround, pre-v2.5.0
  } catch (e) {}

  return false;
}

function isWeakSet(x) {
  if (!weakSetHas || !x || typeof x !== 'object') {
    return false;
  }

  try {
    weakSetHas.call(x, weakSetHas);

    try {
      weakMapHas.call(x, weakMapHas);
    } catch (s) {
      return true;
    }

    return x instanceof WeakSet; // core-js workaround, pre-v2.5.0
  } catch (e) {}

  return false;
}

function isElement(x) {
  if (!x || typeof x !== 'object') {
    return false;
  }

  if (typeof HTMLElement !== 'undefined' && x instanceof HTMLElement) {
    return true;
  }

  return typeof x.nodeName === 'string' && typeof x.getAttribute === 'function';
}

function inspectString(str, opts) {
  if (str.length > opts.maxStringLength) {
    var remaining = str.length - opts.maxStringLength;
    var trailer = '... ' + remaining + ' more character' + (remaining > 1 ? 's' : '');
    return inspectString(str.slice(0, opts.maxStringLength), opts) + trailer;
  } // eslint-disable-next-line no-control-regex


  var s = str.replace(/(['\\])/g, '\\$1').replace(/[\x00-\x1f]/g, lowbyte);
  return wrapQuotes(s, 'single', opts);
}

function lowbyte(c) {
  var n = c.charCodeAt(0);
  var x = {
    8: 'b',
    9: 't',
    10: 'n',
    12: 'f',
    13: 'r'
  }[n];

  if (x) {
    return '\\' + x;
  }

  return '\\x' + (n < 0x10 ? '0' : '') + n.toString(16).toUpperCase();
}

function markBoxed(str) {
  return 'Object(' + str + ')';
}

function weakCollectionOf(type) {
  return type + ' { ? }';
}

function collectionOf(type, size, entries, indent) {
  var joinedEntries = indent ? indentedJoin(entries, indent) : entries.join(', ');
  return type + ' (' + size + ') {' + joinedEntries + '}';
}

function singleLineValues(xs) {
  for (var i = 0; i < xs.length; i++) {
    if (indexOf(xs[i], '\n') >= 0) {
      return false;
    }
  }

  return true;
}

function getIndent(opts, depth) {
  var baseIndent;

  if (opts.indent === '\t') {
    baseIndent = '\t';
  } else if (typeof opts.indent === 'number' && opts.indent > 0) {
    baseIndent = Array(opts.indent + 1).join(' ');
  } else {
    return null;
  }

  return {
    base: baseIndent,
    prev: Array(depth + 1).join(baseIndent)
  };
}

function indentedJoin(xs, indent) {
  if (xs.length === 0) {
    return '';
  }

  var lineJoiner = '\n' + indent.prev + indent.base;
  return lineJoiner + xs.join(',' + lineJoiner) + '\n' + indent.prev;
}

function arrObjKeys(obj, inspect) {
  var isArr = isArray$3(obj);
  var xs = [];

  if (isArr) {
    xs.length = obj.length;

    for (var i = 0; i < obj.length; i++) {
      xs[i] = has$3(obj, i) ? inspect(obj[i], obj) : '';
    }
  }

  var syms = typeof gOPS === 'function' ? gOPS(obj) : [];
  var symMap;

  if (hasShammedSymbols) {
    symMap = {};

    for (var k = 0; k < syms.length; k++) {
      symMap['$' + syms[k]] = syms[k];
    }
  }

  for (var key in obj) {
    // eslint-disable-line no-restricted-syntax
    if (!has$3(obj, key)) {
      continue;
    } // eslint-disable-line no-restricted-syntax, no-continue


    if (isArr && String(Number(key)) === key && key < obj.length) {
      continue;
    } // eslint-disable-line no-restricted-syntax, no-continue


    if (hasShammedSymbols && symMap['$' + key] instanceof Symbol) {
      // this is to prevent shammed Symbols, which are stored as strings, from being included in the string key section
      continue; // eslint-disable-line no-restricted-syntax, no-continue
    } else if (/[^\w$]/.test(key)) {
      xs.push(inspect(key, obj) + ': ' + inspect(obj[key], obj));
    } else {
      xs.push(key + ': ' + inspect(obj[key], obj));
    }
  }

  if (typeof gOPS === 'function') {
    for (var j = 0; j < syms.length; j++) {
      if (isEnumerable.call(obj, syms[j])) {
        xs.push('[' + inspect(syms[j]) + ']: ' + inspect(obj[syms[j]], obj));
      }
    }
  }

  return xs;
}

var GetIntrinsic = getIntrinsic;

var callBound = callBound$1;

var inspect = objectInspect;

var $TypeError = GetIntrinsic('%TypeError%');
var $WeakMap = GetIntrinsic('%WeakMap%', true);
var $Map = GetIntrinsic('%Map%', true);
var $weakMapGet = callBound('WeakMap.prototype.get', true);
var $weakMapSet = callBound('WeakMap.prototype.set', true);
var $weakMapHas = callBound('WeakMap.prototype.has', true);
var $mapGet = callBound('Map.prototype.get', true);
var $mapSet = callBound('Map.prototype.set', true);
var $mapHas = callBound('Map.prototype.has', true);
/*
 * This function traverses the list returning the node corresponding to the
 * given key.
 *
 * That node is also moved to the head of the list, so that if it's accessed
 * again we don't need to traverse the whole list. By doing so, all the recently
 * used nodes can be accessed relatively quickly.
 */

var listGetNode = function (list, key) {
  // eslint-disable-line consistent-return
  for (var prev = list, curr; (curr = prev.next) !== null; prev = curr) {
    if (curr.key === key) {
      prev.next = curr.next;
      curr.next = list.next;
      list.next = curr; // eslint-disable-line no-param-reassign

      return curr;
    }
  }
};

var listGet = function (objects, key) {
  var node = listGetNode(objects, key);
  return node && node.value;
};

var listSet = function (objects, key, value) {
  var node = listGetNode(objects, key);

  if (node) {
    node.value = value;
  } else {
    // Prepend the new node to the beginning of the list
    objects.next = {
      // eslint-disable-line no-param-reassign
      key: key,
      next: objects.next,
      value: value
    };
  }
};

var listHas = function (objects, key) {
  return !!listGetNode(objects, key);
};

var sideChannel = function getSideChannel() {
  var $wm;
  var $m;
  var $o;
  var channel = {
    assert: function (key) {
      if (!channel.has(key)) {
        throw new $TypeError('Side channel does not contain ' + inspect(key));
      }
    },
    get: function (key) {
      // eslint-disable-line consistent-return
      if ($WeakMap && key && (typeof key === 'object' || typeof key === 'function')) {
        if ($wm) {
          return $weakMapGet($wm, key);
        }
      } else if ($Map) {
        if ($m) {
          return $mapGet($m, key);
        }
      } else {
        if ($o) {
          // eslint-disable-line no-lonely-if
          return listGet($o, key);
        }
      }
    },
    has: function (key) {
      if ($WeakMap && key && (typeof key === 'object' || typeof key === 'function')) {
        if ($wm) {
          return $weakMapHas($wm, key);
        }
      } else if ($Map) {
        if ($m) {
          return $mapHas($m, key);
        }
      } else {
        if ($o) {
          // eslint-disable-line no-lonely-if
          return listHas($o, key);
        }
      }

      return false;
    },
    set: function (key, value) {
      if ($WeakMap && key && (typeof key === 'object' || typeof key === 'function')) {
        if (!$wm) {
          $wm = new $WeakMap();
        }

        $weakMapSet($wm, key, value);
      } else if ($Map) {
        if (!$m) {
          $m = new $Map();
        }

        $mapSet($m, key, value);
      } else {
        if (!$o) {
          /*
           * Initialize the linked list as an empty node, so that we don't have
           * to special-case handling of the first node: we can always refer to
           * it as (previous node).next, instead of something like (list).head
           */
          $o = {
            key: {},
            next: null
          };
        }

        listSet($o, key, value);
      }
    }
  };
  return channel;
};

var replace = String.prototype.replace;
var percentTwenties = /%20/g;
var Format = {
  RFC1738: 'RFC1738',
  RFC3986: 'RFC3986'
};
var formats$3 = {
  'default': Format.RFC3986,
  formatters: {
    RFC1738: function (value) {
      return replace.call(value, percentTwenties, '+');
    },
    RFC3986: function (value) {
      return String(value);
    }
  },
  RFC1738: Format.RFC1738,
  RFC3986: Format.RFC3986
};

var formats$2 = formats$3;

var has$2 = Object.prototype.hasOwnProperty;
var isArray$2 = Array.isArray;

var hexTable = function () {
  var array = [];

  for (var i = 0; i < 256; ++i) {
    array.push('%' + ((i < 16 ? '0' : '') + i.toString(16)).toUpperCase());
  }

  return array;
}();

var compactQueue = function compactQueue(queue) {
  while (queue.length > 1) {
    var item = queue.pop();
    var obj = item.obj[item.prop];

    if (isArray$2(obj)) {
      var compacted = [];

      for (var j = 0; j < obj.length; ++j) {
        if (typeof obj[j] !== 'undefined') {
          compacted.push(obj[j]);
        }
      }

      item.obj[item.prop] = compacted;
    }
  }
};

var arrayToObject = function arrayToObject(source, options) {
  var obj = options && options.plainObjects ? Object.create(null) : {};

  for (var i = 0; i < source.length; ++i) {
    if (typeof source[i] !== 'undefined') {
      obj[i] = source[i];
    }
  }

  return obj;
};

var merge = function merge(target, source, options) {
  /* eslint no-param-reassign: 0 */
  if (!source) {
    return target;
  }

  if (typeof source !== 'object') {
    if (isArray$2(target)) {
      target.push(source);
    } else if (target && typeof target === 'object') {
      if (options && (options.plainObjects || options.allowPrototypes) || !has$2.call(Object.prototype, source)) {
        target[source] = true;
      }
    } else {
      return [target, source];
    }

    return target;
  }

  if (!target || typeof target !== 'object') {
    return [target].concat(source);
  }

  var mergeTarget = target;

  if (isArray$2(target) && !isArray$2(source)) {
    mergeTarget = arrayToObject(target, options);
  }

  if (isArray$2(target) && isArray$2(source)) {
    source.forEach(function (item, i) {
      if (has$2.call(target, i)) {
        var targetItem = target[i];

        if (targetItem && typeof targetItem === 'object' && item && typeof item === 'object') {
          target[i] = merge(targetItem, item, options);
        } else {
          target.push(item);
        }
      } else {
        target[i] = item;
      }
    });
    return target;
  }

  return Object.keys(source).reduce(function (acc, key) {
    var value = source[key];

    if (has$2.call(acc, key)) {
      acc[key] = merge(acc[key], value, options);
    } else {
      acc[key] = value;
    }

    return acc;
  }, mergeTarget);
};

var assign = function assignSingleSource(target, source) {
  return Object.keys(source).reduce(function (acc, key) {
    acc[key] = source[key];
    return acc;
  }, target);
};

var decode = function (str, decoder, charset) {
  var strWithoutPlus = str.replace(/\+/g, ' ');

  if (charset === 'iso-8859-1') {
    // unescape never throws, no try...catch needed:
    return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, unescape);
  } // utf-8


  try {
    return decodeURIComponent(strWithoutPlus);
  } catch (e) {
    return strWithoutPlus;
  }
};

var encode = function encode(str, defaultEncoder, charset, kind, format) {
  // This code was originally written by Brian White (mscdex) for the io.js core querystring library.
  // It has been adapted here for stricter adherence to RFC 3986
  if (str.length === 0) {
    return str;
  }

  var string = str;

  if (typeof str === 'symbol') {
    string = Symbol.prototype.toString.call(str);
  } else if (typeof str !== 'string') {
    string = String(str);
  }

  if (charset === 'iso-8859-1') {
    return escape(string).replace(/%u[0-9a-f]{4}/gi, function ($0) {
      return '%26%23' + parseInt($0.slice(2), 16) + '%3B';
    });
  }

  var out = '';

  for (var i = 0; i < string.length; ++i) {
    var c = string.charCodeAt(i);

    if (c === 0x2D // -
    || c === 0x2E // .
    || c === 0x5F // _
    || c === 0x7E // ~
    || c >= 0x30 && c <= 0x39 // 0-9
    || c >= 0x41 && c <= 0x5A // a-z
    || c >= 0x61 && c <= 0x7A // A-Z
    || format === formats$2.RFC1738 && (c === 0x28 || c === 0x29) // ( )
    ) {
        out += string.charAt(i);
        continue;
      }

    if (c < 0x80) {
      out = out + hexTable[c];
      continue;
    }

    if (c < 0x800) {
      out = out + (hexTable[0xC0 | c >> 6] + hexTable[0x80 | c & 0x3F]);
      continue;
    }

    if (c < 0xD800 || c >= 0xE000) {
      out = out + (hexTable[0xE0 | c >> 12] + hexTable[0x80 | c >> 6 & 0x3F] + hexTable[0x80 | c & 0x3F]);
      continue;
    }

    i += 1;
    c = 0x10000 + ((c & 0x3FF) << 10 | string.charCodeAt(i) & 0x3FF);
    out += hexTable[0xF0 | c >> 18] + hexTable[0x80 | c >> 12 & 0x3F] + hexTable[0x80 | c >> 6 & 0x3F] + hexTable[0x80 | c & 0x3F];
  }

  return out;
};

var compact = function compact(value) {
  var queue = [{
    obj: {
      o: value
    },
    prop: 'o'
  }];
  var refs = [];

  for (var i = 0; i < queue.length; ++i) {
    var item = queue[i];
    var obj = item.obj[item.prop];
    var keys = Object.keys(obj);

    for (var j = 0; j < keys.length; ++j) {
      var key = keys[j];
      var val = obj[key];

      if (typeof val === 'object' && val !== null && refs.indexOf(val) === -1) {
        queue.push({
          obj: obj,
          prop: key
        });
        refs.push(val);
      }
    }
  }

  compactQueue(queue);
  return value;
};

var isRegExp = function isRegExp(obj) {
  return Object.prototype.toString.call(obj) === '[object RegExp]';
};

var isBuffer = function isBuffer(obj) {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  return !!(obj.constructor && obj.constructor.isBuffer && obj.constructor.isBuffer(obj));
};

var combine = function combine(a, b) {
  return [].concat(a, b);
};

var maybeMap = function maybeMap(val, fn) {
  if (isArray$2(val)) {
    var mapped = [];

    for (var i = 0; i < val.length; i += 1) {
      mapped.push(fn(val[i]));
    }

    return mapped;
  }

  return fn(val);
};

var utils$2 = {
  arrayToObject: arrayToObject,
  assign: assign,
  combine: combine,
  compact: compact,
  decode: decode,
  encode: encode,
  isBuffer: isBuffer,
  isRegExp: isRegExp,
  maybeMap: maybeMap,
  merge: merge
};

var getSideChannel = sideChannel;

var utils$1 = utils$2;

var formats$1 = formats$3;

var has$1 = Object.prototype.hasOwnProperty;
var arrayPrefixGenerators = {
  brackets: function brackets(prefix) {
    return prefix + '[]';
  },
  comma: 'comma',
  indices: function indices(prefix, key) {
    return prefix + '[' + key + ']';
  },
  repeat: function repeat(prefix) {
    return prefix;
  }
};
var isArray$1 = Array.isArray;
var push = Array.prototype.push;

var pushToArray = function (arr, valueOrArray) {
  push.apply(arr, isArray$1(valueOrArray) ? valueOrArray : [valueOrArray]);
};

var toISO = Date.prototype.toISOString;
var defaultFormat = formats$1['default'];
var defaults$1 = {
  addQueryPrefix: false,
  allowDots: false,
  charset: 'utf-8',
  charsetSentinel: false,
  delimiter: '&',
  encode: true,
  encoder: utils$1.encode,
  encodeValuesOnly: false,
  format: defaultFormat,
  formatter: formats$1.formatters[defaultFormat],
  // deprecated
  indices: false,
  serializeDate: function serializeDate(date) {
    return toISO.call(date);
  },
  skipNulls: false,
  strictNullHandling: false
};

var isNonNullishPrimitive = function isNonNullishPrimitive(v) {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || typeof v === 'symbol' || typeof v === 'bigint';
};

var stringify$1 = function stringify(object, prefix, generateArrayPrefix, strictNullHandling, skipNulls, encoder, filter, sort, allowDots, serializeDate, format, formatter, encodeValuesOnly, charset, sideChannel) {
  var obj = object;

  if (sideChannel.has(object)) {
    throw new RangeError('Cyclic object value');
  }

  if (typeof filter === 'function') {
    obj = filter(prefix, obj);
  } else if (obj instanceof Date) {
    obj = serializeDate(obj);
  } else if (generateArrayPrefix === 'comma' && isArray$1(obj)) {
    obj = utils$1.maybeMap(obj, function (value) {
      if (value instanceof Date) {
        return serializeDate(value);
      }

      return value;
    });
  }

  if (obj === null) {
    if (strictNullHandling) {
      return encoder && !encodeValuesOnly ? encoder(prefix, defaults$1.encoder, charset, 'key', format) : prefix;
    }

    obj = '';
  }

  if (isNonNullishPrimitive(obj) || utils$1.isBuffer(obj)) {
    if (encoder) {
      var keyValue = encodeValuesOnly ? prefix : encoder(prefix, defaults$1.encoder, charset, 'key', format);
      return [formatter(keyValue) + '=' + formatter(encoder(obj, defaults$1.encoder, charset, 'value', format))];
    }

    return [formatter(prefix) + '=' + formatter(String(obj))];
  }

  var values = [];

  if (typeof obj === 'undefined') {
    return values;
  }

  var objKeys;

  if (generateArrayPrefix === 'comma' && isArray$1(obj)) {
    // we need to join elements in
    objKeys = [{
      value: obj.length > 0 ? obj.join(',') || null : undefined
    }];
  } else if (isArray$1(filter)) {
    objKeys = filter;
  } else {
    var keys = Object.keys(obj);
    objKeys = sort ? keys.sort(sort) : keys;
  }

  for (var i = 0; i < objKeys.length; ++i) {
    var key = objKeys[i];
    var value = typeof key === 'object' && key.value !== undefined ? key.value : obj[key];

    if (skipNulls && value === null) {
      continue;
    }

    var keyPrefix = isArray$1(obj) ? typeof generateArrayPrefix === 'function' ? generateArrayPrefix(prefix, key) : prefix : prefix + (allowDots ? '.' + key : '[' + key + ']');
    sideChannel.set(object, true);
    var valueSideChannel = getSideChannel();
    pushToArray(values, stringify(value, keyPrefix, generateArrayPrefix, strictNullHandling, skipNulls, encoder, filter, sort, allowDots, serializeDate, format, formatter, encodeValuesOnly, charset, valueSideChannel));
  }

  return values;
};

var normalizeStringifyOptions = function normalizeStringifyOptions(opts) {
  if (!opts) {
    return defaults$1;
  }

  if (opts.encoder !== null && opts.encoder !== undefined && typeof opts.encoder !== 'function') {
    throw new TypeError('Encoder has to be a function.');
  }

  var charset = opts.charset || defaults$1.charset;

  if (typeof opts.charset !== 'undefined' && opts.charset !== 'utf-8' && opts.charset !== 'iso-8859-1') {
    throw new TypeError('The charset option must be either utf-8, iso-8859-1, or undefined');
  }

  var format = formats$1['default'];

  if (typeof opts.format !== 'undefined') {
    if (!has$1.call(formats$1.formatters, opts.format)) {
      throw new TypeError('Unknown format option provided.');
    }

    format = opts.format;
  }

  var formatter = formats$1.formatters[format];
  var filter = defaults$1.filter;

  if (typeof opts.filter === 'function' || isArray$1(opts.filter)) {
    filter = opts.filter;
  }

  return {
    addQueryPrefix: typeof opts.addQueryPrefix === 'boolean' ? opts.addQueryPrefix : defaults$1.addQueryPrefix,
    allowDots: typeof opts.allowDots === 'undefined' ? defaults$1.allowDots : !!opts.allowDots,
    charset: charset,
    charsetSentinel: typeof opts.charsetSentinel === 'boolean' ? opts.charsetSentinel : defaults$1.charsetSentinel,
    delimiter: typeof opts.delimiter === 'undefined' ? defaults$1.delimiter : opts.delimiter,
    encode: typeof opts.encode === 'boolean' ? opts.encode : defaults$1.encode,
    encoder: typeof opts.encoder === 'function' ? opts.encoder : defaults$1.encoder,
    encodeValuesOnly: typeof opts.encodeValuesOnly === 'boolean' ? opts.encodeValuesOnly : defaults$1.encodeValuesOnly,
    filter: filter,
    format: format,
    formatter: formatter,
    serializeDate: typeof opts.serializeDate === 'function' ? opts.serializeDate : defaults$1.serializeDate,
    skipNulls: typeof opts.skipNulls === 'boolean' ? opts.skipNulls : defaults$1.skipNulls,
    sort: typeof opts.sort === 'function' ? opts.sort : null,
    strictNullHandling: typeof opts.strictNullHandling === 'boolean' ? opts.strictNullHandling : defaults$1.strictNullHandling
  };
};

var stringify_1 = function (object, opts) {
  var obj = object;
  var options = normalizeStringifyOptions(opts);
  var objKeys;
  var filter;

  if (typeof options.filter === 'function') {
    filter = options.filter;
    obj = filter('', obj);
  } else if (isArray$1(options.filter)) {
    filter = options.filter;
    objKeys = filter;
  }

  var keys = [];

  if (typeof obj !== 'object' || obj === null) {
    return '';
  }

  var arrayFormat;

  if (opts && opts.arrayFormat in arrayPrefixGenerators) {
    arrayFormat = opts.arrayFormat;
  } else if (opts && 'indices' in opts) {
    arrayFormat = opts.indices ? 'indices' : 'repeat';
  } else {
    arrayFormat = 'indices';
  }

  var generateArrayPrefix = arrayPrefixGenerators[arrayFormat];

  if (!objKeys) {
    objKeys = Object.keys(obj);
  }

  if (options.sort) {
    objKeys.sort(options.sort);
  }

  var sideChannel = getSideChannel();

  for (var i = 0; i < objKeys.length; ++i) {
    var key = objKeys[i];

    if (options.skipNulls && obj[key] === null) {
      continue;
    }

    pushToArray(keys, stringify$1(obj[key], key, generateArrayPrefix, options.strictNullHandling, options.skipNulls, options.encode ? options.encoder : null, options.filter, options.sort, options.allowDots, options.serializeDate, options.format, options.formatter, options.encodeValuesOnly, options.charset, sideChannel));
  }

  var joined = keys.join(options.delimiter);
  var prefix = options.addQueryPrefix === true ? '?' : '';

  if (options.charsetSentinel) {
    if (options.charset === 'iso-8859-1') {
      // encodeURIComponent('&#10003;'), the "numeric entity" representation of a checkmark
      prefix += 'utf8=%26%2310003%3B&';
    } else {
      // encodeURIComponent('✓')
      prefix += 'utf8=%E2%9C%93&';
    }
  }

  return joined.length > 0 ? prefix + joined : '';
};

var utils = utils$2;

var has = Object.prototype.hasOwnProperty;
var isArray = Array.isArray;
var defaults = {
  allowDots: false,
  allowPrototypes: false,
  allowSparse: false,
  arrayLimit: 20,
  charset: 'utf-8',
  charsetSentinel: false,
  comma: false,
  decoder: utils.decode,
  delimiter: '&',
  depth: 5,
  ignoreQueryPrefix: false,
  interpretNumericEntities: false,
  parameterLimit: 1000,
  parseArrays: true,
  plainObjects: false,
  strictNullHandling: false
};

var interpretNumericEntities = function (str) {
  return str.replace(/&#(\d+);/g, function ($0, numberStr) {
    return String.fromCharCode(parseInt(numberStr, 10));
  });
};

var parseArrayValue = function (val, options) {
  if (val && typeof val === 'string' && options.comma && val.indexOf(',') > -1) {
    return val.split(',');
  }

  return val;
}; // This is what browsers will submit when the ✓ character occurs in an
// application/x-www-form-urlencoded body and the encoding of the page containing
// the form is iso-8859-1, or when the submitted form has an accept-charset
// attribute of iso-8859-1. Presumably also with other charsets that do not contain
// the ✓ character, such as us-ascii.


var isoSentinel = 'utf8=%26%2310003%3B'; // encodeURIComponent('&#10003;')
// These are the percent-encoded utf-8 octets representing a checkmark, indicating that the request actually is utf-8 encoded.

var charsetSentinel = 'utf8=%E2%9C%93'; // encodeURIComponent('✓')

var parseValues = function parseQueryStringValues(str, options) {
  var obj = {};
  var cleanStr = options.ignoreQueryPrefix ? str.replace(/^\?/, '') : str;
  var limit = options.parameterLimit === Infinity ? undefined : options.parameterLimit;
  var parts = cleanStr.split(options.delimiter, limit);
  var skipIndex = -1; // Keep track of where the utf8 sentinel was found

  var i;
  var charset = options.charset;

  if (options.charsetSentinel) {
    for (i = 0; i < parts.length; ++i) {
      if (parts[i].indexOf('utf8=') === 0) {
        if (parts[i] === charsetSentinel) {
          charset = 'utf-8';
        } else if (parts[i] === isoSentinel) {
          charset = 'iso-8859-1';
        }

        skipIndex = i;
        i = parts.length; // The eslint settings do not allow break;
      }
    }
  }

  for (i = 0; i < parts.length; ++i) {
    if (i === skipIndex) {
      continue;
    }

    var part = parts[i];
    var bracketEqualsPos = part.indexOf(']=');
    var pos = bracketEqualsPos === -1 ? part.indexOf('=') : bracketEqualsPos + 1;
    var key, val;

    if (pos === -1) {
      key = options.decoder(part, defaults.decoder, charset, 'key');
      val = options.strictNullHandling ? null : '';
    } else {
      key = options.decoder(part.slice(0, pos), defaults.decoder, charset, 'key');
      val = utils.maybeMap(parseArrayValue(part.slice(pos + 1), options), function (encodedVal) {
        return options.decoder(encodedVal, defaults.decoder, charset, 'value');
      });
    }

    if (val && options.interpretNumericEntities && charset === 'iso-8859-1') {
      val = interpretNumericEntities(val);
    }

    if (part.indexOf('[]=') > -1) {
      val = isArray(val) ? [val] : val;
    }

    if (has.call(obj, key)) {
      obj[key] = utils.combine(obj[key], val);
    } else {
      obj[key] = val;
    }
  }

  return obj;
};

var parseObject = function (chain, val, options, valuesParsed) {
  var leaf = valuesParsed ? val : parseArrayValue(val, options);

  for (var i = chain.length - 1; i >= 0; --i) {
    var obj;
    var root = chain[i];

    if (root === '[]' && options.parseArrays) {
      obj = [].concat(leaf);
    } else {
      obj = options.plainObjects ? Object.create(null) : {};
      var cleanRoot = root.charAt(0) === '[' && root.charAt(root.length - 1) === ']' ? root.slice(1, -1) : root;
      var index = parseInt(cleanRoot, 10);

      if (!options.parseArrays && cleanRoot === '') {
        obj = {
          0: leaf
        };
      } else if (!isNaN(index) && root !== cleanRoot && String(index) === cleanRoot && index >= 0 && options.parseArrays && index <= options.arrayLimit) {
        obj = [];
        obj[index] = leaf;
      } else {
        obj[cleanRoot] = leaf;
      }
    }

    leaf = obj;
  }

  return leaf;
};

var parseKeys = function parseQueryStringKeys(givenKey, val, options, valuesParsed) {
  if (!givenKey) {
    return;
  } // Transform dot notation to bracket notation


  var key = options.allowDots ? givenKey.replace(/\.([^.[]+)/g, '[$1]') : givenKey; // The regex chunks

  var brackets = /(\[[^[\]]*])/;
  var child = /(\[[^[\]]*])/g; // Get the parent

  var segment = options.depth > 0 && brackets.exec(key);
  var parent = segment ? key.slice(0, segment.index) : key; // Stash the parent if it exists

  var keys = [];

  if (parent) {
    // If we aren't using plain objects, optionally prefix keys that would overwrite object prototype properties
    if (!options.plainObjects && has.call(Object.prototype, parent)) {
      if (!options.allowPrototypes) {
        return;
      }
    }

    keys.push(parent);
  } // Loop through children appending to the array until we hit depth


  var i = 0;

  while (options.depth > 0 && (segment = child.exec(key)) !== null && i < options.depth) {
    i += 1;

    if (!options.plainObjects && has.call(Object.prototype, segment[1].slice(1, -1))) {
      if (!options.allowPrototypes) {
        return;
      }
    }

    keys.push(segment[1]);
  } // If there's a remainder, just add whatever is left


  if (segment) {
    keys.push('[' + key.slice(segment.index) + ']');
  }

  return parseObject(keys, val, options, valuesParsed);
};

var normalizeParseOptions = function normalizeParseOptions(opts) {
  if (!opts) {
    return defaults;
  }

  if (opts.decoder !== null && opts.decoder !== undefined && typeof opts.decoder !== 'function') {
    throw new TypeError('Decoder has to be a function.');
  }

  if (typeof opts.charset !== 'undefined' && opts.charset !== 'utf-8' && opts.charset !== 'iso-8859-1') {
    throw new TypeError('The charset option must be either utf-8, iso-8859-1, or undefined');
  }

  var charset = typeof opts.charset === 'undefined' ? defaults.charset : opts.charset;
  return {
    allowDots: typeof opts.allowDots === 'undefined' ? defaults.allowDots : !!opts.allowDots,
    allowPrototypes: typeof opts.allowPrototypes === 'boolean' ? opts.allowPrototypes : defaults.allowPrototypes,
    allowSparse: typeof opts.allowSparse === 'boolean' ? opts.allowSparse : defaults.allowSparse,
    arrayLimit: typeof opts.arrayLimit === 'number' ? opts.arrayLimit : defaults.arrayLimit,
    charset: charset,
    charsetSentinel: typeof opts.charsetSentinel === 'boolean' ? opts.charsetSentinel : defaults.charsetSentinel,
    comma: typeof opts.comma === 'boolean' ? opts.comma : defaults.comma,
    decoder: typeof opts.decoder === 'function' ? opts.decoder : defaults.decoder,
    delimiter: typeof opts.delimiter === 'string' || utils.isRegExp(opts.delimiter) ? opts.delimiter : defaults.delimiter,
    // eslint-disable-next-line no-implicit-coercion, no-extra-parens
    depth: typeof opts.depth === 'number' || opts.depth === false ? +opts.depth : defaults.depth,
    ignoreQueryPrefix: opts.ignoreQueryPrefix === true,
    interpretNumericEntities: typeof opts.interpretNumericEntities === 'boolean' ? opts.interpretNumericEntities : defaults.interpretNumericEntities,
    parameterLimit: typeof opts.parameterLimit === 'number' ? opts.parameterLimit : defaults.parameterLimit,
    parseArrays: opts.parseArrays !== false,
    plainObjects: typeof opts.plainObjects === 'boolean' ? opts.plainObjects : defaults.plainObjects,
    strictNullHandling: typeof opts.strictNullHandling === 'boolean' ? opts.strictNullHandling : defaults.strictNullHandling
  };
};

var parse$1 = function (str, opts) {
  var options = normalizeParseOptions(opts);

  if (str === '' || str === null || typeof str === 'undefined') {
    return options.plainObjects ? Object.create(null) : {};
  }

  var tempObj = typeof str === 'string' ? parseValues(str, options) : str;
  var obj = options.plainObjects ? Object.create(null) : {}; // Iterate over the keys and setup the new object

  var keys = Object.keys(tempObj);

  for (var i = 0; i < keys.length; ++i) {
    var key = keys[i];
    var newObj = parseKeys(key, tempObj[key], options, typeof str === 'string');
    obj = utils.merge(obj, newObj, options);
  }

  if (options.allowSparse === true) {
    return obj;
  }

  return utils.compact(obj);
};

var stringify = stringify_1;

var parse = parse$1;

var formats = formats$3;

var lib = {
  formats: formats,
  parse: parse,
  stringify: stringify
};

var document$1 = window_1.document;
var getQueryParams = function getQueryParams() {
  // document.location is not defined in react-native
  if (document$1 && document$1.location && document$1.location.search) {
    return lib.parse(document$1.location.search, {
      ignoreQueryPrefix: true
    });
  }

  return {};
};
var getQueryParam = function getQueryParam(key) {
  var params = getQueryParams();
  return params[key];
};

var esm = /*#__PURE__*/Object.freeze({
	__proto__: null,
	addArgsEnhancer: addArgsEnhancer,
	addArgTypesEnhancer: addArgTypesEnhancer,
	addDecorator: addDecorator,
	addLoader: addLoader,
	addArgs: addArgs,
	addArgTypes: addArgTypes,
	addParameters: addParameters,
	setGlobalRender: setGlobalRender,
	ClientApi: ClientApi,
	getQueryParams: getQueryParams,
	getQueryParam: getQueryParam,
	StoryStore: StoryStore,
	combineParameters: combineParameters,
	filterArgTypes: filterArgTypes,
	inferControls: inferControls,
	normalizeInputType: normalizeInputType,
	normalizeInputTypes: normalizeInputTypes,
	normalizeStory: normalizeStory,
	processCSFFile: processCSFFile,
	prepareStory: prepareStory,
	normalizeComponentAnnotations: normalizeComponentAnnotations,
	normalizeProjectAnnotations: normalizeProjectAnnotations,
	getValuesFromArgTypes: getValuesFromArgTypes,
	getField: getField,
	getArrayField: getArrayField,
	getObjectField: getObjectField,
	getSingletonField: getSingletonField,
	composeConfigs: composeConfigs,
	setProjectAnnotations: setProjectAnnotations,
	composeStory: composeStory,
	composeStories: composeStories,
	HooksContext: HooksContext,
	applyHooks: applyHooks,
	useMemo: useMemo,
	useCallback: useCallback,
	useRef: useRef,
	useState: useState,
	useReducer: useReducer,
	useEffect: useEffect,
	useChannel: useChannel,
	useStoryContext: useStoryContext,
	useParameter: useParameter,
	useArgs: useArgs,
	useGlobals: useGlobals,
	useSharedState: useSharedState,
	useAddonState: useAddonState,
	decorateStory: decorateStory,
	sanitizeStoryContextUpdate: sanitizeStoryContextUpdate,
	defaultDecorateStory: defaultDecorateStory,
	mapArgsToTypes: mapArgsToTypes,
	combineArgs: combineArgs,
	validateOptions: validateOptions,
	DEEPLY_EQUAL: DEEPLY_EQUAL,
	deepDiff: deepDiff,
	NO_TARGET_NAME: NO_TARGET_NAME,
	groupArgsByTarget: groupArgsByTarget,
	noTargetArgs: noTargetArgs,
	userOrAutoTitleFromSpecifier: userOrAutoTitleFromSpecifier,
	userOrAutoTitle: userOrAutoTitle,
	sortStoriesV7: sortStoriesV7,
	sortStoriesV6: sortStoriesV6
});

var require$$1 = /*@__PURE__*/getAugmentedNamespace(esm);

var require$$2 = /*@__PURE__*/getAugmentedNamespace(esm$1);

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$2(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$2;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;
/** Detect free variable `self`. */


var freeSelf = typeof self == 'object' && self && self.Object === Object && self;
/** Used as a reference to the global object. */

var root$2 = freeGlobal || freeSelf || Function('return this')();
var _root = root$2;

var root$1 = _root;
/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */


var now$1 = function () {
  return root$1.Date.now();
};

var now_1 = now$1;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;
/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */

function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}

  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;
/** Used to match leading whitespace. */


var reTrimStart = /^\s+/;
/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */

function baseTrim$1(string) {
  return string ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '') : string;
}

var _baseTrim = baseTrim$1;

var root = _root;
/** Built-in value references. */


var Symbol$3 = root.Symbol;
var _Symbol = Symbol$3;

var Symbol$2 = _Symbol;
/** Used for built-in method references. */


var objectProto$1 = Object.prototype;
/** Used to check objects for own properties. */

var hasOwnProperty = objectProto$1.hasOwnProperty;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString$1 = objectProto$1.toString;
/** Built-in value references. */

var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;
/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */

function getRawTag$1(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);

  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }

  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto = Object.prototype;
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var nativeObjectToString = objectProto.toString;
/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */

function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$1 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;
/** `Object#toString` result references. */


var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';
/** Built-in value references. */

var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;
/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */

function baseGetTag$1(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }

  return symToStringTag && symToStringTag in Object(value) ? getRawTag(value) : objectToString(value);
}

var _baseGetTag = baseGetTag$1;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$1(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$1;

var baseGetTag = _baseGetTag,
    isObjectLike = isObjectLike_1;
/** `Object#toString` result references. */


var symbolTag = '[object Symbol]';
/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */

function isSymbol$1(value) {
  return typeof value == 'symbol' || isObjectLike(value) && baseGetTag(value) == symbolTag;
}

var isSymbol_1 = isSymbol$1;

var baseTrim = _baseTrim,
    isObject$1 = isObject_1,
    isSymbol = isSymbol_1;
/** Used as references for various `Number` constants. */


var NAN = 0 / 0;
/** Used to detect bad signed hexadecimal string values. */

var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;
/** Used to detect binary string values. */

var reIsBinary = /^0b[01]+$/i;
/** Used to detect octal string values. */

var reIsOctal = /^0o[0-7]+$/i;
/** Built-in method references without a dependency on `root`. */

var freeParseInt = parseInt;
/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */

function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }

  if (isSymbol(value)) {
    return NAN;
  }

  if (isObject$1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$1(other) ? other + '' : other;
  }

  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }

  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}

var toNumber_1 = toNumber$1;

var isObject = isObject_1,
    now = now_1,
    toNumber = toNumber_1;
/** Error message constants. */


var FUNC_ERROR_TEXT = 'Expected a function';
/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeMax = Math.max,
    nativeMin = Math.min;
/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */

function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }

  wait = toNumber(wait) || 0;

  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time; // Start the timer for the trailing edge.

    timerId = setTimeout(timerExpired, wait); // Invoke the leading edge.

    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;
    return maxing ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime; // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.

    return lastCallTime === undefined || timeSinceLastCall >= wait || timeSinceLastCall < 0 || maxing && timeSinceLastInvoke >= maxWait;
  }

  function timerExpired() {
    var time = now();

    if (shouldInvoke(time)) {
      return trailingEdge(time);
    } // Restart the timer.


    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined; // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.

    if (trailing && lastArgs) {
      return invokeFunc(time);
    }

    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }

    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }

      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }

    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }

    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce;

var KnobManager = {};

/*!
 * escape-html
 * Copyright(c) 2012-2013 TJ Holowaychuk
 * Copyright(c) 2015 Andreas Lubbe
 * Copyright(c) 2015 Tiancheng "Timothy" Gu
 * MIT Licensed
 */
/**
 * Module variables.
 * @private
 */

var matchHtmlRegExp = /["'&<>]/;
/**
 * Module exports.
 * @public
 */

var escapeHtml_1 = escapeHtml;
/**
 * Escape special characters in the given string of html.
 *
 * @param  {string} string The string to escape for inserting into HTML
 * @return {string}
 * @public
 */

function escapeHtml(string) {
  var str = '' + string;
  var match = matchHtmlRegExp.exec(str);

  if (!match) {
    return str;
  }

  var escape;
  var html = '';
  var index = 0;
  var lastIndex = 0;

  for (index = match.index; index < str.length; index++) {
    switch (str.charCodeAt(index)) {
      case 34:
        // "
        escape = '&quot;';
        break;

      case 38:
        // &
        escape = '&amp;';
        break;

      case 39:
        // '
        escape = '&#39;';
        break;

      case 60:
        // <
        escape = '&lt;';
        break;

      case 62:
        // >
        escape = '&gt;';
        break;

      default:
        continue;
    }

    if (lastIndex !== index) {
      html += str.substring(lastIndex, index);
    }

    lastIndex = index + 1;
    html += escape;
  }

  return lastIndex !== index ? html + str.substring(lastIndex, index) : html;
}

var KnobStore = {};

(function (exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var callArg = function callArg(fn) {
  return fn();
};

var callAll = function callAll(fns) {
  return fns.forEach(callArg);
};

var KnobStore =
/*#__PURE__*/
function () {
  function KnobStore() {
    _classCallCheck(this, KnobStore);

    _defineProperty(this, "store", {});

    _defineProperty(this, "callbacks", []);
  }

  _createClass(KnobStore, [{
    key: "has",
    value: function has(key) {
      return this.store[key] !== undefined;
    }
  }, {
    key: "set",
    value: function set(key, value) {
      this.store[key] = _objectSpread(_objectSpread({}, value), {}, {
        used: true,
        groupId: value.groupId
      }); // debounce the execution of the callbacks for 50 milliseconds

      if (this.timer) {
        clearTimeout(this.timer);
      }

      this.timer = setTimeout(callAll, 50, this.callbacks);
    }
  }, {
    key: "update",
    value: function update(key, options) {
      this.store[key] = _objectSpread(_objectSpread({}, this.store[key]), options);
    }
  }, {
    key: "get",
    value: function get(key) {
      var knob = this.store[key];

      if (knob) {
        knob.used = true;
      }

      return knob;
    }
  }, {
    key: "getAll",
    value: function getAll() {
      return this.store;
    }
  }, {
    key: "reset",
    value: function reset() {
      this.store = {};
    }
  }, {
    key: "markAllUnused",
    value: function markAllUnused() {
      var _this = this;

      Object.keys(this.store).forEach(function (knobName) {
        _this.store[knobName].used = false;
      });
    }
  }, {
    key: "subscribe",
    value: function subscribe(cb) {
      this.callbacks.push(cb);
    }
  }, {
    key: "unsubscribe",
    value: function unsubscribe(cb) {
      var index = this.callbacks.indexOf(cb);
      this.callbacks.splice(index, 1);
    }
  }]);

  return KnobStore;
}();

exports["default"] = KnobStore;
}(KnobStore));

var converters$1 = {};

Object.defineProperty(converters$1, "__esModule", {
  value: true
});
converters$1.deserializers = converters$1.serializers = converters$1.converters = void 0;

var unconvertable = function unconvertable() {
  return undefined;
};

var converters = {
  jsonParse: function jsonParse(value) {
    return JSON.parse(value);
  },
  jsonStringify: function jsonStringify(value) {
    return JSON.stringify(value);
  },
  simple: function simple(value) {
    return value;
  },
  stringifyIfSet: function stringifyIfSet(value) {
    return value === null || value === undefined ? '' : String(value);
  },
  stringifyIfTruthy: function stringifyIfTruthy(value) {
    return value ? String(value) : null;
  },
  toArray: function toArray(value) {
    if (Array.isArray(value)) {
      return value;
    }

    return value.split(',');
  },
  toBoolean: function toBoolean(value) {
    return value === 'true';
  },
  toDate: function toDate(value) {
    return new Date(value).getTime() || new Date().getTime();
  },
  toFloat: function toFloat(value) {
    return value === '' ? null : parseFloat(value);
  }
};
converters$1.converters = converters;
var serializers = {
  array: converters.simple,
  "boolean": converters.stringifyIfTruthy,
  button: unconvertable,
  checkbox: converters.simple,
  color: converters.simple,
  date: converters.toDate,
  files: unconvertable,
  number: converters.stringifyIfSet,
  object: converters.jsonStringify,
  options: converters.simple,
  radios: converters.simple,
  select: converters.simple,
  text: converters.simple
};
converters$1.serializers = serializers;
var deserializers = {
  array: converters.toArray,
  "boolean": converters.toBoolean,
  button: unconvertable,
  checkbox: converters.simple,
  color: converters.simple,
  date: converters.toDate,
  files: unconvertable,
  number: converters.toFloat,
  object: converters.jsonParse,
  options: converters.simple,
  radios: converters.simple,
  select: converters.simple,
  text: converters.simple
};
converters$1.deserializers = deserializers;

(function (exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _global = window_1;

var _escapeHtml = _interopRequireDefault(escapeHtml_1);

var _clientApi = require$$1;

var _KnobStore = _interopRequireDefault(KnobStore);

var _shared = shared$c;

var _converters = converters$1;

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]);

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

var knobValuesFromUrl = Object.entries((0, _clientApi.getQueryParams)()).reduce(function (acc, _ref) {
  var _ref2 = _slicedToArray(_ref, 2),
      k = _ref2[0],
      v = _ref2[1];

  if (k.includes('knob-')) {
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, k.replace('knob-', ''), v));
  }

  return acc;
}, {}); // This is used by _mayCallChannel to determine how long to wait to before triggering a panel update

var PANEL_UPDATE_INTERVAL = 400;

function escapeStrings(obj) {
  if (typeof obj === 'string') {
    return (0, _escapeHtml["default"])(obj);
  }

  if (obj == null || _typeof(obj) !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    var newArray = obj.map(escapeStrings);
    var didChange = newArray.some(function (newValue, key) {
      return newValue !== obj[key];
    });
    return didChange ? newArray : obj;
  }

  return Object.entries(obj).reduce(function (acc, _ref3) {
    var _ref4 = _slicedToArray(_ref3, 2),
        key = _ref4[0],
        oldValue = _ref4[1];

    var newValue = escapeStrings(oldValue);
    return newValue === oldValue ? acc : _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, key, newValue));
  }, obj);
}

var KnobManager =
/*#__PURE__*/
function () {
  function KnobManager() {
    _classCallCheck(this, KnobManager);

    _defineProperty(this, "knobStore", new _KnobStore["default"]());

    _defineProperty(this, "options", {});

    _defineProperty(this, "calling", false);
  }

  _createClass(KnobManager, [{
    key: "setChannel",
    value: function setChannel(channel) {
      this.channel = channel;
    }
  }, {
    key: "setOptions",
    value: function setOptions(options) {
      this.options = options;
    }
  }, {
    key: "getKnobValue",
    value: function getKnobValue(_ref5) {
      var value = _ref5.value;
      return this.options.escapeHTML ? escapeStrings(value) : value;
    }
  }, {
    key: "knob",
    value: function knob(name, options) {
      this._mayCallChannel();

      var knobName = options.groupId ? "".concat(name, "_").concat(options.groupId) : name;
      var knobStore = this.knobStore;
      var existingKnob = knobStore.get(knobName); // We need to return the value set by the knob editor via this.
      // Normally the knobs are reset and so re-use is safe as long as the types match
      // when in storyshots, though the change event isn't called and so the knobs aren't reset, making this code fail
      // so always create a new knob when in storyshots

      if (existingKnob && options.type === existingKnob.type && _global.navigator && ( // userAgent is not set in react-native
      !_global.navigator.userAgent || !_global.navigator.userAgent.includes('jsdom'))) {
        options.value;
            var restOptions = _objectWithoutProperties(options, ["value"]);

        knobStore.update(knobName, restOptions);
        return this.getKnobValue(existingKnob);
      }

      var knobInfo = _objectSpread(_objectSpread({}, options), {}, {
        name: knobName,
        label: name
      });

      if (knobValuesFromUrl[knobName]) {
        var _value = _converters.deserializers[options.type](knobValuesFromUrl[knobName]);

        knobInfo.defaultValue = _value;
        knobInfo.value = _value;
        delete knobValuesFromUrl[knobName];
      } else {
        knobInfo.defaultValue = options.value;
      }

      knobStore.set(knobName, knobInfo);
      return this.getKnobValue(knobStore.get(knobName));
    }
  }, {
    key: "_mayCallChannel",
    value: function _mayCallChannel() {
      var _this = this; // Re rendering of the story may cause changes to the knobStore. Some new knobs maybe added and
      // Some knobs may go unused. So we need to update the panel accordingly. For example remove the
      // unused knobs from the panel. This function sends the `setKnobs` message to the channel
      // triggering a panel re-render.


      if (!this.channel) {
        // to prevent call to undefined channel and therefore throwing TypeError
        return;
      }

      if (this.calling) {
        // If a call to channel has already registered ignore this call.
        // Once the previous call is completed all the changes to knobStore including the one that
        // triggered this, will be added to the panel.
        // This avoids emitting to the channel within very short periods of time.
        return;
      }

      this.calling = true;
      var timestamp = +new Date();
      setTimeout(function () {
        _this.calling = false; // emit to the channel and trigger a panel re-render

        if (_this.channel) _this.channel.emit(_shared.SET, {
          knobs: _this.knobStore.getAll(),
          timestamp: timestamp
        });
      }, PANEL_UPDATE_INTERVAL);
    }
  }]);

  return KnobManager;
}();

exports["default"] = KnobManager;
}(KnobManager));

Object.defineProperty(registerKnobs$1, "__esModule", {
  value: true
});
registerKnobs$1.registerKnobs = registerKnobs;
registerKnobs$1.manager = void 0;

var _addons = _interopRequireDefault(require$$0$1);

var _clientApi = require$$1;

var _coreEvents = require$$2;

var _debounce = _interopRequireDefault(debounce_1);

var _KnobManager = _interopRequireDefault(KnobManager);

var _shared = shared$c;

function _interopRequireDefault(obj) {
  return obj && obj.__esModule ? obj : {
    "default": obj
  };
}

var manager = new _KnobManager["default"]();
registerKnobs$1.manager = manager;
var knobStore = manager.knobStore;
var COMPONENT_FORCE_RENDER_DEBOUNCE_DELAY_MS = 325;

function forceReRender() {
  _addons["default"].getChannel().emit(_coreEvents.FORCE_RE_RENDER);
}

function setPaneKnobs() {
  var timestamp = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : +new Date();

  var channel = _addons["default"].getChannel();

  channel.emit(_shared.SET, {
    knobs: knobStore.getAll(),
    timestamp: timestamp
  });
}

var resetAndForceUpdate = function resetAndForceUpdate() {
  knobStore.markAllUnused();
  forceReRender();
}; // Increase performance by reducing how frequently the story is recreated during knob changes


var debouncedResetAndForceUpdate = (0, _debounce["default"])(resetAndForceUpdate, COMPONENT_FORCE_RENDER_DEBOUNCE_DELAY_MS);

function knobChanged(change) {
  var name = change.name;
  var value = change.value; // Update the related knob and it's value.

  var knobOptions = knobStore.get(name);
  knobOptions.value = value;

  if (!manager.options.disableForceUpdate && !knobOptions.disableForceUpdate) {
    if (!manager.options.disableDebounce && !knobOptions.disableDebounce) {
      debouncedResetAndForceUpdate();
    } else {
      resetAndForceUpdate();
    }
  }
}

function knobClicked(clicked) {
  var knobOptions = knobStore.get(clicked.name);

  if (knobOptions.callback && knobOptions.callback() !== false) {
    forceReRender();
  }
}

function resetKnobs() {
  knobStore.reset();
  setPaneKnobs(false);
}

function resetKnobsAndForceReRender() {
  knobStore.reset();
  forceReRender();
  setPaneKnobs(false);
}

function disconnectCallbacks() {
  var channel = _addons["default"].getChannel();

  channel.removeListener(_shared.CHANGE, knobChanged);
  channel.removeListener(_shared.CLICK, knobClicked);
  channel.removeListener(_coreEvents.STORY_CHANGED, resetKnobs);
  channel.removeListener(_shared.RESET, resetKnobsAndForceReRender);
  knobStore.unsubscribe(setPaneKnobs);
}

function connectCallbacks() {
  var channel = _addons["default"].getChannel();

  channel.on(_shared.CHANGE, knobChanged);
  channel.on(_shared.CLICK, knobClicked);
  channel.on(_coreEvents.STORY_CHANGED, resetKnobs);
  channel.on(_shared.RESET, resetKnobsAndForceReRender);
  knobStore.subscribe(setPaneKnobs);
  return disconnectCallbacks;
}

function registerKnobs() {
  (0, _clientApi.useEffect)(connectCallbacks, []);
}

(function (module, exports) {

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

Object.defineProperty(exports, "__esModule", {
  value: true
});
var _exportNames = {
  knob: true,
  text: true,
  "boolean": true,
  number: true,
  color: true,
  object: true,
  select: true,
  radios: true,
  array: true,
  date: true,
  button: true,
  files: true,
  optionsKnob: true,
  withKnobs: true
};
exports.knob = knob;
exports.text = text;
exports["boolean"] = _boolean;
exports.number = number;
exports.color = color;
exports.object = object;
exports.select = select;
exports.radios = radios;
exports.array = array;
exports.date = date;
exports.button = button;
exports.files = files;
exports.optionsKnob = optionsKnob;
exports.withKnobs = void 0;

var _addons = _interopRequireWildcard(require$$0$1);

var _shared = shared$c;

Object.keys(_shared).forEach(function (key) {
  if (key === "default" || key === "__esModule") return;
  if (Object.prototype.hasOwnProperty.call(_exportNames, key)) return;
  if (key in exports && exports[key] === _shared[key]) return;
  Object.defineProperty(exports, key, {
    enumerable: true,
    get: function get() {
      return _shared[key];
    }
  });
});

var _registerKnobs = registerKnobs$1;

function _getRequireWildcardCache(nodeInterop) {
  if (typeof WeakMap !== "function") return null;
  var cacheBabelInterop = new WeakMap();
  var cacheNodeInterop = new WeakMap();
  return (_getRequireWildcardCache = function _getRequireWildcardCache(nodeInterop) {
    return nodeInterop ? cacheNodeInterop : cacheBabelInterop;
  })(nodeInterop);
}

function _interopRequireWildcard(obj, nodeInterop) {
  if (!nodeInterop && obj && obj.__esModule) {
    return obj;
  }

  if (obj === null || _typeof(obj) !== "object" && typeof obj !== "function") {
    return {
      "default": obj
    };
  }

  var cache = _getRequireWildcardCache(nodeInterop);

  if (cache && cache.has(obj)) {
    return cache.get(obj);
  }

  var newObj = {};
  var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor;

  for (var key in obj) {
    if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) {
      var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null;

      if (desc && (desc.get || desc.set)) {
        Object.defineProperty(newObj, key, desc);
      } else {
        newObj[key] = obj[key];
      }
    }
  }

  newObj["default"] = obj;

  if (cache) {
    cache.set(obj, newObj);
  }

  return newObj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function knob(name, options) {
  return _registerKnobs.manager.knob(name, options);
}

function text(name, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'text',
    value: value,
    groupId: groupId
  });
}

function _boolean(name, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'boolean',
    value: value,
    groupId: groupId
  });
}

function number(name, value) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var groupId = arguments.length > 3 ? arguments[3] : undefined;
  var rangeDefaults = {
    min: 0,
    max: 10,
    step: 1
  };
  var mergedOptions = options.range ? _objectSpread(_objectSpread({}, rangeDefaults), options) : options;

  var finalOptions = _objectSpread(_objectSpread({
    type: 'number'
  }, mergedOptions), {}, {
    value: value,
    groupId: groupId
  });

  return _registerKnobs.manager.knob(name, finalOptions);
}

function color(name, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'color',
    value: value,
    groupId: groupId
  });
}

function object(name, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'object',
    value: value,
    groupId: groupId
  });
}

function select(name, options, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'select',
    selectV2: true,
    options: options,
    value: value,
    groupId: groupId
  });
}

function radios(name, options, value, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'radios',
    options: options,
    value: value,
    groupId: groupId
  });
}

function array(name, value) {
  var separator = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ',';
  var groupId = arguments.length > 3 ? arguments[3] : undefined;
  return _registerKnobs.manager.knob(name, {
    type: 'array',
    value: value,
    separator: separator,
    groupId: groupId
  });
}

function date(name) {
  var value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date();
  var groupId = arguments.length > 2 ? arguments[2] : undefined;
  var proxyValue = value ? value.getTime() : new Date().getTime();
  return _registerKnobs.manager.knob(name, {
    type: 'date',
    value: proxyValue,
    groupId: groupId
  });
}

function button(name, callback, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'button',
    callback: callback,
    hideLabel: true,
    groupId: groupId
  });
}

function files(name, accept) {
  var value = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  var groupId = arguments.length > 3 ? arguments[3] : undefined;
  return _registerKnobs.manager.knob(name, {
    type: 'files',
    accept: accept,
    value: value,
    groupId: groupId
  });
}

function optionsKnob(name, valuesObj, value, optionsObj, groupId) {
  return _registerKnobs.manager.knob(name, {
    type: 'options',
    options: valuesObj,
    value: value,
    optionsObj: optionsObj,
    groupId: groupId
  });
}

var defaultOptions = {
  escapeHTML: true
};
var withKnobs = (0, _addons.makeDecorator)({
  name: 'withKnobs',
  parameterName: 'knobs',
  skipIfNoParametersOrOptions: false,
  wrapper: function wrapper(getStory, context, _ref) {
    var options = _ref.options,
        parameters = _ref.parameters;
    var storyOptions = parameters || options;

    var allOptions = _objectSpread(_objectSpread({}, defaultOptions), storyOptions);

    var channel = _addons["default"].getChannel();

    _registerKnobs.manager.setChannel(channel);

    _registerKnobs.manager.setOptions(allOptions);

    channel.emit(_shared.SET_OPTIONS, allOptions);
    (0, _registerKnobs.registerKnobs)();
    return getStory(context);
  }
});
exports.withKnobs = withKnobs;

if (module && module.hot && module.hot.decline) {
  module.hot.decline();
}
}(dist$2, dist$2.exports));

/**
 * Makes any type of knob
 * support for more knobs will be added as they become needed
 *
 * @param {Object} propMap
 * @param {String} groupId
 * @returns {Function} a knob factory callback
 */

const createKnobs = (propMap, groupId) => () => knobFactory(propMap, groupId);
const knobFactory = (propMap, groupId) => Object.keys(propMap).reduce((propObj, prop) => {
  const knob = makeKnob(propMap[prop].knobType)(...propMap[prop].knobArgs, groupId);
  return { ...propObj,
    [prop]: knob()
  };
}, {});
/**
 * Makes a single knob depending on the knob type
 *
 * support for more knobs will be added as they become needed
 *
 * @param {String} knobType
 * @returns {Function} a knob function with bound arguments
 */

const makeKnob = knobType => (...args) => {
  switch (knobType) {
    case 'select':
      return dist$2.exports.select.bind(undefined, ...args);

    default:
      return dist$2.exports.text.bind(undefined, ...args);
  }
};

const GROUP_ID$e = 'ColorAnatomy';
/**
 * The knobs for the anatomy
 */

var knobs$e = createKnobs(map$g, GROUP_ID$e);

/**
 * Knobs for Storybook
 */

const GROUP_ID$d = 'BorderAnatomy';
var knobs$d = createKnobs(map$9, GROUP_ID$d);

/**
 * Knobs for Storybook
 */

const GROUP_ID$c = 'SpacingAnatomy';
var knobs$c = createKnobs(map$f, GROUP_ID$c);

/**
 * Knobs for Storybook
 */

const GROUP_ID$b = 'PositionAnatomy';
var knobs$b = createKnobs(map$e, GROUP_ID$b);

/**
 * Knobs for Storybook
 */

const GROUP_ID$a = 'TypographyAnatomy';
var knobs$a = createKnobs(map$d, GROUP_ID$a);

/**
 * Knobs for Storybook
 */

const GROUP_ID$9 = 'DisplayAnatomy';
var knobs$9 = createKnobs(map$c, GROUP_ID$9);

/**
 * Knobs for Storybook
 */

const GROUP_ID$8 = 'FlexAnatomy';
var knobs$8 = createKnobs(map$b, GROUP_ID$8);

/**
 * Knobs for Storybook
 */

const GROUP_ID$7 = 'DimensionsAnatomy';
var knobs$7 = createKnobs(map$a, GROUP_ID$7);

/**
 * Knobs for Storybook
 */

const GROUP_ID$6 = 'ShadowsAnatomy';
var knobs$6 = createKnobs(map$8, GROUP_ID$6);

/**
 * Knobs for Storybook
 */

const GROUP_ID$5 = 'TextFormatAnatomy';
var knobs$5 = createKnobs(map$6, GROUP_ID$5);

/**
 * Knobs for Storybook
 */

const GROUP_ID$4 = 'OptimisticAnatomy';
var knobs$4 = createKnobs(map$5, GROUP_ID$4);

/**
 * Knobs for Storybook
 */

const GROUP_ID$3 = 'ZDepthAnatomy';
var knobs$3 = createKnobs(map$4, GROUP_ID$3);

/**
 * Knobs for Storybook
 */

const GROUP_ID$2 = 'GridAnatomy';
var knobs$2 = createKnobs(map$3, GROUP_ID$2);

/**
 * Knobs for Storybook
 */

const GROUP_ID$1 = 'CoreAnatomy';
var knobs$1 = createKnobs(map$2, GROUP_ID$1);

/**
 * Knobs for Storybook
 */

const GROUP_ID = 'HelperAnatomy';
var knobs = createKnobs(map$1, GROUP_ID);

export { attachDataAttr, anatomy$8 as borderAnatomy, map$9 as borderPropMap, index$a as borderStyles, anatomy$f as colorAnatomy, map$g as colorPropMap, index$h as colorStyles, anatomy$1 as coreAnatomy, map$2 as corePropMap, index$3 as coreStyles, anatomy$9 as dimensionsAnatomy, map$a as dimensionsPropMap, index$b as dimensionsStyles, anatomy$b as displayAnatomy, map$c as displayPropMap, index$d as displayStyles, anatomy$a as flexAnatomy, map$b as flexPropMap, index$c as flexStyles, knobs$d as getBorderKnobs, knobs$e as getColorKnobs, knobs$1 as getCoreKnobs, knobs$7 as getDimensionsKnobs, knobs$9 as getDisplayKnobs, knobs$8 as getFlexKnobs, knobs$2 as getGridKnobs, knobs as getHelperKnobs, knobs$b as getPositionKnobs, knobs$6 as getShadowsKnobs, knobs$c as getSpacingKnobs, knobs$a as getTypographyKnobs, getValueFromTheme, knobs$4 as getoptimisticKnobs, knobs$5 as gettextFormatKnobs, knobs$3 as getzDepthKnobs, anatomy$2 as gridAnatomy, map$3 as gridPropMap, index$4 as gridStyles, anatomy as helperAnatomy, map$1 as helperPropMap, index$2 as helperStyles, mergeAnatomies, anatomy$4 as optimisticAnatomy, map$5 as optimisticPropMap, index$6 as optimisticStyles, anatomy$6 as overflowAnatomy, map$7 as overflowPropMap, index$8 as overflowStyles, anatomy$d as positionAnatomy, map$e as positionPropMap, index$f as positionStyles, anatomy$7 as shadowsAnatomy, map$8 as shadowsPropMap, index$9 as shadowsStyles, anatomy$e as spacingAnatomy, map$f as spacingPropMap, index$g as spacingStyles, styledAnatomyFactory, anatomy$5 as textFormatAnatomy, map$6 as textFormatPropMap, index$7 as textFormatStyles, anatomy$c as typographyAnatomy, map$d as typographyPropMap, index$e as typographyStyles, anatomy$3 as zDepthAnatomy, map$4 as zDepthPropMap, index$5 as zDepthStyles };
